/* ──────────────────────────────────────────
   Room Designer — Flooring Overlay Module
   Phase 3a: Roll layout, room assignment, before/after toggle
   ────────────────────────────────────────── */

/* requestIdleCallback polyfill (Safari lacks native support) */
const _requestIdleCallback = window.requestIdleCallback ||
  function(cb, opts) { return setTimeout(() => cb({ timeRemaining: () => 50 }), opts?.timeout || 100); };
const _cancelIdleCallback = window.cancelIdleCallback || clearTimeout;

/* ── Flooring Installation Rules ──────────────
   Hard constraints:
   - No piece longer than maxLengthFt (max orderable roll)
   - Minimum orderable roll is minLengthFt (short cuts pair up to meet this)
   - 6" overage added to start wall of each strip for field trimming

   Stagger rules (in priority order):
   - Adjacent columns must have seams ≥ 48" (4 ft) apart
   - Single roll (no head seam) when room fits within maxLengthFt
   - 2-piece: split at 1/3-2/3, alternating even/odd columns
   - 2-piece fallback: try alternate position, then maximize distance
   - 3+ piece: shift odd columns by 4 ft
   - Same-parity columns snap seams to align (up to 1 ft extra material)
   - Single-piece columns don't wipe stagger tracking

   Rounding (applied last):
   - All piece lengths rounded UP to whole feet
   - Seam positions recorded from rounded values
   ────────────────────────────────────────────── */

class RoomDesignerOverlay {
  constructor(rd) {
    this.rd = rd;
    this.products = [];
    this.currentStep = 'draw';   // 'draw' | 'floor'
    this.showOverlay = false;    // starts false — Draw Space tab has no floor overlay
    this._hoveredRoom = null;
    this._hoveredZone = null;
    this._hoveredTurf = null;
    this._floorSelections = []; // [{room, zone}] — zone is null for whole-room selection
    this._floorInfoPinned = false; // true when tooltip is shown via click-selection (persistent)
    this._floorSelectCorner1 = null;  // world coords of first box-select click
    this._floorSelectEnd = null;      // world coords of current box endpoint
    this._hoverStartTime = 0;
    this._hoverFadeRAF = null;
    this._trashAlpha = 0;          // current overage text fade level
    this._trashTarget = 0;         // target: 0 = hidden, 1 = visible
    this._trashLastFrame = 0;      // for delta-time lerp
    this._trashScrollTimer = null;
    this._trashFadeRAF = null;
    this._overlayDetailAlpha = 1;  // floor detail fade: 1 = full detail, 0 = solid color only
    this._overlayDetailTarget = 1;
    this._overlayDetailLastFrame = 0;
    this._flashOverlayAlpha = 0;       // >0 = flash preview active, fades to 0
    this._flashOverlayPhase = null;    // 'hold' | 'fade'
    this._flashOverlayTimer = null;
    this._flashOverlayRAF = null;
    this._flashSeamsOverageOnly = false; // when true, skip strip fills in overlay render
    this._overlayDetailTimer = null;
    this._overlayDetailFadeTimer = null;
    this._floorCanvasListenersAdded = false;
    this._activeBrush = null; // { productId, colorId } — set after products load
    this._sidePanelOpen = false;
    this._sidePanelMinimized = false;
    this._textureImages = {}; // url → { img, loaded, failed }
    this._texRenderTimer = null; // coalescing timer for texture load re-renders
    this._loadingOverlay = null;  // DOM element for asset loading spinner
    this._loadingPollTimer = null;
    this._exteriorZoneFlooring = []; // [{cx, cy, productId, colorId, flooringAngle}]
    this._exteriorZoneCache = null; // {fp, zones}
    this._hoveredExteriorZone = null;
    this._rollOptimizationMode = 'minSeams'; // 'minSeams' | 'minMaterial' | 'uniform'
    this._uniformTargetLen = 0; // 0 = auto (longest piece), >0 = user-chosen target in feet
    this._rollLenOverride = {}; // rep-only: { productId: { min, max } } — per-product overrides of the site's saved min/maxLengthFt; persisted with the plan
    this._rollShare = {}; // rep-only: { 'productId::colorId': true|false } — roll sharing across spaces (Ryan 2026-10-15); absent = never asked = separate rolls; persisted with the plan
    this._turfDefaults = { lines: false, hashes: false, hashSides: 'long', hashSpacing: 3, centerHashes: false, rotated: true, lineColor: '#FFFFFF', lineOrigin: 'center', yardNumbers: false, yardNumberSpacing: 10, yardNumberMode: 'football', logoColor: '', borderLine: false, borderLineInset: 4, borderLineEdges: ['top', 'right', 'bottom', 'left'], sledLanes: false, sledLaneCount: 2, sledLaneEdges: ['right'], sledLaneInterval: 5, sledLaneStartFinish: true, sledLaneNumbers: false, agilityLadderLength: 18, lineDirection: 'lengthwise' };

    this._loadProducts();
    this._setupStepBar();
    this._installHooks();
    this._wrapSerialization();
    this._wrapUndoRedo();
    this._wrapMeasurements();
    this._setupKeyboardShortcuts();
    this._createRotateButton();
    // Floor eraser cursor (used by unified eraser in floor mode)
    this._floorEraserCursor = 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27%3E%3Crect x=%274%27 y=%2710%27 width=%2716%27 height=%2710%27 rx=%272%27 fill=%27%23ff6b6b%27 stroke=%27%23333%27 stroke-width=%271.5%27 transform=%27rotate(-30 12 15)%27/%3E%3Crect x=%274%27 y=%2714%27 width=%2716%27 height=%276%27 rx=%271%27 fill=%27%23ffaaaa%27 stroke=%27%23333%27 stroke-width=%271%27 transform=%27rotate(-30 12 15)%27/%3E%3C/svg%3E") 12 20, pointer';
    // Palette button removed — colors now live in Finishing Touches side panel
    this._setupSidePanelEvents();
    this._bgPreloadTimer = setTimeout(() => this._backgroundPreload(), 3000);
    this._bgPreloadRIC = null;
    this._paintRollerCursor = this._buildPaintRollerCursor();
    // Deferred turf panel sync: check selection after each render (outside canvas pipeline)
    const origDoRender = rd._doRender.bind(rd);
    rd._doRender = function() {
      origDoRender();
      try {
        const selTurf = rd.selectedObjects ? [...rd.selectedObjects].filter(o => o.type === 'turf') : [];
        const totalTurf = (rd.objects || []).filter(o => o.type === 'turf').length;
        const selSize = rd.selectedObjects ? rd.selectedObjects.size : 0;
        const fp = 'T' + totalTurf + ':S' + selSize + ':' + (selTurf.length === 0 ? '0' : selTurf.map(o => {
          const idx = (rd.objects || []).indexOf(o);
          return idx + '|' + (o.turfLogos ? o.turfLogos.length : 0) + '|' + (o.turfLogos ? o.turfLogos.map(l => l.color || '').join(';') : '') + '|' + (o.turfLines ? '1' : '0') + '|' + (o.turfHashes ? '1' : '0') + '|' + (o.turfHashSpacing || 3) + '|' + (o.turfYardNumbers ? '1' : '0') + '|' + (o.turfYardNumberSpacing || 10) + '|' + (o.turfYardNumberMode || 'football') + '|' + (o.turfBorderLine ? '1' : '0') + '|' + (o.turfBorderLineEdges ? o.turfBorderLineEdges.sort().join(',') : 'bottom,left,right,top') + '|' + (o.turfSledLanes ? '1' : '0') + '|' + (o.turfSledLaneEdges ? o.turfSledLaneEdges.sort().join(',') : 'right');
        }).join(','));
        if (fp !== self._lastTurfSelFp) {
          self._lastTurfSelFp = fp;
          if (!self._suppressFpSync) {
            const body = document.getElementById('rdSidePanelBody');
            if (body) self._syncTurfPanelToSelection(body);
          }
          self._suppressFpSync = false; // clear after check, not before render
        }
      } catch (_e) { /* never break rendering */ }
    };
    // Rehydrate turf logos from auto-load (Storage loads before overlay wraps _loadDesignData)
    this._rehydrateTurfLogos();
    // Rehydrate overlay data from auto-load (Storage _loadDesignData ran before overlay wrapped it,
    // so _overlay.brandColors and _overlay.platformLogo were ignored — re-read them now).
    // Must use the same rep-prefixed key that _doAutoSave() writes to.
    try {
      const _rehydratePrefix = rd._repMode ? (function() {
        const repId = localStorage.getItem('rd_share_rep_id');
        return repId ? 'rd_rep_' + repId + '_' : '';
      })() : '';
      const autoRaw = localStorage.getItem(_rehydratePrefix + 'rd_autosave');
      if (autoRaw) {
        const autoData = JSON.parse(autoRaw);
        if (autoData._overlay) {
          // Brand colors
          if (autoData._overlay.brandColors) {
            rd._brandColors = autoData._overlay.brandColors;
            // Preload any saved pattern images (camo textures)
            for (const v in rd._brandColors) {
              if (rd._brandColors[v].uphPattern) rd._ensurePatternImage(rd._brandColors[v].uphPattern);
              if (rd._brandColors[v].pcPattern) rd._ensurePatternImage(rd._brandColors[v].pcPattern);
            }
          }
          // Platform logo
          if (autoData._overlay.platformLogo) {
            this._platformLogo = autoData._overlay.platformLogo;
            this._rehydratePlatformLogo();
          }
          if (autoData._overlay.hardwoodLogo) {
            this._hardwoodLogo = autoData._overlay.hardwoodLogo;
            this._rehydrateHardwoodLogo();
          }
          if (autoData._overlay.brandColors || autoData._overlay.platformLogo || autoData._overlay.hardwoodLogo) {
            rd.render();
          }
        }
      }
    } catch (_e) { /* corrupt autosave — ignore */ }
    // Re-run measurements now that overlay stubs are installed (storage already called it before wrapping)
    rd.updateMeasurements();
  }

  // ── Products ─────────────────────────────

  _loadProducts() {
    // Fixed display order for categories
    this._categoryOrder = ['Rubber Rolls', 'Rubber / Vinyl Rolls', 'Tiles', 'Planks', 'Turf', 'Pavers'];

    // 1) Read schema products synchronously (inline data from Liquid)
    const schemaRaw = window.__rdSchemaProducts || [];
    const schemaIds = new Set();
    for (const raw of schemaRaw) {
      const p = this._normalizeSchemaProduct(raw);
      if (p) {
        this.products.push(p);
        schemaIds.add(p.id);
      }
    }
    this._rebuildCategories();
    this._setDefaultBrush();

    // Migrate old product IDs in existing rooms
    this._migrateOldProductIds();
    // Migrate old turfSledLanePosition → turfSledLaneEdges
    this._migrateSledLanePosition();

    // Build side panel content if already in floor mode
    if (this._sidePanelOpen) {
      this._buildFloorPanelContent();
    }
    this._updateMaterialSummary();

    // 2) Fetch JSON fallback — append products whose id isn't already in schema
    const url = window.__rdProductsUrl;
    if (url) {
      fetch(url)
        .then(r => r.json())
        .then(data => {
          let added = false;
          for (const jp of (data.products || [])) {
            if (!schemaIds.has(jp.id) && !this.products.find(p => p.id === jp.id)) {
              // Preserve MOQ from pricing before stripping it
              if (jp.pricing && jp.pricing.moqSqFt > 0) {
                jp.moq = { qty: jp.pricing.moqSqFt, unit: 'sq ft', note: jp.pricing.moqNote || `Minimum order: ${jp.pricing.moqSqFt} sq ft` };
              }
              delete jp.pricing;
              if (!jp.overlayColors) jp.overlayColors = this._defaultOverlayColors();
              if (!jp.colorOverlays && jp.colors) {
                jp.colorOverlays = {};
                for (const col of jp.colors) {
                  if (col.hex) jp.colorOverlays[col.id] = this._generateOverlayColors(col.hex, jp.shape === 'paver');
                }
              }
              this.products.push(jp);
              added = true;
            }
          }
          if (added) {
            this._rebuildCategories();
            this._setDefaultBrush();
            this._migrateOldProductIds();
            if (this._sidePanelOpen) {
              this._buildFloorPanelContent();
            }
            this._updateMaterialSummary();
          }
        })
        .catch(() => {});
    }
  }

  _normalizeSchemaProduct(raw) {
    if (!raw || !raw.handle) return null;

    const category = raw.category || 'Rubber Rolls';
    const shape = raw.shape || 'roll';
    const isTurf = (category === 'Turf');
    const isRoll = (category === 'Rubber Rolls' || isTurf || category === 'Rubber / Vinyl Rolls' || category === 'Vinyl Topped Rubber Rolls');
    // Tile-shaped products in the Turf category (e.g. turf tiles) paint into zones like any other tile,
    // bypassing the two-corner turf-draw flow reserved for roll-shaped turf.
    const isTileShape = (shape === 'square-tile' || shape === 'puzzle-tile' || shape === 'paver' || shape === 'plank');
    const type = isTileShape ? 'tile' : (isTurf ? 'turf' : (isRoll ? 'roll' : 'tile'));
    // Allow a product to surface in multiple panel sections (e.g. a turf tile in both Turf and Tiles).
    // Primary `category` always leads; `extraCategories` from the schema appends extras while dedup-ing.
    const extraCats = Array.isArray(raw.extraCategories) ? raw.extraCategories.filter(c => c && c !== category) : [];
    const productCategories = [category, ...extraCats];

    /* CUSTOM_PLANNER_BY_CLAUDE — Custom product (no Shopify link): build colors from schema slots */
    if (raw.isCustomProduct && raw.customColorSlots) {
      const colors = [];
      for (const slot of raw.customColorSlots) {
        if (!slot || !slot.name) continue;
        const colorId = slot.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        const hex = slot.hex || '#888888';
        const swatchImg = slot.swatchUrl || null;
        colors.push({
          id: colorId,
          name: slot.name,
          hex,
          swatch: swatchImg ? `url(${swatchImg})` : hex,
          imageUrl: swatchImg,
          swatchImageUrl: swatchImg,
          textureUrl: raw.textureUrl || swatchImg,
          variantId: null,
          moq: 0
        });
      }
      if (colors.length === 0) return null;
      const overlayColors = this._defaultOverlayColors();
      const colorOverlays = {};
      for (const col of colors) colorOverlays[col.id] = this._generateOverlayColors(col.hex, shape === 'paver');
      return {
        id: raw.handle,
        name: raw.title || raw.handle,
        shopifyId: null,
        description: '',
        type,
        shape,
        iconUrl: raw.iconUrl || null,
        iconDarkUrl: raw.iconDarkUrl || null,
        widthFt: 0,
        heightFt: 0,
        widthInches: 0,
        heightInches: 0,
        thicknessDisplay: raw.thickness || '',
        priceLevel: raw.priceLevel || '',
        minLengthFt: 0,
        maxLengthFt: 0,
        overageFt: 0,
        minPieceLengthFt: 0,
        categories: productCategories,
        colors,
        defaultColor: colors[0].id,
        overlayColors,
        colorOverlays,
        moqUnit: '',
        moqFallbackQty: 0,
        isDefault: !!raw.isDefault,
        customColors: !!raw.customColors,
        platformCompatible: !!raw.platformCompatible,
        textureNoRotate: !!raw.textureNoRotate,
        url: null,
        _fromSchema: true,
        _isCustomProduct: true
      };
    }
    /* END CUSTOM_PLANNER_BY_CLAUDE */

    // Texture: find each variant's swatch/texture image by gallery position.
    // Shopify groups images per variant: each variant's featured_image starts a contiguous
    // block of images in the gallery. The LAST image in each block is the swatch photo.
    // image.variants in Liquid only returns the featured_image link, so we use position instead.
    const allImages = raw.allImages || [];
    const variantSwatchMap = {};
    if (allImages.length > 1) {
      // Find each variant's featured_image position in the gallery
      const featuredPosMap = {};
      for (const v of (raw.variants || [])) {
        if (!v.imageUrl) continue;
        const idx = allImages.findIndex(img => img.url === v.imageUrl);
        if (idx >= 0) featuredPosMap[v.id] = idx;
      }
      // Unique sorted group start positions
      const starts = [...new Set(Object.values(featuredPosMap))].sort((a, b) => a - b);
      // Map each group start → last image in that group (the swatch)
      const startToSwatch = {};
      for (let i = 0; i < starts.length; i++) {
        const groupEnd = (i + 1 < starts.length) ? starts[i + 1] : allImages.length;
        if (groupEnd - 1 > starts[i]) startToSwatch[starts[i]] = allImages[groupEnd - 1].url;
      }
      for (const [vid, pos] of Object.entries(featuredPosMap)) {
        if (startToSwatch[pos]) variantSwatchMap[vid] = startToSwatch[pos];
      }
    }

    // Build colors from variants (deduplicate by color name — e.g. center/corner/edge pieces).
    // For texture: the LAST variant of each color wins (user puts swatch on the last variant).
    const colors = [];
    const seenColors = new Map(); // colorName → index in colors[]
    for (const v of (raw.variants || [])) {
      const colorName = v.colorName || 'Default';
      if (seenColors.has(colorName)) {
        // Duplicate color (center/corner/edge) — update textureUrl if this variant has a swatch
        if (!raw.textureUrl) {
          const img = variantSwatchMap[v.id] || v.imageUrl || null;
          if (img) colors[seenColors.get(colorName)].textureUrl = img;
        }
        continue;
      }
      const colorId = v.colorHandle || colorName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const hex = this._colorNameToHex(colorName);
      const swatchImg = v.imageUrl || variantSwatchMap[v.id] || null;
      // The storefront's own swatch for this colour (option-value swatch image, emitted by the
      // flooring-config section as optionSwatches) — what the swatch grids show; imageUrl stays the
      // product photo for thumbnails and the quote.
      const nativeSwatch = (raw.optionSwatches && raw.optionSwatches[colorName])
        || (window.__rdSwatchFiles && window.__rdSwatchFiles[String(colorName).trim().toLowerCase()])
        || null;
      const textureImg = raw.textureUrl || variantSwatchMap[v.id] || v.imageUrl || null;
      const swatch = swatchImg ? `url(${swatchImg})` : hex;
      seenColors.set(colorName, colors.length);
      colors.push({
        id: colorId,
        name: colorName,
        hex,
        swatch,
        imageUrl: swatchImg,
        swatchImageUrl: nativeSwatch,
        textureUrl: textureImg,
        variantId: v.id,
        sku: v.sku || '',
        moq: v.moq || 0
      });
    }

    if (colors.length === 0) return null;

    // Universal default overlay colors (fallback if no per-color overlay)
    const overlayColors = this._defaultOverlayColors();
    // Per-color overlay colors with contrast-aware seam lines
    const colorOverlays = {};
    for (const col of colors) {
      colorOverlays[col.id] = this._generateOverlayColors(col.hex, shape === 'paver');
    }

    const product = {
      id: raw.handle,
      name: raw.title,
      shopifyId: raw.shopifyId,
      description: '',
      type,
      shape,
      iconUrl: raw.iconUrl || null,
      iconDarkUrl: raw.iconDarkUrl || null,
      widthFt: (raw.widthInches || 48) / 12,
      heightFt: (raw.heightInches || 0) / 12,
      widthInches: raw.widthInches || 48,
      heightInches: raw.heightInches || 0,
      thicknessDisplay: raw.thickness || '',
      priceLevel: raw.priceLevel || '',
      minLengthFt: raw.minLengthFt || 25,
      maxLengthFt: raw.maxLengthFt || 50,
      overageFt: 0.5,
      minPieceLengthFt: 5,
      categories: productCategories,
      colors,
      defaultColor: colors[0].id,
      overlayColors,
      colorOverlays,
      moqUnit: raw.moqUnit || '',
      moqFallbackQty: raw.moqQty || 0,
      qtyStep: raw.qtyStep || 1,
      isDefault: !!raw.isDefault,
      customColors: !!raw.customColors,
      platformCompatible: !!raw.platformCompatible,
      textureNoRotate: !!raw.textureNoRotate,
      url: raw.productUrl || '',
      _fromSchema: true
    };

    return product;
  }

  _rebuildCategories() {
    const catSet = new Set();
    for (const p of this.products) {
      for (const c of (p.categories || [])) catSet.add(c);
    }
    // Use fixed display order, only include categories that have products
    this._productCategories = this._categoryOrder.filter(c => catSet.has(c));
    // Add any categories not in the fixed order
    for (const c of catSet) {
      if (!this._productCategories.includes(c)) this._productCategories.push(c);
    }
  }

  _setDefaultBrush() {
    // No longer auto-set brush — user must explicitly click a product swatch to start painting
  }

  /** Scan alpha channel to find tight bounding box of opaque pixels.
   *  Returns { x, y, w, h } as fractions of image size (0-1), or null if fully opaque. */
  _computeLogoTightBounds(img) {
    const cw = img.naturalWidth || img.width;
    const ch = img.naturalHeight || img.height;
    // Cap scan resolution for performance (large images get downsampled)
    const maxScan = 512;
    const sw = Math.min(cw, maxScan), sh = Math.min(ch, maxScan);
    const c = document.createElement('canvas');
    c.width = sw; c.height = sh;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, sw, sh);
    const data = ctx.getImageData(0, 0, sw, sh).data;
    let minX = sw, minY = sh, maxX = 0, maxY = 0;
    let hasTransparent = false;
    const alphaThreshold = 10; // pixels below this alpha are "transparent"
    // Per-row X profile: for each scanline Y, track leftmost/rightmost opaque pixel
    const rowProfile = new Array(sh);
    // Per-column Y profile: for each column X, track topmost/bottommost opaque pixel
    const colProfile = new Array(sw);
    for (let i = 0; i < sw; i++) colProfile[i] = null;
    for (let y = 0; y < sh; y++) {
      let rowMinX = sw, rowMaxX = -1;
      for (let x = 0; x < sw; x++) {
        const a = data[(y * sw + x) * 4 + 3];
        if (a >= alphaThreshold) {
          if (x < rowMinX) rowMinX = x;
          if (x > rowMaxX) rowMaxX = x;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          // Column profile
          if (!colProfile[x]) colProfile[x] = { minY: y, maxY: y };
          else colProfile[x].maxY = y;
        } else {
          hasTransparent = true;
        }
      }
      rowProfile[y] = rowMaxX >= 0 ? { minX: rowMinX / sw, maxX: (rowMaxX + 1) / sw } : null;
    }
    // Normalize column profile to fractions
    for (let x = 0; x < sw; x++) {
      if (colProfile[x]) {
        colProfile[x] = { minY: colProfile[x].minY / sh, maxY: (colProfile[x].maxY + 1) / sh };
      }
    }
    if (!hasTransparent) return null; // fully opaque → use full bounds
    if (maxX < minX) return null; // fully transparent → shouldn't happen

    // Fill interior gaps in both profiles so lines can't peek through letter holes
    // (e.g. the hole in "O") or inter-letter spaces. Any transparent slot between the
    // first and last opaque slot gets interpolated from its nearest opaque neighbors.
    const _fillGaps = (profile, len) => {
      // Find first and last opaque indices
      let first = -1, last = -1;
      for (let i = 0; i < len; i++) { if (profile[i]) { first = i; break; } }
      for (let i = len - 1; i >= 0; i--) { if (profile[i]) { last = i; break; } }
      if (first < 0 || first === last) return;
      // For each transparent gap between first and last, inherit envelope from nearest
      // opaque neighbors on each side
      let leftNeighbor = null;
      for (let i = first; i <= last; i++) {
        if (profile[i]) { leftNeighbor = profile[i]; continue; }
        // Find right neighbor
        let rightNeighbor = null;
        for (let j = i + 1; j <= last; j++) { if (profile[j]) { rightNeighbor = profile[j]; break; } }
        if (leftNeighbor && rightNeighbor) {
          // Use the union envelope of both neighbors
          const minKey = Object.keys(leftNeighbor).find(k => k.startsWith('min'));
          const maxKey = Object.keys(leftNeighbor).find(k => k.startsWith('max'));
          profile[i] = {};
          profile[i][minKey] = Math.min(leftNeighbor[minKey], rightNeighbor[minKey]);
          profile[i][maxKey] = Math.max(leftNeighbor[maxKey], rightNeighbor[maxKey]);
        }
      }
    };
    _fillGaps(rowProfile, sh);
    _fillGaps(colProfile, sw);

    return {
      x: minX / sw,      // left edge as fraction
      y: minY / sh,      // top edge as fraction
      w: (maxX - minX + 1) / sw,  // width as fraction
      h: (maxY - minY + 1) / sh,  // height as fraction
      rowProfile: rowProfile,      // per-scanline {minX, maxX} as fractions, or null
      colProfile: colProfile,      // per-column {minY, maxY} as fractions, or null
      scanW: sw,                   // number of columns in profile
      scanH: sh                    // number of scanlines in profile
    };
  }

  _rehydrateTurfLogos() {
    const rd = this.rd;
    if (!rd.objects) return;
    for (const obj of rd.objects) {
      // Migration: singular props → turfLogos array
      if (obj.turfLogoDataURL && !obj.turfLogos) {
        obj.turfLogos = [{
          dataURL: obj.turfLogoDataURL,
          image: obj.turfLogoImage instanceof HTMLImageElement ? obj.turfLogoImage : null,
          width: obj.turfLogoWidth,
          height: obj.turfLogoHeight,
          x: obj.turfLogoX || 0,
          y: obj.turfLogoY || 0,
          rotation: obj.turfLogoRotation || 0,
          color: obj.turfLogoColor || null,
          tightBounds: obj.turfLogoTightBounds || null
        }];
        delete obj.turfLogoDataURL; delete obj.turfLogoImage;
        delete obj.turfLogoWidth; delete obj.turfLogoHeight;
        delete obj.turfLogoX; delete obj.turfLogoY;
        delete obj.turfLogoRotation; delete obj.turfLogoColor;
        delete obj.turfLogoTightBounds;
      }
      // Rehydrate each logo's image from dataURL
      if (obj.turfLogos) {
        for (const logo of obj.turfLogos) {
          if (logo.image && !(logo.image instanceof HTMLImageElement)) {
            logo.image = null;
          }
          if (logo.dataURL && !logo.image) {
            const img = new Image();
            const logoRef = logo;
            img.onload = () => {
              logoRef.image = img;
              if (logoRef.tightBounds === undefined) {
                logoRef.tightBounds = this._computeLogoTightBounds(img);
              }
              rd.render();
            };
            img.src = logo.dataURL;
          }
        }
      }
    }
  }

  _migrateOldProductIds() {
    const migrations = {};
    // Auto-detect schema products that match JSON product names but have different IDs
    for (const sp of this.products) {
      if (!sp._fromSchema) continue;
      for (const jp of this.products) {
        if (jp._fromSchema) continue;
        if (sp.name === jp.name && sp.id !== jp.id) {
          migrations[jp.id] = sp.id;
        }
      }
    }
    for (const room of this.rd.rooms) {
      if (room.flooring && migrations[room.flooring.productId]) {
        room.flooring.productId = migrations[room.flooring.productId];
      }
      if (room.zoneFlooring) {
        for (const zf of room.zoneFlooring) {
          if (migrations[zf.productId]) zf.productId = migrations[zf.productId];
        }
      }
    }
  }

  _migrateSledLanePosition() {
    const rd = this.rd;
    const allObjs = [...rd.objects, ...(rd.currentRoom ? [rd.currentRoom] : [])];
    for (const obj of allObjs) {
      if (obj.turfSledLanePosition !== undefined && obj.turfSledLaneEdges === undefined) {
        const pos = obj.turfSledLanePosition;
        // Sled lanes always use non-rotated (lengthwise) orientation
        obj.turfSledLaneEdges = pos === 'left' ? ['left'] : pos === 'right' ? ['right'] : ['left', 'right'];
        delete obj.turfSledLanePosition;
      }
    }
  }

  _getProduct(id) {
    return this.products.find(p => p.id === id) || null;
  }

  // ── Rep-only per-product roll length overrides ──────────────
  // The product's min/maxLengthFt come from the site (flooring-config block settings /
  // custom.min_roll_length metafields). A rep can override them per-product for a session;
  // overrides persist with the saved plan. Defaults are captured on the product the first
  // time it's touched so Reset can restore them and the UI can show them.
  _rollLenDefaults(product) {
    if (product._defaultMinLengthFt == null) product._defaultMinLengthFt = product.minLengthFt;
    if (product._defaultMaxLengthFt == null) product._defaultMaxLengthFt = product.maxLengthFt;
    return { min: product._defaultMinLengthFt, max: product._defaultMaxLengthFt };
  }

  // Apply the current override (or the captured default) onto the product's live
  // min/maxLengthFt — the fields every roll layout calc already reads.
  _applyRollLenOverride(product) {
    const def = this._rollLenDefaults(product);
    const ov = this._rollLenOverride[product.id];
    product.minLengthFt = (ov && ov.min > 0) ? ov.min : def.min;
    product.maxLengthFt = (ov && ov.max > 0) ? ov.max : def.max;
  }

  _applyAllRollLenOverrides() {
    for (const p of this.products) {
      if (p && p.type === 'roll') this._applyRollLenOverride(p);
    }
  }

  // Drop all cached flooring layouts so the next render/measure recomputes them.
  // The layout cache key does NOT track the roll-length limits, so any change to a
  // product's min/maxLengthFt must invalidate here — otherwise "This Layout" keeps
  // serving a stale layout while the Seam Plan recomputes fresh, and they disagree.
  // (The load path does the same thing inline after restoring overrides.)
  _invalidateFlooringLayoutCaches() {
    for (const r of this.rd.rooms) {
      r._flooringLayout = null;
      r._flooringLayoutOptMode = null;
      r._zoneLayouts = null;
    }
  }

  // Returns a loaded Image for the given URL, or null if not yet ready.
  // Starts loading on first call; triggers re-render when the image arrives.
  _getTextureImage(url) {
    if (!url) return null;
    const entry = this._textureImages[url];
    if (entry) return entry.loaded ? entry.img : null;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    this._textureImages[url] = { img, loaded: false, failed: false };
    img.onload = () => {
      this._textureImages[url].loaded = true;
      // Coalesce multiple texture loads into one render (150ms window)
      clearTimeout(this._texRenderTimer);
      this._texRenderTimer = setTimeout(() => { this._texRenderTimer = null; this.rd.render(); }, 150);
    };
    img.onerror = () => {
      this._textureImages[url].failed = true;
    };
    img.src = url;
    return null;
  }

  // Build a 2×2 composite texture with 90° rotation variations to reduce visual repetition.
  // targetCellPx controls the per-cell resolution — sized to match screen output so the
  // browser's pattern renderer doesn't need to do extreme downsampling (which causes Moiré).
  // Cached per source + quantized size; cache evicts when >20 entries.
  _get2x2Texture(img, targetCellPx) {
    if (!this._composite2x2) this._composite2x2 = new Map();
    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    const maxNative = Math.max(nw, nh);
    // Quantize to nearest power-of-2 for cache stability across minor zoom changes
    const raw = targetCellPx || maxNative;
    const quantized = Math.min(maxNative, Math.max(32, Math.pow(2, Math.round(Math.log2(raw)))));
    const key = (img.src || '') + ':' + quantized;
    if (this._composite2x2.has(key)) return this._composite2x2.get(key);
    if (this._composite2x2.size > 20) this._composite2x2.clear();
    const s = quantized; // cell pixel size
    const c = document.createElement('canvas');
    c.width = s * 2; c.height = s * 2;
    const g = c.getContext('2d');
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    // Top-left: original
    g.drawImage(img, 0, 0, s, s);
    // Top-right: 90° CW
    g.save(); g.translate(s * 2, 0); g.rotate(Math.PI / 2);
    g.drawImage(img, 0, 0, s, s); g.restore();
    // Bottom-left: 270° CW (90° CCW)
    g.save(); g.translate(0, s * 2); g.rotate(-Math.PI / 2);
    g.drawImage(img, 0, 0, s, s); g.restore();
    // Bottom-right: 180°
    g.save(); g.translate(s * 2, s * 2); g.rotate(Math.PI);
    g.drawImage(img, 0, 0, s, s); g.restore();
    this._composite2x2.set(key, c);
    return c;
  }

  // ── Color / Overlay Helpers ─────────────

  _colorNameToHex(name) {
    const n = (name || '').toLowerCase();
    const map = {
      'black': '#2a2a2a', 'dark': '#2a2a2a',
      'gray': '#555555', 'grey': '#555555', 'dark gray': '#555555', 'dark grey': '#555555',
      'charcoal': '#444444', 'slate': '#555566',
      'blue': '#3366aa', 'navy': '#1a2a4a', 'light blue': '#6699cc',
      'red': '#cc3333', 'dark red': '#882222', 'wine': '#772233',
      'green': '#2d7a3a', 'field green': '#2d7a3a', 'forest': '#1a5a2a', 'olive': '#5a6a2a',
      'brown': '#6a4a2a', 'tan': '#b89a6a', 'beige': '#c8b898',
      'white': '#e8e8e8', 'cream': '#e8dcc8',
      'orange': '#cc6622', 'yellow': '#ccaa22',
      'purple': '#6633aa', 'eggplant': '#4a2266'
    };
    // Exact match
    if (map[n]) return map[n];
    // Partial match — find a key that's contained in the name
    for (const [key, hex] of Object.entries(map)) {
      if (n.includes(key)) return hex;
    }
    return '#555555'; // default gray
  }

  _parseHex(hex) {
    hex = hex.replace('#', '');
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16)
    };
  }

  _darkenHex(hex, factor) {
    const c = this._parseHex(hex);
    const d = v => Math.round(v * (1 - factor));
    const h = v => v.toString(16).padStart(2, '0');
    return '#' + h(d(c.r)) + h(d(c.g)) + h(d(c.b));
  }

  /** Draw two-tone seam lines: full-width dark stroke offset top-left, full-width light
   *  stroke offset bottom-right. The overlap blends to neutral; the exposed edges create
   *  a bevel effect visible on any floor color. Same apparent thickness as a single stroke.
   *  @param {CanvasRenderingContext2D} ctx
   *  @param {Array<{x,y,w,h}>} rects - tile or roll rectangles
   *  @param {number} lineW - seam line width in feet */
  _drawTwoToneSeamRects(ctx, rects, lineW) {
    // ~0.4 screen pixels converted to world-space offset
    const px = 0.4 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
    ctx.setLineDash([]);
    ctx.lineWidth = lineW * 0.7;
    ctx.lineJoin = 'miter';
    // Faint seams in multi-view flooring tab
    const faint = this.rd._viewOnlySeamFaint;
    const _psa = this.rd._printSeamAlpha;
    const darkAlpha = _psa != null ? _psa : (faint ? 0.08 : 0.35);
    const lightAlpha = _psa != null ? _psa : (faint ? 0.12 : 0.7);
    // Dark side (shifted top-left)
    ctx.strokeStyle = 'rgba(0,0,0,' + darkAlpha + ')';
    ctx.beginPath();
    for (const r of rects) ctx.rect(r.x - px, r.y - px, r.w, r.h);
    ctx.stroke();
    // Light side (shifted bottom-right)
    ctx.strokeStyle = 'rgba(255,255,255,' + lightAlpha + ')';
    ctx.beginPath();
    for (const r of rects) ctx.rect(r.x + px, r.y + px, r.w, r.h);
    ctx.stroke();
  }

  _defaultOverlayColors() {
    return {
      light: {
        strip: 'rgba(0,107,204,0.15)',
        seam: '#000F1C',
        base: 'rgba(255,255,255,0.55)',
        overage: 'rgba(210,30,30,0.38)'
      },
      dark: {
        strip: 'rgba(255,255,255,0.18)',
        seam: '#c0c8d8',
        base: 'rgba(18,18,28,0.55)',
        overage: 'rgba(230,40,40,0.42)'
      }
    };
  }

  _generateOverlayColors(hex, isPaver) {
    const c = this._parseHex(hex || '#006BCC');
    // Pavers use solid fill (no texture), so strip color needs much higher opacity
    const stripAlpha = isPaver ? 0.85 : 0.13;
    return {
      light: {
        strip: isPaver
          ? `rgba(${Math.round(c.r*0.7)},${Math.round(c.g*0.7)},${Math.round(c.b*0.7)},${stripAlpha})`
          : `rgba(${c.r},${c.g},${c.b},${stripAlpha})`,
        seam: this._darkenHex(hex || '#006BCC', 0.6),
        base: 'rgba(255,255,255,0.55)',
        overage: 'rgba(210,30,30,0.38)'
      },
      dark: {
        strip: `rgba(${Math.min(c.r+60,255)},${Math.min(c.g+60,255)},${Math.min(c.b+60,255)},0.18)`,
        seam: '#c0c8d8',
        base: 'rgba(18,18,28,0.55)',
        overage: 'rgba(230,40,40,0.42)'
      }
    };
  }

  // ── Render Hooks ─────────────────────────

  _installHooks() {
    const self = this;
    this.rd._drawRoomOverlayHook = (room, geo) => self._drawFlooringOverlay(room, geo);
    this.rd._beforeRoomsHook = () => { self._pendingScissors = []; self._pendingTurfOverage = []; };
    this.rd._afterRoomsHook = () => { self._drawExteriorZoneOverlays(); self._drawFloorHighlights(); if (self._flashOverlayAlpha > 0) self._drawFlashOverlayPass(); };
    this.rd._afterWallSegmentsHook = () => { self._drawDeferredScissors(); self._drawCompassZoneHighlight(); self._drawFloorHoverHighlight(); self._drawFloorBoxSelectPreview(); self._drawFloorSelectionOutlines(); self._drawSnappedWallHighlight(); };
    this.rd._afterObjectsHook = () => { self._redrawDividersOverObjects(); self._drawTurfOverageOverlay(); self._drawTurfHoverHighlight(); self._drawPlatformHoverHighlight(); };
    this.rd._onZoomHook = () => self._onZoom();
    this.rd._onPanHook = () => self._onPan();
  }

  // ── Step System ──────────────────────────

  _setupStepBar() {
    const tabs = document.querySelectorAll('.rd-step-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const step = tab.dataset.step;
        if (tab.classList.contains('disabled')) return;
        // "Get a Quote" tab: trigger handoff to RFQ form
        if (step === 'quote') {
          this._prepareQuoteHandoff();
          return;
        }
        this._switchStep(step);
      });
    });
    this._syncFloorTab();
    this._syncEquipmentTab();
    this._syncFinishingTab();
    this._syncQuoteTab();
  }

  _switchStep(step) {
    if (step !== 'finishing') this._minimizePanelOnOpen = false; // CUSTOM_GYMLAYOUT_BY_CLAUDE: one-shot, shared-design open only
    if (step === this.currentStep) return;
    if (this._wipeInProgress) return;   // debounce rapid clicks

    // Cancel any background preload so it doesn't compete with active tab loading
    if (this._bgPreloadTimer) { clearTimeout(this._bgPreloadTimer); this._bgPreloadTimer = null; }
    if (this._bgPreloadRIC) { _cancelIdleCallback(this._bgPreloadRIC); this._bgPreloadRIC = null; }

    const stepNames = { draw: 'Draw Space', floor: 'Add Flooring', equipment: 'Add Equipment', finishing: 'Customize' };
    const overlay = document.getElementById('rdWipeOverlay');
    const label = document.getElementById('rdWipeLabel');
    const toolbar = document.querySelector('.tool-controls');

    // Immediately highlight the clicked tab (visual only, no state change)
    document.querySelectorAll('.rd-step-tab').forEach(tab => {
      tab.classList.remove('active', 'completed');
      if (tab.dataset.step === step) tab.classList.add('active');
    });

    // Set label text
    const steps = ['draw', 'floor', 'equipment', 'finishing'];
    const destIdx = steps.indexOf(step) + 1;
    if (label) label.textContent = 'Step ' + destIdx + ': ' + (stepNames[step] || step);

    // Show loading overlay immediately so the browser can paint it before heavy work
    // (z-index 40, sits under the wipe at z-index 50, visible after wipe exits)
    if (step !== 'draw') this._showLoadingOverlay();

    // Preload assets for the destination step while wipe animation plays
    this._preloadStepAssets(step);

    // Phase 1: Slide old buttons out immediately (user sees them leave)
    if (toolbar) {
      toolbar.classList.remove('toolbar-slide-in');
      toolbar.classList.add('toolbar-slide-out');
    }

    // Fire wipe animation
    if (overlay) {
      this._wipeInProgress = true;
      overlay.classList.remove('wipe-active');
      void overlay.offsetWidth;
      overlay.classList.add('wipe-active');

      // At ~350ms (cover fully in place), swap mode + buttons while hidden
      setTimeout(() => {
        if (toolbar) {
          toolbar.classList.remove('toolbar-slide-out');
          toolbar.classList.add('toolbar-hidden');
        }
        this._applyStepChange(step);
      }, 350);

      // At ~1150ms (cover exits at ~1000ms + 150ms pause), slide new buttons in
      setTimeout(() => {
        if (toolbar) {
          toolbar.classList.remove('toolbar-hidden');
          toolbar.classList.add('toolbar-slide-in');
          const onEnd = () => {
            toolbar.removeEventListener('animationend', onEnd);
            toolbar.classList.remove('toolbar-slide-in');
          };
          toolbar.addEventListener('animationend', onEnd);
        }
      }, 1150);

      // Clean up after animation completes (1.4s + small buffer)
      setTimeout(() => {
        overlay.classList.remove('wipe-active');
        this._wipeInProgress = false;
      }, 1500);
    } else {
      if (toolbar) toolbar.classList.remove('toolbar-slide-out', 'toolbar-hidden');
      this._applyStepChange(step);
    }
  }

  // Kick off network requests for textures/SVGs the destination tab will need.
  // Called before the wipe animation so fetches overlap the 350ms transition.
  _preloadStepAssets(step) {
    const rd = this.rd;
    if (step === 'floor' || step === 'equipment' || step === 'finishing') {
      // Preload flooring textures for all rooms
      for (const room of rd.rooms) {
        if (room.flooring) {
          const product = this._getProduct(room.flooring.productId);
          if (product) {
            const color = product.colors.find(c => c.id === room.flooring.colorId);
            if (color && color.textureUrl) this._getTextureImage(color.textureUrl);
          }
        }
        if (room.zoneFlooring) {
          for (const zf of room.zoneFlooring) {
            const product = this._getProduct(zf.productId);
            if (product) {
              const color = product.colors.find(c => c.id === zf.colorId);
              if (color && color.textureUrl) this._getTextureImage(color.textureUrl);
            }
          }
        }
      }
      // Preload exterior zone textures
      for (const ez of this._exteriorZoneFlooring) {
        const product = this._getProduct(ez.productId);
        if (product) {
          const color = product.colors.find(c => c.id === ez.colorId);
          if (color && color.textureUrl) this._getTextureImage(color.textureUrl);
        }
      }
    }
    if (step === 'finishing') {
      // Preload platform color textures so swatch clicks don't flash
      const epdm = this._getEpdmColors ? this._getEpdmColors() : [];
      for (const c of epdm) { if (c.textureUrl) this._getTextureImage(c.textureUrl); }
      const compatRooms = this._getPlatformCompatibleRooms ? this._getPlatformCompatibleRooms() : [];
      if (compatRooms.length > 0) {
        const product = compatRooms[0].product;
        if (product && product.colors) {
          for (const c of product.colors) { if (c.textureUrl) this._getTextureImage(c.textureUrl); }
        }
      }
    }
    if (step === 'equipment' || step === 'finishing') {
      // Preload SVGs for all placed equipment
      for (const obj of (rd.objects || [])) {
        if (obj.type === 'equipment' && obj.equipmentId && rd._ensureEquipmentLoaded) {
          rd._ensureEquipmentLoaded(obj.equipmentId);
        }
      }
    }
  }

  // Background-preload network assets (textures + SVGs) while user is on Draw/Floor tab.
  // Fires 3s after construction so initial render + autosave restore settle first.
  _backgroundPreload() {
    this._bgPreloadTimer = null;
    // Only run if user is still on draw or floor tab
    if (this.currentStep !== 'draw' && this.currentStep !== 'floor') return;

    // Reuse existing preload for textures + placed equipment SVGs
    this._preloadStepAssets('equipment');

    // Preload equipment panel thumbnail images in idle chunks
    this._preloadEquipmentThumbnails();
  }

  // Preload equipment thumbnail images (small Shopify CDN JPEGs) in idle-time batches.
  // Warms browser HTTP cache so the equipment panel renders instantly on tab switch.
  _preloadEquipmentThumbnails() {
    const rd = this.rd;
    if (!rd._equipmentImages || rd._equipmentLoading) {
      // JSON not loaded yet — retry in 2s
      this._bgPreloadTimer = setTimeout(() => this._preloadEquipmentThumbnails(), 2000);
      return;
    }

    const urls = [];
    for (const id in rd._equipmentImages) {
      const info = rd._equipmentImages[id];
      if (info.imageUrl) urls.push(info.imageUrl);
    }

    let idx = 0;
    const BATCH = 6;
    const loadChunk = () => {
      if (this.currentStep !== 'draw' && this.currentStep !== 'floor') return;
      const end = Math.min(idx + BATCH, urls.length);
      while (idx < end) { const img = new Image(); img.src = urls[idx++]; }
      if (idx < urls.length) {
        this._bgPreloadRIC = _requestIdleCallback(loadChunk, { timeout: 2000 });
      }
    };
    this._bgPreloadRIC = _requestIdleCallback(loadChunk, { timeout: 2000 });
  }

  // Count assets that haven't finished loading yet for the given step.
  _countPendingAssets(step) {
    let pending = 0;
    const rd = this.rd;
    if (step === 'floor') {
      // Only the floor tab blocks on texture loading — equipment/finishing
      // render textures progressively on canvas without blocking the UI.
      for (const room of rd.rooms) {
        if (room.flooring) {
          const product = this._getProduct(room.flooring.productId);
          if (product) {
            const color = product.colors.find(c => c.id === room.flooring.colorId);
            if (color && color.textureUrl) {
              const entry = this._textureImages[color.textureUrl];
              if (!entry || !entry.loaded) pending++;
            }
          }
        }
        if (room.zoneFlooring) {
          for (const zf of room.zoneFlooring) {
            const product = this._getProduct(zf.productId);
            if (product) {
              const color = product.colors.find(c => c.id === zf.colorId);
              if (color && color.textureUrl) {
                const entry = this._textureImages[color.textureUrl];
                if (!entry || !entry.loaded) pending++;
              }
            }
          }
        }
      }
      for (const ez of this._exteriorZoneFlooring) {
        const product = this._getProduct(ez.productId);
        if (product) {
          const color = product.colors.find(c => c.id === ez.colorId);
          if (color && color.textureUrl) {
            const entry = this._textureImages[color.textureUrl];
            if (!entry || !entry.loaded) pending++;
          }
        }
      }
    }
    if (step === 'equipment' || step === 'finishing') {
      // Equipment catalog still loading from JSON
      if (rd._equipmentLoading) pending++;
      // Note: placed equipment SVG rasterization is NOT counted as pending.
      // They render progressively on canvas — no need to block the UI waiting
      // for all pieces to finish. The panel only needs the JSON catalog.
    }
    return pending;
  }

  _showLoadingOverlay() {
    if (this._loadingOverlay) return;
    const container = document.getElementById('canvasContainer');
    if (!container) return;
    const el = document.createElement('div');
    el.className = 'rd-asset-loading';
    el.innerHTML = '<div class="rd-asset-ring"><div class="rd-asset-ring-track"></div><div class="rd-asset-ring-spin"></div></div><div class="rd-asset-label">Loading</div>';
    container.appendChild(el);
    this._loadingOverlay = el;
  }

  _hideLoadingOverlay() {
    const el = this._loadingOverlay;
    if (!el) return;
    clearInterval(this._loadingPollTimer);
    this._loadingPollTimer = null;
    // Fade out gracefully, then remove
    el.classList.add('rd-asset-hiding');
    el.addEventListener('animationend', () => el.remove(), { once: true });
    this._loadingOverlay = null;
  }

  _applyStepChange(step) {
    // Clear any stale poll timer (overlay itself stays — it was shown in _switchStep)
    clearInterval(this._loadingPollTimer);
    this._loadingPollTimer = null;
    // For Draw tab: force-remove overlay immediately
    if (step === 'draw') {
      if (this._loadingOverlay) { this._loadingOverlay.remove(); this._loadingOverlay = null; }
      document.querySelectorAll('.rd-asset-loading').forEach(el => el.remove());
    }
    this.currentStep = step;
    this._autoExpandedMenus = null; // clear auto-expand tracking on tab switch
    this._floorSelections = [];
    this._floorSelectCorner1 = null;
    this._floorSelectEnd = null;
    this._floorInfoPinned = false;
    this._hideFloorInfo();
    const rd = this.rd;

    // Restore product cards if leaving Customize tab
    if (this._stashedProductCards != null && step !== 'finishing') {
      // The drawer button is gone (its slot went to Equipment Labels), so there is
      // nothing to re-sync — the stash/restore itself still matters, because the
      // Customize tab suppresses the cards while you are on it.
      rd.showProductCards = this._stashedProductCards;
      this._stashedProductCards = null;
    }

    // Clear selections and hide delete button when switching tabs
    rd.selectedWalls = new Set();
    rd.selectedDoors = new Set();
    rd.selectedObjects = new Set();
    rd.selectionStart = null;
    rd.selectionEnd = null;
    rd.isSelecting = false;
    if (rd._updateFloatingDelete) rd._updateFloatingDelete();

    // Close all popovers when switching modes
    document.getElementById('penPopover').classList.remove('visible');
    document.getElementById('objectsPopover').classList.remove('visible');
    document.getElementById('zoomPopover').classList.remove('visible');
    document.getElementById('zoomToggleBtn').classList.remove('active');
    const frPop = document.getElementById('floorRotatePopover');
    if (frPop) frPop.classList.remove('visible');
    // brandPalette popover removed — colors now in Finishing Touches side panel
    if (rd._pinnedPopover === 'settings') {
      document.getElementById('settingsDrawer').classList.remove('open');
      document.getElementById('settingsToggle').classList.remove('open');
    }
    rd._pinnedPopover = null;
    if (rd._syncDrawerPins) rd._syncDrawerPins();
    // Clear cardinal axis chain references on any step change
    rd._lastPlacedEquipPos = null;
    rd._lastPastedPos = null;
    rd._pasteCardinalSnap = null;

    if (step === 'floor') {
      rd._floorMode = true;
      rd._equipmentMode = false;
      clearTimeout(rd._quoteNudgeTimer);
      // Clear active brush so no product card appears pre-selected on tab open
      this._activeBrush = null;
      // Hide wall dimensions on non-draw tabs
      if (this._stashedMeasurements == null) this._stashedMeasurements = rd.showMeasurements;
      if (rd.showMeasurements) {
        rd.showMeasurements = false;
        const mb = document.getElementById('toggleMeasurements');
        if (mb) mb.classList.remove('active');
      }
      // Invalidate floor layout caches — room geometry may have changed on another tab
      for (const room of rd.rooms) {
        room._flooringLayout = null;
        room._zoneLayouts = null;
      }
      // Restore seam/overage state that was stashed when entering equipment tab
      if (this._stashedSeamLines != null) {
        rd.showSeamLines = this._stashedSeamLines;
        localStorage.setItem('rd_showSeamLines', rd.showSeamLines ? '1' : '0');
        const seamBtn = document.getElementById('toggleSeamLines');
        if (seamBtn) seamBtn.classList.toggle('active', rd.showSeamLines);
        this._stashedSeamLines = null;
      }
      if (this._stashedHideOverage != null) {
        rd.hideOverage = this._stashedHideOverage;
        localStorage.setItem('rd_hideOverage', rd.hideOverage ? '1' : '0');
        const overageBtn = document.getElementById('toggleHideOverage');
        if (overageBtn) overageBtn.classList.toggle('active', !rd.hideOverage);
        this._stashedHideOverage = null;
      }
      if (this._stashedMaterialOverlay != null) {
        rd.showMaterialOverlay = this._stashedMaterialOverlay;
        localStorage.setItem('rd_showMaterialOverlay', rd.showMaterialOverlay ? '1' : '0');
        const matBtn = document.getElementById('toggleMaterialOverlay');
        if (matBtn) matBtn.classList.toggle('active', rd.showMaterialOverlay);
        this._stashedMaterialOverlay = null;
      }
      // Auto-enable floor seams unless user explicitly toggled them off on this tab
      if (!rd.showSeamLines && !rd._floorSeamUserOff) {
        rd.showSeamLines = true;
        localStorage.setItem('rd_showSeamLines', '1');
        const seamBtn = document.getElementById('toggleSeamLines');
        if (seamBtn) seamBtn.classList.toggle('active', true);
      }
      rd.selectedWalls = new Set();
      rd.selectedDoors = new Set();
      rd.selectedObjects = new Set();
      rd.selectedElement = null;
      if (rd.currentWall) { rd.currentWall = null; rd.hasPlacedFirstPoint = false; }
      rd._resetRectState && rd._resetRectState();
      rd._resetFreehandState && rd._resetFreehandState();
      this.showOverlay = true;
      document.querySelector('.room-designer').classList.add('floor-mode');
      document.querySelector('.room-designer').classList.remove('equipment-mode');
      // Enter with select tool so cursor/select is active
      rd.setActiveTool('select');
      rd.updateStatus(rd._isMobile ? 'Tap rooms to toggle flooring' : 'Pan to explore. Click cursor tool to assign flooring.');
      this._addFloorCanvasListeners();
      // Defer panel building to a separate task so the browser can breathe
      if (!rd._isMobile) setTimeout(() => this._openSidePanel(), 0);
    } else if (step === 'equipment') {
      rd._floorMode = false;
      rd._equipmentMode = true;
      this.showOverlay = true;
      document.querySelector('.room-designer').classList.remove('floor-mode');
      document.querySelector('.room-designer').classList.add('equipment-mode');
      // Hide wall dimensions on non-draw tabs
      if (this._stashedMeasurements == null) this._stashedMeasurements = rd.showMeasurements;
      if (rd.showMeasurements) {
        rd.showMeasurements = false;
        const mb = document.getElementById('toggleMeasurements');
        if (mb) mb.classList.remove('active');
      }
      // Auto-disable floor seams, overage, and material overlay for cleaner equipment view —
      // stash floor-tab state so we can restore it when switching back
      this._stashedSeamLines = rd.showSeamLines;
      this._stashedHideOverage = rd.hideOverage;
      this._stashedMaterialOverlay = rd.showMaterialOverlay;
      if (rd.showSeamLines) {
        rd.showSeamLines = false;
        document.getElementById('toggleSeamLines').classList.remove('active');
      }
      if (!rd.hideOverage) {
        rd.hideOverage = true;
        document.getElementById('toggleHideOverage').classList.remove('active');
      }
      // Material overlay: off by default on equipment tab, unless user
      // previously toggled it on while here (sticky preference)
      const equipOverlayPref = localStorage.getItem('rd_equipOverlayKeepOn') === '1';
      const wantOverlay = equipOverlayPref;
      if (rd.showMaterialOverlay !== wantOverlay) {
        rd.showMaterialOverlay = wantOverlay;
        const matBtn = document.getElementById('toggleMaterialOverlay');
        if (matBtn) matBtn.classList.toggle('active', rd.showMaterialOverlay);
      }
      // Select tool so user can click to place, then drag/rotate
      rd.setActiveTool('select');
      rd.canvas.style.cursor = 'crosshair';
      rd.updateStatus('Click an equipment item in the panel, then click the canvas to place it.');
      // Defer panel building to a separate task so the browser can breathe
      setTimeout(() => this._openEquipmentPanel(), 0);
      // Start nudge timer if equipment already placed
      const hasEquip = (rd.objects || []).some(o => o.type === 'equipment' && o.equipmentId);
      if (hasEquip && typeof rd._resetQuoteNudgeTimer === 'function') rd._resetQuoteNudgeTimer();
    } else if (step === 'finishing') {
      // Reset collapsed state so auto-expand logic re-evaluates each time
      this._finishingCollapsed = null;
      rd._floorMode = false;
      rd._equipmentMode = true;  // Keep equipment visible and interactive
      this.showOverlay = true;
      document.querySelector('.room-designer').classList.remove('floor-mode');
      document.querySelector('.room-designer').classList.add('equipment-mode');
      // Hide wall dimensions on non-draw tabs
      if (this._stashedMeasurements == null) this._stashedMeasurements = rd.showMeasurements;
      if (rd.showMeasurements) {
        rd.showMeasurements = false;
        const mb = document.getElementById('toggleMeasurements');
        if (mb) mb.classList.remove('active');
      }
      // Stash overlay settings same as equipment
      if (this._stashedSeamLines == null) this._stashedSeamLines = rd.showSeamLines;
      if (this._stashedHideOverage == null) this._stashedHideOverage = rd.hideOverage;
      if (this._stashedMaterialOverlay == null) this._stashedMaterialOverlay = rd.showMaterialOverlay;
      if (rd.showSeamLines) { rd.showSeamLines = false; const b = document.getElementById('toggleSeamLines'); if (b) b.classList.remove('active'); }
      if (!rd.hideOverage) { rd.hideOverage = true; const b = document.getElementById('toggleHideOverage'); if (b) b.classList.remove('active'); }
      // Force material overlay on so turf/platform colors are visible while customizing
      if (!rd.showMaterialOverlay) { rd.showMaterialOverlay = true; const b = document.getElementById('toggleMaterialOverlay'); if (b) b.classList.add('active'); }
      // Clear any pending equipment placement from the equipment tab
      rd._pendingEquipmentId = null;
      rd._pendingEquipmentPos = null;
      /* Suppress product info on the Customize tab (stash to restore on leave).
         ⚠️ Since the selection card was removed (2026-07-31) this reaches only the
         FLOORING tooltip — the equipment/turf/platform card it was originally written
         for no longer exists. Kept because that tooltip is still gated by the flag. */
      if (this._stashedProductCards == null) this._stashedProductCards = rd.showProductCards;
      if (rd.showProductCards) rd.showProductCards = false;
      rd.setActiveTool('select');
      rd.canvas.style.cursor = 'default';
      rd.updateStatus('Select turf or equipment to customize.');
      // Defer panel building to a separate task so the browser can breathe
      setTimeout(() => {
        this._openFinishingPanel();
        // CUSTOM_GYMLAYOUT_BY_CLAUDE (Ryan 2026-10-10) — a shared design opens with the
        // panel contracted (storage _applySharedOpenView sets this one-shot flag).
        if (this._minimizePanelOnOpen) {
          this._minimizePanelOnOpen = false;
          this._minimizeSidePanel();
          // Re-fit once the panel has finished closing (its width decides the visible area).
          const inset = (window.RoomDesignerStorage && window.RoomDesignerStorage.LOAD_FIT_INSET) || 0.9;
          setTimeout(() => { if (!this.rd._viewOnly) this.rd.fitToView({ inset }); }, 400);
        }
      }, 0);
    } else {
      rd._floorMode = false;
      rd._equipmentMode = false;
      clearTimeout(rd._quoteNudgeTimer);
      this.showOverlay = false;
      this._closeSidePanel();
      document.querySelector('.room-designer').classList.remove('floor-mode');
      document.querySelector('.room-designer').classList.remove('equipment-mode');
      // Restore wall dimensions when returning to Draw Space
      if (this._stashedMeasurements != null) {
        rd.showMeasurements = this._stashedMeasurements;
        const mb = document.getElementById('toggleMeasurements');
        if (mb) mb.classList.toggle('active', rd.showMeasurements);
        this._stashedMeasurements = null;
      }
      // Force off flooring visuals on Draw tab — no seam lines, material overlay, or overage
      if (rd.showSeamLines) {
        if (this._stashedSeamLines == null) this._stashedSeamLines = rd.showSeamLines;
        rd.showSeamLines = false;
        const sb = document.getElementById('toggleSeamLines'); if (sb) sb.classList.remove('active');
      }
      if (rd.showMaterialOverlay) {
        if (this._stashedMaterialOverlay == null) this._stashedMaterialOverlay = rd.showMaterialOverlay;
        rd.showMaterialOverlay = false;
        const mb2 = document.getElementById('toggleMaterialOverlay'); if (mb2) mb2.classList.remove('active');
      }
      // Enter with select tool so cursor/select is active
      if (rd.activeTool !== 'select') rd.setActiveTool('select');
    }

    // On tab switch to overlay-visible tabs, start with cheap tint then fade in detail
    if (step === 'floor' || step === 'equipment' || step === 'finishing') {
      this._overlayDetailAlpha = 0;
      this._overlayDetailTarget = 1;
      this._overlayDetailLastFrame = 0;
      clearTimeout(this._overlayDetailFadeTimer);
      this._overlayDetailFadeTimer = null;
      this._scheduleOverlayDetailFade();
    }

    this._updateStepUI();
    rd._swapToolbarButtons(step);
    this._syncCompassBtn();

    if (step === 'floor') {
      // Floor tab: wait for textures before revealing (they're visible immediately)
      rd.render();
      const _checkAndHide = () => {
        if (this._countPendingAssets(this.currentStep) <= 0) {
          this._hideLoadingOverlay();
        } else {
          this._loadingPollTimer = setInterval(() => {
            if (this._countPendingAssets(this.currentStep) <= 0) {
              this._hideLoadingOverlay();
              rd.render();
            }
          }, 300);
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(_checkAndHide));
    } else if (step !== 'draw') {
      // Equipment/Finishing tabs: hide spinner immediately, render progressively.
      // Silhouette generation blocks the main thread — waiting behind a double-RAF
      // would keep the spinner visible for the entire duration of that work.
      this._hideLoadingOverlay();
      rd.render();
    } else {
      rd.render();
    }
  }

  _updateStepUI() {
    const tabs = document.querySelectorAll('.rd-step-tab');
    const steps = ['draw', 'floor', 'equipment', 'finishing'];
    const activeIdx = steps.indexOf(this.currentStep);

    tabs.forEach((tab, i) => {
      const step = tab.dataset.step;
      tab.classList.remove('active', 'completed');
      if (step === this.currentStep) {
        tab.classList.add('active');
      } else if (i < activeIdx) {
        tab.classList.add('completed');
        const num = tab.querySelector('.rd-step-num');
        if (num) num.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';
      } else {
        const num = tab.querySelector('.rd-step-num');
        if (num && !num.textContent.match(/^\d$/)) num.textContent = String(i + 1);
      }
    });
  }

  // ── Floor Canvas Event Listeners ─────────

  _addFloorCanvasListeners() {
    if (this._floorCanvasListenersAdded) return;
    this._floorCanvasListenersAdded = true;

    const canvas = this.rd.canvas;

    // Mouse click — assign/remove flooring (roller) or select floored room (select)
    canvas.addEventListener('mousedown', (e) => {
      if (!this.rd._floorMode || e.button !== 0) return;
      // Platform placement — let main module handle it
      if (this.rd.activeTool === 'platform-place') return;
      // Roller tool works even when compass is open
      if (this.rd.activeTool === 'roller') { this._onCanvasClick(e); return; }
      // Eraser in floor mode — try measurements first, then floor erase
      if (this.rd.activeTool === 'floor-eraser' || this.rd.activeTool === 'eraser') {
        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
        const w = this.rd.screenToWorld(sx, sy);
        const hitM = this.rd._hitTestMeasurement(w);
        if (hitM) { this.rd.handleErase(w); return; }
        this._onFloorEraserClick(e); return;
      }
      // Compass open: click selects zone for compass
      if (this._isCompassOpen()) {
        this._onCompassZoneClick(e);
        return;
      }
      if (this.rd.activeTool === 'select') {
        // Skip if tool just switched to select (e.g. turf-draw completed on this same click)
        if (Date.now() < this.rd._boxSelectCooldownUntil) return;
        const _rect = canvas.getBoundingClientRect();
        const _sx = e.clientX - _rect.left, _sy = e.clientY - _rect.top;
        const _sw = this.rd.screenToWorld(_sx, _sy);
        const _hit = this.rd._hitTestSelect(_sw);
        // If box-select is active and 2nd click lands on turf/platform, cancel the box-select
        if (this._floorSelectCorner1 && _hit && _hit.item && (_hit.item.type === 'turf' || _hit.item.type === 'platform')) {
          this._floorSelectCorner1 = null;
          this._floorSelectEnd = null;
          this.rd.render();
          return;
        }
        // If clicking on a turf/platform object or its handles, clear floor selections then let the main handler deal with it
        if (_hit && _hit.item && (_hit.item.type === 'turf' || _hit.item.type === 'platform')) {
          if (this._floorSelections.length > 0) {
            this._floorSelections = [];
            if (this._floorInfoPinned) { this._floorInfoPinned = false; this._hideFloorInfo(); }
            this.rd.render();
          }
          return;
        }
        const _hh = this.rd._hitTestHandles(_sx, _sy);
        if (_hh) return;
        // Check if clicking on a measurement — let main handler select/drag it
        if (this.rd._measurements && this.rd._measurements.length > 0) {
          if (this.rd._hitTestMeasurement(_sw)) return;
          if (this.rd._selectedMeasurements && this.rd._selectedMeasurements.size > 0) {
            const _mh = this.rd._hitTestMeasurementLabel(_sw);
            if (_mh && this.rd._selectedMeasurements.has(_mh.id)) return;
          }
        }
        this._onFloorSelectClick(e); return;
      }
    });

    // Mouse move — hover highlight + cursor swatch (roller), or hover/box-select (select)
    canvas.addEventListener('mousemove', (e) => {
      if (!this.rd._floorMode) return;
      // Roller tool hover works even when compass is open
      if (this.rd.activeTool === 'roller' || this.rd.activeTool === 'floor-eraser' || this.rd.activeTool === 'eraser') {
        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
        const world = this.rd.screenToWorld(sx, sy);
        this._updateHoveredRoom(world.x, world.y);
        return;
      }
      // Compass open: show hover highlight like selector tool
      if (this._isCompassOpen()) {
        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
        const world = this.rd.screenToWorld(sx, sy);
        this._updateFloorSelectHover(world.x, world.y);
        return;
      }
      if (this.rd.activeTool === 'select') {
        const rect = canvas.getBoundingClientRect();
        const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
        const world = this.rd.screenToWorld(sx, sy);
        // During box-select, update end point + render
        if (this._floorSelectCorner1) {
          this._floorSelectEnd = { x: world.x, y: world.y };
          this.rd.render();
        } else {
          this._updateFloorSelectHover(world.x, world.y);
        }
        return;
      }
      if (this.rd.activeTool !== 'roller') return;
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const world = this.rd.screenToWorld(sx, sy);
      this._updateHoveredRoom(world.x, world.y);
    });

    // Touch — tap to assign (roller) or select (select)
    canvas.addEventListener('touchend', (e) => {
      if (!this.rd._floorMode) return;
      if (e.changedTouches.length === 0) return;
      const touch = e.changedTouches[0];
      const rect = canvas.getBoundingClientRect();
      const sx = touch.clientX - rect.left, sy = touch.clientY - rect.top;
      const world = this.rd.screenToWorld(sx, sy);
      if (this.rd.activeTool === 'select') { this._selectFloorAtPoint(world.x, world.y, false); return; }
      // Eraser — try measurements first, then floor erase
      if (this.rd.activeTool === 'floor-eraser' || this.rd.activeTool === 'eraser') {
        const hitM = this.rd._hitTestMeasurement(world);
        if (hitM) { this.rd.handleErase(world); return; }
        this._eraseFloorAtPoint(world.x, world.y); return;
      }
      // Roller tool works even when compass is open
      if (this.rd.activeTool === 'roller') { this._handleFloorTap(world.x, world.y); return; }
      // Compass open: tap selects zone for compass manipulation
      if (this._isCompassOpen()) {
        this._selectCompassZoneAtPoint(world.x, world.y);
        return;
      }
      this._handleFloorTap(world.x, world.y);
    });

    canvas.addEventListener('mouseleave', () => {
    });
  }

  // ── Room Click-to-Assign ─────────────────

  _onCanvasClick(e) {
    const rect = this.rd.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const world = this.rd.screenToWorld(sx, sy);
    this._toggleRoomFlooring(world.x, world.y);
  }

  _handleFloorTap(wx, wy) {
    this._toggleRoomFlooring(wx, wy);
  }

  _onFloorEraserClick(e) {
    const rect = this.rd.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const world = this.rd.screenToWorld(sx, sy);
    this._eraseFloorAtPoint(world.x, world.y);
  }

  _eraseFloorAtPoint(wx, wy) {
    const rd = this.rd;
    // Check rooms
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add') continue;
      if (!this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) { if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; } }
      if (inHole) continue;

      rd.saveUndoState();
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        let clickedZone = null;
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) { clickedZone = z; break; }
        }
        if (!clickedZone) { rd.undoStack.pop(); return; }
        const existing = this._getZoneFlooring(room, clickedZone);
        if (!existing) { rd.undoStack.pop(); return; }
        this._setZoneFlooring(room, clickedZone, null, null);
        this._syncRoomFlooringFromZones(room, zones);
      } else {
        if (!room.flooring) { rd.undoStack.pop(); return; }
        room.flooring = null;
      }
      room._flooringLayout = null;
      room._zoneLayouts = null;
      // Remove platforms sitting on this room — can't exist without flooring
      this._removePlatformsInRoom(room);
      rd._triggerAutoSave();
      rd.render();
      rd.updateMeasurements();
      this._updateMaterialSummary();
      this._updateMiniPreview();
      this._syncCompassBtn();
      this._syncQuoteTab();
      return;
    }
    // Check exterior zones
    const extZones = this._getExteriorZones();
    for (const zone of extZones) {
      if (this._zoneHitTest(zone, wx, wy)) {
        const existing = this._getExteriorZoneFlooring(zone);
        if (!existing) return;
        rd.saveUndoState();
        this._setExteriorZoneFlooring(zone, null, null);
        rd._triggerAutoSave();
        rd.render();
        rd.updateMeasurements();
        this._updateMaterialSummary();
        this._updateMiniPreview();
        this._syncCompassBtn();
        this._syncQuoteTab();
        return;
      }
    }
  }

  // ── Compass Zone Click-to-Select ───────────

  _onCompassZoneClick(e) {
    const rect = this.rd.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const world = this.rd.screenToWorld(sx, sy);
    this._selectCompassZoneAtPoint(world.x, world.y);
  }

  _selectCompassZoneAtPoint(wx, wy) {
    const rd = this.rd;
    // Find which room/zone was clicked
    let found = null, foundZone = null;
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add') continue;
      if (!this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) {
        if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; }
      }
      if (inHole) continue;
      found = room;
      // Check zones
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) { foundZone = z; break; }
        }
      }
      break;
    }
    if (!found) return;

    // Match to floored targets list and update compass selection
    const targets = this._getFlooredTargets();
    for (let ti = 0; ti < targets.length; ti++) {
      const t = targets[ti];
      if (t.room === found && this._zonesMatch(t.zone, foundZone)) {
        this._selectedRotateRoomIdx = ti;
        this._updateRotatePopoverForSelectedRoom();
        // Update hover highlight to show selected zone
        this._hoveredRoom = found;
        this._hoveredZone = foundZone;
        this._hoverStartTime = performance.now();
        if (this._hoverFadeRAF) { cancelAnimationFrame(this._hoverFadeRAF); this._hoverFadeRAF = null; }
        this._runHoverFade();
        break;
      }
    }
  }

  _toggleRoomFlooring(wx, wy) {
    const rd = this.rd;
    // Iterate rooms in reverse (topmost first)
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add') continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      // Check not inside a cutout/hole
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) {
        if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; }
      }
      if (inHole) continue;

      // Block floor paint if click lands on a turf piece (turf owns that area)
      let _onTurf = false;
      for (const _to of rd.objects) {
        if (_to.type !== 'turf') continue;
        const _tr = _to.rotation || 0;
        const _cos = Math.cos(-_tr), _sin = Math.sin(-_tr);
        const _dx = wx - _to.x, _dy = wy - _to.y;
        const _lx = _dx * _cos - _dy * _sin, _ly = _dx * _sin + _dy * _cos;
        if (Math.abs(_lx) <= _to.width / 2 && Math.abs(_ly) <= _to.depth / 2) { _onTurf = true; break; }
      }
      if (_onTurf) return;

      rd.saveUndoState();
      const brush = this._activeBrush;
      const zones = this._getZonesForRoom(room, geo);

      // Multi-zone: assign per-zone (only the clicked zone)
      if (zones.length > 1) {
        // Find which zone was clicked
        let clickedZone = null;
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) { clickedZone = z; break; }
        }
        if (!clickedZone) { rd.undoStack.pop(); return; }

        const existing = this._getZoneFlooring(room, clickedZone);
        if (!brush) {
          if (existing) this._setZoneFlooring(room, clickedZone, null, null);
          else if (this._activeBrush) this._setZoneFlooring(room, clickedZone, this._activeBrush.productId, this._activeBrush.colorId);
        } else if (existing && existing.productId === brush.productId && existing.colorId === brush.colorId) {
          // Same brush: remove
          this._setZoneFlooring(room, clickedZone, null, null);
        } else if (existing) {
          // Different brush on existing floor: replace in one click
          this._setZoneFlooring(room, clickedZone, brush.productId, brush.colorId);
        } else {
          // Empty zone: apply brush
          this._setZoneFlooring(room, clickedZone, brush.productId, brush.colorId);
        }
        // Sync room.flooring: set if any zone has flooring, clear if none do
        this._syncRoomFlooringFromZones(room, zones);
        room._flooringLayout = null;
        room._zoneLayouts = null;
      } else {
        // Single zone: original per-room behavior
        if (!brush) {
          if (room.flooring) room.flooring = null;
          else if (this._activeBrush) room.flooring = { productId: this._activeBrush.productId, colorId: this._activeBrush.colorId };
        } else if (room.flooring
            && room.flooring.productId === brush.productId
            && room.flooring.colorId === brush.colorId) {
          // Same brush: remove
          room.flooring = null;
        } else if (room.flooring) {
          // Different brush on existing floor: replace in one click
          room.flooring = { productId: brush.productId, colorId: brush.colorId };
          room._flooringLayout = null;
        } else {
          // Empty room: apply brush
          room.flooring = { productId: brush.productId, colorId: brush.colorId };
          room._flooringLayout = null;
        }
      }
      rd._triggerAutoSave();
      rd.render();
      rd.updateMeasurements();
      this._updateMaterialSummary();
      this._updateMiniPreview();
      this._syncCompassBtn();
      this._syncQuoteTab();
      // Refresh platform section (may have gained/lost compatible flooring)
      this._refreshPlatformSection();
      // Roll sharing (rep): refresh the panel rows, and ask once when this brush's
      // material is now in 2+ spaces. Deferred so the paint renders before the dialog.
      this._refreshRollShareRows();
      if (brush) setTimeout(() => this._maybePromptRollShare(brush.productId, brush.colorId), 0);
      // Refresh uniform target dropdown (flooring assignment changes available options)
      // Deferred so layout computation from render() is fully settled
      if (this._rollOptimizationMode === 'uniform') setTimeout(() => this._repopulateUniformTargetSelects(), 0);

      // Update compass target to clicked zone/room if compass is open
      if (this._rotatePop && this._rotatePop.classList.contains('visible')) {
        let cZone = null;
        if (zones.length > 1) {
          for (const z of zones) {
            if (this._zoneHitTest(z, wx, wy)) { cZone = z; break; }
          }
        }
        const targets = this._getFlooredTargets();
        for (let ti = 0; ti < targets.length; ti++) {
          if (targets[ti].room === room && this._zonesMatch(targets[ti].zone, cZone)) {
            this._selectedRotateRoomIdx = ti;
            this._updateRotatePopoverForSelectedRoom();
            break;
          }
        }
      }
      return;
    }
    // Click landed outside all rooms — check exterior zones
    const extZones = this._getExteriorZones();
    for (const zone of extZones) {
      if (this._zoneHitTest(zone, wx, wy)) {
        rd.saveUndoState();
        const brush = this._activeBrush;
        const existing = this._getExteriorZoneFlooring(zone);
        if (!brush) {
          if (existing) this._setExteriorZoneFlooring(zone, null, null);
        } else if (existing && existing.productId === brush.productId && existing.colorId === brush.colorId) {
          // Same brush: remove
          this._setExteriorZoneFlooring(zone, null, null);
        } else if (existing) {
          // Different brush on existing floor: replace in one click
          this._setExteriorZoneFlooring(zone, brush.productId, brush.colorId);
        } else {
          this._setExteriorZoneFlooring(zone, brush.productId, brush.colorId);
        }
        rd._triggerAutoSave();
        rd.render();
        rd.updateMeasurements();
        this._updateMaterialSummary();
        this._updateMiniPreview();
        this._syncCompassBtn();
        this._syncQuoteTab();

        // Update compass target if open
        if (this._rotatePop && this._rotatePop.classList.contains('visible')) {
          const targets = this._getFlooredTargets();
          for (let ti = 0; ti < targets.length; ti++) {
            if (targets[ti].isExterior && this._zonesMatch(targets[ti].zone, zone)) {
              this._selectedRotateRoomIdx = ti;
              this._updateRotatePopoverForSelectedRoom();
              break;
            }
          }
        }
        return;
      }
    }
  }

  /** Keep room.flooring in sync: set to first zone's product if any zones have flooring. */
  _syncRoomFlooringFromZones(room, zones) {
    if (!room.zoneFlooring || room.zoneFlooring.length === 0) {
      room.flooring = null;
      return;
    }
    // Use largest-area zone's flooring as the room.flooring representative
    let best = null, bestArea = 0;
    for (const zf of room.zoneFlooring) {
      for (const z of zones) {
        const dist = Math.sqrt((zf.cx - z.centroid.x) ** 2 + (zf.cy - z.centroid.y) ** 2);
        if (dist < 2 && z.area > bestArea) { bestArea = z.area; best = zf; }
      }
    }
    if (best) {
      room.flooring = { productId: best.productId, colorId: best.colorId };
    } else {
      room.flooring = { productId: room.zoneFlooring[0].productId, colorId: room.zoneFlooring[0].colorId };
    }
  }

  _updateHoveredRoom(wx, wy) {
    const rd = this.rd;
    let found = null;
    let foundZone = null;
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add') continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) {
        if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; }
      }
      if (inHole) continue;
      found = room;
      // Find specific zone if multi-zone
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) { foundZone = z; break; }
        }
      }
      break;
    }
    // Check exterior zones if not hovering any room
    let foundExtZone = null;
    if (!found) {
      const extZones = this._getExteriorZones();
      for (const z of extZones) {
        if (this._zoneHitTest(z, wx, wy)) { foundExtZone = z; break; }
      }
    }
    // Check if hovering a turf object (independent highlight)
    // Pad by half wall thickness so turf covers the shared wall zone with adjacent rooms
    const _turfPad = (rd.wallThickness || 0.375) / 2;
    let foundTurf = null;
    if (rd.objects && !rd._selectDragging && !rd._pastePreview) {
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 + _turfPad && Math.abs(ly) <= obj.depth / 2 + _turfPad) { foundTurf = obj; break; }
      }
    }
    // Check if hovering a platform object (same pattern as turf)
    let foundPlatform = null;
    if (rd.objects && !rd._pastePreview && !rd._selectDragging) {
      for (const obj of rd.objects) {
        if (obj.type !== 'platform') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) { foundPlatform = obj; break; }
      }
    }
    // Turf/platform wins over room underneath — clear room hover so the wrong card doesn't show
    if (foundTurf || foundPlatform) { found = null; foundZone = null; foundExtZone = null; }
    const changed = found !== this._hoveredRoom || !this._zonesMatch(foundZone, this._hoveredZone)
      || !this._zonesMatch(foundExtZone, this._hoveredExteriorZone)
      || foundTurf !== this._hoveredTurf
      || foundPlatform !== this._hoveredPlatform;
    if (changed) {
      this._hoveredRoom = found;
      this._hoveredZone = foundZone;
      this._hoveredExteriorZone = foundExtZone;
      this._hoveredTurf = foundTurf;
      this._hoveredPlatform = foundPlatform;
      const hasHover = found || foundExtZone || foundTurf || foundPlatform;
      this._hoverStartTime = hasHover ? performance.now() : 0;
      if (rd.activeTool === 'floor-eraser' || (rd.activeTool === 'eraser' && rd._floorMode)) {
        rd.canvas.style.cursor = this._floorEraserCursor;
      } else if (rd.activeTool === 'roller' && this._activeBrush) {
        rd.canvas.style.cursor = this._paintRollerCursor;
      } else if (rd.activeTool === 'roller') {
        rd.canvas.style.cursor = hasHover ? 'pointer' : 'default';
      } else {
        rd.canvas.style.cursor = hasHover ? 'pointer' : 'default';
      }
      if (this._hoverFadeRAF) { cancelAnimationFrame(this._hoverFadeRAF); this._hoverFadeRAF = null; }
      if (hasHover) this._runHoverFade();
      else rd.render();

      // Auto-switch compass target when hovering a floored zone
      const hoverTarget = found || foundExtZone;
      if (hoverTarget && this._rotatePop && this._rotatePop.classList.contains('visible')) {
        const targets = this._getFlooredTargets();
        for (let ti = 0; ti < targets.length; ti++) {
          const t = targets[ti];
          if (t.isExterior && this._zonesMatch(t.zone, foundExtZone)) {
            if (this._selectedRotateRoomIdx !== ti) {
              this._selectedRotateRoomIdx = ti;
              this._updateRotatePopoverForSelectedRoom();
            }
            break;
          }
          if (!t.isExterior && t.room === found && this._zonesMatch(t.zone, foundZone)) {
            if (this._selectedRotateRoomIdx !== ti) {
              this._selectedRotateRoomIdx = ti;
              this._updateRotatePopoverForSelectedRoom();
            }
            break;
          }
        }
      }
    }
  }

  // ── Floor-Select Tool (select + delete flooring) ──

  _updateFloorSelectHover(wx, wy) {
    const rd = this.rd;
    let found = null, foundZone = null;
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) {
        if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; }
      }
      if (inHole) continue;
      found = room;
      // Check zones — highlight specific floored zone
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) {
            const zf = this._getZoneFlooring(room, z);
            if (zf) { foundZone = z; }
            else { found = null; } // zone exists but has no floor — no hover
            break;
          }
        }
      }
      break;
    }
    // Check exterior zones if not hovering any room
    let foundExtZone = null;
    if (!found) {
      const extZones = this._getExteriorZones();
      for (const z of extZones) {
        if (this._zoneHitTest(z, wx, wy)) {
          const ef = this._getExteriorZoneFlooring(z);
          if (ef) foundExtZone = z;
          break;
        }
      }
    }
    // Check if hovering a turf object (independent highlight)
    // Pad by half wall thickness so turf covers the shared wall zone with adjacent rooms
    const _turfPad = (rd.wallThickness || 0.375) / 2;
    let foundTurf = null;
    if (rd.objects && !rd._selectDragging && !rd._pastePreview) {
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 + _turfPad && Math.abs(ly) <= obj.depth / 2 + _turfPad) { foundTurf = obj; break; }
      }
    }
    // Check if hovering a platform object (same pattern as turf)
    let foundPlatform = null;
    if (rd.objects && !rd._pastePreview && !rd._selectDragging) {
      for (const obj of rd.objects) {
        if (obj.type !== 'platform') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) { foundPlatform = obj; break; }
      }
    }
    // Turf/platform wins over room underneath — clear room hover so the wrong card doesn't show
    if (foundTurf || foundPlatform) { found = null; foundZone = null; foundExtZone = null; }
    const changed = found !== this._hoveredRoom || !this._zonesMatch(foundZone, this._hoveredZone)
      || !this._zonesMatch(foundExtZone, this._hoveredExteriorZone)
      || foundTurf !== this._hoveredTurf
      || foundPlatform !== this._hoveredPlatform;
    if (changed) {
      this._hoveredRoom = found;
      this._hoveredZone = foundZone;
      this._hoveredExteriorZone = foundExtZone;
      this._hoveredTurf = foundTurf;
      this._hoveredPlatform = foundPlatform;
      const hasHover = found || foundExtZone || foundTurf || foundPlatform;
      this._hoverStartTime = hasHover ? performance.now() : 0;
      rd.canvas.style.cursor = hasHover ? 'pointer' : 'default';
      if (this._hoverFadeRAF) { cancelAnimationFrame(this._hoverFadeRAF); this._hoverFadeRAF = null; }
      if (hasHover) this._runHoverFade();
      else rd.render();
      // Hide pinned tooltip when mouse leaves all floored zones
      if (!hasHover && !this._floorInfoPinned) {
        this._hideFloorInfo();
      }
    }
  }

  _hitTestFlooredRoom(wx, wy) {
    const rd = this.rd;
    // If point lands on or near a turf object, don't return the room underneath
    // Pad by half wall thickness so turf covers the shared wall zone with adjacent rooms
    const _turfPad = (rd.wallThickness || 0.375) / 2;
    if (rd.objects) {
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 + _turfPad && Math.abs(ly) <= obj.depth / 2 + _turfPad) return null;
      }
      // Also block room hit when over a platform
      for (const obj of rd.objects) {
        if (obj.type !== 'platform') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) return null;
      }
    }
    for (let i = rd.rooms.length - 1; i >= 0; i--) {
      const room = rd.rooms[i];
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (!rd.pointInPolygon(wx, wy, geo.roomPolygon)) continue;
      let inHole = false;
      const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const hp of allHoles) {
        if (rd.pointInPolygon(wx, wy, hp)) { inHole = true; break; }
      }
      if (inHole) continue;
      // Check zones — return specific zone if multi-zone
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (const z of zones) {
          if (this._zoneHitTest(z, wx, wy)) {
            const zf = this._getZoneFlooring(room, z);
            if (zf) return { room, zone: z };
          }
        }
        // Clicked inside room but not in a floored zone
        return null;
      }
      return { room, zone: null };
    }
    // Check exterior zones
    const extZones = this._getExteriorZones();
    for (const z of extZones) {
      if (this._zoneHitTest(z, wx, wy)) {
        const ef = this._getExteriorZoneFlooring(z);
        if (ef) return { room: null, zone: z, isExterior: true };
        return null;
      }
    }
    return null;
  }

  _onFloorSelectClick(e) {
    const rect = this.rd.canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const world = this.rd.screenToWorld(sx, sy);
    this._selectFloorAtPoint(world.x, world.y, e.shiftKey);
  }

  _selectFloorAtPoint(wx, wy, shiftKey) {
    const rd = this.rd;

    // Second click finalizes box-select — but if it lands on turf, cancel instead
    if (this._floorSelectCorner1) {
      let hitTurf = false;
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        const rot = obj.rotation || 0;
        const cos = Math.cos(-rot), sin = Math.sin(-rot);
        const dx = wx - obj.x, dy = wy - obj.y;
        const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
        if (Math.abs(lx) <= obj.width / 2 && Math.abs(ly) <= obj.depth / 2) { hitTurf = true; break; }
      }
      if (hitTurf) {
        this._floorSelectCorner1 = null;
        this._floorSelectEnd = null;
        rd.render();
        return;
      }
      this._finalizeFloorBoxSelection({ x: wx, y: wy }, shiftKey);
      return;
    }

    const hit = this._hitTestFlooredRoom(wx, wy);
    if (hit) {
      // Clear any selected turf/objects so only the floor zone is highlighted
      if (rd.selectedObjects && rd.selectedObjects.size > 0) {
        rd.selectedObjects = new Set();
      }
      const matchIdx = this._floorSelections.findIndex(s =>
        s.room === hit.room && this._zonesMatch(s.zone, hit.zone));
      if (shiftKey) {
        // Shift-click: toggle in/out of selection
        if (matchIdx >= 0) this._floorSelections.splice(matchIdx, 1);
        else this._floorSelections.push(hit);
      } else {
        // Normal click: if already sole selection → deselect; otherwise select only this
        if (this._floorSelections.length === 1 && matchIdx === 0) {
          this._floorSelections = [];
        } else {
          this._floorSelections = [hit];
        }
      }
    } else {
      // Clicked empty space — start two-click box select
      // Brief cooldown prevents accidental re-entry after tool switch or box-select completion
      if (Date.now() < rd._boxSelectCooldownUntil) {
        if (!shiftKey) { this._floorSelections = []; rd.selectedObjects = new Set(); }
        rd._updateFloatingDelete(); rd.render();
        return;
      }
      if (!shiftKey) {
        this._floorSelections = [];
        rd.selectedObjects = new Set();
      }
      this._floorSelectCorner1 = { x: wx, y: wy };
      this._floorSelectEnd = { x: wx, y: wy };
      rd.updateStatus(rd._isMobile ? 'Tap to complete selection.' : 'Move mouse and click to complete selection.');
    }
    rd._updateFloatingDelete();
    rd.render();
  }

  _finalizeFloorBoxSelection(corner2, shiftKey) {
    const c1 = this._floorSelectCorner1;
    const minX = Math.min(c1.x, corner2.x), maxX = Math.max(c1.x, corner2.x);
    const minY = Math.min(c1.y, corner2.y), maxY = Math.max(c1.y, corner2.y);
    const rd = this.rd;

    if (!shiftKey) this._floorSelections = [];

    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3 || !geo.area) continue;
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        // Multi-zone: select individual floored zones that overlap box
        for (const z of zones) {
          const zf = this._getZoneFlooring(room, z);
          if (!zf) continue;
          const clipped = this._clipPolygonToRect(z.polygon, minX, minY, maxX, maxY);
          const intersectArea = this._shoelaceArea(clipped);
          if (intersectArea / z.area >= 0.5) {
            const already = this._floorSelections.some(s => s.room === room && this._zonesMatch(s.zone, z));
            if (!already) this._floorSelections.push({ room, zone: z });
          }
        }
      } else {
        // Single zone: select whole room
        const clipped = this._clipPolygonToRect(geo.roomPolygon, minX, minY, maxX, maxY);
        let intersectArea = this._shoelaceArea(clipped);
        const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
        for (const hole of allHoles) {
          const clippedHole = this._clipPolygonToRect(hole, minX, minY, maxX, maxY);
          intersectArea -= this._shoelaceArea(clippedHole);
        }
        if (intersectArea / geo.area >= 0.5) {
          const already = this._floorSelections.some(s => s.room === room && s.zone === null);
          if (!already) this._floorSelections.push({ room, zone: null });
        }
      }
    }
    // Box-select exterior zones
    const extZones = this._getExteriorZones();
    for (const z of extZones) {
      const ef = this._getExteriorZoneFlooring(z);
      if (!ef) continue;
      const clipped = this._clipPolygonToRect(z.polygon, minX, minY, maxX, maxY);
      const intersectArea = this._shoelaceArea(clipped);
      if (intersectArea / z.area >= 0.5) {
        const already = this._floorSelections.some(s => s.isExterior && this._zonesMatch(s.zone, z));
        if (!already) this._floorSelections.push({ room: null, zone: z, isExterior: true });
      }
    }

    this._floorSelectCorner1 = null;
    this._floorSelectEnd = null;
    rd._boxSelectCooldownUntil = Date.now() + 300;
    rd._updateFloatingDelete();
    const n = this._floorSelections.length;
    rd.updateStatus(n > 0
      ? `${n} floored zone${n > 1 ? 's' : ''} selected. Delete to remove flooring.`
      : 'Click a floored zone to select it. Delete to remove flooring.');
    rd.render();
  }

  // Sutherland-Hodgman: clip polygon against axis-aligned rectangle
  _clipPolygonToRect(poly, minX, minY, maxX, maxY) {
    if (!poly || poly.length < 3) return [];
    let output = poly;
    const edges = [
      { inside: p => p.x >= minX, intersect: (a, b) => { const t = (minX - a.x) / (b.x - a.x); return { x: minX, y: a.y + t * (b.y - a.y) }; } },
      { inside: p => p.x <= maxX, intersect: (a, b) => { const t = (maxX - a.x) / (b.x - a.x); return { x: maxX, y: a.y + t * (b.y - a.y) }; } },
      { inside: p => p.y >= minY, intersect: (a, b) => { const t = (minY - a.y) / (b.y - a.y); return { x: a.x + t * (b.x - a.x), y: minY }; } },
      { inside: p => p.y <= maxY, intersect: (a, b) => { const t = (maxY - a.y) / (b.y - a.y); return { x: a.x + t * (b.x - a.x), y: maxY }; } },
    ];
    for (const edge of edges) {
      if (output.length === 0) return [];
      const input = output;
      output = [];
      for (let i = 0; i < input.length; i++) {
        const curr = input[i];
        const prev = input[(i + input.length - 1) % input.length];
        const currIn = edge.inside(curr);
        const prevIn = edge.inside(prev);
        if (prevIn && currIn) output.push(curr);
        else if (prevIn) output.push(edge.intersect(prev, curr));
        else if (currIn) { output.push(edge.intersect(prev, curr)); output.push(curr); }
      }
    }
    return output;
  }

  // Shoelace formula — absolute area of a simple polygon
  _shoelaceArea(poly) {
    if (!poly || poly.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      area += poly[i].x * poly[j].y - poly[j].x * poly[i].y;
    }
    return Math.abs(area) / 2;
  }

  _cancelFloorBoxSelect() {
    if (!this._floorSelectCorner1) return false;
    this._floorSelectCorner1 = null;
    this._floorSelectEnd = null;
    this.rd.updateStatus('Click a floored room to select it. Delete to remove flooring.');
    this.rd.render();
    return true;
  }

  deleteFloorSelection(opts) {
    if (this._floorSelections.length === 0) return false;
    const rd = this.rd;
    if (!opts || !opts.skipUndo) rd.saveUndoState();
    for (const sel of this._floorSelections) {
      if (sel.isExterior) {
        // Delete exterior zone flooring
        this._setExteriorZoneFlooring(sel.zone, null, null);
      } else if (sel.zone) {
        // Delete individual zone flooring
        this._setZoneFlooring(sel.room, sel.zone, null, null);
        const geo = rd._getRoomGeoCache(sel.room);
        const zones = this._getZonesForRoom(sel.room, geo);
        this._syncRoomFlooringFromZones(sel.room, zones);
        sel.room._zoneLayouts = null;
      } else {
        // Delete entire room flooring
        sel.room.flooring = null;
        sel.room.zoneFlooring = null;
        sel.room._flooringLayout = null;
        sel.room._zoneLayouts = null;
      }
      // Remove platforms sitting on this room — can't exist without flooring
      if (sel.room) this._removePlatformsInRoom(sel.room);
    }
    this._floorSelections = [];
    this._floorInfoPinned = false;
    this._hideFloorInfo();
    rd._triggerAutoSave();
    rd.render();
    rd.updateMeasurements();
    rd._updateFloatingDelete();
    this._updateMaterialSummary();
    this._updateMiniPreview();
    this._syncCompassBtn();
    this._syncQuoteTab();
    return true;
  }

  // ── Zone Helpers (per-zone flooring) ─────

  /** Hit-test a point against a zone. A zone with island holes is an annulus —
   *  points inside a hole belong to the island zone, not the surrounding one. */
  _zoneHitTest(zone, x, y) {
    if (!zone || !zone.polygon || zone.polygon.length < 3) return false;
    if (!RoomDesignerMath.pointInPolygon(x, y, zone.polygon)) return false;
    if (zone.holes) {
      for (const h of zone.holes) {
        if (h && h.length >= 3 && RoomDesignerMath.pointInPolygon(x, y, h)) return false;
      }
    }
    return true;
  }

  /** Island holes of a zone, as exclusion polygons for layout/render clipping. */
  _zoneHoles(zone) {
    return (zone && zone.holes && zone.holes.length > 0) ? zone.holes : [];
  }

  /** Fingerprint fragment for a zone's island holes — keeps layout caches honest
   *  when an island is moved or resized without the room geo changing. */
  _zoneHolesFp(zone) {
    const holes = this._zoneHoles(zone);
    if (holes.length === 0) return '';
    return ':h' + holes.map(h => `${h.length}@${h[0].x.toFixed(2)},${h[0].y.toFixed(2)}`).join(';');
  }

  /** Get zones for a room, using cache. Returns [{polygon, holes, centroid, labelPoint, area}, ...] */
  _getZonesForRoom(room, geo) {
    if (!room || !room.closed) return [];
    if (!geo) geo = this.rd._getRoomGeoCache(room);
    if (!geo.roomPolygon || geo.roomPolygon.length < 3) return [];

    // Collect divider segments whose endpoints are inside/on this room
    const divSegs = this._getDividerSegmentsForRoom(room, geo);

    // If no dividers, return single zone = whole room
    if (divSegs.length === 0) {
      if (room._zoneCache) room._zoneCache = null;
      return [{ polygon: geo.roomPolygon, centroid: RoomDesignerMath.polygonCentroid(geo.roomPolygon), area: Math.abs(RoomDesignerMath.signedPolygonArea(geo.roomPolygon)) }];
    }

    // Build fingerprint for cache check
    const fp = this._zoneCacheFingerprint(geo, divSegs);
    if (room._zoneCache && room._zoneCache.fp === fp) {
      return room._zoneCache.zones;
    }

    const holes = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
    const zones = RoomDesignerMath.computeRoomZones(geo.roomPolygon, divSegs, holes);

    room._zoneCache = { fp, zones };
    // If room already had whole-room flooring but no per-zone data, promote once
    if (zones.length > 1) this._ensureZoneFlooring(room, zones);
    // Reconcile existing zoneFlooring against new zone structure
    if (zones.length > 1) this._reconcileZoneFlooring(room, zones);
    return zones;
  }

  /** Collect all wall-segment-group segments that are relevant to a room. */
  _getDividerSegmentsForRoom(room, geo) {
    const rd = this.rd;
    if (!rd.wallSegmentGroups || rd.wallSegmentGroups.length === 0) return [];
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return [];
    const EPS = 0.1;
    const result = [];
    for (const group of rd.wallSegmentGroups) {
      for (const seg of group.segments) {
        // Check if both endpoints are inside or on the room polygon
        const sIn = RoomDesignerMath.pointInPolygon(seg.start.x, seg.start.y, poly) || this._pointOnPolygonBoundary(seg.start, poly, EPS);
        const eIn = RoomDesignerMath.pointInPolygon(seg.end.x, seg.end.y, poly) || this._pointOnPolygonBoundary(seg.end, poly, EPS);
        if (sIn && eIn) result.push(seg);
      }
    }
    return result;
  }

  /** Collect wall-type (thick) divider segments for a room — used for scissors edge offset. */
  _getWallDividerSegmentsForRoom(room, geo) {
    const rd = this.rd;
    if (!rd.wallSegmentGroups || rd.wallSegmentGroups.length === 0) return [];
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return [];
    const EPS = 0.1;
    const result = [];
    for (const group of rd.wallSegmentGroups) {
      if (group.type === 'line') continue; // line-type dividers have no thickness
      for (const seg of group.segments) {
        const sIn = RoomDesignerMath.pointInPolygon(seg.start.x, seg.start.y, poly) || this._pointOnPolygonBoundary(seg.start, poly, EPS);
        const eIn = RoomDesignerMath.pointInPolygon(seg.end.x, seg.end.y, poly) || this._pointOnPolygonBoundary(seg.end, poly, EPS);
        if (sIn && eIn) result.push(seg);
      }
    }
    return result;
  }

  /** Check if a point is on a polygon boundary (within eps). */
  _pointOnPolygonBoundary(pt, poly, eps) {
    for (let i = 0; i < poly.length; i++) {
      const j = (i + 1) % poly.length;
      const wall = { start: poly[i], end: poly[j] };
      if (RoomDesignerMath.distanceToWall(pt, wall) < eps) return true;
    }
    return false;
  }

  /** Fingerprint for zone cache: room geo + divider segment coords. */
  _zoneCacheFingerprint(geo, divSegs) {
    let fp = geo.fp || '';
    for (const seg of divSegs) {
      fp += `|${seg.start.x.toFixed(3)},${seg.start.y.toFixed(3)}-${seg.end.x.toFixed(3)},${seg.end.y.toFixed(3)}`;
    }
    return fp;
  }

  /** Compare two zone objects by centroid proximity (handles null). */
  _zonesMatch(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const dx = a.centroid.x - b.centroid.x;
    const dy = a.centroid.y - b.centroid.y;
    return dx * dx + dy * dy < 0.25; // within 0.5 ft
  }

  /** Get flooring data for a specific zone within a room.
   *  Returns {productId, colorId, flooringAngle} or null. */
  _getZoneFlooring(room, zone) {
    // Each zone is independent — only returns flooring if this zone has an explicit entry
    if (!room.zoneFlooring || room.zoneFlooring.length === 0) return null;
    let best = null, bestDist = Infinity;
    for (const zf of room.zoneFlooring) {
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; }
    }
    if (best && bestDist < 2) {
      return { productId: best.productId, colorId: best.colorId, flooringAngle: best.flooringAngle || 0, independent: !!best.independent };
    }
    return null;
  }

  /** Set flooring for a specific zone. */
  _setZoneFlooring(room, zone, productId, colorId) {
    if (!room.zoneFlooring) room.zoneFlooring = [];
    // Find existing entry for this zone (by closest centroid)
    let best = null, bestDist = Infinity, bestIdx = -1;
    for (let i = 0; i < room.zoneFlooring.length; i++) {
      const zf = room.zoneFlooring[i];
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; bestIdx = i; }
    }
    if (best && bestDist < 2) {
      if (productId === null) {
        // Remove
        room.zoneFlooring.splice(bestIdx, 1);
      } else {
        // Update
        best.productId = productId;
        best.colorId = colorId;
        best.cx = zone.centroid.x;
        best.cy = zone.centroid.y;
      }
    } else if (productId !== null) {
      // Add new
      room.zoneFlooring.push({
        cx: zone.centroid.x, cy: zone.centroid.y,
        productId, colorId, flooringAngle: room.flooringAngle || 0
      });
    }
  }

  /** Set rotation angle for a specific zone. */
  _setZoneAngle(room, zone, angleRad) {
    if (!room.zoneFlooring) return;
    let best = null, bestDist = Infinity;
    for (const zf of room.zoneFlooring) {
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; }
    }
    if (best && bestDist < 2) {
      best.flooringAngle = angleRad;
    }
  }

  /** Set independent layout flag for a specific zone. */
  _setZoneIndependent(room, zone, value) {
    if (!room.zoneFlooring) return;
    let best = null, bestDist = Infinity;
    for (const zf of room.zoneFlooring) {
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; }
    }
    if (best && bestDist < 2) {
      if (value) best.independent = true;
      else delete best.independent;
    }
  }

  /** Clear independent layout flag from all zones in a room. */
  _clearAllZoneIndependent(room) {
    if (!room.zoneFlooring) return;
    for (const zf of room.zoneFlooring) {
      delete zf.independent;
    }
    room._zoneLayouts = null;
  }

  // ── Exterior Zone Helpers ──────────────

  /** Get exterior zones: enclosed areas outside rooms formed by line-type wall segments. */
  _getExteriorZones() {
    const rd = this.rd;
    if (!rd.wallSegmentGroups || rd.wallSegmentGroups.length === 0) return [];
    if (!rd.rooms || rd.rooms.length === 0) return [];

    // Collect room polygons
    const roomPolygons = [];
    for (const room of rd.rooms) {
      if (!room || !room.closed || room.type !== 'add') continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      roomPolygons.push(geo.roomPolygon);
    }
    if (roomPolygons.length === 0) return [];

    // Collect line-type segments where at least one endpoint is outside all rooms
    const exteriorSegs = [];
    for (const group of rd.wallSegmentGroups) {
      if ((group.type || 'wall') !== 'line') continue;
      for (const seg of group.segments) {
        const sInside = this._isPointInsideAnyRoomPoly(seg.start, roomPolygons);
        const eInside = this._isPointInsideAnyRoomPoly(seg.end, roomPolygons);
        // Include if at least one endpoint is outside (or on boundary)
        // Also include if both endpoints are on boundary (wall-to-wall exterior line)
        const sOnBoundary = !sInside && this._isPointOnAnyRoomBoundary(seg.start, roomPolygons);
        const eOnBoundary = !eInside && this._isPointOnAnyRoomBoundary(seg.end, roomPolygons);
        if (!sInside || !eInside || sOnBoundary || eOnBoundary) {
          exteriorSegs.push(seg);
        }
      }
    }
    if (exteriorSegs.length === 0) return [];

    // Check cache
    let fp = '';
    for (const poly of roomPolygons) {
      for (const pt of poly) fp += `${pt.x.toFixed(3)},${pt.y.toFixed(3)}|`;
      fp += ';';
    }
    for (const seg of exteriorSegs) {
      fp += `${seg.start.x.toFixed(3)},${seg.start.y.toFixed(3)}-${seg.end.x.toFixed(3)},${seg.end.y.toFixed(3)}|`;
    }
    if (this._exteriorZoneCache && this._exteriorZoneCache.fp === fp) {
      return this._exteriorZoneCache.zones;
    }

    const zones = RoomDesignerMath.computeExteriorZones(roomPolygons, exteriorSegs);
    this._exteriorZoneCache = { fp, zones };
    return zones;
  }

  /** Check if point is strictly inside any room polygon (not just on boundary). */
  _isPointInsideAnyRoomPoly(pt, roomPolygons) {
    for (const poly of roomPolygons) {
      if (RoomDesignerMath.pointInPolygon(pt.x, pt.y, poly)) return true;
    }
    return false;
  }

  /** Check if point is on the boundary of any room polygon. */
  _isPointOnAnyRoomBoundary(pt, roomPolygons) {
    for (const poly of roomPolygons) {
      if (this._pointOnPolygonBoundary(pt, poly, 0.1)) return true;
    }
    return false;
  }

  /** Get flooring data for an exterior zone. */
  _getExteriorZoneFlooring(zone) {
    if (!this._exteriorZoneFlooring || this._exteriorZoneFlooring.length === 0) return null;
    let best = null, bestDist = Infinity;
    for (const zf of this._exteriorZoneFlooring) {
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; }
    }
    if (best && bestDist < 2) {
      return { productId: best.productId, colorId: best.colorId, flooringAngle: best.flooringAngle || 0 };
    }
    return null;
  }

  /** Set flooring for an exterior zone. */
  _setExteriorZoneFlooring(zone, productId, colorId) {
    if (!this._exteriorZoneFlooring) this._exteriorZoneFlooring = [];
    let best = null, bestDist = Infinity, bestIdx = -1;
    for (let i = 0; i < this._exteriorZoneFlooring.length; i++) {
      const zf = this._exteriorZoneFlooring[i];
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; bestIdx = i; }
    }
    if (best && bestDist < 2) {
      if (productId === null) {
        this._exteriorZoneFlooring.splice(bestIdx, 1);
      } else {
        best.productId = productId;
        best.colorId = colorId;
        best.cx = zone.centroid.x;
        best.cy = zone.centroid.y;
      }
    } else if (productId !== null) {
      this._exteriorZoneFlooring.push({
        cx: zone.centroid.x, cy: zone.centroid.y,
        productId, colorId, flooringAngle: 0
      });
    }
  }

  /** Set rotation angle for an exterior zone. */
  _setExteriorZoneAngle(zone, angleRad) {
    if (!this._exteriorZoneFlooring) return;
    let best = null, bestDist = Infinity;
    for (const zf of this._exteriorZoneFlooring) {
      const dist = Math.sqrt((zf.cx - zone.centroid.x) ** 2 + (zf.cy - zone.centroid.y) ** 2);
      if (dist < bestDist) { bestDist = dist; best = zf; }
    }
    if (best && bestDist < 2) {
      best.flooringAngle = angleRad;
    }
  }

  /** Check if any exterior zones have flooring. */
  _hasExteriorFlooring() {
    return this._exteriorZoneFlooring && this._exteriorZoneFlooring.length > 0;
  }

  /** Promote whole-room flooring to per-zone when zones exist. */
  _ensureZoneFlooring(room, zones) {
    if (!room.flooring) return;
    if (zones.length <= 1) return;
    if (room.zoneFlooring && room.zoneFlooring.length > 0) return;
    // Copy room.flooring to all zones
    room.zoneFlooring = zones.map(z => ({
      cx: z.centroid.x, cy: z.centroid.y,
      productId: room.flooring.productId,
      colorId: room.flooring.colorId,
      flooringAngle: room.flooringAngle || 0
    }));
  }

  /**
   * Reconcile zoneFlooring entries against the current zone structure.
   * Each zoneFlooring entry is matched to its closest zone exclusively
   * (no two zones share the same entry), then the stored centroid is
   * updated to track the zone's actual position.  Orphaned entries
   * (no zone within 2 ft) are removed.
   */
  _reconcileZoneFlooring(room, zones) {
    if (!room.zoneFlooring || room.zoneFlooring.length === 0) return;
    if (zones.length <= 1) return;

    // Build distance matrix: for each zoneFlooring entry, find closest zone
    const claimed = new Set();       // zone indices already claimed
    const matched = new Map();       // zf index → zone index
    const pairs = [];                // [{zfIdx, zIdx, dist}]

    for (let fi = 0; fi < room.zoneFlooring.length; fi++) {
      const zf = room.zoneFlooring[fi];
      for (let zi = 0; zi < zones.length; zi++) {
        const d = Math.sqrt((zf.cx - zones[zi].centroid.x) ** 2 + (zf.cy - zones[zi].centroid.y) ** 2);
        if (d < 2) pairs.push({ fi, zi, d });
      }
    }

    // Greedy assignment: shortest distance first, each zf and zone used at most once
    pairs.sort((a, b) => a.d - b.d);
    const usedZf = new Set();
    for (const p of pairs) {
      if (usedZf.has(p.fi) || claimed.has(p.zi)) continue;
      usedZf.add(p.fi);
      claimed.add(p.zi);
      matched.set(p.fi, p.zi);
    }

    // Update matched centroids, remove orphans
    const kept = [];
    for (let fi = 0; fi < room.zoneFlooring.length; fi++) {
      if (matched.has(fi)) {
        const zf = room.zoneFlooring[fi];
        const zone = zones[matched.get(fi)];
        zf.cx = zone.centroid.x;
        zf.cy = zone.centroid.y;
        kept.push(zf);
      }
      // Orphaned entries (no zone within 2 ft) are dropped
    }

    if (kept.length !== room.zoneFlooring.length) {
      room.zoneFlooring = kept;
      room._zoneLayouts = null;
      this._syncRoomFlooringFromZones(room, zones);
    }
  }

  /** Check if a room has any flooring (whole-room or per-zone). */
  _roomHasFlooring(room) {
    if (room.flooring) return true;
    if (room.zoneFlooring && room.zoneFlooring.length > 0) return true;
    return false;
  }

  /** Get zone layout cache for a specific zone index. */
  _getZoneLayoutCache(room, zoneIdx) {
    if (!room._zoneLayouts) return null;
    return room._zoneLayouts.get(zoneIdx) || null;
  }

  /** Set zone layout cache for a specific zone index. */
  _setZoneLayoutCache(room, zoneIdx, layout, cacheKey) {
    if (!room._zoneLayouts) room._zoneLayouts = new Map();
    room._zoneLayouts.set(zoneIdx, { layout, cacheKey });
  }

  // Extra tiles/planks added for install mistakes: 5% (min 1). Heritage Motivate planks get
  // none — the 50% running bond already leaves enough off-cut at the walls (Ryan 2026-10-01).
  // Carton rounding (qtyStep) still applies on top.
  _mistakeExtra(product, count) {
    if (product && product.id === 'ecore-heritage-motivate-vinyl-plank-7mm-pvt-performance-flooring') return 0;
    return Math.max(1, Math.ceil(count * 0.05));
  }

  // Build a readable purchase-roll label with headline + cut-pattern sub-lines.
  // mg = { totalCount, purchaseLen, cutPatterns:[{cuts, count, waste}], singlePieceWaste }
  _buildRollLabel(mg, uniform) {
    const rd = this.rd;
    const rlStr = rd.formatMeasurement(mg.purchaseLen);
    const uTag = uniform ? ' uniform' : '';
    const rollWord = mg.totalCount > 1 ? 'rolls' : 'roll';

    if (mg.cutPatterns.length === 0) {
      let label = `${mg.totalCount} ${rollWord} @ ${rlStr}${uTag}`;
      if (mg.singlePieceWaste > 0.04) label += ` <span class="rd-roll-overage-tag">(${rd.formatMeasurement(mg.singlePieceWaste)} waste)</span>`;
      return label;
    }

    let html = `<strong>${mg.totalCount} ${rollWord} @ ${rlStr}${uTag}</strong>`;
    for (const cp of mg.cutPatterns) {
      const pieces = cp.cuts.map(c => c.count > 1 ? `${c.count} \u00d7 ${rd.formatMeasurement(c.length)}` : rd.formatMeasurement(c.length)).join(' + ');
      const rollCountStr = cp.count > 1 ? `${cp.count} ${rollWord}` : `1 roll`;
      let line = `${rollCountStr} \u2192 cut into ${pieces}`;
      if (cp.waste > 0.04) line += ` <span class="rd-roll-overage-tag">(${rd.formatMeasurement(cp.waste)} waste)</span>`;
      html += `<br><span class="rd-roll-cut-line">${line}</span>`;
    }
    return html;
  }

  // Round up to nearest half-foot (6") for tighter overage
  // Ignore excess under 1/16" before rounding up: wall coordinates carry float crumbs
  // (17.000000025 ft), and a bare ceil turned a 17' run + 6" overage into an 18' cut on
  // some strips and 17'6" on their neighbours (PLAN-1097, 2026-10-12).
  _ceilHalfFoot(ft) { return Math.ceil(ft * 2 - 1 / 96) / 2; }

  // Flash-preview overage + seam lines: show at full opacity, hold 1s, fade over 3s.
  // No-op if overage and seams are already both visible.
  _flashOverlayPreview() {
    // Only flash when floor overage is hidden — if overage is already visible, no need
    if (!this.rd.hideOverage) return;
    // Cancel any running flash
    if (this._flashOverlayTimer) { clearTimeout(this._flashOverlayTimer); this._flashOverlayTimer = null; }
    if (this._flashOverlayRAF) { cancelAnimationFrame(this._flashOverlayRAF); this._flashOverlayRAF = null; }

    // Remember whether seams are currently off — we'll flash them too if so
    this._flashIncludeSeams = !this.rd.showSeamLines;

    this._flashOverlayAlpha = 1;
    this._flashOverlayPhase = 'hold';
    this.rd.render(); // triggers _drawFlashOverlayPass via afterRoomsHook

    // After 1s hold, start the 3s fade
    this._flashOverlayTimer = setTimeout(() => {
      this._flashOverlayPhase = 'fade';
      this._flashOverlayFadeStart = performance.now();
      const fadeDuration = 3000;
      const tick = () => {
        const elapsed = performance.now() - this._flashOverlayFadeStart;
        this._flashOverlayAlpha = Math.max(0, 1 - elapsed / fadeDuration);
        if (this._flashOverlayAlpha > 0) {
          this.rd.render();
          this._flashOverlayRAF = requestAnimationFrame(tick);
        } else {
          this._flashOverlayPhase = null;
          this._flashOverlayRAF = null;
          this._flashIncludeSeams = false;
          this.rd.render(); // final clean render
        }
      };
      this._flashOverlayRAF = requestAnimationFrame(tick);
    }, 1000);
  }

  // Draw a single flash overlay pass: temporarily force flags so only
  // overage (+ seams if they were off) render, at _flashOverlayAlpha opacity.
  _drawFlashOverlayPass() {
    const rd = this.rd;
    const ctx = rd.ctx;
    const savedOverlay = this.showOverlay;
    const savedMat = rd.showMaterialOverlay;
    const savedSeam = rd.showSeamLines;
    const savedHide = rd.hideOverage;

    // Force overlays on so _drawFlooringOverlay runs the full path
    this.showOverlay = true;
    rd.showMaterialOverlay = true;
    rd.showSeamLines = this._flashIncludeSeams ? true : savedSeam;
    rd.hideOverage = false;
    this._flashSeamsOverageOnly = true;

    ctx.save();
    ctx.globalAlpha = this._flashOverlayAlpha;

    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      this._drawFlooringOverlay(room, geo);
    }

    ctx.restore();

    // Restore all flags
    this.showOverlay = savedOverlay;
    rd.showMaterialOverlay = savedMat;
    rd.showSeamLines = savedSeam;
    rd.hideOverage = savedHide;
    this._flashSeamsOverageOnly = false;
  }

  // Compute available uniform target length options for the dropdown.
  // For each strip height in the room, generates candidates by dividing it
  // into 1, 2, 3, or 4 pieces — representing trade-offs between fewer seams
  // (longer rolls, more overage) and more seams (shorter rolls, less overage).
  _getUniformLengthOptions(product) {
    if (!product || product.type === 'tile' || product.shape === 'paver') return [];
    const rd = this.rd;
    const flooredRooms = rd.rooms.filter(r => this._roomHasFlooring(r) && r.closed && r.type === 'add');
    if (flooredRooms.length === 0) return [];

    // Collect strip segment heights (full column heights before uniform splitting).
    // Temporarily force minSeams to get the natural layout.
    const savedMode = this._rollOptimizationMode;
    const savedTarget = this._uniformTargetLen;
    this._rollOptimizationMode = 'minSeams';
    this._uniformTargetLen = 0;

    const stripHeights = [];
    for (const room of flooredRooms) {
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (let zi = 0; zi < zones.length; zi++) {
          const zone = zones[zi];
          const zf = this._getZoneFlooring(room, zone);
          if (!zf || String(zf.productId) !== String(product.id)) continue;
          const _zh = this._zoneHoles(zone);
          const zoneGeo = { roomPolygon: zone.polygon, cutoutPolys: [], holePolygons: _zh };
          const zoneAngle = zf.flooringAngle || 0;
          const _te = [...this._getTurfExclusionsForRoom(room, geo), ..._zh];
          let layout;
          if (zoneAngle !== 0) {
            const centroid = this._getPolygonCentroid(zone.polygon);
            const rP = this._rotatePolygon(zone.polygon, -zoneAngle, centroid.x, centroid.y);
            const rE = _te.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -zoneAngle, centroid.x, centroid.y) : e);
            layout = this._computeRollLayout(room, zoneGeo, product, rP, rE);
          } else {
            layout = this._computeRollLayout(room, zoneGeo, product, null, _te.length > 0 ? _te : []);
          }
          if (layout && layout.strips) {
            for (const strip of layout.strips)
              for (const seg of strip.segments)
                stripHeights.push(seg.pieceLengths.reduce((a, b) => a + b, 0));
          }
        }
      } else {
        if (!room.flooring || String(room.flooring.productId) !== String(product.id)) continue;
        const floorAngle = room.flooringAngle || 0;
        const _te = this._getTurfExclusionsForRoom(room, geo);
        let layout;
        if (floorAngle !== 0) {
          const centroid = this._getPolygonCentroid(geo.roomPolygon);
          const rP = this._rotatePolygon(geo.roomPolygon, -floorAngle, centroid.x, centroid.y);
          const rE = _te.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
          layout = this._computeRollLayout(room, geo, product, rP, rE);
        } else {
          layout = this._computeRollLayout(room, geo, product, null, _te.length > 0 ? _te : []);
        }
        if (layout && layout.strips) {
          for (const strip of layout.strips)
            for (const seg of strip.segments)
              stripHeights.push(seg.pieceLengths.reduce((a, b) => a + b, 0));
        }
      }
    }
    this._rollOptimizationMode = savedMode;
    this._uniformTargetLen = savedTarget;

    if (stripHeights.length === 0) return [];

    const maxLen = product.maxLengthFt || 50;
    const minLen = product.minLengthFt || 25;
    const maxPieces = 4; // max pieces per strip to consider

    // For each distinct strip height, compute the minimum roll length
    // needed for 1, 2, 3, 4 pieces: ceil(H/n). These are the "breakpoints"
    // where seam count changes — the only options that matter, since any
    // length between breakpoints gives the same seams but more overage.
    const candidateSet = new Set();
    const distinctHeights = [...new Set(stripHeights.map(h => Math.ceil(h)))];

    for (const h of distinctHeights) {
      if (h < 10) continue;
      for (let n = 1; n <= maxPieces; n++) {
        const candidate = Math.ceil(h / n);
        if (candidate >= 10 && candidate <= maxLen) {
          candidateSet.add(candidate);
        }
      }
    }

    // Score each candidate: total seams + total laid material across all strips.
    // Filter out options with excessive seams (more than 1 seam per strip on average).
    const scored = [];
    const stripCount = stripHeights.length;
    for (const targetLen of candidateSet) {
      let totalSeams = 0;
      let totalMaterial = 0;
      for (const h of stripHeights) {
        const n = Math.ceil(h / targetLen);
        totalSeams += n - 1;
        totalMaterial += n * targetLen;
      }
      if (totalSeams > stripCount) continue; // too many seams — impractical
      const purchaseLen = Math.max(minLen, targetLen);
      scored.push({ targetLen, purchaseLen, totalSeams, totalMaterial });
    }

    // Remove dominated options: an option is dominated if another option
    // has both fewer-or-equal seams AND less-or-equal material
    const filtered = scored.filter(opt => {
      return !scored.some(other =>
        other !== opt
        && other.totalSeams <= opt.totalSeams
        && other.totalMaterial <= opt.totalMaterial
        && (other.totalSeams < opt.totalSeams || other.totalMaterial < opt.totalMaterial)
      );
    });

    // Deduplicate by purchase length — keep the one with least material
    const optionMap = new Map();
    for (const opt of filtered) {
      if (!optionMap.has(opt.purchaseLen) || opt.totalMaterial < optionMap.get(opt.purchaseLen).totalMaterial) {
        optionMap.set(opt.purchaseLen, opt);
      }
    }

    return [...optionMap.values()]
      .sort((a, b) => a.purchaseLen - b.purchaseLen)
      .map(opt => ({ length: opt.purchaseLen, _targetLen: opt.targetLen }));
  }

  // Pick the uniform length with the least overage from segment totals.
  // Used as the auto/default when _uniformTargetLen === 0.
  _pickLeastOverageLen(segTotals, product) {
    if (!segTotals || segTotals.length === 0) return this._ceilHalfFoot(0);
    const minLen = product.minLengthFt || 25;
    // Collect distinct candidate lengths (ceiled to half-foot)
    const candidates = new Set();
    for (const h of segTotals) candidates.add(this._ceilHalfFoot(h));
    if (product.maxLengthFt) candidates.add(product.maxLengthFt);

    let bestLen = this._ceilHalfFoot(Math.max(...segTotals)); // fallback: longest
    let bestWaste = Infinity;
    for (const targetLen of candidates) {
      if (targetLen < 5) continue;
      let totalCut = 0;
      let totalNatural = 0;
      for (const h of segTotals) {
        const n = h > targetLen ? Math.ceil(h / targetLen) : 1;
        totalCut += n * targetLen;
        totalNatural += h;
      }
      const waste = totalCut - totalNatural;
      if (waste < bestWaste) {
        bestWaste = waste;
        bestLen = targetLen;
      }
    }
    return bestLen;
  }

  // Repopulate all uniform target dropdowns in the DOM with fresh options.
  // Auto-selects the best option if current target is missing or unset.
  // Returns true if the target changed (caller should re-render).
  _repopulateUniformTargetSelects() {
    const panel = document.getElementById('rdSidePanelBody');
    if (!panel) return;
    let targetChanged = false;
    const containers = panel.querySelectorAll('.rd-uniform-target');
    containers.forEach(ct => {
      const productId = ct.dataset.productId;
      const product = (this.products || []).find(p => String(p.id) === String(productId));
      if (!product) return;
      const sel = ct.querySelector('.rd-uniform-target-select');
      if (!sel) return;
      const opts = this._getUniformLengthOptions(product);
      // If current target doesn't exist in the new options, pick the best one
      const curExists = opts.some(o => o._targetLen === this._uniformTargetLen);
      if ((!curExists || this._uniformTargetLen === 0) && opts.length > 0) {
        this._uniformTargetLen = opts[0]._targetLen;
        targetChanged = true;
      }
      const curVal = this._uniformTargetLen;
      let html = '';
      for (const opt of opts) {
        html += '<option value="' + opt._targetLen + '"' + (curVal === opt._targetLen ? ' selected' : '') + '>';
        html += opt.length + '\u2032 rolls';
        html += '</option>';
      }
      sel.innerHTML = html;
      // Force browser to redraw the select's displayed value
      if (opts.length > 0) sel.value = String(curVal);
    });
    // If the target changed, re-render with the new target
    if (targetChanged) {
      this.rd.updateMeasurements();
      this.rd.render();
    }
  }

  // ── Roll Optimization Helpers ──────────────

  // Estimate best total purchase length across 3 bin-packing strategies (lengths only, no width grouping)
  _estimateBestPurchase(pieceLengths, maxLen, minLen) {
    if (pieceLengths.length === 0) return 0;
    const purchase = (bins) => bins.reduce((s, b) => s + Math.max(minLen, Math.ceil(b)), 0);

    // Strategy A: ascending, fill bins until ≥ minLen then close
    const stratA = () => {
      const sorted = [...pieceLengths].sort((a, b) => a - b);
      const bins = [];
      let cur = 0;
      for (const p of sorted) {
        if (cur + p <= maxLen) {
          cur += p;
          if (cur >= minLen) { bins.push(cur); cur = 0; }
        } else {
          if (cur > 0) bins.push(cur);
          cur = p;
        }
      }
      if (cur > 0) bins.push(cur);
      return bins;
    };

    // Strategy B: FFD descending (current default)
    const stratB = () => {
      const sorted = [...pieceLengths].sort((a, b) => b - a);
      const bins = [];
      for (const p of sorted) {
        let placed = false;
        for (let i = 0; i < bins.length; i++) {
          if (bins[i] + p <= maxLen) { bins[i] += p; placed = true; break; }
        }
        if (!placed) bins.push(p);
      }
      return bins;
    };

    // Strategy C: balanced pairs (smallest + largest)
    const stratC = () => {
      const sorted = [...pieceLengths].sort((a, b) => a - b);
      const bins = [];
      let lo = 0, hi = sorted.length - 1;
      while (lo <= hi) {
        if (lo === hi) { bins.push(sorted[lo]); lo++; }
        else if (sorted[lo] + sorted[hi] <= maxLen) { bins.push(sorted[lo] + sorted[hi]); lo++; hi--; }
        else { bins.push(sorted[hi]); hi--; }
      }
      return bins;
    };

    return Math.min(purchase(stratA()), purchase(stratB()), purchase(stratC()));
  }

  // Width-aware bin-packing: returns bins with { width, usedLen, remaining, cuts }
  // mode: 'minSeams' = FFD (default), 'minMaterial' = best of 3 strategies per width group
  _binPackRollPieces(pieces, maxLen, minLen, mode) {
    if (pieces.length === 0) return [];

    // Helper: FFD bin-pack (the existing algorithm)
    const ffdPack = (items, capacity) => {
      const sorted = [...items].sort((a, b) => b.length - a.length);
      const bins = [];
      for (const p of sorted) {
        let placed = false;
        for (const bin of bins) {
          if (bin.width === p.width && bin.remaining >= p.length) {
            bin.remaining -= p.length;
            bin.usedLen += p.length;
            const existing = bin.cuts.find(c => Math.abs(c.length - p.length) < 0.01);
            if (existing) existing.count++;
            else bin.cuts.push({ length: p.length, count: 1 });
            placed = true;
            break;
          }
        }
        if (!placed) {
          bins.push({ width: p.width, remaining: capacity - p.length, usedLen: p.length, cuts: [{ length: p.length, count: 1 }] });
        }
      }
      return bins;
    };

    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-11-03, PLAN-1111-A) — rebalance after packing.
       FFD fills the first roll as full as it can and never asks whether the LAST roll is
       about to be bumped to the minimum anyway. PLAN-1111-A (55'6" of cuts, 25'/50' rolls):
       FFD bought 49' + 25' = 74' with one 6'6" cut on the second roll; moving cuts onto that
       roll gives 32' + 25' = 57'. Same cuts, same strips, same seams - only which roll each
       cut comes off changes. Moves one cut, or swaps two, between same-width rolls while the
       total bought (each roll max(min, ceil(used)), an emptied roll 0) strictly drops; a tie
       keeps the original packing, so a plan that was already optimal does not change. */
    const rebalance = (bins, capacity) => {
      if (bins.length < 2) return bins;
      const cost = (u) => (u > 1e-9 ? Math.max(minLen, Math.ceil(u)) : 0);
      const take = (bin, len) => {
        const c = bin.cuts.find(x => Math.abs(x.length - len) < 0.01);
        if (--c.count === 0) bin.cuts.splice(bin.cuts.indexOf(c), 1);
        bin.usedLen -= len; bin.remaining += len;
      };
      const put = (bin, len) => {
        const c = bin.cuts.find(x => Math.abs(x.length - len) < 0.01);
        if (c) c.count++; else bin.cuts.push({ length: len, count: 1 });
        bin.usedLen += len; bin.remaining -= len;
      };
      for (let guard = 0; guard < 200; guard++) {
        let best = null;
        for (const A of bins) for (const B of bins) {
          if (A === B || Math.abs(A.width - B.width) > 0.01) continue;
          const base = cost(A.usedLen) + cost(B.usedLen);
          for (const ca of A.cuts) {
            const a = ca.length;
            if (B.usedLen + a <= capacity + 1e-6) {
              const d = cost(A.usedLen - a) + cost(B.usedLen + a) - base;
              if (d < -1e-9 && (!best || d < best.d)) best = { d, A, B, a, b: null };
            }
            for (const cb of B.cuts) {
              const b = cb.length;
              if (a <= b + 0.01 || B.usedLen - b + a > capacity + 1e-6) continue; // (B,A) covers the other direction
              const d = cost(A.usedLen - a + b) + cost(B.usedLen - b + a) - base;
              if (d < -1e-9 && (!best || d < best.d)) best = { d, A, B, a, b };
            }
          }
        }
        if (!best) break;
        take(best.A, best.a); put(best.B, best.a);
        if (best.b != null) { take(best.B, best.b); put(best.A, best.b); }
        bins = bins.filter(b => b.cuts.length > 0);
      }
      return bins;
    };

    if (mode !== 'minMaterial') return rebalance(ffdPack(pieces, maxLen), maxLen);

    // minMaterial: group by width, try 3 strategies per group
    const byWidth = new Map();
    for (const p of pieces) {
      const key = p.width.toFixed(2);
      if (!byWidth.has(key)) byWidth.set(key, []);
      byWidth.get(key).push(p);
    }

    const totalPurchase = (bins) => bins.reduce((s, b) => s + Math.max(minLen, Math.ceil(b.usedLen)), 0);

    const allBins = [];
    for (const [, gPieces] of byWidth) {
      const w = gPieces[0].width;

      // Helper to build bins with cut tracking for a given ordering + strategy
      const buildBins = (items, strategy) => {
        const bins = [];
        if (strategy === 'ascending') {
          let cur = null;
          for (const p of items) {
            if (!cur) cur = { width: w, remaining: maxLen, usedLen: 0, cuts: [] };
            if (cur.remaining >= p.length) {
              cur.remaining -= p.length;
              cur.usedLen += p.length;
              const ex = cur.cuts.find(c => Math.abs(c.length - p.length) < 0.01);
              if (ex) ex.count++; else cur.cuts.push({ length: p.length, count: 1 });
              if (cur.usedLen >= minLen) { bins.push(cur); cur = null; }
            } else {
              if (cur.usedLen > 0) bins.push(cur);
              cur = { width: w, remaining: maxLen - p.length, usedLen: p.length, cuts: [{ length: p.length, count: 1 }] };
              if (cur.usedLen >= minLen) { bins.push(cur); cur = null; }
            }
          }
          if (cur && cur.usedLen > 0) bins.push(cur);
        } else if (strategy === 'ffd') {
          for (const p of items) {
            let placed = false;
            for (const bin of bins) {
              if (bin.remaining >= p.length) {
                bin.remaining -= p.length;
                bin.usedLen += p.length;
                const ex = bin.cuts.find(c => Math.abs(c.length - p.length) < 0.01);
                if (ex) ex.count++; else bin.cuts.push({ length: p.length, count: 1 });
                placed = true; break;
              }
            }
            if (!placed) bins.push({ width: w, remaining: maxLen - p.length, usedLen: p.length, cuts: [{ length: p.length, count: 1 }] });
          }
        } else { // balanced
          const sorted = [...items].sort((a, b) => a.length - b.length);
          let lo = 0, hi = sorted.length - 1;
          while (lo <= hi) {
            const bin = { width: w, remaining: 0, usedLen: 0, cuts: [] };
            if (lo === hi) {
              bin.usedLen = sorted[lo].length; bin.remaining = maxLen - bin.usedLen;
              bin.cuts.push({ length: sorted[lo].length, count: 1 });
              lo++;
            } else if (sorted[lo].length + sorted[hi].length <= maxLen) {
              bin.usedLen = sorted[lo].length + sorted[hi].length;
              bin.remaining = maxLen - bin.usedLen;
              if (Math.abs(sorted[lo].length - sorted[hi].length) < 0.01) {
                bin.cuts.push({ length: sorted[lo].length, count: 2 });
              } else {
                bin.cuts.push({ length: sorted[hi].length, count: 1 });
                bin.cuts.push({ length: sorted[lo].length, count: 1 });
              }
              lo++; hi--;
            } else {
              bin.usedLen = sorted[hi].length; bin.remaining = maxLen - bin.usedLen;
              bin.cuts.push({ length: sorted[hi].length, count: 1 });
              hi--;
            }
            bins.push(bin);
          }
        }
        return bins;
      };

      const asc = [...gPieces].sort((a, b) => a.length - b.length);
      const desc = [...gPieces].sort((a, b) => b.length - a.length);
      const results = [buildBins(asc, 'ascending'), buildBins(desc, 'ffd'), buildBins(gPieces, 'balanced')];

      let best = results[0], bestT = totalPurchase(results[0]);
      for (let i = 1; i < results.length; i++) {
        const t = totalPurchase(results[i]);
        if (t < bestT) { bestT = t; best = results[i]; }
      }
      allBins.push(...rebalance(best, maxLen));
    }
    return allBins;
  }

  // ── Piece Length Calculator ──────────────

  _computePieceLengths(totalRun, product) {
    const maxLen = product.maxLengthFt;
    const minPiece = product.minPieceLengthFt || 5;

    // CASE 1 — Single piece, no head seam needed
    if (totalRun <= maxLen) return [totalRun];

    const numPieces = Math.ceil(totalRun / maxLen);

    // CASE 2 — Two pieces: target 1/3-2/3 split for seam pattern
    if (numPieces === 2) {
      const shortTarget = totalRun / 3;
      const longTarget = 2 * totalRun / 3;
      // Valid when short >= minPiece AND long <= maxLen
      if (shortTarget >= minPiece && longTarget <= maxLen) {
        return [shortTarget, longTarget]; // [1/3, 2/3]
      }
      // Fallback (rooms ~52-70 ft): clamp short piece
      const shortPiece = Math.max(minPiece, totalRun - maxLen);
      const longPiece = totalRun - shortPiece;
      return [shortPiece, longPiece];
    }

    // CASE 3 — Three+ pieces: equal split for roll consistency
    const equalLen = totalRun / numPieces;
    if (equalLen >= minPiece && equalLen <= maxLen) {
      return Array(numPieces).fill(equalLen);
    }

    // Fill (N-1) at maxLen, remainder gets what's left
    const remainder = totalRun - (numPieces - 1) * maxLen;
    if (remainder >= minPiece) {
      const pieces = Array(numPieces - 1).fill(maxLen);
      pieces.push(remainder);
      return pieces;
    }

    // Redistribute: shorten each full piece so remainder >= minPiece
    const deficit = minPiece - remainder;
    const reduceEach = Math.ceil((deficit / (numPieces - 1)) * 100) / 100;
    const adjustedFull = maxLen - reduceEach;
    const adjustedRemainder = totalRun - (numPieces - 1) * adjustedFull;
    const pieces = Array(numPieces - 1).fill(adjustedFull);
    pieces.push(adjustedRemainder);
    return pieces;
  }

  // ── Polygon Rotation Helpers ──

  _getPolygonCentroid(poly) {
    let cx = 0, cy = 0;
    for (const p of poly) { cx += p.x; cy += p.y; }
    return { x: cx / poly.length, y: cy / poly.length };
  }

  _rotatePoint(p, angle, center) {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    return {
      x: center.x + (p.x - center.x) * cos - (p.y - center.y) * sin,
      y: center.y + (p.x - center.x) * sin + (p.y - center.y) * cos
    };
  }

  _rotatePolygon(poly, angle, cx, cy) {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    return poly.map(p => ({
      x: cx + (p.x - cx) * cos - (p.y - cy) * sin,
      y: cy + (p.x - cx) * sin + (p.y - cy) * cos
    }));
  }

  // ── Vertical Scanline (room-shape-aware per-column coverage) ──

  _verticalScanlineSegments(x, roomPoly, exclusionPolys) {
    // For a vertical line at x=X, find Y-ranges where the room exists
    const getYHits = (poly) => {
      const hits = [];
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y;
        const xj = poly[j].x, yj = poly[j].y;
        if ((xi < x && xj >= x) || (xj < x && xi >= x)) {
          const t = (x - xi) / (xj - xi);
          hits.push(yi + t * (yj - yi));
        }
      }
      return hits.sort((a, b) => a - b);
    };

    // Room segments via even-odd pairing
    const roomHits = getYHits(roomPoly);
    let segments = [];
    for (let i = 0; i + 1 < roomHits.length; i += 2) {
      segments.push({ yMin: roomHits[i], yMax: roomHits[i + 1] });
    }
    if (segments.length === 0) return segments;

    // Subtract each exclusion polygon (cutouts + holes)
    for (const excl of (exclusionPolys || [])) {
      if (!excl || excl.length < 3) continue;
      const exHits = getYHits(excl);
      const exSegs = [];
      for (let i = 0; i + 1 < exHits.length; i += 2) {
        exSegs.push({ yMin: exHits[i], yMax: exHits[i + 1] });
      }
      if (exSegs.length === 0) continue;
      const next = [];
      for (const seg of segments) {
        let parts = [seg];
        for (const ex of exSegs) {
          const surviving = [];
          for (const p of parts) {
            if (ex.yMax <= p.yMin || ex.yMin >= p.yMax) { surviving.push(p); continue; }
            if (ex.yMin > p.yMin) surviving.push({ yMin: p.yMin, yMax: ex.yMin });
            if (ex.yMax < p.yMax) surviving.push({ yMin: ex.yMax, yMax: p.yMax });
          }
          parts = surviving;
        }
        next.push(...parts);
      }
      segments = next;
    }

    return segments.sort((a, b) => a.yMin - b.yMin);
  }

  _mergeCloseSegments(segments, gapThreshold) {
    if (segments.length <= 1) return segments.map(s => ({ ...s, bridgedGaps: [] }));
    const sorted = segments.slice().sort((a, b) => a.yMin - b.yMin);
    const merged = [{ yMin: sorted[0].yMin, yMax: sorted[0].yMax, bridgedGaps: [] }];
    for (let i = 1; i < sorted.length; i++) {
      const prev = merged[merged.length - 1];
      const gap = sorted[i].yMin - prev.yMax;
      if (gap > 0 && gap < gapThreshold) {
        prev.bridgedGaps.push({ yMin: prev.yMax, yMax: sorted[i].yMin });
        prev.yMax = sorted[i].yMax;
      } else {
        merged.push({ yMin: sorted[i].yMin, yMax: sorted[i].yMax, bridgedGaps: [] });
      }
    }
    return merged;
  }

  _unionIntervals(segments) {
    if (segments.length <= 1) return segments.slice();
    const sorted = segments.slice().sort((a, b) => a.yMin - b.yMin);
    const merged = [{ yMin: sorted[0].yMin, yMax: sorted[0].yMax }];
    for (let i = 1; i < sorted.length; i++) {
      const prev = merged[merged.length - 1];
      if (sorted[i].yMin <= prev.yMax) {
        prev.yMax = Math.max(prev.yMax, sorted[i].yMax);
      } else {
        merged.push({ yMin: sorted[i].yMin, yMax: sorted[i].yMax });
      }
    }
    return merged;
  }

  // Liang-Barsky line segment clip to axis-aligned rectangle
  _clipLineToRect(x1, y1, x2, y2, xmin, ymin, xmax, ymax) {
    const dx = x2 - x1, dy = y2 - y1;
    const p = [-dx, dx, -dy, dy];
    const q = [x1 - xmin, xmax - x1, y1 - ymin, ymax - y1];
    let tmin = 0, tmax = 1;
    for (let i = 0; i < 4; i++) {
      if (p[i] === 0) {
        if (q[i] < 0) return null;
      } else {
        const t = q[i] / p[i];
        if (p[i] < 0) tmin = Math.max(tmin, t);
        else tmax = Math.min(tmax, t);
      }
    }
    if (tmin > tmax) return null;
    return {
      x1: x1 + tmin * dx, y1: y1 + tmin * dy,
      x2: x1 + tmax * dx, y2: y1 + tmax * dy
    };
  }

  // ── Roll Layout Anchor ────────────────
  // Candidate starts put a strip edge flush on every wall that runs WITH the rolls (a vertical
  // edge in rotated space); the outermost wall is today's start and wins every tie, so a room
  // that gains nothing keeps its layout. Rolls never turn — only the starting edge moves.
  // Scored on what the customer buys: Minimize Head Seams ranks head seams first, then material;
  // Minimize Material ranks material first. Fewer strips (side seams) breaks a remaining tie.
  _pickRollAnchor(room, geo, product, rotatedPoly, rotatedExclusions, poly) {
    const w = product.widthFt;
    let minX = Infinity;
    for (const p of poly) minX = Math.min(minX, p.x);
    const layoutAt = (sx) => this._computeRollLayout(room, geo, product, rotatedPoly, rotatedExclusions, null, sx);
    const best = { layout: layoutAt(minX) };
    if (!(w > 0)) return best.layout;

    const phases = [];
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length];
      if (Math.abs(a.x - b.x) > 1e-6 || Math.abs(a.y - b.y) < 1e-6) continue;
      // Shift the edge back by whole strip widths until it sits at or before the outermost wall
      const sx = a.x - Math.ceil((a.x - minX) / w - 1e-9) * w;
      if (Math.abs(sx - minX) < 1e-4 || Math.abs(sx + w - minX) < 1e-4) continue;
      if (phases.some(q => Math.abs(q - sx) < 1e-4)) continue;
      phases.push(sx);
      if (phases.length >= 12) break;
    }
    if (phases.length === 0) return best.layout;

    const minLen = product.minLengthFt || 25;
    const maxLen = product.maxLengthFt || 50;
    const mode = this._rollOptimizationMode;
    const score = (layout) => {
      const pieces = [];
      let headSeams = 0;
      for (const strip of layout.strips) {
        for (const roll of strip.rolls) pieces.push({ width: strip.width, length: roll.naturalLength != null ? roll.naturalLength : roll.length });
        for (const seg of strip.segments) headSeams += Math.max(0, seg.rolls.length - 1);
      }
      const bins = this._binPackRollPieces(pieces, maxLen, minLen, mode);
      const area = bins.reduce((s, b) => s + b.width * Math.max(minLen, Math.ceil(b.usedLen)), 0);
      return mode === 'minMaterial'
        ? [area, headSeams, layout.strips.length]
        : [headSeams, area, layout.strips.length];
    };
    const better = (a, b) => {
      for (let i = 0; i < a.length; i++) {
        if (a[i] < b[i] - 1e-6) return true;
        if (a[i] > b[i] + 1e-6) return false;
      }
      return false;
    };
    best.score = score(best.layout);
    for (const sx of phases) {
      const layout = layoutAt(sx);
      const s = score(layout);
      if (better(s, best.score)) { best.layout = layout; best.score = s; }
    }
    return best.layout;
  }

  // ── Roll Layout Algorithm ────────────────

  _computeRollLayout(room, geo, product, rotatedPoly, rotatedExclusions, gridOrigin, forcedStartX) {
    const poly = rotatedPoly || geo.roomPolygon;
    if (!poly || poly.length < 3) return { strips: [], roomMinY: 0, roomMaxY: 0, roomMinX: 0, roomMaxX: 0 };

    // Where the first strip starts is a choice, not a given: anchored on the outermost wall,
    // an 8" nook pushes every strip 8" over and the far side needs a whole extra
    // full-length strip. Try a strip edge on each wall line and keep the cheapest.
    // Shared zone grids (gridOrigin) and Uniform mode keep the fixed start.
    if (forcedStartX == null && !gridOrigin && this._rollOptimizationMode !== 'uniform') {
      return this._pickRollAnchor(room, geo, product, rotatedPoly, rotatedExclusions, poly);
    }

    const stripWidth = product.widthFt;
    const overage = product.overageFt || 0.5; // 6" total
    const exclusions = rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];

    // Bounding box
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of poly) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }

    // Grid-aligned start: use room-level origin for cross-zone strip alignment
    const startX = forcedStartX != null ? forcedStartX
      : gridOrigin
        ? gridOrigin.minX + Math.floor((minX - gridOrigin.minX) / stripWidth) * stripWidth
        : minX;
    const strips = [];
    let anyHeadSeams = false;
    let prevColSeamYs = []; // absolute Y positions of seams in previous column
    let evenTargetSeamY = null; // alignment target so even-column seams line up
    let oddTargetSeamY = null;  // alignment target so odd-column seams line up

    // 1e-6: a strip edge landing on the far wall plus a float crumb must not start a sliver strip
    for (let x = startX; x < maxX - 1e-6; x += stripWidth) {
      const colIndex = Math.round((x - startX) / stripWidth);

      // Multi-point scanline: sample left, center, right, plus near polygon
      // vertex X-coords within this strip to catch narrow features (e.g. 1' protrusions)
      const eps = 0.01;
      const scanXs = new Set();
      scanXs.add(x + eps);
      scanXs.add(x + stripWidth / 2);
      scanXs.add(Math.min(x + stripWidth - eps, maxX - eps));
      // Add scanlines near polygon vertex X-coords within this strip
      for (const p of poly) {
        if (p.x > x + eps * 2 && p.x < x + stripWidth - eps * 2) {
          scanXs.add(p.x - eps);
          scanXs.add(p.x + eps);
        }
      }
      for (const excl of exclusions) {
        if (!excl) continue;
        for (const p of excl) {
          if (p.x > x + eps * 2 && p.x < x + stripWidth - eps * 2) {
            scanXs.add(p.x - eps);
            scanXs.add(p.x + eps);
          }
        }
      }
      const allSegs = [];
      for (const sx of scanXs) allSegs.push(...this._verticalScanlineSegments(sx, poly, exclusions));
      const rawSegs = this._unionIntervals(allSegs);
      if (rawSegs.length === 0) continue; // No room at this column

      // Merge close segments (bridge small gaps < 3')
      const merged = this._mergeCloseSegments(rawSegs, 3);

      // Per merged segment: compute rolls
      const segments = [];
      const flatRolls = [];
      const currentColSeamYs = [];

      for (const seg of merged) {
        const segHeight = seg.yMax - seg.yMin;
        const totalRun = segHeight + overage; // 6" overage on start wall only
        let pieceLengths = this._computePieceLengths(totalRun, product);

        const numPieces = pieceLengths.length;
        const hasHeadSeams = numPieces > 1;
        if (hasHeadSeams) anyHeadSeams = true;

        let yStart = seg.yMin - overage;

        // ── Head seam stagger: enforce 48" minimum between adjacent columns ──
        // Uses 1/3-2/3 alternation as the preferred pattern, but checks actual
        // seam Y positions against the previous column and adjusts if needed.
        const MIN_STAGGER_FT = 4; // 48 inches
        let staggeredPieces;

        if (numPieces <= 1) {
          staggeredPieces = pieceLengths;
        } else if (numPieces === 2) {
          const maxLen = product.maxLengthFt;
          const minPiece = product.minPieceLengthFt || 5;

          // Valid seam Y range (absolute coordinates)
          const validMin = yStart + Math.max(minPiece, totalRun - maxLen);
          const validMax = yStart + Math.min(maxLen, totalRun - minPiece);

          // Target seam positions: 1/3 and 2/3 of totalRun
          const target1 = yStart + totalRun / 3;
          const target2 = yStart + 2 * totalRun / 3;

          // Preferred position alternates by column index
          const preferred = colIndex % 2 === 0 ? target1 : target2;
          const alternate = colIndex % 2 === 0 ? target2 : target1;

          // Check if a seam Y clears all previous column seams by MIN_STAGGER_FT
          const clearsStagger = (seamY) => {
            for (const ps of prevColSeamYs) {
              if (Math.abs(seamY - ps) < MIN_STAGGER_FT) return false;
            }
            return true;
          };

          let chosenSeamY;
          if (clearsStagger(preferred)) {
            chosenSeamY = preferred;
          } else if (clearsStagger(alternate)) {
            chosenSeamY = alternate;
          } else {
            // Neither 1/3 nor 2/3 works — find best Y that maximizes
            // minimum distance from all previous seams
            const candidates = [validMin, validMax, preferred, alternate];
            for (const ps of prevColSeamYs) {
              candidates.push(ps + MIN_STAGGER_FT, ps - MIN_STAGGER_FT);
            }

            let bestY = preferred;
            let bestDist = -1;
            for (const c of candidates) {
              const clamped = Math.max(validMin, Math.min(validMax, c));
              const minDist = prevColSeamYs.length > 0
                ? Math.min(...prevColSeamYs.map(ps => Math.abs(clamped - ps)))
                : Infinity;
              if (minDist > bestDist) {
                bestDist = minDist;
                bestY = clamped;
              }
            }
            chosenSeamY = bestY;
          }

          // Clamp to valid range
          chosenSeamY = Math.max(validMin, Math.min(validMax, chosenSeamY));

          // ── Same-parity alignment: add up to 1 ft material so seams line up ──
          const isEven = colIndex % 2 === 0;
          const parityTarget = isEven ? evenTargetSeamY : oddTargetSeamY;
          if (parityTarget !== null && Math.abs(chosenSeamY - parityTarget) <= 1.0) {
            const snapped = Math.max(validMin, Math.min(validMax, parityTarget));
            if (clearsStagger(snapped)) chosenSeamY = snapped;
          } else if (parityTarget === null) {
            if (isEven) evenTargetSeamY = chosenSeamY;
            else oddTargetSeamY = chosenSeamY;
          }

          // Derive piece lengths from the chosen seam position
          const firstPiece = chosenSeamY - yStart;
          const secondPiece = totalRun - firstPiece;
          staggeredPieces = [firstPiece, secondPiece];
        } else {
          // 3+ pieces: compute base seam positions, shift odd columns by half-piece
          const shiftAmount = MIN_STAGGER_FT;
          if (colIndex % 2 === 0) {
            staggeredPieces = pieceLengths;
          } else {
            const maxLen = product.maxLengthFt;
            const minPiece = product.minPieceLengthFt || 5;
            const first = pieceLengths[0] + shiftAmount;
            const last = pieceLengths[numPieces - 1] - shiftAmount;
            if (first <= maxLen && last >= minPiece) {
              staggeredPieces = [first, ...pieceLengths.slice(1, -1), last];
            } else {
              staggeredPieces = pieceLengths;
            }
          }
        }

        // ── Round all pieces UP to nearest half-foot (6" overage max) ──
        staggeredPieces = staggeredPieces.map(p => this._ceilHalfFoot(p));

        // Anchor rolls to south wall so all rounding excess extends north (single-end overage)
        let roundedTotal = staggeredPieces.reduce((a, b) => a + b, 0);
        yStart = seg.yMax - roundedTotal;

        // ── Post-rounding re-validation: fix seams that drifted past 48" rule ──
        if (staggeredPieces.length >= 2 && prevColSeamYs.length > 0) {
          const maxLen = product.maxLengthFt;
          const minPiece = product.minPieceLengthFt || 5;
          let cum = yStart;
          for (let si = 0; si < staggeredPieces.length - 1; si++) {
            cum += staggeredPieces[si];
            const seamY = cum;
            let violated = false;
            for (const ps of prevColSeamYs) {
              if (Math.abs(seamY - ps) < MIN_STAGGER_FT) { violated = true; break; }
            }
            if (!violated) continue;

            // Try nudging seam by ±0.5 to ±5 ft (half-foot steps) to clear stagger
            let fixed = false;
            for (let nudge = 0.5; nudge <= 5 && !fixed; nudge += 0.5) {
              for (const sign of [1, -1]) {
                const dir = sign * nudge;
                const newA = staggeredPieces[si] + dir;
                const newB = staggeredPieces[si + 1] - dir;
                if (newA < minPiece || newA > maxLen || newB < minPiece || newB > maxLen) continue;
                const newSeamY = seamY + dir;
                let clears = true;
                for (const ps of prevColSeamYs) {
                  if (Math.abs(newSeamY - ps) < MIN_STAGGER_FT) { clears = false; break; }
                }
                if (clears) {
                  staggeredPieces[si] = newA;
                  staggeredPieces[si + 1] = newB;
                  cum = seamY + dir;
                  fixed = true;
                  break;
                }
              }
            }
          }
          // Recompute yStart in case nudges changed total (they shouldn't, but be safe)
          roundedTotal = staggeredPieces.reduce((a, b) => a + b, 0);
          yStart = seg.yMax - roundedTotal;
        }

        // Record seam positions from rounded pieces (for next column's stagger check)
        if (staggeredPieces.length >= 2) {
          let cum = yStart;
          for (let i = 0; i < staggeredPieces.length - 1; i++) {
            cum += staggeredPieces[i];
            currentColSeamYs.push(cum);
          }
        }

        const rolls = [];
        let y = yStart;
        for (const pieceLen of staggeredPieces) {
          rolls.push({ y, length: pieceLen });
          y += pieceLen;
        }

        // Compute valid seam range for repair pass (only meaningful for 2+ pieces)
        let segValidMin, segValidMax;
        if (staggeredPieces.length >= 2) {
          const _maxLen = product.maxLengthFt;
          const _minP = product.minPieceLengthFt || 5;
          segValidMin = yStart + Math.max(_minP, roundedTotal - _maxLen);
          segValidMax = yStart + Math.min(_maxLen, roundedTotal - _minP);
        }

        segments.push({
          segMinY: seg.yMin,
          segMaxY: seg.yMax,
          bridgedGaps: seg.bridgedGaps || [],
          rolls,
          pieceLengths: staggeredPieces,
          hasHeadSeams,
          yStart,
          validMin: segValidMin,
          validMax: segValidMax
        });
        flatRolls.push(...rolls);
      }

      if (currentColSeamYs.length > 0) {
        prevColSeamYs = currentColSeamYs;
      }

      strips.push({
        x,
        colIndex,
        width: stripWidth,
        segments,
        rolls: flatRolls // flattened for backward compat (Layers 1-3, measurements)
      });
    }

    // Mark last strip as rip-strip if it extends past room right edge
    if (strips.length > 0) {
      const last = strips[strips.length - 1];
      if (last.x + last.width > maxX + 0.001) {
        last.isRipStrip = true;
        last.effectiveWidth = maxX - last.x;
      }
    }

    // ── Post-layout repair pass: fix any remaining stagger violations ──
    this._repairSeamStagger(strips, product);

    // ── Final half-foot cleanup: ceil all pieces to 6", re-anchor, rebuild rolls ──
    for (const strip of strips) {
      for (const seg of strip.segments) {
        if (seg.pieceLengths.length < 2) continue;
        if (seg.pieceLengths.every(p => p === this._ceilHalfFoot(p))) continue;
        const origLengths = seg.pieceLengths.slice();
        seg.pieceLengths = seg.pieceLengths.map(p => this._ceilHalfFoot(p));
        const rt = seg.pieceLengths.reduce((a, b) => a + b, 0);
        seg.yStart = seg.segMaxY - rt;
        seg.rolls = [];
        let y = seg.yStart;
        for (let ri = 0; ri < seg.pieceLengths.length; ri++) {
          const pl = seg.pieceLengths[ri];
          seg.rolls.push({ y, length: pl, inRoomLength: origLengths[ri] });
          y += pl;
        }
      }
      strip.rolls = [];
      for (const s of strip.segments) strip.rolls.push(...s.rolls);
    }

    // ── Minimize Material mode: try forced column splits ──
    if (this._rollOptimizationMode === 'minMaterial' && strips.length > 0) {
      const _minPiece = product.minPieceLengthFt || 5;
      const _maxLen = product.maxLengthFt;
      const _minLen = product.minLengthFt || 25;

      // Collect piece lengths from normal layout
      const normalPieces = [];
      for (const strip of strips) {
        for (const seg of strip.segments) {
          for (const pl of seg.pieceLengths) normalPieces.push(pl);
        }
      }

      // Create split variant: force 2-piece split on single-piece columns > 2×minPiece
      const splitPieces = [];
      let anySplittable = false;
      for (const strip of strips) {
        for (const seg of strip.segments) {
          if (seg.pieceLengths.length === 1 && seg.pieceLengths[0] > 2 * _minPiece) {
            const total = seg.pieceLengths[0];
            const isEven = strip.colIndex % 2 === 0;
            const shortRaw = isEven ? total / 3 : 2 * total / 3;
            const short = this._ceilHalfFoot(shortRaw);
            const long = this._ceilHalfFoot(total - short);
            if (short >= _minPiece && long <= _maxLen && long >= _minPiece) {
              splitPieces.push(short, long);
              anySplittable = true;
            } else {
              splitPieces.push(total);
            }
          } else {
            splitPieces.push(...seg.pieceLengths);
          }
        }
      }

      // Compare: keep split only if it reduces total purchase
      if (anySplittable) {
        const normalTotal = this._estimateBestPurchase(normalPieces, _maxLen, _minLen);
        const splitTotal = this._estimateBestPurchase(splitPieces, _maxLen, _minLen);

        if (splitTotal < normalTotal) {
          // Apply splits to the actual strips
          for (const strip of strips) {
            for (const seg of strip.segments) {
              if (seg.pieceLengths.length === 1 && seg.pieceLengths[0] > 2 * _minPiece) {
                const total = seg.pieceLengths[0];
                const isEven = strip.colIndex % 2 === 0;
                const shortRaw = isEven ? total / 3 : 2 * total / 3;
                const short = this._ceilHalfFoot(shortRaw);
                const long = this._ceilHalfFoot(total - short);
                if (short >= _minPiece && long <= _maxLen && long >= _minPiece) {
                  seg.pieceLengths = [short, long];
                  seg.hasHeadSeams = true;
                  anyHeadSeams = true;
                  const rt = short + long;
                  seg.yStart = seg.segMaxY - rt;
                  seg.rolls = [];
                  let y = seg.yStart;
                  for (const pl of seg.pieceLengths) {
                    seg.rolls.push({ y, length: pl });
                    y += pl;
                  }
                }
              }
            }
            strip.rolls = [];
            for (const s of strip.segments) strip.rolls.push(...s.rolls);
          }
        }
      }
    }

    // ── Uniform roll length extension with 1/3 running-bond stagger ──
    if (this._rollOptimizationMode === 'uniform' && strips.length > 0) {
      let maxPieceLen = 0;
      const _segTotals = [];
      for (const strip of strips) {
        for (const seg of strip.segments) {
          const segTotal = seg.pieceLengths.reduce((a, b) => a + b, 0);
          _segTotals.push(segTotal);
          for (const pl of seg.pieceLengths) {
            if (pl > maxPieceLen) maxPieceLen = pl;
          }
        }
      }
      let uniformLen;
      if (this._uniformTargetLen > 0) {
        uniformLen = this._uniformTargetLen;
      } else {
        // Auto: pick the length with least overage
        uniformLen = this._pickLeastOverageLen(_segTotals, product);
      }

      for (let si = 0; si < strips.length; si++) {
        const strip = strips[si];
        // 1/3 running bond: columns cycle offsets 0, L/3, 2L/3
        const staggerPhase = si % 3;
        const staggerOffset = staggerPhase * (uniformLen / 3);

        for (const seg of strip.segments) {
          const naturalLengths = seg.pieceLengths.slice();
          const totalNatural = naturalLengths.reduce((a, b) => a + b, 0);
          seg._preUniformTotal = totalNatural; // preserve for target length options

          // Re-split: if target is shorter than the segment's natural total, we need more pieces
          let N;
          if (totalNatural > uniformLen) {
            N = Math.ceil(totalNatural / uniformLen);
          } else {
            N = seg.pieceLengths.length;
          }

          // Build staggered piece lengths
          let newPieces;
          if (staggerOffset > 0 && N > 1) {
            // Split: first piece shortened by offset, tail piece added at end
            newPieces = [uniformLen - staggerOffset];
            for (let i = 1; i < N; i++) newPieces.push(uniformLen);
            newPieces.push(staggerOffset);
          } else {
            newPieces = new Array(N).fill(uniformLen);
          }

          seg.pieceLengths = newPieces;
          const rt = newPieces.reduce((a, b) => a + b, 0);
          seg.yStart = seg.segMaxY - rt;

          // Rebuild rolls — distribute natural lengths proportionally
          seg.rolls = [];
          let y = seg.yStart;
          for (let i = 0; i < newPieces.length; i++) {
            const nl = rt > 0 ? totalNatural * (newPieces[i] / rt) : 0;
            seg.rolls.push({ y, length: newPieces[i], naturalLength: nl, inRoomLength: nl });
            y += newPieces[i];
          }

          // Mark segments with multiple pieces so repair pass can fix stagger violations
          if (newPieces.length > 1) {
            seg.hasHeadSeams = true;
            anyHeadSeams = true;
          }
        }
        strip.rolls = [];
        for (const s of strip.segments) strip.rolls.push(...s.rolls);
      }

      // Re-run stagger repair — the uniform 1/3 running-bond pattern is purely
      // mathematical and may place adjacent seams within 4 ft of each other
      // for certain room dimensions. The repair pass nudges only violating seams.
      this._repairSeamStagger(strips, product);

      // Update naturalLength on any rolls the repair pass may have nudged
      for (const strip of strips) {
        for (const seg of strip.segments) {
          if (!seg.hasHeadSeams || !seg._preUniformTotal) continue;
          const rt = seg.pieceLengths.reduce((a, b) => a + b, 0);
          if (rt <= 0) continue;
          for (let i = 0; i < seg.rolls.length; i++) {
            seg.rolls[i].naturalLength = seg._preUniformTotal * (seg.pieceLengths[i] / rt);
            seg.rolls[i].inRoomLength = seg.rolls[i].naturalLength;
          }
        }
      }

      for (const strip of strips) {
        for (const seg of strip.segments) {
          if (seg.yStart < minY) minY = seg.yStart;
        }
      }
    }

    // ── Purchase-length extension: show true waste per purchased roll ──
    const minLen = product.minLengthFt;
    if (minLen > 0) {
      // Save natural lengths before extension — material summary needs pre-extension cut lengths.
      // If naturalLength was already set (e.g. by uniform stagger pass), preserve it;
      // otherwise use the current piece length as the natural cut length.
      for (const strip of strips) {
        for (const seg of strip.segments) {
          seg._naturalLengths = seg.rolls.map((r, i) =>
            r.naturalLength != null ? r.naturalLength : seg.pieceLengths[i]
          );
        }
      }

      // Roll sharing (rep): a borrowing space buys no rolls, so it shows no leftover;
      // the lead space packs every member's pieces and the leftover lands on its own.
      const _share = this._rollShareLayoutRole(room, geo);
      if (_share && _share.mode === 'borrow') {
        // pieces stay at cut length
      } else if (this._rollOptimizationMode !== 'uniform') {
        // Bin-pack-aware: distribute waste from shared rolls to specific pieces
        const allPieces = [];
        for (let si = 0; si < strips.length; si++) {
          for (let gi = 0; gi < strips[si].segments.length; gi++) {
            const seg = strips[si].segments[gi];
            for (let pi = 0; pi < seg.pieceLengths.length; pi++) {
              allPieces.push({ width: strips[si].width, length: seg.pieceLengths[pi], si, gi, pi });
            }
          }
        }
        if (_share && _share.mode === 'lead') {
          for (const op of this._rollShareOtherPieces(_share)) allPieces.push({ width: op.width, length: op.length, si: -1 });
        }

        const maxLenFt = product.maxLengthFt;
        const bins = this._binPackRollPieces(allPieces, maxLenFt, minLen, this._rollOptimizationMode);
        const used = new Array(allPieces.length).fill(false);

        for (const bin of bins) {
          const purchaseLen = Math.max(minLen, Math.ceil(bin.usedLen));
          const waste = purchaseLen - bin.usedLen;
          if (waste < 0.01) continue;

          // Match cuts back to pieces, find first piece to extend
          let targetIdx = -1;
          for (const cut of bin.cuts) {
            let rem = cut.count;
            for (let i = 0; i < allPieces.length && rem > 0; i++) {
              if (!used[i] && Math.abs(allPieces[i].length - cut.length) < 0.01) {
                used[i] = true;
                if (targetIdx < 0 && allPieces[i].si >= 0) targetIdx = i;
                rem--;
              }
            }
          }
          if (targetIdx < 0) continue;

          // Extend the target piece by the waste amount
          const ref = allPieces[targetIdx];
          strips[ref.si].segments[ref.gi].pieceLengths[ref.pi] += waste;
        }

        // Rebuild all rolls after extensions (preserve naturalLength from pre-extension)
        for (const strip of strips) {
          for (const seg of strip.segments) {
            const nat = seg._naturalLengths;
            const rt = seg.pieceLengths.reduce((a, b) => a + b, 0);
            seg.yStart = seg.segMaxY - rt;
            seg.rolls = [];
            let y = seg.yStart;
            for (let i = 0; i < seg.pieceLengths.length; i++) {
              seg.rolls.push({ y, length: seg.pieceLengths[i], naturalLength: nat[i] });
              y += seg.pieceLengths[i];
            }
          }
          strip.rolls = [];
          for (const s of strip.segments) strip.rolls.push(...s.rolls);
        }
      } else {
        // Uniform only: simple per-segment extension (each column = its own fixed-length roll)
        for (const strip of strips) {
          for (const seg of strip.segments) {
            const totalCut = seg.pieceLengths.reduce((a, b) => a + b, 0);
            if (totalCut >= minLen) continue;
            const nat = seg._naturalLengths;
            const extra = minLen - totalCut;
            seg.pieceLengths[0] += extra;
            const rt = seg.pieceLengths.reduce((a, b) => a + b, 0);
            seg.yStart = seg.segMaxY - rt;
            seg.rolls = [];
            let y = seg.yStart;
            for (let i = 0; i < seg.pieceLengths.length; i++) {
              seg.rolls.push({ y, length: seg.pieceLengths[i], naturalLength: nat[i] });
              y += seg.pieceLengths[i];
            }
          }
          strip.rolls = [];
          for (const s of strip.segments) strip.rolls.push(...s.rolls);
        }
      }

      // Update minY after extensions (both paths)
      for (const strip of strips) {
        for (const seg of strip.segments) {
          if (seg.yStart < minY) minY = seg.yStart;
        }
      }
    }

    // ── Compute geometric inRoomLength for every roll ──
    // Each roll's in-room portion = overlap of [roll.y, roll.y+length] with [segMinY, segMaxY]
    for (const strip of strips) {
      for (const seg of strip.segments) {
        for (const roll of seg.rolls) {
          const clampedStart = Math.max(roll.y, seg.segMinY);
          const clampedEnd = Math.min(roll.y + roll.length, seg.segMaxY);
          roll.inRoomLength = Math.max(0, clampedEnd - clampedStart);
        }
      }
    }

    return { strips, hasHeadSeams: anyHeadSeams, roomMinY: minY, roomMaxY: maxY, roomMinX: minX, roomMaxX: maxX };
  }

  // ── Seam Stagger Repair ────────────────

  _getSeamYsForStrip(strip) {
    const seams = [];
    for (const seg of strip.segments) {
      for (let i = 0; i < seg.rolls.length - 1; i++) {
        seams.push(seg.rolls[i].y + seg.rolls[i].length);
      }
    }
    return seams;
  }

  _repairSeamStagger(strips, product) {
    const MIN_STAGGER = 4; // 48 inches = 4 ft
    const maxLen = product.maxLengthFt;
    const minPiece = product.minPieceLengthFt || 5;

    for (let pass = 0; pass < 3; pass++) {
      let anyFixed = false;

      for (let i = 0; i < strips.length - 1; i++) {
        const seamsA = this._getSeamYsForStrip(strips[i]);
        const seamsB = this._getSeamYsForStrip(strips[i + 1]);
        if (seamsA.length === 0 || seamsB.length === 0) continue;

        for (const sA of seamsA) {
          for (const sB of seamsB) {
            if (Math.abs(sA - sB) >= MIN_STAGGER) continue;

            // Violation — try to fix strip[i+1]'s segment containing sB
            const strip = strips[i + 1];
            let foundSeg = null, foundRollIdx = -1;
            for (const seg of strip.segments) {
              for (let r = 0; r < seg.rolls.length - 1; r++) {
                const seamY = seg.rolls[r].y + seg.rolls[r].length;
                if (Math.abs(seamY - sB) < 0.01) {
                  foundSeg = seg;
                  foundRollIdx = r;
                  break;
                }
              }
              if (foundSeg) break;
            }
            if (!foundSeg || !foundSeg.hasHeadSeams) continue;
            const seg = foundSeg;
            const rollIdx = foundRollIdx;

            // Compute per-seam valid range based on the two adjacent pieces
            const pieceA = seg.pieceLengths[rollIdx];
            const pieceB = seg.pieceLengths[rollIdx + 1];
            const diffMin = Math.max(minPiece - pieceA, pieceB - maxLen);
            const diffMax = Math.min(maxLen - pieceA, pieceB - minPiece);
            if (diffMin > diffMax) continue; // no valid range
            const seamValidMin = sB + diffMin;
            const seamValidMax = sB + diffMax;

            // Collect all neighbor seams this strip must clear
            const neighborSeams = [...seamsA];
            if (i + 2 < strips.length) {
              neighborSeams.push(...this._getSeamYsForStrip(strips[i + 2]));
            }

            // Generate candidate seam positions
            const candidates = [
              seamValidMin, seamValidMax,
              sB + MIN_STAGGER, sB - MIN_STAGGER,
              sB + MIN_STAGGER + 1, sB - MIN_STAGGER - 1
            ];
            for (const ns of neighborSeams) {
              candidates.push(ns + MIN_STAGGER, ns - MIN_STAGGER);
            }

            let bestY = null;
            let bestDist = -1;
            for (const c of candidates) {
              // Clamp to per-seam valid range and round to nearest half-foot
              let clamped = Math.max(seamValidMin, Math.min(seamValidMax, c));
              clamped = Math.round(clamped * 2) / 2;
              clamped = Math.max(seamValidMin, Math.min(seamValidMax, clamped));

              // Verify both pieces remain valid
              const newA = pieceA + (clamped - sB);
              const newB = pieceB - (clamped - sB);
              if (newA < minPiece || newA > maxLen || newB < minPiece || newB > maxLen) continue;

              const minDist = neighborSeams.length > 0
                ? Math.min(...neighborSeams.map(ns => Math.abs(clamped - ns)))
                : Infinity;
              if (minDist > bestDist) {
                bestDist = minDist;
                bestY = clamped;
              }
            }

            // Only apply if strictly better than current
            const currentDist = Math.abs(sA - sB);
            if (bestY !== null && bestDist > currentDist) {
              const diff = bestY - sB;
              seg.pieceLengths[rollIdx] = pieceA + diff;
              seg.pieceLengths[rollIdx + 1] = pieceB - diff;
              seg.rolls[rollIdx].length = pieceA + diff;
              seg.rolls[rollIdx + 1].y = seg.rolls[rollIdx].y + seg.rolls[rollIdx].length;
              seg.rolls[rollIdx + 1].length = pieceB - diff;

              // Rebuild strip's flatRolls
              strip.rolls = [];
              for (const s of strip.segments) strip.rolls.push(...s.rolls);
              anyFixed = true;
            }
          }
        }
      }

      if (!anyFixed) break;
    }
  }

  // ── Tile Layout ──────────────────────────

  _computeTileLayout(room, geo, product, rotatedPoly, rotatedExclusions, gridOrigin) {
    const poly = rotatedPoly || geo.roomPolygon;
    if (!poly || poly.length < 3) return { tiles: [], tileW: 1, tileH: 1, roomMinX: 0, roomMaxX: 0, roomMinY: 0, roomMaxY: 0 };

    const rd = this.rd;
    const tileW = product.widthFt || 2;
    const tileH = product.heightFt || tileW; // square default
    const exclusions = rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];

    // Bounding box of zone/room polygon
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of poly) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }

    // Grid-aligned start: use room-level origin for cross-zone tile alignment
    const gridMinX = gridOrigin
      ? gridOrigin.minX + Math.floor((minX - gridOrigin.minX) / tileW) * tileW
      : minX;
    const gridMinY = gridOrigin
      ? gridOrigin.minY + Math.floor((minY - gridOrigin.minY) / tileH) * tileH
      : minY;

    // Plank stagger: 50% running bond — offset along the LONG axis (tileH) per alternating column
    const isPlank = product.shape === 'plank';
    const staggerY = isPlank ? tileH / 2 : 0;

    const tiles = [];
    for (let x = gridMinX; x < maxX; x += tileW) {
      // Determine column index relative to grid origin for consistent stagger across zones
      const colIdx = gridOrigin
        ? Math.round((x - gridOrigin.minX) / tileW)
        : Math.round((x - gridMinX) / tileW);
      const colOffset = (isPlank && colIdx % 2 !== 0) ? staggerY : 0;
      // Start one plank earlier when staggered to fill the top edge
      const colStartY = gridMinY - (colOffset > 0 ? tileH : 0) + colOffset;
      for (let y = colStartY; y < maxY; y += tileH) {
        // Include tile if ANY part overlaps the room polygon
        if (!this._tileOverlapsPolygon(x, y, tileW, tileH, poly, rd)) continue;
        // Exclude tiles fully inside an exclusion (all sample points in exclusion)
        let fullyExcluded = false;
        for (const excl of exclusions) {
          if (!excl || excl.length < 3) continue;
          if (this._tileFullyInsidePolygon(x, y, tileW, tileH, excl, rd)) { fullyExcluded = true; break; }
        }
        if (fullyExcluded) continue;
        // Skip tiles whose room-overlapping area is entirely inside exclusions
        // (e.g. tile straddles turf boundary but its room-visible part is all under turf)
        if (!fullyExcluded && exclusions.length > 0) {
          const eps2 = 0.01;
          const samplePts = [
            [x + eps2, y + eps2], [x + tileW - eps2, y + eps2],
            [x + eps2, y + tileH - eps2], [x + tileW - eps2, y + tileH - eps2],
            [x + tileW / 2, y + tileH / 2],
            [x + tileW / 2, y + eps2], [x + tileW / 2, y + tileH - eps2],
            [x + eps2, y + tileH / 2], [x + tileW - eps2, y + tileH / 2]
          ];
          let hasVisibleArea = false;
          for (const [px, py] of samplePts) {
            if (!rd.pointInPolygon(px, py, poly)) continue;
            let inExcl = false;
            for (const excl of exclusions) {
              if (excl && excl.length >= 3 && rd.pointInPolygon(px, py, excl)) { inExcl = true; break; }
            }
            if (!inExcl) { hasVisibleArea = true; break; }
          }
          if (!hasVisibleArea) continue;
        }
        tiles.push({ x, y, w: tileW, h: tileH });
      }
    }

    return { tiles, tileW, tileH, roomMinX: minX, roomMaxX: maxX, roomMinY: minY, roomMaxY: maxY };
  }

  // Check if a tile rectangle overlaps a polygon (any intersection)
  _tileOverlapsPolygon(tx, ty, tw, th, poly, rd) {
    const eps = 0.01;
    // 1) Any of 5 sample points (corners + center) inside polygon → overlap
    const pts = [
      [tx + eps, ty + eps], [tx + tw - eps, ty + eps],
      [tx + eps, ty + th - eps], [tx + tw - eps, ty + th - eps],
      [tx + tw / 2, ty + th / 2]
    ];
    for (const [px, py] of pts) {
      if (rd.pointInPolygon(px, py, poly)) return true;
    }
    // 2) Any polygon vertex inside tile rect → overlap
    for (const p of poly) {
      if (p.x >= tx && p.x <= tx + tw && p.y >= ty && p.y <= ty + th) return true;
    }
    // 3) Any polygon edge crosses tile rect edges → overlap
    const edges = [
      [[tx, ty], [tx + tw, ty]], [[tx + tw, ty], [tx + tw, ty + th]],
      [[tx, ty + th], [tx + tw, ty + th]], [[tx, ty], [tx, ty + th]]
    ];
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      for (const [a, b] of edges) {
        if (this._segsIntersect(poly[j].x, poly[j].y, poly[i].x, poly[i].y, a[0], a[1], b[0], b[1])) return true;
      }
    }
    return false;
  }

  // Check if tile is fully inside a polygon (all corners inside)
  _tileFullyInsidePolygon(tx, ty, tw, th, poly, rd) {
    const eps = 0.01;
    return rd.pointInPolygon(tx + eps, ty + eps, poly)
        && rd.pointInPolygon(tx + tw - eps, ty + eps, poly)
        && rd.pointInPolygon(tx + eps, ty + th - eps, poly)
        && rd.pointInPolygon(tx + tw - eps, ty + th - eps, poly);
  }

  // Segment-segment intersection test (returns true/false)
  _segsIntersect(ax, ay, bx, by, cx, cy, dx, dy) {
    const d1x = bx - ax, d1y = by - ay;
    const d2x = dx - cx, d2y = dy - cy;
    const cross = d1x * d2y - d1y * d2x;
    if (Math.abs(cross) < 1e-10) return false;
    const t = ((cx - ax) * d2y - (cy - ay) * d2x) / cross;
    const u = ((cx - ax) * d1y - (cy - ay) * d1x) / cross;
    return t > 0 && t < 1 && u > 0 && u < 1;
  }

  // ── Paver Layout ─────────────────────────

  /**
   * Compute an interlocking dog-bone paver grid for a room/zone polygon.
   * Running-bond pattern: all pavers face the same direction, odd columns
   * offset vertically by half a paver height so the heads of one column
   * nestle into the notch gaps of adjacent columns.
   *
   * Returns { pavers: [{cx, cy, w, h, rot}], paverW, paverH, gridStep,
   *           notchDepth, headHeight, roomMinX/Y, roomMaxX/Y }
   */
  _computePaverLayout(room, geo, product, rotatedPoly, rotatedExclusions, gridOrigin) {
    const poly = rotatedPoly || geo.roomPolygon;
    if (!poly || poly.length < 3) return { pavers: [], paverW: 1, paverH: 1, gridStep: 1, notchDepth: 0, headHeight: 0, roomMinX: 0, roomMaxX: 0, roomMinY: 0, roomMaxY: 0 };

    const rd = this.rd;
    const paverW = product.widthFt || (6.25 / 12);
    const paverH = product.heightFt || (7.75 / 12);

    // Dog-bone shape proportions
    const notchDepthRatio = 0.18;
    const headHeightRatio = 0.28;
    const notchDepth = paverW * notchDepthRatio;
    const headHeight = paverH * headHeightRatio;

    const exclusions = rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];

    // Bounding box of zone/room polygon
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of poly) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }

    // Grid-aligned start: columns at paverW, rows at paverH
    const gridMinX = gridOrigin
      ? gridOrigin.minX + Math.floor((minX - gridOrigin.minX) / paverW) * paverW
      : minX;
    const gridMinY = gridOrigin
      ? gridOrigin.minY + Math.floor((minY - gridOrigin.minY) / paverH) * paverH
      : minY;

    const pavers = [];
    const halfH = paverH / 2;
    for (let col = 0; gridMinX + col * paverW < maxX + paverW; col++) {
      const yOff = (col % 2 === 1) ? halfH : 0;
      // Start one row early on offset columns to catch partial pavers at top
      const startY = gridMinY - (yOff > 0 ? paverH : 0) + yOff;
      for (let y = startY; y < maxY + paverH; y += paverH) {
        const cx = gridMinX + col * paverW + paverW / 2;
        const cy = y + paverH / 2;
        const bx = cx - paverW / 2;
        const by = cy - paverH / 2;

        if (!this._tileOverlapsPolygon(bx, by, paverW, paverH, poly, rd)) continue;

        let fullyExcluded = false;
        for (const excl of exclusions) {
          if (!excl || excl.length < 3) continue;
          if (this._tileFullyInsidePolygon(bx, by, paverW, paverH, excl, rd)) { fullyExcluded = true; break; }
        }
        if (fullyExcluded) continue;

        pavers.push({ cx, cy, w: paverW, h: paverH, rot: 0 });
      }
    }

    // Pre-compute neighbor lookup Sets for _paverStrokeEdges (avoids rebuilding per frame)
    const posKey = (x, y) => x.toFixed(4) + ',' + y.toFixed(4);
    const _centerSet = new Set();
    const _colSet = new Set();
    for (const p of pavers) {
      _centerSet.add(posKey(p.cx, p.cy));
      _colSet.add(p.cx.toFixed(4));
    }

    return {
      pavers, paverW, paverH, gridStep: paverW, notchDepth, headHeight,
      roomMinX: minX, roomMaxX: maxX, roomMinY: minY, roomMaxY: maxY,
      gridMinX, gridMinY,
      _centerSet, _colSet
    };
  }

  /**
   * Trace the 12-vertex dog-bone paver outline path centered at (cx, cy).
   * rot: 0 = default (tall), 1 = 90° CW (wide).
   * Uses moveTo/lineTo/closePath — caller manages beginPath/fill/stroke.
   */
  _paverPath(ctx, cx, cy, paverW, paverH, notchDepth, headH, rot) {
    const hw = paverW / 2, hh = paverH / 2;
    const ww = hw - notchDepth; // waist half-width
    // 45° beveled transitions: diagonal from head edge to waist edge
    const bevelH = notchDepth; // equal horizontal and vertical distance = 45°
    const flatH = headH - bevelH; // flat rectangular portion of each head
    if (!rot) {
      // Default orientation: taller than wide, heads at top/bottom
      //   ┌──────┐        full width head (flatH tall)
      //    \    /         45° bevel inward (bevelH tall)
      //    │    │         waist (narrower, waistW wide)
      //    /    \         45° bevel outward (bevelH tall)
      //   └──────┘        full width head (flatH tall)
      ctx.moveTo(cx - hw, cy - hh);
      ctx.lineTo(cx + hw, cy - hh);
      ctx.lineTo(cx + hw, cy - hh + flatH);
      ctx.lineTo(cx + ww, cy - hh + flatH + bevelH);
      ctx.lineTo(cx + ww, cy + hh - flatH - bevelH);
      ctx.lineTo(cx + hw, cy + hh - flatH);
      ctx.lineTo(cx + hw, cy + hh);
      ctx.lineTo(cx - hw, cy + hh);
      ctx.lineTo(cx - hw, cy + hh - flatH);
      ctx.lineTo(cx - ww, cy + hh - flatH - bevelH);
      ctx.lineTo(cx - ww, cy - hh + flatH + bevelH);
      ctx.lineTo(cx - hw, cy - hh + flatH);
      ctx.closePath();
    } else {
      // 90° CW: wider than tall, heads at left/right
      // Rotation (lx, ly) → (-ly, lx)
      ctx.moveTo(cx + hh, cy - hw);
      ctx.lineTo(cx + hh, cy + hw);
      ctx.lineTo(cx + hh - flatH, cy + hw);
      ctx.lineTo(cx + hh - flatH - bevelH, cy + ww);
      ctx.lineTo(cx - hh + flatH + bevelH, cy + ww);
      ctx.lineTo(cx - hh + flatH, cy + hw);
      ctx.lineTo(cx - hh, cy + hw);
      ctx.lineTo(cx - hh, cy - hw);
      ctx.lineTo(cx - hh + flatH, cy - hw);
      ctx.lineTo(cx - hh + flatH + bevelH, cy - ww);
      ctx.lineTo(cx + hh - flatH - bevelH, cy - ww);
      ctx.lineTo(cx + hh - flatH, cy - hw);
      ctx.closePath();
    }
  }

  /**
   * Stroke paver edges in two passes — each shared boundary drawn once.
   * Skips entire right profile when right column exists, bottom edge when
   * neighbor below exists.
   *  Pass 1: Horizontal seams (top/bottom) at 3× base, reduced opacity
   *  Pass 2: Side profiles (dog-bone) at 0.5× base, full stroke color
   */
  _paverStrokeEdges(ctx, pavers, paverW, paverH, notchDepth, headH, layout) {
    if (!pavers || pavers.length === 0) return;
    const hw = paverW / 2, hh = paverH / 2;
    const ww = hw - notchDepth;
    const bevelH = notchDepth;
    const flatH = headH - bevelH;
    // Use pre-computed Sets from layout when available, otherwise build on the fly
    const posKey = (x, y) => x.toFixed(4) + ',' + y.toFixed(4);
    let centerSet, colSet;
    if (layout && layout._centerSet && layout._colSet) {
      centerSet = layout._centerSet;
      colSet = layout._colSet;
    } else {
      centerSet = new Set();
      colSet = new Set();
      for (const p of pavers) {
        centerSet.add(posKey(p.cx, p.cy));
        colSet.add(p.cx.toFixed(4));
      }
    }
    const hasBelow = (cx, cy) => centerSet.has(posKey(cx, cy + paverH));
    const hasColRight = (cx) => colSet.has((cx + paverW).toFixed(4));
    const savedLW = ctx.lineWidth;
    const savedStroke = ctx.strokeStyle;
    if (this.rd.showSeamLines) {
      ctx.strokeStyle = this.rd.darkMode ? '#000F1C' : '#ffffff';
    } else if (this.rd._equipmentMode) {
      // Completely hidden on equipment tab
      ctx.strokeStyle = 'rgba(0,0,0,0)';
    } else {
      ctx.strokeStyle = this.rd.darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,15,28,0.06)';
    }
    // ── Pass 1: Horizontal seams ──
    ctx.lineWidth = savedLW * 3;
    ctx.beginPath();
    for (const p of pavers) {
      const { cx, cy } = p;
      // Top edge (always — canonical horizontal)
      ctx.moveTo(cx - hw, cy - hh);
      ctx.lineTo(cx + hw, cy - hh);
      // Bottom edge — only if no neighbor below
      if (!hasBelow(cx, cy)) {
        ctx.moveTo(cx + hw, cy + hh);
        ctx.lineTo(cx - hw, cy + hh);
      }
    }
    ctx.stroke();
    // ── Pass 2: Side profiles ──
    ctx.lineWidth = savedLW * 0.5;
    ctx.beginPath();
    for (const p of pavers) {
      const { cx, cy } = p;
      const skipR = hasColRight(cx);
      // Right profile — skip entirely if right column exists
      if (!skipR) {
        ctx.moveTo(cx + hw, cy - hh);
        ctx.lineTo(cx + hw, cy - hh + flatH);
        ctx.lineTo(cx + ww, cy - hh + flatH + bevelH);
        ctx.lineTo(cx + ww, cy + hh - flatH - bevelH);
        ctx.lineTo(cx + hw, cy + hh - flatH);
        ctx.lineTo(cx + hw, cy + hh);
      }
      // Left profile (always — canonical side edge)
      ctx.moveTo(cx - hw, cy + hh);
      ctx.lineTo(cx - hw, cy + hh - flatH);
      ctx.lineTo(cx - ww, cy + hh - flatH - bevelH);
      ctx.lineTo(cx - ww, cy - hh + flatH + bevelH);
      ctx.lineTo(cx - hw, cy - hh + flatH);
      ctx.lineTo(cx - hw, cy - hh);
    }
    ctx.stroke();
    ctx.lineWidth = savedLW;
    ctx.strokeStyle = savedStroke;
  }

  /**
   * Draw cheap grid lines for pavers instead of per-paver dog-bone edge tracing.
   * Vertical lines at column boundaries, horizontal lines at row boundaries
   * (staggered by half-height on odd columns). Always visible for pavers.
   */
  _paverGridLines(ctx, layout, isDark) {
    const { paverW, paverH, gridMinX, gridMinY, roomMinX, roomMaxX, roomMinY, roomMaxY } = layout;
    if (!paverW || !paverH) return;
    const halfH = paverH / 2;
    // Extend bounds by one paver in each direction to cover overage
    const yTop = roomMinY - paverH;
    const yBot = roomMaxY + paverH;
    const xLeft = roomMinX - paverW;
    const xRight = roomMaxX + paverW;

    ctx.strokeStyle = isDark ? '#000F1C' : '#ffffff';

    // ── Vertical column dividers (thinner) ──
    ctx.lineWidth = 0.15 / 12;
    ctx.beginPath();
    for (let x = gridMinX; x <= xRight + paverW; x += paverW) {
      if (x < xLeft) continue;
      ctx.moveTo(x, yTop);
      ctx.lineTo(x, yBot);
    }
    ctx.stroke();

    // ── Horizontal row dividers (thicker, per-column stagger) ──
    ctx.lineWidth = 0.4 / 12;
    ctx.beginPath();
    // Even columns (no offset)
    for (let y = gridMinY; y <= yBot + paverH; y += paverH) {
      if (y < yTop) continue;
      ctx.moveTo(xLeft, y);
      ctx.lineTo(xRight, y);
    }
    ctx.stroke();
    // Odd columns: draw short horizontal segments at staggered positions
    ctx.beginPath();
    for (let col = 1; gridMinX + col * paverW < xRight + paverW; col += 2) {
      const colX = gridMinX + col * paverW;
      const colRight = colX + paverW;
      if (colRight < xLeft || colX > xRight) continue;
      // Offset rows by halfH
      const startY = gridMinY + halfH - paverH;
      for (let y = startY; y <= yBot + paverH; y += paverH) {
        if (y < yTop) continue;
        ctx.moveTo(colX, y);
        ctx.lineTo(colRight, y);
      }
    }
    ctx.stroke();
  }

  // ── Flooring Overlay Rendering ───────────

  /* CUSTOM_ROOM_DESIGNER_BY_CLAUDE — Turf exclusion polygons for flooring clip */
  /**
   * Returns array of 4-corner polygons for turf objects overlapping the given room.
   * Used by all flooring clip sites so flooring renders around (not under) turf.
   */
  /**
   * Area of one turf piece. CUSTOM_ROOMDESIGNER_BY_CLAUDE — a zone-filled piece is
   * its zone, not its bounding rectangle, so the quote reflects what is actually
   * being laid. Ordinary rectangular pieces fall through to width × depth.
   */
  _turfPieceArea(obj) {
    if (obj && obj.turfZone && this.rd._turfZoneShape) {
      const z = this.rd._turfZoneShape(obj);
      if (z && z.polygon && z.polygon.length >= 3) {
        let a = Math.abs(this._polygonArea(z.polygon));
        for (const h of (z.holes || [])) { if (h && h.length >= 3) a -= Math.abs(this._polygonArea(h)); }
        return Math.max(0, a);
      }
    }
    return (obj.width || 0) * (obj.depth || 0);
  }

  _getTurfExclusionsForRoom(room, geo, includePlatforms) {
    if (includePlatforms === undefined) includePlatforms = true;
    const polys = [];
    const roomPoly = geo.roomPolygon;
    if (!roomPoly || roomPoly.length < 3 || !this.rd.objects) return polys;
    // Expand exclusion by half a screen pixel to cover anti-aliasing fringe at turf/floor boundary
    const scale = this.rd.basePixelsPerFoot * this.rd.zoomLevel;
    const _exclExpand = 0.5 / scale;
    for (const obj of this.rd.objects) {
      if (obj.type === 'platform' && !includePlatforms) continue;
      if (obj.type !== 'turf' && obj.type !== 'platform') continue;
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a zone-filled piece excludes its ZONE, not
      // its bounding rectangle. The rectangle would carve flooring out of corners the
      // turf doesn't actually cover, and the take-off would under-count the floor by
      // exactly that difference.
      if (obj.type === 'turf' && obj.turfZone && this.rd._turfZoneShape) {
        const _z = this.rd._turfZoneShape(obj);
        if (_z && _z.polygon && _z.polygon.length >= 3) {
          polys.push(_z.polygon.map(pt => ({ x: pt.x, y: pt.y })));
          continue;
        }
      }
      const rot = obj.rotation || 0;
      const cos = Math.cos(rot), sin = Math.sin(rot);
      // Expanded corners for the exclusion polygon (anti-aliasing fringe coverage)
      const hw = obj.width / 2 + _exclExpand, hd = obj.depth / 2 + _exclExpand;
      const corners = [
        { x: obj.x + (-hw) * cos - (-hd) * sin, y: obj.y + (-hw) * sin + (-hd) * cos },
        { x: obj.x + ( hw) * cos - (-hd) * sin, y: obj.y + ( hw) * sin + (-hd) * cos },
        { x: obj.x + ( hw) * cos - ( hd) * sin, y: obj.y + ( hw) * sin + ( hd) * cos },
        { x: obj.x + (-hw) * cos - ( hd) * sin, y: obj.y + (-hw) * sin + ( hd) * cos }
      ];
      // Raw (unexpanded) corners for overlap detection — expanded corners overshoot
      // zone boundaries by ~0.025ft, causing false negatives at zone edges
      const rhw = obj.width / 2, rhd = obj.depth / 2;
      const rawCorners = [
        { x: obj.x + (-rhw) * cos - (-rhd) * sin, y: obj.y + (-rhw) * sin + (-rhd) * cos },
        { x: obj.x + ( rhw) * cos - (-rhd) * sin, y: obj.y + ( rhw) * sin + (-rhd) * cos },
        { x: obj.x + ( rhw) * cos - ( rhd) * sin, y: obj.y + ( rhw) * sin + ( rhd) * cos },
        { x: obj.x + (-rhw) * cos - ( rhd) * sin, y: obj.y + (-rhw) * sin + ( rhd) * cos }
      ];
      // Include if turf overlaps zone polygon (center, corners, zone vertices, or edge crossing)
      let _overlaps = this.rd.pointInPolygon(obj.x, obj.y, roomPoly);
      if (!_overlaps) _overlaps = rawCorners.some(c => this.rd.pointInPolygon(c.x, c.y, roomPoly));
      if (!_overlaps) {
        const cosN = Math.cos(-rot), sinN = Math.sin(-rot);
        _overlaps = roomPoly.some(v => {
          const dx = v.x - obj.x, dy = v.y - obj.y;
          return (dx * cosN - dy * sinN) >= -rhw && (dx * cosN - dy * sinN) <= rhw
              && (dx * sinN + dy * cosN) >= -rhd && (dx * sinN + dy * cosN) <= rhd;
        });
      }
      if (!_overlaps) {
        for (let i = 0; i < 4 && !_overlaps; i++) {
          const a1 = rawCorners[i], a2 = rawCorners[(i + 1) % 4];
          for (let j = 0, k = roomPoly.length - 1; j < roomPoly.length; k = j++) {
            const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
            const d2x = roomPoly[j].x - roomPoly[k].x, d2y = roomPoly[j].y - roomPoly[k].y;
            const denom = d1x * d2y - d1y * d2x;
            if (Math.abs(denom) < 1e-10) continue;
            const t = ((roomPoly[k].x - a1.x) * d2y - (roomPoly[k].y - a1.y) * d2x) / denom;
            const u = ((roomPoly[k].x - a1.x) * d1y - (roomPoly[k].y - a1.y) * d1x) / denom;
            if (t >= 0 && t <= 1 && u >= 0 && u <= 1) { _overlaps = true; break; }
          }
        }
      }
      if (_overlaps) {
        polys.push(corners);
      }
    }
    return polys;
  }

  /**
   * Fingerprint of turf objects inside a room — changes when turf is moved/resized/rotated/added/deleted.
   * Used to invalidate flooring layout caches.
   */
  _getTurfFingerprint(room, geo) {
    const roomPoly = geo.roomPolygon;
    if (!roomPoly || roomPoly.length < 3 || !this.rd.objects) return '';
    let fp = '';
    for (const obj of this.rd.objects) {
      if (obj.type !== 'turf' && obj.type !== 'platform') continue;
      // Same overlap logic as _getTurfExclusionsForRoom
      const hw = obj.width / 2, hd = obj.depth / 2;
      const rot = obj.rotation || 0;
      const cos = Math.cos(rot), sin = Math.sin(rot);
      const corners = [
        { x: obj.x + (-hw) * cos - (-hd) * sin, y: obj.y + (-hw) * sin + (-hd) * cos },
        { x: obj.x + ( hw) * cos - (-hd) * sin, y: obj.y + ( hw) * sin + (-hd) * cos },
        { x: obj.x + ( hw) * cos - ( hd) * sin, y: obj.y + ( hw) * sin + ( hd) * cos },
        { x: obj.x + (-hw) * cos - ( hd) * sin, y: obj.y + (-hw) * sin + ( hd) * cos }
      ];
      let _ov = this.rd.pointInPolygon(obj.x, obj.y, roomPoly);
      if (!_ov) _ov = corners.some(c => this.rd.pointInPolygon(c.x, c.y, roomPoly));
      if (!_ov) {
        const cosN = Math.cos(-rot), sinN = Math.sin(-rot);
        _ov = roomPoly.some(v => {
          const dx = v.x - obj.x, dy = v.y - obj.y;
          return (dx * cosN - dy * sinN) >= -hw && (dx * cosN - dy * sinN) <= hw
              && (dx * sinN + dy * cosN) >= -hd && (dx * sinN + dy * cosN) <= hd;
        });
      }
      if (!_ov) {
        for (let i = 0; i < 4 && !_ov; i++) {
          const a1 = corners[i], a2 = corners[(i + 1) % 4];
          for (let j = 0, k = roomPoly.length - 1; j < roomPoly.length; k = j++) {
            const d1x = a2.x - a1.x, d1y = a2.y - a1.y;
            const d2x = roomPoly[j].x - roomPoly[k].x, d2y = roomPoly[j].y - roomPoly[k].y;
            const denom = d1x * d2y - d1y * d2x;
            if (Math.abs(denom) < 1e-10) continue;
            const t = ((roomPoly[k].x - a1.x) * d2y - (roomPoly[k].y - a1.y) * d2x) / denom;
            const u = ((roomPoly[k].x - a1.x) * d1y - (roomPoly[k].y - a1.y) * d1x) / denom;
            if (t >= 0 && t <= 1 && u >= 0 && u <= 1) { _ov = true; break; }
          }
        }
      }
      if (_ov) {
        fp += `${obj.x.toFixed(4)},${obj.y.toFixed(4)},${obj.width.toFixed(4)},${obj.depth.toFixed(4)},${(obj.rotation || 0).toFixed(4)};`;
      }
    }
    return fp;
  }

  /**
   * Apply a canvas clip that excludes turf object rects.
   * Call inside a ctx.save()/restore() block AFTER rotation transform.
   * Returns true if clip was applied (caller doesn't need to do anything extra —
   * the enclosing ctx.restore() will release the clip).
   */
  _applyTurfClip(ctx, room, geo, layout) {
    const turfExcl = this._getTurfExclusionsForRoom(room, geo);
    if (turfExcl.length === 0) return false;
    ctx.beginPath();
    ctx.rect(-1e6, -1e6, 2e6, 2e6);
    for (const tp of turfExcl) {
      const rtp = (layout && layout._angle && layout._centroid)
        ? this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y)
        : tp;
      ctx.moveTo(rtp[0].x, rtp[0].y);
      for (let i = 1; i < rtp.length; i++) ctx.lineTo(rtp[i].x, rtp[i].y);
      ctx.closePath();
    }
    ctx.clip('evenodd');
    return true;
  }
  /* END CUSTOM_ROOM_DESIGNER_BY_CLAUDE */

  _drawFlooringOverlay(room, geo) {
    if (!room.closed || (!this.showOverlay && !this.rd._viewOnly)) return;
    if (!this._roomHasFlooring(room)) return;
    /* In view-only presentation mode, only show flooring on tabs where floor is active
       (Flooring Layout + Full Layout). Hide on Floor Plan + Equipment Layout tabs. */
    if (this.rd._viewOnly && !this.rd._viewOnlyShowFloor) return;

    // Material overlay off → uniform "floored" tint (same for all products)
    if (!this.rd.showMaterialOverlay) {
      this._drawFlooredZoneTint(room, geo);
      // Continue if seam lines or overage need rendering
      if (!this.rd.showSeamLines && this.rd.hideOverage) return;
      this._seamOnlyMode = true;
    } else {
      this._seamOnlyMode = false;
    }

    // Lerp overlay detail alpha here so it advances even when we early-return below
    const _now = performance.now();
    const _dt = this._overlayDetailLastFrame ? (_now - this._overlayDetailLastFrame) / 1000 : 0;
    this._overlayDetailLastFrame = _now;
    if (this._overlayDetailAlpha < this._overlayDetailTarget) {
      this._overlayDetailAlpha = Math.min(this._overlayDetailAlpha + _dt / 0.6, this._overlayDetailTarget);
    }

    // Overlay detail fully suppressed (active zoom/pan) — draw the same
    // uniform tint as overlay-off so the color matches, skip all heavy rendering
    if (!this._seamOnlyMode && !this._flashSeamsOverageOnly && this._overlayDetailAlpha < 0.01) {
      this._drawFlooredZoneTint(room, geo);
      return;
    }

    const ctx = this.rd.ctx;
    const isDark = this.rd.darkMode;
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return;

    // Multi-zone dispatch: render each zone independently
    const zones = this._getZonesForRoom(room, geo);
    if (zones.length > 1) {
      // Room-level context for cross-zone grid alignment
      room._zoneRenderContext = {
        roomPoly: geo.roomPolygon,
        roomCentroid: this._getPolygonCentroid(geo.roomPolygon),
        wallDividerSegs: this._getWallDividerSegmentsForRoom(room, geo)
      };
      // Whole-room opaque background — covers blue roomFill so it can't bleed through flooring
      // Skip in seam-only mode — the tint was already drawn
      if (this._seamOnlyMode || this._flashSeamsOverageOnly) { /* skip bg fill */ } else {
      const bgColor = isDark ? '#141c2e' : '#ffffff';
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k].x, poly[k].y);
      ctx.closePath();
      // Exclude cutouts/holes but NOT turf — turf has opaque fill so bg underneath is invisible,
      // and excluding turf creates a gap where room fill (blue) bleeds through near walls
      const _bgExcl = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      for (const excl of _bgExcl) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let k = 1; k < excl.length; k++) ctx.lineTo(excl[k].x, excl[k].y);
        ctx.closePath();
      }
      ctx.fillStyle = bgColor;
      ctx.fill('evenodd');
      // Stroke outer boundary to cover anti-aliasing gaps at adjacent room edges
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k].x, poly[k].y);
      ctx.closePath();
      ctx.strokeStyle = bgColor;
      ctx.lineWidth = 1.5 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
      ctx.stroke();
      ctx.restore();
      } // end bg fill skip
      // Re-draw blue tint + grid dim for zones WITHOUT flooring so they stay distinguishable
      if (!this._seamOnlyMode && !this._flashSeamsOverageOnly) {
      const _dimColor = this.rd._tc.roomGridDim;
      const _roomFill = this.rd._tc.roomFill;
      for (let zi = 0; zi < zones.length; zi++) {
        const zone = zones[zi];
        const zf = this._getZoneFlooring(room, zone);
        if (zf) continue; // floored — leave covered
        const zp = zone.polygon;
        if (!zp || zp.length < 3) continue;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(zp[0].x, zp[0].y);
        for (let k = 1; k < zp.length; k++) ctx.lineTo(zp[k].x, zp[k].y);
        ctx.closePath();
        if (_dimColor) { ctx.fillStyle = _dimColor; ctx.fill(); }
        ctx.fillStyle = _roomFill; ctx.fill();
        ctx.restore();
      }
      } // end seam-only skip for unfloored zone redraw
      // Two-pass rendering: Pass 1 draws strips/tiles only, Pass 2 draws overage on top.
      // This prevents zone B's strips from covering zone A's overage at shared boundaries.
      for (let pass = 1; pass <= 2; pass++) {
        room._zoneRenderPass = pass; // 1 = strips only, 2 = overage only
        for (let zi = 0; zi < zones.length; zi++) {
          const zone = zones[zi];
          const zf = this._getZoneFlooring(room, zone);
          if (!zf) continue;
          const zoneGeo = {
            roomPolygon: zone.polygon,
            cutoutPolys: [],
            holePolygons: this._zoneHoles(zone),
            area: zone.area,
            fp: (geo.fp || '') + ':z' + zi + this._zoneHolesFp(zone)
          };
          const savedFlooring = room.flooring;
          const savedAngle = room.flooringAngle;
          const savedLayout = room._flooringLayout;
          const savedLayoutGeo = room._flooringLayoutGeo;
          const savedLayoutProduct = room._flooringLayoutProduct;
          const savedLayoutColor = room._flooringLayoutColor;
          const savedLayoutAngle = room._flooringLayoutAngle;

          room.flooring = { productId: zf.productId, colorId: zf.colorId, independent: !!zf.independent };
          room.flooringAngle = zf.flooringAngle || 0;

          const cached = this._getZoneLayoutCache(room, zi);
          const _zoneTurfFP = this._getTurfFingerprint(room, zoneGeo);
          const cacheKey = `${zf.productId}:${zf.colorId}:${zf.flooringAngle || 0}:${zoneGeo.fp}:${zf.independent ? 'I' : ''}:${this._rollOptimizationMode}:${this._uniformTargetLen}:${_zoneTurfFP}:${this._rollShareLayoutFP(room, zoneGeo)}`;
          if (cached && cached.cacheKey === cacheKey) {
            room._flooringLayout = cached.layout;
            room._flooringLayoutGeo = zoneGeo;
            room._flooringLayoutProduct = zf.productId;
            room._flooringLayoutColor = zf.colorId;
            room._flooringLayoutAngle = zf.flooringAngle || 0;
          } else {
            room._flooringLayout = null;
            room._flooringLayoutGeo = null;
          }

          this._drawFlooringOverlaySingle(room, zoneGeo);

          if (room._flooringLayout) {
            this._setZoneLayoutCache(room, zi, room._flooringLayout, cacheKey);
          }

          room.flooring = savedFlooring;
          room.flooringAngle = savedAngle;
          room._flooringLayout = savedLayout;
          room._flooringLayoutGeo = savedLayoutGeo;
          room._flooringLayoutProduct = savedLayoutProduct;
          room._flooringLayoutColor = savedLayoutColor;
          room._flooringLayoutAngle = savedLayoutAngle;
        }
      }
      delete room._zoneRenderPass;
      delete room._zoneRenderContext;
            return;
    }

    // Single-zone: whole-room background fill + original rendering
    if (!room.flooring) return;
    if (!this._seamOnlyMode && !this._flashSeamsOverageOnly) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      const bgExclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ...this._getTurfExclusionsForRoom(room, geo)];
      for (const excl of bgExclusions) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      const _bgColor = isDark ? '#141c2e' : '#ffffff';
      ctx.fillStyle = _bgColor;
      ctx.fill('evenodd');
      // Stroke outer boundary to cover anti-aliasing gaps at adjacent room edges
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      ctx.strokeStyle = _bgColor;
      ctx.lineWidth = 1.5 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
      ctx.stroke();
      ctx.restore();
    }
    this._drawFlooringOverlaySingle(room, geo);
      }

  /** When material overlay is off, paint floored zones with a uniform darker tint
   *  so the user can see which zones have flooring without revealing which product. */
  _drawFlooredZoneTint(room, geo) {
    const ctx = this.rd.ctx;
    const flooredFill = this.rd._tc.roomFlooredFill;
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return;

    const zones = this._getZonesForRoom(room, geo);
    if (zones.length > 1) {
      // Multi-zone: tint only the zones that have flooring assigned
      // Exclude turf areas that fall inside THIS zone (turf draws its own roomFlooredFill)
      // Only add turf sub-paths for turf whose center is inside the zone polygon —
      // otherwise even-odd creates spurious fills in turf areas from other zones.
      for (const zone of zones) {
        const zf = this._getZoneFlooring(room, zone);
        if (!zf) continue;
        const zp = zone.polygon;
        if (!zp || zp.length < 3) continue;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(zp[0].x, zp[0].y);
        for (let k = 1; k < zp.length; k++) ctx.lineTo(zp[k].x, zp[k].y);
        ctx.closePath();
        let _hasExcl = false;
        if (this.rd.objects) {
          for (const obj of this.rd.objects) {
            if (obj.type !== 'turf') continue;
            if (!RoomDesignerMath.pointInPolygon(obj.x, obj.y, zp)) continue;
            _hasExcl = true;
            const hw = obj.width / 2, hd = obj.depth / 2;
            const rot = obj.rotation || 0;
            const cos = Math.cos(rot), sin = Math.sin(rot);
            const corners = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
            const pts = corners.map(([lx, ly]) => ({ x: obj.x + lx * cos - ly * sin, y: obj.y + lx * sin + ly * cos }));
            ctx.moveTo(pts[0].x, pts[0].y);
            for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
            ctx.closePath();
          }
        }
        ctx.fillStyle = flooredFill;
        ctx.fill(_hasExcl ? 'evenodd' : 'nonzero');
        ctx.restore();
      }
    } else {
      // Single-zone: tint the whole room
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k].x, poly[k].y);
      ctx.closePath();
      const excl = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ...this._getTurfExclusionsForRoom(room, geo)];
      for (const e of excl) {
        if (!e || e.length < 3) continue;
        ctx.moveTo(e[0].x, e[0].y);
        for (let k = 1; k < e.length; k++) ctx.lineTo(e[k].x, e[k].y);
        ctx.closePath();
      }
      ctx.fillStyle = flooredFill;
      ctx.fill('evenodd');
      ctx.restore();
    }
  }

  /** Render flooring for a single polygon (room or zone). Assumes background already drawn. */
  _drawFlooringOverlaySingle(room, geo) {
    if (!room.flooring) return;

    const product = this._getProduct(room.flooring.productId);
    if (!product) return;

    const ctx = this.rd.ctx;
    const isDark = this.rd.darkMode;

    // Anti-aliasing gap fix: always expand fill rects by ~1 screen pixel
    // so adjacent strips/rolls overlap slightly instead of showing sub-pixel gaps
    const _seamEps = 1.0 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
    const colorId = room.flooring.colorId;
    const themeKey = isDark ? 'dark' : 'light';
    let colors = (product.colorOverlays && product.colorOverlays[colorId] && product.colorOverlays[colorId][themeKey])
      || product.overlayColors[themeKey];

    // In print mode, overlay colors are semi-transparent (designed for on-screen
    // compositing over dark room fills). On the white PDF background they're nearly
    // invisible — especially when texture images haven't loaded yet (first PDF after
    // page load). Override with opaque versions so the floor is always clearly visible.
    if (this.rd._printMode) {
      const colorObj = product.colors.find(c => c.id === colorId);
      const texUrl = colorObj && colorObj.textureUrl;
      const texLoaded = texUrl && this._getTextureImage(texUrl);
      if (!texLoaded) {
        // Extract RGB from the strip color and render at full opacity
        const _m = colors.strip.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (_m) {
          const [, r, g, b] = _m;
          colors = Object.assign({}, colors, {
            strip: `rgb(${r},${g},${b})`,
            base: 'rgb(245,245,245)'
          });
        }
      }
    }

    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return;

    // Zone grid alignment: when rendering zones, use room-level origin + centroid
    // so tile/strip grids are consistent across zone boundaries.
    // Exception: "independent" zones bypass shared alignment for optimal waste reduction.
    const _zrc = room._zoneRenderContext;
    const _floorAng = room.flooringAngle || 0;
    let _gridOrigin = null;
    let _rotCentroid = null;
    const _isIndependent = !!(room.flooring && room.flooring.independent);
    if (_zrc && !_isIndependent) {
      _rotCentroid = _zrc.roomCentroid;
      if (_floorAng !== 0) {
        const rRoomPoly = this._rotatePolygon(_zrc.roomPoly, -_floorAng, _rotCentroid.x, _rotCentroid.y);
        let rmX = Infinity, rmY = Infinity;
        for (const p of rRoomPoly) { rmX = Math.min(rmX, p.x); rmY = Math.min(rmY, p.y); }
        _gridOrigin = { minX: rmX, minY: rmY };
      } else {
        let rmX = Infinity, rmY = Infinity;
        for (const p of _zrc.roomPoly) { rmX = Math.min(rmX, p.x); rmY = Math.min(rmY, p.y); }
        _gridOrigin = { minX: rmX, minY: rmY };
      }
    }

    // Divider wall segments for scissors edge offset (rotated to match polygon space)
    const _wallDivHalfThk = 1 / 6; // half of 4" wall = 2" = 1/6 ft
    let _wallDivSegsRotated = null;
    let _zoneCentroidRotated = null;
    if (_zrc && _zrc.wallDividerSegs && _zrc.wallDividerSegs.length > 0) {
      const zoneCentroid = this._getPolygonCentroid(poly);
      if (_floorAng !== 0) {
        const rc = _rotCentroid || zoneCentroid;
        _wallDivSegsRotated = _zrc.wallDividerSegs.map(seg => ({
          start: this._rotatePoint(seg.start, -_floorAng, rc),
          end: this._rotatePoint(seg.end, -_floorAng, rc)
        }));
        _zoneCentroidRotated = this._rotatePoint(zoneCentroid, -_floorAng, rc);
      } else {
        _wallDivSegsRotated = _zrc.wallDividerSegs;
        _zoneCentroidRotated = zoneCentroid;
      }
    }

    // Multi-zone two-pass: 1 = strips only, 2 = overage only (0 or undefined = both)
    const _zonePass = room._zoneRenderPass || 0;

    // Material overlay off — show solid color fill only (no textures, no zoom fade)
    if (!this.rd.showMaterialOverlay) {
      const floorAngle = _floorAng;
      const isPaver = product.shape === 'paver';
      const isTile = !isPaver && product.type === 'tile';
      const _turfFP = this._getTurfFingerprint(room, geo);
      let layout = room._flooringLayout;
      if (!layout || room._flooringLayoutGeo !== geo
          || room._flooringLayoutProduct !== room.flooring.productId
          || room._flooringLayoutColor !== room.flooring.colorId
          || room._flooringLayoutAngle !== floorAngle
          || room._flooringLayoutOptMode !== this._rollOptimizationMode
          || room._flooringLayoutTargetLen !== this._uniformTargetLen
          || room._flooringLayoutTurfFP !== _turfFP
          || room._flooringLayoutShareFP !== this._rollShareLayoutFP(room, geo)) {
        const _isRoll = !isPaver && !isTile;
        const _turfExcl = this._getTurfExclusionsForRoom(room, geo, !_isRoll);
        const computeFn = isPaver
          ? (rP, rE) => this._computePaverLayout(room, geo, product, rP, rE, _gridOrigin)
          : isTile
            ? (rP, rE) => this._computeTileLayout(room, geo, product, rP, rE, _gridOrigin)
            : (rP, rE) => this._computeRollLayout(room, geo, product, rP, rE, _gridOrigin);
        if (floorAngle !== 0) {
          const centroid = _rotCentroid || this._getPolygonCentroid(poly);
          const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._turfExcl];
          const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
          const rExcl = exclusions.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
          layout = computeFn(rPoly, rExcl);
          layout._angle = floorAngle;
          layout._centroid = centroid;
          layout._rotatedPoly = rPoly;
          layout._rotatedExclusions = rExcl;
        } else {
          const _allExcl = _turfExcl.length > 0 ? [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._turfExcl] : null;
          layout = isPaver
            ? this._computePaverLayout(room, geo, product, null, _allExcl, _gridOrigin)
            : isTile
              ? this._computeTileLayout(room, geo, product, null, _allExcl, _gridOrigin)
              : this._computeRollLayout(room, geo, product, null, _allExcl, _gridOrigin);
          layout._angle = 0;
          layout._centroid = null;
          layout._rotatedPoly = null;
          layout._rotatedExclusions = null;
        }
        room._flooringLayout = layout;
        room._flooringLayoutGeo = geo;
        room._flooringLayoutProduct = room.flooring.productId;
        room._flooringLayoutColor = room.flooring.colorId;
        room._flooringLayoutAngle = floorAngle;
        room._flooringLayoutOptMode = this._rollOptimizationMode;
        room._flooringLayoutTargetLen = this._uniformTargetLen;
        room._flooringLayoutTurfFP = _turfFP;
        room._flooringLayoutShareFP = this._rollShareLayoutFP(room, geo);
      }
      // Pass 1 (or single-zone): draw strips/tiles
      if (_zonePass !== 2) {
        ctx.save();
        if (layout._angle) {
          const { x: cx, y: cy } = layout._centroid;
          ctx.translate(cx, cy);
          ctx.rotate(layout._angle);
          ctx.translate(-cx, -cy);
        }
        // Clip around turf/platform objects (always active when present)
        this._applyTurfClip(ctx, room, geo, layout);
        // Clip to room interior when hideOverage is on (no material outside room)
        if (this.rd.hideOverage) {
          const clipPoly = layout._rotatedPoly || poly;
          const clipExcl = layout._rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
          ctx.beginPath();
          ctx.moveTo(clipPoly[0].x, clipPoly[0].y);
          for (let i = 1; i < clipPoly.length; i++) ctx.lineTo(clipPoly[i].x, clipPoly[i].y);
          ctx.closePath();
          for (const excl of clipExcl) {
            if (!excl || excl.length < 3) continue;
            ctx.moveTo(excl[0].x, excl[0].y);
            for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
            ctx.closePath();
          }
          ctx.clip('evenodd');
        }
        if (layout.pavers && layout.pavers.length > 0) {
          if (!this._seamOnlyMode && !this._flashSeamsOverageOnly) {
            ctx.fillStyle = colors.strip;
            ctx.beginPath();
            for (const p of layout.pavers) this._paverPath(ctx, p.cx, p.cy, layout.paverW, layout.paverH, layout.notchDepth, layout.headHeight, p.rot);
            ctx.fill();
          }
          ctx.setLineDash([]);
          this._paverGridLines(ctx, layout, isDark);
        } else if (layout.tiles && layout.tiles.length > 0) {
          if (!this._seamOnlyMode && !this._flashSeamsOverageOnly) {
            ctx.fillStyle = colors.strip;
            ctx.beginPath();
            for (const t of layout.tiles) ctx.rect(t.x - _seamEps, t.y - _seamEps, t.w + 2 * _seamEps, t.h + 2 * _seamEps);
            ctx.fill();
          }
          if (this.rd.showSeamLines) {
            this._drawTwoToneSeamRects(ctx, layout.tiles, 0.5 / 12);
          }
        } else if (layout.strips && layout.strips.length > 0) {
          if (!this._seamOnlyMode && !this._flashSeamsOverageOnly) {
            ctx.fillStyle = colors.strip;
            ctx.beginPath();
            for (const strip of layout.strips) {
              for (const roll of strip.rolls) ctx.rect(strip.x - _seamEps, roll.y - _seamEps, strip.width + 2 * _seamEps, roll.length + 2 * _seamEps);
            }
            ctx.fill();
          }
          if (this.rd.showSeamLines) {
            const _rollRects = [];
            for (const strip of layout.strips) {
              for (const roll of strip.rolls) _rollRects.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
            }
            this._drawTwoToneSeamRects(ctx, _rollRects, 0.5 / 12);
          }
        }
        ctx.restore();
      }

      // Overage rendering (when overage toggle is on) — pass 2 or single-zone
      if (!this.rd.hideOverage && _zonePass !== 1) {
        const _trashNow = performance.now();
        const _trashDt = this._trashLastFrame ? (_trashNow - this._trashLastFrame) / 1000 : 0;
        this._trashLastFrame = _trashNow;
        if (this._trashAlpha < this._trashTarget) {
          this._trashAlpha = Math.min(this._trashAlpha + _trashDt / 0.3, this._trashTarget);
        }
        const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
        const overagePoly = layout._rotatedPoly || poly;
        const turfExcl = this._getTurfExclusionsForRoom(room, geo);
        let overageExcl;
        let turfExclRotated;
        if (layout._rotatedExclusions) {
          turfExclRotated = (layout._angle && layout._centroid)
            ? turfExcl.map(tp => this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y))
            : turfExcl;
          overageExcl = [...layout._rotatedExclusions, ...turfExclRotated];
        } else {
          turfExclRotated = turfExcl;
          overageExcl = [...exclusions, ...turfExcl];
        }
        const overageColor = colors.overage || 'rgba(204, 50, 50, 0.25)';
        const zoom = this.rd.zoomLevel;
        const scale = this.rd.basePixelsPerFoot * zoom;

        // Collect all rects (pavers/tiles/rolls bounding boxes) and compute bounding box
        const rects = [];
        if (layout.pavers && layout.pavers.length > 0) {
          for (const p of layout.pavers) rects.push({ x: p.cx - p.w / 2, y: p.cy - p.h / 2, w: p.w, h: p.h });
        } else if (layout.tiles && layout.tiles.length > 0) {
          for (const t of layout.tiles) rects.push({ x: t.x, y: t.y, w: t.w, h: t.h });
        } else if (layout.strips && layout.strips.length > 0) {
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) rects.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
          }
        }
        if (rects.length > 0) {
          let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
          for (const r of rects) {
            rMinX = Math.min(rMinX, r.x); rMinY = Math.min(rMinY, r.y);
            rMaxX = Math.max(rMaxX, r.x + r.w); rMaxY = Math.max(rMaxY, r.y + r.h);
          }

          ctx.save();
          if (layout._angle && layout._centroid) {
            ctx.translate(layout._centroid.x, layout._centroid.y);
            ctx.rotate(layout._angle);
            ctx.translate(-layout._centroid.x, -layout._centroid.y);
          }
          // Clip out turf/platform areas so overage never renders under them
          this._applyTurfClip(ctx, room, geo, layout);
          // Clip to all rects
          ctx.beginPath();
          for (const r of rects) ctx.rect(r.x, r.y, r.w, r.h);
          ctx.clip();
          // Even-odd path: big rect minus room polygon (minus cutouts/holes) = overage
          const nonTurfExcl = layout._rotatedExclusions || exclusions;
          ctx.beginPath();
          ctx.rect(rMinX - 50, rMinY - 50, (rMaxX - rMinX) + 100, (rMaxY - rMinY) + 100);
          ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
          for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
          ctx.closePath();
          for (const excl of nonTurfExcl) {
            if (!excl || excl.length < 3) continue;
            ctx.moveTo(excl[0].x, excl[0].y);
            for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
            ctx.closePath();
          }
          ctx.fillStyle = overageColor;
          ctx.fill('evenodd');

          ctx.restore();

          // Scissors along room boundary edges
          const scissorAlpha = Math.min(Math.max((zoom - 1.5) / 0.5, 0), 1);
          if (scissorAlpha > 0) {
            const scissorSize = Math.max(16 / scale, 16 / (this.rd.basePixelsPerFoot * 3));
            const scissorSpacing = 2.5;
            const scissorColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,15,28,0.55)';
            const inset = 0.005;
            const _scissorBatch = [];
            const allBoundaryPolys = [overagePoly, ...overageExcl.filter(e => e && e.length >= 3)];

            if (layout.strips && layout.strips.length > 0) {
              // Roll path: per-strip clipping
              for (const strip of layout.strips) {
                let sMinY = Infinity, sMaxY = -Infinity;
                for (const seg of strip.segments) { sMinY = Math.min(sMinY, seg.segMinY); sMaxY = Math.max(sMaxY, seg.segMaxY); }
                const clipX1 = strip.x + inset, clipY1 = sMinY - inset;
                const clipX2 = strip.x + strip.width - inset, clipY2 = sMaxY + inset;
                for (const bPoly of allBoundaryPolys) {
                  for (let ei = 0, ej = bPoly.length - 1; ei < bPoly.length; ej = ei++) {
                    const clipped = this._clipLineToRect(bPoly[ej].x, bPoly[ej].y, bPoly[ei].x, bPoly[ei].y, clipX1, clipY1, clipX2, clipY2);
                    if (!clipped) continue;
                    const dx = clipped.x2 - clipped.x1, dy = clipped.y2 - clipped.y1;
                    const len = Math.sqrt(dx * dx + dy * dy);
                    if (len < 0.1) continue;
                    const edgeAngle = Math.atan2(dy, dx);
                    const count = Math.max(1, Math.round(len / scissorSpacing));
                    for (let k = 1; k <= count; k++) {
                      const t = k / (count + 1);
                      _scissorBatch.push({ x: clipped.x1 + t * dx, y: clipped.y1 + t * dy, angle: edgeAngle + Math.PI / 2 });
                    }
                  }
                }
              }
            } else {
              // Tile path: full bounding box clipping
              for (const bPoly of allBoundaryPolys) {
                for (let ei = 0, ej = bPoly.length - 1; ei < bPoly.length; ej = ei++) {
                  const clipped = this._clipLineToRect(bPoly[ej].x, bPoly[ej].y, bPoly[ei].x, bPoly[ei].y, rMinX + inset, rMinY + inset, rMaxX - inset, rMaxY - inset);
                  if (!clipped) continue;
                  const dx = clipped.x2 - clipped.x1, dy = clipped.y2 - clipped.y1;
                  const len = Math.sqrt(dx * dx + dy * dy);
                  if (len < 0.1) continue;
                  const edgeAngle = Math.atan2(dy, dx);
                  const count = Math.max(1, Math.round(len / scissorSpacing));
                  for (let k = 1; k <= count; k++) {
                    const t = k / (count + 1);
                    _scissorBatch.push({ x: clipped.x1 + t * dx, y: clipped.y1 + t * dy, angle: edgeAngle + Math.PI / 2 });
                  }
                }
              }
            }

            // Offset scissors near divider walls toward zone edge
            if (_wallDivSegsRotated) {
              this._offsetScissorsForDividers(_scissorBatch, _wallDivSegsRotated, _zoneCentroidRotated, _wallDivHalfThk);
            }

            const minDistSq = scissorSize * scissorSize * 0.25;
            const deduped = [];
            for (const sc of _scissorBatch) {
              let replaceIdx = -1;
              for (let i = 0; i < deduped.length; i++) {
                const dx = sc.x - deduped[i].x, dy = sc.y - deduped[i].y;
                if (dx * dx + dy * dy < minDistSq) { replaceIdx = i; break; }
              }
              if (replaceIdx >= 0) deduped[replaceIdx] = sc;
              else deduped.push(sc);
            }
            if (deduped.length > 0) {
              this._pendingScissors.push({
                batch: deduped, scissorSize, scissorColor, scissorAlpha, isDark,
                rotAngle: layout._angle || 0, centroid: layout._centroid
              });
            }
          }
          // Collect turf overage data for deferred rendering on top of turf objects
          if (turfExcl.length > 0) {
            const ov = product.overageFt || 0.5;
            const turfRects = rects.map(r => ({
              x: r.x - ov, y: r.y - ov, w: r.w + 2 * ov, h: r.h + 2 * ov
            }));
            let trMinX = Infinity, trMinY = Infinity, trMaxX = -Infinity, trMaxY = -Infinity;
            for (const r of turfRects) {
              trMinX = Math.min(trMinX, r.x); trMinY = Math.min(trMinY, r.y);
              trMaxX = Math.max(trMaxX, r.x + r.w); trMaxY = Math.max(trMaxY, r.y + r.h);
            }
            this._pendingTurfOverage.push({
              rects: turfRects, rMinX: trMinX, rMinY: trMinY, rMaxX: trMaxX, rMaxY: trMaxY,
              overagePoly, overageExcl, overageColor,
              angle: layout._angle || 0, centroid: layout._centroid,
              turfExclRotated, turfExclWorld: turfExcl
            });
          }
        }
      }
      return;
    }

    // Cache layout per room — invalidates when geometry, product, angle, or turf changes
    const floorAngle = _floorAng;
    const isPaver = product.shape === 'paver';
    const isTile = !isPaver && product.type === 'tile';
    const _turfFP = this._getTurfFingerprint(room, geo);
    let layout;
    if (room._flooringLayout
        && room._flooringLayoutGeo === geo
        && room._flooringLayoutProduct === room.flooring.productId
        && room._flooringLayoutColor === room.flooring.colorId
        && room._flooringLayoutAngle === floorAngle
        && room._flooringLayoutOptMode === this._rollOptimizationMode
        && room._flooringLayoutTargetLen === this._uniformTargetLen
        && room._flooringLayoutTurfFP === _turfFP
        && room._flooringLayoutShareFP === this._rollShareLayoutFP(room, geo)) {
      layout = room._flooringLayout;
    } else {
      const _isRoll2 = !isPaver && !isTile;
      const _turfExcl = this._getTurfExclusionsForRoom(room, geo, !_isRoll2);
      const computeFn = isPaver
        ? (rP, rE) => this._computePaverLayout(room, geo, product, rP, rE, _gridOrigin)
        : isTile
          ? (rP, rE) => this._computeTileLayout(room, geo, product, rP, rE, _gridOrigin)
          : (rP, rE) => this._computeRollLayout(room, geo, product, rP, rE, _gridOrigin);
      if (floorAngle !== 0) {
        const centroid = _rotCentroid || this._getPolygonCentroid(poly);
        const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._turfExcl];
        const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
        const rExcl = exclusions.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
        layout = computeFn(rPoly, rExcl);
        layout._angle = floorAngle;
        layout._centroid = centroid;
        layout._rotatedPoly = rPoly;
        layout._rotatedExclusions = rExcl;
      } else {
        const _allExcl = _turfExcl.length > 0 ? [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._turfExcl] : null;
        layout = isPaver
          ? this._computePaverLayout(room, geo, product, null, _allExcl, _gridOrigin)
          : isTile
            ? this._computeTileLayout(room, geo, product, null, _allExcl, _gridOrigin)
            : this._computeRollLayout(room, geo, product, null, _allExcl, _gridOrigin);
        layout._angle = 0;
        layout._centroid = null;
        layout._rotatedPoly = null;
        layout._rotatedExclusions = null;
      }
      room._flooringLayout = layout;
      room._flooringLayoutGeo = geo;
      room._flooringLayoutProduct = room.flooring.productId;
      room._flooringLayoutColor = room.flooring.colorId;
      room._flooringLayoutAngle = floorAngle;
      room._flooringLayoutOptMode = this._rollOptimizationMode;
      room._flooringLayoutTargetLen = this._uniformTargetLen;
      room._flooringLayoutTurfFP = _turfFP;
      room._flooringLayoutShareFP = this._rollShareLayoutFP(room, geo);
    }

    // Flash preview: skip all fill/texture detail — draw only seams + overage
    if (this._flashSeamsOverageOnly) {
      ctx.save();
      if (layout._angle && layout._centroid) {
        ctx.translate(layout._centroid.x, layout._centroid.y);
        ctx.rotate(layout._angle);
        ctx.translate(-layout._centroid.x, -layout._centroid.y);
      }
      // Seam lines (only if seams were off — otherwise they're already visible)
      if (this._flashIncludeSeams) {
        if (layout.strips && layout.strips.length > 0) {
          const _rr = [];
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) _rr.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
          }
          this._drawTwoToneSeamRects(ctx, _rr, 0.5 / 12);
        } else if (layout.tiles && layout.tiles.length > 0) {
          this._drawTwoToneSeamRects(ctx, layout.tiles, 0.5 / 12);
        }
      }
      ctx.restore();
      // Overage
      const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      const overagePoly = layout._rotatedPoly || poly;
      const turfExcl = this._getTurfExclusionsForRoom(room, geo);
      let overageExcl;
      if (layout._rotatedExclusions) {
        const turfExclRotated = (layout._angle && layout._centroid)
          ? turfExcl.map(tp => this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y))
          : turfExcl;
        overageExcl = [...layout._rotatedExclusions, ...turfExclRotated];
      } else {
        overageExcl = [...exclusions, ...turfExcl];
      }
      const overageColor = colors.overage || 'rgba(204, 50, 50, 0.25)';
      const rects = [];
      if (layout.strips && layout.strips.length > 0) {
        for (const strip of layout.strips) {
          for (const roll of strip.rolls) rects.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
        }
      } else if (layout.tiles && layout.tiles.length > 0) {
        for (const t of layout.tiles) rects.push({ x: t.x, y: t.y, w: t.w, h: t.h });
      } else if (layout.pavers && layout.pavers.length > 0) {
        for (const p of layout.pavers) rects.push({ x: p.cx - p.w / 2, y: p.cy - p.h / 2, w: p.w, h: p.h });
      }
      if (rects.length > 0) {
        let rMinX = Infinity, rMinY = Infinity, rMaxX = -Infinity, rMaxY = -Infinity;
        for (const r of rects) {
          rMinX = Math.min(rMinX, r.x); rMinY = Math.min(rMinY, r.y);
          rMaxX = Math.max(rMaxX, r.x + r.w); rMaxY = Math.max(rMaxY, r.y + r.h);
        }
        ctx.save();
        if (layout._angle && layout._centroid) {
          ctx.translate(layout._centroid.x, layout._centroid.y);
          ctx.rotate(layout._angle);
          ctx.translate(-layout._centroid.x, -layout._centroid.y);
        }
        this._applyTurfClip(ctx, room, geo, layout);
        ctx.beginPath();
        for (const r of rects) ctx.rect(r.x, r.y, r.w, r.h);
        ctx.clip();
        const nonTurfExcl = layout._rotatedExclusions || exclusions;
        ctx.beginPath();
        ctx.rect(rMinX - 50, rMinY - 50, (rMaxX - rMinX) + 100, (rMaxY - rMinY) + 100);
        ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
        for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
        ctx.closePath();
        for (const excl of nonTurfExcl) {
          if (!excl || excl.length < 3) continue;
          ctx.moveTo(excl[0].x, excl[0].y);
          for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
          ctx.closePath();
        }
        ctx.fillStyle = overageColor;
        ctx.fill('evenodd');
        ctx.restore();
      }
      return;
    }

    // Look up texture image for this color (last variant image, for overlay)
    const colorObj = product.colors.find(c => c.id === colorId);
    const textureImg = colorObj ? this._getTextureImage(colorObj.textureUrl) : null;

    // Lerp trash alpha toward target (fade in = 300ms) — must run for both tile and roll paths
    const _trashNow = performance.now();
    const _trashDt = this._trashLastFrame ? (_trashNow - this._trashLastFrame) / 1000 : 0;
    this._trashLastFrame = _trashNow;
    if (this._trashAlpha < this._trashTarget) {
      this._trashAlpha = Math.min(this._trashAlpha + _trashDt / 0.3, this._trashTarget);
    }

    // Detail alpha lerp now runs in _drawFlooringOverlay (caller) so it
    // advances even when we early-return during full suppression.
    const _detailAlpha = this._overlayDetailAlpha;
    const _detailSuppressed = _detailAlpha < 0.01;
    const _detailFading = _detailAlpha >= 0.01 && _detailAlpha < 0.99;

    // Simplified base layer: product color fill + grid lines (cheap to draw).
    // Shown during zoom/pan AND during fade-in as a stable background for the
    // full detail to layer over. Removed once full detail is fully opaque.
    if (_detailAlpha < 0.99 && _zonePass !== 2) {
      ctx.save();
      if (layout._angle) {
        const { x: cx, y: cy } = layout._centroid;
        ctx.translate(cx, cy);
        ctx.rotate(layout._angle);
        ctx.translate(-cx, -cy);
      }
      // Clip around turf objects (always active when turf present)
      this._applyTurfClip(ctx, room, geo, layout);
      // Clip to room interior when hideOverage is on so base layer
      // doesn't flash overage during zoom/pan transitions
      if (this.rd.hideOverage) {
        const _basePoly = layout._rotatedPoly || poly;
        ctx.beginPath();
        ctx.moveTo(_basePoly[0].x, _basePoly[0].y);
        for (let i = 1; i < _basePoly.length; i++) ctx.lineTo(_basePoly[i].x, _basePoly[i].y);
        ctx.closePath();
        ctx.clip();
      }
      // When fully suppressed (active zoom/pan), skip individual piece geometry.
      // The caller draws the roomFlooredFill tint instead (matching overlay-off look).
      if (_detailSuppressed) {
        ctx.restore();
        return;
      }
      const _showSeams = this.rd.showSeamLines;
      if (layout.pavers && layout.pavers.length > 0) {
        ctx.fillStyle = colors.strip;
        ctx.beginPath();
        for (const p of layout.pavers) this._paverPath(ctx, p.cx, p.cy, layout.paverW, layout.paverH, layout.notchDepth, layout.headHeight, p.rot);
        ctx.fill();
        ctx.setLineDash([]);
        this._paverGridLines(ctx, layout, isDark);
      } else if (layout.tiles && layout.tiles.length > 0) {
        ctx.fillStyle = colors.strip;
        ctx.beginPath();
        for (const t of layout.tiles) ctx.rect(t.x - _seamEps, t.y - _seamEps, t.w + 2 * _seamEps, t.h + 2 * _seamEps);
        ctx.fill();
        if (_showSeams) {
          this._drawTwoToneSeamRects(ctx, layout.tiles, 0.5 / 12);
        }
      } else if (layout.strips && layout.strips.length > 0) {
        ctx.fillStyle = colors.strip;
        ctx.beginPath();
        for (const strip of layout.strips) {
          for (const roll of strip.rolls) ctx.rect(strip.x - _seamEps, roll.y - _seamEps, strip.width + 2 * _seamEps, roll.length + 2 * _seamEps);
        }
        ctx.fill();
        if (_showSeams) {
          const _rr = [];
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) _rr.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
          }
          this._drawTwoToneSeamRects(ctx, _rr, 0.5 / 12);
        }
      }
      ctx.restore();
      // Fully suppressed case already returned above; if we reach here
      // we're in the fading transition — fall through to full detail
    }

    // Tile/paver LOD: when shapes are too small on screen, skip textures and edge detail
    // For planks, use the long dimension (tileH) since the short side is very narrow
    const _tileLODDim = (isTile && layout.tiles)
      ? Math.max(layout.tileW, layout.tileH)
      : (isPaver && layout.pavers) ? layout.gridStep : 1;
    const _tileLOD = (isTile && layout.tiles)
      ? (_tileLODDim * this.rd.basePixelsPerFoot * this.rd.zoomLevel >= 5)
      : (isPaver && layout.pavers)
        ? (_tileLODDim * this.rd.basePixelsPerFoot * this.rd.zoomLevel >= 5)
        : true;

    // Branch rendering: paver grid vs tile grid vs roll strips
    // During fade-in, full detail draws at partial alpha over the base layer
    if (layout.pavers) {
      if (layout.pavers.length === 0) return;
      // Pavers are too small on screen for textures to be visible — always use
      // solid color fill.  Skips 500+ per-paver save/clip/drawImage/restore.
      const _texImg = null;

      // ── Offscreen bitmap cache for pavers (non-zoned rooms only) ──
      // Pre-renders all 4 paver layers to an offscreen canvas, then blits with
      // a single drawImage(). Avoids 500+ save/clip/drawImage/restore per frame.
      if (_zonePass === 0) {
        const _showSeams = this.rd.showSeamLines;
        const _hideOvg = this.rd.hideOverage;
        const _eqMode = !!this.rd._equipmentMode;
        // Zoom-dependent resolution: match screen quality up to 96 px/ft
        const _cacheRes = Math.min(Math.max(Math.round(this.rd.zoomLevel), 1) * 20, 96);
        const pc = layout._paverBitmapCache;
        const pcValid = pc
          && pc.isDark === isDark
          && pc.showSeamLines === _showSeams
          && pc.hideOverage === _hideOvg
          && pc.tileLOD === _tileLOD
          && pc.equipmentMode === _eqMode
          && pc.cacheRes === _cacheRes;

        if (pcValid) {
          // Cache hit — blit cached bitmap
          ctx.save();
          if (_detailFading) ctx.globalAlpha = _detailAlpha;
          if (layout._angle) {
            const { x: cx, y: cy } = layout._centroid;
            ctx.translate(cx, cy);
            ctx.rotate(layout._angle);
            ctx.translate(-cx, -cy);
          }
          this._applyTurfClip(ctx, room, geo, layout);
          ctx.drawImage(pc.canvas, pc.worldX, pc.worldY, pc.worldW, pc.worldH);
          ctx.restore();
          // Replay deferred data captured during cache build
          if (pc.scissors) for (const s of pc.scissors) this._pendingScissors.push(s);
          if (pc.turfOverage) for (const t of pc.turfOverage) this._pendingTurfOverage.push(t);
          return;
        }

        // Cache miss — render to offscreen canvas
        const { pavers, paverW, paverH } = layout;
        let pMinX = Infinity, pMinY = Infinity, pMaxX = -Infinity, pMaxY = -Infinity;
        const phw = paverW / 2, phh = paverH / 2;
        for (const p of pavers) {
          pMinX = Math.min(pMinX, p.cx - phw);
          pMinY = Math.min(pMinY, p.cy - phh);
          pMaxX = Math.max(pMaxX, p.cx + phw);
          pMaxY = Math.max(pMaxY, p.cy + phh);
        }
        // Margin for stroke lines and scissors
        const _margin = 2;
        pMinX -= _margin; pMinY -= _margin;
        pMaxX += _margin; pMaxY += _margin;
        const _worldW = pMaxX - pMinX;
        const _worldH = pMaxY - pMinY;
        let _cw = Math.ceil(_worldW * _cacheRes);
        let _ch = Math.ceil(_worldH * _cacheRes);
        const _maxDim = 4096;
        if (_cw > _maxDim || _ch > _maxDim) {
          const _ds = _maxDim / Math.max(_cw, _ch);
          _cw = Math.ceil(_cw * _ds);
          _ch = Math.ceil(_ch * _ds);
        }
        const _offCanvas = document.createElement('canvas');
        _offCanvas.width = _cw;
        _offCanvas.height = _ch;
        const _offCtx = _offCanvas.getContext('2d');
        // Transform: world coords → offscreen pixels
        _offCtx.setTransform(_cw / _worldW, 0, 0, _ch / _worldH, -pMinX * (_cw / _worldW), -pMinY * (_ch / _worldH));

        // Snapshot deferred arrays to capture what _drawPaverOverlay adds
        const _scStart = this._pendingScissors.length;
        const _toStart = this._pendingTurfOverage.length;

        this._drawPaverOverlay(_offCtx, layout, colors, geo, isDark, _texImg, _tileLOD, _zonePass, _wallDivSegsRotated, _zoneCentroidRotated, _wallDivHalfThk);

        // Capture deferred data added during render
        const _newSc = this._pendingScissors.slice(_scStart);
        const _newTo = this._pendingTurfOverage.slice(_toStart);

        layout._paverBitmapCache = {
          canvas: _offCanvas,
          worldX: pMinX, worldY: pMinY, worldW: _worldW, worldH: _worldH,
          isDark, showSeamLines: _showSeams, hideOverage: _hideOvg,
          tileLOD: _tileLOD,
          equipmentMode: _eqMode, cacheRes: _cacheRes,
          scissors: _newSc.length > 0 ? _newSc : null,
          turfOverage: _newTo.length > 0 ? _newTo : null
        };

        // Blit to main canvas
        ctx.save();
        if (_detailFading) ctx.globalAlpha = _detailAlpha;
        if (layout._angle) {
          const { x: cx, y: cy } = layout._centroid;
          ctx.translate(cx, cy);
          ctx.rotate(layout._angle);
          ctx.translate(-cx, -cy);
        }
        this._applyTurfClip(ctx, room, geo, layout);
        ctx.drawImage(_offCanvas, pMinX, pMinY, _worldW, _worldH);
        ctx.restore();
        return;
      }

      // Zoned rendering (zone passes 1 or 2) — uncached path
      ctx.save();
      if (_detailFading) ctx.globalAlpha = _detailAlpha;
      if (layout._angle) {
        const { x: cx, y: cy } = layout._centroid;
        ctx.translate(cx, cy);
        ctx.rotate(layout._angle);
        ctx.translate(-cx, -cy);
      }
      this._applyTurfClip(ctx, room, geo, layout);
      this._drawPaverOverlay(ctx, layout, colors, geo, isDark, _texImg, _tileLOD, _zonePass, _wallDivSegsRotated, _zoneCentroidRotated, _wallDivHalfThk);
      ctx.restore();
      return;
    }
    if (layout.tiles) {
      if (layout.tiles.length === 0) return;
      ctx.save();
      if (_detailFading) ctx.globalAlpha = _detailAlpha;
      if (layout._angle) {
        const { x: cx, y: cy } = layout._centroid;
        ctx.translate(cx, cy);
        ctx.rotate(layout._angle);
        ctx.translate(-cx, -cy);
      }
      this._applyTurfClip(ctx, room, geo, layout);
      this._drawTileOverlay(ctx, layout, colors, geo, isDark, _tileLOD ? textureImg : null, _tileLOD, _zonePass, _wallDivSegsRotated, _zoneCentroidRotated, _wallDivHalfThk, product);
      ctx.restore();
      return;
    }

    const { strips, roomMinY, roomMaxY, roomMinX, roomMaxX } = layout;
    if (strips.length === 0) return;

    ctx.save();
    if (_detailFading) ctx.globalAlpha = _detailAlpha;

    // ── Rotation transform (inverse-rotate strategy) ──
    if (layout._angle) {
      const { x: cx, y: cy } = layout._centroid;
      ctx.translate(cx, cy);
      ctx.rotate(layout._angle);
      ctx.translate(-cx, -cy);
    }

    // Clip around turf objects (always active when turf present)
    this._applyTurfClip(ctx, room, geo, layout);

    // Clip to room interior when hideOverage is on (only show kept floor, not waste)
    if (this.rd.hideOverage && _zonePass !== 2) {
      ctx.save();
      const clipPoly = layout._rotatedPoly || poly;
      const clipExcl = layout._rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      ctx.beginPath();
      ctx.moveTo(clipPoly[0].x, clipPoly[0].y);
      for (let i = 1; i < clipPoly.length; i++) ctx.lineTo(clipPoly[i].x, clipPoly[i].y);
      ctx.closePath();
      for (const excl of clipExcl) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      ctx.clip('evenodd');
    }

    // ── Layer 1: Grid dimming base (covers full roll extent) ──
    if (_zonePass !== 2) {
    ctx.fillStyle = colors.base;
    ctx.beginPath();
    for (const strip of strips) {
      for (const roll of strip.rolls) {
        ctx.rect(strip.x - _seamEps, roll.y - _seamEps, strip.width + 2 * _seamEps, roll.length + 2 * _seamEps);
      }
    }
    ctx.fill();

    // ── Layer 2: Strip fill (texture or uniform color, full extent) ──
    if (textureImg) {
      // Per-roll texture with pi-driven rotation (same approach as tiles).
      // 200 digits of pi — more than enough for rolls since there are far fewer per room.
      const _piRoll = '14159265358979323846264338327950288419716939937510582097494459230781640628620899862803482534211706798214808651328230664709384460955058223172535940812848111745028841027019385211055596446229489549303819644288109756659334461284756482337867831652712019091456485669234603486104';
      const _piRLen = _piRoll.length;
      const _rollBleed = 0.5 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
      const _noRotate = !!product.textureNoRotate;
      // Tile each roll in square cells (cellW × cellW) so the texture isn't stretched.
      // Each cell gets pi-driven rotation instead of the old fixed 2×2 pattern.
      // Wood grain mode (_noRotate): alternates vertical flip instead of rotating,
      // so grain direction stays consistent along the roll length.
      const cellW = strips[0].width;
      for (const strip of strips) {
        for (const roll of strip.rolls) {
          const rx = strip.x - _seamEps;
          const ry = roll.y - _seamEps;
          const rw = strip.width + 2 * _seamEps;
          const rh = roll.length + 2 * _seamEps;
          const nCells = Math.ceil(rh / cellW);
          const col = Math.round(strip.x / cellW);
          for (let ci = 0; ci < nCells; ci++) {
            const cy = ry + ci * cellW;
            const ch = Math.min(cellW, ry + rh - cy);
            const row = Math.round((roll.y + ci * cellW) / cellW);
            if (_noRotate) {
              // Wood grain: alternate vertical flip (scaleY -1) per cell
              const flipY = (col + row) % 2 !== 0;
              if (flipY) {
                ctx.save();
                ctx.translate(rx + rw / 2, cy + ch / 2);
                ctx.scale(1, -1);
                ctx.drawImage(textureImg, -rw / 2 - _rollBleed, -ch / 2 - _rollBleed, rw + 2 * _rollBleed, ch + 2 * _rollBleed);
                ctx.restore();
              } else {
                ctx.drawImage(textureImg, rx - _rollBleed, cy - _rollBleed, rw + 2 * _rollBleed, ch + 2 * _rollBleed);
              }
            } else {
              const _piIdx = (((col * 997) + (row * 1013)) % _piRLen + _piRLen) % _piRLen;
              const _piDig = _piRoll.charCodeAt(_piIdx) - 48;
              const rot = (_piDig % 4) * Math.PI / 2;
              if (rot) {
                ctx.save();
                ctx.translate(rx + rw / 2, cy + ch / 2);
                ctx.rotate(rot);
                // Swap draw dimensions for 90°/270° so rotated texture covers the cell rect
                const isOrtho = (rot === Math.PI / 2 || rot === 3 * Math.PI / 2);
                const dw = isOrtho ? ch : rw;
                const dh = isOrtho ? rw : ch;
                ctx.drawImage(textureImg, -dw / 2 - _rollBleed, -dh / 2 - _rollBleed, dw + 2 * _rollBleed, dh + 2 * _rollBleed);
                ctx.restore();
              } else {
                ctx.drawImage(textureImg, rx - _rollBleed, cy - _rollBleed, rw + 2 * _rollBleed, ch + 2 * _rollBleed);
              }
            }
          }
        }
      }
    } else {
      ctx.fillStyle = colors.strip;
      ctx.beginPath();
      for (const strip of strips) {
        for (const roll of strip.rolls) {
          ctx.rect(strip.x - _seamEps, roll.y - _seamEps, strip.width + 2 * _seamEps, roll.length + 2 * _seamEps);
        }
      }
      ctx.fill();
    }
    } // end pass !== 2

    // ── Layer 2.5 + 2.75: Red overage fill + trash icons (shared clip) ──
    const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
    const overagePoly = layout._rotatedPoly || poly;
    const turfExcl = this._getTurfExclusionsForRoom(room, geo);
    let overageExcl;
    let turfExclRotated;
    if (layout._rotatedExclusions) {
      turfExclRotated = (layout._angle && layout._centroid)
        ? turfExcl.map(tp => this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y))
        : turfExcl;
      overageExcl = [...layout._rotatedExclusions, ...turfExclRotated];
    } else {
      turfExclRotated = turfExcl;
      overageExcl = [...exclusions, ...turfExcl];
    }

    if (!this.rd.hideOverage && _zonePass !== 1) {
    const overageColor = (colors.overage) || 'rgba(204, 50, 50, 0.25)';
    ctx.save();

    // Clip to all roll rectangles (shared by both layers)
    const rects = [];
    ctx.beginPath();
    for (const strip of strips) {
      for (const roll of strip.rolls) {
        ctx.rect(strip.x, roll.y, strip.width, roll.length);
        rects.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
      }
    }
    ctx.clip();

    // Build even-odd path (waste = outside room + inside exclusions)
    // Use rotated polygon when angle != 0 (strip rects are in rotated space)
    ctx.beginPath();
    ctx.rect(roomMinX - 50, roomMinY - 50,
             (roomMaxX - roomMinX) + 100, (roomMaxY - roomMinY) + 100);
    ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
    for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
    ctx.closePath();
    for (const excl of overageExcl) {
      if (!excl || excl.length < 3) continue;
      ctx.moveTo(excl[0].x, excl[0].y);
      for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
      ctx.closePath();
    }

    // Red overage fill (path still available after fill)
    ctx.fillStyle = overageColor;
    ctx.fill('evenodd');

    ctx.restore();

    // Collect turf overage data for deferred rendering on top of turf objects
    // Expand rects by overage so they extend into turf gaps (rolls are excluded
    // from turf areas during layout, so raw rects don't overlap turf polygons)
    if (turfExcl.length > 0 && rects.length > 0) {
      const _ov = product.overageFt || 0.5;
      const _turfRects = rects.map(r => ({
        x: r.x - _ov, y: r.y - _ov, w: r.w + 2 * _ov, h: r.h + 2 * _ov
      }));
      let _trMinX = Infinity, _trMinY = Infinity, _trMaxX = -Infinity, _trMaxY = -Infinity;
      for (const r of _turfRects) {
        _trMinX = Math.min(_trMinX, r.x); _trMinY = Math.min(_trMinY, r.y);
        _trMaxX = Math.max(_trMaxX, r.x + r.w); _trMaxY = Math.max(_trMaxY, r.y + r.h);
      }
      this._pendingTurfOverage.push({
        rects: _turfRects, rMinX: _trMinX, rMinY: _trMinY, rMaxX: _trMaxX, rMaxY: _trMaxY,
        overagePoly, overageExcl, overageColor,
        angle: layout._angle || 0, centroid: layout._centroid,
        turfExclRotated, turfExclWorld: turfExcl
      });
    }
    } // end if (!hideOverage)

    // ── Layer 3: Seam lines (head seams + vertical roll boundaries) ──
    if (_zonePass !== 2 && this.rd.showSeamLines) {
    const _rollRects = [];
    for (const strip of strips) {
      for (const roll of strip.rolls) _rollRects.push({ x: strip.x, y: roll.y, w: strip.width, h: roll.length });
    }
    this._drawTwoToneSeamRects(ctx, _rollRects, 0.5 / 12);
    } // end pass !== 2

    // ── Layer 4b: Scissors (fade in 150%→200% zoom, drawn under walls) ──
    const rd = this.rd;
    const zoom = rd.zoomLevel;
    const measAlpha = Math.min(Math.max((zoom - 0.75) / 0.5, 0), 1);
    if (measAlpha > 0 && _zonePass !== 1) {
      ctx.save();
      ctx.globalAlpha = measAlpha;
      const scale = rd.basePixelsPerFoot * zoom;
      // ── Scissors icons along room-wall cut lines ──
      // Scissors use their own zoom threshold (150%→200%), independent of measurements
      const scissorAlpha = Math.min(Math.max((zoom - 1.5) / 0.5, 0), 1);
      if (scissorAlpha > 0 && !this.rd.hideOverage) {
      ctx.globalAlpha = scissorAlpha;
      const scissorSize = Math.max(16 / scale, 16 / (rd.basePixelsPerFoot * 3));
      const scissorSpacing = 2.5; // feet between icons
      const scissorColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,15,28,0.55)';

      // Collect scissors positions for batched rendering (2 strokes total)
      const _scissorBatch = [];
      const drawScissors = (sx, sy, angle) => {
        _scissorBatch.push({ x: sx, y: sy, angle });
      };

      // ── Room-boundary scissors (polygon edges clipped to room-interior area) ──
      // Scissors appear along polygon edges that cross the room interior within
      // each strip. Clipped to segment bounds (not roll bounds) so scissors never
      // appear in pure overage areas outside the room.
      const scissorPoly = layout._rotatedPoly || poly;
      const allBoundaryPolys = [scissorPoly, ...overageExcl.filter(e => e && e.length >= 3)];
      for (const strip of strips) {
        // Room-interior Y extent (segment bounds, NOT roll bounds with overage)
        // Scissors only belong where the polygon edge crosses the actual room area
        let sMinY = Infinity, sMaxY = -Infinity;
        for (const seg of strip.segments) {
          sMinY = Math.min(sMinY, seg.segMinY);
          sMaxY = Math.max(sMaxY, seg.segMaxY);
        }
        // Clip rect: X inset keeps scissors inside strip width;
        // Y expands past room boundary so wall edges at segMinY/segMaxY get scissors
        const inset = 0.005;
        const clipX1 = strip.x + inset, clipY1 = sMinY - inset;
        const clipX2 = strip.x + strip.width - inset, clipY2 = sMaxY + inset;

        for (const bPoly of allBoundaryPolys) {
          for (let ei = 0, ej = bPoly.length - 1; ei < bPoly.length; ej = ei++) {
            const clipped = this._clipLineToRect(
              bPoly[ej].x, bPoly[ej].y, bPoly[ei].x, bPoly[ei].y,
              clipX1, clipY1, clipX2, clipY2
            );
            if (!clipped) continue;

            const dx = clipped.x2 - clipped.x1;
            const dy = clipped.y2 - clipped.y1;
            const len = Math.sqrt(dx * dx + dy * dy);
            if (len < 0.1) continue;

            const edgeAngle = Math.atan2(dy, dx);
            const count = Math.max(1, Math.round(len / scissorSpacing));
            for (let k = 1; k <= count; k++) {
              const t = k / (count + 1);
              drawScissors(
                clipped.x1 + t * dx,
                clipped.y1 + t * dy,
                edgeAngle + Math.PI / 2
              );
            }
          }
        }
      }

      // ── Head/end seam scissors with dotted cut lines (per-segment) ──
      // Pieces shorter than minLengthFt were cut from a longer purchased roll.
      // Dotted line just inside the piece, gap from the seam, scissors straddle it.
      const minLen = product.minLengthFt;
      const seamHalfW = (1.2 / 12) / 2; // half the seam line width
      const cutLineGap = seamHalfW; // dotted line right against inner edge of seam line
      const dashLen = 3 / 12;       // dash segment length (3")
      const dashGap = 2 / 12;       // dash gap (2")
      if (layout.hasHeadSeams) {
        for (const strip of strips) {
          const sx = strip.x;
          const sw = strip.width;

          // Helper: draw a horizontal dotted line + scissors, clipped to room polygon
          const cutLinePoly = layout._rotatedPoly || poly;
          const drawCutLine = (lineY, x0, x1) => {
            // Horizontal scanline: find X segments where room exists at this Y
            const hits = [];
            for (let pi = 0, pj = cutLinePoly.length - 1; pi < cutLinePoly.length; pj = pi++) {
              const yi = cutLinePoly[pi].y, yj = cutLinePoly[pj].y;
              const xi = cutLinePoly[pi].x, xj = cutLinePoly[pj].x;
              if ((yi < lineY && yj >= lineY) || (yj < lineY && yi >= lineY)) {
                hits.push(xi + (lineY - yi) / (yj - yi) * (xj - xi));
              }
            }
            hits.sort((a, b) => a - b);

            // Draw clipped segments (even-odd pairing)
            for (let h = 0; h + 1 < hits.length; h += 2) {
              const cx0 = Math.max(x0, hits[h]);
              const cx1 = Math.min(x1, hits[h + 1]);
              if (cx1 - cx0 < 0.05) continue; // skip tiny slivers
              ctx.save();
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1 / scale;
              ctx.setLineDash([dashLen, dashGap]);
              ctx.beginPath();
              ctx.moveTo(cx0, lineY);
              ctx.lineTo(cx1, lineY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();
              // Scissors along clipped segment
              const segW = cx1 - cx0;
              const count = Math.max(1, Math.round(segW / scissorSpacing));
              const step = segW / (count + 1);
              for (let i = 1; i <= count; i++) {
                drawScissors(cx0 + i * step, lineY, Math.PI / 2);
              }
            }
          };

          for (const seg of strip.segments) {
            if (!seg.hasHeadSeams) continue;
            const numRolls = seg.rolls.length;

            for (let r = 0; r < numRolls; r++) {
              const roll = seg.rolls[r];
              const isShort = roll.length < minLen - 0.01;

              // Head seam at bottom of this roll
              if (r < numRolls - 1) {
                const seamY = roll.y + roll.length;
                const nextRoll = seg.rolls[r + 1];
                const nextIsShort = nextRoll.length < minLen - 0.01;

                // Dotted line + scissors inside piece ABOVE seam
                if (isShort) {
                  drawCutLine(seamY - cutLineGap, sx, sx + sw);
                }
                // Dotted line + scissors inside piece BELOW seam
                if (nextIsShort) {
                  drawCutLine(seamY + cutLineGap, sx, sx + sw);
                }
              }

              // Outer end of short piece (end that's NOT a head seam)
              // Use wall boundary (segMinY/segMaxY), not roll edge which extends into overage
              if (isShort) {
                // Top of first roll — dotted line at the wall boundary
                if (r === 0) {
                  drawCutLine(seg.segMinY, sx, sx + sw);
                }
                // Bottom of last roll — dotted line at the wall boundary
                if (r === numRolls - 1) {
                  drawCutLine(seg.segMaxY, sx, sx + sw);
                }
              }
            }
          }
        }
      }

      // Offset scissors near divider walls toward zone edge
      if (_wallDivSegsRotated) {
        this._offsetScissorsForDividers(_scissorBatch, _wallDivSegsRotated, _zoneCentroidRotated, _wallDivHalfThk);
      }

      // ── Defer scissors to _afterRoomsHook so they draw on top of walls ──
      const minDistSq = scissorSize * scissorSize * 0.25;
      const deduped = [];
      for (const sc of _scissorBatch) {
        let replaceIdx = -1;
        for (let i = 0; i < deduped.length; i++) {
          const dx = sc.x - deduped[i].x, dy = sc.y - deduped[i].y;
          if (dx * dx + dy * dy < minDistSq) { replaceIdx = i; break; }
        }
        if (replaceIdx >= 0) deduped[replaceIdx] = sc;
        else deduped.push(sc);
      }
      if (deduped.length > 0) {
        this._pendingScissors.push({
          batch: deduped,
          scissorSize,
          scissorColor,
          scissorAlpha,
          isDark,
          rotAngle: layout._angle || 0,
          centroid: layout._centroid
        });
      }
      } // end scissorAlpha guard

      ctx.restore(); // end measAlpha
    }

    // Restore canvas state from hideOverage clip
    if (this.rd.hideOverage && _zonePass !== 2) ctx.restore();

    ctx.restore();
  }

  // ── Tile Overlay Rendering ──────────────

  _drawTileOverlay(ctx, layout, colors, geo, isDark, textureImg, highDetail, zonePass, wallDivSegsRotated, zoneCentroidRotated, wallDivHalfThk, product) {
    const _zp = zonePass || 0; // 0 = both, 1 = tiles only, 2 = overage only
    const { tiles, tileW, tileH, roomMinX, roomMaxX, roomMinY, roomMaxY } = layout;
    const _seamEps = 0.25 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
    const overagePoly = layout._rotatedPoly || geo.roomPolygon;
    const exclusions = layout._rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
    const _tileTurfExcl = this._getTurfExclusionsForRoom(null, geo);
    const _tileTurfRotated = (layout._angle && layout._centroid)
      ? _tileTurfExcl.map(tp => this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y))
      : _tileTurfExcl;
    const _tileOverageExcl = _tileTurfRotated.length > 0 ? [...exclusions, ..._tileTurfRotated] : exclusions;

    // Clip to room interior when hideOverage is on (only show kept floor, not waste)
    if (this.rd.hideOverage && _zp !== 2) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
      for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
      ctx.closePath();
      for (const excl of exclusions) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      ctx.clip('evenodd');
    }

    // ── Layer 1: Base + fill per tile ──
    const _isPlank = product && product.shape === 'plank';
    if (_zp !== 2 && textureImg) {
      const _tileBleed = 0.5 / (this.rd.basePixelsPerFoot * this.rd.zoomLevel);
      if (_isPlank) {
        // Plank texture: draw texture at consistent scale, shifted horizontally per plank
        // so each plank shows a different grain slice. The texture is drawn at the plank's
        // height (maintaining aspect ratio), then shifted left/right by a pi-driven offset.
        // Clipped to plank bounds so each plank is a unique "window" into the grain.
        const _srcW = textureImg.naturalWidth || textureImg.width;
        const _srcH = textureImg.naturalHeight || textureImg.height;
        // Draw texture at plank height, maintaining aspect ratio
        const _drawH = tileH + 2 * _tileBleed;
        const _drawW = _drawH * (_srcW / _srcH); // aspect-correct width
        // Pi digits for pseudo-random offsets — non-repeating, deterministic per position
        const _piPlank = '14159265358979323846264338327950288419716939937510582097494459230781640628620899862803482534211706798214808651328230664709384460955058223172535940812848111745028410270193852110555964462294895493038196442881097566593344612847564823378678316527120190914564856692346034861045432664821339360726024914127372458700660631558817488152092096282925409171536436789259036001133053054882046652138414695194151160943305727036575959195309218611738193261179310511854807446237996274956735188575272489122793818301194912983367336244065664308602139494639522473719070217986094370277053921717629317675238467481846766940513200056812714526356082778577134275778960917363717872146844090122495343014654958537105079227968925892354201995611212902196086403441815981362977471';
        const _piPLen = _piPlank.length;
        for (const t of tiles) {
          const row = Math.round(t.y / tileH);
          const col = Math.round(t.x / tileW);
          const flipY = (col + row) % 2 !== 0;
          // Pi-driven horizontal offset: 2 digits → 0-99, mapped to full texture width
          const _piIdx = (((col * 997) + (row * 1013)) % (_piPLen - 1) + _piPLen) % (_piPLen - 1);
          const _piVal = (_piPlank.charCodeAt(_piIdx) - 48) * 10 + (_piPlank.charCodeAt(_piIdx + 1) - 48); // 0-99
          const _shiftX = (_piVal / 100) * _drawW; // shift as fraction of drawn width
          ctx.save();
          ctx.beginPath();
          ctx.rect(t.x - _tileBleed, t.y - _tileBleed, t.w + 2 * _tileBleed, _drawH);
          ctx.clip();
          if (flipY) {
            ctx.translate(t.x + t.w / 2, t.y + t.h / 2);
            ctx.scale(1, -1);
            const _bx = -(t.w / 2) - _tileBleed - _shiftX;
            ctx.drawImage(textureImg, _bx, -_drawH / 2, _drawW, _drawH);
            ctx.drawImage(textureImg, _bx + _drawW, -_drawH / 2, _drawW, _drawH);
          } else {
            const _bx = t.x - _tileBleed - _shiftX;
            ctx.drawImage(textureImg, _bx, t.y - _tileBleed, _drawW, _drawH);
            ctx.drawImage(textureImg, _bx + _drawW, t.y - _tileBleed, _drawW, _drawH);
          }
          ctx.restore();
        }
      } else {
        // Tiles draw the FULL source texture per tile, rotated by an angle derived from the digits
        // of pi. Pi's digits are proven non-repeating, so no rotation pattern can ever emerge at any
        // scale. Square tiles get the full 0/90/180/270° set; rectangular tiles (e.g. 46"×23") are
        // restricted to 0/180° because 90°/270° would draw the texture rotated to h×w on canvas,
        // leaving dark gaps inside the tile rect.
        const _piDigits = '1415926535897932384626433832795028841971693993751058209749445923078164062862089986280348253421170679821480865132823066470938446095505822317253594081284811174502841027019385211055596446229489549303819644288109756659334461284756482337867831652712019091456485669234603486104543266482133936072602491412737245870066063155881748815209209628292540917153643678925903600113305305488204665213841469519415116094330572703657595919530921861173819326117931051185480744623799627495673518857527248912279381830119491298336733624406566430860213949463952247371907021798609437027705392171762931767523846748184676694051320005681271452635608277857713427577896091736371787214684409012249534301465495853710507922796892589235420199561121290219608640344181598136297747713099605187072113499999983729780499510597317328160963185950244594553469083026425223082533446850352619311881710100031378387528865875332083814206171776691473035982534904287554687311595628638823537875937519577818577805321712268066130019278766111959092164201989';
        const _piLen = _piDigits.length;
        const _isSquareTile = Math.abs(tileW - tileH) < 0.001;
        for (const t of tiles) {
          const col = Math.round(t.x / tileW);
          const row = Math.round(t.y / tileH);
          const _piIdx = (((col * 997) + (row * 1013)) % _piLen + _piLen) % _piLen;
          const _piDig = _piDigits.charCodeAt(_piIdx) - 48; // 0-9
          const rot = _isSquareTile
            ? (_piDig % 4) * Math.PI / 2  // 0°, 90°, 180°, 270°
            : (_piDig % 2) * Math.PI;     // 0° or 180° only — preserve tile aspect on canvas
          if (rot) {
            ctx.save();
            ctx.translate(t.x + t.w / 2, t.y + t.h / 2);
            ctx.rotate(rot);
            ctx.drawImage(textureImg, -(t.w + 2 * _tileBleed) / 2, -(t.h + 2 * _tileBleed) / 2, t.w + 2 * _tileBleed, t.h + 2 * _tileBleed);
            ctx.restore();
          } else {
            ctx.drawImage(textureImg, t.x - _tileBleed, t.y - _tileBleed, t.w + 2 * _tileBleed, t.h + 2 * _tileBleed);
          }
        }
      }
    } else if (_zp !== 2) {
      ctx.fillStyle = colors.base;
      ctx.beginPath();
      for (const t of tiles) ctx.rect(t.x - _seamEps, t.y - _seamEps, t.w + 2 * _seamEps, t.h + 2 * _seamEps);
      ctx.fill();
      ctx.fillStyle = colors.strip;
      ctx.beginPath();
      for (const t of tiles) ctx.rect(t.x - _seamEps, t.y - _seamEps, t.w + 2 * _seamEps, t.h + 2 * _seamEps);
      ctx.fill();
    }

    // Shared references for layers 2-4
    const rd = this.rd;
    const zoom = rd.zoomLevel;

    // ── Layer 2: Overage fill + OVERAGE text (shared clip to tile rects) ──
    if (!this.rd.hideOverage && _zp !== 1) {
    const overageColor = colors.overage || 'rgba(204, 50, 50, 0.25)';
    ctx.save();
    const _tileRects = [];
    ctx.beginPath();
    for (const t of tiles) { ctx.rect(t.x, t.y, t.w, t.h); _tileRects.push({ x: t.x, y: t.y, w: t.w, h: t.h }); }
    ctx.clip();

    // Even-odd path: big rect minus room polygon plus exclusions (incl. turf)
    ctx.beginPath();
    ctx.rect(roomMinX - 50, roomMinY - 50, (roomMaxX - roomMinX) + 100, (roomMaxY - roomMinY) + 100);
    ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
    for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
    ctx.closePath();
    for (const excl of _tileOverageExcl) {
      if (!excl || excl.length < 3) continue;
      ctx.moveTo(excl[0].x, excl[0].y);
      for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
      ctx.closePath();
    }
    ctx.fillStyle = overageColor;
    ctx.fill('evenodd');

    ctx.restore();

    // Collect turf overage data for deferred rendering on top of turf objects
    // Expand rects by overage so they extend into turf gaps
    if (_tileTurfExcl.length > 0 && _tileRects.length > 0) {
      const _tov = 0.5; // 6" overage expansion (product.overageFt not in scope)
      const _tileTurfRects = _tileRects.map(r => ({
        x: r.x - _tov, y: r.y - _tov, w: r.w + 2 * _tov, h: r.h + 2 * _tov
      }));
      let _ttrMinX = Infinity, _ttrMinY = Infinity, _ttrMaxX = -Infinity, _ttrMaxY = -Infinity;
      for (const r of _tileTurfRects) {
        _ttrMinX = Math.min(_ttrMinX, r.x); _ttrMinY = Math.min(_ttrMinY, r.y);
        _ttrMaxX = Math.max(_ttrMaxX, r.x + r.w); _ttrMaxY = Math.max(_ttrMaxY, r.y + r.h);
      }
      this._pendingTurfOverage.push({
        rects: _tileTurfRects, rMinX: _ttrMinX, rMinY: _ttrMinY, rMaxX: _ttrMaxX, rMaxY: _ttrMaxY,
        overagePoly, overageExcl: _tileOverageExcl, overageColor,
        angle: layout._angle || 0, centroid: layout._centroid,
        turfExclRotated: _tileTurfRotated, turfExclWorld: _tileTurfExcl
      });
    }
    } // end if (!hideOverage)

    // ── Layer 3: Tile grid seam lines — matches roll seam style ──
    if (highDetail && _zp !== 2 && this.rd.showSeamLines) {
    this._drawTwoToneSeamRects(ctx, tiles, 0.5 / 12);
    } // end tile seam lines

    // ── Layer 4: Scissors along room boundary edges (clipped to tile extent) ──
    const scissorAlpha = Math.min(Math.max((zoom - 1.5) / 0.5, 0), 1);
    if (scissorAlpha > 0 && !this.rd.hideOverage && _zp !== 1) {
      const scale = rd.basePixelsPerFoot * zoom;
      const scissorSize = Math.max(16 / scale, 16 / (rd.basePixelsPerFoot * 3));
      const scissorSpacing = 2.5;
      const scissorColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,15,28,0.55)';

      // Compute tile extent bounding box for clipping
      let tMinX = Infinity, tMinY = Infinity, tMaxX = -Infinity, tMaxY = -Infinity;
      for (const t of tiles) {
        tMinX = Math.min(tMinX, t.x); tMinY = Math.min(tMinY, t.y);
        tMaxX = Math.max(tMaxX, t.x + t.w); tMaxY = Math.max(tMaxY, t.y + t.h);
      }
      const inset = 0.005;

      const _scissorBatch = [];
      const allBoundaryPolys = [overagePoly, ..._tileOverageExcl.filter(e => e && e.length >= 3)];
      for (const bPoly of allBoundaryPolys) {
        for (let ei = 0, ej = bPoly.length - 1; ei < bPoly.length; ej = ei++) {
          const clipped = this._clipLineToRect(
            bPoly[ej].x, bPoly[ej].y, bPoly[ei].x, bPoly[ei].y,
            tMinX + inset, tMinY + inset, tMaxX - inset, tMaxY - inset
          );
          if (!clipped) continue;
          const dx = clipped.x2 - clipped.x1;
          const dy = clipped.y2 - clipped.y1;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len < 0.1) continue;
          const edgeAngle = Math.atan2(dy, dx);
          const count = Math.max(1, Math.round(len / scissorSpacing));
          for (let k = 1; k <= count; k++) {
            const t = k / (count + 1);
            _scissorBatch.push({
              x: clipped.x1 + t * dx,
              y: clipped.y1 + t * dy,
              angle: edgeAngle + Math.PI / 2
            });
          }
        }
      }

      // Offset scissors near divider walls toward zone edge
      if (wallDivSegsRotated) {
        this._offsetScissorsForDividers(_scissorBatch, wallDivSegsRotated, zoneCentroidRotated, wallDivHalfThk);
      }

      // Deduplicate scissors and defer to _afterRoomsHook (drawn on top of walls)
      const minDistSq = scissorSize * scissorSize * 0.25;
      const deduped = [];
      for (const sc of _scissorBatch) {
        let replaceIdx = -1;
        for (let i = 0; i < deduped.length; i++) {
          const dx = sc.x - deduped[i].x, dy = sc.y - deduped[i].y;
          if (dx * dx + dy * dy < minDistSq) { replaceIdx = i; break; }
        }
        if (replaceIdx >= 0) deduped[replaceIdx] = sc;
        else deduped.push(sc);
      }
      if (deduped.length > 0) {
        this._pendingScissors.push({
          batch: deduped,
          scissorSize,
          scissorColor,
          scissorAlpha,
          isDark,
          rotAngle: layout._angle || 0,
          centroid: layout._centroid
        });
      }
    }

    // Restore canvas state from hideOverage clip
    if (this.rd.hideOverage && _zp !== 2) ctx.restore();
  }

  /**
   * Draw an interlocking dog-bone paver overlay for a single room/zone.
   * Layers: 1) base fill (texture or solid), 2) overage, 3) edge lines, 4) scissors.
   */
  _drawPaverOverlay(ctx, layout, colors, geo, isDark, textureImg, highDetail, zonePass, wallDivSegsRotated, zoneCentroidRotated, wallDivHalfThk) {
    const _zp = zonePass || 0;
    const { pavers, paverW, paverH, notchDepth, headHeight, roomMinX, roomMaxX, roomMinY, roomMaxY } = layout;
    const overagePoly = layout._rotatedPoly || geo.roomPolygon;
    const exclusions = layout._rotatedExclusions || [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
    const _paverTurfExcl = this._getTurfExclusionsForRoom(null, geo);
    const _paverTurfRotated = (layout._angle && layout._centroid)
      ? _paverTurfExcl.map(tp => this._rotatePolygon(tp, -layout._angle, layout._centroid.x, layout._centroid.y))
      : _paverTurfExcl;
    const _paverOverageExcl = _paverTurfRotated.length > 0 ? [...exclusions, ..._paverTurfRotated] : exclusions;

    // Clip to room interior when hideOverage is on
    if (this.rd.hideOverage && _zp !== 2) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
      for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
      ctx.closePath();
      for (const excl of exclusions) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      ctx.clip('evenodd');
    }

    // ── Layer 1: Base fill per paver ──
    if (_zp !== 2) {
      if (textureImg) {
        // Crop-based texture: each paver samples from a different region of the
        // source swatch, creating natural variation (same approach as tiles).
        const _srcW = textureImg.naturalWidth || textureImg.width;
        const _srcH = textureImg.naturalHeight || textureImg.height;
        const _cropSz = Math.floor(Math.min(_srcW, _srcH) / 4);
        const _maxOX = _srcW - _cropSz;
        const _maxOY = _srcH - _cropSz;
        const _gs = layout.gridStep || paverW;
        for (const p of pavers) {
          ctx.save();
          ctx.beginPath();
          this._paverPath(ctx, p.cx, p.cy, paverW, paverH, notchDepth, headHeight, p.rot);
          ctx.clip();
          const _pc = Math.round(p.cx / _gs);
          const _pr = Math.round(p.cy / _gs);
          let _h = ((_pc * 2654435761) + (_pr * 2246822519)) >>> 0;
          _h = (((_h >>> 16) ^ _h) * 0x45d9f3b) >>> 0;
          const _cropX = _h % (_maxOX + 1);
          const _cropY = ((_h >>> 8) % (_maxOY + 1));
          if (p.rot) {
            ctx.translate(p.cx, p.cy);
            ctx.rotate(Math.PI / 2);
            ctx.drawImage(textureImg, _cropX, _cropY, _cropSz, _cropSz,
              -paverW / 2, -paverH / 2, paverW, paverH);
          } else {
            ctx.drawImage(textureImg, _cropX, _cropY, _cropSz, _cropSz,
              p.cx - paverW / 2, p.cy - paverH / 2, paverW, paverH);
          }
          ctx.restore();
        }
      } else {
        ctx.fillStyle = colors.strip;
        ctx.beginPath();
        for (const p of pavers) this._paverPath(ctx, p.cx, p.cy, paverW, paverH, notchDepth, headHeight, p.rot);
        ctx.fill();
      }
    }

    const rd = this.rd;
    const zoom = rd.zoomLevel;

    // ── Layer 2: Overage fill (area outside room but inside paver shapes) ──
    if (!this.rd.hideOverage && _zp !== 1) {
      const overageColor = colors.overage || 'rgba(204, 50, 50, 0.25)';
      ctx.save();
      // Clip to union of all paver shapes
      ctx.beginPath();
      for (const p of pavers) this._paverPath(ctx, p.cx, p.cy, paverW, paverH, notchDepth, headHeight, p.rot);
      ctx.clip();
      // Even-odd: big rect minus room polygon plus exclusions (incl. turf)
      ctx.beginPath();
      ctx.rect(roomMinX - 50, roomMinY - 50, (roomMaxX - roomMinX) + 100, (roomMaxY - roomMinY) + 100);
      ctx.moveTo(overagePoly[0].x, overagePoly[0].y);
      for (let i = 1; i < overagePoly.length; i++) ctx.lineTo(overagePoly[i].x, overagePoly[i].y);
      ctx.closePath();
      for (const excl of _paverOverageExcl) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      ctx.fillStyle = overageColor;
      ctx.fill('evenodd');
      ctx.restore();

      // Collect turf overage data for deferred rendering on top of turf objects
      // Expand rects by overage so they extend into turf gaps
      if (_paverTurfExcl.length > 0) {
        const _pov = 0.5; // 6" overage expansion (product.overageFt not in scope)
        const _paverRects = pavers.map(p => ({
          x: p.cx - p.w / 2 - _pov, y: p.cy - p.h / 2 - _pov,
          w: p.w + 2 * _pov, h: p.h + 2 * _pov
        }));
        let _prMinX = Infinity, _prMinY = Infinity, _prMaxX = -Infinity, _prMaxY = -Infinity;
        for (const r of _paverRects) {
          _prMinX = Math.min(_prMinX, r.x); _prMinY = Math.min(_prMinY, r.y);
          _prMaxX = Math.max(_prMaxX, r.x + r.w); _prMaxY = Math.max(_prMaxY, r.y + r.h);
        }
        this._pendingTurfOverage.push({
          rects: _paverRects, rMinX: _prMinX, rMinY: _prMinY, rMaxX: _prMaxX, rMaxY: _prMaxY,
          overagePoly, overageExcl: _paverOverageExcl, overageColor,
          angle: layout._angle || 0, centroid: layout._centroid,
          turfExclRotated: _paverTurfRotated, turfExclWorld: _paverTurfExcl
        });
      }
    }

    // ── Layer 3: Grid lines (always visible for pavers) ──
    if (_zp !== 2) {
      ctx.setLineDash([]);
      this._paverGridLines(ctx, layout, isDark);
    }

    // ── Layer 4: Scissors along room boundary edges ──
    const scissorAlpha = Math.min(Math.max((zoom - 1.5) / 0.5, 0), 1);
    if (scissorAlpha > 0 && !this.rd.hideOverage && _zp !== 1) {
      const scale = rd.basePixelsPerFoot * zoom;
      const scissorSize = Math.max(16 / scale, 16 / (rd.basePixelsPerFoot * 3));
      const scissorSpacing = 2.5;
      const scissorColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,15,28,0.55)';

      // Compute paver extent bounding box for clipping
      let tMinX = Infinity, tMinY = Infinity, tMaxX = -Infinity, tMaxY = -Infinity;
      for (const p of pavers) {
        tMinX = Math.min(tMinX, p.cx - p.w / 2); tMinY = Math.min(tMinY, p.cy - p.h / 2);
        tMaxX = Math.max(tMaxX, p.cx + p.w / 2); tMaxY = Math.max(tMaxY, p.cy + p.h / 2);
      }
      const inset = 0.005;

      const _scissorBatch = [];
      const allBoundaryPolys = [overagePoly, ..._paverOverageExcl.filter(e => e && e.length >= 3)];
      for (const bPoly of allBoundaryPolys) {
        for (let ei = 0, ej = bPoly.length - 1; ei < bPoly.length; ej = ei++) {
          const clipped = this._clipLineToRect(
            bPoly[ej].x, bPoly[ej].y, bPoly[ei].x, bPoly[ei].y,
            tMinX + inset, tMinY + inset, tMaxX - inset, tMaxY - inset
          );
          if (!clipped) continue;
          const dx = clipped.x2 - clipped.x1;
          const dy = clipped.y2 - clipped.y1;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len < 0.1) continue;
          const edgeAngle = Math.atan2(dy, dx);
          const count = Math.max(1, Math.round(len / scissorSpacing));
          for (let k = 1; k <= count; k++) {
            const t = k / (count + 1);
            _scissorBatch.push({
              x: clipped.x1 + t * dx,
              y: clipped.y1 + t * dy,
              angle: edgeAngle + Math.PI / 2
            });
          }
        }
      }

      // Offset scissors near divider walls toward zone edge
      if (wallDivSegsRotated) {
        this._offsetScissorsForDividers(_scissorBatch, wallDivSegsRotated, zoneCentroidRotated, wallDivHalfThk);
      }

      // Deduplicate and defer to _afterRoomsHook
      const minDistSq = scissorSize * scissorSize * 0.25;
      const deduped = [];
      for (const sc of _scissorBatch) {
        let replaceIdx = -1;
        for (let i = 0; i < deduped.length; i++) {
          const dx = sc.x - deduped[i].x, dy = sc.y - deduped[i].y;
          if (dx * dx + dy * dy < minDistSq) { replaceIdx = i; break; }
        }
        if (replaceIdx >= 0) deduped[replaceIdx] = sc;
        else deduped.push(sc);
      }
      if (deduped.length > 0) {
        this._pendingScissors.push({
          batch: deduped,
          scissorSize,
          scissorColor,
          scissorAlpha,
          isDark,
          rotAngle: layout._angle || 0,
          centroid: layout._centroid
        });
      }
    }

    // Restore canvas state from hideOverage clip
    if (this.rd.hideOverage && _zp !== 2) ctx.restore();
  }

  // ── Deferred measurements (drawn after walls so ticks overlap them) ──

  /**
   * Offset scissors that sit on divider wall center lines to the wall edge.
   * dividerSegs: wall-type divider segments (in same coord space as batch).
   * centroid: zone polygon centroid (to determine "inward" direction).
   * halfThk: half wall thickness (1/6 ft for 4" walls).
   */
  _offsetScissorsForDividers(batch, dividerSegs, centroid, halfThk) {
    if (!dividerSegs || dividerSegs.length === 0 || !centroid) return;
    for (const sc of batch) {
      for (const seg of dividerSegs) {
        const d = RoomDesignerMath.distanceToWall(sc, seg);
        if (d < 0.15) { // ~1.8" tolerance
          const sdx = seg.end.x - seg.start.x;
          const sdy = seg.end.y - seg.start.y;
          const slen = Math.sqrt(sdx * sdx + sdy * sdy);
          if (slen < 0.001) continue;
          // Perpendicular to segment
          const nx = -sdy / slen, ny = sdx / slen;
          // Direction toward zone centroid
          const toCx = centroid.x - sc.x, toCy = centroid.y - sc.y;
          const dot = toCx * nx + toCy * ny;
          const sign = dot > 0 ? 1 : -1;
          sc.x += sign * nx * halfThk;
          sc.y += sign * ny * halfThk;
          break;
        }
      }
    }
  }

  _drawDeferredScissors() {
    if (!this._pendingScissors || this._pendingScissors.length === 0) return;
    const ctx = this.rd.ctx;
    const scale = this.rd.basePixelsPerFoot * this.rd.zoomLevel;

    // Collect all scissors into world-space, then deduplicate globally
    // This prevents overlapping scissors on shared zone boundaries (divider lines)
    let globalScissors = [];
    let scissorSize = 0, scissorAlpha = 0, isDark = false;

    for (const entry of this._pendingScissors) {
      const { batch, rotAngle, centroid } = entry;
      scissorSize = entry.scissorSize;
      scissorAlpha = entry.scissorAlpha;
      isDark = entry.isDark;

      for (const sc of batch) {
        let wx = sc.x, wy = sc.y, wa = sc.angle;
        // Transform from rotated space back to world space
        if (rotAngle && centroid) {
          const cosR = Math.cos(rotAngle), sinR = Math.sin(rotAngle);
          const dx = sc.x - centroid.x, dy = sc.y - centroid.y;
          wx = centroid.x + dx * cosR - dy * sinR;
          wy = centroid.y + dx * sinR + dy * cosR;
          wa = sc.angle + rotAngle;
        }
        globalScissors.push({ x: wx, y: wy, angle: wa });
      }
    }

    // Global deduplication: when two scissors from adjacent zones overlap,
    // keep only the first one encountered
    const minDistSq = scissorSize * scissorSize * 0.25;
    const deduped = [];
    for (const sc of globalScissors) {
      let tooClose = false;
      for (let i = 0; i < deduped.length; i++) {
        const dx = sc.x - deduped[i].x, dy = sc.y - deduped[i].y;
        if (dx * dx + dy * dy < minDistSq) { tooClose = true; break; }
      }
      if (!tooClose) deduped.push(sc);
    }

    if (deduped.length === 0) return;

    ctx.save();
    ctx.globalAlpha = scissorAlpha;

    const s = scissorSize * 0.45;
    const r = s * 0.25;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const bladePath = new Path2D();
    const handlePath = new Path2D();
    for (const sc of deduped) {
      const cos = Math.cos(sc.angle), sin = Math.sin(sc.angle);
      const wx = (lx, ly) => sc.x + lx * cos - ly * sin;
      const wy = (lx, ly) => sc.y + lx * sin + ly * cos;
      bladePath.moveTo(wx(-s * 0.1, 0), wy(-s * 0.1, 0));
      bladePath.lineTo(wx(-s * 0.45, -s * 0.75), wy(-s * 0.45, -s * 0.75));
      bladePath.moveTo(wx(s * 0.1, 0), wy(s * 0.1, 0));
      bladePath.lineTo(wx(s * 0.45, -s * 0.75), wy(s * 0.45, -s * 0.75));
      const hlx = wx(-s * 0.25, s * 0.4), hly = wy(-s * 0.25, s * 0.4);
      handlePath.moveTo(hlx + r, hly);
      handlePath.arc(hlx, hly, r, 0, Math.PI * 2);
      const hrx = wx(s * 0.25, s * 0.4), hry = wy(s * 0.25, s * 0.4);
      handlePath.moveTo(hrx + r, hry);
      handlePath.arc(hrx, hry, r, 0, Math.PI * 2);
    }

    // Halo pass (outline so scissors stand out on solid walls)
    ctx.strokeStyle = isDark ? 'rgba(20,28,46,0.9)' : 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 3.5 / scale;
    ctx.stroke(bladePath);
    ctx.stroke(handlePath);

    // Foreground pass
    ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,15,28,0.55)';
    ctx.lineWidth = 1.4 / scale;
    ctx.stroke(bladePath);
    ctx.stroke(handlePath);

    ctx.restore();
  }

  // ── Deferred turf overage overlay (drawn after objects) ───────────

  _drawTurfOverageOverlay() {
    // Redraw overage (red waste) on top of turf/platform objects so it's visible
    if (!this._pendingTurfOverage || this._pendingTurfOverage.length === 0) return;
    const ctx = this.rd.ctx;

    // Pass 1: redraw overage fill on top of turf objects
    for (const entry of this._pendingTurfOverage) {
      ctx.save();

      // Apply rotation (into strip space) if needed
      if (entry.angle && entry.centroid) {
        ctx.translate(entry.centroid.x, entry.centroid.y);
        ctx.rotate(entry.angle);
        ctx.translate(-entry.centroid.x, -entry.centroid.y);
      }

      // Clip to turf areas only (in rotated strip space)
      ctx.beginPath();
      for (const tp of entry.turfExclRotated) {
        ctx.moveTo(tp[0].x, tp[0].y);
        for (let i = 1; i < tp.length; i++) ctx.lineTo(tp[i].x, tp[i].y);
        ctx.closePath();
      }
      ctx.clip();

      // Clip to material rects
      ctx.beginPath();
      for (const r of entry.rects) ctx.rect(r.x, r.y, r.w, r.h);
      ctx.clip();

      // Even-odd fill: big rect minus room polygon minus exclusions = overage
      ctx.beginPath();
      ctx.rect(entry.rMinX - 50, entry.rMinY - 50, (entry.rMaxX - entry.rMinX) + 100, (entry.rMaxY - entry.rMinY) + 100);
      const op = entry.overagePoly;
      ctx.moveTo(op[0].x, op[0].y);
      for (let i = 1; i < op.length; i++) ctx.lineTo(op[i].x, op[i].y);
      ctx.closePath();
      for (const excl of entry.overageExcl) {
        if (!excl || excl.length < 3) continue;
        ctx.moveTo(excl[0].x, excl[0].y);
        for (let i = 1; i < excl.length; i++) ctx.lineTo(excl[i].x, excl[i].y);
        ctx.closePath();
      }
      // Dark base to neutralize turf green so red reads as red, not orange
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fill('evenodd');
      // Red overage on top (path is still active after fill)
      ctx.fillStyle = entry.overageColor;
      ctx.fill('evenodd');

      ctx.restore();
    }

    // Pass 1.5 skipped: seam lines are already drawn in the base flooring pass
    // and visible through the overage fill — redrawing them here caused doubled lines.

    // Pass 2: redraw scissors on top of turf objects (clipped to turf areas)
    if (this._pendingScissors && this._pendingScissors.length > 0) {
      ctx.save();
      // Build world-space clip from all turf polys
      ctx.beginPath();
      for (const entry of this._pendingTurfOverage) {
        for (const tp of entry.turfExclWorld) {
          ctx.moveTo(tp[0].x, tp[0].y);
          for (let i = 1; i < tp.length; i++) ctx.lineTo(tp[i].x, tp[i].y);
          ctx.closePath();
        }
      }
      ctx.clip();
      // Draw all scissors (clip ensures only those on turf are visible)
      this._drawDeferredScissors();
      ctx.restore();
    }
  }

  // ── Hover highlight render trigger ───────────

  _runHoverFade() {
    this._hoverFadeRAF = null;
    this.rd.render();
  }

  // ── Overage text fade (triggered by zoom hook) ──

  _onZoom() {
    this._suppressOverlayDetail();
    if (!this.rd._floorMode) return;
    this._trashAlpha = 0;
    this._trashTarget = 0;
    clearTimeout(this._trashScrollTimer);
    this._trashScrollTimer = setTimeout(() => {
      this._trashTarget = 1;
      this._trashLastFrame = 0;
      this._scheduleTrashFade();
    }, 500);
  }

  _onPan() {
    this._suppressOverlayDetail();
  }

  // Suppress detailed floor overlay during zoom/pan, restore after idle
  // Currently disabled — testing whether full-detail rendering is fast enough
  // without fade. Re-enable if tiles/rolls with textures cause jank.
  _suppressOverlayDetail() {
    return;
  }

  _scheduleOverlayDetailFade() {
    // Use 120ms intervals instead of RAF — gives ~5 frames over 600ms.
    // At high zoom, full-detail renders are expensive; RAF at 60fps causes
    // jitter because frames pile up. Coarser steps give the GPU breathing room.
    if (this._overlayDetailFadeTimer) return;
    this._overlayDetailFadeTimer = setTimeout(() => {
      this._overlayDetailFadeTimer = null;
      if (this._overlayDetailAlpha < this._overlayDetailTarget - 0.01) {
        this.rd.render();
        this._scheduleOverlayDetailFade();
      } else {
        // Final frame: snap to target and render once more
        this._overlayDetailAlpha = this._overlayDetailTarget;
        this.rd.render();
      }
    }, 120);
  }

  _scheduleTrashFade() {
    if (this._trashFadeRAF) return;
    this._trashFadeRAF = requestAnimationFrame(() => {
      this._trashFadeRAF = null;
      if (Math.abs(this._trashAlpha - this._trashTarget) > 0.01) {
        this.rd.render();
        this._scheduleTrashFade();
      }
    });
  }

  // ── Floor Highlights (hover outlines) ────

  /** Draw flooring overlays for exterior zones (rendered after rooms, before wall segments). */
  _drawExteriorZoneOverlays() {
    if (!this.showOverlay && !this.rd._viewOnly) return;
    if (!this._exteriorZoneFlooring || this._exteriorZoneFlooring.length === 0) return;
    const extZones = this._getExteriorZones();
    if (extZones.length === 0) return;
    const ctx = this.rd.ctx;
    const isDark = this.rd.darkMode;
    const bgColor = isDark ? '#141c2e' : '#ffffff';

    for (const zone of extZones) {
      const ef = this._getExteriorZoneFlooring(zone);
      if (!ef) continue;
      const zp = zone.polygon;
      if (!zp || zp.length < 3) continue;

      // Background fill
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(zp[0].x, zp[0].y);
      for (let k = 1; k < zp.length; k++) ctx.lineTo(zp[k].x, zp[k].y);
      ctx.closePath();
      ctx.fillStyle = bgColor;
      ctx.fill();
      ctx.restore();

      // Render flooring using a fake room-like object to reuse _drawFlooringOverlaySingle
      const fakeRoom = {
        closed: true,
        flooring: { productId: ef.productId, colorId: ef.colorId },
        flooringAngle: ef.flooringAngle || 0,
        _flooringLayout: null,
        _flooringLayoutGeo: null,
        _flooringLayoutProduct: null,
        _flooringLayoutColor: null,
        _flooringLayoutAngle: null,
        _zoneRenderPass: null,
        _zoneRenderContext: null,
        doors: [],
        walls: []
      };
      const fakeGeo = {
        roomPolygon: zp,
        cutoutPolys: [],
        holePolygons: [],
        area: zone.area,
        fp: `ext:${zone.centroid.x.toFixed(3)},${zone.centroid.y.toFixed(3)}`
      };
      this._drawFlooringOverlaySingle(fakeRoom, fakeGeo);
    }
  }

  _drawFloorHighlights() {
    if (this.currentStep !== 'floor') return;

    // Sync popover if floored room count changed (room added/deleted)
    const flooredCount = this._getFlooredRooms().length;
    if (flooredCount !== this._lastFlooredCount) {
      this._lastFlooredCount = flooredCount;
      this._updateRotatePopoverForSelectedRoom();
    }

    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;

    // Draw box-select rectangle
    if (this._floorSelectCorner1 && this._floorSelectEnd) {
      const bx = Math.min(this._floorSelectCorner1.x, this._floorSelectEnd.x);
      const by = Math.min(this._floorSelectCorner1.y, this._floorSelectEnd.y);
      const bw = Math.abs(this._floorSelectEnd.x - this._floorSelectCorner1.x);
      const bh = Math.abs(this._floorSelectEnd.y - this._floorSelectCorner1.y);
      ctx.fillStyle = 'rgba(0, 107, 204, 0.1)';
      ctx.fillRect(bx, by, bw, bh);
      ctx.strokeStyle = 'rgba(0, 107, 204, 0.6)';
      ctx.lineWidth = 2 / scale;
      ctx.setLineDash([5 / scale, 5 / scale]);
      ctx.strokeRect(bx, by, bw, bh);
      ctx.setLineDash([]);
    }
  }

  // Hover highlight — drawn after wall segments so it overlaps divider walls (like compass zone)
  _drawFloorHoverHighlight() {
    if (this.currentStep !== 'floor') return;
    if (!this._hoveredRoom && !this._hoveredExteriorZone && !this._hoveredTurf) return;
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;

    // Turf/platform hover is drawn in dedicated methods (after objects, so it renders on top)
    if (this._hoveredTurf || this._hoveredPlatform) return;

    let hoverPoly;
    if (this._hoveredExteriorZone) {
      hoverPoly = this._hoveredExteriorZone.polygon;
    } else {
      hoverPoly = this._hoveredZone ? this._hoveredZone.polygon
        : rd._getRoomGeoCache(this._hoveredRoom).roomPolygon;
    }
    if (!hoverPoly || hoverPoly.length < 3) return;

    // Determine outline color: roller uses green/red, eraser uses red (floored only), selector uses blue
    let strokeColor;
    if (rd.activeTool === 'floor-eraser') {
      let hasFloor = false;
      if (this._hoveredExteriorZone) {
        hasFloor = !!this._getExteriorZoneFlooring(this._hoveredExteriorZone);
      } else if (this._hoveredZone) {
        hasFloor = !!this._getZoneFlooring(this._hoveredRoom, this._hoveredZone);
      } else if (this._hoveredRoom) {
        hasFloor = this._roomHasFlooring(this._hoveredRoom);
      }
      if (!hasFloor) return;
      strokeColor = '#CC4429';
    } else if (rd.activeTool === 'roller') {
      let existingFlooring;
      if (this._hoveredExteriorZone) {
        existingFlooring = this._getExteriorZoneFlooring(this._hoveredExteriorZone);
      } else if (this._hoveredZone) {
        existingFlooring = this._getZoneFlooring(this._hoveredRoom, this._hoveredZone);
      } else {
        existingFlooring = this._hoveredRoom.flooring
          ? { productId: this._hoveredRoom.flooring.productId, colorId: this._hoveredRoom.flooring.colorId }
          : null;
      }
      strokeColor = existingFlooring ? '#CC4429' : '#28a745';
    } else {
      strokeColor = '#006BCC';
    }

    // Zone/room hover: stroke outline, then fill minus turf areas inside this zone
    ctx.save();
    // Stroke zone outline (no turf sub-paths — clean outline)
    ctx.beginPath();
    ctx.moveTo(hoverPoly[0].x, hoverPoly[0].y);
    for (let k = 1; k < hoverPoly.length; k++) ctx.lineTo(hoverPoly[k].x, hoverPoly[k].y);
    ctx.closePath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 3.5 / scale;
    ctx.globalAlpha = 0.85;
    ctx.stroke();
    // Fill removed — outline-only hover highlight
    ctx.restore();
  }

  // Turf hover highlight — drawn after objects so it renders on top of the turf fill
  _drawTurfHoverHighlight() {
    if (this.currentStep !== 'floor') return;
    if (!this._hoveredTurf) return;
    // No highlight when using paint roller — turf blocks floor paint
    if (this.rd.activeTool === 'roller') return;
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    const obj = this._hoveredTurf;
    const hw = obj.width / 2, hd = obj.depth / 2;
    const rot = obj.rotation || 0;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    ctx.rotate(rot);
    ctx.strokeStyle = '#006BCC';
    ctx.lineWidth = 3.5 / scale;
    ctx.globalAlpha = 0.85;
    ctx.strokeRect(-hw, -hd, obj.width, obj.depth);
    ctx.restore();
  }

  /** Redraw line-type divider segments on top of turf/platform objects.
   *  Wall segments draw before objects in the render pipeline, so turf/platform
   *  opaque fills cover them. This repaints just the dividing lines after objects. */
  _redrawDividersOverObjects() {
    const rd = this.rd;
    if (!rd.wallSegmentGroups || rd.wallSegmentGroups.length === 0) return;
    if (!rd.objects || rd.objects.length === 0) return;
    // Only bother if there are turf or platform objects
    const hasOverlap = rd.objects.some(o => o.type === 'turf' || o.type === 'platform');
    if (!hasOverlap) return;
    // Same visibility check as wallseg module (equipment tab: faint instead of hidden)
    const _onCustomizeTab = this.currentStep === 'finishing';
    if (!rd.showSeamLines && (rd._floorMode || _onCustomizeTab || rd._viewOnlyHideZoneDividers)) return;
    const _faintDivider = !rd.showSeamLines && rd._equipmentMode;
    // In print/view-only mode, dividers are invisible (alpha 0)
    if (rd._printMode || rd._viewOnly) return;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    ctx.save();
    ctx.strokeStyle = rd._tc.wallColor;
    ctx.lineWidth = 1.5 / scale;
    ctx.lineCap = 'butt';
    ctx.setLineDash([4 / scale, 4.5 / scale]);
    ctx.globalAlpha = _faintDivider ? 0.20 : 0.6;
    for (const group of rd.wallSegmentGroups) {
      if (group.type !== 'line') continue;
      for (const seg of group.segments) {
        ctx.beginPath();
        ctx.moveTo(seg.start.x, seg.start.y);
        ctx.lineTo(seg.end.x, seg.end.y);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // Platform hover highlight — drawn after objects so it renders on top of the platform fill
  _drawPlatformHoverHighlight() {
    if (this.currentStep !== 'floor') return;
    if (!this._hoveredPlatform) return;
    // No highlight when using paint roller — platform blocks floor paint
    if (this.rd.activeTool === 'roller') return;
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    const obj = this._hoveredPlatform;
    const hw = obj.width / 2, hd = obj.depth / 2;
    const rot = obj.rotation || 0;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    ctx.rotate(rot);
    ctx.strokeStyle = '#006BCC';
    ctx.lineWidth = 3.5 / scale;
    ctx.globalAlpha = 0.85;
    ctx.strokeRect(-hw, -hd, obj.width, obj.depth);
    ctx.restore();
  }

  _isCompassOpen() {
    return this._rotatePop && this._rotatePop.classList.contains('visible');
  }

  // Compass zone highlight — drawn after wall segments so it overlaps divider walls
  _drawCompassZoneHighlight() {
    if (this.currentStep !== 'floor') return;
    if (!this._rotatePop || !this._rotatePop.classList.contains('visible')) return;
    const entry = this._getTargetFlooringEntry();
    if (!entry) return;
    let zPoly;
    if (entry.isExterior) {
      zPoly = entry.zone ? entry.zone.polygon : null;
    } else {
      zPoly = entry.zone ? entry.zone.polygon
        : this.rd._getRoomGeoCache(entry.room).roomPolygon;
    }
    if (!zPoly || zPoly.length < 3) return;
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(zPoly[0].x, zPoly[0].y);
    for (let k = 1; k < zPoly.length; k++) ctx.lineTo(zPoly[k].x, zPoly[k].y);
    ctx.closePath();
    ctx.strokeStyle = '#006BCC';
    ctx.lineWidth = 3.5 / scale;
    ctx.globalAlpha = 0.85;
    ctx.stroke();
    ctx.fillStyle = '#006BCC';
    ctx.globalAlpha = 0.14;
    ctx.fill();
    ctx.restore();
  }

  // Box-select preview — highlight zones that would be selected during drag
  _drawFloorBoxSelectPreview() {
    if (this.currentStep !== 'floor') return;
    if (!this._floorSelectCorner1 || !this._floorSelectEnd) return;

    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    const c1 = this._floorSelectCorner1, c2 = this._floorSelectEnd;
    const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
    const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);

    // Collect polygons that meet the 50% overlap threshold (same logic as _finalizeFloorBoxSelection)
    const previewPolys = [];

    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3 || !geo.area) continue;
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (const z of zones) {
          if (!this._getZoneFlooring(room, z)) continue;
          const clipped = this._clipPolygonToRect(z.polygon, minX, minY, maxX, maxY);
          if (this._shoelaceArea(clipped) / z.area >= 0.5) previewPolys.push(z.polygon);
        }
      } else {
        const clipped = this._clipPolygonToRect(geo.roomPolygon, minX, minY, maxX, maxY);
        let intersectArea = this._shoelaceArea(clipped);
        const allHoles = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
        for (const hole of allHoles) intersectArea -= this._shoelaceArea(this._clipPolygonToRect(hole, minX, minY, maxX, maxY));
        if (intersectArea / geo.area >= 0.5) previewPolys.push(geo.roomPolygon);
      }
    }

    // Exterior zones
    const extZones = this._getExteriorZones();
    for (const z of extZones) {
      if (!this._getExteriorZoneFlooring(z)) continue;
      const clipped = this._clipPolygonToRect(z.polygon, minX, minY, maxX, maxY);
      if (this._shoelaceArea(clipped) / z.area >= 0.5) previewPolys.push(z.polygon);
    }

    if (previewPolys.length === 0) return;

    ctx.save();
    for (const poly of previewPolys) {
      if (!poly || poly.length < 3) continue;
      ctx.beginPath();
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      ctx.strokeStyle = '#006BCC';
      ctx.lineWidth = 3.5 / scale;
      ctx.globalAlpha = 0.85;
      ctx.stroke();
      ctx.fillStyle = '#006BCC';
      ctx.globalAlpha = 0.14;
      ctx.fill();
    }
    ctx.restore();
  }

  // Floor selection outlines — drawn after wall segments so they overlap divider walls
  _drawFloorSelectionOutlines() {
    if (this.currentStep !== 'floor') return;
    if (this._floorSelections.length === 0) {
      if (this._floorInfoPinned) { this._floorInfoPinned = false; this._hideFloorInfo(); }
      return;
    }
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;
    ctx.save();
    // Accumulate centroid for product info tooltip positioning
    let cx = 0, cy = 0, count = 0;
    for (const sel of this._floorSelections) {
      const poly = sel.zone ? sel.zone.polygon
        : (sel.room ? rd._getRoomGeoCache(sel.room).roomPolygon : null);
      if (poly && poly.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(255, 100, 100, 0.85)';
        ctx.lineWidth = 3.5 / scale;
        ctx.globalAlpha = 1;
        ctx.stroke();
        const c = sel.zone ? sel.zone.centroid
          : RoomDesignerMath.polygonCentroid(poly);
        if (c) { cx += c.x; cy += c.y; count++; }
      }
    }
    ctx.restore();
    // Show product info tooltip pinned (persists until Esc/click-away)
    if (count > 0) {
      this._floorInfoPinned = true;
      if (!this.rd.showProductCards) { this._hideFloorInfo(); }
      else this._updateFloorInfo(cx / count, cy / count);
    } else if (this._floorInfoPinned) {
      this._floorInfoPinned = false;
      this._hideFloorInfo();
    }
  }

  _ensureFloorInfoEl() {
    if (this._floorInfoEl) return this._floorInfoEl;
    const el = document.createElement('div');
    el.className = 'rd-item-info';
    el.innerHTML = '<a class="rd-item-info-link" href="#" target="_blank" rel="noopener" title="View product" style="display:none"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8.5v5a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1H8"/><polyline points="10 1.5 14.5 1.5 14.5 6"/><line x1="6.5" y1="9.5" x2="14.5" y2="1.5"/></svg></a>'
      + '<img class="rd-item-info-img" src="" alt="">'
      + '<div class="rd-item-info-text">'
      + '<span class="rd-item-info-inner"></span>'
      + '</div>'
      + '<div class="rd-item-info-detail"></div>';
    const container = this.rd.canvas.parentElement;
    container.appendChild(el);
    this._floorInfoEl = el;
    return el;
  }

  _applyScrollIfNeeded(innerEl) {
    const container = innerEl.parentElement;
    if (!container) return;
    const textW = innerEl.scrollWidth;
    const boxW = container.clientWidth;
    if (textW > boxW + 2) {
      // Duplicate content for seamless marquee loop
      const onePassHTML = innerEl._onePassHTML || innerEl.innerHTML;
      innerEl.innerHTML = onePassHTML + '<span class="scroll-gap"></span>' + onePassHTML;
      const gap = 28;
      const dist = -(textW + gap);
      innerEl.style.setProperty('--scroll-dist', dist + 'px');
      const dur = Math.max(2.5, (textW + gap) / 40);
      innerEl.style.setProperty('--scroll-dur', dur + 's');
      innerEl.classList.add('scrolling');
    } else {
      innerEl.classList.remove('scrolling');
    }
  }

  _updateFloorInfo(worldX, worldY) {
    // Determine product/color from click-selection
    let flooring = null;
    const sel = this._floorSelections[0];
    if (sel) {
      if (sel.isExterior) {
        flooring = this._getExteriorZoneFlooring(sel.zone);
      } else if (sel.zone) {
        flooring = this._getZoneFlooring(sel.room, sel.zone);
      } else if (sel.room && sel.room.flooring) {
        flooring = sel.room.flooring;
      }
    }
    if (!flooring || !flooring.productId) { this._hideFloorInfo(); return; }
    const product = this._getProduct(flooring.productId);
    if (!product) { this._hideFloorInfo(); return; }
    const color = product.colors.find(c => c.id === flooring.colorId);

    const el = this._ensureFloorInfoEl();
    const img = el.querySelector('.rd-item-info-img');
    const innerEl = el.querySelector('.rd-item-info-inner');

    // Cache key — skip content rebuild if same product/color (preserves scroll animation)
    const cacheKey = flooring.productId + '|' + (flooring.colorId || '');
    const contentChanged = innerEl._cacheKey !== cacheKey;

    if (contentChanged) {
      innerEl._cacheKey = cacheKey;
      const imgUrl = color ? (color.imageUrl || '') : '';
      if (img.src !== imgUrl) img.src = imgUrl;
      if (!imgUrl) img.style.display = 'none';
      else img.style.display = '';

      // Product link icon
      const linkEl = el.querySelector('.rd-item-info-link');
      if (linkEl) {
        if (product.url) {
          linkEl.href = product.url;
          linkEl.style.display = '';
        } else {
          linkEl.style.display = 'none';
        }
      }

      // Product name on line 1 (scrolls if long), color on line 2 (static)
      innerEl._onePassHTML = product.name;
      innerEl.innerHTML = product.name;

      const detailEl = el.querySelector('.rd-item-info-detail');
      const colorName = color ? color.name : '';
      if (detailEl) {
        detailEl.textContent = colorName;
        detailEl.style.display = colorName ? '' : 'none';
      }

      // Measure and set up scroll on next frame (after layout)
      innerEl.classList.remove('scrolling');
      requestAnimationFrame(() => {
        this._applyScrollIfNeeded(innerEl);
      });
    }

    // Always reposition (centroid moves during pan/zoom), flip below if clipped at top
    const screen = this.rd.worldToScreen(worldX, worldY);
    const dpr = window.devicePixelRatio || 1;
    const sx = screen.x / dpr;
    const sy = screen.y / dpr;
    el.style.left = sx + 'px';
    el.style.top = sy + 'px';
    const ch = el.offsetHeight || 0;
    const cw = el.offsetWidth || 0;
    if (sy - ch - 12 < 0) {
      el.style.transform = 'translate(-50%, 12px)';
    } else {
      el.style.transform = 'translate(-50%, calc(-100% - 12px))';
    }
    const container = this.rd.canvas.parentElement;
    if (container) {
      const halfW = cw / 2;
      const containerW = container.clientWidth;
      if (sx - halfW < 4) {
        el.style.left = (halfW + 4) + 'px';
      } else if (sx + halfW > containerW - 4) {
        el.style.left = (containerW - halfW - 4) + 'px';
      }
    }
    el.classList.add('visible');
  }

  _hideFloorInfo() {
    if (!this._floorInfoEl) return;
    this._floorInfoEl.classList.remove('visible');
    const inner = this._floorInfoEl.querySelector('.rd-item-info-inner');
    if (inner) { inner._cacheKey = null; inner.classList.remove('scrolling'); }
  }

  /** Clear all floor hover/selection state — called by main JS on tool switch */
  _clearFloorState() {
    this._floorSelections = [];
    this._floorSelectCorner1 = null;
    this._floorSelectEnd = null;
    this._floorInfoPinned = false;
    this._hideFloorInfo();
    this._hoveredRoom = null;
    this._hoveredZone = null;
    this._hoveredExteriorZone = null;
    this._hoverStartTime = 0;
    if (this._hoverFadeRAF) { cancelAnimationFrame(this._hoverFadeRAF); this._hoverFadeRAF = null; }
  }

  // ── Before/After Toggle ──────────────────

  _toggleOverlay() {
    // Toggle material overlay (textures + turf) — keeps showOverlay true
    // so floored rooms still show the uniform "material assigned" tint
    this.rd.showMaterialOverlay = !this.rd.showMaterialOverlay;
    const btn = document.getElementById('toggleMaterialOverlay');
    if (btn) btn.classList.toggle('active', this.rd.showMaterialOverlay);
    this.rd.render();
  }

  // ── Keyboard Shortcuts ───────────────────

  _setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Don't intercept when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

      if (e.key === 'f' || e.key === 'F') {
        // F: Toggle floor overlay (only when flooring or turf is assigned; not on Draw Space tab)
        if (this.currentStep === 'draw') return;
        const hasFlooring = this.rd.rooms.some(r => this._roomHasFlooring(r)) || this._hasExteriorFlooring();
        const hasTurf = this.rd.objects && this.rd.objects.some(o => o.type === 'turf');
        if (hasFlooring || hasTurf) {
          e.preventDefault();
          this._toggleOverlay();
        }
      }

      if (e.key === 'r' || e.key === 'R') {
        // R: Toggle rotation popover (only in floor mode, and only if a room has flooring)
        if (this.rd._floorMode && this._toggleRotatePopover
            && (this.rd.rooms.some(r => r.closed && this._roomHasFlooring(r)) || this._hasExteriorFlooring())) {
          e.preventDefault();
          this._toggleRotatePopover();
        }
      }
    });
  }

  // ── Save/Load Wrapping ───────────────────

  _wrapSerialization() {
    const rd = this.rd;
    const self = this;

    // Wrap _serializeDesign to include overlay state
    const origSerialize = rd._serializeDesign.bind(rd);
    rd._serializeDesign = function() {
      const data = origSerialize();
      // Strip runtime Image objects and tightBounds from turf logos (not serializable)
      if (data.objects) {
        for (const obj of data.objects) {
          if (obj.turfLogos) {
            obj.turfLogos = obj.turfLogos.map(l => {
              const copy = Object.assign({}, l);
              delete copy.image;
              delete copy.tightBounds;
              // Also strip transient snap/resize flags
              delete copy._snapX; delete copy._snapY; delete copy._resizing;
              return copy;
            });
          }
        }
      }
      data._overlay = {
        currentStep: self.currentStep,
        showOverlay: self.showOverlay,
        activeBrush: self._activeBrush,
        exteriorZoneFlooring: self._exteriorZoneFlooring,
        brandColors: rd._brandColors || {},
        rollOptimizationMode: self._rollOptimizationMode || 'minSeams',
        uniformTargetLen: self._uniformTargetLen || 0,
        rollLenOverrides: self._rollLenOverride || {},
        rollShare: self._rollShare || {},
        platformLogo: self._platformLogo ? {
          dataURL: self._platformLogo.dataURL,
          width: self._platformLogo.width,
          height: self._platformLogo.height,
          x: self._platformLogo.x,
          y: self._platformLogo.y,
          rotation: self._platformLogo.rotation,
          color: self._platformLogo.color
        } : null,
        hardwoodLogo: self._hardwoodLogo ? {
          dataURL: self._hardwoodLogo.dataURL,
          width: self._hardwoodLogo.width,
          height: self._hardwoodLogo.height,
          color: self._hardwoodLogo.color
        } : null
      };
      return data;
    };

    // Wrap _loadDesignData to restore overlay state
    const origLoad = rd._loadDesignData.bind(rd);
    rd._loadDesignData = function(data) {
      // Restore roll optimization mode BEFORE origLoad so the render + updateMeasurements
      // inside origLoad use the correct mode from the start (not the default minSeams).
      if (data._overlay) {
        if (data._overlay.rollOptimizationMode) {
          self._rollOptimizationMode = data._overlay.rollOptimizationMode;
        } else if (data._overlay.uniformRollLengths) {
          self._rollOptimizationMode = 'uniform';
        } else {
          self._rollOptimizationMode = 'minSeams';
        }
        self._uniformTargetLen = data._overlay.uniformTargetLen || 0;
        // Restore rep roll-length overrides and push them onto the products BEFORE origLoad,
        // so measurements/layouts computed inside origLoad use the custom limits.
        self._rollLenOverride = data._overlay.rollLenOverrides || {};
        self._applyAllRollLenOverrides();
        self._rollShare = data._overlay.rollShare || {};
      }
      origLoad(data);
      if (data._overlay) {
        if (data._overlay.showOverlay != null) self.showOverlay = data._overlay.showOverlay;
        // Force overlay off on Draw tab — saved state may have been from floor/equipment tab.
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — showOverlay is enough on its own: it is the
        // switch _drawFlooringOverlay checks first, so nothing flooring-related reaches
        // the canvas regardless of the other two. This block used to wipe showSeamLines
        // and showMaterialOverlay as well — the SECOND of the two places doing it, so
        // fixing the storage one alone would have changed nothing. They are the user's
        // saved preferences; the tab machinery owns tab state through its own stash.
        if (!self.currentStep || self.currentStep === 'draw') {
          self.showOverlay = false;
        }
        // The drawer was synced by storage's load before this ran; re-sync so the
        // buttons agree with whatever this wrapper has just settled on.
        if (rd._storage && rd._storage._applySettingsUI) rd._storage._applySettingsUI();
        if (data._overlay.activeBrush) self._activeBrush = data._overlay.activeBrush;
        self._uniformTargetLen = data._overlay.uniformTargetLen || 0;
        // Invalidate ALL flooring layout caches so they recompute with restored mode/target
        // (origLoad may have already cached layouts using the default minSeams mode)
        for (const r of rd.rooms) { r._flooringLayout = null; r._flooringLayoutOptMode = null; r._zoneLayouts = null; }
        self._exteriorZoneFlooring = data._overlay.exteriorZoneFlooring || [];
        if (data._overlay.brandColors) {
          rd._brandColors = data._overlay.brandColors;
          // Migrate old hex values to Pantone-corrected values (March 2026)
          const _colorMigration = {
            '#1a1a1a': '#2D2926', '#58585c': '#53565A', '#b5b3b2': '#B1B3B3',
            '#a7a089': '#A69F88', '#17439d': '#004B87', '#033259': '#003057',
            '#037ebc': '#007DBA', '#e12a29': '#DA291C', '#99282d': '#93272C',
            '#6f2d3e': '#6A2C3E', '#e87627': '#E57200', '#e65728': '#E35205',
            '#ffd042': '#FED141', '#f5a91f': '#F2A900', '#125841': '#115740',
            '#529f31': '#509E2F', '#037c55': '#007A53', '#6c8347': '#6E7544',
            '#54382b': '#4E3629', '#b06f07': '#AF6D04', '#5d2f83': '#582C83',
            '#7e6b93': '#7C6992', '#e46587': '#E06287', '#9a1217': '#AB2328',
            '#4a4b4c': '#63666A', '#808182': '#8A8D8F', '#847850': '#84754E',
            '#2a2a2e': '#2D2926', '#92908f': '#B1B3B3', '#636367': '#63666A',
            '#163f66': '#003057', '#1d49a3': '#004B87', '#1486c4': '#007DBA',
            '#5f3286': '#582C83', '#ee6e91': '#E06287', '#b93743': '#DA291C',
            '#a31a27': '#AB2328', '#ab3a3f': '#93272C', '#7c3a45': '#6A2C3E',
            '#e34e11': '#E35205', '#734431': '#AF6D04', '#533c31': '#4E3629',
            '#d1901d': '#F2A900', '#706841': '#84754E', '#f0d725': '#FED141',
            '#257d4d': '#007A53', '#0b4d38': '#115740', '#647841': '#6E7544'
          };
          for (const v in rd._brandColors) {
            const bc = rd._brandColors[v];
            if (bc.powderCoat) {
              const migrated = _colorMigration[bc.powderCoat.toLowerCase()];
              if (migrated) bc.powderCoat = migrated;
            }
            if (bc.upholstery) {
              const migrated = _colorMigration[bc.upholstery.toLowerCase()];
              if (migrated) bc.upholstery = migrated;
            }
          }
          // Preload any saved pattern images (camo textures)
          for (const v in rd._brandColors) {
            if (rd._brandColors[v].uphPattern) rd._ensurePatternImage(rd._brandColors[v].uphPattern);
            if (rd._brandColors[v].pcPattern) rd._ensurePatternImage(rd._brandColors[v].pcPattern);
          }
        }
      }
      self._exteriorZoneCache = null;
      // Restore platform logo
      if (data._overlay && data._overlay.platformLogo) {
        self._platformLogo = data._overlay.platformLogo;
        self._rehydratePlatformLogo();
      } else if (!data._overlay || data._overlay.platformLogo === null) {
        self._platformLogo = null;
      }
      // Restore hardwood platform logo (absent on a plan saved before it existed → none)
      if (data._overlay && data._overlay.hardwoodLogo) {
        self._hardwoodLogo = data._overlay.hardwoodLogo;
        self._rehydrateHardwoodLogo();
      } else {
        self._hardwoodLogo = null;
      }
      // Rehydrate turf logo Images from dataURLs
      self._rehydrateTurfLogos();
      // Migrate old product IDs after load
      self._migrateOldProductIds();
      // Migrate old turfSledLanePosition → turfSledLaneEdges
      self._migrateSledLanePosition();
      // Refresh panel if open
      if (self._sidePanelOpen) {
        self._buildFloorPanelContent();
      }
      // Force render to recompute layouts with the restored optimization mode.
      // Invalidate caches again in case the render during origLoad re-cached with wrong mode.
      for (const r of rd.rooms) { r._flooringLayout = null; r._flooringLayoutOptMode = null; r._zoneLayouts = null; }
      rd.render();
      self._updateMaterialSummary();
      // Deferred re-update: the render pipeline may trigger async layout recomputation
      // that hasn't completed by the time _updateMaterialSummary runs synchronously above.
      // A deferred call ensures the panel reflects the final correct layouts.
      setTimeout(() => {
        for (const r of rd.rooms) { r._flooringLayout = null; r._flooringLayoutOptMode = null; r._zoneLayouts = null; }
        rd.render();
        self._updateMaterialSummary();
      }, 100);
      self._syncCompassBtn();
      self._syncFloorTab();
      self._syncEquipmentTab();
      self._syncFinishingTab();
      self._syncQuoteTab();
    };

    // Wrap _newDesign to reset platform defaults & logo on project clear
    const origNew = rd._newDesign.bind(rd);
    rd._newDesign = function() {
      origNew();
      self._platformDefaults = null;
      self._platformLogo = null;
      self._hardwoodLogo = null;
      self._platformPresetPicked = false;
      self._initPlatformDefaults();
    };
  }

  // ── Undo/Redo Animation Wrapping ─────────────────

  _wrapUndoRedo() {
    const rd = this.rd;
    const self = this;

    // Wrap saveUndoState to also capture exterior zone flooring
    const origSave = rd.saveUndoState.bind(rd);
    rd.saveUndoState = function() {
      origSave();
      // Attach exterior zone flooring snapshot to the undo entry
      const entry = rd.undoStack[rd.undoStack.length - 1];
      if (entry) entry._exteriorZoneFlooring = JSON.parse(JSON.stringify(self._exteriorZoneFlooring || []));
    };

    const origUndo = rd.undo.bind(rd);
    rd.undo = function() {
      const before = self._snapshotFlooringAngles();
      const savedExt = JSON.parse(JSON.stringify(self._exteriorZoneFlooring || []));
      origUndo();
      // Restore exterior zone flooring from the undo entry that was just popped
      const redoEntry = rd.redoStack[rd.redoStack.length - 1];
      if (redoEntry && redoEntry._exteriorZoneFlooring !== undefined) {
        // Save current state to redo entry, restore previous state
        redoEntry._exteriorZoneFlooring = savedExt;
        const undoEntry = rd.undoStack[rd.undoStack.length - 1];
        if (undoEntry && undoEntry._exteriorZoneFlooring !== undefined) {
          self._exteriorZoneFlooring = JSON.parse(JSON.stringify(undoEntry._exteriorZoneFlooring));
        }
      }
      self._exteriorZoneCache = null;
      self._rehydrateTurfLogos();
      self._lastTurfSelFp = null; // force panel re-sync on next render
      self._animateFlooringTransition(before);
    };

    const origRedo = rd.redo.bind(rd);
    rd.redo = function() {
      const before = self._snapshotFlooringAngles();
      origRedo();
      // Restore exterior zone flooring from the redo entry
      const undoEntry = rd.undoStack[rd.undoStack.length - 1];
      if (undoEntry && undoEntry._exteriorZoneFlooring !== undefined) {
        self._exteriorZoneFlooring = JSON.parse(JSON.stringify(undoEntry._exteriorZoneFlooring));
      }
      self._exteriorZoneCache = null;
      self._rehydrateTurfLogos();
      self._lastTurfSelFp = null; // force panel re-sync on next render
      self._animateFlooringTransition(before);
    };
  }

  /** Snapshot current flooring angles for all rooms (room-level + per-zone). */
  _snapshotFlooringAngles() {
    return this.rd.rooms.map(r => ({
      roomAngle: r.flooringAngle || 0,
      zoneAngles: (r.zoneFlooring || []).map(zf => ({
        cx: zf.cx, cy: zf.cy,
        angle: zf.flooringAngle || 0
      }))
    }));
  }

  /** After undo/redo, animate flooring angles from old values to the restored values. */
  _animateFlooringTransition(before) {
    const rooms = this.rd.rooms;
    // Only animate when room count matches (structural changes skip animation)
    if (before.length !== rooms.length) return;

    // Collect all angle deltas
    const deltas = []; // { roomIdx, oldRoom, newRoom, zoneDeltas[] }
    for (let i = 0; i < before.length; i++) {
      const b = before[i];
      const r = rooms[i];
      const newRoomAngle = r.flooringAngle || 0;
      const roomChanged = Math.abs(b.roomAngle - newRoomAngle) > 0.001;

      // Check zone-level changes
      const zoneDeltas = [];
      if (r.zoneFlooring) {
        for (const zf of r.zoneFlooring) {
          const match = b.zoneAngles.find(z =>
            Math.sqrt((z.cx - zf.cx) ** 2 + (z.cy - zf.cy) ** 2) < 2
          );
          const oldZA = match ? match.angle : 0;
          const newZA = zf.flooringAngle || 0;
          if (Math.abs(oldZA - newZA) > 0.001) {
            zoneDeltas.push({ zf, oldAngle: oldZA, newAngle: newZA });
          }
        }
      }

      if (roomChanged || zoneDeltas.length > 0) {
        deltas.push({
          room: r, roomIdx: i,
          oldRoomAngle: b.roomAngle, newRoomAngle,
          roomChanged, zoneDeltas
        });
      }
    }

    if (deltas.length === 0) return;

    // Cancel any running flooring animation
    if (this._angleAnimRAF) {
      cancelAnimationFrame(this._angleAnimRAF);
      this._angleAnimRAF = null;
    }

    // Set angles back to old values (undo already set them to new)
    for (const d of deltas) {
      if (d.roomChanged) d.room.flooringAngle = d.oldRoomAngle;
      for (const zd of d.zoneDeltas) zd.zf.flooringAngle = zd.oldAngle;
      d.room._flooringLayout = null;
      d.room._zoneLayouts = null;
    }

    const duration = 400;
    const startTime = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const et = ease(t);

      for (const d of deltas) {
        if (d.roomChanged) {
          // Shortest angular path
          let diff = d.newRoomAngle - d.oldRoomAngle;
          while (diff > Math.PI) diff -= 2 * Math.PI;
          while (diff < -Math.PI) diff += 2 * Math.PI;
          d.room.flooringAngle = d.oldRoomAngle + diff * et;
        }
        for (const zd of d.zoneDeltas) {
          let diff = zd.newAngle - zd.oldAngle;
          while (diff > Math.PI) diff -= 2 * Math.PI;
          while (diff < -Math.PI) diff += 2 * Math.PI;
          zd.zf.flooringAngle = zd.oldAngle + diff * et;
        }
        d.room._flooringLayout = null;
        d.room._zoneLayouts = null;
      }

      this.rd.render();
      this._updateRotatePopoverForSelectedRoom();
      this._updateMaterialSummary();

      if (t < 1) {
        this._angleAnimRAF = requestAnimationFrame(tick);
      } else {
        this._angleAnimRAF = null;
        // Ensure exact final values
        for (const d of deltas) {
          if (d.roomChanged) d.room.flooringAngle = d.newRoomAngle;
          for (const zd of d.zoneDeltas) zd.zf.flooringAngle = zd.newAngle;
          d.room._flooringLayout = null;
          d.room._zoneLayouts = null;
        }
        this.rd.render();
        this._updateMaterialSummary();
        if (this._rollOptimizationMode === 'uniform') this._repopulateUniformTargetSelects();
      }
    };
    this._angleAnimRAF = requestAnimationFrame(tick);
  }

  /** Return per-room flooring entries with full purchase details for space cards.
   *  Returns [{productName, colorName, imageUrl, swatch, hasImage,
   *            floorArea, materialArea, wastePercent,
   *            purchaseRolls:[{label,count}], moqNote, underMoq, zoneName}] */
  _computePerRoomFlooring(room) {
    if (!room || !room.closed || room.type !== 'add') return [];
    if (!this._roomHasFlooring(room)) return [];
    const rd = this.rd;
    const geo = rd._getRoomGeoCache(room);
    if (!geo.roomPolygon || geo.roomPolygon.length < 3) return [];

    const results = [];
    const zones = this._getZonesForRoom(room, geo);
    const turfExcl = this._getTurfExclusionsForRoom(room, geo);
    const turfExclNoPlat = this._getTurfExclusionsForRoom(room, geo, false);

    // Helper: compute material details for a polygon with given product/color
    const computeEntry = (product, color, poly, floorAngle, cachedLayout, zoneName, holes = []) => {
      const imageUrl = color ? (color.imageUrl || '') : '';
      const swatch = color ? (color.imageUrl ? `url(${color.imageUrl})` : (color.swatch || color.hex)) : '#888';
      const hasImage = color && !!color.imageUrl;
      // Rolls: platforms don't reduce area (roll runs underneath); tiles/pavers: platforms replace material
      const _isRollProduct = product.shape !== 'paver' && product.type !== 'tile';
      const _areaExcl = _isRollProduct ? turfExclNoPlat : turfExcl;
      let turfArea = 0;
      for (const tp of _areaExcl) {
        if (tp && tp.length >= 3) {
          const cx = tp.reduce((s, p) => s + p.x, 0) / tp.length;
          const cy = tp.reduce((s, p) => s + p.y, 0) / tp.length;
          if (this.rd.pointInPolygon(cx, cy, poly)) {
            turfArea += Math.abs(this._polygonArea(tp));
          }
        }
      }
      // Island cut-outs carved out of this zone: their area isn't ours, and their
      // perimeter is a material transition reported on its own line.
      const _holeRings = (holes || []).filter(h => h && h.length >= 3);
      let holeArea = 0, islandTransitionLF = 0;
      for (const h of _holeRings) {
        holeArea += Math.abs(this._polygonArea(h));
        for (let i = 0; i < h.length; i++) {
          const p1 = h[i], p2 = h[(i + 1) % h.length];
          islandTransitionLF += Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
        }
      }
      const netArea = Math.max(0, Math.abs(this._polygonArea(poly)) - turfArea - holeArea);
      let materialArea = 0;
      let seamLF = 0;
      const pieces = [];
      let tileCount = 0;

      // Compute border & divider line measurements:
      //   Pavers: borderFt = full polygon perimeter, dividerLineLF = 0
      //   Other products: borderFt = 0, dividerLineLF = edges along dividing lines only
      let borderFt = 0;
      let dividerLineLF = 0;
      if (product.shape === 'paver') {
        for (let i = 0; i < poly.length; i++) {
          const p1 = poly[i], p2 = poly[(i + 1) % poly.length];
          borderFt += Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
        }
      } else {
        const divSegs = this._getDividerSegmentsForRoom(room, geo);
        if (divSegs.length > 0) {
          const wallDivSegs = this._getWallDividerSegmentsForRoom(room, geo);
          const roomPoly = geo.roomPolygon;
          const halfT = rd.wallSegmentThickness / 2 + 0.05;
          for (let i = 0; i < poly.length; i++) {
            const p1 = poly[i], p2 = poly[(i + 1) % poly.length];
            const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
            const edgeLen = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
            // Skip edges on the room wall boundary
            let onRoomWall = false;
            for (let j = 0; j < roomPoly.length; j++) {
              const rWall = { start: roomPoly[j], end: roomPoly[(j + 1) % roomPoly.length] };
              if (RoomDesignerMath.distanceToWall(mid, rWall) < 0.15) {
                onRoomWall = true; break;
              }
            }
            if (onRoomWall) continue;
            // Check if edge is along a wall-type divider
            let onWallDivider = false;
            for (const seg of wallDivSegs) {
              if (RoomDesignerMath.distanceToWall(mid, { start: seg.start, end: seg.end }) < halfT) {
                onWallDivider = true; break;
              }
            }
            // Only count line-type divider edges as potential transitions for non-pavers
            if (!onWallDivider) {
              dividerLineLF += edgeLen;
            }
          }
        }
      }

      if (product.shape === 'paver') {
        // Formula-based: floor area × pavers-per-sq-ft, plus 5% extra for mistakes
        const paverShapeArea = product.widthFt * product.heightFt * 0.86924;
        tileCount = Math.ceil(netArea / paverShapeArea);
        const extra = Math.max(1, Math.ceil(tileCount * 0.05));
        materialArea = (tileCount + extra) * paverShapeArea;
      } else if (product.type === 'tile') {
        let layout = cachedLayout;
        if (!layout || !layout.tiles) {
          const allExcl = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ...turfExcl, ..._holeRings];
          if (floorAngle !== 0) {
            const centroid = this._getPolygonCentroid(poly);
            const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
            const rExcl = allExcl.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
            layout = this._computeTileLayout(room, geo, product, rPoly, rExcl);
          } else {
            layout = this._computeTileLayout(room, geo, product, poly, allExcl);
          }
        }
        tileCount = layout.tiles.length;
        materialArea = tileCount * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
      } else {
        // Rolls: exclude platforms from layout computation (roll lengths unaffected by platforms)
        let layout = cachedLayout;
        if (!layout) {
          const allExcl = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ...turfExclNoPlat, ..._holeRings];
          if (floorAngle !== 0) {
            const centroid = this._getPolygonCentroid(poly);
            const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
            const rExcl = allExcl.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
            layout = this._computeRollLayout(room, geo, product, rPoly, rExcl);
          } else {
            layout = this._computeRollLayout(room, geo, product, poly, allExcl);
          }
        }
        if (layout && layout.strips) {
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) {
              const cutLen = roll.naturalLength != null ? roll.naturalLength : roll.length;
              materialArea += strip.width * cutLen;
              pieces.push({ width: strip.width, length: cutLen });
            }
          }
          // Seam LF: side seams (between adjacent strips) + head seams (between pieces)
          for (let si = 0; si < layout.strips.length; si++) {
            const strip = layout.strips[si];
            if (si < layout.strips.length - 1) {
              for (const seg of strip.segments) seamLF += seg.segMaxY - seg.segMinY;
            }
            for (const seg of strip.segments) {
              if (seg.rolls && seg.rolls.length > 1) seamLF += (seg.rolls.length - 1) * strip.width;
            }
          }
        }
      }

      // Build purchase rolls from pieces
      const purchaseRolls = [];
      let purchaseArea = materialArea; // default for tiles/pavers (overridden for rolls)
      if (product.shape === 'paver' && tileCount > 0) {
        // Extra already included in materialArea/tileCount above
        const extra = Math.max(1, Math.ceil(tileCount * 0.05));
        purchaseRolls.push({ label: `${tileCount} pavers + ${extra} extra for mistakes`, count: tileCount + extra });
        // purchaseArea already = materialArea which includes the 5% extra
      } else if (product.type === 'tile' && tileCount > 0) {
        const _step = product.qtyStep || 1;
        const _tileUnit = product.shape === 'plank' ? 'planks' : 'tiles';
        const _mistakes = this._mistakeExtra(product, tileCount);
        const _withExtra = tileCount + _mistakes;
        // Round up to qtyStep (carton size) if > 1
        const _purchaseQty = _step > 1 ? Math.ceil(_withExtra / _step) * _step : _withExtra;
        const _extraTotal = _purchaseQty - tileCount;
        if (_step > 1) {
          const _cartons = _purchaseQty / _step;
          purchaseRolls.push({ label: `${_cartons} carton${_cartons !== 1 ? 's' : ''} of ${_step} = ${_purchaseQty} total ${_tileUnit}`, count: _purchaseQty });
          if (_extraTotal > 0) purchaseRolls.push({ label: `${_extraTotal} ${_tileUnit} ${_mistakes > 0 ? 'included for mistakes' : 'left over from the last carton'}`, count: 0 });
        } else {
          purchaseRolls.push({ label: _extraTotal > 0 ? `${tileCount} ${_tileUnit} + ${_extraTotal} extra for mistakes` : `${tileCount} ${_tileUnit}`, count: _purchaseQty });
        }
        // Update purchaseArea to reflect carton-rounded quantity
        purchaseArea = _purchaseQty * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
      } else {
        const maxLen = product.maxLengthFt || 50;
        const minLen = product.minLengthFt || 25;
        const optMode = this._rollOptimizationMode;
        const uniform = optMode === 'uniform';

        // Uniform mode: all purchase rolls use the same length (longest piece, ceil half-foot)
        let binCapacity = maxLen;
        let uniformLen = 0;
        if (uniform && pieces.length > 0) {
          const maxPiece = pieces.reduce((m, p) => Math.max(m, p.length), 0);
          uniformLen = (this._uniformTargetLen > 0)
            ? this._uniformTargetLen
            : this._pickLeastOverageLen(pieces.map(p => p.length), product);
          binCapacity = Math.max(minLen, uniformLen);
        }

        // Bin-pack pieces (minMaterial uses best-of-3 strategies, others use FFD)
        const bins = uniform
          ? this._binPackRollPieces(pieces, Math.max(minLen, uniformLen), minLen, 'minSeams') // uniform caps at uniformLen
          : this._binPackRollPieces(pieces, maxLen, minLen, optMode);

        // Compute purchase area from bins
        purchaseArea = 0;
        for (const bin of bins) {
          const purchaseLen = uniform ? Math.max(minLen, uniformLen) : Math.max(minLen, Math.ceil(bin.usedLen));
          bin.purchaseLen = purchaseLen;
          purchaseArea += bin.width * purchaseLen;
        }

        // Merge bins by purchase length, collect cut patterns
        const mergedByLen = new Map();
        for (const bin of bins) {
          const lenKey = `${bin.width.toFixed(2)}|${bin.purchaseLen}`;
          if (!mergedByLen.has(lenKey)) {
            mergedByLen.set(lenKey, { width: bin.width, purchaseLen: bin.purchaseLen, totalCount: 0, cutPatterns: [] });
          }
          const mg = mergedByLen.get(lenKey);
          mg.totalCount++;
          const totalPieces = bin.cuts.reduce((s, c) => s + c.count, 0);
          if (totalPieces > 1) {
            const cutKey = bin.cuts.map(c => `${c.count}@${c.length.toFixed(2)}`).sort().join('+');
            const existing = mg.cutPatterns.find(cp => cp.key === cutKey);
            if (existing) existing.count++;
            else mg.cutPatterns.push({ key: cutKey, cuts: bin.cuts, count: 1, waste: bin.purchaseLen - bin.usedLen });
          } else {
            mg.singlePieceWaste = bin.purchaseLen - bin.usedLen;
          }
        }

        // Sort longest to shortest
        const uTag = uniform ? ' uniform' : '';
        const sortedGroups = [...mergedByLen.values()].sort((a, b) => b.purchaseLen - a.purchaseLen);
        for (const mg of sortedGroups) {
          purchaseRolls.push({ label: this._buildRollLabel(mg, uniform), count: mg.totalCount, _mg: mg });
        }
      }

      // Waste computed from purchase area (what you actually buy)
      const wastePercent = netArea > 0 ? Math.round(((purchaseArea - netArea) / netArea) * 100) : 0;

      // MOQ
      const colorMoq = color ? (color.moq || 0) : 0;
      const effectiveMoq = colorMoq > 0 ? colorMoq : (product.moqFallbackQty || 0);
      const moqUnitRaw = product.moqUnit || '';
      const moqUnit = (effectiveMoq === 1 && moqUnitRaw.endsWith('s')) ? moqUnitRaw.slice(0, -1) : moqUnitRaw;
      let moqNote = '';
      let underMoq = false;
      let atMoq = false;
      if (effectiveMoq > 0) {
        const unitLower = moqUnitRaw.toLowerCase().replace(/s$/, '');
        let userQty = 0;
        if (unitLower.includes('sq') || unitLower.includes('square')) userQty = purchaseArea;
        else if (unitLower.includes('tile') || unitLower.includes('paver') || unitLower.includes('plank')) userQty = tileCount;
        else if (unitLower.includes('roll')) userQty = purchaseRolls.reduce((s, r) => s + r.count, 0);
        else userQty = purchaseArea;
        underMoq = userQty < effectiveMoq;
        if (underMoq) {
          moqNote = `Minimum order: ${effectiveMoq} ${moqUnit}`.trim();
        } else if (userQty <= effectiveMoq * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${effectiveMoq} ${moqUnit} minimum`.trim();
        }
      }
      // Fallback: if no MOQ configured but purchase equals a single minimum roll,
      // flag it — the min roll length is the binding constraint
      if (!moqNote && product.type === 'roll' && purchaseArea > 0) {
        const minRollArea = (product.minLengthFt || 25) * (product.widthFt || 4);
        if (purchaseArea <= minRollArea * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${Math.round(minRollArea)} sq ft minimum`;
        }
      }

      // Detail toggle label based on product type
      const detailLabel = product.shape === 'paver' ? 'Paver details'
        : product.shape === 'plank' ? 'Plank details'
        : product.type === 'tile' ? 'Tile details'
        : 'Roll details';

      return {
        productName: product.name,
        colorName: color ? color.name : '',
        imageUrl,
        swatch,
        hasImage,
        floorArea: netArea,
        materialArea: purchaseArea,
        seamLF: Math.round(seamLF),
        wastePercent,
        purchaseRolls,
        moqNote,
        underMoq,
        atMoq,
        detailLabel,
        zoneName,
        borderFt,
        dividerLineLF,
        islandTransitionLF,
        _pieces: pieces,
        _tileCount: tileCount,
        _product: product,
        _color: color
      };
    };

    if (zones.length > 1) {
      // Collect per-zone entries with product/color keys
      const zoneEntries = [];
      for (let zi = 0; zi < zones.length; zi++) {
        const zone = zones[zi];
        const zf = this._getZoneFlooring(room, zone);
        if (!zf) continue;
        const product = this._getProduct(zf.productId);
        if (!product) continue;
        const color = product.colors.find(c => c.id === zf.colorId);
        const entry = computeEntry(product, color, zone.polygon, zf.flooringAngle || 0,
          null, `Zone ${zi + 1}`, this._zoneHoles(zone));
        if (entry) {
          entry._key = `${zf.productId}|${zf.colorId}`;
          entry._zoneIdx = zi; // roll sharing: which zone these pieces belong to (zone order of _getZonesForRoom)
          zoneEntries.push(entry);
        }
      }

      // Group by product+color
      const groups = new Map();
      for (const e of zoneEntries) {
        if (!groups.has(e._key)) groups.set(e._key, []);
        groups.get(e._key).push(e);
      }

      for (const entries of groups.values()) {
        if (entries.length === 1) {
          results.push(entries[0]);
          continue;
        }
        // Merge zones with same product+color
        const first = entries[0];
        const product = first._product;
        const color = first._color;
        const zoneDetails = entries.map(e => ({
          zoneName: e.zoneName,
          floorArea: e.floorArea,
          materialArea: e.materialArea,
          wastePercent: e.wastePercent,
          borderFt: e.borderFt,
          dividerLineLF: e.dividerLineLF,
          islandTransitionLF: e.islandTransitionLF
        }));
        const totalFloorArea = entries.reduce((s, e) => s + e.floorArea, 0);
        const totalBorderFt = entries.reduce((s, e) => s + (e.borderFt || 0), 0);
        const totalDividerLineLF = entries.reduce((s, e) => s + (e.dividerLineLF || 0), 0);
        const totalIslandTransitionLF = entries.reduce((s, e) => s + (e.islandTransitionLF || 0), 0);

        // Re-bin-pack combined pieces
        let purchaseRolls = [];
        let purchaseArea = 0;
        let combinedTileCount = 0;

        if (product.shape === 'paver') {
          combinedTileCount = entries.reduce((s, e) => s + e._tileCount, 0);
          const extra = Math.max(1, Math.ceil(combinedTileCount * 0.05));
          purchaseRolls = [{ label: `${combinedTileCount} pavers + ${extra} extra for mistakes`, count: combinedTileCount + extra }];
          purchaseArea = (combinedTileCount + extra) * product.widthFt * product.heightFt * 0.86924;
        } else if (product.type === 'tile') {
          combinedTileCount = entries.reduce((s, e) => s + e._tileCount, 0);
          const extra = this._mistakeExtra(product, combinedTileCount);
          purchaseRolls = [{ label: extra > 0 ? `${combinedTileCount} tiles + ${extra} extra for mistakes` : `${combinedTileCount} ${product.shape === 'plank' ? 'planks' : 'tiles'}`, count: combinedTileCount + extra }];
          purchaseArea = combinedTileCount * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
        } else {
          // Roll products: the zones' pieces packed together on one set of rolls - or, in rep
          // mode while roll sharing is off for this material, each zone on its own rolls
          // (Ryan 2026-11-03: a zone is an area like a space, and one Share-rolls answer
          // covers both). The public designer always packs them together, as before.
          const sets = this._rollPieceSetsBySpace(this._rollShareKey(product, color), entries.map(e => e._pieces || []));
          const packed = this._packRollPurchase(sets, product);
          purchaseArea = packed.purchaseArea;
          purchaseRolls = packed.purchaseRolls;
        }

        const wastePercent = totalFloorArea > 0 ? Math.round(((purchaseArea - totalFloorArea) / totalFloorArea) * 100) : 0;

        // Combined MOQ
        const colorMoq = color ? (color.moq || 0) : 0;
        const effectiveMoq = colorMoq > 0 ? colorMoq : (product.moqFallbackQty || 0);
        const moqUnitRaw = product.moqUnit || '';
        const moqUnit = (effectiveMoq === 1 && moqUnitRaw.endsWith('s')) ? moqUnitRaw.slice(0, -1) : moqUnitRaw;
        let moqNote = '';
        let underMoq = false;
        let atMoq = false;
        if (effectiveMoq > 0) {
          const unitLower = moqUnitRaw.toLowerCase().replace(/s$/, '');
          let userQty = 0;
          if (unitLower.includes('sq') || unitLower.includes('square')) userQty = purchaseArea;
          else if (unitLower.includes('tile') || unitLower.includes('paver')) userQty = combinedTileCount;
          else if (unitLower.includes('roll')) userQty = purchaseRolls.reduce((s, r) => s + r.count, 0);
          else userQty = purchaseArea;
          underMoq = userQty < effectiveMoq;
          if (underMoq) {
            moqNote = `Minimum order: ${effectiveMoq} ${moqUnit}`.trim();
          } else if (userQty <= effectiveMoq * 1.05) {
            atMoq = true;
            moqNote = `Order meets ${effectiveMoq} ${moqUnit} minimum`.trim();
          }
        }
        if (!moqNote && product.type === 'roll' && purchaseArea > 0) {
          const minRollArea = (product.minLengthFt || 25) * (product.widthFt || 4);
          if (purchaseArea <= minRollArea * 1.05) {
            atMoq = true;
            moqNote = `Order meets ${Math.round(minRollArea)} sq ft minimum`;
          }
        }

        results.push({
          productName: first.productName,
          colorName: first.colorName,
          imageUrl: first.imageUrl,
          swatch: first.swatch,
          hasImage: first.hasImage,
          floorArea: totalFloorArea,
          materialArea: purchaseArea,
          wastePercent,
          purchaseRolls,
          moqNote,
          underMoq,
          atMoq,
          detailLabel: first.detailLabel,
          zoneName: null,
          zoneDetails,
          borderFt: totalBorderFt,
          dividerLineLF: totalDividerLineLF,
          islandTransitionLF: totalIslandTransitionLF,
          _pieces: entries.flatMap(e => e._pieces),
          _zonePieces: entries.map(e => ({ zoneIdx: e._zoneIdx, pieces: e._pieces || [] })), // roll sharing: per zone
          _tileCount: entries.reduce((s, e) => s + e._tileCount, 0),
          _product: product,
          _color: color
        });
      }
    } else {
      if (!room.flooring) return [];
      const product = this._getProduct(room.flooring.productId);
      if (!product) return [];
      const color = product.colors.find(c => c.id === room.flooring.colorId);
      // For single-zone, compute net polygon (minus holes)
      const roomPoly = geo.roomPolygon;
      const entry = computeEntry(product, color, roomPoly, room.flooringAngle || 0,
        null, null);
      if (entry) results.push(entry);
    }
    return results;
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Roll sharing across areas (Ryan 2026-10-15, zones 2026-11-03).
     Two AREAS floored in the same roll product AND colour can cut from one set of
     rolls, so a 25 sq ft closet stops buying its own minimum roll. An area is a zone
     (a part of a space behind a dividing line) or a whole space with no zones, so one
     space split into two zones counts, and so do 17 spaces with any mix of zones.
     Rep-only, and off for a material until the rep turns it on (_rollShare[key] ===
     true); the public designer never asks and packs everything together, as it always
     has. When on, every area using that material shares; the largest space (the
     "lead") carries the whole purchase and the other spaces read "cut from <lead>
     rolls"; inside the lead, its largest zone draws the leftover. Off (rep mode), each
     area buys its own rolls everywhere — the space cards, the totals, the quote, the
     Seam Plan and the canvas. */
  /** Panel rows: one checkbox per colour of this roll product that is painted
   *  into 2+ areas. Empty string otherwise (the container hides itself). */
  _rollShareRowsHtml(product) {
    if (!this.rd._repMode || !product || product.type !== 'roll') return '';
    let html = '';
    for (const color of (product.colors || [])) {
      const areas = this._rollAreasUsingMaterial(product.id, color.id);
      if (this._rollAreaCount(areas) < 2) continue;
      const key = this._rollShareKey(product, color);
      html += '<label class="rd-roll-share-row">';
      html += '<input type="checkbox" class="rd-roll-share-cb" data-share-key="' + this._escAttr(key) + '"' + (this._rollShare[key] === true ? ' checked' : '') + '>';
      html += '<span>Share rolls between areas: ' + this._escHtml(color.name);
      html += '<span class="rd-roll-share-sub">' + this._escHtml(areas.map(a => a.label).join(', ')) + '. Off: each space and zone buys its own rolls.</span>';
      html += '</span></label>';
    }
    return html;
  }

  _refreshRollShareRows() {
    if (!this.rd._repMode) return;
    document.querySelectorAll('.rd-roll-share[data-product-id]').forEach(el => {
      const product = this._getProduct(el.dataset.productId);
      el.innerHTML = product ? this._rollShareRowsHtml(product) : '';
    });
  }

  _setRollShare(key, on) {
    if (!key) return;
    this._rollShare[key] = !!on;
    this._allRoomFlooringMemo = null;
    this._rollSharePiecesMemo = null;
    const rd = this.rd;
    rd._triggerAutoSave();
    rd.render();
    rd.updateMeasurements();
    this._updateMaterialSummary();
    this._syncQuoteTab();
    this._refreshRollShareRows();
  }

  /** Ask the rep once per material, the first time it lands in a second area (a second
   *  zone of the same space, or a second space). */
  _maybePromptRollShare(productId, colorId) {
    const rd = this.rd;
    if (!rd._repMode || !productId || typeof rd._showConfirm !== 'function') return;
    const product = this._getProduct(productId);
    if (!product || product.type !== 'roll') return;
    const color = (product.colors || []).find(c => c.id === colorId) || null;
    const key = this._rollShareKey(product, color);
    if (this._rollShare[key] === true || this._rollShare[key] === false) return; // already decided
    const areas = this._rollAreasUsingMaterial(productId, colorId);
    if (this._rollAreaCount(areas) < 2) return;
    const esc = (t) => this._escHtml(t);
    const msg = '<strong>Share rolls between these areas?</strong><br><br>'
      + esc(areas.map(a => a.label).join(', ')) + ' all use ' + esc(product.name) + (color ? ' in ' + esc(color.name) : '') + '. '
      + 'Sharing cuts the smaller areas\u2019 pieces from the largest area\u2019s rolls, so a small zone or space does not need its own minimum roll. '
      + 'Keep them separate when the areas are far apart and each needs its own rolls.<br><br>'
      + 'You can change this any time with the \u201cShare rolls between areas\u201d checkbox in this product\u2019s roll options.';
    rd._showConfirm(msg, 'Share rolls', () => this._setRollShare(key, true),
      { blueOk: true, cancelText: 'Keep separate', onCancel: () => this._setRollShare(key, false) });
  }

  /* A COLOUR CHANGE KEEPS THE ROOM'S ROLL SHARING (Ryan 2026-11-04, RW-1536-B). The choice is stored per product and
     colour, so changing the colour left the new colour with no choice at all and "Share rolls between areas" read as
     off: the plan redrew with separate rolls and far more overage. The old colour's choice carries to the new one. A
     never-asked old colour carries nothing, so a choice already made on the new colour stands. */
  _carryRollShare(product, fromColorId, toColorId) {
    if (!this._rollShare || !product || fromColorId === toColorId) return;
    const colors = product.colors || [];
    const from = colors.find((c) => c.id === fromColorId);
    const to = colors.find((c) => c.id === toColorId);
    if (!from || !to) return;
    const was = this._rollShare[this._rollShareKey(product, from)];
    if (was !== true && was !== false) return;
    this._rollShare[this._rollShareKey(product, to)] = was;
    this._allRoomFlooringMemo = null;
    this._rollSharePiecesMemo = null;
  }

  _rollShareKey(product, color) {
    return `${product ? product.id : ''}::${color ? color.id : ''}`;
  }

  _rollShareOn(key) {
    return !!(this.rd._repMode && this._rollShare && this._rollShare[key] === true);
  }

  _anyRollShareOn() {
    if (!this.rd._repMode || !this._rollShare) return false;
    for (const k in this._rollShare) if (this._rollShare[k] === true) return true;
    return false;
  }

  /** The spaces whose floor uses this roll material, in plan order, each with its
   *  areas: `zones` = how many of its zones wear the material (1 for a space with no
   *  zones), `area` = their floor area, `leadZone` = the index (zone order of
   *  _getZonesForRoom) of the largest of them, `label` = "Basement (2 zones)".
   *  Reads the painted assignments and the zone geometry only — no layouts. */
  _rollAreasUsingMaterial(pid, cid) {
    const rd = this.rd;
    const out = [];
    let idx = 0;
    for (const r of rd.rooms) {
      if (!r.closed || r.type !== 'add') continue;
      idx++;
      const name = r.name || `Space ${idx}`;
      if (r.zoneFlooring && r.zoneFlooring.length > 0) {
        if (!r.zoneFlooring.some(z => z && z.productId === pid && z.colorId === cid)) continue;
        const geo = rd._getRoomGeoCache(r);
        const zones = (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) ? (this._getZonesForRoom(r, geo) || []) : [];
        let count = 0, area = 0, leadZone = -1, leadArea = -1;
        zones.forEach((z, zi) => {
          const zf = this._getZoneFlooring(r, z);
          if (!zf || zf.productId !== pid || zf.colorId !== cid) return;
          const a = z.polygon ? Math.abs(this._polygonArea(z.polygon)) : 0;
          count++; area += a;
          if (a > leadArea) { leadArea = a; leadZone = zi; }
        });
        if (count === 0) continue; // painted entries whose zones no longer exist
        out.push({ room: r, name, zones: count, area, leadZone, label: count > 1 ? `${name} (${count} zones)` : name });
        continue;
      }
      if (!(r.flooring && r.flooring.productId === pid && r.flooring.colorId === cid)) continue;
      const geo = rd._getRoomGeoCache(r);
      const area = (geo && geo.roomPolygon && geo.roomPolygon.length >= 3) ? Math.abs(this._polygonArea(geo.roomPolygon)) : 0;
      out.push({ room: r, name, zones: 1, area, leadZone: -1, label: name });
    }
    return out;
  }

  /** How many areas (zones + whole spaces) a _rollAreasUsingMaterial list holds. */
  _rollAreaCount(areas) {
    return areas.reduce((s, a) => s + a.zones, 0);
  }

  /** The spaces sharing rolls of this material and the lead (largest, same rule as
   *  the Seam Plan). Null when sharing is off or fewer than two AREAS use it - one
   *  space split into two zones is a group of one member. */
  _rollShareMembers(pid, cid) {
    if (!this._rollShareOn(`${pid}::${cid}`)) return null;
    const members = this._rollAreasUsingMaterial(pid, cid);
    if (this._rollAreaCount(members) < 2) return null;
    const lead = members.reduce((a, b) => (b.area > a.area ? b : a));
    return { members, lead: lead.room, leadZone: lead.leadZone };
  }

  /** How this layout draws the leftover of a bought roll when its material is shared:
   *  'borrow' (no leftover, the pieces come off the lead's rolls) or 'lead' (pack every
   *  member's pieces). Inside the lead space, its largest zone in the material is the
   *  lead and its other zones borrow; a zone geo is told apart by the ':z<i>' its
   *  fingerprint carries. Null = not shared. */
  _rollShareLayoutRole(room, geo) {
    if (!this.rd || !this._anyRollShareOn() || !room || !room.flooring) return null;
    const pid = room.flooring.productId, cid = room.flooring.colorId;
    const grp = this._rollShareMembers(pid, cid);
    if (!grp || !grp.members.some(m => m.room === room)) return null;
    const others = grp.members.filter(m => m.room !== grp.lead).map(m => m.room);
    const fp = grp.members.map(m => {
      const g = this.rd._getRoomGeoCache(m.room);
      return (m.room === grp.lead ? 'L' : '') + (g && g.fp || '') + ':' + (m.room.flooringAngle || 0)
        + ':' + JSON.stringify(m.room.zoneFlooring || []);
    }).join('|');
    if (room !== grp.lead) return { mode: 'borrow', fp: 'B' + fp };
    const zm = /:z(\d+)/.exec((geo && geo.fp) || '');
    if (zm) {
      const zi = +zm[1];
      if (zi !== grp.leadZone) return { mode: 'borrow', fp: 'B' + fp };
      return { mode: 'lead', fp: 'L' + fp + ':z' + zi, pid, cid, others, room, zoneIdx: zi }; // own fp: its gathered pieces differ from the whole-room lead's
    }
    if (geo !== this.rd._getRoomGeoCache(room)) return null;
    return { mode: 'lead', fp: 'L' + fp, pid, cid, others, room };
  }

  _rollShareLayoutFP(room, geo) {
    const role = this._rollShareLayoutRole(room, geo);
    return role ? role.fp : '';
  }

  /** Cut pieces of the other areas in a shared group (for the lead's leftover): the
   *  other spaces, and - when the lead space is zoned - its other zones. */
  _rollShareOtherPieces(role) {
    if (this._rollSharePiecesMemo && this._rollSharePiecesMemo.fp === role.fp) return this._rollSharePiecesMemo.pieces;
    if (this._rollShareGathering) return [];
    this._rollShareGathering = true;
    const pieces = [];
    try {
      const sameMaterial = (e) => e._product && e._product.id === role.pid && e._color && e._color.id === role.cid;
      for (const r of role.others) {
        for (const e of this._computePerRoomFlooring(r)) {
          if (sameMaterial(e)) pieces.push(...(e._pieces || []));
        }
      }
      if (role.zoneIdx != null && role.room) {
        for (const e of this._computePerRoomFlooring(role.room)) {
          if (!sameMaterial(e)) continue;
          for (const zp of (e._zonePieces || [])) if (zp.zoneIdx !== role.zoneIdx) pieces.push(...zp.pieces);
        }
      }
    } finally {
      this._rollShareGathering = false;
    }
    this._rollSharePiecesMemo = { fp: role.fp, pieces };
    return pieces;
  }

  /** Names of the spaces whose flooring uses this roll material, in plan order. */
  _spacesUsingRollMaterial(productId, colorId) {
    return this._rollAreasUsingMaterial(productId, colorId).map(a => a.name);
  }

  /** Pack roll pieces into purchase rolls. Each array in pieceSets is packed on its
   *  own (separate rolls); the bins from every set merge into one purchase list.
   *  Same maths as computeEntry's roll branch. */
  _packRollPurchase(pieceSets, product) {
    const maxLen = product.maxLengthFt || 50;
    const minLen = product.minLengthFt || 25;
    const optMode = this._rollOptimizationMode;
    const uniform = optMode === 'uniform';
    const bins = [];
    for (const pieces of pieceSets) {
      if (!pieces || pieces.length === 0) continue;
      let uniformLen = 0;
      if (uniform) {
        uniformLen = (this._uniformTargetLen > 0)
          ? this._uniformTargetLen
          : this._pickLeastOverageLen(pieces.map(p => p.length), product);
      }
      const setBins = uniform
        ? this._binPackRollPieces(pieces, Math.max(minLen, uniformLen), minLen, 'minSeams')
        : this._binPackRollPieces(pieces, maxLen, minLen, optMode);
      for (const bin of setBins) {
        bin.purchaseLen = uniform ? Math.max(minLen, uniformLen) : Math.max(minLen, Math.ceil(bin.usedLen));
        bins.push(bin);
      }
    }
    let purchaseArea = 0;
    for (const bin of bins) purchaseArea += bin.width * bin.purchaseLen;
    const mergedByLen = new Map();
    for (const bin of bins) {
      const lenKey = `${bin.width.toFixed(2)}|${bin.purchaseLen}`;
      if (!mergedByLen.has(lenKey)) {
        mergedByLen.set(lenKey, { width: bin.width, purchaseLen: bin.purchaseLen, totalCount: 0, cutPatterns: [] });
      }
      const mg = mergedByLen.get(lenKey);
      mg.totalCount++;
      const totalPieces = bin.cuts.reduce((s, c) => s + c.count, 0);
      if (totalPieces > 1) {
        const cutKey = bin.cuts.map(c => `${c.count}@${c.length.toFixed(2)}`).sort().join('+');
        const existing = mg.cutPatterns.find(cp => cp.key === cutKey);
        if (existing) existing.count++;
        else mg.cutPatterns.push({ key: cutKey, cuts: bin.cuts, count: 1, waste: bin.purchaseLen - bin.usedLen });
      } else {
        mg.singlePieceWaste = bin.purchaseLen - bin.usedLen;
      }
    }
    const purchaseRolls = [...mergedByLen.values()]
      .sort((a, b) => b.purchaseLen - a.purchaseLen)
      .map(mg => ({ label: this._buildRollLabel(mg, uniform), count: mg.totalCount, _mg: mg }));
    return { purchaseArea, purchaseRolls };
  }

  /** The pieces of one product+colour group, split into the sets that get their own
   *  rolls: one set per area (a space, or each zone of a zoned space) unless sharing is
   *  on for that material. Public mode keeps the old behaviour (one set — everything
   *  packed together). */
  _rollPieceSetsBySpace(key, piecesBySpace) {
    if (!this.rd._repMode || this._rollShareOn(key)) return [piecesBySpace.flat()];
    return piecesBySpace;
  }

  /** Per-room flooring entries for every space, with roll sharing applied.
   *  Returns Map(room -> entries). Memoised for the current task, because the space
   *  list asks once per room and every answer needs every room. */
  _computeAllRoomFlooring() {
    if (this._allRoomFlooringMemo) return this._allRoomFlooringMemo;
    const rd = this.rd;
    const out = new Map();
    const rows = [];
    let idx = 0;
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') { out.set(room, []); continue; }
      idx++;
      const entries = this._computePerRoomFlooring(room);
      rows.push({ room, spaceName: room.name || `Space ${idx}`, entries });
      out.set(room, entries);
    }
    const groups = new Map();
    for (const row of rows) {
      for (let i = 0; i < row.entries.length; i++) {
        const e = row.entries[i];
        if (!e._product || e._product.type !== 'roll') continue;
        const key = this._rollShareKey(e._product, e._color);
        if (!this._rollShareOn(key)) continue;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ row, i, entry: e });
      }
    }
    for (const members of groups.values()) {
      if (members.length < 2) continue;
      const _grp = this._rollShareMembers(members[0].entry._product.id, members[0].entry._color ? members[0].entry._color.id : '');
      const _inLead = _grp ? members.filter(m => m.row.room === _grp.lead) : [];
      const lead = (_inLead.length ? _inLead : members).reduce((a, b) => (b.entry.floorArea > a.entry.floorArea ? b : a));
      const product = lead.entry._product;
      const { purchaseArea, purchaseRolls } = this._packRollPurchase([members.flatMap(m => m.entry._pieces || [])], product);
      const totalFloor = members.reduce((s, m) => s + m.entry.floorArea, 0);
      const others = members.filter(m => m !== lead);
      const moq = this._rollMoqInfo(product, lead.entry._color, purchaseArea, purchaseRolls.reduce((s, r) => s + r.count, 0));
      lead.row.entries[lead.i] = {
        ...lead.entry,
        materialArea: purchaseArea,
        purchaseRolls,
        wastePercent: totalFloor > 0 ? Math.round(((purchaseArea - totalFloor) / totalFloor) * 100) : 0,
        moqNote: moq.moqNote, underMoq: moq.underMoq, atMoq: moq.atMoq,
        _shareWith: others.map(m => ({ spaceName: m.row.spaceName, floorArea: m.entry.floorArea })),
        _shareFloorArea: totalFloor,
      };
      for (const m of others) {
        m.row.entries[m.i] = {
          ...m.entry,
          materialArea: 0,
          wastePercent: 0,
          purchaseRolls: [{ label: `Cut from ${this._escHtml(lead.row.spaceName)} rolls`, count: 0 }],
          moqNote: '', underMoq: false, atMoq: false,
          _borrowedFrom: lead.row.spaceName,
        };
      }
    }
    this._allRoomFlooringMemo = out;
    queueMicrotask(() => { this._allRoomFlooringMemo = null; });
    return out;
  }

  /** _computePerRoomFlooring with roll sharing applied (what every space card,
   *  the quote and the plan item list read). Untouched when nothing is shared. */
  _computePerRoomFlooringShared(room) {
    if (!this._anyRollShareOn()) return this._computePerRoomFlooring(room);
    return this._computeAllRoomFlooring().get(room) || [];
  }

  /** MOQ note for a roll purchase — the same rules as computeEntry. */
  _rollMoqInfo(product, color, purchaseArea, rollCount) {
    const colorMoq = color ? (color.moq || 0) : 0;
    const effectiveMoq = colorMoq > 0 ? colorMoq : (product.moqFallbackQty || 0);
    const moqUnitRaw = product.moqUnit || '';
    const moqUnit = (effectiveMoq === 1 && moqUnitRaw.endsWith('s')) ? moqUnitRaw.slice(0, -1) : moqUnitRaw;
    let moqNote = '', underMoq = false, atMoq = false;
    if (effectiveMoq > 0) {
      const unitLower = moqUnitRaw.toLowerCase().replace(/s$/, '');
      const userQty = unitLower.includes('roll') ? rollCount : purchaseArea;
      underMoq = userQty < effectiveMoq;
      if (underMoq) moqNote = `Minimum order: ${effectiveMoq} ${moqUnit}`.trim();
      else if (userQty <= effectiveMoq * 1.05) { atMoq = true; moqNote = `Order meets ${effectiveMoq} ${moqUnit} minimum`.trim(); }
    }
    if (!moqNote && purchaseArea > 0) {
      const minRollArea = (product.minLengthFt || 25) * (product.widthFt || 4);
      if (purchaseArea <= minRollArea * 1.05) { atMoq = true; moqNote = `Order meets ${Math.round(minRollArea)} sq ft minimum`; }
    }
    return { moqNote, underMoq, atMoq };
  }

  /** Return combined flooring data across ALL rooms, grouped by product+color.
   *  Each entry has the same shape as per-room entries but with spaceDetails
   *  (array of per-space breakdowns) instead of zoneDetails when a variant
   *  spans multiple spaces. */
  _getCombinedFlooringData() {
    const rd = this.rd;
    // Collect tagged entries from every room
    const taggedEntries = [];
    for (const room of rd.rooms) {
      const entries = this._computePerRoomFlooringShared(room);
      const spaceName = room.name || 'Space';
      for (const e of entries) {
        e._spaceName = spaceName;
        taggedEntries.push(e);
      }
    }
    if (taggedEntries.length === 0) return [];

    // Group by product+color key
    const groups = new Map();
    for (const e of taggedEntries) {
      const key = `${e.productName}||${e.colorName}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }

    const results = [];
    for (const entries of groups.values()) {
      if (entries.length === 1) {
        // Single entry — pass through with spaceDetails for consistency
        // Strip zoneDetails so the total summary doesn't show zone-level breakdown
        const { zoneDetails: _, ...rest } = entries[0];
        results.push({
          ...rest,
          spaceDetails: [{ spaceName: rest._spaceName, floorArea: rest.floorArea, materialArea: rest.materialArea, seamLF: rest.seamLF, wastePercent: rest.wastePercent, borderFt: rest.borderFt, dividerLineLF: rest.dividerLineLF, _borrowedFrom: rest._borrowedFrom }]
        });
        continue;
      }

      // Multiple spaces with same product+color — combine
      const first = entries[0];
      const product = first._product;
      const color = first._color;
      const totalFloorArea = entries.reduce((s, e) => s + e.floorArea, 0);

      // Re-bin-pack combined pieces for accurate purchase calculation
      let purchaseRolls = [];
      let purchaseArea = 0;
      let combinedTileCount = 0;

      if (product.shape === 'paver') {
        combinedTileCount = entries.reduce((s, e) => s + e._tileCount, 0);
        const extra = Math.max(1, Math.ceil(combinedTileCount * 0.05));
        purchaseRolls = [{ label: `${combinedTileCount} pavers + ${extra} extra for mistakes`, count: combinedTileCount + extra }];
        purchaseArea = (combinedTileCount + extra) * product.widthFt * product.heightFt * 0.86924;
      } else if (product.type === 'tile') {
        combinedTileCount = entries.reduce((s, e) => s + e._tileCount, 0);
        const extra = this._mistakeExtra(product, combinedTileCount);
        purchaseRolls = [{ label: extra > 0 ? `${combinedTileCount} tiles + ${extra} extra for mistakes` : `${combinedTileCount} ${product.shape === 'plank' ? 'planks' : 'tiles'}`, count: combinedTileCount + extra }];
        purchaseArea = combinedTileCount * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
      } else {
        // Roll products: re-bin-pack the pieces. Rep mode packs each space on its own
        // unless roll sharing is on for this material (_rollPieceSetsBySpace).
        const packed = this._packRollPurchase(
          this._rollPieceSetsBySpace(this._rollShareKey(product, color), entries.map(e => e._pieces || [])), product);
        purchaseArea = packed.purchaseArea;
        purchaseRolls = packed.purchaseRolls;
      }

      const wastePercent = totalFloorArea > 0 ? Math.round(((purchaseArea - totalFloorArea) / totalFloorArea) * 100) : 0;

      // Combined MOQ
      const colorMoq = color ? (color.moq || 0) : 0;
      const effectiveMoq = colorMoq > 0 ? colorMoq : (product.moqFallbackQty || 0);
      const moqUnitRaw = product.moqUnit || '';
      const moqUnit = (effectiveMoq === 1 && moqUnitRaw.endsWith('s')) ? moqUnitRaw.slice(0, -1) : moqUnitRaw;
      let moqNote = '';
      let underMoq = false;
      let atMoq = false;
      if (effectiveMoq > 0) {
        const unitLower = moqUnitRaw.toLowerCase().replace(/s$/, '');
        let userQty = 0;
        if (unitLower.includes('sq') || unitLower.includes('square')) userQty = purchaseArea;
        else if (unitLower.includes('tile') || unitLower.includes('paver')) userQty = combinedTileCount;
        else if (unitLower.includes('roll')) userQty = purchaseRolls.reduce((s, r) => s + r.count, 0);
        else userQty = purchaseArea;
        underMoq = userQty < effectiveMoq;
        if (underMoq) {
          moqNote = `Minimum order: ${effectiveMoq} ${moqUnit}`.trim();
        } else if (userQty <= effectiveMoq * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${effectiveMoq} ${moqUnit} minimum`.trim();
        }
      }
      if (!moqNote && product.type === 'roll' && purchaseArea > 0) {
        const minRollArea = (product.minLengthFt || 25) * (product.widthFt || 4);
        if (purchaseArea <= minRollArea * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${Math.round(minRollArea)} sq ft minimum`;
        }
      }

      const spaceDetails = entries.map(e => ({
        spaceName: e._spaceName,
        floorArea: e.floorArea,
        materialArea: e.materialArea,
        wastePercent: e.wastePercent,
        borderFt: e.borderFt,
        dividerLineLF: e.dividerLineLF,
        islandTransitionLF: e.islandTransitionLF,
        _borrowedFrom: e._borrowedFrom
      }));
      const totalBorderFt = entries.reduce((s, e) => s + (e.borderFt || 0), 0);
      const totalDividerLineLF = entries.reduce((s, e) => s + (e.dividerLineLF || 0), 0);
      const totalIslandTransitionLF = entries.reduce((s, e) => s + (e.islandTransitionLF || 0), 0);

      results.push({
        productName: first.productName,
        colorName: first.colorName,
        imageUrl: first.imageUrl,
        swatch: first.swatch,
        hasImage: first.hasImage,
        floorArea: totalFloorArea,
        borderFt: totalBorderFt,
        dividerLineLF: totalDividerLineLF,
        islandTransitionLF: totalIslandTransitionLF,
        materialArea: purchaseArea,
        wastePercent,
        purchaseRolls,
        moqNote,
        underMoq,
        atMoq,
        detailLabel: first.detailLabel,
        spaceDetails
      });
    }
    return results;
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Per-room turf data for space cards */
  _getPerRoomTurfData(room) {
    const rd = this.rd;
    if (!room || !room.closed || room.type !== 'add') return [];
    const geo = rd._getRoomGeoCache(room);
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return [];

    // Filter turf objects whose center falls inside this room
    const turfObjs = (rd.objects || []).filter(obj =>
      obj.type === 'turf' && obj.turfProductId && rd.pointInPolygon(obj.x, obj.y, poly)
    );
    if (turfObjs.length === 0) return [];

    // Group by product+color
    const groups = new Map();
    for (const obj of turfObjs) {
      const key = `${obj.turfProductId}::${obj.turfColorId}`;
      if (!groups.has(key)) groups.set(key, { objs: [], totalArea: 0 });
      const g = groups.get(key);
      g.objs.push(obj);
      g.totalArea += this._turfPieceArea(obj);
    }

    const results = [];
    for (const [, g] of groups) {
      const sample = g.objs[0];
      const product = this._getProduct(sample.turfProductId);
      if (!product) continue;
      const color = product.colors.find(c => c.id === sample.turfColorId);
      const imageUrl = color ? (color.imageUrl || '') : '';
      const swatch = color ? (color.imageUrl ? `url(${color.imageUrl})` : (color.swatch || color.hex)) : '#888';
      const hasImage = color && !!color.imageUrl;

      // Build per-piece detail entries
      const turfPieces = g.objs.map(o => ({
        dims: `${rd.formatMeasurement(o.width)} × ${rd.formatMeasurement(o.depth)}`,
        area: o.width * o.depth
      }));

      results.push({
        productName: product.name || product.id,
        colorName: color ? color.name : '',
        imageUrl,
        swatch,
        hasImage,
        _product: product,
        floorArea: g.totalArea,
        materialArea: g.totalArea,
        wastePercent: '0',
        purchaseRolls: [],
        moqNote: null,
        underMoq: false,
        detailLabel: null,
        turfPieces,
        turfCustomizations: this._serializeTurfCustomizations(sample, product)
      });
    }
    return results;
  }

  _getCombinedTurfData() {
    const rd = this.rd;
    const turfObjs = (rd.objects || []).filter(obj => obj.type === 'turf' && obj.turfProductId);
    if (turfObjs.length === 0) return [];

    // Group by product+color across all turf
    const groups = new Map();
    for (const obj of turfObjs) {
      const key = `${obj.turfProductId}::${obj.turfColorId}`;
      if (!groups.has(key)) groups.set(key, { objs: [], totalArea: 0 });
      const g = groups.get(key);
      g.objs.push(obj);
      g.totalArea += this._turfPieceArea(obj);
    }

    const results = [];
    for (const [, g] of groups) {
      const sample = g.objs[0];
      const product = this._getProduct(sample.turfProductId);
      if (!product) continue;
      const color = product.colors.find(c => c.id === sample.turfColorId);
      const imageUrl = color ? (color.imageUrl || '') : '';
      const swatch = color ? (color.imageUrl ? `url(${color.imageUrl})` : (color.swatch || color.hex)) : '#888';
      const hasImage = color && !!color.imageUrl;

      const turfPieces = g.objs.map(o => ({
        dims: `${rd.formatMeasurement(o.width)} × ${rd.formatMeasurement(o.depth)}`,
        area: o.width * o.depth
      }));

      results.push({
        productName: product.name || product.id,
        colorName: color ? color.name : '',
        imageUrl,
        swatch,
        hasImage,
        _product: product,
        floorArea: g.totalArea,
        materialArea: g.totalArea,
        wastePercent: '0',
        purchaseRolls: [],
        moqNote: null,
        underMoq: false,
        detailLabel: null,
        turfPieces,
        turfCustomizations: this._serializeTurfCustomizations(sample, product)
      });
    }
    return results;
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Serialize turf customizations for breakdown + RFQ handoff */
  _serializeTurfCustomizations(obj, product) {
    const c = {};

    // Build hex→name lookup from product colors (same list as _buildTurfControlsHtml)
    const colorNameMap = { '#FFFFFF': 'White', '#000000': 'Black' };
    if (product && product.colors) {
      for (const pc of product.colors) {
        if (pc.hex) colorNameMap[pc.hex.toUpperCase()] = pc.name;
      }
    }

    // Field Lines
    if (obj.turfLines) {
      const originMap = { center: 'Center', offset: 'Offset', edge: 'Edge-to-Edge' };
      const rawHex = (obj.turfLineColor || '#FFFFFF').toUpperCase();
      c.fieldLines = {
        origin: originMap[obj.turfLineOrigin || 'center'] || 'Center',
        color: colorNameMap[rawHex] || obj.turfLineColor || '#FFFFFF'
      };
    }

    // Hash Marks
    if (obj.turfHashes) {
      c.hashMarks = {
        sides: (obj.turfHashSides === 'long' || !obj.turfHashSides) ? 'Long Sides' : 'Both Sides',
        spacing: (obj.turfHashSpacing === 1) ? "1'" : '1 yd',
        center: obj.turfCenterHashes || false
      };
    }

    // Yard Numbers
    if (obj.turfYardNumbers) {
      c.yardNumbers = {
        spacing: (obj.turfYardNumberSpacing || 10) + ' yd',
        style: obj.turfYardNumberMode === 'sequential' ? 'Sequential' : 'Football (50 center)'
      };
    }

    // Border Line
    if (obj.turfBorderLine) {
      const inset = obj.turfBorderLineInset || 4;
      c.borderLine = {
        width: inset >= 12 ? (inset / 12) + "'" : inset + '"',
        edges: (obj.turfBorderLineEdges || ['top', 'right', 'bottom', 'left']).map(
          e => e.charAt(0).toUpperCase() + e.slice(1)
        )
      };
    }

    // Sled/Sprint Lanes
    if (obj.turfSledLanes) {
      c.sledLanes = {
        count: obj.turfSledLaneCount || 2,
        edges: (obj.turfSledLaneEdges || ['right']).map(
          e => e.charAt(0).toUpperCase() + e.slice(1)
        ),
        interval: (obj.turfSledLaneInterval === 0 ? 'None' : (obj.turfSledLaneInterval || 5) + ' yd'),
        startFinish: obj.turfSledLaneStartFinish !== false,
        numbers: obj.turfSledLaneNumbers || false
      };
    }

    // Logos / markings
    if (obj.turfLogos && obj.turfLogos.length > 0) {
      c.logos = obj.turfLogos.map(l => {
        if (l.type === 'ladder') return { type: 'ladder', length: (obj.turfAgilityLadderLength || 18) + ' ft' };
        if (l.type === 'dotdrill') return { type: 'dotdrill' };
        return { type: 'image' };
      });
    }

    // Markings direction
    if (obj.turfLineDirection) {
      c.direction = obj.turfLineDirection === 'widthwise' ? 'Widthwise' : 'Lengthwise';
    } else if (obj.turfMarkingsRotated) {
      c.direction = 'Widthwise';
    }

    // Build summary string
    const parts = [];
    if (c.fieldLines) parts.push('Field Lines (' + c.fieldLines.origin + ')');
    if (c.hashMarks) parts.push('Hash Marks (' + c.hashMarks.spacing + ')' + (c.hashMarks.center ? ' + Center' : ''));
    if (c.yardNumbers) parts.push('Yard Numbers (' + c.yardNumbers.spacing + ')');
    if (c.borderLine) parts.push('Border Line (' + c.borderLine.width + ')');
    if (c.sledLanes) parts.push(c.sledLanes.count + ' Sled Lane' + (c.sledLanes.count > 1 ? 's' : ''));
    if (c.logos) {
      const imgs = c.logos.filter(l => l.type === 'image').length;
      const ladders = c.logos.filter(l => l.type === 'ladder').length;
      const dots = c.logos.filter(l => l.type === 'dotdrill').length;
      if (imgs) parts.push(imgs + ' Logo' + (imgs > 1 ? 's' : ''));
      if (ladders) parts.push(ladders + ' Agility Ladder' + (ladders > 1 ? 's' : ''));
      if (dots) parts.push(dots + ' Dot Drill' + (dots > 1 ? 's' : ''));
    }
    c.summary = parts.length > 0 ? parts.join(' \u00B7 ') : 'No customizations';

    return c;
  }
  /* END CUSTOM_PLANNER_BY_CLAUDE */

  // ── Measurements Wrapping ─────────────────

  _wrapMeasurements() {
    const rd = this.rd;
    const self = this;
    rd._getPerRoomFlooringData = (room) => self._computePerRoomFlooringShared(room);
    rd._getCombinedFlooringData = () => self._getCombinedFlooringData();
    rd._getPerRoomTurfData = (room) => self._getPerRoomTurfData(room);
    rd._getCombinedTurfData = () => self._getCombinedTurfData();
    const origUpdate = rd.updateMeasurements.bind(rd);
    rd.updateMeasurements = function() {
      origUpdate();
      self._updateMaterialSummary();
      self._syncCompassBtn();
      self._syncFloorTab();
      self._syncEquipmentTab();
      self._syncFinishingTab();
      self._syncQuoteTab();
    };
    // Debounced version for high-frequency events (compass drag, etc.)
    this._measureUpdateTimer = null;
    this._debouncedMeasurementsUpdate = () => {
      clearTimeout(this._measureUpdateTimer);
      this._measureUpdateTimer = setTimeout(() => rd.updateMeasurements(), 300);
    };
  }

  _appendFlooringMeasurements() {
    // Superseded by _updateMaterialSummary() → rdFlooringSummary
  }

  // ── Rotation Button + Popover ──────────

  _createRotateButton() {
    const toolbar = document.querySelector('.tool-controls');
    if (!toolbar) return;

    const btn = document.createElement('button');
    btn.className = 'tool-btn';
    btn.id = 'floorRotateBtn';
    btn.title = 'Roll Angle (R)';
    btn.style.display = 'none';
    btn.innerHTML = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polygon points="12,3.5 13.8,10 12,8.5 10.2,10" fill="currentColor" stroke="none"/><polygon points="12,20.5 10.2,14 12,15.5 13.8,14" fill="currentColor" stroke="none" opacity="0.4"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/></svg>';

    const pop = document.createElement('div');
    pop.className = 'floor-rotate-popover';
    pop.id = 'floorRotatePopover';
    pop.innerHTML = this._buildRotatePopoverHTML();

    // Insert compass before zoom button (compass left, zoom right in the row)
    const zoomBtn = document.getElementById('zoomToggleBtn');
    if (zoomBtn) {
      toolbar.insertBefore(btn, zoomBtn);
    } else {
      toolbar.appendChild(btn);
    }
    // On mobile, move popover to canvas-container (like other popovers)
    if (this.rd._isMobile) {
      this.rd.container.appendChild(pop);
    } else {
      toolbar.appendChild(pop);
    }

    this._rotateBtn = btn;
    this._rotatePop = pop;
    this._rotateDialCanvas = pop.querySelector('.floor-rotate-dial-canvas');

    this._rotateMaterialDisplay = pop.querySelector('.floor-rotate-material-display');
    this._rotateZoneNav = pop.querySelector('.floor-rotate-zone-nav');
    this._rotateZoneLabel = pop.querySelector('.floor-rotate-zone-label');
    this._rotateRoomLabel = pop.querySelector('.floor-rotate-room-label');
    this._rotateZoneLabels = pop.querySelector('.floor-rotate-zone-labels');
    this._selectedRotateRoomIdx = 0;
    this._drawRotateDial(this._rotateDialCanvas, 0);
    this._setupRotatePopoverEvents(btn, pop);

    // Zone cycle buttons
    pop.querySelector('.floor-rotate-zone-prev').addEventListener('click', () => this._cycleTargetRoom(-1));
    pop.querySelector('.floor-rotate-zone-next').addEventListener('click', () => this._cycleTargetRoom(1));

    this._rotateOptimizeBtn = pop.querySelector('.floor-rotate-optimize-btn:not(.floor-rotate-optimize-space-btn)');
    this._rotateOptimizeBtn.addEventListener('click', () => {
      this._resetWallSnap();
      this._optimizeFlooringAngle();
    });

    this._rotateOptimizeSpaceBtn = pop.querySelector('.floor-rotate-optimize-space-btn');
    this._rotateOptimizeSpaceBtn.addEventListener('click', () => {
      if (this._rotateOptimizeSpaceBtn.disabled) return;
      this._resetWallSnap();
      this._optimizeSpaceAngle();
    });
  }

  _createFloorEraserButton() {
    const toolbar = document.querySelector('.tool-controls');
    if (!toolbar) return;

    const btn = document.createElement('button');
    btn.className = 'tool-btn';
    btn.id = 'floorEraserBtn';
    btn.title = 'Erase Flooring';
    btn.style.display = 'none';
    btn.innerHTML = '<svg width="22" height="22" viewBox="0 2 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="10" rx="2" transform="rotate(-30 12 15)"/><line x1="4.5" y1="14.5" x2="19.5" y2="14.5" stroke-width="1" transform="rotate(-30 12 15)"/></svg>';

    // Insert before the end spacer (after compass + zoom)
    const spacers = toolbar.querySelectorAll('.toolbar-spacer');
    const endSpacer = spacers.length > 0 ? spacers[spacers.length - 1] : null;
    if (endSpacer) {
      toolbar.insertBefore(btn, endSpacer);
    } else {
      toolbar.appendChild(btn);
    }

    const rd = this.rd;
    const self = this;
    const eraserCursor = 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2724%27 height=%2724%27 viewBox=%270 0 24 24%27%3E%3Crect x=%274%27 y=%2710%27 width=%2716%27 height=%2710%27 rx=%272%27 fill=%27%23ff6b6b%27 stroke=%27%23333%27 stroke-width=%271.5%27 transform=%27rotate(-30 12 15)%27/%3E%3Crect x=%274%27 y=%2714%27 width=%2716%27 height=%276%27 rx=%271%27 fill=%27%23ffaaaa%27 stroke=%27%23333%27 stroke-width=%271%27 transform=%27rotate(-30 12 15)%27/%3E%3C/svg%3E") 12 20, pointer';

    btn.addEventListener('click', () => {
      if (rd.activeTool === 'floor-eraser') {
        // Toggle off — back to hand
        rd.activeTool = 'hand';
        rd.mode = 'hand';
        rd.canvas.style.cursor = 'grab';
        btn.classList.remove('active');
        rd.updateStatus('Pan to explore your layout.');
      } else {
        rd.activeTool = 'floor-eraser';
        rd.mode = 'floor-eraser';
        rd.canvas.style.cursor = eraserCursor;
        // Deactivate all other tool buttons
        ['selectToolBtn', 'handToolBtn', 'floorRotateBtn'].forEach(id => {
          const el = document.getElementById(id);
          if (el) el.classList.remove('active');
        });
        // Close compass popover if open
        if (self._rotatePop) self._rotatePop.classList.remove('visible');
        btn.classList.add('active');
        rd.updateStatus('Click a floored zone to erase its flooring.');
      }
      // Clear any roller brush activation
      self._hoveredRoom = null;
      self._hoveredZone = null;
      self._hoveredExteriorZone = null;
      rd.render();
    });

    this._floorEraserBtn = btn;
    this._floorEraserCursor = eraserCursor;
  }

  // ── Brand Color Palette Button ──

  _createPaletteButton() {
    const toolbar = document.querySelector('.tool-controls');
    if (!toolbar) return;

    const btn = document.createElement('button');
    btn.className = 'tool-btn';
    btn.id = 'brandPaletteBtn';
    btn.title = 'Equipment Colors';
    btn.style.display = 'none';
    btn.innerHTML = '<svg style="width:28px;height:28px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><g transform="rotate(-35 12 12)"><rect x="4.5" y="14.5" width="11" height="7" rx="1" fill="currentColor" stroke="none"/><rect x="5.8" y="12" width="8.4" height="2.5"/><path d="M8 12C7.5 9.8 9 8.2 9.2 6.2 9.3 4.8 8.2 3 7.7 1.5L12.3 1.5C11.8 3 10.7 4.8 10.8 6.2 11 8.2 12.5 9.8 12 12"/></g></svg>';

    // Insert before the end spacer
    const spacers = toolbar.querySelectorAll('.toolbar-spacer');
    const endSpacer = spacers.length > 0 ? spacers[spacers.length - 1] : null;
    if (endSpacer) {
      toolbar.insertBefore(btn, endSpacer);
    } else {
      toolbar.appendChild(btn);
    }

    const rd = this.rd;
    const self = this;
    const popName = 'brandPalette';
    const popId = 'brandPalettePopover';

    // Popover panel — append inside toolbar so it positions relative to it
    const pop = document.createElement('div');
    pop.className = 'brand-palette-popover';
    pop.id = popId;
    if (rd._isMobile) {
      rd.container.appendChild(pop);
    } else {
      toolbar.appendChild(pop);
    }

    // Desktop: hover-to-show, mouseleave-to-hide, click-to-pin
    if (!rd._isMobile) {
      btn.addEventListener('mouseenter', () => {
        if (btn.disabled) return;
        if (rd._pinnedPopover !== popName) {
          rd._hoverShowPop(popId, () => self._buildPalettePopover(pop));
        }
      });
      btn.addEventListener('mouseleave', () => {
        rd._cancelHoverShow();
        if (rd._pinnedPopover !== popName) rd._hidePop(popId);
      });
      pop.addEventListener('mouseenter', () => clearTimeout(rd._popoverTimers[popId]));
      pop.addEventListener('mouseleave', () => {
        if (rd._pinnedPopover !== popName) rd._hidePop(popId);
      });
    }

    // Click: pin/unpin
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (btn.disabled) return;
      self._buildPalettePopover(pop);
      rd._pinToggle(popName, popId);
      // If pinned, take over as the blue button — clear all others
      if (rd._pinnedPopover === popName) {
        ['selectToolBtn', 'handToolBtn', 'penToolBtn', 'objectsToolBtn', 'labelToolBtn', 'eraserBtn', 'zoomToggleBtn', 'floorRotateBtn'].forEach(id => {
          const el = document.getElementById(id);
          if (el) el.classList.remove('active');
        });
        btn.classList.add('active');
      } else {
        // Unpinned — remove blue, switch back to select/cursor tool
        btn.classList.remove('active');
        rd.activeTool = 'select';
        rd.mode = 'select';
        rd.canvas.style.cursor = 'default';
        const selBtn = document.getElementById('selectToolBtn');
        if (selBtn) selBtn.classList.add('active');
      }
    });

    this._paletteBtn = btn;
    this._palettePop = pop;
  }

  /** Brand color config — Legend Fitness full palette */
  _getBrandColorConfig() {
    return {
      'Legend Fitness': {
        powderCoat: [
          { name: 'Jet Black', hex: '#2D2926', swatch: 'jet-black' },
          { name: 'Olympia White', hex: '#ffffff', swatch: 'olympia-white' },
          { name: 'Slate Gray', hex: '#53565A', swatch: 'slate-gray' },
          { name: 'Storm Gray', hex: '#B1B3B3', swatch: 'storm-gray' },
          { name: 'Stone Gray', hex: '#A69F88', swatch: 'stone-gray' },
          { name: 'Silver Vein', hex: '#52565b', swatch: 'silver-vein', pattern: true },
          { name: 'Patriot Blue', hex: '#004B87', swatch: 'patriot-blue' },
          { name: 'Post Office Blue', hex: '#003057', swatch: 'post-office-blue' },
          { name: 'Light Blue', hex: '#007DBA', swatch: 'light-blue' },
          { name: 'Mars Red', hex: '#DA291C', swatch: 'mars-red' },
          { name: 'Red Rock', hex: '#93272C', swatch: 'red-rock' },
          { name: 'Wine Red', hex: '#6A2C3E', swatch: 'wine-red' },
          { name: 'Burnt Orange', hex: '#E57200', swatch: 'burnt-orange' },
          { name: 'Organic Orange', hex: '#E35205', swatch: 'organic-orange' },
          { name: 'Buttercup', hex: '#FED141', swatch: 'buttercup' },
          { name: 'Signal Yellow', hex: '#F2A900', swatch: 'signal-yellow' },
          { name: 'Evergreen', hex: '#115740', swatch: 'evergreen' },
          { name: 'Rush Green', hex: '#509E2F', swatch: 'rush-green' },
          { name: 'Mint Green', hex: '#007A53', swatch: 'mint-green' },
          { name: 'Reseda Green', hex: '#6E7544', swatch: 'reseda-green' },
          { name: 'Chocolate Brown', hex: '#4E3629', swatch: 'chocolate-brown' },
          { name: 'Coffee Brown', hex: '#AF6D04', swatch: 'coffee-brown' },
          { name: 'Purple Wave', hex: '#582C83', swatch: 'purple-wave' },
          { name: 'Lilac', hex: '#7C6992', swatch: 'lilac' },
          { name: 'Pink', hex: '#E06287', swatch: 'pink' },
          { name: 'Textured Black', hex: '#2D2926', swatch: 'textured-black', matte: true },
          { name: 'Textured Red', hex: '#AB2328', swatch: 'textured-red', matte: true },
          { name: 'Ebony Chrome', hex: '#63666A', swatch: 'ebony-chrome', sparkle: true },
          { name: 'Platinum Sparkle', hex: '#8A8D8F', swatch: 'platinum-sparkle', sparkle: true },
          { name: 'Sun Gold', hex: '#84754E', swatch: 'sun-gold', sparkle: true }
        ],
        upholstery: [
          { name: 'Black', hex: '#2D2926', swatch: 'uph-black' },
          { name: 'White', hex: '#eeeeee', swatch: 'uph-white' },
          { name: 'Dove', hex: '#B1B3B3', swatch: 'uph-dove' },
          { name: 'Graystone', hex: '#63666A', swatch: 'uph-graystone' },
          { name: 'Regimental Blue', hex: '#003057', swatch: 'uph-regimental-blue' },
          { name: 'Royal', hex: '#004B87', swatch: 'uph-royal' },
          { name: 'Space Blue', hex: '#007DBA', swatch: 'uph-space-blue' },
          { name: 'Deep Violet', hex: '#582C83', swatch: 'uph-deep-violet' },
          { name: 'Rosalee', hex: '#E06287', swatch: 'uph-rosalee' },
          { name: 'American Beauty', hex: '#DA291C', swatch: 'uph-american-beauty' },
          { name: 'Candy Apple Red', hex: '#AB2328', swatch: 'uph-candy-apple-red' },
          { name: 'Paprika', hex: '#93272C', swatch: 'uph-paprika' },
          { name: 'Claret', hex: '#6A2C3E', swatch: 'uph-claret' },
          { name: 'Nasturtium', hex: '#E35205', swatch: 'uph-nasturtium' },
          { name: 'Cinnamon', hex: '#AF6D04', swatch: 'uph-cinnamon' },
          { name: 'Mocha', hex: '#4E3629', swatch: 'uph-mocha' },
          { name: 'Golden Corn', hex: '#F2A900', swatch: 'uph-golden-corn' },
          { name: 'Bonanza Gold', hex: '#84754E', swatch: 'uph-bonanza-gold' },
          { name: 'Sun Yellow', hex: '#FED141', swatch: 'uph-sun-yellow' },
          { name: 'Emerald', hex: '#007A53', swatch: 'uph-emerald' },
          { name: 'Forest', hex: '#115740', swatch: 'uph-forest' },
          { name: 'Olive Green', hex: '#6E7544', swatch: 'uph-olive-green' },
          { name: 'Woodland Camo', hex: '#4d4f42', swatch: 'uph-woodland-camo', pattern: true },
          { name: 'Desert Camo', hex: '#a5986a', swatch: 'uph-desert-camo', pattern: true }
        ]
      }
    };
  }

  _buildPalettePopover(pop) {
    const rd = this.rd;
    const self = this;
    // Determine which vendors are currently placed
    const placedVendors = new Set();
    if (rd.objects) {
      for (const obj of rd.objects) {
        if (obj.type === 'equipment' && obj.equipmentId) {
          const info = rd._equipmentImages[obj.equipmentId];
          if (info && info.vendor) placedVendors.add(info.vendor);
        }
      }
    }
    const config = this._getBrandColorConfig();
    // Also add vendors from config that may not be placed yet
    const allVendors = new Set([...placedVendors, ...Object.keys(config)]);

    if (allVendors.size === 0) {
      pop.innerHTML = '<div class="palette-empty">Place equipment to customize colors</div>';
      return;
    }

    let html = '<div class="palette-title">Equipment Colors</div>';
    for (const vendor of allVendors) {
      const vendorConf = config[vendor];
      if (!vendorConf) continue; // Skip vendors without color config
      const current = rd._brandColors[vendor] || {};
      html += `<div class="palette-brand">`;
      html += `<div class="palette-brand-name">${vendor}</div>`;
      // Powder Coat row
      html += `<div class="palette-row">`;
      html += `<span class="palette-label">Frame</span>`;
      html += `<div class="palette-swatches">`;
      for (const sw of vendorConf.powderCoat) {
        const swPcPattern = sw.pattern ? sw.swatch : null;
        const isActive = sw.hex === null ? !current.powderCoat : current.powderCoat === sw.hex && !!sw.matte === !!current.pcMatte && (swPcPattern || null) === (current.pcPattern || null);
        const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
        const bgStyle = sw.hex === null
          ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
          : swUrl ? `background:url(${swUrl}) center/cover` : `background:${sw.hex}`;
        const matteAttr = sw.matte ? ' data-matte="true"' : '';
        const pcPatAttr = sw.pattern ? ` data-pattern="${sw.swatch}"` : '';
        const sparkleAttr = sw.sparkle ? ' data-sparkle="true"' : '';
        html += `<button class="palette-swatch${isActive ? ' active' : ''}" data-vendor="${vendor}" data-type="powderCoat" data-color="${sw.hex || ''}"${matteAttr}${pcPatAttr}${sparkleAttr} title="${sw.name}" style="${bgStyle}"></button>`;
      }
      html += `</div></div>`;
      // Upholstery row — only show if any placed equipment of this vendor has upholstery
      let hasUph = false;
      if (rd.objects) {
        for (const obj of rd.objects) {
          if (obj.type !== 'equipment') continue;
          const info = rd._equipmentImages[obj.equipmentId];
          if (info && info.vendor === vendor && info.hasUpholstery) { hasUph = true; break; }
        }
      }
      // Also check all loaded entries (not just placed) for this POC
      if (!hasUph) {
        for (const id in rd._equipmentImages) {
          const info = rd._equipmentImages[id];
          if (info.vendor === vendor && info.hasUpholstery) { hasUph = true; break; }
        }
      }
      if (hasUph && vendorConf.upholstery) {
        html += `<div class="palette-row">`;
        html += `<span class="palette-label">Pads</span>`;
        html += `<div class="palette-swatches">`;
        for (const sw of vendorConf.upholstery) {
          const swPattern = (sw.pattern && sw.swatch) ? sw.swatch : null;
          const isActive = sw.hex === null ? !current.upholstery : current.upholstery === sw.hex && (swPattern || null) === (current.uphPattern || null);
          const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
          const bgStyle = sw.hex === null
            ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
            : swUrl ? `background:url(${swUrl}) center/cover` : `background:${sw.hex}`;
          const patternAttr = sw.pattern && sw.swatch ? ` data-pattern="${sw.swatch}"` : '';
          html += `<button class="palette-swatch${isActive ? ' active' : ''}" data-vendor="${vendor}" data-type="upholstery" data-color="${sw.hex || ''}"${patternAttr} title="${sw.name}" style="${bgStyle}"></button>`;
        }
        html += `</div></div>`;
      }
      html += `</div>`;
    }
    pop.innerHTML = html;

    // Swatch click handlers
    pop.querySelectorAll('.palette-swatch').forEach(sw => {
      sw.addEventListener('click', (e) => {
        e.stopPropagation();
        const vendor = sw.dataset.vendor;
        const type = sw.dataset.type;
        const color = sw.dataset.color || null;
        const patternKey = sw.dataset.pattern || null;
        if (!rd._brandColors[vendor]) rd._brandColors[vendor] = {};
        if (color) {
          rd._brandColors[vendor][type] = color;
          // Gloss flag: smooth powder coats get a sheen, textured/matte do not
          if (type === 'powderCoat') {
            rd._brandColors[vendor].pcMatte = sw.dataset.matte === 'true';
            rd._brandColors[vendor].pcSparkle = sw.dataset.sparkle === 'true';
            // Pattern overlay for textured powder coats (e.g. Silver Vein)
            if (patternKey) {
              rd._brandColors[vendor].pcPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[vendor].pcPattern;
            }
          }
          // Camo patterns: only update uphPattern when clicking an upholstery swatch
          if (type === 'upholstery') {
            if (patternKey) {
              rd._brandColors[vendor].uphPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[vendor].uphPattern;
            }
          }
        } else {
          delete rd._brandColors[vendor][type];
          if (type === 'powderCoat') { delete rd._brandColors[vendor].pcMatte; delete rd._brandColors[vendor].pcPattern; delete rd._brandColors[vendor].pcSparkle; }
          if (type === 'upholstery') delete rd._brandColors[vendor].uphPattern;
          if (!rd._brandColors[vendor].powderCoat && !rd._brandColors[vendor].upholstery) {
            delete rd._brandColors[vendor];
          }
        }
        rd._invalidateBrandColorCaches(vendor);
        self._buildPalettePopover(pop); // refresh active states
        rd.updateMeasurements(); // refresh layout panel color names
        // Trigger auto-save
        if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') {
          rd._storage._triggerAutoSave();
        } else if (typeof rd._triggerAutoSave === 'function') {
          rd._triggerAutoSave();
        }
      });
    });
  }

  _syncCompassBtn() {
    const btn = this._rotateBtn;
    if (!btn) return;
    const hasFlooring = this.rd.rooms.some(r => r.closed && this._roomHasFlooring(r)) || this._hasExteriorFlooring();
    if (hasFlooring) {
      btn.disabled = false;
      btn.title = 'Roll Angle (R)';
    } else {
      // Close popover if it was open
      if (this.rd._pinnedPopover === 'floorRotate') {
        this._resetWallSnap();
        this.rd._pinnedPopover = null;
        this.rd._syncPinIcons();
        btn.classList.remove('active');
      }
      this.rd._hidePop('floorRotatePopover');
      btn.disabled = true;
      btn.title = 'Add flooring to a room first to rotate direction';
    }
  }

  _syncFloorTab() {
    const tab = document.querySelector('.rd-step-tab[data-step="floor"]');
    if (!tab) return;
    const hasRoom = this.rd.rooms.some(r => r.closed);
    if (hasRoom) {
      tab.classList.remove('disabled');
    } else {
      // If currently on floor step, kick back to draw
      if (this.currentStep === 'floor') this._switchStep('draw');
      tab.classList.add('disabled');
    }
  }

  _syncEquipmentTab() {
    const tab = document.querySelector('.rd-step-tab[data-step="equipment"]');
    if (!tab) return;
    const hasRoom = this.rd.rooms.some(r => r.closed);
    if (hasRoom) {
      tab.classList.remove('disabled');
    } else {
      if (this.currentStep === 'equipment') this._switchStep('draw');
      tab.classList.add('disabled');
    }
  }

  _syncQuoteTab() {
    const tab = document.querySelector('.rd-step-tab[data-step="quote"]');
    if (!tab) return;
    // Enable when flooring or equipment has been added
    const hasFlooring = this.rd.rooms.some(r => r.closed && this._roomHasFlooring(r)) || this._hasExteriorFlooring();
    const hasEquipment = this.rd.objects && this.rd.objects.some(o => o.type === 'equipment');
    if (hasFlooring || hasEquipment) {
      tab.classList.remove('disabled');
    } else {
      tab.classList.add('disabled');
    }
    // Rep mode: "Create a Quote" vs public "Get a Quote"
    const label = tab.querySelector('.rd-step-label');
    if (label) label.textContent = this.rd._repMode ? 'Create a Quote' : 'Get a Quote';
    // Portal mode: disable quote tab if workstation page is not live for this user
    if (this.rd._portalMode) {
      try {
        const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
        const pageConfig = JSON.parse(sessionStorage.getItem('rep_portal_page_config') || '{}');
        /* CUSTOM_PORTALACCESS_BY_CLAUDE (2026-10-02) — an external user (role 'agency') never
           quotes: the Workstation and /rep-quotes refuse his token, and the 401 logs him out. */
        if ((sess.role || '') === 'agency') {
          tab.classList.add('disabled');
          if (label) label.textContent = 'Quote (team only)';
        } else if (pageConfig.workstation === false && !sess.admin) {
          tab.classList.add('disabled');
          if (label) label.textContent = 'Coming Soon';
        }
      } catch (e) {}
    }
  }

  _syncFinishingTab() {
    const tab = document.querySelector('.rd-step-tab[data-step="finishing"]');
    if (!tab) return;
    const hasEquipment = this.rd.objects && this.rd.objects.some(o => o.type === 'equipment');
    const hasTurf = this.rd.objects && this.rd.objects.some(o => o.type === 'turf');
    const hasPlatforms = this.rd.objects && this.rd.objects.some(o => o.type === 'platform');
    if (hasEquipment || hasTurf || hasPlatforms) {
      tab.classList.remove('disabled');
    } else {
      if (this.currentStep === 'finishing') this._switchStep('draw');
      tab.classList.add('disabled');
    }
  }

  /* ── Smooth accordion helpers (shared across flooring / equipment / customize panels) ── */

  /** Set initial max-height on all accordion bodies in a container after innerHTML rebuild */
  _accordionInit(container, catSelector, bodySelector) {
    container.querySelectorAll(catSelector).forEach(cat => {
      const body = cat.querySelector(':scope > ' + bodySelector);
      if (!body) return;
      body.style.maxHeight = cat.classList.contains('collapsed') ? '0' : 'none';
    });
  }

  /** Smoothly open an accordion category */
  _accordionOpen(catEl, bodyEl) {
    catEl.classList.remove('collapsed');
    bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
    const onEnd = () => { bodyEl.style.maxHeight = 'none'; bodyEl.removeEventListener('transitionend', onEnd); };
    bodyEl.addEventListener('transitionend', onEnd);
  }

  /** Smoothly close an accordion category */
  _accordionClose(catEl, bodyEl) {
    bodyEl.style.maxHeight = bodyEl.scrollHeight + 'px';
    bodyEl.offsetHeight; // force reflow
    catEl.classList.add('collapsed');
    bodyEl.style.maxHeight = '0';
  }

  /**
   * Toggle a category open/closed with smooth animation.
   * Closes all sibling categories first (one-open-at-a-time).
   * @param {Element} catEl — the category wrapper element
   * @returns {boolean} true if the category is now open
   */
  _accordionToggle(catEl) {
    // Detect body class and sibling selector based on category type
    let bodyClass, sibSelector;
    if (catEl.classList.contains('rd-eq-category')) {
      bodyClass = 'rd-eq-category-body'; sibSelector = '.rd-eq-category';
    } else if (catEl.classList.contains('rd-finishing-vendor-collapsible')) {
      bodyClass = 'rd-finishing-vendor-body'; sibSelector = '.rd-finishing-vendor-collapsible';
    } else if (catEl.classList.contains('rd-finishing-category')) {
      bodyClass = 'rd-finishing-category-body'; sibSelector = '.rd-finishing-category';
    } else if (catEl.classList.contains('rd-customize-category')) {
      bodyClass = 'rd-product-category-body'; sibSelector = '.rd-customize-category';
    } else {
      bodyClass = 'rd-product-category-body'; sibSelector = '.rd-product-category';
    }
    const bodyEl = catEl.querySelector(':scope > .' + bodyClass);
    if (!bodyEl) return false;
    const wasCollapsed = catEl.classList.contains('collapsed');
    if (wasCollapsed) {
      // Close open siblings first (one-at-a-time per level)
      const parent = catEl.parentElement;
      if (parent) {
        parent.querySelectorAll(':scope > ' + sibSelector + ':not(.collapsed)').forEach(sib => {
          if (sib === catEl) return;
          const sibBody = sib.querySelector(':scope > .' + bodyClass);
          if (sibBody) this._accordionClose(sib, sibBody);
        });
      }
      this._accordionOpen(catEl, bodyEl);
    } else {
      this._accordionClose(catEl, bodyEl);
    }
    return wasCollapsed; // true = now open
  }

  _openFinishingPanel() {
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return;
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    // Update header
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Customize';
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = 'none';
    // Populate body with turf + equipment customization
    const body = document.getElementById('rdSidePanelBody');
    if (body) this._buildFinishingPanelBody(body);
  }

  _buildFinishingPanelBody(body) {
    const rd = this.rd;
    const self = this;
    const config = this._getBrandColorConfig();

    // ── Gather placed turf products ──
    const turfByProduct = {};
    if (rd.objects) {
      for (const obj of rd.objects) {
        if (obj.type === 'turf' && obj.turfProductId) {
          if (!turfByProduct[obj.turfProductId]) turfByProduct[obj.turfProductId] = [];
          turfByProduct[obj.turfProductId].push(obj);
        }
      }
    }
    const turfProductIds = Object.keys(turfByProduct);
    const hasTurf = turfProductIds.length > 0;

    // ── Gather placed equipment vendors ──
    const placedVendors = new Set();
    const vendorHasPC = new Set();   // vendors with at least one placed item that has powder coat
    const vendorHasUph = new Set();  // vendors with at least one placed item that has upholstery
    if (rd.objects) {
      for (const obj of rd.objects) {
        if (obj.type === 'equipment' && obj.equipmentId) {
          const info = rd._equipmentImages[obj.equipmentId];
          if (info && info.vendor && config[info.vendor]) {
            placedVendors.add(info.vendor);
            if (info.hasPowderCoat) vendorHasPC.add(info.vendor);
            if (info.hasUpholstery) vendorHasUph.add(info.vendor);
          }
        }
      }
    }
    const hasEquipment = placedVendors.size > 0;

    // ── Gather placed platforms ──
    const hasPlatforms = rd.objects && rd.objects.some(o => o.type === 'platform');
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-09-24) — Legend hardwood platforms take a logo decal
    const hardwoodCount = rd.objects ? rd.objects.filter(o => this._isHardwoodPlatformObj(o)).length : 0;
    const hasHardwood = hardwoodCount > 0;

    if (!hasTurf && !hasEquipment && !hasPlatforms) {
      body.innerHTML = '<div style="padding:20px 16px;text-align:center;color:#888;font-size:13px;line-height:1.5;">Place turf, equipment, or platforms on your floor plan to customize markings and colors.</div>';
      return;
    }

    const catChevron = '<svg class="rd-product-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>';
    const finChevron = '<svg class="rd-finishing-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';

    if (!this._finishingCollapsed) {
      this._finishingCollapsed = {};
      // Count top-level categories present
      const _catCount = (hasTurf ? 1 : 0) + (hasEquipment ? 1 : 0) + (hasPlatforms ? 1 : 0) + (hasHardwood ? 1 : 0);
      // Single category → auto-expand it; multiple → all collapsed
      if (_catCount === 1) {
        if (hasTurf) this._finishingCollapsed['_cat:turf'] = false;
        if (hasEquipment) this._finishingCollapsed['_cat:equipment'] = false;
        if (hasPlatforms) this._finishingCollapsed['_cat:platforms'] = false;
        if (hasHardwood) this._finishingCollapsed['_cat:hardwood'] = false;
        // Single vendor in sole category → auto-expand that vendor too
        if (hasEquipment && placedVendors.size === 1) {
          this._finishingCollapsed['_vendor:' + [...placedVendors][0]] = false;
        }
      }
    }
    const turfCatCollapsed = this._finishingCollapsed['_cat:turf'] !== false;
    const equipCatCollapsed = this._finishingCollapsed['_cat:equipment'] !== false;

    let html = '';

    // ═══════════ TURF CATEGORY (only if turf is placed) ═══════════
    // Build flat list of all individual turf objects for the switcher
    const allTurfObjs = [];
    if (hasTurf && rd.objects) {
      for (const obj of rd.objects) {
        if (obj.type === 'turf' && obj.turfProductId) allTurfObjs.push(obj);
      }
    }
    if (hasTurf) {
      // Clamp switcher index
      if ((this._turfSwitcherIndex || 0) >= allTurfObjs.length) this._turfSwitcherIndex = Math.max(0, allTurfObjs.length - 1);
      html += `<div class="rd-product-category rd-customize-category${turfCatCollapsed ? ' collapsed' : ''}" data-customize-cat="turf">`;
      html += `<div class="rd-product-category-title">`;
      html += catChevron;
      html += `<span class="rd-product-category-name">Turf</span>`;
      html += `<span class="rd-product-category-count">${allTurfObjs.length}</span>`;
      html += `</div>`;
      html += `<div class="rd-product-category-body">`;
      if (allTurfObjs.length > 1) {
        // Turf switcher nav (prev/next arrows with "Turf N of M" label)
        const activeIdx = this._turfSwitcherIndex || 0;
        html += '<div class="rd-turf-switcher">';
        html += '<button class="rd-turf-switcher-btn rd-turf-switcher-prev" title="Previous turf"' + (activeIdx <= 0 ? ' disabled' : '') + '>';
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';
        html += '</button>';
        html += '<span class="rd-turf-switcher-label">Turf ' + (activeIdx + 1) + ' of ' + allTurfObjs.length + '</span>';
        html += '<button class="rd-turf-switcher-btn rd-turf-switcher-next" title="Next turf"' + (activeIdx >= allTurfObjs.length - 1 ? ' disabled' : '') + '>';
        html += '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
        html += '</button>';
        html += '</div>';
      }
      for (let _ti = 0; _ti < allTurfObjs.length; _ti++) {
        const turfObj = allTurfObjs[_ti];
        const pid = turfObj.turfProductId;
        const product = this._getProduct(pid);
        const productName = product ? product.title : pid;
        const _turfActive = allTurfObjs.length <= 1 || _ti === (this._turfSwitcherIndex || 0);
        html += `<div class="rd-finishing-vendor rd-turf-switchable" data-product-id="${this._escAttr(pid)}" data-turf-index="${_ti}"${_turfActive ? '' : ' style="display:none"'}>`;
        html += `<div class="rd-finishing-vendor-name">${this._escHtml(productName)}</div>`;
        html += this._buildTurfControlsHtml(product, turfObj);
        html += `</div>`;
      }
      html += `</div></div>`; // close turf category
    }

    // ═══════════ EQUIPMENT CATEGORY (only if equipment is placed) ═══════════
    if (hasEquipment) {
      html += `<div class="rd-product-category rd-customize-category${equipCatCollapsed ? ' collapsed' : ''}" data-customize-cat="equipment">`;
      html += `<div class="rd-product-category-title">`;
      html += catChevron;
      html += `<span class="rd-product-category-name">Equipment</span>`;
      html += `<span class="rd-product-category-count">${placedVendors.size}</span>`;
      html += `</div>`;
      html += `<div class="rd-product-category-body">`;

      for (const vendor of placedVendors) {
        const vendorConf = config[vendor];
        if (!vendorConf) continue;
        const current = rd._brandColors[vendor] || {};
        const vendorCollapsed = this._finishingCollapsed['_vendor:' + vendor] !== false; // default collapsed

        html += `<div class="rd-finishing-vendor rd-finishing-vendor-collapsible${vendorCollapsed ? ' collapsed' : ''}" data-vendor-key="${this._escAttr(vendor)}">`;
        html += `<div class="rd-finishing-vendor-header">`;
        html += catChevron;
        html += `<span class="rd-finishing-vendor-name">${vendor}</span>`;
        html += `</div>`;
        html += `<div class="rd-finishing-vendor-body">`;

        // Frame / Powder Coat section
        const pcDisabled = !vendorHasPC.has(vendor);
        if (vendorConf.powderCoat && vendorConf.powderCoat.length > 0) {
          html += `<div class="rd-finishing-subheading${pcDisabled ? ' rd-swatch-disabled' : ''}">Frames${pcDisabled ? ' <span class="rd-swatch-disabled-note">(no framed equipment placed)</span>' : ''}</div>`;
          html += `<div class="rd-finishing-swatches${pcDisabled ? ' rd-swatch-disabled' : ''}">`;
          for (const sw of vendorConf.powderCoat) {
            const swPcPattern = sw.pattern ? sw.swatch : null;
            const isActive = sw.hex === null ? !current.powderCoat : current.powderCoat === sw.hex && !!sw.matte === !!current.pcMatte && (swPcPattern || null) === (current.pcPattern || null);
            const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
            const bgStyle = sw.hex === null
              ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
              : swUrl ? `background:url(${swUrl}) center/cover` : `background:${sw.hex}`;
            const matteAttr = sw.matte ? ' data-matte="true"' : '';
            const pcPatAttr = sw.pattern ? ` data-pattern="${sw.swatch}"` : '';
            const sparkleAttr = sw.sparkle ? ' data-sparkle="true"' : '';
            html += `<button class="rd-finishing-swatch${isActive ? ' active' : ''}" data-vendor="${vendor}" data-type="powderCoat" data-color="${sw.hex || ''}"${matteAttr}${pcPatAttr}${sparkleAttr} title="${sw.name}" style="${bgStyle}"></button>`;
          }
          html += `</div>`;
        }

        // Pads section
        const uphDisabled = !vendorHasUph.has(vendor);
        if (vendorConf.upholstery && vendorConf.upholstery.length > 0) {
          html += `<div class="rd-finishing-subheading${uphDisabled ? ' rd-swatch-disabled' : ''}">Pads${uphDisabled ? ' <span class="rd-swatch-disabled-note">(no padded equipment placed)</span>' : ''}</div>`;
          html += `<div class="rd-finishing-swatches${uphDisabled ? ' rd-swatch-disabled' : ''}">`;
          for (const sw of vendorConf.upholstery) {
            const swPattern = (sw.pattern && sw.swatch) ? sw.swatch : null;
            const isActive = sw.hex === null ? !current.upholstery : current.upholstery === sw.hex && (swPattern || null) === (current.uphPattern || null);
            const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
            const bgStyle = sw.hex === null
              ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
              : swUrl ? `background:url(${swUrl}) center/cover` : `background:${sw.hex}`;
            const patternAttr = sw.pattern && sw.swatch ? ` data-pattern="${sw.swatch}"` : '';
            html += `<button class="rd-finishing-swatch${isActive ? ' active' : ''}" data-vendor="${vendor}" data-type="upholstery" data-color="${sw.hex || ''}"${patternAttr} title="${sw.name}" style="${bgStyle}"></button>`;
          }
          html += `</div>`;
        }

        html += `</div></div>`; // close vendor-body + vendor
      }

      html += `</div></div>`; // close equipment category
    }

    // ═══════════ PLATFORMS CATEGORY ═══════════
    if (hasPlatforms) {
      const platCatCollapsed = this._finishingCollapsed['_cat:platforms'] !== false;
      this._initPlatformDefaults();
      const pd = this._platformDefaults;
      const epdmColors = this._getEpdmColors();

      // Collect stock colors from compatible products (Rally variant images as swatches)
      const stockColors = [];
      const compatRooms = this._getPlatformCompatibleRooms();
      if (compatRooms.length > 0) {
        const product = compatRooms[0].product;
        if (product && product.colors) {
          for (const c of product.colors) {
            stockColors.push({ id: 'stock-' + c.id, name: c.name, hex: c.hex, textureUrl: c.textureUrl || null, source: 'stock' });
          }
        }
      }

      const platCount = rd.objects.filter(o => o.type === 'platform').length;

      html += `<div class="rd-product-category rd-customize-category${platCatCollapsed ? ' collapsed' : ''}" data-customize-cat="platforms">`;
      html += `<div class="rd-product-category-title">`;
      html += catChevron;
      html += `<span class="rd-product-category-name">Platforms</span>`;
      html += `<span class="rd-product-category-count">${platCount}</span>`;
      html += `</div>`;
      html += `<div class="rd-product-category-body">`;

      // ── Preset Cards (Solid Fill / Two Tone) ──
      const curPreset = this._getPlatformPresets().find(p => p.id === pd.preset);
      const wingsAvail = curPreset ? curPreset.wingsAvail : true;
      html += '<div class="rd-turf-preset-row">';
      // Solid Fill card
      html += '<div class="rd-turf-preset-card rd-platform-preset-card' + (!pd.wings ? ' rd-turf-preset-card--active' : '') + '" data-wing-mode="solid">';
      html += '<svg class="rd-turf-preset-icon" viewBox="0 0 32 20" fill="none"><rect x="1" y="1" width="30" height="18" rx="2" fill="#666" stroke="#444" stroke-width="1"/></svg>';
      html += '<span class="rd-turf-preset-label">Solid Fill</span>';
      html += '</div>';
      // Two Tone card (grayed out if current preset doesn't support wings)
      html += '<div class="rd-turf-preset-card rd-platform-preset-card' + (pd.wings ? ' rd-turf-preset-card--active' : '') + (!wingsAvail ? ' rd-turf-preset-card--disabled' : '') + '" data-wing-mode="wings">';
      html += '<svg class="rd-turf-preset-icon" viewBox="0 0 32 20" fill="none"><rect x="1" y="1" width="30" height="18" rx="2" fill="#666" stroke="#444" stroke-width="1"/><rect x="1" y="1" width="7" height="18" rx="0 2 2 0" fill="#333"/><rect x="24" y="1" width="7" height="18" rx="0 2 2 0" fill="#333"/></svg>';
      html += '<span class="rd-turf-preset-label">Two Tone</span>';
      html += '</div>';
      html += '</div>'; // end preset row

      // Color target toggle (Center / Sides) — below preset cards, only shown when wings enabled
      const colorTarget = this._platformColorTarget || 'center';
      if (pd.wings) {
        html += `<div class="rd-platform-color-target">`;
        html += `<button class="rd-platform-target-btn${colorTarget === 'center' ? ' rd-platform-target-btn--active' : ''}" data-color-target="center">`;
        html += `<svg width="20" height="14" viewBox="0 0 20 14"><rect x="0" y="0" width="4" height="14" rx="1" fill="currentColor" opacity="0.18"/><rect x="5" y="0" width="10" height="14" rx="1" fill="currentColor"/><rect x="16" y="0" width="4" height="14" rx="1" fill="currentColor" opacity="0.18"/></svg>`;
        html += `<span>Center</span></button>`;
        html += `<button class="rd-platform-target-btn${colorTarget === 'wings' ? ' rd-platform-target-btn--active' : ''}" data-color-target="wings">`;
        html += `<svg width="20" height="14" viewBox="0 0 20 14"><rect x="0" y="0" width="4" height="14" rx="1" fill="currentColor"/><rect x="5" y="0" width="10" height="14" rx="1" fill="currentColor" opacity="0.18"/><rect x="16" y="0" width="4" height="14" rx="1" fill="currentColor"/></svg>`;
        html += `<span>Sides</span></button>`;
        html += `</div>`;
      }

      // ══════════════════════════════════════════
      // ── Accordion 1: Colors ──
      // ══════════════════════════════════════════
      const colorsAccordOpen = this._finishingCollapsed['_plat-accord:colors'] === false; // default closed
      let colorBadge = 'Default';
      if (pd.colorConfirmed) {
        colorBadge = pd.wings ? (pd.centerColorName + ' / ' + pd.wingColorName) : pd.centerColorName;
      }
      html += '<div class="rd-turf-accord rd-plat-accord' + (colorsAccordOpen ? ' rd-turf-accord--open' : '') + '" data-plat-accord="colors">';
      html += '<div class="rd-turf-accord-header">';
      html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Colors</span></div>';
      html += '<span class="rd-turf-accord-badge">' + this._escHtml(colorBadge) + '</span>';
      html += '</div>';
      html += '<div class="rd-turf-accord-body">';

      // Single unified swatch grid — active indicators depend on target mode
      const _isSwatchActive = (c, source) => {
        if (!pd.colorConfirmed) return false;
        const centerMatch = pd.centerColor === c.hex && pd.centerColorSource === source && pd.centerColorName === c.name;
        const wingMatch = pd.wings && pd.wingColor === c.hex && pd.wingColorSource === source && pd.wingColorName === c.name;
        if (!pd.wings) return centerMatch;
        if (colorTarget === 'center') return centerMatch;
        return wingMatch;
      };

      if (stockColors.length > 0) {
        html += `<div class="rd-finishing-subheading rd-swatch-group-label" style="font-weight:400;font-size:11px;text-transform:uppercase;letter-spacing:0.3px;">Stock Colors</div>`;
        html += `<div class="rd-finishing-swatches">`;
        for (const c of stockColors) {
          const bgStyle = c.textureUrl ? `background:url(${c.textureUrl}) center/cover` : `background:${c.hex}`;
          html += `<button class="rd-finishing-swatch${_isSwatchActive(c, 'stock') ? ' active' : ''}" data-platform-cust-color="target" data-hex="${c.hex}" data-name="${this._escAttr(c.name)}" data-source="stock" data-texture-url="${c.textureUrl || ''}" title="${this._escAttr(c.name)}" style="${bgStyle}"></button>`;
        }
        html += `</div>`;
      }
      html += `<div class="rd-finishing-subheading rd-swatch-group-label" style="font-weight:400;font-size:11px;text-transform:uppercase;letter-spacing:0.3px;">Custom Colors</div>`;
      html += `<div class="rd-finishing-swatches">`;
      for (const c of epdmColors) {
        const eBgStyle = c.textureUrl ? `background:url(${c.textureUrl}) center/cover` : `background:${c.hex}`;
        html += `<button class="rd-finishing-swatch${_isSwatchActive(c, 'epdm') ? ' active' : ''}" data-platform-cust-color="target" data-hex="${c.hex}" data-name="${this._escAttr(c.name)}" data-source="epdm" data-texture-url="${c.textureUrl || ''}" title="${this._escAttr(c.name)}" style="${eBgStyle}"></button>`;
      }
      html += `</div>`;
      html += '</div>'; // end .rd-turf-accord-body (colors)
      html += '</div>'; // end colors accordion

      // ══════════════════════════════════════════
      // ── Accordion 2: Logo & Branding ──
      // ══════════════════════════════════════════
      const hasLogo = !!(this._platformLogo && this._platformLogo.dataURL);
      const logoColor = (this._platformLogo && this._platformLogo.color) || '';
      const logoAccordOpen = this._finishingCollapsed['_plat-accord:logo'] === false; // default closed
      const logoBadge = hasLogo ? '1 logo' : 'None';
      html += '<div class="rd-turf-accord rd-plat-accord' + (logoAccordOpen ? ' rd-turf-accord--open' : '') + '" data-plat-accord="logo">';
      html += '<div class="rd-turf-accord-header">';
      html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Logo & Branding</span></div>';
      html += '<span class="rd-turf-accord-badge">' + logoBadge + '</span>';
      html += '</div>';
      html += '<div class="rd-turf-accord-body">';

      // Logo upload + color + preview (wrapped in rd-turf-logo-section for tooltip positioning)
      html += '<div class="rd-turf-logo-section">';
      html += '<div class="rd-turf-toggle-row">';
      html += '<span class="rd-turf-toggle-label">Logo ';
      html += '<span class="rd-roll-cutting-help" tabindex="0">';
      html += '<span class="rd-roll-cutting-qmark">?</span>';
      html += '<span class="rd-roll-cutting-tooltip">Recommended file types: .PNG, .SVG, or .WebP.</span>';
      html += '</span>';
      html += '</span>';
      html += '<button class="rd-platform-cust-logo-upload-btn rd-turf-logo-upload-btn">Upload</button>';
      html += '</div>';
      html += '<div class="rd-turf-toggle-row rd-platform-logo-color-row" style="display:' + (hasLogo ? '' : 'none') + '">';
      html += '<span class="rd-turf-toggle-label">Logo Color ';
      html += '<span class="rd-roll-cutting-help" tabindex="0">';
      html += '<span class="rd-roll-cutting-qmark">?</span>';
      html += '<span class="rd-roll-cutting-tooltip rd-turf-logo-color-tip">For single-color logos, use this tool to change their color.</span>';
      html += '</span>';
      html += '</span>';
      html += '<select class="rd-turf-select rd-platform-logo-color-select">';
      html += '<option value=""' + (logoColor === '' ? ' selected' : '') + '>Original</option>';
      html += '<option value="#FFFFFF"' + (logoColor === '#FFFFFF' ? ' selected' : '') + '>White</option>';
      html += '<option value="#000000"' + (logoColor === '#000000' ? ' selected' : '') + '>Black</option>';
      if (stockColors.length > 0) {
        html += '<optgroup label="Stock Colors">';
        for (const c of stockColors) {
          html += '<option value="' + c.hex + '"' + (logoColor === c.hex ? ' selected' : '') + '>' + this._escHtml(c.name) + '</option>';
        }
        html += '</optgroup>';
      }
      html += '<optgroup label="Custom Colors">';
      for (const c of epdmColors) {
        html += '<option value="' + c.hex + '"' + (logoColor === c.hex ? ' selected' : '') + '>' + this._escHtml(c.name) + '</option>';
      }
      html += '</optgroup>';
      html += '</select>';
      html += '</div>';
      html += '<div class="rd-turf-logo-preview rd-platform-logo-preview" style="display:' + (hasLogo ? '' : 'none') + '">';
      if (hasLogo) {
        html += '<div class="rd-turf-logo-thumb">';
        html += '<img src="' + this._platformLogo.dataURL + '" />';
        html += '<button class="rd-turf-logo-remove-btn rd-platform-cust-logo-remove" title="Remove logo">&times;</button>';
        html += '</div>';
      }
      html += '</div>';
      html += '<input type="file" class="rd-platform-cust-logo-input" accept="image/png,image/svg+xml,image/webp,image/jpeg" style="display:none">';
      html += '<div class="rd-turf-logo-hint"></div>';
      html += '</div>'; // close .rd-turf-logo-section

      html += '</div>'; // end .rd-turf-accord-body (logo)
      html += '</div>'; // end logo accordion

      html += `</div></div>`; // close platforms category
    }

    // ═══════════ HARDWOOD PLATFORMS CATEGORY (Legend 3194 / 3195 / 3223 on the plan) ═══════════
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-09-24) — the logo on a hardwood platform is a clear
       gloss DECAL on the wood, not a rubber inlay, so it gets its own category with only the logo:
       no centre / wing colours (the wood is the wood), and no EPDM tints — Original, White or Black. */
    if (hasHardwood) {
      const hwCatCollapsed = this._finishingCollapsed['_cat:hardwood'] !== false;
      const hwHasLogo = !!(this._hardwoodLogo && this._hardwoodLogo.dataURL);
      const hwColor = (this._hardwoodLogo && this._hardwoodLogo.color) || '';
      const hwAccordOpen = this._finishingCollapsed['_hw-accord:logo'] !== true; // default OPEN — it is the only thing here
      html += `<div class="rd-product-category rd-customize-category${hwCatCollapsed ? ' collapsed' : ''}" data-customize-cat="hardwood">`;
      html += `<div class="rd-product-category-title">`;
      html += catChevron;
      html += `<span class="rd-product-category-name">Hardwood Platforms</span>`;
      html += `<span class="rd-product-category-count">${hardwoodCount}</span>`;
      html += `</div>`;
      html += `<div class="rd-product-category-body">`;
      html += '<div class="rd-turf-accord rd-hw-accord' + (hwAccordOpen ? ' rd-turf-accord--open' : '') + '" data-hw-accord="logo">';
      html += '<div class="rd-turf-accord-header">';
      html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Logo & Branding</span></div>';
      html += '<span class="rd-turf-accord-badge">' + (hwHasLogo ? '1 logo' : 'None') + '</span>';
      html += '</div>';
      html += '<div class="rd-turf-accord-body">';
      html += '<div class="rd-turf-logo-section">';
      html += '<div class="rd-turf-toggle-row">';
      html += '<span class="rd-turf-toggle-label">Logo ';
      html += '<span class="rd-roll-cutting-help" tabindex="0">';
      html += '<span class="rd-roll-cutting-qmark">?</span>';
      html += '<span class="rd-roll-cutting-tooltip">Printed as a clear gloss decal on the hardwood center. Recommended file types: .PNG, .SVG, or .WebP.</span>';
      html += '</span>';
      html += '</span>';
      html += '<button class="rd-hardwood-logo-upload-btn rd-turf-logo-upload-btn">Upload</button>';
      html += '</div>';
      html += '<div class="rd-turf-toggle-row rd-hardwood-logo-color-row" style="display:' + (hwHasLogo ? '' : 'none') + '">';
      html += '<span class="rd-turf-toggle-label">Logo Color ';
      html += '<span class="rd-roll-cutting-help" tabindex="0">';
      html += '<span class="rd-roll-cutting-qmark">?</span>';
      html += '<span class="rd-roll-cutting-tooltip rd-turf-logo-color-tip">For single-color logos, use this tool to change their color.</span>';
      html += '</span>';
      html += '</span>';
      html += '<select class="rd-turf-select rd-hardwood-logo-color-select">';
      html += '<option value=""' + (hwColor === '' ? ' selected' : '') + '>Original</option>';
      html += '<option value="#FFFFFF"' + (hwColor === '#FFFFFF' ? ' selected' : '') + '>White</option>';
      html += '<option value="#000000"' + (hwColor === '#000000' ? ' selected' : '') + '>Black</option>';
      html += '</select>';
      html += '</div>';
      html += '<div class="rd-turf-logo-preview rd-hardwood-logo-preview" style="display:' + (hwHasLogo ? '' : 'none') + '">';
      if (hwHasLogo) {
        html += '<div class="rd-turf-logo-thumb">';
        html += '<img src="' + this._hardwoodLogo.dataURL + '" />';
        html += '<button class="rd-turf-logo-remove-btn rd-hardwood-logo-remove" title="Remove logo">&times;</button>';
        html += '</div>';
      }
      html += '</div>';
      html += '<input type="file" class="rd-hardwood-logo-input" accept="image/png,image/svg+xml,image/webp,image/jpeg" style="display:none">';
      html += '<div class="rd-turf-logo-hint">' + (hwHasLogo ? 'Shown on every hardwood platform on the plan.' : '') + '</div>';
      html += '</div>'; // close .rd-turf-logo-section
      html += '</div>'; // end .rd-turf-accord-body
      html += '</div>'; // end accordion
      html += `</div></div>`; // close hardwood category
    }

    body.innerHTML = html;

    // ── Init accordion max-heights for customize panel ──
    this._accordionInit(body, '.rd-customize-category', '.rd-product-category-body');
    this._accordionInit(body, '.rd-finishing-vendor-collapsible', '.rd-finishing-vendor-body');
    this._accordionInit(body, '.rd-finishing-category', '.rd-finishing-category-body');

    // ── Category toggle handlers ──
    body.querySelectorAll('.rd-customize-category > .rd-product-category-title').forEach(title => {
      title.addEventListener('click', () => {
        const catEl = title.closest('.rd-customize-category');
        this._accordionToggle(catEl);
        const catKey = '_cat:' + catEl.dataset.customizeCat;
        const isNowExpanded = !catEl.classList.contains('collapsed');
        this._finishingCollapsed[catKey] = !isNowExpanded;
        // Manual toggle removes auto-expanded tracking
        if (this._autoExpandedMenus) this._autoExpandedMenus.delete(catKey);
        // When expanding equipment category with a single vendor, auto-expand that vendor too
        if (isNowExpanded && catEl.dataset.customizeCat === 'equipment') {
          const vendorEls = catEl.querySelectorAll('.rd-finishing-vendor-collapsible');
          if (vendorEls.length === 1 && vendorEls[0].classList.contains('collapsed')) {
            this._accordionToggle(vendorEls[0]);
            const vendorKey = '_vendor:' + vendorEls[0].dataset.vendorKey;
            this._finishingCollapsed[vendorKey] = false;
          }
        }
      });
    });

    // ── Vendor collapsible toggle handlers ──
    body.querySelectorAll('.rd-finishing-vendor-header').forEach(header => {
      header.addEventListener('click', () => {
        const vendorEl = header.closest('.rd-finishing-vendor-collapsible');
        this._accordionToggle(vendorEl);
        const vendorKey = '_vendor:' + vendorEl.dataset.vendorKey;
        this._finishingCollapsed[vendorKey] = vendorEl.classList.contains('collapsed');
        // Manual toggle removes auto-expanded tracking
        if (this._autoExpandedMenus) this._autoExpandedMenus.delete(vendorKey);
      });
    });

    // ── Equipment swatch click handlers ──
    body.querySelectorAll('.rd-finishing-swatch').forEach(sw => {
      sw.addEventListener('click', (e) => {
        e.stopPropagation();
        // User interacted — pin parent menus open
        if (this._autoExpandedMenus) {
          this._autoExpandedMenus.delete('_cat:equipment');
          this._autoExpandedMenus.delete('_vendor:' + sw.dataset.vendor);
        }
        const vendor = sw.dataset.vendor;
        const type = sw.dataset.type;
        const color = sw.dataset.color || null;
        const patternKey = sw.dataset.pattern || null;
        if (!rd._brandColors[vendor]) rd._brandColors[vendor] = {};
        if (color) {
          rd._brandColors[vendor][type] = color;
          if (type === 'powderCoat') {
            rd._brandColors[vendor].pcMatte = sw.dataset.matte === 'true';
            rd._brandColors[vendor].pcSparkle = sw.dataset.sparkle === 'true';
            if (patternKey) {
              rd._brandColors[vendor].pcPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[vendor].pcPattern;
            }
          }
          if (type === 'upholstery') {
            if (patternKey) {
              rd._brandColors[vendor].uphPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[vendor].uphPattern;
            }
          }
        } else {
          delete rd._brandColors[vendor][type];
          if (type === 'powderCoat') { delete rd._brandColors[vendor].pcMatte; delete rd._brandColors[vendor].pcPattern; delete rd._brandColors[vendor].pcSparkle; }
          if (type === 'upholstery') delete rd._brandColors[vendor].uphPattern;
          if (!rd._brandColors[vendor].powderCoat && !rd._brandColors[vendor].upholstery) {
            delete rd._brandColors[vendor];
          }
        }
        rd._invalidateBrandColorCaches(vendor);
        self._buildFinishingPanelBody(body);
        rd.updateMeasurements(); // refresh layout panel color names
        if (rd._viewOnly) self._updateViewOnlyReplyBtn();
        if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') {
          rd._storage._triggerAutoSave();
        } else if (typeof rd._triggerAutoSave === 'function') {
          rd._triggerAutoSave();
        }
      });
    });

    // ── Wire turf switcher ──
    if (hasTurf && allTurfObjs.length > 1) {
      const switcherNav = (delta) => {
        const max = allTurfObjs.length - 1;
        const cur = this._turfSwitcherIndex || 0;
        const next = Math.max(0, Math.min(max, cur + delta));
        if (next === cur) return;
        this._turfSwitcherIndex = next;
        // Load turfDefaults from the newly-visible turf object
        const nextObj = allTurfObjs[next];
        if (nextObj) this._loadTurfDefaultsFromObj(nextObj);
        self._buildFinishingPanelBody(body);
      };
      const prevBtn = body.querySelector('.rd-turf-switcher-prev');
      const nextBtn = body.querySelector('.rd-turf-switcher-next');
      if (prevBtn) prevBtn.addEventListener('click', () => switcherNav(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => switcherNav(1));
    }

    // ── Wire turf controls ──
    if (hasTurf) {
      this._wireTurfControls(body);
      this._lastTurfSelFp = null;
      this._syncTurfPanelToSelection(body);
      // Pin turf category open on any user interaction inside it
      const turfBody = body.querySelector('.rd-customize-category[data-customize-cat="turf"] .rd-product-category-body');
      if (turfBody && this._autoExpandedMenus) {
        const pinTurf = () => { this._autoExpandedMenus.delete('_cat:turf'); };
        turfBody.addEventListener('click', pinTurf);
        turfBody.addEventListener('change', pinTurf);
      }
    }

    // ── Wire platform customize controls ──
    if (hasPlatforms) {
      // Platform preset card clicks (Solid Fill / Two Tone)
      body.querySelectorAll('.rd-platform-preset-card').forEach(card => {
        card.addEventListener('click', () => {
          if (card.classList.contains('rd-turf-preset-card--disabled')) return;
          self._initPlatformDefaults();
          const goingWings = card.dataset.wingMode === 'wings';
          self._platformDefaults.wings = goingWings;
          // Always reset color target to center when switching presets
          self._platformColorTarget = 'center';
          if (goingWings) {
            const pd = self._platformDefaults;
            pd.wingColor = pd.centerColor;
            pd.wingColorName = pd.centerColorName;
            pd.wingColorSource = pd.centerColorSource;
            pd.wingTextureUrl = pd.centerTextureUrl;
          }
          self._applyPlatformDefaultsToAll();
          // Auto-open colors accordion on preset click
          self._finishingCollapsed['_plat-accord:colors'] = false;
          self._skipAutoExpand = true;
          self._buildFinishingPanelBody(body);
          self._skipAutoExpand = false;
        });
      });

      // Platform accordion header click handlers
      body.querySelectorAll('.rd-plat-accord > .rd-turf-accord-header').forEach(header => {
        header.addEventListener('click', () => {
          const accord = header.closest('.rd-plat-accord');
          if (!accord) return;
          const isOpen = accord.classList.contains('rd-turf-accord--open');
          accord.classList.toggle('rd-turf-accord--open', !isOpen);
          const key = '_plat-accord:' + accord.dataset.platAccord;
          self._finishingCollapsed[key] = isOpen; // store collapsed = was open
        });
      });

      // Color target toggle (Center / Sides)
      body.querySelectorAll('.rd-platform-target-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          self._platformColorTarget = btn.dataset.colorTarget;
          self._skipAutoExpand = true;
          self._buildFinishingPanelBody(body);
          self._skipAutoExpand = false;
        });
      });

      // Color swatches — applies to targeted zones based on toggle
      body.querySelectorAll('[data-platform-cust-color]').forEach(sw => {
        sw.addEventListener('click', (e) => {
          e.stopPropagation();
          const hex = sw.dataset.hex;
          const name = sw.dataset.name;
          const source = sw.dataset.source;
          const textureUrl = sw.dataset.textureUrl || null;
          self._initPlatformDefaults();
          const pd = self._platformDefaults;
          const target = self._platformColorTarget || 'center';
          if (target === 'center') {
            pd.centerColor = hex;
            pd.centerColorName = name;
            pd.centerColorSource = source;
            pd.centerTextureUrl = textureUrl;
          }
          if (pd.wings && target === 'wings') {
            pd.wingColor = hex;
            pd.wingColorName = name;
            pd.wingColorSource = source;
            pd.wingTextureUrl = textureUrl;
          }
          pd.colorConfirmed = true;
          self._applyPlatformDefaultsToAll();
          self._skipAutoExpand = true;
          self._buildFinishingPanelBody(body);
          self._skipAutoExpand = false;
        });
      });

      // Logo upload
      body.querySelectorAll('.rd-platform-cust-logo-upload-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const input = body.querySelector('.rd-platform-cust-logo-input');
          if (input) input.click();
        });
      });
      body.querySelectorAll('.rd-platform-cust-logo-input').forEach(input => {
        input.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataURL = ev.target.result;
            const img = new Image();
            img.onload = () => {
              const aspect = img.naturalWidth / img.naturalHeight;
              let lw = Math.min(3, 4);
              let lh = lw / aspect;
              if (lh > 3) { lh = 3; lw = lh * aspect; }
              self._platformLogo = {
                dataURL,
                image: img,
                width: lw,
                height: lh,
                x: 0,
                y: 0,
                rotation: 0,
                color: null,
                tightBounds: self._computeLogoTightBounds(img)
              };
              rd.render();
              self._skipAutoExpand = true;
              self._buildFinishingPanelBody(body);
              self._skipAutoExpand = false;
              if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
            };
            img.src = dataURL;
          };
          reader.readAsDataURL(file);
        });
      });

      // Logo remove
      body.querySelectorAll('.rd-platform-cust-logo-remove').forEach(btn => {
        btn.addEventListener('click', () => {
          self._platformLogo = null;
          rd.render();
          self._skipAutoExpand = true;
          self._buildFinishingPanelBody(body);
          self._skipAutoExpand = false;
          if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
        });
      });

      // Logo color tint
      body.querySelectorAll('.rd-platform-logo-color-select').forEach(sel => {
        sel.addEventListener('change', () => {
          if (self._platformLogo) {
            self._platformLogo.color = sel.value || null;
            rd.render();
            if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
          }
        });
      });
    }

    // ── Hardwood platform logo (CUSTOM_ROOMDESIGNER_BY_CLAUDE, Ryan 2026-09-24) ──
    if (hasHardwood) {
      body.querySelectorAll('.rd-hw-accord > .rd-turf-accord-header').forEach(header => {
        header.addEventListener('click', () => {
          const accord = header.closest('.rd-hw-accord');
          if (!accord) return;
          const isOpen = accord.classList.contains('rd-turf-accord--open');
          accord.classList.toggle('rd-turf-accord--open', !isOpen);
          self._finishingCollapsed['_hw-accord:' + accord.dataset.hwAccord] = isOpen;
        });
      });
      const _hwRebuild = () => {
        rd.render();
        self._skipAutoExpand = true;
        self._buildFinishingPanelBody(body);
        self._skipAutoExpand = false;
        if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
      };
      body.querySelectorAll('.rd-hardwood-logo-upload-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const input = body.querySelector('.rd-hardwood-logo-input');
          if (input) input.click();
        });
      });
      body.querySelectorAll('.rd-hardwood-logo-input').forEach(input => {
        input.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataURL = ev.target.result;
            const img = new Image();
            img.onload = () => {
              // width / height carry the image's ASPECT; the renderer sizes the decal to each platform's wood band.
              const aspect = img.naturalWidth / img.naturalHeight || 1;
              self._hardwoodLogo = { dataURL, image: img, width: 3, height: 3 / aspect, color: null, tightBounds: self._computeLogoTightBounds(img) };
              _hwRebuild();
            };
            img.src = dataURL;
          };
          reader.readAsDataURL(file);
        });
      });
      body.querySelectorAll('.rd-hardwood-logo-remove').forEach(btn => {
        btn.addEventListener('click', () => { self._hardwoodLogo = null; _hwRebuild(); });
      });
      body.querySelectorAll('.rd-hardwood-logo-color-select').forEach(sel => {
        sel.addEventListener('change', () => {
          if (!self._hardwoodLogo) return;
          self._hardwoodLogo.color = sel.value || null;
          rd.render();
          if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
        });
      });
    }
  }

  /** True for an equipment object that is one of the Legend hardwood lifting platforms. */
  _isHardwoodPlatformObj(obj) {
    if (!obj || obj.type !== 'equipment') return false;
    const info = this.rd._equipmentImages && this.rd._equipmentImages[obj.equipmentId];
    return !!(info && window.RoomDesignerRender && RoomDesignerRender.HARDWOOD_PLATFORM_SKUS.has(String(info.sku)));
  }

  /** Find the name of the currently-active swatch in a list */
  _findSwatchName(swatches, current, type) {
    if (type === 'powderCoat' && current.powderCoat) {
      const match = swatches.find(sw => sw.hex === current.powderCoat && !!sw.matte === !!current.pcMatte && ((sw.pattern ? sw.swatch : null) || null) === (current.pcPattern || null));
      return match ? match.name : null;
    }
    if (type === 'upholstery' && current.upholstery) {
      const match = swatches.find(sw => sw.hex === current.upholstery && ((sw.pattern && sw.swatch ? sw.swatch : null) || null) === (current.uphPattern || null));
      return match ? match.name : null;
    }
    return null;
  }

  /** Build turf markings + logo HTML for the Customize panel */
  _buildTurfControlsHtml(product, turfObj) {
    const td = this._turfDefaults;
    const anyOn = td.lines || td.hashes || td.borderLine || td.sledLanes;
    let html = '';

    // Determine turf run length to decide which toggles to show
    const _tObj = turfObj || (product && this.rd.objects ? this.rd.objects.find(o => o.type === 'turf' && o.turfProductId === product.id) : null);
    let _runFt = 0;
    if (_tObj) {
      const rotated = _tObj.turfMarkingsRotated || false;
      _runFt = rotated ? (_tObj.width || 0) : (_tObj.depth || 0);
    }
    const _showYardNums = _runFt >= 60;

    // Build color list (used by line color + logo color dropdowns)
    const lineColors = [
      { hex: '#FFFFFF', name: 'White' },
      { hex: '#000000', name: 'Black' }
    ];
    const seenHex = new Set(lineColors.map(c => c.hex.toUpperCase()));
    if (product && product.colors) {
      for (const c of product.colors) {
        const upper = (c.hex || '').toUpperCase();
        if (upper && !seenHex.has(upper)) {
          seenHex.add(upper);
          lineColors.push({ hex: c.hex, name: c.name });
        }
      }
    }

    html += '<div class="rd-turf-markings">';

    // ── Quick Presets Row (first) ──
    // Only show active preset highlight after user has explicitly clicked a preset
    const activePreset = this._turfPresetClicked ? this._detectActivePreset() : null;
    html += '<div class="rd-turf-preset-row">';
    // Plain preset
    html += '<div class="rd-turf-preset-card' + (activePreset === 'plain' ? ' rd-turf-preset-card--active' : '') + '" data-preset="plain">';
    html += '<svg class="rd-turf-preset-icon" viewBox="0 0 32 20" fill="none"><rect x="1" y="1" width="30" height="18" rx="2" fill="#4a8c54" stroke="#357040" stroke-width="1"/></svg>';
    html += '<span class="rd-turf-preset-label">Plain</span>';
    html += '</div>';
    // Football preset
    html += '<div class="rd-turf-preset-card' + (activePreset === 'football' ? ' rd-turf-preset-card--active' : '') + '" data-preset="football">';
    html += '<svg class="rd-turf-preset-icon" viewBox="0 0 32 20" fill="none"><rect x="1" y="1" width="30" height="18" rx="2" fill="#4a8c54" stroke="#357040" stroke-width="1"/>';
    html += '<line x1="16" y1="1" x2="16" y2="19" stroke="#fff" stroke-width="0.8"/>';
    html += '<line x1="6" y1="1" x2="6" y2="19" stroke="#fff" stroke-width="0.5" stroke-dasharray="1.5,1.5"/>';
    html += '<line x1="11" y1="1" x2="11" y2="19" stroke="#fff" stroke-width="0.5" stroke-dasharray="1.5,1.5"/>';
    html += '<line x1="21" y1="1" x2="21" y2="19" stroke="#fff" stroke-width="0.5" stroke-dasharray="1.5,1.5"/>';
    html += '<line x1="26" y1="1" x2="26" y2="19" stroke="#fff" stroke-width="0.5" stroke-dasharray="1.5,1.5"/>';
    html += '<rect x="1" y="1" width="30" height="18" rx="2" fill="none" stroke="#fff" stroke-width="0.6"/>';
    html += '</svg>';
    html += '<span class="rd-turf-preset-label">Football</span>';
    html += '</div>';
    // Training preset
    html += '<div class="rd-turf-preset-card' + (activePreset === 'training' ? ' rd-turf-preset-card--active' : '') + '" data-preset="training">';
    html += '<svg class="rd-turf-preset-icon" viewBox="0 0 32 20" fill="none"><rect x="1" y="1" width="30" height="18" rx="2" fill="#4a8c54" stroke="#357040" stroke-width="1"/>';
    html += '<rect x="25" y="1" width="6" height="18" rx="0" fill="rgba(255,255,255,0.15)"/>';
    html += '<line x1="25" y1="1" x2="25" y2="19" stroke="#fff" stroke-width="0.6"/>';
    html += '<line x1="28" y1="1" x2="28" y2="19" stroke="#fff" stroke-width="0.4" stroke-dasharray="1,2"/>';
    html += '<rect x="1" y="1" width="30" height="18" rx="2" fill="none" stroke="#fff" stroke-width="0.6"/>';
    html += '</svg>';
    html += '<span class="rd-turf-preset-label">Training</span>';
    html += '</div>';
    html += '</div>'; // end preset row

    // ── Turf Color dropdown ──
    if (product && product.colors && product.colors.length > 0) {
      html += '<div class="rd-turf-toggle-row">';
      html += '<span class="rd-turf-toggle-label">Turf Color</span>';
      html += '<select class="rd-turf-select rd-turf-color-select-product" data-product-id="' + this._escAttr(product.id) + '">';
      for (const color of product.colors) {
        html += '<option value="' + this._escAttr(color.id) + '" data-hex="' + this._escAttr(color.hex || '#3a7d44') + '">' + this._escHtml(color.name) + '</option>';
      }
      html += '</select>';
      html += '</div>';
    }

    // ── Logo Color Row (view-only, shown only when turf has logos in presentation mode) ──
    html += '<div class="rd-turf-toggle-row rd-turf-viewonly-logo-color">';
    html += '<span class="rd-turf-toggle-label">Logo Color</span>';
    html += '<select class="rd-turf-select rd-turf-logo-color-select">';
    html += '<option value="">Original</option>';
    for (const lc of lineColors) {
      html += '<option value="' + lc.hex + '"' + (td.logoColor === lc.hex ? ' selected' : '') + '>' + this._escHtml(lc.name) + '</option>';
    }
    html += '</select>';
    html += '</div>';

    // ── Line Color Row (view-only duplicate, shown only in presentation mode) ──
    html += '<div class="rd-turf-toggle-row rd-turf-viewonly-line-color">';
    html += '<span class="rd-turf-toggle-label">Line Color</span>';
    html += '<select class="rd-turf-select rd-turf-color-select">';
    for (const lc of lineColors) {
      html += '<option value="' + lc.hex + '"' + (td.lineColor === lc.hex ? ' selected' : '') + '>' + this._escHtml(lc.name) + '</option>';
    }
    html += '</select>';
    html += '</div>';

    // ── Line Color Row (normal mode, inside global row) ──
    html += '<div class="rd-turf-global-row"' + (anyOn ? '' : ' style="display:none"') + '>';
    html += '<div class="rd-turf-global-item rd-turf-line-color">';
    html += '<span class="rd-turf-toggle-label">Line Color</span>';
    html += '<select class="rd-turf-select rd-turf-color-select">';
    for (const lc of lineColors) {
      html += '<option value="' + lc.hex + '"' + (td.lineColor === lc.hex ? ' selected' : '') + '>' + this._escHtml(lc.name) + '</option>';
    }
    html += '</select>';
    html += '</div>';
    html += '<div class="rd-turf-global-item rd-turf-line-direction">';
    html += '<span class="rd-turf-toggle-label">Line Direction</span>';
    html += '<select class="rd-turf-select rd-turf-line-direction-select">';
    html += '<option value="lengthwise"' + (td.lineDirection === 'lengthwise' ? ' selected' : '') + '>Lengthwise</option>';
    html += '<option value="widthwise"' + (td.lineDirection === 'widthwise' ? ' selected' : '') + '>Widthwise</option>';
    html += '</select>';
    html += '</div>';
    html += '</div>';

    // ══════════════════════════════════════════
    // ── Accordion 1: Field Markings ──
    // ══════════════════════════════════════════
    html += '<div class="rd-turf-accord" data-accord="field">';
    html += '<div class="rd-turf-accord-header">';
    html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Field Markings</span></div>';
    html += '<span class="rd-turf-accord-badge">Off</span>';
    html += '</div>';
    html += '<div class="rd-turf-accord-body">';

    // Field Lines toggle
    html += '<div class="rd-turf-toggle-row">';
    html += '<span class="rd-turf-toggle-label">Field Lines</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-lines-cb"' + (td.lines ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-lineorigin"' + (td.lines ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label">Line Origin</span>';
    html += '<select class="rd-turf-select rd-turf-line-origin-select">';
    html += '<option value="center"' + (td.lineOrigin === 'center' ? ' selected' : '') + '>Center Line</option>';
    html += '<option value="offset"' + (td.lineOrigin === 'offset' ? ' selected' : '') + '>Center Gap</option>';
    html += '<option value="edge"' + (td.lineOrigin === 'edge' ? ' selected' : '') + '>Edge-to-Edge</option>';
    html += '</select>';
    html += '</div>';

    if (_showYardNums) {
      const _ynDisabled = td.lineOrigin === 'offset';
      html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-yardnums' + (_ynDisabled ? ' rd-turf-disabled' : '') + '"' + (td.lines ? '' : ' style="display:none"') + '>';
      html += '<span class="rd-turf-toggle-label">Yard Numbers</span>';
      html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-yardnums-cb"' + (td.yardNumbers ? ' checked' : '') + (_ynDisabled ? ' disabled' : '') + '><span class="rd-toggle-slider"></span></label>';
      html += '</div>';

      const _ynOptsVis = td.lines && td.yardNumbers && !_ynDisabled;
      html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-yardnums-opts rd-turf-yardnums-spacing"' + (_ynOptsVis ? '' : ' style="display:none"') + '>';
      html += '<span class="rd-turf-toggle-label">Spacing</span>';
      html += '<select class="rd-turf-select rd-turf-yardnum-spacing-select">';
      html += '<option value="10"' + (td.yardNumberSpacing !== 5 ? ' selected' : '') + '>10 yd</option>';
      html += '<option value="5"' + (td.yardNumberSpacing === 5 ? ' selected' : '') + '>5 yd</option>';
      html += '</select>';
      html += '</div>';

      html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-yardnums-opts rd-turf-yardnums-style"' + (_ynOptsVis && td.lineOrigin !== 'edge' ? '' : ' style="display:none"') + '>';
      html += '<span class="rd-turf-toggle-label">Style</span>';
      html += '<select class="rd-turf-select rd-turf-yardnum-mode-select">';
      html += '<option value="football"' + (td.yardNumberMode !== 'sequential' ? ' selected' : '') + '>Football</option>';
      html += '<option value="sequential"' + (td.yardNumberMode === 'sequential' ? ' selected' : '') + '>Sequential</option>';
      html += '</select>';
      html += '</div>';
    }

    // Hash Marks toggle
    html += '<div class="rd-turf-toggle-row" style="margin-top:4px">';
    html += '<span class="rd-turf-toggle-label">Hash Marks</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-hashes-cb"' + (td.hashes ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-hash-spacing"' + (td.hashes ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label">Spacing</span>';
    html += '<select class="rd-turf-select rd-turf-hash-spacing-select">';
    html += '<option value="3"' + (td.hashSpacing === 1 ? '' : ' selected') + '>1 yd</option>';
    html += '<option value="1"' + (td.hashSpacing === 1 ? ' selected' : '') + '>1 ft</option>';
    html += '</select>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-center-hashes"' + (td.hashes ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label">Center Hashes</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-center-hashes-cb"' + (td.centerHashes ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    // Border Line toggle
    html += '<div class="rd-turf-toggle-row" style="margin-top:4px">';
    html += '<span class="rd-turf-toggle-label">Border Line</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-borderline-cb"' + (td.borderLine ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-borderline-inset"' + (td.borderLine ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label">Width</span>';
    html += '<select class="rd-turf-select rd-turf-borderline-inset-select">';
    const blInsets = [4, 12, 24, 36, 48, 60, 72];
    const blLabels = ['4"', '1\'', '2\'', '3\'', '4\'', '5\'', '6\''];
    for (let bi = 0; bi < blInsets.length; bi++) {
      html += '<option value="' + blInsets[bi] + '"' + (td.borderLineInset === blInsets[bi] ? ' selected' : '') + '>' + blLabels[bi] + '</option>';
    }
    html += '</select>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-borderline-edges"' + (td.borderLine ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label" style="margin-right:auto">Edges</span>';
    var _blEdges = td.borderLineEdges || ['top', 'right', 'bottom', 'left'];
    html += '<div class="rd-borderline-edge-diagram" style="display:grid;grid-template-columns:18px 74px 18px;grid-template-rows:20px 20px;gap:0;align-items:stretch;justify-items:stretch">';
    html += '<button class="rd-borderline-edge-btn" data-edge="left" style="grid-column:1;grid-row:1/3;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:3px 0 0 3px;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_blEdges.includes('left') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2039;</button>';
    html += '<button class="rd-borderline-edge-btn" data-edge="top" style="grid-column:2;grid-row:1;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-bottom:none;border-radius:0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_blEdges.includes('top') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2227;</button>';
    html += '<button class="rd-borderline-edge-btn" data-edge="right" style="grid-column:3;grid-row:1/3;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:0 3px 3px 0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_blEdges.includes('right') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x203A;</button>';
    html += '<button class="rd-borderline-edge-btn" data-edge="bottom" style="grid-column:2;grid-row:2;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_blEdges.includes('bottom') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2228;</button>';
    html += '</div>';
    html += '</div>';

    html += '</div>'; // end .rd-turf-accord-body
    html += '</div>'; // end accordion 1

    // ══════════════════════════════════════════
    // ── Accordion 2: Training Features ──
    // ══════════════════════════════════════════
    html += '<div class="rd-turf-accord" data-accord="training">';
    html += '<div class="rd-turf-accord-header">';
    html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Training Features</span></div>';
    html += '<span class="rd-turf-accord-badge">Off</span>';
    html += '</div>';
    html += '<div class="rd-turf-accord-body">';

    // Sled/Sprint Lanes
    html += '<div class="rd-turf-toggle-row">';
    html += '<span class="rd-turf-toggle-label">Sled/Sprint Lanes</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-sledlanes-cb"' + (td.sledLanes ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    html += '<div class="rd-turf-sledlanes-children"' + (td.sledLanes ? '' : ' style="display:none"') + '>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent">';
    html += '<span class="rd-turf-toggle-label">Lanes</span>';
    html += '<select class="rd-turf-select rd-turf-sledlane-count-select">';
    for (let lc = 1; lc <= 4; lc++) {
      html += '<option value="' + lc + '"' + (td.sledLaneCount === lc ? ' selected' : '') + '>' + lc + '</option>';
    }
    html += '</select>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent" style="justify-content:center">';
    html += '<span class="rd-turf-toggle-label" style="margin-right:auto">Edges</span>';
    var _sledEdges = td.sledLaneEdges || ['right'];
    html += '<div class="rd-sledlane-edge-diagram" style="display:grid;grid-template-columns:18px 74px 18px;grid-template-rows:20px 20px;gap:0;align-items:stretch;justify-items:stretch">';
    html += '<button class="rd-sledlane-edge-btn" data-edge="left" style="grid-column:1;grid-row:1/3;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:3px 0 0 3px;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_sledEdges.includes('left') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2039;</button>';
    html += '<button class="rd-sledlane-edge-btn" data-edge="top" style="grid-column:2;grid-row:1;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-bottom:none;border-radius:0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_sledEdges.includes('top') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2227;</button>';
    html += '<button class="rd-sledlane-edge-btn" data-edge="right" style="grid-column:3;grid-row:1/3;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:0 3px 3px 0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_sledEdges.includes('right') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x203A;</button>';
    html += '<button class="rd-sledlane-edge-btn" data-edge="bottom" style="grid-column:2;grid-row:2;display:flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:0;cursor:pointer;font-size:11px;font-weight:bold;padding:0;background:' + (_sledEdges.includes('bottom') ? '#fff;color:#006BCC;border-color:#006BCC' : '#fff;color:#ccc') + '">&#x2228;</button>';
    html += '</div>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent">';
    html += '<span class="rd-turf-toggle-label">Yard Markers</span>';
    html += '<select class="rd-turf-select rd-turf-sledlane-interval-select">';
    html += '<option value="0"' + (td.sledLaneInterval === 0 ? ' selected' : '') + '>None</option>';
    html += '<option value="5"' + (td.sledLaneInterval === 5 ? ' selected' : '') + '>5 yd</option>';
    html += '<option value="10"' + (td.sledLaneInterval === 10 ? ' selected' : '') + '>10 yd</option>';
    html += '</select>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent">';
    html += '<span class="rd-turf-toggle-label">Start/Finish Lines</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-sledlane-sf-cb"' + (td.sledLaneStartFinish ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent rd-turf-sledlane-nums-row"' + (td.sledLaneInterval > 0 ? '' : ' style="display:none"') + '>';
    html += '<span class="rd-turf-toggle-label">Yard Numbers</span>';
    html += '<label class="rd-toggle-switch"><input type="checkbox" class="rd-turf-sledlane-nums-cb"' + (td.sledLaneNumbers ? ' checked' : '') + '><span class="rd-toggle-slider"></span></label>';
    html += '</div>';

    var _sledEdgeArr = td.sledLaneEdges || ['right'];

    html += '</div>'; // end .rd-turf-sledlanes-children

    // Agility Ladders
    html += '<div class="rd-turf-toggle-row rd-turf-ladder-header" style="margin-top:8px">';
    html += '<span class="rd-turf-toggle-label" style="font-weight:600">Agility Ladders</span>';
    html += '</div>';

    html += '<div class="rd-turf-toggle-row rd-turf-child-indent">';
    html += '<span class="rd-turf-toggle-label">Length</span>';
    html += '<select class="rd-turf-select rd-turf-agilityladder-length-select">';
    var _alLens = [18, 29];
    for (var _ali = 0; _ali < _alLens.length; _ali++) {
      html += '<option value="' + _alLens[_ali] + '"' + (td.agilityLadderLength === _alLens[_ali] ? ' selected' : '') + '>' + _alLens[_ali] + ' ft</option>';
    }
    html += '</select>';
    html += '</div>';


    html += '<div class="rd-turf-toggle-row rd-turf-child-indent" style="justify-content:flex-end">';
    html += '<button class="rd-turf-add-ladder-btn rd-turf-action-btn">+ Add Ladder</button>';
    html += '</div>';

    // Dot Drills
    html += '<div class="rd-turf-toggle-row rd-turf-dotdrill-header" style="margin-top:8px">';
    html += '<span class="rd-turf-toggle-label" style="font-weight:600">Dot Drills</span>';
    html += '</div>';


    html += '<div class="rd-turf-toggle-row rd-turf-child-indent" style="justify-content:flex-end">';
    html += '<button class="rd-turf-add-dotdrill-btn rd-turf-action-btn">+ Add Dot Drill</button>';
    html += '</div>';

    html += '</div>'; // end .rd-turf-accord-body
    html += '</div>'; // end accordion 2

    // ══════════════════════════════════════════
    // ── Accordion 3: Logo & Branding ──
    // ══════════════════════════════════════════
    html += '<div class="rd-turf-accord" data-accord="logo">';
    html += '<div class="rd-turf-accord-header">';
    html += '<div class="rd-turf-accord-header-left"><svg class="rd-turf-accord-chevron" width="12" height="12" viewBox="0 0 12 12"><path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Logo & Branding</span></div>';
    html += '<span class="rd-turf-accord-badge">None</span>';
    html += '</div>';
    html += '<div class="rd-turf-accord-body">';

    html += '<div class="rd-turf-logo-section">';
    html += '<div class="rd-turf-toggle-row">';
    html += '<span class="rd-turf-toggle-label">Logo ';
    html += '<span class="rd-roll-cutting-help" tabindex="0">';
    html += '<span class="rd-roll-cutting-qmark">?</span>';
    html += '<span class="rd-roll-cutting-tooltip">Recommended file types: .PNG, .SVG, or .WebP.</span>';
    html += '</span>';
    html += '</span>';
    html += '<button class="rd-turf-logo-upload-btn">Upload</button>';
    html += '</div>';
    html += '<div class="rd-turf-toggle-row rd-turf-logo-color" style="display:none">';
    html += '<span class="rd-turf-toggle-label">Logo Color ';
    html += '<span class="rd-roll-cutting-help" tabindex="0">';
    html += '<span class="rd-roll-cutting-qmark">?</span>';
    html += '<span class="rd-roll-cutting-tooltip rd-turf-logo-color-tip">For single-color logos, use this tool to change their color.</span>';
    html += '</span>';
    html += '</span>';
    html += '<select class="rd-turf-select rd-turf-logo-color-select">';
    html += '<option value="">Original</option>';
    for (const lc of lineColors) {
      html += '<option value="' + lc.hex + '"' + (td.logoColor === lc.hex ? ' selected' : '') + '>' + this._escHtml(lc.name) + '</option>';
    }
    html += '</select>';
    html += '</div>';
    html += '<div class="rd-turf-logo-preview" style="display:none"></div>';
    html += '<input type="file" class="rd-turf-logo-file" accept=".png,.webp,.jpg,.jpeg,.svg" style="display:none">';
    html += '<div class="rd-turf-logo-hint"></div>';
    html += '</div>'; // close .rd-turf-logo-section

    html += '</div>'; // end .rd-turf-accord-body
    html += '</div>'; // end accordion 3

    html += '</div>'; // close .rd-turf-markings

    return html;
  }

  /** Wire turf control event handlers within a given container */
  _wireTurfControls(container) {
    const self = this;
    const rd = this.rd;

    // ── Preset card click handlers ──
    container.querySelectorAll('.rd-turf-preset-card').forEach(card => {
      card.addEventListener('click', () => {
        const preset = card.dataset.preset;
        self._turfPresetClicked = true;
        self._applyTurfPreset(preset);
        const td = self._turfDefaults;
        // Sync all UI controls to match the new defaults
        container.querySelectorAll('.rd-turf-lines-cb').forEach(cb => { cb.checked = td.lines; });
        container.querySelectorAll('.rd-turf-hashes-cb').forEach(cb => { cb.checked = td.hashes; });
        container.querySelectorAll('.rd-turf-center-hashes-cb').forEach(cb => { cb.checked = td.centerHashes; });
        container.querySelectorAll('.rd-turf-borderline-cb').forEach(cb => { cb.checked = td.borderLine; });
        container.querySelectorAll('.rd-turf-sledlanes-cb').forEach(cb => { cb.checked = td.sledLanes; });
        container.querySelectorAll('.rd-turf-sledlane-sf-cb').forEach(cb => { cb.checked = td.sledLaneStartFinish; });
        container.querySelectorAll('.rd-turf-sledlane-nums-cb').forEach(cb => { cb.checked = td.sledLaneNumbers; });
        container.querySelectorAll('.rd-turf-yardnums-cb').forEach(cb => { cb.checked = td.yardNumbers; });
        container.querySelectorAll('.rd-turf-line-origin-select').forEach(s => { s.value = td.lineOrigin; });
        container.querySelectorAll('.rd-turf-hash-spacing-select').forEach(s => { s.value = String(td.hashSpacing); });
        container.querySelectorAll('.rd-turf-yardnum-spacing-select').forEach(s => { s.value = String(td.yardNumberSpacing); });
        container.querySelectorAll('.rd-turf-yardnum-mode-select').forEach(s => { s.value = td.yardNumberMode; });
        container.querySelectorAll('.rd-turf-borderline-inset-select').forEach(s => { s.value = String(td.borderLineInset); });
        container.querySelectorAll('.rd-turf-sledlane-count-select').forEach(s => { s.value = String(td.sledLaneCount); });
        container.querySelectorAll('.rd-turf-sledlane-interval-select').forEach(s => { s.value = String(td.sledLaneInterval); });
        container.querySelectorAll('.rd-turf-color-select').forEach(s => { s.value = td.lineColor; });
        container.querySelectorAll('.rd-turf-line-direction-select').forEach(s => { s.value = td.lineDirection; });
        self._syncBorderLineEdgeButtons(container);
        self._syncSledLaneEdgeButtons(container);
        self._updateSledLaneWidthInfo(container);
        container.querySelectorAll('.rd-turf-markings').forEach(m => {
          self._turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body') || container);
        });
        self._turfApplyToSelected();
        // Remove any auto-placed ladders/dot drills when switching presets
        {
          const selTurf = self._getEffectiveTurfTargets();
          for (const obj of (selTurf || [])) {
            if (obj.turfLogos) {
              obj.turfLogos = obj.turfLogos.filter(l => l.type !== 'ladder' && l.type !== 'dotdrill');
              self._syncTurfLogoPanelPreview(container, obj);
            }
          }
        }
        self._updateAccordionBadges(container);
        rd.render();
      });
    });

    // ── Accordion header click handlers (turf accords only, not platform) ──
    container.querySelectorAll('.rd-turf-accord[data-accord] > .rd-turf-accord-header').forEach(header => {
      header.addEventListener('click', () => {
        const accord = header.closest('.rd-turf-accord');
        if (!accord) return;
        const isOpen = accord.classList.contains('rd-turf-accord--open');
        // On mobile: single-accordion mode (close others first)
        if (rd._isMobile) {
          container.querySelectorAll('.rd-turf-accord').forEach(a => a.classList.remove('rd-turf-accord--open'));
        }
        accord.classList.toggle('rd-turf-accord--open', !isOpen);
      });
    });

    // ── Turf color dropdown handler ──
    container.querySelectorAll('.rd-turf-color-select-product').forEach(sel => {
      sel.addEventListener('change', () => {
        const pid = sel.dataset.productId;
        const cid = sel.value;
        const selectedOpt = sel.options[sel.selectedIndex];
        const hex = (selectedOpt && selectedOpt.dataset.hex) || '#3a7d44';
        const selTurf = this._getEffectiveTurfTargets();
        if (selTurf.length > 0) {
          rd.saveUndoState();
          for (const obj of selTurf) {
            obj.turfProductId = pid;
            obj.turfColorId = cid;
            obj.turfColorHex = hex;
          }
          // Also update floor panel active swatch if open
          this._updatePanelSelection();
          rd.render();
          rd.updateMeasurements();
          if (rd._viewOnly) this._updateViewOnlyReplyBtn();
          if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') {
            rd._storage._triggerAutoSave();
          } else if (typeof rd._triggerAutoSave === 'function') {
            rd._triggerAutoSave();
          }
        }
      });
    });

    const _turfUpdateSubVis = (panelRoot) => {
      if (!panelRoot) return;
      const td = this._turfDefaults;
      const anyOn = td.lines || td.hashes || td.borderLine || td.sledLanes;
      const lineOriginEl = panelRoot.querySelector('.rd-turf-lineorigin');
      if (lineOriginEl) lineOriginEl.style.display = td.lines ? '' : 'none';
      const _ynOff = td.lineOrigin === 'offset';
      const yardNumsEl = panelRoot.querySelector('.rd-turf-yardnums');
      if (yardNumsEl) {
        yardNumsEl.style.display = td.lines ? '' : 'none';
        yardNumsEl.classList.toggle('rd-turf-disabled', _ynOff);
        const ynCb = yardNumsEl.querySelector('.rd-turf-yardnums-cb');
        if (ynCb) ynCb.disabled = _ynOff;
      }
      const _ynOptsVis = td.lines && td.yardNumbers && !_ynOff;
      panelRoot.querySelectorAll('.rd-turf-yardnums-spacing').forEach(el => { el.style.display = _ynOptsVis ? '' : 'none'; });
      panelRoot.querySelectorAll('.rd-turf-yardnums-style').forEach(el => { el.style.display = (_ynOptsVis && td.lineOrigin !== 'edge') ? '' : 'none'; });
      const hashSpacingEl = panelRoot.querySelector('.rd-turf-hash-spacing');
      if (hashSpacingEl) hashSpacingEl.style.display = td.hashes ? '' : 'none';
      const centerHashEl = panelRoot.querySelector('.rd-turf-center-hashes');
      if (centerHashEl) centerHashEl.style.display = td.hashes ? '' : 'none';
      if (!td.hashes) {
        td.centerHashes = false;
        const chCb = panelRoot.querySelector('.rd-turf-center-hashes-cb');
        if (chCb) chCb.checked = false;
      }
      const blInsetEl = panelRoot.querySelector('.rd-turf-borderline-inset');
      if (blInsetEl) blInsetEl.style.display = td.borderLine ? '' : 'none';
      const blEdgesEl = panelRoot.querySelector('.rd-turf-borderline-edges');
      if (blEdgesEl) blEdgesEl.style.display = td.borderLine ? '' : 'none';
      const sledEl = panelRoot.querySelector('.rd-turf-sledlanes-children');
      if (sledEl) sledEl.style.display = td.sledLanes ? '' : 'none';
      // Global row (Direction + Line Color)
      panelRoot.querySelectorAll('.rd-turf-global-row').forEach(el => { el.style.display = anyOn ? '' : 'none'; });
    };
    // Expose for preset handler
    this._turfUpdateSubVis = _turfUpdateSubVis;

    const _applyTurfMarkingsToSelected = () => {
      const selTurf = this._getEffectiveTurfTargets();
      if (selTurf && selTurf.length > 0) {
        rd.saveUndoState();
        const td = this._turfDefaults;
        // Snapshot old field centers before applying changes (for logo anchoring)
        const _oldCenters = new Map();
        for (const obj of selTurf) {
          if (obj.turfLogos && obj.turfLogos.length > 0) {
            _oldCenters.set(obj, rd._getTurfFieldCenter(obj));
          }
        }
        for (const obj of selTurf) {
          obj.turfLines = td.lines;
          obj.turfHashes = td.hashes;
          obj.turfHashSides = td.hashSides;
          obj.turfHashSpacing = td.hashSpacing;
          obj.turfCenterHashes = td.centerHashes;
          obj.turfMarkingsRotated = td.rotated;
          obj.turfLineDirection = td.lineDirection;
          obj.turfLineColor = td.lineColor;
          obj.turfHalfwayLine = td.lineOrigin === 'center';
          obj.turfLineOrigin = td.lineOrigin;
          obj.turfYardNumbers = td.yardNumbers;
          obj.turfYardNumberSpacing = td.yardNumberSpacing;
          obj.turfYardNumberMode = td.yardNumberMode;
          obj.turfBorderLine = td.borderLine;
          obj.turfBorderLineInset = td.borderLineInset;
          obj.turfBorderLineEdges = td.borderLineEdges.slice();
          obj.turfSledLanes = td.sledLanes;
          obj.turfSledLaneCount = td.sledLaneCount;
          obj.turfSledLaneEdges = td.sledLaneEdges.slice();
          obj.turfSledLaneInterval = td.sledLaneInterval;
          obj.turfSledLaneStartFinish = td.sledLaneStartFinish;
          obj.turfSledLaneNumbers = td.sledLaneNumbers;
          obj.turfAgilityLadderLength = td.agilityLadderLength;
          // Update existing ladder entries' width when length changes
          if (obj.turfLogos) {
            for (const _ll of obj.turfLogos) {
              if (_ll.type === 'ladder') _ll.width = td.agilityLadderLength;
            }
          }
          if (obj.turfLogos && obj.turfLogos.length > 0) {
            const selSet = rd._turfLogoSelectedIdxs;
            if (selSet && selSet.size > 0) {
              for (const si of selSet) { if (obj.turfLogos[si]) obj.turfLogos[si].color = td.logoColor || null; }
            } else {
              for (const logo of obj.turfLogos) logo.color = td.logoColor || null;
            }
            // Reposition center-anchored logos to the new field center.
            // Also auto-anchor logos that were sitting at the OLD center (migration for pre-flag logos).
            const oldFc = _oldCenters.get(obj);
            const newFc = rd._getTurfFieldCenter(obj);
            const tol = 0.05;
            for (const logo of obj.turfLogos) {
              if (logo.anchoredToCenter) {
                logo.x = newFc.x; logo.y = newFc.y;
              } else if (oldFc && Math.abs((logo.x || 0) - oldFc.x) <= tol && Math.abs((logo.y || 0) - oldFc.y) <= tol) {
                logo.anchoredToCenter = true;
                logo.x = newFc.x; logo.y = newFc.y;
              }
            }
          }
        }
        // Suppress fingerprint-triggered sync — we just wrote these values from defaults
        this._suppressFpSync = true;
        rd.render();
        // Flag cleared in _doRender hook after fingerprint check
        if (rd._viewOnly) this._updateViewOnlyReplyBtn();
      }
      // Update preset highlights and accordion badges after any marking change
      self._turfPresetClicked = true; // user interacted — show preset active states
      self._updatePresetHighlights(container);
      self._updateAccordionBadges(container);
    };
    // Expose for preset handler
    this._turfApplyToSelected = () => _applyTurfMarkingsToSelected();

    // Line Direction dropdown handler
    container.querySelectorAll('.rd-turf-line-direction-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.lineDirection = sel.value;
        this._turfComputeRotatedFromDirection();
        container.querySelectorAll('.rd-turf-line-direction-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    // Turf field lines toggle
    container.querySelectorAll('.rd-turf-lines-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.lines = cb.checked;
        container.querySelectorAll('.rd-turf-lines-cb').forEach(o => { o.checked = cb.checked; });
        container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-hashes-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.hashes = cb.checked;
        container.querySelectorAll('.rd-turf-hashes-cb').forEach(o => { o.checked = cb.checked; });
        container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-center-hashes-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.centerHashes = cb.checked;
        container.querySelectorAll('.rd-turf-center-hashes-cb').forEach(o => { o.checked = cb.checked; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-hash-spacing-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.hashSpacing = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-hash-spacing-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-line-origin-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.lineOrigin = sel.value;
        container.querySelectorAll('.rd-turf-line-origin-select').forEach(o => { o.value = sel.value; });
        const _ynOff = sel.value === 'offset';
        container.querySelectorAll('.rd-turf-yardnums').forEach(el => {
          el.classList.toggle('rd-turf-disabled', _ynOff);
          const ynCb = el.querySelector('.rd-turf-yardnums-cb');
          if (ynCb) ynCb.disabled = _ynOff;
        });
        const _ynOptsVis = !_ynOff && this._turfDefaults.yardNumbers;
        container.querySelectorAll('.rd-turf-yardnums-spacing').forEach(el => { el.style.display = _ynOptsVis ? '' : 'none'; });
        container.querySelectorAll('.rd-turf-yardnums-style').forEach(el => { el.style.display = (_ynOptsVis && sel.value !== 'edge') ? '' : 'none'; });
        // Edge mode forces sequential yard numbers
        if (sel.value === 'edge') {
          this._turfDefaults.yardNumberMode = 'sequential';
          container.querySelectorAll('.rd-turf-yardnum-mode-select').forEach(s => { s.value = 'sequential'; });
        }
        container.querySelectorAll('.rd-turf-lineorigin').forEach(el => { el.classList.toggle('rd-turf-child-mid', this._turfDefaults.lines); });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-yardnums-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.yardNumbers = cb.checked;
        container.querySelectorAll('.rd-turf-yardnums-cb').forEach(o => { o.checked = cb.checked; });
        container.querySelectorAll('.rd-turf-yardnums-opts').forEach(el => { el.style.display = cb.checked ? '' : 'none'; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-yardnum-spacing-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.yardNumberSpacing = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-yardnum-spacing-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-yardnum-mode-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.yardNumberMode = sel.value;
        container.querySelectorAll('.rd-turf-yardnum-mode-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-borderline-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.borderLine = cb.checked;
        if (cb.checked && this._turfDefaults.borderLineEdges.length === 0) {
          this._turfDefaults.borderLineEdges = ['top', 'right', 'bottom', 'left'];
          this._syncBorderLineEdgeButtons(container);
        }
        container.querySelectorAll('.rd-turf-borderline-cb').forEach(o => { o.checked = cb.checked; });
        container.querySelectorAll('.rd-turf-borderline-inset').forEach(el => { el.style.display = cb.checked ? '' : 'none'; });
        container.querySelectorAll('.rd-turf-borderline-edges').forEach(el => { el.style.display = cb.checked ? '' : 'none'; });
        container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-borderline-inset-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.borderLineInset = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-borderline-inset-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-borderline-edge-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const edge = btn.dataset.edge;
        const edges = this._turfDefaults.borderLineEdges;
        const idx = edges.indexOf(edge);
        if (idx >= 0) {
          edges.splice(idx, 1);
          if (edges.length === 0) {
            this._turfDefaults.borderLine = false;
            container.querySelectorAll('.rd-turf-borderline-cb').forEach(o => { o.checked = false; });
            container.querySelectorAll('.rd-turf-borderline-inset').forEach(el => { el.style.display = 'none'; });
            container.querySelectorAll('.rd-turf-borderline-edges').forEach(el => { el.style.display = 'none'; });
            container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
          }
        } else {
          edges.push(edge);
        }
        this._syncBorderLineEdgeButtons(container);
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    // Sled/Sprint Lanes event handlers
    container.querySelectorAll('.rd-turf-sledlanes-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.sledLanes = cb.checked;
        if (cb.checked && this._turfDefaults.sledLaneEdges.length === 0) {
          this._turfDefaults.sledLaneEdges = ['right'];
          this._syncSledLaneEdgeButtons(container);
          this._updateSledLaneWidthInfo(container);
        }
        if (cb.checked && rd._viewOnly) {
          this._turfDefaults.sledLaneInterval = 0;
          this._turfDefaults.sledLaneStartFinish = false;
        }
        container.querySelectorAll('.rd-turf-sledlanes-cb').forEach(o => { o.checked = cb.checked; });
        container.querySelectorAll('.rd-turf-sledlanes-children').forEach(el => { el.style.display = cb.checked ? '' : 'none'; });
        container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-sledlane-count-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.sledLaneCount = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-sledlane-count-select').forEach(o => { o.value = sel.value; });
        this._updateSledLaneWidthInfo(container);
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-sledlane-edge-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const edge = btn.dataset.edge;
        const edges = this._turfDefaults.sledLaneEdges;
        const idx = edges.indexOf(edge);
        if (idx >= 0) {
          // Toggle off — disable sled lanes entirely
          edges.splice(idx, 1);
          this._turfDefaults.sledLanes = false;
          container.querySelectorAll('.rd-turf-sledlanes-cb').forEach(o => { o.checked = false; });
          container.querySelectorAll('.rd-turf-sledlanes-children').forEach(el => { el.style.display = 'none'; });
          container.querySelectorAll('.rd-turf-markings').forEach(m => _turfUpdateSubVis(m.closest('.rd-finishing-vendor') || m.closest('.rd-product-card-body')));
        } else {
          // Single-select: replace all with just this edge
          edges.length = 0;
          edges.push(edge);
        }
        this._syncSledLaneEdgeButtons(container);
        this._updateSledLaneWidthInfo(container);
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-sledlane-interval-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.sledLaneInterval = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-sledlane-interval-select').forEach(o => { o.value = sel.value; });
        container.querySelectorAll('.rd-turf-sledlane-nums-row').forEach(el => { el.style.display = parseInt(sel.value, 10) > 0 ? '' : 'none'; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-sledlane-sf-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.sledLaneStartFinish = cb.checked;
        container.querySelectorAll('.rd-turf-sledlane-sf-cb').forEach(o => { o.checked = cb.checked; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-sledlane-nums-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        this._turfDefaults.sledLaneNumbers = cb.checked;
        container.querySelectorAll('.rd-turf-sledlane-nums-cb').forEach(o => { o.checked = cb.checked; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    // Agility Ladders event handlers
    container.querySelectorAll('.rd-turf-agilityladder-length-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.agilityLadderLength = parseInt(sel.value, 10);
        container.querySelectorAll('.rd-turf-agilityladder-length-select').forEach(o => { o.value = sel.value; });
        this._updateAgilityLadderInfo(container);
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-add-ladder-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const selTurf = this._getEffectiveTurfTargets();
        if (!selTurf || selTurf.length === 0) return;
        const lengthFt = this._turfDefaults.agilityLadderLength || 18;
        const turfObj = selTurf[0]; // Ghost placement targets first turf
        // Enter ghost placement mode instead of immediately creating
        rd._pendingLadder = { turfObj, lengthFt, heightFt: 20 / 12 };
        rd._pendingLadderPos = null;
        rd._pendingLadderGhostPos = null;
        rd._pendingLadderRotation = 0;
        // Select the turf so it renders
        if (!rd.selectedObjects.has(turfObj)) {
          rd.selectedWalls = new Set(); rd.selectedDoors = new Set(); rd.selectedObjects = new Set();
          rd.selectedObjects.add(turfObj);
        }
        rd.canvas.style.cursor = 'crosshair';
        rd.updateStatus('Click on the turf to place ladder.');
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-add-dotdrill-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const selTurf = this._getEffectiveTurfTargets();
        if (!selTurf || selTurf.length === 0) return;
        const turfObj = selTurf[0];
        // Enter ghost placement mode (reuses pending ladder infrastructure with dotdrill type)
        rd._pendingLadder = { turfObj, lengthFt: 3, heightFt: 2, itemType: 'dotdrill' };
        rd._pendingLadderPos = null;
        rd._pendingLadderGhostPos = null;
        rd._pendingLadderRotation = 0;
        if (!rd.selectedObjects.has(turfObj)) {
          rd.selectedWalls = new Set(); rd.selectedDoors = new Set(); rd.selectedObjects = new Set();
          rd.selectedObjects.add(turfObj);
        }
        rd.canvas.style.cursor = 'crosshair';
        rd.updateStatus('Click on the turf to place dot drill.');
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-color-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.lineColor = sel.value;
        container.querySelectorAll('.rd-turf-color-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-logo-color-select').forEach(sel => {
      sel.addEventListener('change', () => {
        this._turfDefaults.logoColor = sel.value;
        container.querySelectorAll('.rd-turf-logo-color-select').forEach(o => { o.value = sel.value; });
        _applyTurfMarkingsToSelected();
        rd.render();
      });
    });

    container.querySelectorAll('.rd-turf-logo-upload-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const section = btn.closest('.rd-turf-logo-section');
        if (section) section.querySelector('.rd-turf-logo-file').click();
      });
    });

    container.querySelectorAll('.rd-turf-logo-file').forEach(input => {
      input.addEventListener('change', () => {
        const file = input.files[0];
        if (!file) return;
        const selTurf = this._getEffectiveTurfTargets();
        if (!selTurf || selTurf.length === 0) { input.value = ''; return; }
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataURL = e.target.result;
          const img = new Image();
          img.onload = () => {
            let w = img.naturalWidth, h = img.naturalHeight;
            const MAX_DIM = 4000;
            let finalDataURL = dataURL;
            if (w > MAX_DIM || h > MAX_DIM) {
              const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
              w = Math.round(w * ratio); h = Math.round(h * ratio);
              const canvas = document.createElement('canvas');
              canvas.width = w; canvas.height = h;
              canvas.getContext('2d').drawImage(img, 0, 0, w, h);
              finalDataURL = canvas.toDataURL('image/png', 0.9);
            }
            const loadedImg = new Image();
            loadedImg.onload = () => {
              const newSize = finalDataURL.length;
              let totalSize = newSize;
              for (const o of (rd.objects || [])) {
                if (o.turfLogos) for (const l of o.turfLogos) totalSize += (l.dataURL || '').length;
              }
              const MAX_LOGO_BYTES = 3 * 1024 * 1024;
              if (totalSize > MAX_LOGO_BYTES) {
                rd.updateStatus('\u26A0 Logo storage limit reached \u2014 remove a logo before adding more.');
                return;
              }
              rd.saveUndoState();
              const tightBounds = this._computeLogoTightBounds(loadedImg);
              for (const obj of selTurf) {
                const longestDim = Math.max(obj.width, obj.depth);
                const targetSize = longestDim * 0.25;
                const aspect = w / h;
                const fc = rd._getTurfFieldCenter(obj);
                const logoObj = {
                  dataURL: finalDataURL,
                  image: loadedImg,
                  tightBounds: tightBounds,
                  width: aspect >= 1 ? targetSize : targetSize * aspect,
                  height: aspect >= 1 ? targetSize / aspect : targetSize,
                  x: fc.x, y: fc.y, rotation: 0, color: null,
                  anchoredToCenter: true
                };
                obj.turfLogos = obj.turfLogos || [];
                obj.turfLogos.push(logoObj);
              }
              this._syncTurfLogoPanelPreview(container, selTurf[0]);
              rd.render();
            };
            loadedImg.src = finalDataURL;
          };
          img.src = dataURL;
        };
        reader.readAsDataURL(file);
        input.value = '';
      });
    });

    // Turf logo remove — delegated
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.rd-turf-logo-remove-btn');
      if (!btn) return;
      const logoIdx = btn.dataset.logoIdx;
      const vendorCard = btn.closest('.rd-finishing-vendor');
      const pid = vendorCard && vendorCard.dataset.productId;
      const targets = pid
        ? (rd.objects || []).filter(o => o.type === 'turf' && o.turfProductId === pid && o.turfLogos && o.turfLogos.length > 0)
        : this._getEffectiveTurfTargets().filter(o => o.turfLogos && o.turfLogos.length > 0);
      if (targets.length === 0) return;
      rd.saveUndoState();
      if (logoIdx != null && logoIdx !== '') {
        const idx = parseInt(logoIdx, 10);
        for (const obj of targets) {
          if (obj.turfLogos && idx >= 0 && idx < obj.turfLogos.length) {
            obj.turfLogos.splice(idx, 1);
            if (obj.turfLogos.length === 0) obj.turfLogos = null;
          }
        }
        if (rd._turfLogoFocusedIdx != null) rd._turfLogoFocusedIdx = null;
        rd._turfLogoSelectedIdxs.clear();
      } else {
        for (const obj of targets) { obj.turfLogos = null; }
        rd._turfLogoFocusedIdx = null;
        rd._turfLogoSelectedIdxs.clear();
      }
      this._turfDefaults.logoColor = '';
      this._syncTurfLogoPanelPreview(container, targets[0]);
      rd.render();
    });
  }

  /* ── View-only color panel (click equipment in shared/view-only mode) ── */

  _openViewOnlyColorPanel(equipId) {
    const rd = this.rd;
    const info = rd._equipmentImages && rd._equipmentImages[equipId];
    const config = this._getBrandColorConfig();
    const vendorConf = info && info.vendor ? config[info.vendor] : null;
    if (!info || !info.vendor) return;
    if (!vendorConf) return;

    // Already open with same vendor — just update tracked equipId, no animation
    const alreadyOpen = this._viewOnlyPanelOpen && this._sidePanelOpen && this._viewOnlyPanelVendor === info.vendor;
    // Switching from a different panel type (e.g. turf → equipment)
    const switching = !alreadyOpen && this._viewOnlyPanelOpen && this._sidePanelOpen;

    if (switching) {
      // Slide down, swap content, slide back up
      const panel = document.getElementById('rdSidePanel');
      if (!panel) return;
      this._markPanelAnimating();
      panel.classList.remove('open');
      setTimeout(() => {
        this._viewOnlyPanelEquipId = equipId;
        this._viewOnlyPanelVendor = info.vendor;
        this._viewOnlyPanelTurfObj = null;
        this._viewOnlyPanelTurfProductId = null;
        this._viewOnlyPanelFloor = false;
        this._populateViewOnlyColorPanel(panel, equipId, info, vendorConf);
      }, 350);
      return;
    }

    this._viewOnlyPanelEquipId = equipId;
    this._viewOnlyPanelVendor = info.vendor;
    // Clear turf tracking
    this._viewOnlyPanelTurfObj = null;
    this._viewOnlyPanelTurfProductId = null;
        this._viewOnlyPanelFloor = false;

    if (!alreadyOpen) {
      const panel = document.getElementById('rdSidePanel');
      if (!panel) return;
      this._populateViewOnlyColorPanel(panel, equipId, info, vendorConf);
    } else {
      // Same vendor — just rebuild body
      const panel = document.getElementById('rdSidePanel');
      const body = document.getElementById('rdSidePanelBody');
      if (body) this._buildViewOnlyColorBody(body, equipId, info, vendorConf);
      if (panel && rd._isMobile) {
        panel.style.maxHeight = '';
        body.style.maxHeight = '';
        body.style.overflowY = '';
        requestAnimationFrame(() => { this._viewOnlyColorPanelHeight = panel.offsetHeight; });
      }
    }
  }

  _populateViewOnlyColorPanel(panel, equipId, info, vendorConf) {
    const rd = this.rd;
    if (rd._isMobile) this._setFloatingPillHidden(true);
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    this._viewOnlyPanelOpen = true;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    this._updateViewOnlyToggle(false);

    // Header
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Customize';
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = 'none';
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) {
      if (rd._isMobile) {
        closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';
      } else {
        closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
      }
    }

    // Body
    const body = document.getElementById('rdSidePanelBody');
    if (body) this._buildViewOnlyColorBody(body, equipId, info, vendorConf);
    // Measure color panel height after render so turf panel can match it
    if (rd._isMobile) {
      panel.style.maxHeight = '';
      if (body) { body.style.maxHeight = ''; body.style.overflowY = ''; }
      requestAnimationFrame(() => { this._viewOnlyColorPanelHeight = panel.offsetHeight; });
    }
  }

  _buildViewOnlyColorBody(body, equipId, info, vendorConf) {
    const rd = this.rd;
    const self = this;
    const vendor = info.vendor;
    const current = rd._brandColors[vendor] || {};
    const chevronSvg = '<svg class="rd-finishing-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
    const vendorChevron = '<svg class="rd-product-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>';
    const frameCollapsed = this._finishingCollapsed && this._finishingCollapsed[vendor + ':frame'];
    const uphCollapsed = this._finishingCollapsed && this._finishingCollapsed[vendor + ':uph'];

    // Determine which capabilities placed equipment has for this vendor
    let vendorHasPC = false, vendorHasUph = false;
    if (rd.objects && rd._equipmentImages) {
      for (const obj of rd.objects) {
        if (obj.type !== 'equipment') continue;
        const eqInfo = rd._equipmentImages[obj.equipmentId];
        if (!eqInfo || eqInfo.vendor !== vendor) continue;
        if (eqInfo.hasPowderCoat) vendorHasPC = true;
        if (eqInfo.hasUpholstery) vendorHasUph = true;
        if (vendorHasPC && vendorHasUph) break;
      }
    }
    const pcDisabled = !vendorHasPC;
    const uphDisabled = !vendorHasUph;

    let html = '';

    // Vendor swatch sections (reuse .rd-finishing-* classes)
    const vendorCollapsed = this._finishingCollapsed && this._finishingCollapsed['_vendor:' + vendor];
    html += '<div class="rd-finishing-vendor rd-finishing-vendor-collapsible' + (vendorCollapsed ? ' collapsed' : '') + '" data-vendor-key="' + vendor + '">';
    html += '<div class="rd-finishing-vendor-header">' + vendorChevron;
    html += '<span class="rd-finishing-vendor-name">' + vendor + '</span>';
    html += '</div>';
    html += '<div class="rd-finishing-vendor-body">';

    // Frame / Powder Coat
    if (vendorConf.powderCoat && vendorConf.powderCoat.length > 0) {
      const activeName = this._findSwatchName(vendorConf.powderCoat, current, 'powderCoat');
      html += '<div class="rd-finishing-category' + (frameCollapsed ? ' collapsed' : '') + (pcDisabled ? ' rd-swatch-disabled' : '') + '" data-section="' + vendor + ':frame">';
      html += '<div class="rd-finishing-category-header">' + chevronSvg;
      html += '<span class="rd-finishing-category-name' + (pcDisabled ? ' rd-swatch-disabled' : '') + '">Frame Color' + (pcDisabled ? ' <span class="rd-swatch-disabled-note">(no framed equipment placed)</span>' : '') + '</span>';
      if (activeName) html += '<span class="rd-finishing-active-label">' + activeName + '</span>';
      html += '</div>';
      html += '<div class="rd-finishing-category-body"><div class="rd-finishing-swatches' + (pcDisabled ? ' rd-swatch-disabled' : '') + '">';
      for (const sw of vendorConf.powderCoat) {
        const swPcPattern = sw.pattern ? sw.swatch : null;
        const isActive = sw.hex === null ? !current.powderCoat : current.powderCoat === sw.hex && !!sw.matte === !!current.pcMatte && (swPcPattern || null) === (current.pcPattern || null);
        const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
        const bgStyle = sw.hex === null
          ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
          : swUrl ? 'background:url(' + swUrl + ') center/cover' : 'background:' + sw.hex;
        const matteAttr = sw.matte ? ' data-matte="true"' : '';
        const pcPatAttr = sw.pattern ? ' data-pattern="' + sw.swatch + '"' : '';
        const sparkleAttr = sw.sparkle ? ' data-sparkle="true"' : '';
        html += '<button class="rd-finishing-swatch' + (isActive ? ' active' : '') + '" data-vendor="' + vendor + '" data-type="powderCoat" data-color="' + (sw.hex || '') + '"' + matteAttr + pcPatAttr + sparkleAttr + ' title="' + sw.name + '" style="' + bgStyle + '"></button>';
      }
      html += '</div></div></div>';
    }

    // Upholstery / Pads — always show if vendor has upholstery options (colors apply to all vendor equipment)
    if (vendorConf.upholstery && vendorConf.upholstery.length > 0) {
      const activeName = this._findSwatchName(vendorConf.upholstery, current, 'upholstery');
      html += '<div class="rd-finishing-category' + (uphCollapsed ? ' collapsed' : '') + (uphDisabled ? ' rd-swatch-disabled' : '') + '" data-section="' + vendor + ':uph">';
      html += '<div class="rd-finishing-category-header">' + chevronSvg;
      html += '<span class="rd-finishing-category-name' + (uphDisabled ? ' rd-swatch-disabled' : '') + '">Pad Color' + (uphDisabled ? ' <span class="rd-swatch-disabled-note">(no padded equipment placed)</span>' : '') + '</span>';
      if (activeName) html += '<span class="rd-finishing-active-label">' + activeName + '</span>';
      html += '</div>';
      html += '<div class="rd-finishing-category-body"><div class="rd-finishing-swatches' + (uphDisabled ? ' rd-swatch-disabled' : '') + '">';
      for (const sw of vendorConf.upholstery) {
        const swPattern = (sw.pattern && sw.swatch) ? sw.swatch : null;
        const isActive = sw.hex === null ? !current.upholstery : current.upholstery === sw.hex && (swPattern || null) === (current.uphPattern || null);
        const swUrl = sw.swatch && window.__rdSwatchUrls && window.__rdSwatchUrls[sw.swatch];
        const bgStyle = sw.hex === null
          ? 'background:linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%),linear-gradient(135deg,#ccc 25%,transparent 25%,transparent 75%,#ccc 75%);background-size:6px 6px;background-position:0 0,3px 3px;background-color:#f0f0f0'
          : swUrl ? 'background:url(' + swUrl + ') center/cover' : 'background:' + sw.hex;
        const patternAttr = sw.pattern && sw.swatch ? ' data-pattern="' + sw.swatch + '"' : '';
        html += '<button class="rd-finishing-swatch' + (isActive ? ' active' : '') + '" data-vendor="' + vendor + '" data-type="upholstery" data-color="' + (sw.hex || '') + '"' + patternAttr + ' title="' + sw.name + '" style="' + bgStyle + '"></button>';
      }
      html += '</div></div></div>';
    }

    html += '</div></div>'; // close vendor-body + vendor

    body.innerHTML = html;

    // Init accordion max-heights
    if (!this._finishingCollapsed) this._finishingCollapsed = {};
    this._accordionInit(body, '.rd-finishing-vendor-collapsible', '.rd-finishing-vendor-body');
    this._accordionInit(body, '.rd-finishing-category', '.rd-finishing-category-body');

    // Vendor-level collapse
    body.querySelectorAll('.rd-finishing-vendor-header').forEach(header => {
      header.addEventListener('click', () => {
        const vendorEl = header.closest('.rd-finishing-vendor-collapsible');
        this._accordionToggle(vendorEl);
        const key = '_vendor:' + vendorEl.dataset.vendorKey;
        this._finishingCollapsed[key] = vendorEl.classList.contains('collapsed');
      });
    });

    body.querySelectorAll('.rd-finishing-category-header').forEach(header => {
      header.addEventListener('click', (e) => {
        e.stopPropagation();
        const cat = header.closest('.rd-finishing-category');
        this._accordionToggle(cat);
        const section = cat.dataset.section;
        this._finishingCollapsed[section] = cat.classList.contains('collapsed');
      });
    });

    // Swatch click handlers
    body.querySelectorAll('.rd-finishing-swatch').forEach(sw => {
      sw.addEventListener('click', (e) => {
        e.stopPropagation();
        const swVendor = sw.dataset.vendor;
        const type = sw.dataset.type;
        const color = sw.dataset.color || null;
        const patternKey = sw.dataset.pattern || null;
        if (!rd._brandColors[swVendor]) rd._brandColors[swVendor] = {};
        if (color) {
          rd._brandColors[swVendor][type] = color;
          if (type === 'powderCoat') {
            rd._brandColors[swVendor].pcMatte = sw.dataset.matte === 'true';
            rd._brandColors[swVendor].pcSparkle = sw.dataset.sparkle === 'true';
            if (patternKey) {
              rd._brandColors[swVendor].pcPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[swVendor].pcPattern;
            }
          }
          if (type === 'upholstery') {
            if (patternKey) {
              rd._brandColors[swVendor].uphPattern = patternKey;
              rd._ensurePatternImage(patternKey);
            } else {
              delete rd._brandColors[swVendor].uphPattern;
            }
          }
        } else {
          delete rd._brandColors[swVendor][type];
          if (type === 'powderCoat') { delete rd._brandColors[swVendor].pcMatte; delete rd._brandColors[swVendor].pcPattern; delete rd._brandColors[swVendor].pcSparkle; }
          if (type === 'upholstery') delete rd._brandColors[swVendor].uphPattern;
          if (!rd._brandColors[swVendor].powderCoat && !rd._brandColors[swVendor].upholstery) {
            delete rd._brandColors[swVendor];
          }
        }
        rd._invalidateBrandColorCaches(swVendor);
        // Refresh panel to update active states
        const freshConf = self._getBrandColorConfig()[vendor];
        if (freshConf) self._buildViewOnlyColorBody(body, equipId, info, freshConf);
        rd.updateMeasurements(); // refresh layout panel color names
        if (rd._viewOnly) self._updateViewOnlyReplyBtn();
        // Trigger auto-save
        if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') {
          rd._storage._triggerAutoSave();
        } else if (typeof rd._triggerAutoSave === 'function') {
          rd._triggerAutoSave();
        }
      });
    });

  }

  /* ── View-only turf color panel (click turf in shared/view-only mode) ── */

  /* ══════════════════════════════════════════════════════════════════════════════════════
     CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-09-24) — THE PRESENTATION FLOORING PANEL
     *"when on the Flooring Layout tab, have the left side slide-out pane slide out, and like it does
     when you're on the Equipment Layout tab with Legend Fitness equipment added, make it so you have
     drop-downs for the various rooms/zones (labelled), with swatches (square ones like we've used
     for Legend) with the color swatches shown for the ECORE products so you can click and see
     different color options for the flooring."*
     One collapsible per floored space (its name, or "Space N"), one collapsible per zone inside it
     ("Zone N", the quote's own numbering) — or the space itself when it has one zone — each with
     the product's name, the current colour's name, and the product's colours as the same square
     `.rd-finishing-swatch` grid the equipment panel uses. A click sets that zone's / room's colour
     the way the editor's brush does (_setZoneFlooring / room.flooring), drops the room's layout
     caches, re-renders. Turf gets a section too (colour only; a button opens the full turf panel
     for markings). Tracked as `_viewOnlyPanelFloor`, cleared with the other panel flags. */
  _collectViewOnlyFloorTargets() {
    const rd = this.rd;
    const groups = [];
    let spaceIdx = 0;
    for (const room of (rd.rooms || [])) {
      if (!room || !room.closed || room.type !== 'add') continue;
      spaceIdx++;
      if (!this._roomHasFlooring(room)) continue;
      const geo = rd._getRoomGeoCache(room);
      if (!geo || !geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      const roomLabel = (room.name && !/^Space\s*\d*$/i.test(room.name.trim())) ? room.name.trim() : ('Space ' + spaceIdx);
      const zones = this._getZonesForRoom(room, geo);
      const targets = [];
      if (zones.length > 1) {
        for (let zi = 0; zi < zones.length; zi++) {
          const zone = zones[zi];
          const zf = this._getZoneFlooring(room, zone);
          if (!zf) continue;
          const product = this._getProduct(zf.productId);
          if (!product || !product.colors || product.colors.length === 0) continue;
          targets.push({
            label: (room.zoneNames && room.zoneNames[zi]) || ('Zone ' + (zi + 1)), product, colorId: zf.colorId,
            area: zone.area || Math.abs(RoomDesignerMath.signedPolygonArea(zone.polygon)) || 0,
            apply: (cid) => { this._carryRollShare(product, zf.colorId, cid); this._setZoneFlooring(room, zone, zf.productId, cid); room._flooringLayout = null; room._zoneLayouts = null; }
          });
        }
        // Ryan 2026-09-24: the biggest zone first, then the rest alphabetically
        if (targets.length > 1) {
          let bi = 0;
          for (let i = 1; i < targets.length; i++) if (targets[i].area > targets[bi].area) bi = i;
          const biggest = targets.splice(bi, 1)[0];
          targets.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: 'base' }));
          targets.unshift(biggest);
        }
      } else if (room.flooring) {
        const product = this._getProduct(room.flooring.productId);
        if (product && product.colors && product.colors.length > 0) {
          targets.push({
            label: null, product, colorId: room.flooring.colorId,
            apply: (cid) => { this._carryRollShare(product, room.flooring.colorId, cid); room.flooring = { productId: room.flooring.productId, colorId: cid }; room._flooringLayout = null; room._zoneLayouts = null; }
          });
        }
      }
      if (targets.length) groups.push({ key: 'room' + spaceIdx, label: roomLabel, targets });
    }
    let turfIdx = 0;
    for (const obj of (rd.objects || [])) {
      if (obj.type !== 'turf' || !obj.turfProductId) continue;
      turfIdx++;
      const product = this._getProduct(obj.turfProductId);
      if (!product || !product.colors || product.colors.length === 0) continue;
      groups.push({ key: 'turf' + turfIdx, label: 'Turf ' + turfIdx, turfObj: obj, targets: [{
        label: null, product, colorId: obj.turfColorId,
        apply: (cid) => { const c = product.colors.find(x => x.id === cid); obj.turfColorId = cid; if (c) obj.turfColorHex = c.hex; }
      }] });
    }
    return groups;
  }

  /** Open the panel for the Flooring Layout tab. Returns false when the plan has nothing to colour. */
  _openViewOnlyFloorPanel() {
    const rd = this.rd;
    const groups = this._collectViewOnlyFloorTargets();
    if (!groups.length) return false;
    const alreadyOpen = this._viewOnlyPanelOpen && this._sidePanelOpen && this._viewOnlyPanelFloor;
    const switching = !alreadyOpen && this._viewOnlyPanelOpen && this._sidePanelOpen;
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return false;
    const take = () => {
      this._viewOnlyPanelFloor = true;
      this._viewOnlyPanelVendor = null;
      this._viewOnlyPanelEquipId = null;
      this._viewOnlyPanelTurfObj = null;
      this._viewOnlyPanelTurfProductId = null;
    };
    if (switching) {
      this._markPanelAnimating();
      panel.classList.remove('open');
      setTimeout(() => { take(); this._populateViewOnlyFloorPanel(panel); }, 350);
      return true;
    }
    take();
    if (!alreadyOpen) this._populateViewOnlyFloorPanel(panel);
    else { const body = document.getElementById('rdSidePanelBody'); if (body) this._buildViewOnlyFloorBody(body); }
    return true;
  }

  _populateViewOnlyFloorPanel(panel) {
    const rd = this.rd;
    if (rd._isMobile) this._setFloatingPillHidden(true);
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    this._viewOnlyPanelOpen = true;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    this._updateViewOnlyToggle(false);
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Flooring Colors';
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = 'none';
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) {
      closeBtn.innerHTML = rd._isMobile
        ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>'
        : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
    }
    const body = document.getElementById('rdSidePanelBody');
    if (body) this._buildViewOnlyFloorBody(body);
    if (body && rd._isMobile) {
      const capH = this._viewOnlyColorPanelHeight || this._estimateColorPanelHeight();
      if (capH) { panel.style.maxHeight = capH + 'px'; body.style.overflowY = 'auto'; }
    }
  }

  _buildViewOnlyFloorBody(body) {
    const rd = this.rd;
    const groups = this._collectViewOnlyFloorTargets();
    if (!this._finishingCollapsed) this._finishingCollapsed = {};
    const chevronSvg = '<svg class="rd-finishing-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>';
    const groupChevron = '<svg class="rd-product-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>';
    // The website's swatch image, not the product photo (Ryan 2026-09-24); photo only as the fallback
    const swatchStyle = (c) => { const u = c.swatchImageUrl || c.imageUrl; return u ? 'background:url(' + u + ') center/cover' : 'background:' + (c.hex || '#888'); };
    let html = '';
    const flatSingle = groups.filter(g => !g.turfObj).length === 1;
    groups.forEach((g, gi) => {
      // The first space opens by itself; the rest start closed unless the viewer opened them before.
      const gKey = '_vofloor:' + g.key;
      const gCollapsed = this._finishingCollapsed[gKey] !== undefined ? this._finishingCollapsed[gKey] : gi !== 0;
      // Ryan 2026-09-24: a ONE-space plan gets no space header — its zones sit flush left; the
      // space collapsible (and its indent) only exists when there are 2+ spaces. Turf keeps its own.
      const flat = flatSingle && !g.turfObj;
      if (flat) {
        html += '<div class="rd-vofloor-group rd-vofloor-flat">';
      } else {
        html += '<div class="rd-finishing-vendor rd-finishing-vendor-collapsible rd-vofloor-group' + (gCollapsed ? ' collapsed' : '') + '" data-vendor-key="' + this._escAttr(g.key) + '">';
        html += '<div class="rd-finishing-vendor-header">' + groupChevron;
        html += '<span class="rd-finishing-vendor-name">' + this._escHtml(g.label) + '</span>';
        html += '</div>';
        html += '<div class="rd-finishing-vendor-body">';
      }
      g.targets.forEach((t, ti) => {
        const pName = String(t.product.title || t.product.name || 'Flooring').split(' - ')[0].trim();
        const active = t.product.colors.find(c => c.id === t.colorId);
        const sKey = '_vofloor:' + g.key + ':' + ti;
        const sCollapsed = this._finishingCollapsed[sKey] === true; // zones open by default
        // Ryan 2026-09-24: "ZONE 2 ......... Medium Gray - ES504" on one line, the product under it
        const heading = t.label || pName;
        html += '<div class="rd-finishing-category' + (sCollapsed ? ' collapsed' : '') + '" data-section="' + this._escAttr(sKey) + '">';
        html += '<div class="rd-finishing-category-header rd-vofloor-head">' + chevronSvg;
        html += '<span class="rd-finishing-category-name">' + this._escHtml(heading) + '</span>';
        if (active && active.name) html += '<span class="rd-finishing-active-label">' + this._escHtml(active.name) + '</span>';
        html += '</div>';
        if (t.label) html += '<div class="rd-vofloor-product">' + this._escHtml(pName) + '</div>';
        html += '<div class="rd-finishing-category-body"><div class="rd-finishing-swatches">';
        for (const c of t.product.colors) {
          html += '<button class="rd-finishing-swatch' + (c.id === t.colorId ? ' active' : '') + '" data-vofloor-group="' + gi + '" data-vofloor-target="' + ti + '" data-color-id="' + this._escAttr(c.id) + '" title="' + this._escAttr(c.name || '') + '" style="' + swatchStyle(c) + '"></button>';
        }
        html += '</div>';
        if (g.turfObj) html += '<button class="rd-turf-logo-upload-btn rd-vofloor-turf-more" data-vofloor-group="' + gi + '" style="margin-top:8px">Turf markings &amp; logo</button>';
        html += '</div></div>';
      });
      html += flat ? '</div>' : '</div></div>';
    });
    body.innerHTML = html;
    this._accordionInit(body, '.rd-finishing-vendor-collapsible', '.rd-finishing-vendor-body');
    this._accordionInit(body, '.rd-finishing-category', '.rd-finishing-category-body');
    // Sections here open and close INDEPENDENTLY (Ryan 2026-09-24): _accordionToggle closes the open
    // siblings first, which is right for the equipment vendors and wrong for a list of zones that
    // starts fully open — closing two and reopening them one at a time is how it read as a bug.
    const _toggleFree = (el, bodySel) => {
      const b = el.querySelector(':scope > ' + bodySel);
      if (el.classList.contains('collapsed')) this._accordionOpen(el, b); else this._accordionClose(el, b);
    };
    body.querySelectorAll('.rd-finishing-vendor-header').forEach(header => {
      header.addEventListener('click', () => {
        const el = header.closest('.rd-finishing-vendor-collapsible');
        _toggleFree(el, '.rd-finishing-vendor-body');
        this._finishingCollapsed['_vofloor:' + el.dataset.vendorKey] = el.classList.contains('collapsed');
      });
    });
    body.querySelectorAll('.rd-finishing-category-header').forEach(header => {
      header.addEventListener('click', (e) => {
        e.stopPropagation();
        const cat = header.closest('.rd-finishing-category');
        _toggleFree(cat, '.rd-finishing-category-body');
        this._finishingCollapsed[cat.dataset.section] = cat.classList.contains('collapsed');
      });
    });
    body.querySelectorAll('.rd-finishing-swatch[data-vofloor-target]').forEach(sw => {
      sw.addEventListener('click', (e) => {
        e.stopPropagation();
        const g = groups[parseInt(sw.dataset.vofloorGroup, 10)];
        const t = g && g.targets[parseInt(sw.dataset.vofloorTarget, 10)];
        if (!t) return;
        t.apply(sw.dataset.colorId);
        rd.render();
        rd.updateMeasurements();
        if (rd._viewOnly) this._updateViewOnlyReplyBtn();
        if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
        else if (typeof rd._triggerAutoSave === 'function') rd._triggerAutoSave();
        this._buildViewOnlyFloorBody(body); // refresh active states + colour names
      });
    });
    body.querySelectorAll('.rd-vofloor-turf-more').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const g = groups[parseInt(btn.dataset.vofloorGroup, 10)];
        if (g && g.turfObj) this._openViewOnlyTurfPanel(g.turfObj);
      });
    });
  }

  _openViewOnlyTurfPanel(turfObj) {
    const rd = this.rd;
    if (!turfObj || !turfObj.turfProductId) return;
    const product = this._getProduct(turfObj.turfProductId);
    if (!product || !product.colors || product.colors.length === 0) return;

    // Already open for same product — just update tracked obj
    const alreadyOpen = this._viewOnlyPanelOpen && this._sidePanelOpen && this._viewOnlyPanelTurfProductId === turfObj.turfProductId;
    // Switching from a different panel type (e.g. equipment → turf)
    const switching = !alreadyOpen && this._viewOnlyPanelOpen && this._sidePanelOpen;

    if (switching) {
      const panel = document.getElementById('rdSidePanel');
      if (!panel) return;
      this._markPanelAnimating();
      panel.classList.remove('open');
      setTimeout(() => {
        this._viewOnlyPanelTurfObj = turfObj;
        this._viewOnlyPanelTurfProductId = turfObj.turfProductId;
        this._viewOnlyPanelVendor = null;
        this._viewOnlyPanelEquipId = null;
        this._viewOnlyPanelFloor = false;
        this._populateViewOnlyTurfPanel(panel, turfObj, product);
      }, 350);
      return;
    }

    this._viewOnlyPanelTurfObj = turfObj;
    this._viewOnlyPanelTurfProductId = turfObj.turfProductId;
    // Clear equipment tracking
    this._viewOnlyPanelVendor = null;
    this._viewOnlyPanelEquipId = null;
        this._viewOnlyPanelFloor = false;

    if (!alreadyOpen) {
      const panel = document.getElementById('rdSidePanel');
      if (!panel) return;
      this._populateViewOnlyTurfPanel(panel, turfObj, product);
    } else {
      // Same product — just rebuild body
      const panel = document.getElementById('rdSidePanel');
      const body = document.getElementById('rdSidePanelBody');
      if (body) this._buildViewOnlyTurfBody(body, turfObj, product);
      if (panel && body && rd._isMobile) {
        const capH = this._viewOnlyColorPanelHeight || this._estimateColorPanelHeight();
        if (capH) {
          panel.style.maxHeight = capH + 'px';
          body.style.overflowY = 'auto';
        }
      }
    }
  }

  _populateViewOnlyTurfPanel(panel, turfObj, product) {
    const rd = this.rd;
    if (rd._isMobile) this._setFloatingPillHidden(true);
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    this._viewOnlyPanelOpen = true;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    this._updateViewOnlyToggle(false);

    // Header
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Customize';
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = 'none';
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) {
      if (rd._isMobile) {
        closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';
      } else {
        closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
      }
    }

    // Body
    const body = document.getElementById('rdSidePanelBody');
    if (body) this._buildViewOnlyTurfBody(body, turfObj, product);
    // Cap turf panel to color panel height so it never grows taller
    if (body && rd._isMobile) {
      const capH = this._viewOnlyColorPanelHeight || this._estimateColorPanelHeight();
      if (capH) {
        panel.style.maxHeight = capH + 'px';
        body.style.overflowY = 'auto';
      }
    }
  }

  _panToTurfObject(turfObj) {
    const rd = this.rd;
    if (!turfObj) return;
    // Turf center in world coords
    const cx = turfObj.x;
    const cy = turfObj.y;
    // Convert to screen, then pan so it's centered in visible area
    const ws = rd.worldToScreen(cx, cy);
    // Account for mobile bottom panel
    let availH = rd._cssHeight;
    if (rd._isMobile) {
      const panel = document.getElementById('rdSidePanel');
      if (panel && panel.classList.contains('open')) {
        availH -= (panel.offsetHeight || 0);
      }
    }
    const targetScreenX = rd._cssWidth / 2;
    const targetScreenY = availH / 2;
    rd.panX += targetScreenX - ws.x;
    rd.panY += targetScreenY - ws.y;
    rd.render();
  }

  _estimateColorPanelHeight() {
    // Estimate what the color panel height would be based on swatch counts + screen width
    const config = this._getBrandColorConfig();
    const vendors = Object.keys(config);
    if (vendors.length === 0) return null;
    const vc = config[vendors[0]];
    const pcCount = (vc.powderCoat || []).length;
    const uphCount = (vc.upholstery || []).length;
    if (pcCount === 0 && uphCount === 0) return null;
    // Grid columns from available width (8px body padding each side)
    const bodyWidth = window.innerWidth - 16;
    const cols = Math.max(1, Math.floor((bodyWidth + 3) / 35)); // 32px swatch + 3px gap
    const rowH = 35;
    const pcH = pcCount > 0 ? Math.ceil(pcCount / cols) * rowH - 3 : 0;
    const uphH = uphCount > 0 ? Math.ceil(uphCount / cols) * rowH - 3 : 0;
    const headerRows = (pcCount > 0 ? 22 : 0) + (uphCount > 0 ? 22 : 0);
    const bodyPad = 6 + (pcCount > 0 ? 6 : 0) + (uphCount > 0 ? 6 : 0);
    const panelHeader = 34;
    return panelHeader + headerRows + bodyPad + pcH + uphH + 10;
  }

  _buildViewOnlyTurfBody(body, turfObj, product) {
    const rd = this.rd;
    const self = this;
    const activeColorId = turfObj.turfColorId || '';

    const allTurf = (rd.objects || []).filter(o => o.type === 'turf');
    const turfIdx = allTurf.indexOf(turfObj);

    let html = '';

    html += '<div class="rd-finishing-vendor" data-product-id="' + this._escAttr(product.id) + '">';
    html += '<div class="rd-finishing-vendor-name">' + this._escHtml(product.title) + '</div>';

    // Turf selection dropdown (only when 2+ turfs)
    if (allTurf.length > 1) {
      html += '<div class="rd-turf-toggle-row">';
      html += '<span class="rd-turf-toggle-label">Turf Selection</span>';
      html += '<select class="rd-turf-select rd-viewonly-turf-select">';
      for (let i = 0; i < allTurf.length; i++) {
        const t = allTurf[i];
        const tProduct = this._getProduct(t.turfProductId);
        const name = tProduct ? tProduct.title : ('Turf ' + (i + 1));
        html += '<option value="' + i + '"' + (i === turfIdx ? ' selected' : '') + '>Turf ' + (i + 1) + ' \u2014 ' + this._escHtml(name) + '</option>';
      }
      html += '</select>';
      html += '</div>';
    }

    // Turf controls (includes color swatches at top + markings)
    html += this._buildTurfControlsHtml(product, turfObj);

    html += '</div>';

    body.innerHTML = html;

    // Wire turf selector dropdown
    if (allTurf.length > 1) {
      const sel = body.querySelector('.rd-viewonly-turf-select');
      if (sel) {
        sel.addEventListener('change', () => {
          const idx = parseInt(sel.value, 10);
          const target = allTurf[idx];
          if (!target) return;
          const targetProduct = this._getProduct(target.turfProductId);
          if (!targetProduct) return;
          this._viewOnlyPanelTurfObj = target;
          this._viewOnlyPanelTurfProductId = target.turfProductId;
          this._buildViewOnlyTurfBody(body, target, targetProduct);
          this._panToTurfObject(target);
        });
      }
    }

    // ── Wire all turf controls (color swatches + markings) ──
    this._wireTurfControls(body);
    // Sync panel to turfObj state
    this._turfDefaults.lines = turfObj.turfLines || false;
    this._turfDefaults.hashes = turfObj.turfHashes || false;
    this._turfDefaults.hashSides = turfObj.turfHashSides || 'long';
    this._turfDefaults.hashSpacing = turfObj.turfHashSpacing || 3;
    this._turfDefaults.centerHashes = turfObj.turfCenterHashes || false;
    this._turfDefaults.rotated = turfObj.turfMarkingsRotated || false;
    this._turfDefaults.lineColor = turfObj.turfLineColor || '#FFFFFF';
    this._turfDefaults.lineOrigin = turfObj.turfLineOrigin || (turfObj.turfHalfwayLine === false ? 'offset' : 'center');
    this._turfDefaults.yardNumbers = turfObj.turfYardNumbers || false;
    this._turfDefaults.yardNumberSpacing = turfObj.turfYardNumberSpacing || 10;
    this._turfDefaults.yardNumberMode = turfObj.turfYardNumberMode || 'football';
    this._turfDefaults.borderLine = turfObj.turfBorderLine || false;
    this._turfDefaults.borderLineInset = turfObj.turfBorderLineInset || 4;
    this._turfDefaults.borderLineEdges = (turfObj.turfBorderLineEdges && turfObj.turfBorderLineEdges.length > 0) ? turfObj.turfBorderLineEdges.slice() : ['top', 'right', 'bottom', 'left'];
    this._turfDefaults.logoColor = (turfObj.turfLogos && turfObj.turfLogos.length > 0 && turfObj.turfLogos[0].color) || '';
    this._syncTurfPanelToSelection(body);
  }

  _buildRotatePopoverHTML() {
    return `
      <div class="popover-title-row">
        <span class="floor-rotate-popover-title">Roll Angle</span>
      </div>
      <div class="floor-rotate-zone-nav" style="display:none;">
        <button class="floor-rotate-zone-prev" title="Previous zone">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="floor-rotate-zone-labels">
          <span class="floor-rotate-room-label" style="display:none;"></span>
          <span class="floor-rotate-zone-label">Zone 1 / 1</span>
        </div>
        <button class="floor-rotate-zone-next" title="Next zone">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg>
        </button>
      </div>
      <div class="floor-rotate-dial-wrap">
        <canvas class="floor-rotate-dial-canvas" width="120" height="120"></canvas>
      </div>
      <div class="floor-rotate-presets">
        <button class="floor-rotate-preset-btn active" data-angle="0" title="Vertical">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="4" x2="12" y2="20"/><polyline points="8,8 12,4 16,8"/><polyline points="8,16 12,20 16,16"/></svg>
        </button>
        <button class="floor-rotate-preset-btn" data-angle="90" title="Horizontal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="4" y1="12" x2="20" y2="12"/><polyline points="8,8 4,12 8,16"/><polyline points="16,8 20,12 16,16"/></svg>
        </button>
      </div>
      <button class="floor-rotate-optimize-btn" title="Rotates the floor to the angle with the least material overage in this zone">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/><circle cx="12" cy="12" r="3"/></svg>
        Minimize Waste
      </button>
      <button class="floor-rotate-optimize-btn floor-rotate-optimize-space-btn" disabled title="Rotates all floors to the angle with the least combined overage across the entire space">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="12" x2="21" y2="12"/></svg>
        Minimize Space Waste
      </button>
      <div class="floor-rotate-material-display">&mdash; sq ft</div>
    `;
  }

  _drawRotateDial(canvas, angleDeg) {
    if (!canvas) return;
    const size = canvas.width;
    const ctx = canvas.getContext('2d');
    const cx = size / 2, cy = size / 2;
    const r = size / 2 - 16;
    const isDark = this.rd.darkMode;

    ctx.clearRect(0, 0, size, size);

    // Ring
    const compassColor = isDark ? '#c0c8d8' : '#000F1C';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = compassColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Cardinal tick marks (N/E/S/W)
    const cardinals = [0, 90, 180, 270];
    const labels = ['N', 'E', 'S', 'W'];
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = compassColor;
    for (let i = 0; i < 4; i++) {
      const a = (cardinals[i] - 90) * Math.PI / 180;
      const ix = cx + Math.cos(a) * (r - 6);
      const iy = cy + Math.sin(a) * (r - 6);
      const ox = cx + Math.cos(a) * (r + 1);
      const oy = cy + Math.sin(a) * (r + 1);
      ctx.beginPath();
      ctx.moveTo(ix, iy);
      ctx.lineTo(ox, oy);
      ctx.strokeStyle = compassColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      const lx = cx + Math.cos(a) * (r + 9);
      let ly = cy + Math.sin(a) * (r + 9);
      // Nudge N and S down so they look centered between the two rings
      if (cardinals[i] === 0 || cardinals[i] === 180) ly += 1.5;
      ctx.fillText(labels[i], lx, ly);
    }

    // Diamond/rhombus magnetic needle
    const aRad = (angleDeg - 90) * Math.PI / 180;
    const halfWidth = 4;
    const dirX = Math.cos(aRad), dirY = Math.sin(aRad);
    const perpX = -dirY, perpY = dirX;

    // Diamond vertices
    const tipX = cx + dirX * r,   tipY = cy + dirY * r;       // active tip
    const tailX = cx - dirX * r,  tailY = cy - dirY * r;      // tail tip
    const lx = cx + perpX * halfWidth, ly = cy + perpY * halfWidth;  // left shoulder
    const rx = cx - perpX * halfWidth, ry = cy - perpY * halfWidth;  // right shoulder

    // Active half (dark blue)
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(lx, ly);
    ctx.lineTo(rx, ry);
    ctx.closePath();
    ctx.fillStyle = '#006BCC';
    ctx.fill();

    // Tail half (light blue — theme-aware)
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(lx, ly);
    ctx.lineTo(rx, ry);
    ctx.closePath();
    ctx.fillStyle = isDark ? '#7A8090' : '#D8DBDF';
    ctx.fill();

    // Thin outline around full diamond
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(lx, ly);
    ctx.lineTo(tailX, tailY);
    ctx.lineTo(rx, ry);
    ctx.closePath();
    ctx.strokeStyle = isDark ? 'rgba(200,210,230,0.3)' : 'rgba(0,15,28,0.2)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Center pivot circle
    ctx.beginPath();
    ctx.arc(cx, cy, 3, 0, Math.PI * 2);
    ctx.fillStyle = compassColor;
    ctx.fill();

    // Degree readout in center (outlined for readability over needle)
    const degText = angleDeg + '\u00B0';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = isDark ? '#1a1a2e' : '#ffffff';
    ctx.strokeText(degText, cx, cy);
    ctx.fillStyle = '#006BCC';
    ctx.fillText(degText, cx, cy);
  }

  _setupRotatePopoverEvents(btn, pop) {
    const rd = this.rd;
    const popId = 'floorRotatePopover';
    const popName = 'floorRotate';

    // Hook into the shared popover system on RoomDesigner
    // Desktop: hover-to-show, mouseleave-to-hide, click-to-pin
    if (!rd._isMobile) {
      btn.addEventListener('mouseenter', () => {
        if (btn.disabled) return;
        if (rd._pinnedPopover !== popName) {
          rd._hoverShowPop(popId, () => this._updateRotatePopoverForSelectedRoom());
        }
      });
      btn.addEventListener('mouseleave', () => {
        rd._cancelHoverShow();
        if (rd._pinnedPopover !== popName) { this._resetWallSnap(); rd._hidePop(popId); }
      });
      pop.addEventListener('mouseenter', () => clearTimeout(rd._popoverTimers[popId]));
      pop.addEventListener('mouseleave', () => {
        if (rd._pinnedPopover !== popName) { this._resetWallSnap(); rd._hidePop(popId); }
      });
    }

    // Click: pin/unpin via shared pinToggle
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (btn.disabled) return;
      if (rd._isMobile) {
        // Mobile: toggle pin directly
        if (rd._pinnedPopover === popName) this._resetWallSnap();
        rd._pinToggle(popName, popId);
      } else {
        if (rd._pinnedPopover === popName) {
          this._resetWallSnap();
          rd._pinToggle(popName, popId);
        } else {
          // Close other popovers and deactivate their buttons
          rd._pinToggle(popName, popId);
        }
      }
      btn.classList.toggle('active', rd._pinnedPopover === popName);
      // When compass opens, sync its target to the current floor selection
      // and clear the selection so only one highlight shows at a time
      if (rd._pinnedPopover === popName && this._floorSelections.length > 0) {
        const sel = this._floorSelections[0];
        const targets = this._getFlooredTargets();
        for (let ti = 0; ti < targets.length; ti++) {
          const t = targets[ti];
          if (t.room === sel.room && this._zonesMatch(t.zone, sel.zone)) {
            this._selectedRotateRoomIdx = ti;
            break;
          }
        }
        this._floorSelections = [];
        this._floorInfoPinned = false;
        this._hideFloorInfo();
      }
      this._updateRotatePopoverForSelectedRoom();
    });

    // Preset buttons
    pop.querySelectorAll('.floor-rotate-preset-btn').forEach(p => {
      p.addEventListener('click', () => {
        this._resetWallSnap();
        const room = this._getTargetFlooringRoom();
        if (room) {
          rd.saveUndoState();
          this._clearAllZoneIndependent(room);
        }
        const deg = parseInt(p.dataset.angle, 10);
        this._animateToAngle(deg * Math.PI / 180, 350);
      });
    });

    // Dial drag
    const dialCanvas = pop.querySelector('.floor-rotate-dial-canvas');
    let dragging = false;
    let dragRAF = null;

    const onDragStart = (e) => {
      dragging = true;
      this._resetWallSnap();
      dialCanvas.style.cursor = 'grabbing';
      this._onDialDrag(e, dialCanvas, true);
    };
    const onDragMove = (e) => {
      if (!dragging) return;
      e.preventDefault();
      if (dragRAF) return;
      dragRAF = requestAnimationFrame(() => {
        dragRAF = null;
        this._onDialDrag(e, dialCanvas, false);
      });
    };
    const onDragEnd = () => {
      if (!dragging) return;
      dragging = false;
      dialCanvas.style.cursor = 'grab';
      this._snappedWalls = null;
      this.rd.render();
    };

    dialCanvas.addEventListener('mousedown', onDragStart);
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);
    dialCanvas.addEventListener('touchstart', (e) => { e.preventDefault(); onDragStart(e.touches[0]); }, { passive: false });
    document.addEventListener('touchmove', (e) => { if (dragging) onDragMove(e.touches[0]); }, { passive: false });
    document.addEventListener('touchend', onDragEnd);

    this._snappedWalls = null;

    // Expose toggle for keyboard shortcut
    this._toggleRotatePopover = () => rd._pinToggle(popName, popId);
  }

  _onDialDrag(e, dialCanvas, isStart) {
    const rect = dialCanvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    let angleDeg = ((Math.atan2(dy, dx) * 180 / Math.PI) + 90 + 360) % 360;

    // Build snap points: cardinals + wall-aligned angles
    const snaps = [0, 90, 180, 270];
    const entries = this._getSnapAngles();
    const nonCardinalEntries = [];
    for (const entry of entries) {
      const deg = entry.stripAngle * 180 / Math.PI;
      if (deg < 3 || Math.abs(deg - 90) < 3 || Math.abs(deg - 180) < 3) continue;
      snaps.push(Math.round(deg));
      snaps.push(Math.round(deg + 180) % 360);
      nonCardinalEntries.push(entry);
    }

    // Dead-zone snapping (±5°)
    for (const s of snaps) {
      let diff = angleDeg - s;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      if (Math.abs(diff) <= 5) { angleDeg = s; break; }
    }

    angleDeg = Math.round(angleDeg) % 360;

    // Save undo only on drag start; clear independent zone layout (manual rotation re-links to shared grid)
    if (isStart) {
      const room = this._getTargetFlooringRoom();
      if (room) {
        this.rd.saveUndoState();
        this._clearAllZoneIndependent(room);
      }
    }

    // Highlight walls only when snapped to a wall-aligned angle
    this._matchWallsDuringDrag(angleDeg, nonCardinalEntries);

    this._setFlooringAngle(angleDeg * Math.PI / 180);
  }

  _setFlooringAngle(angleRad) {
    const target = this._getTargetFlooringEntry();
    if (!target) return;

    if (target.isExterior) {
      // Exterior zone rotation
      this._setExteriorZoneAngle(target.zone, angleRad);
    } else if (target.zone) {
      // Per-zone rotation
      this._setZoneAngle(target.room, target.zone, angleRad);
      target.room._zoneLayouts = null;
    } else {
      // Whole-room rotation
      target.room.flooringAngle = angleRad;
      target.room._flooringLayout = null;
    }
    this.rd.render();
    this._updateRotatePopoverForSelectedRoom();
    this._updateMaterialSummary();
    this._debouncedMeasurementsUpdate();
    if (this._rollOptimizationMode === 'uniform') this._repopulateUniformTargetSelects();
  }

  _getFlooredRooms() {
    return this.rd.rooms.filter(r => this._roomHasFlooring(r) && r.closed);
  }

  /** Build a flat list of flooring targets: each is {room, zone?}.
   *  Single-zone rooms → one entry with zone=null.
   *  Multi-zone rooms → one entry per floored zone. */
  _getFlooredTargets() {
    const targets = [];
    for (const room of this.rd.rooms) {
      if (!room.closed || room.type !== 'add' || !this._roomHasFlooring(room)) continue;
      const geo = this.rd._getRoomGeoCache(room);
      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        for (let zi = 0; zi < zones.length; zi++) {
          const z = zones[zi];
          const zf = this._getZoneFlooring(room, z);
          if (zf) targets.push({ room, zone: z, zoneIdx: zi + 1, totalZones: zones.length });
        }
      } else {
        if (room.flooring) targets.push({ room, zone: null });
      }
    }
    // Include exterior zones with flooring
    if (this._exteriorZoneFlooring && this._exteriorZoneFlooring.length > 0) {
      const extZones = this._getExteriorZones();
      for (const z of extZones) {
        const ef = this._getExteriorZoneFlooring(z);
        if (ef) targets.push({ room: null, zone: z, isExterior: true });
      }
    }
    return targets;
  }

  _getTargetFlooringRoom() {
    const entry = this._getTargetFlooringEntry();
    return entry ? entry.room : null;
  }

  _getTargetFlooringEntry() {
    const targets = this._getFlooredTargets();
    if (targets.length === 0) return null;
    const idx = Math.min(this._selectedRotateRoomIdx || 0, targets.length - 1);
    return targets[idx];
  }

  _updateRotatePopoverForSelectedRoom() {
    const targets = this._getFlooredTargets();

    // Clamp index to valid range
    if (targets.length > 0) {
      this._selectedRotateRoomIdx = Math.min(this._selectedRotateRoomIdx || 0, targets.length - 1);
    } else {
      this._selectedRotateRoomIdx = 0;
    }

    const entry = this._getTargetFlooringEntry();
    let angleDeg = 0;
    if (entry) {
      if (entry.isExterior) {
        const ef = this._getExteriorZoneFlooring(entry.zone);
        angleDeg = ef ? Math.round(((ef.flooringAngle || 0) * 180 / Math.PI + 360) % 360) : 0;
      } else if (entry.zone) {
        const zf = this._getZoneFlooring(entry.room, entry.zone);
        angleDeg = zf ? Math.round(((zf.flooringAngle || 0) * 180 / Math.PI + 360) % 360) : 0;
      } else {
        angleDeg = Math.round(((entry.room.flooringAngle || 0) * 180 / Math.PI + 360) % 360);
      }
    }

    if (this._rotateDialCanvas) {
      this._drawRotateDial(this._rotateDialCanvas, angleDeg);
    }

    if (this._rotatePop) {
      this._rotatePop.querySelectorAll('.floor-rotate-preset-btn').forEach(btn => {
        const pAngle = parseInt(btn.dataset.angle, 10);
        btn.classList.toggle('active', pAngle === angleDeg);
      });
    }

    // Zone navigation row — show only when 2+ targets exist
    if (this._rotateZoneNav) {
      if (targets.length > 1) {
        this._rotateZoneNav.style.display = '';
        const curIdx = this._selectedRotateRoomIdx || 0;
        const cur = targets[curIdx];

        // Count distinct rooms among targets
        const roomSet = new Set();
        for (const t of targets) roomSet.add(t.room);
        const multiRoom = roomSet.size > 1;
        if (this._rotateZoneLabels) this._rotateZoneLabels.classList.toggle('multi-room', multiRoom);

        // Room label — only if multiple rooms
        if (this._rotateRoomLabel) {
          if (multiRoom && cur) {
            const roomNum = [...roomSet].indexOf(cur.room) + 1;
            this._rotateRoomLabel.textContent = `Space ${roomNum}`;
            this._rotateRoomLabel.style.display = '';
          } else {
            this._rotateRoomLabel.style.display = 'none';
          }
        }

        // Zone label — use stable zone indices that match Flooring Breakdown
        if (cur && cur.zoneIdx) {
          this._rotateZoneLabel.textContent = `Zone ${cur.zoneIdx} / ${cur.totalZones}`;
        } else if (multiRoom) {
          // Single-zone room in multi-room context — no zone label needed
          this._rotateZoneLabel.textContent = '';
        } else {
          // Single room, single zone — no zone label needed
          this._rotateZoneLabel.textContent = '';
        }
      } else {
        this._rotateZoneNav.style.display = 'none';
      }
    }

    // Update optimize button label to reflect zone vs room scope
    if (this._rotateOptimizeBtn) {
      const svgIcon = this._rotateOptimizeBtn.querySelector('svg');
      const svgHTML = svgIcon ? svgIcon.outerHTML + ' ' : '';
      this._rotateOptimizeBtn.innerHTML = entry && entry.zone
        ? svgHTML + 'Minimize Zone Waste'
        : svgHTML + 'Minimize Waste';
    }

    // Enable/disable "Minimize Space Waste" — needs 2+ zones with flooring
    if (this._rotateOptimizeSpaceBtn && entry && !entry.isExterior) {
      const geo = this.rd._getRoomGeoCache(entry.room);
      const zones = this._getZonesForRoom(entry.room, geo);
      const flooredZoneCount = zones.filter(z => this._getZoneFlooring(entry.room, z)).length;
      this._rotateOptimizeSpaceBtn.disabled = flooredZoneCount < 2;
    } else if (this._rotateOptimizeSpaceBtn) {
      this._rotateOptimizeSpaceBtn.disabled = true;
    }

    // Update material counter — per-zone if targeting a zone, else whole room
    if (this._rotateMaterialDisplay && entry) {
      let totalArea;
      if (entry.isExterior) {
        totalArea = entry.zone ? entry.zone.area : null;
      } else {
        totalArea = entry.zone
          ? this._computeMaterialAreaForZone(entry.room, entry.zone)
          : this._computeMaterialAreaForRoom(entry.room);
      }
      if (totalArea !== null) {
        const rounded = Math.round(totalArea);
        const unit = this.rd.unit;
        let areaStr;
        if (unit === 'inches') areaStr = `${Math.round(totalArea * 144).toLocaleString()} sq in`;
        else if (unit === 'meters' || unit === 'm-cm') areaStr = `${Math.round(totalArea * 0.092903).toLocaleString()} sq m`;
        else if (unit === 'cm') areaStr = `${Math.round(totalArea * 929.03).toLocaleString()} sq cm`;
        else areaStr = `${rounded.toLocaleString()} sq ft`;
        this._rotateMaterialDisplay.innerHTML = `Material: ${areaStr}`;
      } else {
        this._rotateMaterialDisplay.innerHTML = '&mdash; sq ft';
      }
    } else if (this._rotateMaterialDisplay) {
      this._rotateMaterialDisplay.innerHTML = '&mdash; sq ft';
    }
  }

  _cycleTargetRoom(dir) {
    const targets = this._getFlooredTargets();
    if (targets.length <= 1) return;
    const d = dir || 1;
    this._selectedRotateRoomIdx = (((this._selectedRotateRoomIdx || 0) + d) % targets.length + targets.length) % targets.length;
    this._resetWallSnap();
    this._updateRotatePopoverForSelectedRoom();

    // Zoom to fit the newly selected room/zone
    const target = targets[this._selectedRotateRoomIdx];
    if (target && target.room) this._fitToRoom(target.room);
  }

  _fitToRoom(room) {
    const rd = this.rd;
    const points = [];
    for (const w of room.walls) {
      points.push(w.start);
      points.push(w.end);
    }
    if (points.length === 0) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    const pad = 4; // feet of padding around room
    minX -= pad; minY -= pad; maxX += pad; maxY += pad;

    const cw = maxX - minX, ch = maxY - minY;
    if (cw <= 0 || ch <= 0) return;

    // Account for side panel covering left portion of canvas (desktop only)
    let panelOffset = 0;
    if (!rd._isMobile) {
      const panel = document.getElementById('rdSidePanel');
      if (panel && (panel.classList.contains('open') || panel.classList.contains('minimized'))) {
        panelOffset = panel.offsetWidth || 0;
      }
    }
    const availW = rd._cssWidth - panelOffset;
    let availH = rd._cssHeight;
    if (rd._viewOnly && rd._isMobile) {
      const mobilePanel = document.getElementById('rdSidePanel');
      if (mobilePanel && mobilePanel.classList.contains('open')) {
        availH -= (mobilePanel.offsetHeight || 0);
      }
    }

    // Account for canvas rotation when computing fit scale
    const a = rd._canvasRotationRad || 0;
    const cosA = Math.abs(Math.cos(a)), sinA = Math.abs(Math.sin(a));
    const screenCW = cw * cosA + ch * sinA;
    const screenCH = cw * sinA + ch * cosA;
    const scaleX = availW / (screenCW * rd.basePixelsPerFoot);
    const scaleY = availH / (screenCH * rd.basePixelsPerFoot);
    rd.zoomLevel = Math.max(rd.minZoom, Math.min(rd.maxZoom, Math.min(scaleX, scaleY)));

    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    // Center in the visible area (offset by panel width)
    const ws = rd.worldToScreen(cx, cy);
    rd.panX += (panelOffset + availW / 2) - ws.x;
    rd.panY += availH / 2 - ws.y;

    document.getElementById('zoomLevel').textContent = Math.round(rd.zoomLevel * 100) + '%';
    if (rd._onZoomHook) rd._onZoomHook();
    rd.render();
  }

  // ── Material Area Helpers ──────────

  _computeMaterialAreaForRoom(room) {
    if (!room || !room.flooring) return null;
    const product = this._getProduct(room.flooring.productId);
    if (!product) return null;
    const geo = this.rd._getRoomGeoCache(room);
    if (!geo.roomPolygon || geo.roomPolygon.length < 3) return null;

    // Use cached layout if available (already computed by render)
    const layout = room._flooringLayout;
    if (layout && layout.strips) {
      let total = 0;
      for (const strip of layout.strips) {
        for (const roll of strip.rolls) {
          total += strip.width * roll.length;
        }
      }
      return total;
    }

    // Fallback: compute fresh
    return this._computeMaterialAreaAtAngle(room, geo, product, room.flooringAngle || 0);
  }

  _computeMaterialAreaForZone(room, zone) {
    if (!room || !zone) return null;
    const zf = this._getZoneFlooring(room, zone);
    if (!zf) return null;
    const product = this._getProduct(zf.productId);
    if (!product) return null;

    // Pavers: formula-based (floor area + 5% extra for mistakes)
    if (product.shape === 'paver') {
      const netArea = Math.abs(this._polygonArea(zone.polygon));
      const paverShapeArea = product.widthFt * product.heightFt * 0.86924;
      const base = Math.ceil(netArea / paverShapeArea);
      const extra = Math.max(1, Math.ceil(base * 0.05));
      return (base + extra) * paverShapeArea;
    }

    // Check per-zone layout cache first
    const geo = this.rd._getRoomGeoCache(room);
    const zones = this._getZonesForRoom(room, geo);
    const zi = zones.indexOf(zone);
    if (zi >= 0) {
      const cached = this._getZoneLayoutCache(room, zi);
      if (cached && cached.layout) {
        const layout = cached.layout;
        if (layout.tiles) {
          const tileArea = (product.widthFt || 2) * (product.heightFt || product.widthFt || 2);
          return layout.tiles.length * tileArea;
        }
        if (layout.strips) {
          let total = 0;
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) total += strip.width * roll.length;
          }
          return total;
        }
      }
    }

    // Fallback: compute from zone polygon
    const _zHoles = this._zoneHoles(zone);
    const zoneGeo = { roomPolygon: zone.polygon, cutoutPolys: [], holePolygons: _zHoles, area: zone.area };
    const isTile = product.type === 'tile';
    const floorAngle = zf.flooringAngle || 0;
    let layout;
    if (floorAngle !== 0) {
      const centroid = this._getPolygonCentroid(zone.polygon);
      const rPoly = this._rotatePolygon(zone.polygon, -floorAngle, centroid.x, centroid.y);
      const rHoles = _zHoles.map(h => this._rotatePolygon(h, -floorAngle, centroid.x, centroid.y));
      layout = isTile
        ? this._computeTileLayout(room, zoneGeo, product, rPoly, rHoles)
        : this._computeRollLayout(room, zoneGeo, product, rPoly, rHoles);
    } else {
      layout = isTile
        ? this._computeTileLayout(room, zoneGeo, product)
        : this._computeRollLayout(room, zoneGeo, product);
    }
    if (layout.tiles) {
      const tileArea = (product.widthFt || 2) * (product.heightFt || product.widthFt || 2);
      return layout.tiles.length * tileArea;
    }
    let total = 0;
    if (layout.strips) {
      for (const strip of layout.strips) {
        for (const roll of strip.rolls) total += strip.width * roll.length;
      }
    }
    return total;
  }

  _computeMaterialAreaAtAngle(room, geo, product, angleRad, overridePoly) {
    const poly = overridePoly || geo.roomPolygon;
    const isPaver = product.shape === 'paver';
    const isTile = !isPaver && product.type === 'tile';

    // Pavers: formula-based (floor area + 5% extra) — rotation doesn't change count
    if (isPaver) {
      const netArea = Math.abs(this._polygonArea(poly));
      const paverShapeArea = product.widthFt * product.heightFt * 0.86924;
      const base = Math.ceil(netArea / paverShapeArea);
      const extra = Math.max(1, Math.ceil(base * 0.05));
      return (base + extra) * paverShapeArea;
    }

    const _isRoll3 = !isTile;
    const _turfExcl = overridePoly ? [] : this._getTurfExclusionsForRoom(room, geo, !_isRoll3);
    const exclusions = overridePoly ? [] : [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._turfExcl];
    let layout;
    if (angleRad !== 0) {
      const centroid = this._getPolygonCentroid(poly);
      const rPoly = this._rotatePolygon(poly, -angleRad, centroid.x, centroid.y);
      const rExcl = exclusions.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -angleRad, centroid.x, centroid.y) : e);
      layout = isTile
        ? this._computeTileLayout(room, geo, product, rPoly, rExcl)
        : this._computeRollLayout(room, geo, product, rPoly, rExcl);
    } else {
      layout = isTile
        ? this._computeTileLayout(room, geo, product, poly, exclusions)
        : this._computeRollLayout(room, geo, product, poly, exclusions);
    }
    if (layout.tiles) {
      const tileArea = (product.widthFt || 2) * (product.heightFt || product.widthFt || 2);
      return layout.tiles.length * tileArea;
    }
    let total = 0;
    for (const strip of layout.strips) {
      for (const roll of strip.rolls) {
        total += strip.width * roll.length;
      }
    }
    return total;
  }

  _optimizeFlooringAngle() {
    const entry = this._getTargetFlooringEntry();
    if (!entry) return;
    const room = entry.room;

    // Get product from zone flooring or room flooring
    let productId;
    if (entry.zone) {
      const zf = this._getZoneFlooring(room, entry.zone);
      if (!zf) return;
      productId = zf.productId;
    } else {
      if (!room.flooring) return;
      productId = room.flooring.productId;
    }
    const product = this._getProduct(productId);
    if (!product) return;

    // Use zone polygon if targeting a zone, else room polygon
    const geo = this.rd._getRoomGeoCache(room);
    const poly = entry.zone ? entry.zone.polygon : geo.roomPolygon;
    if (!poly || poly.length < 3) return;

    this.rd.saveUndoState();

    // Coarse sweep: 0°–355° in 5° steps (all four quadrants)
    let bestAngle = 0;
    let bestArea = Infinity;
    for (let deg = 0; deg < 360; deg += 5) {
      const rad = deg * Math.PI / 180;
      const area = this._computeMaterialAreaAtAngle(room, geo, product, rad, poly);
      if (area < bestArea) {
        bestArea = area;
        bestAngle = deg;
      }
    }

    // Fine sweep: ±4° around winner in 1° steps
    for (let d = -4; d <= 4; d++) {
      const deg = ((bestAngle + d) % 360 + 360) % 360;
      const rad = deg * Math.PI / 180;
      const area = this._computeMaterialAreaAtAngle(room, geo, product, rad, poly);
      if (area < bestArea) {
        bestArea = area;
        bestAngle = deg;
      }
    }

    // Snap rule: within 5° of any cardinal → snap to that cardinal
    const cardinals = [0, 90, 180, 270];
    for (const c of cardinals) {
      let diff = bestAngle - c;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      if (Math.abs(diff) <= 5) { bestAngle = c; break; }
    }

    const targetRad = bestAngle * Math.PI / 180;

    // For zone targets, enable independent layout (own grid origin, not room-level)
    // so strips align optimally to this zone's bounds, reducing overage
    if (entry.zone) {
      this._setZoneIndependent(room, entry.zone, true);
      room._zoneLayouts = null;
    }

    this._animateToAngle(targetRad, 500, () => {
      // Green flash feedback after animation completes
      if (this._rotateMaterialDisplay) {
        this._rotateMaterialDisplay.classList.add('optimized');
        clearTimeout(this._optimizeFlashTimer);
        this._optimizeFlashTimer = setTimeout(() => {
          this._rotateMaterialDisplay.classList.remove('optimized');
        }, 1500);
      }
    });
  }

  _optimizeSpaceAngle() {
    const entry = this._getTargetFlooringEntry();
    if (!entry) return;
    const room = entry.room;
    const geo = this.rd._getRoomGeoCache(room);
    const roomPoly = geo.roomPolygon;
    if (!roomPoly || roomPoly.length < 3) return;

    // Clear any per-zone independent layout — space optimization re-aligns all zones
    this._clearAllZoneIndependent(room);

    // Gather all floored zones with their products
    const zones = this._getZonesForRoom(room, geo);
    if (zones.length <= 1) return;
    const zoneProducts = [];
    for (const z of zones) {
      const zf = this._getZoneFlooring(room, z);
      if (!zf) continue;
      const product = this._getProduct(zf.productId);
      if (product) zoneProducts.push({ zone: z, product });
    }
    if (zoneProducts.length === 0) return;

    this.rd.saveUndoState();

    // Compute total material area across all zones at a given angle
    const totalAreaAt = (deg) => {
      const rad = deg * Math.PI / 180;
      let total = 0;
      for (const { zone, product } of zoneProducts) {
        total += this._computeMaterialAreaAtAngle(room, geo, product, rad, zone.polygon);
      }
      return total;
    };

    // Coarse sweep: 0°–355° in 5° steps
    let bestAngle = 0;
    let bestArea = Infinity;
    for (let deg = 0; deg < 360; deg += 5) {
      const area = totalAreaAt(deg);
      if (area < bestArea) { bestArea = area; bestAngle = deg; }
    }

    // Fine sweep: ±4° around winner in 1° steps
    for (let d = -4; d <= 4; d++) {
      const deg = ((bestAngle + d) % 360 + 360) % 360;
      const area = totalAreaAt(deg);
      if (area < bestArea) { bestArea = area; bestAngle = deg; }
    }

    // Snap rule: within 5° of any cardinal → snap to that cardinal
    for (const c of [0, 90, 180, 270]) {
      let diff = bestAngle - c;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;
      if (Math.abs(diff) <= 5) { bestAngle = c; break; }
    }

    const targetRad = bestAngle * Math.PI / 180;

    // Animate all zones together to the optimal angle
    this._animateAllZonesToAngle(room, zones, targetRad, 500, () => {
      if (this._rotateMaterialDisplay) {
        // Show total material for the entire room, not just the last selected zone
        const totalArea = this._computeMaterialAreaForRoom(room);
        if (totalArea !== null) {
          const unit = this.rd.unit;
          let areaStr;
          if (unit === 'inches') areaStr = `${Math.round(totalArea * 144).toLocaleString()} sq in`;
          else if (unit === 'meters' || unit === 'm-cm') areaStr = `${Math.round(totalArea * 0.092903).toLocaleString()} sq m`;
          else if (unit === 'cm') areaStr = `${Math.round(totalArea * 929.03).toLocaleString()} sq cm`;
          else areaStr = `${Math.round(totalArea).toLocaleString()} sq ft`;
          this._rotateMaterialDisplay.innerHTML = `Material: ${areaStr}`;
        }
        this._rotateMaterialDisplay.classList.add('optimized');
        clearTimeout(this._optimizeFlashTimer);
        this._optimizeFlashTimer = setTimeout(() => {
          this._rotateMaterialDisplay.classList.remove('optimized');
        }, 1500);
      }
    });
  }

  _animateAllZonesToAngle(room, zones, targetRad, duration, onDone) {
    if (this._angleAnimRAF) cancelAnimationFrame(this._angleAnimRAF);

    // Per-zone start angles — only animate zones that need to change
    const zoneAnims = [];
    for (const z of zones) {
      const zf = this._getZoneFlooring(room, z);
      if (!zf) continue;
      const start = zf.flooringAngle || 0;
      let diff = targetRad - start;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      if (Math.abs(diff) < 0.001) {
        // Already at target — snap and skip animation
        this._setZoneAngle(room, z, targetRad);
        continue;
      }
      zoneAnims.push({ zone: z, start, diff });
    }

    room.flooringAngle = targetRad;
    room._flooringLayout = null;
    room._zoneLayouts = null;

    if (zoneAnims.length === 0) {
      // All zones already at target
      this.rd.render();
      this._updateRotatePopoverForSelectedRoom();
      this._updateMaterialSummary();
      if (this._rollOptimizationMode === 'uniform') this._repopulateUniformTargetSelects();
      if (onDone) onDone();
      return;
    }

    const startTime = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);

      for (const za of zoneAnims) {
        const angle = za.start + za.diff * ease(t);
        this._setZoneAngle(room, za.zone, angle);
      }
      room._flooringLayout = null;
      room._zoneLayouts = null;
      this.rd.render();
      this._updateRotatePopoverForSelectedRoom();
      this._updateMaterialSummary();

      if (t < 1) {
        this._angleAnimRAF = requestAnimationFrame(tick);
      } else {
        this._angleAnimRAF = null;
        if (this._rollOptimizationMode === 'uniform') this._repopulateUniformTargetSelects();
        if (onDone) onDone();
      }
    };
    this._angleAnimRAF = requestAnimationFrame(tick);
  }

  _animateToAngle(targetRad, duration, onDone) {
    // Cancel any running animation
    if (this._angleAnimRAF) cancelAnimationFrame(this._angleAnimRAF);

    const entry = this._getTargetFlooringEntry();
    if (!entry) return;

    let startRad;
    if (entry.isExterior) {
      const ef = this._getExteriorZoneFlooring(entry.zone);
      startRad = ef ? (ef.flooringAngle || 0) : 0;
    } else if (entry.zone) {
      const zf = this._getZoneFlooring(entry.room, entry.zone);
      startRad = zf ? (zf.flooringAngle || 0) : 0;
    } else {
      startRad = entry.room.flooringAngle || 0;
    }
    // Shortest angular path (normalize diff to [-PI, PI])
    let diff = targetRad - startRad;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;

    // If already there, skip
    if (Math.abs(diff) < 0.001) { if (onDone) onDone(); return; }

    const startTime = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const tick = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const angle = startRad + diff * ease(t);
      this._setFlooringAngle(angle);

      if (t < 1) {
        this._angleAnimRAF = requestAnimationFrame(tick);
      } else {
        this._angleAnimRAF = null;
        // Ensure exact final value
        this._setFlooringAngle(targetRad);
        if (onDone) onDone();
      }
    };
    this._angleAnimRAF = requestAnimationFrame(tick);
  }

  // ── Wall Snap Matching ──────────
  // During compass drag, walls matching the current angle highlight blue.

  _getRoomWalls() {
    const room = this._getTargetFlooringRoom();
    if (!room || !room.closed) return [];
    const geo = this.rd._getRoomGeoCache(room);
    const perimeterWalls = geo.allVisibleWalls || room.walls;
    // Include divider segments (wall-type + line-type) inside this room
    const dividers = this._getDividerSegmentsForRoom(room, geo);
    if (dividers.length === 0) return perimeterWalls;
    return [...perimeterWalls, ...dividers];
  }

  // Build a flat list of snap angles with their matching walls
  _getSnapAngles() {
    const walls = this._getRoomWalls();
    if (walls.length === 0) return [];

    const tolerance = 0.035; // ~2°
    const normAngle = a => ((a % Math.PI) + Math.PI) % Math.PI;

    // Collect unique wall orientations
    const wallGroups = [];
    for (const w of walls) {
      const angle = normAngle(Math.atan2(w.end.y - w.start.y, w.end.x - w.start.x));
      let found = false;
      for (const g of wallGroups) {
        if (Math.abs(g.angle - angle) < tolerance) {
          g.walls.push(w);
          found = true;
          break;
        }
      }
      if (!found) wallGroups.push({ angle, walls: [w] });
    }

    // For each wall group, generate perp AND parallel strip angles
    const entries = []; // { stripAngle, walls[] }
    const addEntry = (stripAngle, groupWalls) => {
      const sa = normAngle(stripAngle);
      for (const e of entries) {
        if (Math.abs(e.stripAngle - sa) < tolerance) {
          // Merge walls, avoiding duplicates
          for (const w of groupWalls) {
            if (!e.walls.includes(w)) e.walls.push(w);
          }
          return;
        }
      }
      entries.push({ stripAngle: sa, walls: [...groupWalls] });
    };

    for (const g of wallGroups) {
      addEntry(g.angle, g.walls);             // perpendicular
      addEntry(g.angle + Math.PI / 2, g.walls); // parallel
    }

    entries.sort((a, b) => a.stripAngle - b.stripAngle);
    return entries;
  }

  _matchWallsDuringDrag(angleDeg, nonCardinalEntries) {
    if (!nonCardinalEntries || nonCardinalEntries.length === 0) {
      this._snappedWalls = null;
      return;
    }

    // Tight tolerance — only matches when the dead-zone snap locked onto this angle
    const normPI = a => ((a % Math.PI) + Math.PI) % Math.PI;
    const dragRad = normPI(angleDeg * Math.PI / 180);
    const toleranceRad = 0.5 * Math.PI / 180; // 0.5°

    for (const entry of nonCardinalEntries) {
      let diff = Math.abs(dragRad - entry.stripAngle);
      if (diff > Math.PI / 2) diff = Math.PI - diff;

      if (diff <= toleranceRad) {
        this._snappedWalls = entry.walls;
        return;
      }
    }
    this._snappedWalls = null;
  }

  _resetWallSnap() {
    const had = !!this._snappedWalls;
    this._snappedWalls = null;
    if (had) this.rd.render();
  }

  // ── Side Panel (expanded / minimized / hidden) ─────

  // Suppress hover-triggered popovers on settings buttons while panel animates
  _markPanelAnimating() {
    const rd = document.querySelector('.room-designer');
    if (!rd) return;
    clearTimeout(this._panelAnimTimer);
    rd.classList.add('side-panel-animating');
    this._panelAnimTimer = setTimeout(() => rd.classList.remove('side-panel-animating'), 350);
  }

  _openSidePanel() {
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return;
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    // Flip close button chevron to "collapse" direction (points left)
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
    // Restore floor panel title + mini preview
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Flooring';
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = '';
    this._buildFloorPanelContent();
    this._updateMaterialSummary();
    // Auto-scroll to default product if no brush is active yet
    if (!this._activeBrush) this._scrollToDefaultProduct();
  }

  _scrollToDefaultProduct() {
    // No-op: categories start collapsed, user opens what they need
  }

  _minimizeSidePanel() {
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return;
    this._markPanelAnimating();
    panel.classList.remove('open');
    panel.classList.add('minimized');
    this._sidePanelOpen = false;
    this._sidePanelMinimized = true;
    document.querySelector('.room-designer').classList.remove('side-panel-open');
    document.querySelector('.room-designer').classList.add('side-panel-minimized');
    // Flip close button chevron to "expand" direction (points right)
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 4 15 12 9 20"/></svg>';
    this._updateMiniPreview();
  }

  _closeSidePanel() {
    // Full hide — used when leaving floor mode
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return;
    this._markPanelAnimating();
    panel.classList.remove('open', 'minimized');
    this._sidePanelOpen = false;
    this._sidePanelMinimized = false;
    const rd = document.querySelector('.room-designer');
    rd.classList.remove('side-panel-open', 'side-panel-minimized');
  }

  _setFloatingPillHidden(hide) {
    const pill = this.rd._viewOnlyFloatingPill;
    if (!pill) return;
    if (hide) { pill.style.opacity = '0'; pill.style.pointerEvents = 'none'; }
    else { pill.style.opacity = ''; pill.style.pointerEvents = ''; }
  }

  _closeViewOnlyPanel(full) {
    this._setFloatingPillHidden(false);
    this._viewOnlyPanelOpen = false;
    const panel = document.getElementById('rdSidePanel');

    if (full) {
      // Full cleanup — leaving equipment tab entirely
      this._closeSidePanel();
      this._viewOnlyPanelVendor = null;
      this._viewOnlyPanelEquipId = null;
      this._viewOnlyPanelTurfObj = null;
      this._viewOnlyPanelTurfProductId = null;
      this._viewOnlyPanelFloor = false;
      this._updateViewOnlyToggle(false);
    } else if (this.rd._isMobile) {
      // Mobile: fully close panel and deactivate tab so re-tap reopens
      this._closeSidePanel();
      this._viewOnlyPanelVendor = null;
      this._viewOnlyPanelEquipId = null;
      this._viewOnlyPanelTurfObj = null;
      this._viewOnlyPanelTurfProductId = null;
      this._viewOnlyPanelFloor = false;
      // Deactivate the Equipment Layout tab so it looks tappable again
      const activeTab = document.querySelector('.rd-multiview-tab.active');
      if (activeTab) activeTab.classList.remove('active');
    } else {
      // Desktop: slide away — show toggle button as ">" chevron to reopen
      this._closeSidePanel();
      this._updateViewOnlyToggle(true);
    }
  }

  _updateViewOnlyToggle(show) {
    const toggleBtn = document.getElementById('rdSidePanelToggle');
    if (!toggleBtn) return;
    if (show) {
      toggleBtn.classList.add('vo-visible');
      toggleBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 4 15 12 9 20"/></svg>';
      toggleBtn.title = 'Customize Colors';
    } else {
      toggleBtn.classList.remove('vo-visible');
      toggleBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>';
      toggleBtn.title = 'Product catalog';
    }
  }

  _toggleSidePanel() {
    // View-only: toggle reopens the color/turf panel
    if (this._viewOnlyPanelVendor) {
      if (this._sidePanelOpen) { this._closeViewOnlyPanel(); return; }
      this._openViewOnlyColorPanel(this._viewOnlyPanelEquipId);
      return;
    }
    if (this._viewOnlyPanelTurfObj) {
      if (this._sidePanelOpen) { this._closeViewOnlyPanel(); return; }
      this._openViewOnlyTurfPanel(this._viewOnlyPanelTurfObj);
      return;
    }
    if (this._viewOnlyPanelFloor) {
      if (this._sidePanelOpen) { this._closeViewOnlyPanel(); return; }
      this._openViewOnlyFloorPanel();
      return;
    }
    if (this._sidePanelOpen) this._minimizeSidePanel();
    else if (this.currentStep === 'equipment') this._openEquipmentPanel();
    else if (this.currentStep === 'finishing') this._openFinishingPanel();
    else this._openSidePanel();
  }

  /* ── Equipment Panel ── */

  _openEquipmentPanel() {
    const panel = document.getElementById('rdSidePanel');
    if (!panel) return;
    this._markPanelAnimating();
    panel.classList.remove('minimized');
    panel.classList.add('open');
    this._sidePanelOpen = true;
    this._sidePanelMinimized = false;
    document.querySelector('.room-designer').classList.add('side-panel-open');
    document.querySelector('.room-designer').classList.remove('side-panel-minimized');
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 4 9 12 15 20"/></svg>';
    // Swap header title
    const title = panel.querySelector('.rd-side-panel-title');
    if (title) title.textContent = 'Equipment';
    // Hide mini preview (not used for equipment)
    const mini = document.getElementById('rdMiniPreview');
    if (mini) mini.style.display = 'none';
    this._buildEquipmentPanelContent();
  }

  _buildEquipmentPanelContent() {
    const body = document.getElementById('rdSidePanelBody');
    if (!body) return;
    const rd = this.rd;

    // Show loading state while async fetch is in progress
    if (rd._equipmentLoading) {
      body.innerHTML = '<div style="padding:24px 16px;text-align:center;color:#888;font-size:14px;">'
        + '<svg width="24" height="24" viewBox="0 0 24 24" style="animation:spin 1s linear infinite;margin-bottom:8px;"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="31 31" stroke-linecap="round"/></svg>'
        + '<br>Loading equipment catalog\u2026</div>';
      return;
    }

    const images = rd._equipmentImages || {};

    // ── Category normalization ──
    const CAT_MAP = {
      'Barbell Storage': 'Storage',
      'Dumbbell Storage': 'Storage',
      'Kettlebell Storage': 'Storage',
      'Plate Storage': 'Storage',
      'Accessory Storage': 'Storage',
      'Plate Storage, Kettlebell Storage': 'Storage',
      'Rack & Cage Attachment': 'Racks & Cages',
      'Olympic Bench': 'Benches',
      'Utility Bench': 'Benches',
      'Utility Bench, Olympic Lifting': 'Benches',
      'Body Weight & Core Equipment': 'Body Weight & Core',
      'Glute Ham Developers': 'Body Weight & Core',
      'Weight Stack Machine': 'Weight Stack Machines',
      'Plate-Loaded Machine': 'Plate Loaded',
      'Free Weight Equipment': 'Plate Loaded',
      'Lifting Platform': 'Lifting Platforms',
      'Speed & Agility': 'Other Strength',
      'Olympic Lifting': 'Other Strength',
    };
    const CAT_ORDER = [
      'Weight Stack Machines', 'Plate Loaded', 'Racks & Cages',
      'Benches', 'Storage', 'Body Weight & Core',
      'Lifting Platforms', 'Other Strength', 'Accessories & Attachments',
      'TBD'
    ];
    const BENCH_PRIORITY = { 'Utility Bench': 0, 'Utility Bench, Olympic Lifting': 0, 'Olympic Bench': 1 };
    const normCat = (type) => CAT_MAP[type] || type || 'Other';

    // ── Bucket items by normalized category + collect vendors ──
    const buckets = {};
    const vendorSet = new Set();
    for (const [id, info] of Object.entries(images)) {
      const cat = normCat(info.productType);
      if (!buckets[cat]) buckets[cat] = [];
      buckets[cat].push({ id, info });
      if (info.vendor) vendorSet.add(info.vendor);
    }
    const vendors = [...vendorSet].sort();
    // Sort by numeric SKU (extracted leading digits), then title as fallback
    const skuNum = (item) => { const m = (item.info.sku || '').match(/^(\d+)/); return m ? parseInt(m[1], 10) : 99999; };
    for (const cat of Object.keys(buckets)) {
      if (cat === 'Benches') {
        buckets[cat].sort((a, b) => {
          const pa = BENCH_PRIORITY[a.info.productType] ?? 2;
          const pb = BENCH_PRIORITY[b.info.productType] ?? 2;
          if (pa !== pb) return pa - pb;
          const sa = skuNum(a), sb = skuNum(b);
          if (sa !== sb) return sa - sb;
          return (a.info.title || a.id).localeCompare(b.info.title || b.id);
        });
      } else {
        buckets[cat].sort((a, b) => {
          const sa = skuNum(a), sb = skuNum(b);
          if (sa !== sb) return sa - sb;
          return (a.info.title || a.id).localeCompare(b.info.title || b.id);
        });
      }
    }
    const knownCats = CAT_ORDER.filter(c => buckets[c]);
    const unknownCats = Object.keys(buckets).filter(c => !CAT_ORDER.includes(c)).sort();
    const orderedCats = [...knownCats, ...unknownCats];

    const totalCount = Object.keys(images).length;
    const placeholderSvg = '<svg class="rd-equipment-swatch-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>';
    const chevronSvg = '<svg class="rd-eq-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>';

    let html = '';
    // Search input
    html += '<div class="rd-equipment-search-wrap">';
    html += '<input class="rd-equipment-search" type="text" placeholder="Search ' + totalCount + ' products...">';
    html += '<svg class="rd-equipment-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>';
    html += '<div class="rd-eq-brand-bar">';
    html += '<button class="rd-eq-brand-pill active" data-eq-brand="">All</button>';
    for (const v of vendors) {
      html += '<button class="rd-eq-brand-pill" data-eq-brand="' + this._escAttr(v) + '">' + this._escHtml(v) + '</button>';
    }
    html += '</div>';
    html += '</div>';

    // ── Render category sections (headers only — swatches built lazily on expand/search) ──
    this._eqBuckets = buckets;
    this._eqPlaceholderSvg = placeholderSvg;
    for (const cat of orderedCats) {
      const catItems = buckets[cat];
      html += `<div class="rd-eq-category collapsed" data-eq-cat="${this._escHtml(cat)}">`;
      html += `<div class="rd-eq-category-header">`;
      html += chevronSvg;
      html += `<span class="rd-eq-category-name">${this._escHtml(cat)}</span>`;
      html += `<span class="rd-eq-category-count" data-eq-cat-total="${catItems.length}">${catItems.length}</span>`;
      html += `</div>`;
      html += `<div class="rd-eq-category-body" data-eq-lazy="1">`;
      html += `<div class="rd-equipment-swatch-grid"></div>`;
      html += '</div>';
      html += '</div>';
    }

    html += '<div class="rd-customize-hint">';
    html += '<svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-.75 3.5a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4ZM8 12a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>';
    html += '<span>Customize frame and pad colors in the <strong>Customize</strong> tab.</span>';
    html += '</div>';
    body.innerHTML = html;

    // Init accordion max-heights for equipment panel
    this._accordionInit(body, '.rd-eq-category', '.rd-eq-category-body');

    // ── Category-aware search + brand filter ──
    if (!this._eqCatManualCollapse) this._eqCatManualCollapse = {};
    this._eqActiveBrand = null; // null = all
    const searchInput = body.querySelector('.rd-equipment-search');
    if (searchInput) {
      searchInput.addEventListener('input', () => this._applyEquipmentFilters(body));
    }
    // Brand pill click
    const brandBar = body.querySelector('.rd-eq-brand-bar');
    if (brandBar) {
      brandBar.addEventListener('click', (e) => {
        const pill = e.target.closest('.rd-eq-brand-pill');
        if (!pill || pill.classList.contains('active')) return;
        brandBar.querySelectorAll('.rd-eq-brand-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        this._eqActiveBrand = pill.dataset.eqBrand || null;
        this._applyEquipmentFilters(body);
      });
    }

    // Delegated listener for swatch select (with lazy loading)
    this._setupEquipmentListeners(body, rd);

    // Canvas click handler for placing equipment
    this._addEquipmentCanvasListener();
  }

  /** Build swatch DOM for a single lazy category */
  _inflateCategorySwatches(catEl) {
    const catBody = catEl.querySelector('.rd-eq-category-body');
    if (!catBody || !catBody.dataset.eqLazy) return; // already inflated
    delete catBody.dataset.eqLazy;
    const cat = catEl.dataset.eqCat;
    const items = this._eqBuckets && this._eqBuckets[cat];
    if (!items || items.length === 0) return;
    const rd = this.rd;
    const placeholderSvg = this._eqPlaceholderSvg;
    const grid = catBody.querySelector('.rd-equipment-swatch-grid');
    if (!grid) return;
    let html = '';
    for (const { id, info } of items) {
      const label = info.title || id;
      const selected = rd._pendingEquipmentId === id ? ' selected' : '';
      const vendor = info.vendor || '';
      const tagStr = Array.isArray(info.tags) ? ' ' + info.tags.join(' ') : '';
      html += `<div class="rd-equipment-swatch-wrap" data-eq-id="${id}" data-eq-vendor="${this._escAttr(vendor)}" data-eq-search="${(label + ' ' + (info.sku || '') + ' ' + cat + ' ' + vendor + tagStr).toLowerCase()}">`;
      const hasImage = !!info.imageUrl;
      html += `<div class="rd-equipment-swatch${selected}${hasImage ? ' has-image' : ''}" data-equipment-id="${id}"`;
      if (hasImage) html += ` style="background-image: url(${info.imageUrl})"`;
      html += `>`;
      if (info.url) {
        html += `<a class="rd-swatch-zoom rd-swatch-link" href="${this._escAttr(info.url)}" target="_blank" rel="noopener" title="View product"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8.5v5a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1H8"/><polyline points="10 1.5 14.5 1.5 14.5 6"/><line x1="6.5" y1="9.5" x2="14.5" y2="1.5"/></svg></a>`;
      } else if (hasImage) {
        html += `<span class="rd-swatch-zoom" data-image-url="${info.imageUrl}" data-zoom-name="${this._escHtml(label)}"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="6.5" cy="6.5" r="4.5"/><line x1="10" y1="10" x2="14.5" y2="14.5"/></svg></span>`;
      }
      if (!hasImage && info.loaded) {
        const canvas = rd._ensureEquipmentCanvas(info, 'normal');
        html += `<img src="${canvas.toDataURL()}" alt="${label}">`;
      } else if (!hasImage && !info.loaded) {
        html += placeholderSvg;
      }
      html += `</div>`;
      html += `<div class="rd-equipment-swatch-label"><span>${label}</span></div>`;
      html += `</div>`;
    }
    grid.innerHTML = html;
  }

  /** Inflate all lazy categories (needed before search/brand filter can scan DOM) */
  _inflateAllCategories(body) {
    body.querySelectorAll('.rd-eq-category').forEach(catEl => this._inflateCategorySwatches(catEl));
  }

  _applyEquipmentFilters(body) {
    const searchInput = body.querySelector('.rd-equipment-search');
    const q = searchInput ? searchInput.value.toLowerCase().trim() : '';
    const brand = this._eqActiveBrand || null;
    let totalVisible = 0;

    // Inflate all lazy categories before filtering (one-time cost on first search/brand)
    if (q || brand) this._inflateAllCategories(body);

    body.querySelectorAll('.rd-eq-category').forEach(catEl => {
      const countBadge = catEl.querySelector('.rd-eq-category-count');
      const total = parseInt(countBadge.dataset.eqCatTotal, 10);
      let visible = 0;
      catEl.querySelectorAll('.rd-equipment-swatch-wrap').forEach(wrap => {
        const searchText = wrap.dataset.eqSearch || '';
        const wrapVendor = wrap.dataset.eqVendor || '';
        const matchesBrand = !brand || wrapVendor === brand;
        const matchesQuery = !q || searchText.includes(q);
        const show = matchesBrand && matchesQuery;
        wrap.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      const eqBody = catEl.querySelector(':scope > .rd-eq-category-body');
      if (q) {
        // Text search: hide empties, auto-expand matches
        catEl.style.display = visible > 0 ? '' : 'none';
        if (visible > 0) {
          this._inflateCategorySwatches(catEl);
          catEl.classList.remove('collapsed');
          if (eqBody) eqBody.style.maxHeight = 'none';
          if (!catEl._overflowChecked) {
            catEl._overflowChecked = true;
            requestAnimationFrame(() => this._markOverflowLabels(catEl, '.rd-equipment-swatch-label'));
          }
        }
        countBadge.textContent = visible + '/' + total;
      } else if (brand) {
        // Brand-only: hide empties, update counts, but respect manual collapse
        catEl.style.display = visible > 0 ? '' : 'none';
        const catName = catEl.dataset.eqCat;
        if (this._eqCatManualCollapse[catName] === false) {
          catEl.classList.remove('collapsed');
          if (eqBody) eqBody.style.maxHeight = 'none';
        } else {
          catEl.classList.add('collapsed');
          if (eqBody) eqBody.style.maxHeight = '0';
        }
        countBadge.textContent = visible + '/' + total;
      } else {
        catEl.style.display = '';
        const catName = catEl.dataset.eqCat;
        if (this._eqCatManualCollapse[catName] === false) {
          catEl.classList.remove('collapsed');
          if (eqBody) eqBody.style.maxHeight = 'none';
        } else {
          catEl.classList.add('collapsed');
          if (eqBody) eqBody.style.maxHeight = '0';
        }
        countBadge.textContent = total;
      }
      totalVisible += visible;
    });

    // Update placeholder to reflect filtered count
    if (searchInput) {
      if (brand) {
        searchInput.placeholder = 'Search ' + totalVisible + ' ' + brand + ' products\u2026';
      } else {
        const totalCount = Object.keys(this.rd._equipmentImages || {}).length;
        searchInput.placeholder = 'Search ' + totalCount + ' products\u2026';
      }
    }
  }

  _setupEquipmentListeners(body, rd) {
    // Guard: only attach once — body is the same DOM element across rebuilds
    if (this._equipmentTreeListenerAdded) return;
    this._equipmentTreeListenerAdded = true;
    body.addEventListener('click', (e) => {
      // Category header toggle (must come before swatch check)
      const catHeader = e.target.closest('.rd-eq-category-header');
      if (catHeader) {
        const catEl = catHeader.closest('.rd-eq-category');
        if (catEl) {
          // Inflate lazy swatches on first expand
          this._inflateCategorySwatches(catEl);
          this._accordionToggle(catEl);
          if (!this._eqCatManualCollapse) this._eqCatManualCollapse = {};
          this._eqCatManualCollapse[catEl.dataset.eqCat] = catEl.classList.contains('collapsed');
          // Lazy overflow check — labels have zero width while collapsed
          if (!catEl.classList.contains('collapsed') && !catEl._overflowChecked) {
            catEl._overflowChecked = true;
            requestAnimationFrame(() => this._markOverflowLabels(catEl, '.rd-equipment-swatch-label'));
          }
        }
        return;
      }
      // Product link click — open in new tab (must come before swatch click)
      const linkBtn = e.target.closest('.rd-swatch-link');
      if (linkBtn) {
        e.stopPropagation();
        return; // <a> tag handles navigation
      }
      // Zoom icon click — open lightbox (must come before swatch click)
      const zoomBtn = e.target.closest('.rd-swatch-zoom');
      if (zoomBtn) {
        e.stopPropagation();
        this._openSwatchLightbox(zoomBtn);
        return;
      }
      // Equipment swatch select
      const swatch = e.target.closest('.rd-equipment-swatch');
      if (!swatch) return;
      const eqId = swatch.dataset.equipmentId;

      // Deselect if clicking same item
      if (rd._pendingEquipmentId === eqId) {
        rd._clearPendingEquipment();
        return;
      }

      // Clear previous selection
      body.querySelectorAll('.rd-equipment-swatch').forEach(c => c.classList.remove('selected'));

      // Switching equipment type — reset remembered angle and chain reference
      rd._lastEquipmentRotation = null;
      rd._lastPlacedEquipPos = null;

      // Lazy load, then select
      const info = rd._equipmentImages[eqId];
      if (info && !info.loaded) {
        swatch.classList.add('loading');
        rd._ensureEquipmentLoaded(eqId, (loaded) => {
          swatch.classList.remove('loading');
          if (!loaded) {
            rd.updateStatus('Failed to load equipment image.', { error: true });
            return;
          }
          // Update swatch thumbnail (keep product photo if available)
          if (!loaded.imageUrl) {
            const canvas = rd._ensureEquipmentCanvas(loaded, 'normal');
            swatch.innerHTML = `<img src="${canvas.toDataURL()}" alt="${loaded.title || eqId}">`;
          }
          // Select it
          rd._pendingEquipmentId = eqId;
          rd._pendingEquipmentPos = null;
          rd._pendingEquipmentRotation = 0;
          swatch.classList.add('selected');
          rd.canvas.style.cursor = 'crosshair';
          document.getElementById('selectToolBtn').classList.remove('active');
          document.getElementById('handToolBtn').classList.remove('active');
          document.getElementById('penToolBtn').classList.remove('active');
          document.getElementById('objectsToolBtn').classList.remove('active');
          document.getElementById('eraserBtn').classList.remove('active');
          rd.updateStatus('Click the canvas to place equipment.');
          rd.render();
        });
      } else {
        rd._pendingEquipmentId = eqId;
        rd._pendingEquipmentPos = null;
        rd._pendingEquipmentRotation = 0;
        swatch.classList.add('selected');
        rd.canvas.style.cursor = 'crosshair';
        document.getElementById('selectToolBtn').classList.remove('active');
        rd.updateStatus('Click the canvas to place equipment.');
        rd.render();
      }
    });

    // Hover-prefetch: start SVG rasterization on mouseenter so it's ready before click
    body.addEventListener('mouseenter', (e) => {
      const swatch = e.target.closest('.rd-equipment-swatch');
      if (!swatch) return;
      const eqId = swatch.dataset.equipmentId;
      if (!eqId) return;
      const info = rd._equipmentImages && rd._equipmentImages[eqId];
      if (info && !info.loaded && !info._loading) {
        rd._ensureEquipmentLoaded(eqId);
      }
    }, true);
  }

  _addEquipmentCanvasListener() {
    if (this._equipmentCanvasListenerAdded) return;
    this._equipmentCanvasListenerAdded = true;
    const rd = this.rd;
    rd.canvas.addEventListener('click', (e) => {
      if (!rd._equipmentMode || !rd._pendingEquipmentId) return;
      // Don't place if user is dragging/panning
      if (rd.isPanning) return;
      const rect = rd.canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const world = rd.screenToWorld(sx, sy);
      const snapped = rd.snapToGrid(world.x, world.y);

      if (!rd._pendingEquipmentPos) {
        if (e.shiftKey && rd._lastEquipmentRotation != null) {
          // Shift-click — instant place at last-used angle, stay in placement mode
          // Apply cardinal axis constraint with edge-gap snapping
          let placeX = snapped.x, placeY = snapped.y;
          if (rd._lastPlacedEquipPos) {
            const info = rd._equipmentImages[rd._pendingEquipmentId];
            if (info) {
              const gWft = (info.tightWidthIn || info.widthIn) / 12, gDft = (info.tightDepthIn || info.depthIn) / 12;
              const snap = rd._computeCardinalEquipSnap(world.x, world.y, gWft, gDft, rd._lastEquipmentRotation);
              if (snap) { placeX = snap.x; placeY = snap.y; }
            }
          }
          // Block placement if ghost overlaps a wall
          if (rd._equipmentGhostBlocked) { rd.updateStatus('Cannot place — overlaps a wall', { error: true }); return; }
          rd._pendingEquipmentRotation = rd._lastEquipmentRotation;
          rd.placeEquipment(rd._pendingEquipmentId, placeX, placeY, rd._pendingEquipmentRotation);
          const deg = Math.round(rd._pendingEquipmentRotation * 180 / Math.PI);
          rd.updateStatus(`Placed at ${deg}°. Shift+click to stamp again, click to set new rotation.`);
        } else {
          // First click — lock position, enter rotation preview
          if (rd._equipmentGhostBlocked) { rd.updateStatus('Cannot place — overlaps a wall', { error: true }); return; }
          rd._pendingEquipmentPos = { x: snapped.x, y: snapped.y };
          const deg = Math.round((rd._pendingEquipmentRotation || 0) * 180 / Math.PI);
          rd.updateStatus(`Rotation: ${deg}°  —  Move mouse to rotate, click to confirm`);
        }
        rd.render();
      } else {
        // Second click — place with current rotation, remember angle
        const rot = rd._pendingEquipmentRotation || 0;
        rd.placeEquipment(rd._pendingEquipmentId, rd._pendingEquipmentPos.x, rd._pendingEquipmentPos.y, rot);
        // Auto-slide out of walls if rotated position overlaps
        const placedObj = rd.objects[rd.objects.length - 1];
        if (placedObj && placedObj.type === 'equipment') {
          rd._slideEquipmentInBounds(placedObj);
        }
        rd._lastEquipmentRotation = rot;
        rd._pendingEquipmentPos = null;
        rd._pendingEquipmentRotation = rot;
        const deg = Math.round(rot * 180 / Math.PI);
        rd.updateStatus(`Placed at ${deg}°. Shift+click to stamp again, click to set new rotation.`);
        rd.render();
      }
    });
  }

  _updateMiniPreview() {
    const el = document.getElementById('rdMiniPreview');
    if (!el) return;
    el.innerHTML = '';
    el.style.background = '';

    // Collect all unique product/color combos assigned to rooms
    const rd = this.rd;
    const combos = new Map();
    if (rd && rd.rooms) {
      for (const room of rd.rooms) {
        if (room.flooring && room.flooring.productId && room.flooring.colorId) {
          const key = room.flooring.productId + '::' + room.flooring.colorId;
          if (!combos.has(key)) combos.set(key, room.flooring);
        }
        if (room.zoneFlooring) {
          for (const zf of room.zoneFlooring) {
            const key = zf.productId + '::' + zf.colorId;
            if (!combos.has(key)) combos.set(key, { productId: zf.productId, colorId: zf.colorId });
          }
        }
      }
    }
    // Include exterior zone flooring in mini preview
    if (this._exteriorZoneFlooring) {
      for (const zf of this._exteriorZoneFlooring) {
        const key = zf.productId + '::' + zf.colorId;
        if (!combos.has(key)) combos.set(key, { productId: zf.productId, colorId: zf.colorId });
      }
    }

    if (combos.size === 0) return;

    const maxVisible = 4;
    let count = 0;
    for (const [, combo] of combos) {
      if (count >= maxVisible) break;
      const product = this._getProduct(combo.productId);
      if (!product) continue;
      const color = product.colors.find(c => c.id === combo.colorId) || product.colors[0];
      if (!color) continue;
      const swatch = document.createElement('div');
      swatch.className = 'rd-mini-swatch';
      const _miniImg = color.swatchImageUrl || color.imageUrl;
      swatch.style.background = _miniImg ? `url(${_miniImg})` : (color.swatch || color.hex);
      if (_miniImg) swatch.style.backgroundSize = 'cover';
      el.appendChild(swatch);
      count++;
    }

    if (combos.size > maxVisible) {
      const more = document.createElement('div');
      more.className = 'rd-mini-swatch-more';
      more.textContent = '+' + (combos.size - maxVisible);
      el.appendChild(more);
    }
  }

  _setupSidePanelEvents() {
    const closeBtn = document.getElementById('rdSidePanelClose');
    if (closeBtn) closeBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent bubble to panel (innerHTML swap detaches e.target, breaking closest() guard)
      // View-only panel on mobile: toggle between open and minimized strip
      if (this._viewOnlyPanelOpen) {
        this._closeViewOnlyPanel();
        return;
      }
      // Mobile minimized button — handled by its own click listener

      if (this._sidePanelOpen) this._minimizeSidePanel();
      else if (this._sidePanelMinimized) this._reopenSidePanel();
    });
    const toggleBtn = document.getElementById('rdSidePanelToggle');
    if (toggleBtn) toggleBtn.addEventListener('click', () => this._toggleSidePanel());
    // Clicking the minimized panel strip also expands
    const panel = document.getElementById('rdSidePanel');
    if (panel) panel.addEventListener('click', (e) => {
      // Mobile minimized button is a separate element now — no panel click needed
      if (this._sidePanelMinimized && !e.target.closest('.rd-side-panel-close')) {
        this._reopenSidePanel();
      }
    });
  }

  // Re-open the panel in the correct mode (floor vs equipment)
  _reopenSidePanel() {
    if (this.currentStep === 'equipment') this._openEquipmentPanel();
    else this._openSidePanel();
  }

  _buildFloorPanelContent() {
    const body = document.getElementById('rdSidePanelBody');
    if (!body) return;
    if (this.products.length === 0) {
      body.innerHTML = '<div class="rd-side-panel-loading">Loading products...</div>';
      return;
    }

    let html = '';
    const categories = this._productCategories.length > 0 ? this._productCategories : ['All'];
    const brush = this._activeBrush;

    // Search bar (sticky at top, mirrors equipment search pattern)
    const totalProducts = this.products.length;
    html += '<div class="rd-floor-search-wrap">';
    html += '<input class="rd-floor-search" type="text" placeholder="Search ' + totalProducts + ' products...">';
    html += '<svg class="rd-floor-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>';
    html += '</div>';

    for (const cat of categories) {
      const catProducts = this.products.filter(p =>
        (p.categories && p.categories.includes(cat)) || (categories.length === 1 && categories[0] === 'All')
      );
      if (catProducts.length === 0) continue;
      // Sort by price level (fewest $ first), then alphabetically by name
      catProducts.sort((a, b) => {
        const pa = (a.priceLevel || '').length, pb = (b.priceLevel || '').length;
        if (pa !== pb) return pa - pb;
        return (a.name || '').localeCompare(b.name || '');
      });

      html += `<div class="rd-product-category collapsed" data-floor-cat="${this._escHtml(cat)}">`;
      html += `<div class="rd-product-category-title">`;
      html += `<svg class="rd-product-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>`;
      html += `<span class="rd-product-category-name">${this._escHtml(cat)}</span>`;
      html += `<span class="rd-product-category-count" data-floor-cat-total="${catProducts.length}">${catProducts.length}</span>`;
      html += `</div>`;
      html += `<div class="rd-product-category-body">`;

      for (const product of catProducts) {
        const isActive = brush && brush.productId === product.id;
        const expanded = false;
        const activeColor = brush && brush.productId === product.id ? brush.colorId : product.defaultColor;
        const previewColor = product.colors[0];
        const metaStr = this._buildProductMetaStr(product);
        const isTile = product.type === 'tile';
        const shape = product.shape || (product.type === 'tile' ? 'square-tile' : 'roll');
        const iconColor = previewColor.hex || '#555';
        let shapeIcon;
        if (product.iconUrl) {
          shapeIcon = `<img class="rd-icon-light" src="${this._escAttr(product.iconUrl)}" width="36" height="36" alt="">`;
          if (product.iconDarkUrl) {
            shapeIcon += `<img class="rd-icon-dark" src="${this._escAttr(product.iconDarkUrl)}" width="36" height="36" alt="">`;
          }
        } else {
          shapeIcon = this._getShapeIcon(shape, iconColor);
        }

        const colorNames = product.colors.map(c => c.name).join(' ');
        const searchText = (product.name + ' ' + colorNames + ' ' + (product.type || '')).toLowerCase();
        html += `<div class="rd-product-card${isActive ? ' active' : ''}${expanded ? ' expanded' : ''}" data-product-id="${product.id}" data-floor-search="${this._escAttr(searchText)}">`;
        html += `<div class="rd-product-card-header">`;
        html += `<div class="rd-product-card-icon">${shapeIcon}</div>`;
        html += `<div class="rd-product-card-info">`;
        html += `<div class="rd-product-card-name"><span>${this._escHtml(product.name)}</span>`;
        html += `</div>`;
        html += `<div class="rd-product-card-meta">${metaStr}</div>`;
        html += `</div>`;
        html += `<svg class="rd-product-card-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 6 15 12 9 18"/></svg>`;
        html += `</div>`; // header

        html += `<div class="rd-product-card-body">`;
        if (product.type === 'roll') {
          const curMode = this._rollOptimizationMode;
          const radioName = 'rd-roll-opt-' + product.id;
          html += '<div class="rd-roll-opt-group">';

          // Minimize Material ($)
          html += '<label class="rd-roll-opt-row">';
          html += '<input type="radio" name="' + radioName + '" class="rd-roll-opt-radio" data-mode="minMaterial"' + (curMode === 'minMaterial' ? ' checked' : '') + '>';
          html += '<span class="rd-roll-opt-dot"></span>';
          html += '<span class="rd-roll-opt-label">Minimize Material <span class="rd-roll-cost-tag">$</span></span>';
          html += '<span class="rd-roll-cutting-help" tabindex="0">';
          html += '<span class="rd-roll-cutting-qmark">?</span>';
          html += '<span class="rd-roll-cutting-tooltip">';
          html += '<span class="rd-roll-cutting-tooltip-rec">$ Best Value</span>';
          html += 'Rolls get optimized to minimize the material needed. This means more roll cuts and more head seams. Best when cost matters more than appearance.';
          html += '</span>';
          html += '</span>';
          html += '</label>';

          // Minimize Head Seams ($$) — default
          html += '<label class="rd-roll-opt-row">';
          html += '<input type="radio" name="' + radioName + '" class="rd-roll-opt-radio" data-mode="minSeams"' + (curMode === 'minSeams' ? ' checked' : '') + '>';
          html += '<span class="rd-roll-opt-dot"></span>';
          html += '<span class="rd-roll-opt-label">Minimize Head Seams <span class="rd-roll-cost-tag">$$</span></span>';
          html += '<span class="rd-roll-cutting-help" tabindex="0">';
          html += '<span class="rd-roll-cutting-qmark">?</span>';
          html += '<span class="rd-roll-cutting-tooltip">';
          html += '<span class="rd-roll-cutting-tooltip-rec">\u2696 Best Overall</span>';
          html += 'Rolls get optimized to reduce seams AND minimize the material needed. We\u2019ll provide just enough rubber to cover your space while reducing labor required to install, giving you a balance of cost and appearance.';
          html += '</span>';
          html += '</span>';
          html += '</label>';

          // Uniform Roll Lengths ($$$)
          html += '<label class="rd-roll-opt-row">';
          html += '<input type="radio" name="' + radioName + '" class="rd-roll-opt-radio" data-mode="uniform"' + (curMode === 'uniform' ? ' checked' : '') + '>';
          html += '<span class="rd-roll-opt-dot"></span>';
          html += '<span class="rd-roll-opt-label">Uniform Roll Lengths <span class="rd-roll-cost-tag">$$$</span></span>';
          html += '<span class="rd-roll-cutting-help" tabindex="0">';
          html += '<span class="rd-roll-cutting-qmark">?</span>';
          html += '<span class="rd-roll-cutting-tooltip">';
          html += '<span class="rd-roll-cutting-tooltip-rec">\u2605 Manufacturer recommended</span>';
          html += 'Rolls get cut to the same length, ensuring they are close on the production line to guarantee a closer color match. Best when appearance is the most important factor.';
          html += '</span>';
          html += '</span>';
          html += '</label>';

          // Target length dropdown (shown when uniform is active)
          const showTarget = curMode === 'uniform';
          html += '<div class="rd-uniform-target" data-product-id="' + product.id + '" style="display:' + (showTarget ? 'block' : 'none') + '">';
          html += '<label class="rd-uniform-target-label">Target Roll Length</label>';
          html += '<select class="rd-uniform-target-select">';
          const targetOpts = this._getUniformLengthOptions(product);
          for (const opt of targetOpts) {
            html += '<option value="' + opt._targetLen + '"' + (this._uniformTargetLen === opt._targetLen ? ' selected' : '') + '>';
            html += opt.length + '\u2032 rolls';
            html += '</option>';
          }
          html += '</select>';
          html += '</div>';

          html += '</div>'; // .rd-roll-opt-group

          // Rep-only: editable per-product roll length limits (min / max), separate from the
          // optimization modes. Prefill from the product's saved defaults; overrides persist with the plan.
          if (this.rd._repMode) {
            // Roll sharing across spaces — filled by _rollShareRowsHtml, empty (hidden) until
            // a colour of this product is painted into 2+ spaces.
            html += '<div class="rd-roll-share" data-product-id="' + this._escAttr(product.id) + '">' + this._rollShareRowsHtml(product) + '</div>';
            const def = this._rollLenDefaults(product);
            const ov = this._rollLenOverride[product.id] || {};
            const curMin = (ov.min > 0) ? ov.min : def.min;
            const curMax = (ov.max > 0) ? ov.max : def.max;
            const isCustom = !!this._rollLenOverride[product.id];
            html += '<div class="rd-roll-len-override" data-product-id="' + this._escAttr(product.id) + '">';
            html += '<div class="rd-roll-len-override-title">Roll Length Limits</div>';
            html += '<div class="rd-roll-len-override-row">';
            html += '<label class="rd-roll-len-field"><span>Min (ft)</span>';
            html += '<input type="number" class="rd-roll-len-input" data-bound="min" min="1" step="1" inputmode="decimal" value="' + curMin + '" placeholder="' + def.min + '"></label>';
            html += '<label class="rd-roll-len-field"><span>Max (ft)</span>';
            html += '<input type="number" class="rd-roll-len-input" data-bound="max" min="1" step="1" inputmode="decimal" value="' + curMax + '" placeholder="' + def.max + '"></label>';
            html += '<button type="button" class="rd-roll-len-reset"' + (isCustom ? '' : ' disabled') + ' title="Reset to the site defaults">Reset</button>';
            html += '</div>';
            html += '<div class="rd-roll-len-default-note">Saved defaults: ' + def.min + '′ – ' + def.max + '′</div>';
            html += '</div>';
          }
        }
        if (product.type === 'turf') {
          html += '<div class="rd-customize-hint">';
          html += '<svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-.75 3.5a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4ZM8 12a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>';
          html += '<span>Customize field lines, logos, and markings in the <strong>Customize</strong> tab.</span>';
          html += '</div>';
        }
        if (product.description) {
          html += `<p class="rd-product-card-desc">${this._escHtml(product.description)}</p>`;
        }

        // Color swatches
        html += `<div class="rd-color-swatches">`;
        for (const color of product.colors) {
          const isColorActive = isActive && activeColor === color.id;
          const _swImg = color.swatchImageUrl || color.imageUrl;
          const swatchBg = _swImg ? `url(${_swImg})` : (color.swatch || color.hex);
          const hasImage = !!_swImg;
          html += `<div class="rd-color-swatch-wrap">`;
          html += `<div class="rd-color-swatch${isColorActive ? ' active' : ''}${hasImage ? ' has-image' : ''}" `;
          html += `data-product-id="${product.id}" data-color-id="${color.id}" `;
          html += `style="background: ${this._escAttr(swatchBg)}" `;
          html += `>`;
          if (product.url) {
            html += `<a class="rd-swatch-zoom rd-swatch-link" href="${this._escAttr(product.url)}" target="_blank" rel="noopener"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8.5v5a1 1 0 0 1-1 1H2.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1H8"/><polyline points="10 1.5 14.5 1.5 14.5 6"/><line x1="6.5" y1="9.5" x2="14.5" y2="1.5"/></svg></a>`;
          }
          html += `</div>`;
          html += `<div class="rd-color-swatch-label"><span>${this._escHtml(color.name)}</span></div>`;
          html += `</div>`;
        }
        html += `</div>`;

        // Custom colors notice
        if (product.customColors) {
          html += `<div class="rd-custom-colors-notice">`;
          html += `<svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-.75 3.5a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-1.5 0v-4ZM8 12a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>`;
          html += `<span>Also available in custom colors, with logos, or inlaid weightlifting platforms embedded. <a href="/pages/contact" target="_blank">Contact us</a> for a quote.</span>`;
          html += `</div>`;
        }

        html += `</div>`; // body
        html += `</div>`; // card
      }
      html += `</div>`; // .rd-product-category-body
      html += `</div>`; // .rd-product-category
    }

    // Inlaid platforms section (only if any product supports platforms)
    html += this._buildPlatformSectionHtml();

    body.innerHTML = html;

    // Init accordion max-heights for flooring panel
    this._accordionInit(body, '.rd-product-category', '.rd-product-category-body');

    // Category-aware search filter (bind fresh each rebuild since input is re-created)
    const floorSearchInput = body.querySelector('.rd-floor-search');
    if (floorSearchInput) {
      if (!this._floorCatManualCollapse) this._floorCatManualCollapse = {};
      floorSearchInput.addEventListener('input', () => {
        const q = floorSearchInput.value.toLowerCase().trim();
        body.querySelectorAll('.rd-product-category').forEach(catEl => {
          const countBadge = catEl.querySelector('.rd-product-category-count');
          const total = parseInt(countBadge.dataset.floorCatTotal, 10);
          let visible = 0;
          catEl.querySelectorAll('.rd-product-card').forEach(card => {
            const txt = card.dataset.floorSearch || '';
            const show = !q || txt.includes(q);
            card.style.display = show ? '' : 'none';
            if (show) visible++;
          });
          const flBody = catEl.querySelector(':scope > .rd-product-category-body');
          if (q) {
            catEl.style.display = visible > 0 ? '' : 'none';
            if (visible > 0) {
              catEl.classList.remove('collapsed');
              if (flBody) flBody.style.maxHeight = 'none';
              if (!catEl._overflowChecked) {
                catEl._overflowChecked = true;
                requestAnimationFrame(() => this._markOverflowLabels(catEl, '.rd-color-swatch-label, .rd-product-card-name'));
              }
            }
            countBadge.textContent = visible + '/' + total;
          } else {
            catEl.style.display = '';
            const catName = catEl.dataset.floorCat;
            if (this._floorCatManualCollapse[catName] === false) {
              catEl.classList.remove('collapsed');
              if (flBody) flBody.style.maxHeight = 'none';
            } else {
              catEl.classList.add('collapsed');
              if (flBody) flBody.style.maxHeight = '0';
            }
            countBadge.textContent = total;
          }
        });
      });
    }

    // Roll sharing checkboxes (rep only). Delegated, and bound once: body survives rebuilds.
    if (!body._rollShareBound) {
      body._rollShareBound = true;
      body.addEventListener('change', (e) => {
        const cb = e.target;
        if (!cb || !cb.classList || !cb.classList.contains('rd-roll-share-cb')) return;
        this._setRollShare(cb.dataset.shareKey, cb.checked);
      });
    }

    // Roll optimization mode radios (synced across product cards)
    const rollOptRadios = body.querySelectorAll('.rd-roll-opt-radio');
    const uniformTargetContainers = body.querySelectorAll('.rd-uniform-target');
    rollOptRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const mode = radio.dataset.mode;
        this._rollOptimizationMode = mode;
        // Sync radios in other product cards
        rollOptRadios.forEach(other => {
          if (other.dataset.mode === mode) other.checked = true;
        });
        // Show/hide target length dropdown
        uniformTargetContainers.forEach(ct => {
          ct.style.display = mode === 'uniform' ? 'block' : 'none';
        });
        // Reset target when switching away from uniform
        if (mode !== 'uniform') {
          this._uniformTargetLen = 0;
        }
        // Repopulate dropdown BEFORE render so the target is set
        if (mode === 'uniform') this._repopulateUniformTargetSelects();
        this.rd.updateMeasurements();
        this.rd.render();
        this._flashOverlayPreview();
      });
    });

    // Uniform target length selects
    const uniformTargetSelects = body.querySelectorAll('.rd-uniform-target-select');
    uniformTargetSelects.forEach(sel => {
      sel.addEventListener('change', () => {
        this._uniformTargetLen = parseFloat(sel.value) || 0;
        // Sync other target selects
        uniformTargetSelects.forEach(other => {
          if (other !== sel) other.value = sel.value;
        });
        this.rd.updateMeasurements();
        this.rd.render();
        this._repopulateUniformTargetSelects();
        this._flashOverlayPreview();
      });
    });

    // Rep-only per-product roll length override inputs (live on edit)
    const applyRollLenEdit = (wrap, changedInput) => {
      const pid = wrap.dataset.productId;
      const product = this._getProduct(pid);
      if (!product) return;
      const def = this._rollLenDefaults(product);
      const minInp = wrap.querySelector('.rd-roll-len-input[data-bound="min"]');
      const maxInp = wrap.querySelector('.rd-roll-len-input[data-bound="max"]');
      let min = parseFloat(minInp.value);
      let max = parseFloat(maxInp.value);
      if (!(min > 0)) min = def.min;
      if (!(max > 0)) max = def.max;
      // Keep min ≤ max, nudging the box the rep didn't just touch.
      if (min > max) { if (changedInput === minInp) max = min; else min = max; }
      minInp.value = min;
      maxInp.value = max;
      if (min === def.min && max === def.max) {
        delete this._rollLenOverride[pid];
      } else {
        this._rollLenOverride[pid] = { min, max };
      }
      this._applyRollLenOverride(product);
      const resetBtn = wrap.querySelector('.rd-roll-len-reset');
      if (resetBtn) resetBtn.disabled = !this._rollLenOverride[pid];
      this._invalidateFlooringLayoutCaches();
      this.rd.updateMeasurements();
      this.rd.render();
      this._flashOverlayPreview();
      // Roll-length limits are part of the saved design (_overlay.rollLenOverrides).
      // Mark dirty + autosave like every other edit, so the change persists — otherwise
      // it can be silently lost if the rep navigates away without an explicit re-save.
      this.rd._triggerAutoSave();
      this.rd._markDirty();
    };
    body.querySelectorAll('.rd-roll-len-input').forEach(inp => {
      inp.addEventListener('change', () => {
        const wrap = inp.closest('.rd-roll-len-override');
        if (wrap) applyRollLenEdit(wrap, inp);
      });
    });
    body.querySelectorAll('.rd-roll-len-reset').forEach(btn => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('.rd-roll-len-override');
        if (!wrap) return;
        const pid = wrap.dataset.productId;
        const product = this._getProduct(pid);
        if (!product) return;
        const def = this._rollLenDefaults(product);
        delete this._rollLenOverride[pid];
        this._applyRollLenOverride(product);
        wrap.querySelector('.rd-roll-len-input[data-bound="min"]').value = def.min;
        wrap.querySelector('.rd-roll-len-input[data-bound="max"]').value = def.max;
        btn.disabled = true;
        this._invalidateFlooringLayoutCaches();
        this.rd.updateMeasurements();
        this.rd.render();
        this._flashOverlayPreview();
        this.rd._triggerAutoSave();
        this.rd._markDirty();
      });
    });

    // Wire platform controls
    this._wirePlatformControls(body);

    // Event delegation for product cards and swatches (bind once only)
    if (body._floorClickBound) return;
    body._floorClickBound = true;
    body.addEventListener('click', (e) => {
      // Floor category header toggle (must come before other checks)
      const floorCatTitle = e.target.closest('.rd-product-category-title');
      if (floorCatTitle) {
        const catEl = floorCatTitle.closest('.rd-product-category');
        if (catEl && (catEl.dataset.floorCat || catEl.classList.contains('rd-platform-section'))) {
          this._accordionToggle(catEl);
          const catKey = catEl.dataset.floorCat || '_platform';
          if (!this._floorCatManualCollapse) this._floorCatManualCollapse = {};
          this._floorCatManualCollapse[catKey] = catEl.classList.contains('collapsed');
          // Lazy overflow check — labels have zero width while collapsed
          if (!catEl.classList.contains('collapsed') && !catEl._overflowChecked) {
            catEl._overflowChecked = true;
            requestAnimationFrame(() => this._markOverflowLabels(catEl, '.rd-color-swatch-label, .rd-product-card-name'));
          }
        }
        return;
      }
      // Swatch link click — open product page in new tab (must come before swatch click)
      const swatchLink = e.target.closest('.rd-swatch-link');
      if (swatchLink) {
        e.stopPropagation();
        return; // let the <a> default navigation happen
      }
      // Color swatch click
      const swatch = e.target.closest('.rd-color-swatch');
      if (swatch) {
        const pid = swatch.dataset.productId;
        const cid = swatch.dataset.colorId;
        const product = this._getProduct(pid);
        if (product && product.type === 'turf') {
          // If turf pieces are selected, update their color instead of entering draw mode
          const rd = this.rd;
          const selTurf = rd.selectedObjects && [...rd.selectedObjects].filter(o => o.type === 'turf');
          if (selTurf && selTurf.length > 0) {
            const color = product.colors.find(c => c.id === cid);
            rd.saveUndoState();
            for (const obj of selTurf) {
              obj.turfProductId = pid;
              obj.turfColorId = cid;
              obj.turfColorHex = color ? color.hex : '#3a7d44';
            }
            this._updatePanelSelection();
            rd.render();
            rd.updateMeasurements();
          } else {
            this._activateTurfDraw(pid, cid);
          }
          return;
        }
        this._setActiveBrush(pid, cid);
        return;
      }
      // Product card header click (expand/collapse)
      const header = e.target.closest('.rd-product-card-header');
      if (header) {
        const card = header.closest('.rd-product-card');
        if (card) {
          const pid = card.dataset.productId;
          const product = this._getProduct(pid);
          // Capture state BEFORE _setActiveBrush → _updatePanelSelection mutates classes
          const wasExpanded = card.classList.contains('expanded');
          if (product) {
            const currentBrush = this._activeBrush;
            const isSameProduct = currentBrush && currentBrush.productId === pid;
            if (isSameProduct && wasExpanded) {
              // Collapsing the active product — clear brush and deselect
              this._activeBrush = null;
              card.classList.remove('active');
              body.querySelectorAll('.rd-color-swatch').forEach(sw => sw.classList.remove('active'));
              this._updateMiniPreview();
              if (this.rd._floorMode) {
                // Exit turf-draw mode if active
                if (this.rd.activeTool === 'turf-draw') this.rd._resetTurfState();
                this.rd.activeTool = 'select';
                this.rd.mode = '';
                this.rd.canvas.style.cursor = 'default';
              }
              this.rd.updateStatus(this.rd._isMobile ? 'Tap rooms to toggle flooring' : 'Pan to explore. Click cursor tool to assign flooring.');
            }
            // Don't auto-select a variant — let the user pick from the swatches
          }
          // Force intended toggle — _updatePanelSelection auto-adds 'expanded' for
          // active products, so a bare toggle() would always remove it (stuck collapsed)
          card.classList.toggle('expanded', !wasExpanded);
          // Mark overflow labels on newly-visible color swatches
          if (!wasExpanded) {
            requestAnimationFrame(() => this._markOverflowLabels(card, '.rd-color-swatch-label, .rd-product-card-name'));
          }
        }
      }
    });

  }

  // ── Swatch Lightbox ─────────────────

  _openSwatchLightbox(zoomBtn) {
    // Remove existing lightbox if any
    this._closeSwatchLightbox();

    const imageUrl = zoomBtn.dataset.imageUrl;
    const color = zoomBtn.dataset.color;
    const name = zoomBtn.dataset.zoomName || '';

    const overlay = document.createElement('div');
    overlay.className = 'rd-swatch-lightbox-overlay';

    const box = document.createElement('div');
    box.className = 'rd-swatch-lightbox-box';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'rd-swatch-lightbox-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.setAttribute('aria-label', 'Close');
    box.appendChild(closeBtn);

    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = name;
      img.className = 'rd-swatch-lightbox-img';
      box.appendChild(img);
    } else {
      const swatch = document.createElement('div');
      swatch.className = 'rd-swatch-lightbox-color';
      swatch.style.background = color || '#ccc';
      box.appendChild(swatch);
    }

    if (name) {
      const label = document.createElement('div');
      label.className = 'rd-swatch-lightbox-label';
      label.textContent = name;
      box.appendChild(label);
    }

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    // Animate in
    requestAnimationFrame(() => overlay.classList.add('visible'));

    // Close handlers
    closeBtn.addEventListener('click', () => this._closeSwatchLightbox());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this._closeSwatchLightbox();
    });
    this._swatchLightboxEscHandler = (e) => {
      if (e.key === 'Escape') this._closeSwatchLightbox();
    };
    document.addEventListener('keydown', this._swatchLightboxEscHandler);
    this._swatchLightboxEl = overlay;
  }

  _closeSwatchLightbox() {
    if (this._swatchLightboxEl) {
      this._swatchLightboxEl.remove();
      this._swatchLightboxEl = null;
    }
    if (this._swatchLightboxEscHandler) {
      document.removeEventListener('keydown', this._swatchLightboxEscHandler);
      this._swatchLightboxEscHandler = null;
    }
  }

  // ── Product Meta String ─────────────────

  _formatInches(inches) {
    if (inches <= 0) return '';
    if (inches % 12 === 0) return `${inches / 12} ft`;
    return `${inches}"`;
  }

  _buildProductMetaStr(product) {
    const parts = [];
    const wIn = product.widthInches || 0;
    const hIn = product.heightInches || 0;
    const thick = product.thicknessDisplay || '';
    const isTile = product.type === 'tile';

    if (product._isCustomProduct) {
      parts.push('Custom cut');
    } else if (isTile) {
      // Tiles/pavers/planks: show W × H + thickness
      if (wIn > 0 && hIn > 0) {
        parts.push(`${this._formatInches(wIn)} × ${this._formatInches(hIn)}`);
      } else if (wIn > 0) {
        parts.push(`${this._formatInches(wIn)} × ${this._formatInches(wIn)}`);
      }
    } else {
      // Rolls/turf: show width only (length is cut-to-order)
      if (wIn > 0) parts.push(`${this._formatInches(wIn)} wide`);
    }

    if (thick) parts.push(`${thick} thick`);

    if (product.priceLevel) parts.push(product.priceLevel);

    return parts.join(' · ');
  }

  // ── Shape Icons ─────────────────────────

  _getShapeIcon(shape, color) {
    const c = this._escAttr(color || '#555');
    switch (shape) {
      case 'roll':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="14" height="10" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/><ellipse cx="16" cy="10" rx="2" ry="5" fill="${c}" opacity="0.3" stroke="${c}" stroke-width="1.2"/></svg>`;
      case 'turf-roll':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="14" height="10" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/><ellipse cx="16" cy="10" rx="2" ry="5" fill="${c}" opacity="0.3" stroke="${c}" stroke-width="1.2"/><line x1="5" y1="5" x2="5" y2="3" stroke="${c}" stroke-width="0.8" stroke-linecap="round"/><line x1="8" y1="5" x2="8" y2="2.5" stroke="${c}" stroke-width="0.8" stroke-linecap="round"/><line x1="11" y1="5" x2="11" y2="3.5" stroke="${c}" stroke-width="0.8" stroke-linecap="round"/></svg>`;
      case 'square-tile':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/></svg>`;
      case 'puzzle-tile':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><path d="M3 3 L6 3 C6 1,9 1,9 3 L11 3 C11 1,14 1,14 3 L17 3 L17 6 C19 6,19 9,17 9 L17 11 C19 11,19 14,17 14 L17 17 L14 17 C14 19,11 19,11 17 L9 17 C9 19,6 19,6 17 L3 17 L3 14 C1 14,1 11,3 11 L3 9 C1 9,1 6,3 6 Z" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1"/></svg>`;
      case 'paver':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="3" y="5" width="14" height="9" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/><line x1="3" y1="14" x2="5" y2="16" stroke="${c}" stroke-width="1" stroke-linecap="round"/><line x1="17" y1="14" x2="19" y2="16" stroke="${c}" stroke-width="1" stroke-linecap="round"/><line x1="5" y1="16" x2="19" y2="16" stroke="${c}" stroke-width="1" stroke-linecap="round"/></svg>`;
      case 'plank':
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="2" y="7" width="16" height="6" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/></svg>`;
      default:
        return `<svg width="26" height="26" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="1" fill="${c}" opacity="0.2" stroke="${c}" stroke-width="1.2"/></svg>`;
    }
  }

  _setActiveBrush(productId, colorId) {
    this._activeBrush = { productId, colorId };
    this._updatePanelSelection();
    this._updateMiniPreview();
    // In floor mode: activate roller tool so clicks apply flooring
    if (this.rd._floorMode) {
      this.rd.activeTool = 'roller';
      this.rd.mode = 'roller';
      this.rd.canvas.style.cursor = this._paintRollerCursor;
      // No toolbar button lit for roller — it's activated via the panel
      ['selectToolBtn', 'handToolBtn', 'eraserBtn', 'floorRotateBtn'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.remove('active');
      });
      // Close compass popover if open
      if (this._rotatePop) this._rotatePop.classList.remove('visible');
      this.rd._pinnedPopover = null;
    }
    // Update status bar
    const product = this._getProduct(productId);
    if (product) {
      const color = product.colors.find(c => c.id === colorId);
      const colorName = color ? color.name : '';
      this.rd.updateStatus(`Click rooms to apply ${product.name} (${colorName})`);
    }
  }

  /* CUSTOM_PLANNER_BY_CLAUDE — Turf draw mode activation */
  _activateTurfDraw(productId, colorId) {
    const product = this._getProduct(productId);
    const color = product ? product.colors.find(c => c.id === colorId) : null;
    const hex = color ? color.hex : '#3a7d44';
    const rd = this.rd;
    rd._turfBrush = { productId, colorId, hex };
    rd._turfCorner1 = null;
    rd.activeTool = 'turf-draw';
    rd.mode = 'turf-draw';
    rd.canvas.style.cursor = 'crosshair';
    // Set brush so panel highlights the selected swatch
    this._activeBrush = { productId, colorId };
    this._updatePanelSelection();
    this._updateMiniPreview();
    // Clear roller/floor state
    ['selectToolBtn', 'handToolBtn', 'eraserBtn', 'floorRotateBtn'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.classList.remove('active');
    });
    if (this._rotatePop) this._rotatePop.classList.remove('visible');
    rd._pinnedPopover = null;
    const colorName = color ? color.name : '';
    rd.updateStatus(`Click to place first corner of turf (${colorName}).`);
    rd.render();
  }
  /* END CUSTOM_PLANNER_BY_CLAUDE */

  // ── Paint Roller Cursor & Floating Swatch ──

  _buildPaintRollerCursor() {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><rect x='2' y='2' width='22' height='9' rx='3.5' fill='#006BCC' stroke='#004488' stroke-width='1'/><rect x='3' y='3' width='20' height='3' rx='1.5' fill='#2a90e0' opacity='0.5'/><path d='M13 11V16' stroke='#777' stroke-width='2' stroke-linecap='round'/><rect x='10.5' y='16' width='5' height='14' rx='2.5' fill='#ddd' stroke='#999' stroke-width='1'/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 13 11, pointer`;
  }


  _updatePanelSelection() {
    const body = document.getElementById('rdSidePanelBody');
    if (!body) return;
    const brush = this._activeBrush;

    // Update card active states (clear all when no brush)
    body.querySelectorAll('.rd-product-card').forEach(card => {
      const isActive = brush && card.dataset.productId === brush.productId;
      card.classList.toggle('active', isActive);
      // Auto-expand active product
      if (isActive) card.classList.add('expanded');
    });

    // Update swatch active states (clear all when no brush)
    body.querySelectorAll('.rd-color-swatch').forEach(sw => {
      const isActive = brush && sw.dataset.productId === brush.productId && sw.dataset.colorId === brush.colorId;
      sw.classList.toggle('active', isActive);
    });

    // Sync turf markings panel to selected turf object
    this._syncTurfPanelToSelection(body);
  }

  // Returns turf objects to target: explicit selection first, view-only panel target, else auto-target if only 1 turf exists
  _getEffectiveTurfTargets() {
    const rd = this.rd;
    const selTurf = rd.selectedObjects ? [...rd.selectedObjects].filter(o => o.type === 'turf') : [];
    if (selTurf.length > 0) return selTurf;
    // View-only mode: target the clicked turf object
    if (this._viewOnlyPanelTurfObj && (rd.objects || []).includes(this._viewOnlyPanelTurfObj)) {
      return [this._viewOnlyPanelTurfObj];
    }
    const allTurf = (rd.objects || []).filter(o => o.type === 'turf');
    return allTurf.length === 1 ? allTurf : [];
  }

  _updateSledLaneWidthInfo(container) {
    const td = this._turfDefaults;
  }

  _loadTurfDefaultsFromObj(obj) {
    const td = this._turfDefaults;
    td.lines = obj.turfLines || false;
    td.hashes = obj.turfHashes || false;
    td.hashSides = obj.turfHashSides || 'long';
    td.hashSpacing = obj.turfHashSpacing || 3;
    td.centerHashes = obj.turfCenterHashes || false;
    td.rotated = obj.turfMarkingsRotated || false;
    td.lineColor = obj.turfLineColor || '#FFFFFF';
    td.lineOrigin = obj.turfLineOrigin || (obj.turfHalfwayLine === false ? 'offset' : 'center');
    td.yardNumbers = obj.turfYardNumbers || false;
    td.yardNumberSpacing = obj.turfYardNumberSpacing || 10;
    td.yardNumberMode = obj.turfYardNumberMode || 'football';
    td.borderLine = obj.turfBorderLine || false;
    td.borderLineInset = obj.turfBorderLineInset || 4;
    td.borderLineEdges = (obj.turfBorderLineEdges && obj.turfBorderLineEdges.length > 0) ? obj.turfBorderLineEdges.slice() : ['top', 'right', 'bottom', 'left'];
    td.sledLanes = obj.turfSledLanes || false;
    td.sledLaneCount = obj.turfSledLaneCount || 2;
    td.sledLaneEdges = (obj.turfSledLaneEdges && obj.turfSledLaneEdges.length > 0) ? obj.turfSledLaneEdges.slice() : ['right'];
    td.sledLaneInterval = obj.turfSledLaneInterval === undefined ? 5 : obj.turfSledLaneInterval;
    td.sledLaneStartFinish = obj.turfSledLaneStartFinish !== false;
    td.sledLaneNumbers = obj.turfSledLaneNumbers || false;
    td.agilityLadderLength = obj.turfAgilityLadderLength || 15;
    td.logoColor = (obj.turfLogos && obj.turfLogos.length > 0 && obj.turfLogos[0].color) || '';
    // Load line direction — infer from old saves that don't have turfLineDirection
    if (obj.turfLineDirection) {
      td.lineDirection = obj.turfLineDirection;
    } else {
      // Migration: infer from rotated + dimensions
      const w = obj.width || 0, d = obj.depth || 0;
      const rot = obj.turfMarkingsRotated || false;
      // rotated=true means run axis = width; lengthwise if that's the longer axis
      if (!rot && d >= w) td.lineDirection = 'lengthwise';
      else if (rot && w >= d) td.lineDirection = 'lengthwise';
      else td.lineDirection = 'widthwise';
    }
  }

  /** Compute td.rotated from td.lineDirection + turf dimensions.
   *  Lengthwise = run axis along the longer dimension.
   *  When rotated=false, run axis = depth. When rotated=true, run axis = width. */
  _turfComputeRotatedFromDirection() {
    const td = this._turfDefaults;
    const targets = this._getEffectiveTurfTargets() || [];
    if (targets.length === 0) {
      // No turf selected — use simple default
      td.rotated = (td.lineDirection === 'widthwise');
      return;
    }
    const obj = targets[0];
    const w = obj.width || 0, d = obj.depth || 0;
    if (Math.abs(w - d) < 0.01) {
      // Square turf — arbitrary: lengthwise = not rotated
      td.rotated = (td.lineDirection === 'widthwise');
    } else if (td.lineDirection === 'lengthwise') {
      // Run axis should be the longer dimension
      td.rotated = (w > d); // if width is longer, rotate so run axis = width
    } else {
      // Widthwise: run axis should be the shorter dimension
      td.rotated = (d > w); // if depth is longer, rotate so run axis = width (shorter)
    }
  }

  _syncBorderLineEdgeButtons(container) {
    const edges = this._turfDefaults.borderLineEdges || [];
    const dk = document.querySelector('.room-designer')?.classList.contains('dark-mode');
    const bg = dk ? '#1e2636' : '#fff';
    const inactiveColor = dk ? '#4a5060' : '#ccc';
    const activeColor = dk ? '#5ba8e6' : '#006BCC';
    container.querySelectorAll('.rd-borderline-edge-btn').forEach(btn => {
      const active = edges.includes(btn.dataset.edge);
      btn.style.background = bg;
      btn.style.color = active ? activeColor : inactiveColor;
      btn.style.borderColor = active ? activeColor : inactiveColor;
    });
  }

  _syncSledLaneEdgeButtons(container) {
    const edges = this._turfDefaults.sledLaneEdges || [];
    const dk = document.querySelector('.room-designer')?.classList.contains('dark-mode');
    const bg = dk ? '#1e2636' : '#fff';
    const inactiveColor = dk ? '#4a5060' : '#ccc';
    const activeColor = dk ? '#5ba8e6' : '#006BCC';
    container.querySelectorAll('.rd-sledlane-edge-btn').forEach(btn => {
      const active = edges.includes(btn.dataset.edge);
      btn.style.background = bg;
      btn.style.color = active ? activeColor : inactiveColor;
      btn.style.borderColor = active ? activeColor : inactiveColor;
    });
  }

  _updateAgilityLadderInfo(container) {
  }

  _updateDotDrillInfo(container) {
  }

  /** Detect which preset (if any) matches the current _turfDefaults state */
  _detectActivePreset() {
    const td = this._turfDefaults;
    // Plain: all markings off
    if (!td.lines && !td.hashes && !td.borderLine && !td.sledLanes) return 'plain';
    // Football: lines ON (center), hashes ON, no border, no sled lanes, no yard numbers
    if (td.lines && td.lineOrigin === 'center' && td.hashes && (td.hashSpacing || 3) === 3 && !td.yardNumbers &&
        !td.borderLine && !td.sledLanes) return 'football';
    // Training: sled lanes ON (2, right, no yard markers), no start/finish, no border, no lines, no hashes
    if (!td.lines && !td.hashes && td.sledLanes && td.sledLaneCount === 2 &&
        td.sledLaneEdges.length === 1 && td.sledLaneEdges[0] === 'right' &&
        td.sledLaneInterval === 0 && !td.sledLaneStartFinish &&
        !td.borderLine) return 'training';
    return null;
  }

  /** Apply a named preset to _turfDefaults and trigger update */
  _applyTurfPreset(name) {
    const td = this._turfDefaults;
    if (name === 'plain') {
      td.lines = false; td.hashes = false; td.centerHashes = false;
      td.borderLine = false; td.sledLanes = false;
    } else if (name === 'football') {
      td.lines = true; td.lineOrigin = 'center'; td.hashes = true; td.centerHashes = false; td.hashSpacing = 3;
      td.yardNumbers = false; td.yardNumberSpacing = 10; td.yardNumberMode = 'football';
      td.borderLine = false;
      td.sledLanes = false; td.lineDirection = 'lengthwise'; td.lineColor = '#FFFFFF';
      this._turfComputeRotatedFromDirection();
    } else if (name === 'training') {
      td.lines = false; td.hashes = false; td.centerHashes = false;
      td.sledLanes = true; td.sledLaneCount = 2; td.sledLaneEdges = ['right'];
      td.sledLaneInterval = 0; td.sledLaneStartFinish = false; td.sledLaneNumbers = false;
      td.borderLine = false;
      td.lineDirection = 'lengthwise'; td.lineColor = '#FFFFFF';
      this._turfComputeRotatedFromDirection();
    }
  }

  /** Update accordion badge text based on current _turfDefaults */
  _updateAccordionBadges(container) {
    const td = this._turfDefaults;
    // Field Markings badge
    const fieldAccord = container.querySelector('.rd-turf-accord[data-accord="field"]');
    if (fieldAccord) {
      const badge = fieldAccord.querySelector('.rd-turf-accord-badge');
      if (badge) {
        let count = 0;
        if (td.lines) count++;
        if (td.hashes) count++;
        if (td.borderLine) count++;
        badge.textContent = count === 0 ? 'Off' : count + ' active';
      }
    }
    // Training Features badge
    const trainAccord = container.querySelector('.rd-turf-accord[data-accord="training"]');
    if (trainAccord) {
      const badge = trainAccord.querySelector('.rd-turf-accord-badge');
      if (badge) {
        const parts = [];
        if (td.sledLanes) parts.push('Sled Lanes');
        const selTurf = this._getEffectiveTurfTargets();
        if (selTurf.length > 0) {
          const obj = selTurf[0];
          if (obj.turfLogos) {
            const ladders = obj.turfLogos.filter(l => l.type === 'ladder').length;
            const drills = obj.turfLogos.filter(l => l.type === 'dotdrill').length;
            if (ladders > 0) parts.push(ladders + (ladders === 1 ? ' ladder' : ' ladders'));
            if (drills > 0) parts.push(drills + (drills === 1 ? ' drill' : ' drills'));
          }
        }
        badge.textContent = parts.length === 0 ? 'Off' : parts.join(', ');
      }
    }
    // Logo badge
    const logoAccord = container.querySelector('.rd-turf-accord[data-accord="logo"]');
    if (logoAccord) {
      const badge = logoAccord.querySelector('.rd-turf-accord-badge');
      if (badge) {
        const selTurf = this._getEffectiveTurfTargets();
        let logoCount = 0;
        if (selTurf.length > 0 && selTurf[0].turfLogos) logoCount = selTurf[0].turfLogos.length;
        badge.textContent = logoCount === 0 ? 'None' : logoCount + (logoCount === 1 ? ' logo' : ' logos');
      }
    }
  }

  /** Update preset card highlights in a container */
  _updatePresetHighlights(container) {
    // Only target turf preset cards (not platform preset cards which share the base class)
    const turfCards = container.querySelectorAll('.rd-turf-preset-card[data-preset]');
    // Only show active highlights once the user has interacted with turf controls
    if (!this._turfPresetClicked) {
      turfCards.forEach(card => {
        card.classList.remove('rd-turf-preset-card--active');
      });
      return;
    }
    const active = this._detectActivePreset();
    turfCards.forEach(card => {
      card.classList.toggle('rd-turf-preset-card--active', card.dataset.preset === active);
    });
  }

  _syncTurfPanelToSelection(body) {
    const selTurf = this._getEffectiveTurfTargets();
    const hasTurf = selTurf.length > 0;

    // Auto-expand parent categories on Customize tab when relevant object selected
    // Skip when rebuild was triggered by platform controls (prevents cross-category expansion)
    if (this.currentStep === 'finishing' && !this._skipAutoExpand) {
      if (!this._autoExpandedMenus) this._autoExpandedMenus = new Set();
      const rd = window.roomDesigner;
      const selEquip = rd && rd.selectedObjects ? [...rd.selectedObjects].filter(o => o.type === 'equipment') : [];

      // Determine which keys should be auto-expanded now
      const wantKeys = new Set();
      // Only auto-expand turf when explicitly selected (not the implicit single-turf fallback)
      const explicitTurfSel = rd && rd.selectedObjects ? [...rd.selectedObjects].some(o => o.type === 'turf') : false;
      if (explicitTurfSel) wantKeys.add('_cat:turf');
      if (selEquip.length > 0) {
        wantKeys.add('_cat:equipment');
        for (const obj of selEquip) {
          const info = rd._equipmentImages && rd._equipmentImages[obj.equipmentId];
          if (info && info.vendor) wantKeys.add('_vendor:' + info.vendor);
          if (this._isHardwoodPlatformObj(obj)) wantKeys.add('_cat:hardwood');
        }
      }
      // Auto-expand platforms category when a platform is selected
      const explicitPlatSel = rd && rd.selectedObjects ? [...rd.selectedObjects].some(o => o.type === 'platform') : false;
      if (explicitPlatSel) wantKeys.add('_cat:platforms');

      // Close previously auto-expanded menus that are no longer needed
      for (const key of [...this._autoExpandedMenus]) {
        if (!wantKeys.has(key)) {
          this._autoExpandedMenus.delete(key);
          // Collapse it (animated)
          if (key.startsWith('_cat:')) {
            const catName = key.slice(5);
            const catEl = body.querySelector(`.rd-customize-category[data-customize-cat="${catName}"]`);
            if (catEl && !catEl.classList.contains('collapsed')) {
              const catBody = catEl.querySelector(':scope > .rd-product-category-body');
              if (catBody) this._accordionClose(catEl, catBody);
              else catEl.classList.add('collapsed');
              if (this._finishingCollapsed) this._finishingCollapsed[key] = true;
            }
          } else if (key.startsWith('_vendor:')) {
            const vendorName = key.slice(8);
            const vendorEl = body.querySelector(`.rd-finishing-vendor-collapsible[data-vendor-key="${vendorName}"]`);
            if (vendorEl && !vendorEl.classList.contains('collapsed')) {
              const vBody = vendorEl.querySelector(':scope > .rd-finishing-vendor-body');
              if (vBody) this._accordionClose(vendorEl, vBody);
              else vendorEl.classList.add('collapsed');
              if (this._finishingCollapsed) this._finishingCollapsed[key] = true;
            }
          }
        }
      }

      // Auto-expand needed menus (animated)
      for (const key of wantKeys) {
        if (key.startsWith('_cat:')) {
          const catName = key.slice(5);
          const catEl = body.querySelector(`.rd-customize-category[data-customize-cat="${catName}"]`);
          if (catEl && catEl.classList.contains('collapsed')) {
            const catBody = catEl.querySelector(':scope > .rd-product-category-body');
            if (catBody) this._accordionOpen(catEl, catBody);
            else catEl.classList.remove('collapsed');
            if (this._finishingCollapsed) this._finishingCollapsed[key] = false;
            this._autoExpandedMenus.add(key);
          }
        } else if (key.startsWith('_vendor:')) {
          const vendorName = key.slice(8);
          const vendorEl = body.querySelector(`.rd-finishing-vendor-collapsible[data-vendor-key="${vendorName}"]`);
          if (vendorEl && vendorEl.classList.contains('collapsed')) {
            const vBody = vendorEl.querySelector(':scope > .rd-finishing-vendor-body');
            if (vBody) this._accordionOpen(vendorEl, vBody);
            else vendorEl.classList.remove('collapsed');
            if (this._finishingCollapsed) this._finishingCollapsed[key] = false;
            this._autoExpandedMenus.add(key);
          }
        }
      }
    }

    // Disable/enable turf controls based on selection
    body.querySelectorAll('.rd-turf-markings, .rd-turf-logo-section').forEach(el => {
      el.classList.toggle('rd-turf-disabled', !hasTurf);
      el.querySelectorAll('input, select, button').forEach(ctrl => { ctrl.disabled = !hasTurf; });
    });
    if (!hasTurf) {
      // Hide logo preview when nothing selected
      body.querySelectorAll('.rd-turf-logo-preview').forEach(el => { el.style.display = 'none'; });
      return;
    }
    const obj = selTurf[0];
    // Sync turf color dropdown to selected turf's color
    const activeColorId = obj.turfColorId || '';
    body.querySelectorAll('.rd-turf-color-select-product').forEach(sel => {
      sel.value = activeColorId;
    });
    // Read markings state from selected turf into defaults so panel reflects it
    this._turfDefaults.lines = obj.turfLines || false;
    this._turfDefaults.hashes = obj.turfHashes || false;
    this._turfDefaults.hashSides = obj.turfHashSides || 'long';
    this._turfDefaults.hashSpacing = obj.turfHashSpacing || 3;
    this._turfDefaults.centerHashes = obj.turfCenterHashes || false;
    this._turfDefaults.rotated = obj.turfMarkingsRotated || false;
    // Sync line direction from object (with migration for old saves)
    if (obj.turfLineDirection) {
      this._turfDefaults.lineDirection = obj.turfLineDirection;
    } else {
      const w = obj.width || 0, d = obj.depth || 0;
      const rot = obj.turfMarkingsRotated || false;
      if (!rot && d >= w) this._turfDefaults.lineDirection = 'lengthwise';
      else if (rot && w >= d) this._turfDefaults.lineDirection = 'lengthwise';
      else this._turfDefaults.lineDirection = 'widthwise';
    }
    this._turfDefaults.lineColor = obj.turfLineColor || '#FFFFFF';
    this._turfDefaults.lineOrigin = obj.turfLineOrigin || (obj.turfHalfwayLine === false ? 'offset' : 'center');
    this._turfDefaults.yardNumbers = obj.turfYardNumbers || false;
    this._turfDefaults.yardNumberSpacing = obj.turfYardNumberSpacing || 10;
    this._turfDefaults.yardNumberMode = obj.turfYardNumberMode || 'football';
    this._turfDefaults.borderLine = obj.turfBorderLine || false;
    this._turfDefaults.borderLineInset = obj.turfBorderLineInset || 4;
    this._turfDefaults.borderLineEdges = (obj.turfBorderLineEdges && obj.turfBorderLineEdges.length > 0) ? obj.turfBorderLineEdges.slice() : ['top', 'right', 'bottom', 'left'];
    this._turfDefaults.sledLanes = obj.turfSledLanes || false;
    this._turfDefaults.sledLaneCount = obj.turfSledLaneCount || 2;
    this._turfDefaults.sledLaneEdges = (obj.turfSledLaneEdges && obj.turfSledLaneEdges.length > 0) ? obj.turfSledLaneEdges.slice() : ['right'];
    this._turfDefaults.sledLaneInterval = obj.turfSledLaneInterval === undefined ? 5 : obj.turfSledLaneInterval;
    this._turfDefaults.sledLaneStartFinish = obj.turfSledLaneStartFinish !== false;
    this._turfDefaults.sledLaneNumbers = obj.turfSledLaneNumbers || false;
    this._turfDefaults.agilityLadderLength = obj.turfAgilityLadderLength || 15;
    this._turfDefaults.logoColor = (obj.turfLogos && obj.turfLogos.length > 0 && obj.turfLogos[0].color) || '';
    const td = this._turfDefaults;
    const anyOn = td.lines || td.hashes || td.borderLine || td.sledLanes;
    // Update checkboxes
    body.querySelectorAll('.rd-turf-lines-cb').forEach(cb => { cb.checked = td.lines; });
    body.querySelectorAll('.rd-turf-hashes-cb').forEach(cb => { cb.checked = td.hashes; });
    body.querySelectorAll('.rd-turf-center-hashes-cb').forEach(cb => { cb.checked = td.centerHashes; });
    body.querySelectorAll('.rd-turf-center-hashes').forEach(el => { el.style.display = td.hashes ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-hash-spacing-select').forEach(s => { s.value = String(td.hashSpacing); });
    body.querySelectorAll('.rd-turf-hash-spacing').forEach(el => { el.style.display = td.hashes ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-line-origin-select').forEach(s => { s.value = td.lineOrigin; });
    body.querySelectorAll('.rd-turf-yardnums-cb').forEach(cb => { cb.checked = td.yardNumbers; });
    const _ynOff2 = td.lineOrigin === 'offset';
    const _ynOptsVis2 = td.lines && td.yardNumbers && !_ynOff2;
    body.querySelectorAll('.rd-turf-yardnums-spacing').forEach(el => { el.style.display = _ynOptsVis2 ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-yardnums-style').forEach(el => { el.style.display = (_ynOptsVis2 && td.lineOrigin !== 'edge') ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-yardnum-spacing-select').forEach(s => { s.value = String(td.yardNumberSpacing); });
    body.querySelectorAll('.rd-turf-yardnum-mode-select').forEach(s => { s.value = td.yardNumberMode; });
    body.querySelectorAll('.rd-turf-borderline-cb').forEach(cb => { cb.checked = td.borderLine; });
    body.querySelectorAll('.rd-turf-borderline-inset').forEach(el => { el.style.display = td.borderLine ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-borderline-inset-select').forEach(s => { s.value = String(td.borderLineInset); });
    body.querySelectorAll('.rd-turf-borderline-edges').forEach(el => { el.style.display = td.borderLine ? '' : 'none'; });
    this._syncBorderLineEdgeButtons(body);
    // Sled lanes sync
    body.querySelectorAll('.rd-turf-sledlanes-cb').forEach(cb => { cb.checked = td.sledLanes; });
    body.querySelectorAll('.rd-turf-sledlanes-children').forEach(el => { el.style.display = td.sledLanes ? '' : 'none'; });
    body.querySelectorAll('.rd-turf-sledlane-count-select').forEach(s => { s.value = String(td.sledLaneCount); });
    this._syncSledLaneEdgeButtons(body);
    body.querySelectorAll('.rd-turf-sledlane-interval-select').forEach(s => { s.value = String(td.sledLaneInterval); });
    body.querySelectorAll('.rd-turf-sledlane-sf-cb').forEach(cb => { cb.checked = td.sledLaneStartFinish; });
    body.querySelectorAll('.rd-turf-sledlane-nums-cb').forEach(cb => { cb.checked = td.sledLaneNumbers; });
    body.querySelectorAll('.rd-turf-sledlane-nums-row').forEach(el => { el.style.display = td.sledLaneInterval > 0 ? '' : 'none'; });
    this._updateSledLaneWidthInfo(body);
    // Agility ladders sync
    body.querySelectorAll('.rd-turf-agilityladder-length-select').forEach(s => { s.value = String(td.agilityLadderLength); });
    this._updateAgilityLadderInfo(body);
    // Dot drills sync
    this._updateDotDrillInfo(body);
    // Update dropdowns
    body.querySelectorAll('.rd-turf-color-select').forEach(s => { s.value = td.lineColor; });
    body.querySelectorAll('.rd-turf-line-direction-select').forEach(s => { s.value = td.lineDirection; });
    // Show/hide sub-controls
    body.querySelectorAll('.rd-turf-lineorigin').forEach(el => { el.style.display = td.lines ? '' : 'none'; });
    const _ynOff3 = td.lineOrigin === 'offset';
    body.querySelectorAll('.rd-turf-yardnums').forEach(el => {
      el.style.display = td.lines ? '' : 'none';
      el.classList.toggle('rd-turf-disabled', _ynOff3);
      const ynCb = el.querySelector('.rd-turf-yardnums-cb');
      if (ynCb) ynCb.disabled = _ynOff3;
    });
    // Global row visibility
    body.querySelectorAll('.rd-turf-global-row').forEach(el => { el.style.display = anyOn ? '' : 'none'; });
    // Sync logo preview
    this._syncTurfLogoPanelPreview(body, obj);

    // Show/hide view-only logo color row based on whether turf has logos
    const hasLogos = obj.turfLogos && obj.turfLogos.length > 0;
    body.querySelectorAll('.rd-turf-viewonly-logo-color').forEach(el => {
      el.style.cssText = hasLogos ? '' : 'display:none !important';
    });

    // Update preset highlights and accordion badges
    this._updatePresetHighlights(body);
    this._updateAccordionBadges(body);
    // Force render hook to re-evaluate next frame (prevents stale fingerprint after direct calls)
    this._lastTurfSelFp = null;
  }

  _syncTurfLogoPanelPreview(body, obj) {
    const logos = obj && obj.turfLogos;
    const hasLogo = logos && logos.length > 0;
    const selSet = this.rd._turfLogoSelectedIdxs;
    body.querySelectorAll('.rd-turf-logo-preview').forEach(el => {
      if (hasLogo) {
        el.style.display = '';
        // Build thumbnail row
        let thumbsHtml = '';
        for (let i = 0; i < logos.length; i++) {
          const focused = (selSet && selSet.has(i)) ? ' rd-turf-logo-thumb--focused' : '';
          thumbsHtml += '<div class="rd-turf-logo-thumb' + focused + '" data-logo-idx="' + i + '">';
          thumbsHtml += '<img src="' + (logos[i].dataURL || '') + '" />';
          thumbsHtml += '<button class="rd-turf-logo-remove-btn" data-logo-idx="' + i + '" title="Remove logo">&times;</button>';
          thumbsHtml += '</div>';
        }
        el.innerHTML = thumbsHtml;
      } else {
        el.style.display = 'none';
        el.innerHTML = '';
      }
    });
    body.querySelectorAll('.rd-turf-logo-color').forEach(el => {
      el.style.display = hasLogo ? '' : 'none';
    });
    body.querySelectorAll('.rd-turf-logo-color-select').forEach(sel => {
      sel.value = this._turfDefaults.logoColor || '';
    });
  }

  _markOverflowLabels(container, selector) {
    container.querySelectorAll(selector).forEach(el => {
      if (el.classList.contains('overflows')) return; // already marked
      const span = el.querySelector('span');
      if (span && span.scrollWidth > el.clientWidth + 2) {
        el.classList.add('overflows');
        const overflow = span.scrollWidth - el.clientWidth;
        el.style.setProperty('--scroll-overflow', `-${overflow}px`);
        el.style.setProperty('--scroll-duration', `${Math.max(2.5, overflow / 40 + 1.5)}s`);
      }
    });
  }

  _escHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  _escAttr(str) {
    return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Material Calculator ───────────────

  _updateMaterialSummary() {
    const container = document.getElementById('rdFlooringSummary');
    if (!container) return;
    const summary = this._computeFullMaterialSummary();
    container.innerHTML = this._renderMaterialSummary(summary);
    // Wire CTA click → handoff
    const cta = document.getElementById('rdQuoteCTA');
    if (cta) {
      cta.addEventListener('click', () => this._prepareQuoteHandoff());
    }
  }

  _computeFullMaterialSummary() {
    const rd = this.rd;
    const flooredRooms = rd.rooms.filter(r => this._roomHasFlooring(r) && r.closed && r.type === 'add');
    const hasExteriorFlooring = this._exteriorZoneFlooring && this._exteriorZoneFlooring.length > 0;
    const hasTurfObjects = rd.objects && rd.objects.some(o => o.type === 'turf');
    if (flooredRooms.length === 0 && !hasExteriorFlooring && !hasTurfObjects) return { groups: [], grandArea: 0 };

    // Group by product+color
    const groupMap = new Map();

    // Helper: add one polygon's flooring to the summary
    const addFlooringEntry = (room, poly, productId, colorId, floorAngle, label, cachedLayout) => {
      const key = `${productId}::${colorId}`;
      const product = this._getProduct(productId);
      if (!product) return;

      const netArea = Math.abs(this._polygonArea(poly));
      let materialArea = 0;
      const pieces = [];
      let tileCount = 0;

      if (product.shape === 'paver') {
        // Formula-based: floor area × pavers-per-sq-ft, plus 5% extra for mistakes
        const paverShapeArea = product.widthFt * product.heightFt * 0.86924;
        tileCount = Math.ceil(netArea / paverShapeArea);
        const extra = Math.max(1, Math.ceil(tileCount * 0.05));
        materialArea = (tileCount + extra) * paverShapeArea;
      } else if (product.type === 'tile') {
        let layout = cachedLayout;
        if (!layout || !layout.tiles) {
          const geo = room ? rd._getRoomGeoCache(room) : { roomPolygon: poly, cutoutPolys: [], holePolygons: [] };
          const _te = room ? this._getTurfExclusionsForRoom(room, geo) : [];
          if (floorAngle !== 0) {
            const centroid = this._getPolygonCentroid(poly);
            const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
            const rExcl = _te.map(e => this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y));
            layout = this._computeTileLayout(room, geo, product, rPoly, rExcl);
          } else {
            layout = this._computeTileLayout(room, geo, product, poly, _te);
          }
        }
        tileCount = layout.tiles.length;
        materialArea = tileCount * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
      } else {
        let layout = cachedLayout;
        if (!layout) {
          const geo = room ? rd._getRoomGeoCache(room) : { roomPolygon: poly, cutoutPolys: [], holePolygons: [] };
          const _te = room ? this._getTurfExclusionsForRoom(room, geo) : [];
          if (floorAngle !== 0) {
            const centroid = this._getPolygonCentroid(poly);
            const rPoly = this._rotatePolygon(poly, -floorAngle, centroid.x, centroid.y);
            const rExcl = _te.map(e => this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y));
            layout = this._computeRollLayout(room, geo, product, rPoly, rExcl);
          } else {
            layout = this._computeRollLayout(room, geo, product, poly, _te.length > 0 ? _te : []);
          }
        }
        for (const strip of layout.strips) {
          for (const roll of strip.rolls) {
            materialArea += strip.width * roll.length;
            pieces.push({ width: strip.width, length: roll.length });
          }
        }
      }

      if (!groupMap.has(key)) {
        const color = product.colors.find(c => c.id === colorId);
        groupMap.set(key, {
          productId, colorId, product, color,
          rooms: [], totalFloorArea: 0, totalMaterialArea: 0, pieces: [], piecesBySpace: new Map(), totalTiles: 0
        });
      }
      const g = groupMap.get(key);
      g.rooms.push(label);
      g.totalFloorArea += netArea;
      g.totalMaterialArea += materialArea;
      g.pieces.push(...pieces);
      // Keyed by the ROOM (zones of one space share its rolls, as per-room packing does)
      const _spaceKey = room || label;
      if (!g.piecesBySpace.has(_spaceKey)) g.piecesBySpace.set(_spaceKey, []);
      g.piecesBySpace.get(_spaceKey).push(...pieces);
      g.totalTiles += tileCount;
    };

    for (const room of flooredRooms) {
      const geo = rd._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      const roomLabel = room.name || `Space ${rd.rooms.indexOf(room) + 1}`;

      const zones = this._getZonesForRoom(room, geo);
      if (zones.length > 1) {
        // Multi-zone: iterate zones
        for (let zi = 0; zi < zones.length; zi++) {
          const zone = zones[zi];
          const zf = this._getZoneFlooring(room, zone);
          if (!zf) continue;
          const cached = this._getZoneLayoutCache(room, zi);
          addFlooringEntry(room, zone.polygon, zf.productId, zf.colorId,
            zf.flooringAngle || 0, `${roomLabel} Zone ${zi + 1}`,
            cached ? cached.layout : null);
        }
      } else {
        // Single zone: use room.flooring
        if (!room.flooring) continue;
        const { productId, colorId } = room.flooring;
        // Compute net room area for single-zone (subtract holes + turf)
        const roomArea = this._polygonArea(geo.roomPolygon);
        let holeArea = 0;
        for (const hp of [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])]) {
          if (hp && hp.length >= 3) holeArea += this._polygonArea(hp);
        }
        const _sumTurfExcl = this._getTurfExclusionsForRoom(room, geo);
        for (const tp of _sumTurfExcl) {
          if (tp && tp.length >= 3) holeArea += this._polygonArea(tp);
        }
        const product = this._getProduct(productId);
        if (!product) continue;
        const netRoomArea = Math.abs(roomArea) - Math.abs(holeArea);
        let materialArea = 0;
        const pieces = [];
        let tileCount = 0;

        // Validate layout cache — stale if geo/product/color/angle/turf/optMode changed
        const _floorAngle = room.flooringAngle || 0;
        const _sumTurfFP = this._getTurfFingerprint(room, geo);
        const _layoutValid = room._flooringLayout
          && room._flooringLayoutGeo === geo
          && room._flooringLayoutProduct === productId
          && room._flooringLayoutColor === colorId
          && room._flooringLayoutAngle === _floorAngle
          && room._flooringLayoutOptMode === this._rollOptimizationMode
          && room._flooringLayoutTurfFP === _sumTurfFP;
        if (product.shape === 'paver') {
          // Formula-based: floor area × pavers-per-sq-ft, plus 5% extra for mistakes
          const paverShapeArea = product.widthFt * product.heightFt * 0.86924;
          tileCount = Math.ceil(netRoomArea / paverShapeArea);
          const paverExtra = Math.max(1, Math.ceil(tileCount * 0.05));
          materialArea = (tileCount + paverExtra) * paverShapeArea;
        } else if (product.type === 'tile') {
          let layout = _layoutValid ? room._flooringLayout : null;
          if (layout && layout.tiles) {
            tileCount = layout.tiles.length;
          } else {
            const tileArea = (product.widthFt || 2) * (product.heightFt || product.widthFt || 2);
            tileCount = Math.ceil(netRoomArea / tileArea);
          }
          materialArea = tileCount * ((product.widthFt || 2) * (product.heightFt || product.widthFt || 2));
        } else {
          let layout = _layoutValid ? room._flooringLayout : null;
          if (!layout) {
            const floorAngle = room.flooringAngle || 0;
            if (floorAngle !== 0) {
              const centroid = this._getPolygonCentroid(geo.roomPolygon);
              const exclusions = [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._sumTurfExcl];
              const rPoly = this._rotatePolygon(geo.roomPolygon, -floorAngle, centroid.x, centroid.y);
              const rExcl = exclusions.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, centroid.x, centroid.y) : e);
              layout = this._computeRollLayout(room, geo, product, rPoly, rExcl);
            } else {
              const _msExcl = _sumTurfExcl.length > 0 ? [...(geo.cutoutPolys || []), ...(geo.holePolygons || []), ..._sumTurfExcl] : null;
              layout = _msExcl ? this._computeRollLayout(room, geo, product, null, _msExcl) : this._computeRollLayout(room, geo, product);
            }
          }
          for (const strip of layout.strips) {
            for (const roll of strip.rolls) {
              materialArea += strip.width * roll.length;
              pieces.push({ width: strip.width, length: roll.length });
            }
          }
        }

        const key = `${productId}::${colorId}`;
        if (!groupMap.has(key)) {
          const color = product.colors.find(c => c.id === colorId);
          groupMap.set(key, {
            productId, colorId, product, color,
            rooms: [], totalFloorArea: 0, totalMaterialArea: 0, pieces: [], piecesBySpace: new Map(), totalTiles: 0
          });
        }
        const g = groupMap.get(key);
        g.rooms.push(roomLabel);
        g.totalFloorArea += netRoomArea;
        g.totalMaterialArea += materialArea;
        g.pieces.push(...pieces);
        if (!g.piecesBySpace.has(room)) g.piecesBySpace.set(room, []);
        g.piecesBySpace.get(room).push(...pieces);
        g.totalTiles += tileCount;
      }
    }

    // Exterior zones with flooring
    if (hasExteriorFlooring) {
      const extZones = this._getExteriorZones();
      for (let ei = 0; ei < extZones.length; ei++) {
        const zone = extZones[ei];
        const ef = this._getExteriorZoneFlooring(zone);
        if (!ef) continue;
        addFlooringEntry(null, zone.polygon, ef.productId, ef.colorId,
          ef.flooringAngle || 0, `Exterior Zone ${ei + 1}`, null);
      }
    }

    /* CUSTOM_PLANNER_BY_CLAUDE — Turf objects in material summary */
    if (hasTurfObjects) {
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        const key = `${obj.turfProductId}::${obj.turfColorId}`;
        const product = this._getProduct(obj.turfProductId);
        if (!product) continue;
        const area = this._turfPieceArea(obj);
        if (!groupMap.has(key)) {
          const color = product.colors.find(c => c.id === obj.turfColorId);
          groupMap.set(key, {
            productId: obj.turfProductId, colorId: obj.turfColorId, product, color,
            rooms: [], totalFloorArea: 0, totalMaterialArea: 0, pieces: [], piecesBySpace: new Map(), totalTiles: 0
          });
        }
        const g = groupMap.get(key);
        g.rooms.push(`Turf ${this.rd.formatMeasurement(obj.width)} × ${this.rd.formatMeasurement(obj.depth)}`);
        g.totalFloorArea += area;
        g.totalMaterialArea += area;
      }
    }
    /* END CUSTOM_PLANNER_BY_CLAUDE */

    // Build purchase summaries per group (no cost)
    const groups = [];
    let grandArea = 0;

    for (const g of groupMap.values()) {
      // Build purchase lines
      const purchaseRolls = [];
      let purchaseArea = g.totalMaterialArea; // default for tiles/pavers
      if (g.product.shape === 'paver' && g.totalTiles > 0) {
        const extra = Math.max(1, Math.ceil(g.totalTiles * 0.05));
        purchaseRolls.push({ label: `${g.totalTiles} pavers + ${extra} extra for mistakes`, count: g.totalTiles + extra });
        purchaseArea = (g.totalTiles + extra) * g.product.widthFt * g.product.heightFt * 0.86924;
      } else if (g.product.type === 'tile' && g.totalTiles > 0) {
        const _step = g.product.qtyStep || 1;
        const _tileUnit = g.product.shape === 'plank' ? 'planks' : 'tiles';
        const _mistakes = this._mistakeExtra(g.product, g.totalTiles);
        const _withExtra = g.totalTiles + _mistakes;
        const _purchaseQty = _step > 1 ? Math.ceil(_withExtra / _step) * _step : _withExtra;
        const _extraTotal = _purchaseQty - g.totalTiles;
        if (_step > 1) {
          const _cartons = _purchaseQty / _step;
          purchaseRolls.push({ label: `${_cartons} carton${_cartons !== 1 ? 's' : ''} of ${_step} = ${_purchaseQty} total ${_tileUnit}`, count: _purchaseQty });
          if (_extraTotal > 0) purchaseRolls.push({ label: `${_extraTotal} ${_tileUnit} ${_mistakes > 0 ? 'included for mistakes' : 'left over from the last carton'}`, count: 0 });
        } else {
          purchaseRolls.push({ label: _extraTotal > 0 ? `${g.totalTiles} ${_tileUnit} + ${_extraTotal} extra for mistakes` : `${g.totalTiles} ${_tileUnit}`, count: _purchaseQty });
        }
        purchaseArea = _purchaseQty * ((g.product.widthFt || 2) * (g.product.heightFt || g.product.widthFt || 2));
      } else {
        // Rep mode packs each space on its own unless roll sharing is on for this material
        const packed = this._packRollPurchase(this._rollPieceSetsBySpace(g.productId + '::' + g.colorId, [...g.piecesBySpace.values()]), g.product);
        purchaseArea = packed.purchaseArea;
        purchaseRolls.push(...packed.purchaseRolls);
      }

      // Waste computed from purchase area
      const wastePercent = g.totalFloorArea > 0
        ? Math.round(((purchaseArea - g.totalFloorArea) / g.totalFloorArea) * 100)
        : 0;

      // MOQ info — per-color from variant metafield, fallback to block-level
      const colorMoq = g.color ? (g.color.moq || 0) : 0;
      const effectiveMoq = colorMoq > 0 ? colorMoq : (g.product.moqFallbackQty || 0);
      const moqUnitRaw = g.product.moqUnit || '';
      const moqUnit = (effectiveMoq === 1 && moqUnitRaw.endsWith('s')) ? moqUnitRaw.slice(0, -1) : moqUnitRaw;
      let moqNote = '';
      let underMoq = false;
      let atMoq = false;
      if (effectiveMoq > 0) {
        const unitLower = moqUnitRaw.toLowerCase().replace(/s$/, '');
        let userQty = 0;
        if (unitLower.includes('sq') || unitLower.includes('square')) {
          userQty = purchaseArea;
        } else if (unitLower.includes('tile') || unitLower.includes('paver')) {
          userQty = g.totalTiles;
        } else if (unitLower.includes('roll')) {
          userQty = purchaseRolls.reduce((sum, r) => sum + r.count, 0);
        } else {
          userQty = purchaseArea;
        }
        underMoq = userQty < effectiveMoq;
        if (underMoq) {
          moqNote = `Minimum order: ${effectiveMoq} ${moqUnit}`.trim();
        } else if (userQty <= effectiveMoq * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${effectiveMoq} ${moqUnit} minimum`.trim();
        }
      }
      // Fallback: if no MOQ configured but purchase equals a single minimum roll
      if (!moqNote && g.product.type === 'roll' && purchaseArea > 0) {
        const minRollArea = (g.product.minLengthFt || 25) * (g.product.widthFt || 4);
        if (purchaseArea <= minRollArea * 1.05) {
          atMoq = true;
          moqNote = `Order meets ${Math.round(minRollArea)} sq ft minimum`;
        }
      }

      grandArea += purchaseArea;

      groups.push({
        productId: g.productId,
        colorId: g.colorId,
        productName: g.product.name,
        colorName: g.color ? g.color.name : '',
        colorSwatch: g.color ? (g.color.swatch || g.color.hex) : '#888',
        rooms: g.rooms,
        floorArea: g.totalFloorArea,
        materialArea: purchaseArea,
        wastePercent,
        purchaseRolls,
        moqNote,
        underMoq,
        atMoq
      });
    }

    return { groups, grandArea };
  }

  _polygonArea(poly) {
    let area = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      area += (poly[j].x + poly[i].x) * (poly[j].y - poly[i].y);
    }
    return area / 2;
  }

  _renderMaterialSummary(summary) {
    // All per-product detail now lives in per-room space cards.
    // This section renders the Quote CTA when rooms have flooring.
    if (summary.groups.length === 0) return '';

    // Portal mode: hide CTA — reps use the tab instead
    if (this.rd._portalMode) return '';

    const _rep = this.rd._repMode;
    let html = `<div class="rd-material-quote-cta" id="rdQuoteCTA">`;
    html += `<div class="rd-material-quote-cta-label">${_rep ? 'Ready to create a quote for this floor plan?' : 'Ready to get a quote on your floor plan?'}</div>`;
    html += `<div class="rd-material-quote-cta-text">${_rep ? 'Create a Quote' : 'Get a Quote'}</div>`;
    html += `</div>`;
    return html;
  }

  _drawSnappedWallHighlight() {
    if (!this._snappedWalls || this.currentStep !== 'floor') return;
    const rd = this.rd;
    const ctx = rd.ctx;
    const scale = rd.basePixelsPerFoot * rd.zoomLevel;

    ctx.save();
    ctx.strokeStyle = '#28C85A';
    const wallPx = rd.getWallPixelWidth(scale);
    ctx.lineWidth = Math.max(wallPx, 4) / scale;
    ctx.lineCap = 'round';
    ctx.shadowColor = 'rgba(40, 200, 90, 0.5)';
    ctx.shadowBlur = 8 / scale;
    ctx.beginPath();
    for (const w of this._snappedWalls) {
      ctx.moveTo(w.start.x, w.start.y);
      ctx.lineTo(w.end.x, w.end.y);
    }
    ctx.stroke();
    ctx.restore();
  }

  /* ── PHASE 5B: ITEM COLLECTION + QUOTE HANDOFF ── */

  /**
   * Collect flooring + equipment items from the current design.
   * Reusable by both _prepareQuoteHandoff() and rep PDF handlers.
   * Returns { flooringItems, equipmentItems, turfItems, flooringSwatches, spaceSummary, totalArea }
   */
  /* ── Seam Plan PDF — Data Collector ──────────────────────────────── */

  _computeSeamPlanData(room, colorOffset = 0, opts = {}) {
    const rd = this.rd;
    if (!room.closed || room.type !== 'add') return null;
    const geo = rd._getRoomGeoCache(room);
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return null;

    const zones = this._getZonesForRoom(room, geo);
    // Collect divider segments with type tags for rendering (wall-type = thick, line-type = thin)
    const _allDivSegs = this._getDividerSegmentsForRoom(room, geo);
    const _wallDivSegs = new Set(this._getWallDividerSegmentsForRoom(room, geo));
    const dividerSegs = _allDivSegs.map(seg => ({ start: seg.start, end: seg.end, isWall: _wallDivSegs.has(seg) }));
    const zoneEntries = [];
    const allPieces = [];

    // Cross-zone grid alignment: compute room-level gridOrigin + rotation centroid
    // so strip positions match the live renderer exactly (prevents seam misalignment)
    const _isMultiZone = zones.length > 1;
    const _floorAng = room.flooringAngle || 0;
    const _roomCentroid = this._getPolygonCentroid(poly);
    let _gridOrigin = null;
    if (_isMultiZone) {
      if (_floorAng !== 0) {
        const rRoomPoly = this._rotatePolygon(poly, -_floorAng, _roomCentroid.x, _roomCentroid.y);
        let rmX = Infinity, rmY = Infinity;
        for (const p of rRoomPoly) { rmX = Math.min(rmX, p.x); rmY = Math.min(rmY, p.y); }
        _gridOrigin = { minX: rmX, minY: rmY };
      } else {
        let rmX = Infinity, rmY = Infinity;
        for (const p of poly) { rmX = Math.min(rmX, p.x); rmY = Math.min(rmY, p.y); }
        _gridOrigin = { minX: rmX, minY: rmY };
      }
    }

    // Helper: collect pieces + layout for one zone (or whole room if zoneIdx < 0).
    // zoneHoles: the zone's island holes — free-standing divider loops carved out of
    // this zone. computeRoomZones() reports them, and they're passed as exclusions so
    // this zone's rolls stop at the island edge instead of running through it.
    const processZone = (zonePoly, product, color, floorAngle, zoneIdx, zoneHoles = []) => {
      const zoneGeo = zoneIdx >= 0
        ? { roomPolygon: zonePoly, cutoutPolys: [], holePolygons: zoneHoles, area: Math.abs(RoomDesignerMath.signedPolygonArea(zonePoly)), fp: (geo.fp || '') + ':z' + zoneIdx + ':o' + zoneHoles.length }
        : geo;
      // Seam plan is always rolls — exclude platforms from layout computation
      const turfExcl = this._getTurfExclusionsForRoom ? this._getTurfExclusionsForRoom(room, geo, false) : [];
      // Always exclude cutouts/holes (defensive; covers cases where the zone
      // polygon doesn't subtract them — same reason as otherZonePolys above).
      const cutExcl = [...(geo.cutoutPolys || []), ...(geo.holePolygons || [])];
      const allExcl = [...cutExcl, ...turfExcl, ...zoneHoles];

      let layout;
      if (zoneIdx >= 0) {
        const cached = this._getZoneLayoutCache(room, zoneIdx);
        const cacheKey = `${product.name}:${color ? color.id : ''}:${floorAngle}:${zoneGeo.fp}:${this._rollOptimizationMode}`;
        if (cached && cached.cacheKey === cacheKey) layout = cached.layout;
      } else {
        layout = room._flooringLayout;
      }
      // Rotation center — needed for layout computation AND screen-space visibility check
      const rotCenter = (_isMultiZone && zoneIdx >= 0) ? _roomCentroid : this._getPolygonCentroid(zonePoly);
      if (!layout) {
        if (floorAngle !== 0) {
          const rPoly = this._rotatePolygon(zonePoly, -floorAngle, rotCenter.x, rotCenter.y);
          const rExcl = allExcl.map(e => (e && e.length >= 3) ? this._rotatePolygon(e, -floorAngle, rotCenter.x, rotCenter.y) : e);
          layout = this._computeRollLayout(room, zoneGeo, product, rPoly, rExcl, _gridOrigin);
        } else {
          layout = this._computeRollLayout(room, zoneGeo, product, zonePoly, allExcl, _gridOrigin);
        }
      }
      if (!layout || !layout.strips || layout.strips.length === 0) return;

      // Screen-space visibility helper: for rotated rooms, check if a piece has any
      // visible fill by sampling points in layout space, rotating back to screen space,
      // and testing against the original (unrotated) zone polygon. Returns the count of
      // sample points inside the polygon and the effective in-room length along the Y axis.
      const _screenVis = (stripX, rollY, width, pieceLen) => {
        if (floorAngle === 0) return { count: 1, inRoom: pieceLen }; // no rotation, always visible
        const cos = Math.cos(floorAngle), sin = Math.sin(floorAngle);
        const cx = rotCenter.x, cy = rotCenter.y;
        const stepsX = 3, stepsY = 8;
        let count = 0, yMin = Infinity, yMax = -Infinity;
        for (let xi = 0; xi <= stepsX; xi++) {
          const px = stripX + width * xi / stepsX;
          for (let yi = 0; yi <= stepsY; yi++) {
            const py = rollY + pieceLen * yi / stepsY;
            // Rotate back by +floorAngle (reverse of the -floorAngle used for layout)
            const dx = px - cx, dy = py - cy;
            const sx = cx + dx * cos - dy * sin;
            const sy = cy + dx * sin + dy * cos;
            if (RoomDesignerMath.pointInPolygon(sx, sy, zonePoly)) {
              count++;
              if (py < yMin) yMin = py;
              if (py > yMax) yMax = py;
            }
          }
        }
        // Effective in-room Y span from sample hits
        const span = count > 0 ? (yMax - yMin) : 0;
        return { count, inRoom: span };
      };

      const pieces = [];
      for (let si = 0; si < layout.strips.length; si++) {
        const strip = layout.strips[si];
        for (let segi = 0; segi < strip.segments.length; segi++) {
          const seg = strip.segments[segi];
          for (let ri = 0; ri < seg.rolls.length; ri++) {
            const roll = seg.rolls[ri];
            const cutLen = roll.naturalLength != null ? roll.naturalLength : roll.length;
            const inRoom = roll.inRoomLength != null ? roll.inRoomLength : cutLen;
            // Skip pieces with negligible layout-space in-room coverage (< 1")
            if (inRoom < 1 / 12) continue;
            // Screen-space visibility check: with compass rotation, a piece can be
            // "in the room" in layout space but entirely outside the room boundary
            // visually (its fill is 100% overage). Skip these — their material folds
            // into the roll's waste. Also use screen-space in-room span when it's
            // tighter than the layout-space inRoomLength.
            const vis = _screenVis(strip.x, roll.y, strip.width, roll.length);
            if (vis.count === 0) continue;
            // Effective piece length = tighter of naturalLength and layout inRoomLength.
            // vis.inRoom is too coarse (8-step sampling) for accurate length — only used
            // for the skip check above (vis.count === 0 catches phantom pieces).
            const effectiveLen = Math.min(cutLen, inRoom);
            // Final sanity: skip if effective length is negligible
            if (effectiveLen < 1 / 12) continue;
            pieces.push({ stripIndex: si, segIndex: segi, rollIndex: ri, stripX: strip.x, rollY: roll.y, width: strip.width, length: effectiveLen, fullLength: cutLen, rollLength: roll.length, inRoomLength: inRoom, ref: '', rollLetter: '', pieceNum: 0 });
          }
        }
      }
      if (pieces.length === 0) return;

      zoneEntries.push({
        pieces, layout, floorAngle,
        _key: this._rollShareKey(product, color), // roll sharing (rep): matched against opts.deferKeys
        product: { name: product.name, color: color ? color.name : '', widthFt: product.widthFt, maxLengthFt: product.maxLengthFt || 50 },
        stripWidth: product.widthFt,
        minLengthFt: product.minLengthFt || 25,
        centroid: (_isMultiZone && zoneIdx >= 0) ? _roomCentroid : this._getPolygonCentroid(zonePoly),
        zonePoly: zonePoly,
      });
      allPieces.push(...pieces);
    };

    // Collect non-roll zones (turf, tile, paver) for diagram labeling
    const nonRollZones = [];

    // Multi-zone or single-zone
    if (zones.length > 1 && room.zoneFlooring && room.zoneFlooring.length > 0) {
      for (let zi = 0; zi < zones.length; zi++) {
        const zone = zones[zi];
        const zf = this._getZoneFlooring(room, zone);
        if (!zf) continue;
        const product = this._getProduct(zf.productId);
        if (!product) continue;
        const cats = product.categories || [];
        const isTurfProduct = product.type === 'turf' || cats.includes('Turf');
        if (product.shape !== 'roll' || isTurfProduct) {
          // Determine display label from product shape/categories. Turf wins over Tile when both apply
          // (e.g. a turf tile listed under both Turf and Tiles is labeled TURF in the diagram).
          let label = 'OTHER';
          if (isTurfProduct) label = 'TURF';
          else if (product.shape === 'paver' || cats.includes('Pavers')) label = 'PAVERS';
          else if (product.shape === 'square-tile' || product.shape === 'puzzle-tile' || cats.includes('Tiles')) label = 'TILES';
          else if (product.shape === 'plank' || cats.includes('Planks')) label = 'PLANKS';
          // Compute bounding dims from zone polygon
          let _zMinX = Infinity, _zMinY = Infinity, _zMaxX = -Infinity, _zMaxY = -Infinity;
          for (const p of zone.polygon) { _zMinX = Math.min(_zMinX, p.x); _zMinY = Math.min(_zMinY, p.y); _zMaxX = Math.max(_zMaxX, p.x); _zMaxY = Math.max(_zMaxY, p.y); }
          const _zDimLabel = rd.formatMeasurement(_zMaxX - _zMinX) + ' \u00d7 ' + rd.formatMeasurement(_zMaxY - _zMinY);
          nonRollZones.push({ polygon: zone.polygon, label, productName: product.name, dimLabel: _zDimLabel });
          continue;
        }
        const color = product.colors ? product.colors.find(c => c.id === zf.colorId) : null;
        // Island holes as exclusions — the zone's rolls stop at a free-standing
        // divider loop instead of running through it.
        processZone(zone.polygon, product, color, zf.flooringAngle || 0, zi, this._zoneHoles(zone));
      }
    } else {
      const f = room.flooring;
      if (!f) return null;
      const product = this._getProduct(f.productId);
      if (!product || product.shape !== 'roll') return null;
      const color = product.colors ? product.colors.find(c => c.id === f.colorId) : null;
      processZone(poly, product, color, room.flooringAngle || 0, -1);
    }

    // Collect turf objects (from rd.objects[]) that are inside this room
    if (rd.objects && rd.objects.length > 0) {
      for (const obj of rd.objects) {
        if (obj.type !== 'turf') continue;
        if (!rd.pointInPolygon(obj.x, obj.y, poly)) continue;
        // Rotation-aware 4-corner polygon (same math as _getTurfExclusionsForRoom)
        const hw = obj.width / 2, hd = obj.depth / 2;
        const rot = obj.rotation || 0;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const turfPoly = [
          { x: obj.x + (-hw) * cos - (-hd) * sin, y: obj.y + (-hw) * sin + (-hd) * cos },
          { x: obj.x + ( hw) * cos - (-hd) * sin, y: obj.y + ( hw) * sin + (-hd) * cos },
          { x: obj.x + ( hw) * cos - ( hd) * sin, y: obj.y + ( hw) * sin + ( hd) * cos },
          { x: obj.x + (-hw) * cos - ( hd) * sin, y: obj.y + (-hw) * sin + ( hd) * cos },
        ];
        let productName = 'Turf';
        const turfProduct = this._getProduct(obj.turfProductId);
        if (turfProduct) {
          const colorObj = obj.turfColorId && turfProduct.colors
            ? turfProduct.colors.find(c => c.id === obj.turfColorId) : null;
          productName = turfProduct.name + (colorObj ? ' \u2014 ' + colorObj.name : '');
        }
        const turfDimLabel = rd.formatMeasurement(obj.width) + ' \u00d7 ' + rd.formatMeasurement(obj.depth);
        nonRollZones.push({ polygon: turfPoly, label: 'TURF', productName, _obj: obj, dimLabel: turfDimLabel });
      }
      // Collect platform objects inside this room
      for (const obj of rd.objects) {
        if (obj.type !== 'platform') continue;
        if (!rd.pointInPolygon(obj.x, obj.y, poly)) continue;
        const hw = obj.width / 2, hd = obj.depth / 2;
        const rot = obj.rotation || 0;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const platPoly = [
          { x: obj.x + (-hw) * cos - (-hd) * sin, y: obj.y + (-hw) * sin + (-hd) * cos },
          { x: obj.x + ( hw) * cos - (-hd) * sin, y: obj.y + ( hw) * sin + (-hd) * cos },
          { x: obj.x + ( hw) * cos - ( hd) * sin, y: obj.y + ( hw) * sin + ( hd) * cos },
          { x: obj.x + (-hw) * cos - ( hd) * sin, y: obj.y + (-hw) * sin + ( hd) * cos },
        ];
        const dimLabel = rd.formatMeasurement(obj.width) + ' \u00d7 ' + rd.formatMeasurement(obj.depth);
        nonRollZones.push({ polygon: platPoly, label: 'PLATFORM', productName: dimLabel, _obj: obj, dimLabel });
      }
    }

    if (zoneEntries.length === 0 || allPieces.length === 0) return null;

    // Assign color indices to zone entries for consistent product differentiation.
    // colorOffset shifts this room's whole palette block so each SPACE in a multi-space
    // plan gets its own colors (same product in a different space is still a different
    // color) — the caller advances the offset by each room's colorCount.
    const productColorMap = new Map();
    let nextColorIdx = 0;
    for (const ze of zoneEntries) {
      const key = ze.product ? `${ze.product.name}|${ze.product.color}` : 'default';
      if (!productColorMap.has(key)) {
        productColorMap.set(key, colorOffset + nextColorIdx);
        nextColorIdx++;
      }
      ze._colorIdx = productColorMap.get(key);
    }

    // Bin-pack pieces per zone entry (each zone = distinct product/color)
    // This keeps Charcoal and Medium Gray rolls separate (can't cut two colors from one roll)
    const purchaseRolls = [];
    const productGroups = [];
    let letterIdx = 0;
    // Roll sharing (rep): materials in opts.deferKeys are packed later ACROSS spaces by
    // _computeSeamPlanAll, so they're handed back unpacked in deferredEntries.
    const deferKeys = opts.deferKeys || null;
    const deferredEntries = [];

    for (const ze of zoneEntries) {
      if (deferKeys && deferKeys.has(ze._key)) { deferredEntries.push(ze); continue; }
      const packed = this._packSeamZoneEntry(ze, letterIdx);
      letterIdx = packed.letterIdx;
      purchaseRolls.push(...packed.groupRolls);
      productGroups.push({ product: ze.product, purchaseRolls: packed.groupRolls, colorIdx: ze._colorIdx });
    }

    return {
      pieces: allPieces,
      purchaseRolls,
      product: zoneEntries[0].product,
      productGroups,
      zoneEntries,
      dividerSegs,
      nonRollZones,
      layout: zoneEntries[0].layout,
      floorAngle: zoneEntries[0].floorAngle,
      colorOffset,                       // this room's palette start (for the single-zone canvas fallback)
      colorCount: productColorMap.size,  // distinct colors this room consumed — caller advances colorOffset by this
      letterCount: letterIdx,            // roll letters this room used (a shared pack letters on from here)
      deferredEntries,                   // roll-sharing zone entries left for _computeSeamPlanAll
    };
  }

  /** Bin-pack one seam-plan zone entry into lettered purchase rolls, starting at
   *  letter index letterStart. Returns { groupRolls, letterIdx } (the next free index).
   *  Pieces get their ref (A1, A2…) written onto them, which the diagram draws. */
  _packSeamZoneEntry(ze, letterStart) {
    let letterIdx = letterStart;
    const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const maxLen = ze.product.maxLengthFt || 50;
    const minLen = ze.minLengthFt || 25;
    const optMode = this._rollOptimizationMode;
    const uniform = optMode === 'uniform';
    let binCapacity = maxLen;
    if (uniform && ze.pieces.length > 0) {
      // Use fullLength (original naturalLength) for purchase roll sizing — not effectiveLen
      const maxPiece = ze.pieces.reduce((m, p) => Math.max(m, p.fullLength || p.length), 0);
      binCapacity = Math.max(minLen, this._ceilHalfFoot(maxPiece));
    }

    // Bin-pack with piece tracking (needed for roll lettering).
    // Uses fullLength (original naturalLength) for physical roll sizing — the roll
    // must be long enough to contain the full material including overage portions.
    // piece.length (effectiveLen) is only for the cutting guide display.
    const _fl = (p) => p.fullLength || p.length;
    const _ffdBins = (items, cap) => {
      const bins = [];
      for (const piece of items) {
        let placed = false;
        const fl = _fl(piece);
        for (const bin of bins) {
          if (bin.remaining >= fl) { bin.remaining -= fl; bin.pieces.push(piece); placed = true; break; }
        }
        if (!placed) bins.push({ remaining: cap - fl, pieces: [piece] });
      }
      return bins;
    };

    let bins;
    if (optMode === 'minMaterial' && ze.pieces.length > 1) {
      // Try 3 strategies, pick lowest total purchase
      const _total = (bs) => bs.reduce((s, b) => {
        const used = b.pieces.reduce((a, p) => a + _fl(p), 0);
        return s + Math.max(minLen, Math.ceil(used));
      }, 0);
      const asc = [...ze.pieces].sort((a, b) => _fl(a) - _fl(b));
      const desc = [...ze.pieces].sort((a, b) => _fl(b) - _fl(a));
      // A: ascending fill-to-min
      const binsA = (() => {
        const bins = []; let cur = null;
        for (const p of asc) {
          const fl = _fl(p);
          if (!cur) cur = { remaining: maxLen, pieces: [] };
          if (cur.remaining >= fl) {
            cur.remaining -= fl; cur.pieces.push(p);
            const used = cur.pieces.reduce((a, x) => a + _fl(x), 0);
            if (used >= minLen) { bins.push(cur); cur = null; }
          } else {
            if (cur.pieces.length > 0) bins.push(cur);
            cur = { remaining: maxLen - fl, pieces: [p] };
            if (fl >= minLen) { bins.push(cur); cur = null; }
          }
        }
        if (cur && cur.pieces.length > 0) bins.push(cur);
        return bins;
      })();
      // B: FFD
      const binsB = _ffdBins(desc, maxLen);
      // C: balanced pairs
      const binsC = (() => {
        const sorted = [...ze.pieces].sort((a, b) => _fl(a) - _fl(b));
        const bins = []; let lo = 0, hi = sorted.length - 1;
        while (lo <= hi) {
          if (lo === hi) { bins.push({ remaining: maxLen - _fl(sorted[lo]), pieces: [sorted[lo]] }); lo++; }
          else if (_fl(sorted[lo]) + _fl(sorted[hi]) <= maxLen) {
            bins.push({ remaining: maxLen - _fl(sorted[lo]) - _fl(sorted[hi]), pieces: [sorted[hi], sorted[lo]] }); lo++; hi--;
          } else { bins.push({ remaining: maxLen - _fl(sorted[hi]), pieces: [sorted[hi]] }); hi--; }
        }
        return bins;
      })();
      const results = [binsA, binsB, binsC];
      bins = results[0]; let bestT = _total(results[0]);
      for (let i = 1; i < results.length; i++) {
        const t = _total(results[i]);
        if (t < bestT) { bestT = t; bins = results[i]; }
      }
    } else {
      const sorted = [...ze.pieces].sort((a, b) => _fl(b) - _fl(a));
      bins = _ffdBins(sorted, binCapacity);
    }
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-11-03, PLAN-1111-A) — the Seam Plan's twin of the
       rebalance in _binPackRollPieces; without it the PDF buys 74' where the quote buys 56'. Same
       rule: move one piece, or swap two, between rolls while the total bought strictly drops. Not
       in Uniform (every roll is bought at the same length, so nothing moves the total). */
    if (!uniform && bins.length > 1) {
      const cost = (u) => (u > 1e-9 ? Math.max(minLen, Math.ceil(u)) : 0);
      const used = (b) => b.pieces.reduce((s, p) => s + _fl(p), 0);
      for (let guard = 0; guard < 200; guard++) {
        let best = null;
        for (const A of bins) for (const B of bins) {
          if (A === B) continue;
          const uA = used(A), uB = used(B), base = cost(uA) + cost(uB);
          for (const pa of A.pieces) {
            const a = _fl(pa);
            if (uB + a <= binCapacity + 1e-6) {
              const d = cost(uA - a) + cost(uB + a) - base;
              if (d < -1e-9 && (!best || d < best.d)) best = { d, A, B, pa, pb: null };
            }
            for (const pb of B.pieces) {
              const b = _fl(pb);
              if (a <= b + 0.01 || uB - b + a > binCapacity + 1e-6) continue;
              const d = cost(uA - a + b) + cost(uB - b + a) - base;
              if (d < -1e-9 && (!best || d < best.d)) best = { d, A, B, pa, pb };
            }
          }
        }
        if (!best) break;
        best.A.pieces.splice(best.A.pieces.indexOf(best.pa), 1); best.B.pieces.push(best.pa);
        if (best.pb) { best.B.pieces.splice(best.B.pieces.indexOf(best.pb), 1); best.A.pieces.push(best.pb); }
        bins = bins.filter(b => b.pieces.length > 0);
      }
      for (const b of bins) b.remaining = binCapacity - used(b);
    }
    // Sort bins by spatial position (left→right, top→bottom) so letters
    // follow the visual layout order on the PDF diagram.
    bins.sort((a, b) => {
      const ap = a.pieces[0], bp = b.pieces[0];
      if ((ap._spaceOrder || 0) !== (bp._spaceOrder || 0)) return (ap._spaceOrder || 0) - (bp._spaceOrder || 0);
      if (ap.stripIndex !== bp.stripIndex) return ap.stripIndex - bp.stripIndex;
      return ap.rollY - bp.rollY;
    });
    const groupRolls = [];
    for (const bin of bins) {
      const letter = letterIdx < 26 ? LETTERS[letterIdx] : LETTERS[Math.floor(letterIdx / 26) - 1] + LETTERS[letterIdx % 26];
      letterIdx++;
      // usedLen based on fullLength (physical material) for purchase roll sizing
      const usedLen = bin.pieces.reduce((s, p) => s + _fl(p), 0);
      const purchaseLen = uniform ? binCapacity : Math.max(minLen, Math.ceil(usedLen));
      const rollPieces = [];
      let cutPos = 0;
      for (let pi = 0; pi < bin.pieces.length; pi++) {
        const piece = bin.pieces[pi];
        piece.rollLetter = letter;
        piece.pieceNum = pi + 1;
        piece.ref = letter + (pi + 1);
        // length = display length (in-room), fullLength = physical material on roll
        const pfl = _fl(piece);
        const rp = { ref: piece.ref, length: piece.length, fullLength: pfl, inRoomLength: piece.inRoomLength || piece.length, cutPos };
        if (piece._shareSpace) { rp.space = piece._shareSpace; rp.spaceIndex = piece._shareSpaceIndex; }
        rollPieces.push(rp);
        cutPos += pfl;
      }
      const roll = { letter, width: ze.stripWidth, purchaseLength: purchaseLen, pieces: rollPieces, waste: Math.max(0, purchaseLen - usedLen) };
      groupRolls.push(roll);
    }
    return { groupRolls, letterIdx };
  }

  /** Seam data for every space, with roll sharing applied (rep). A shared material is
   *  packed once across the spaces that use it: the largest space (the lead) owns the
   *  rolls, lettered on from every member space's own letters so a ref like G3 means the
   *  same roll on every page; each other space gets a group that lists the pieces it is
   *  cut from the lead's rolls and buys nothing. Returns [{ room, spaceIdx, spaceName, seamData }]. */
  _computeSeamPlanAll() {
    const rd = this.rd;
    const deferKeys = new Set();
    if (this._anyRollShareOn()) {
      for (const k in this._rollShare) if (this._rollShare[k] === true) deferKeys.add(k);
    }
    const rows = [];
    let spaceIdx = 0;
    let colorOffset = 0; // advances per space so each space gets its own palette block
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      spaceIdx++;
      const seamData = this._computeSeamPlanData(room, colorOffset, deferKeys.size ? { deferKeys } : {});
      if (!seamData) continue;
      colorOffset += seamData.colorCount || 1;
      rows.push({ room, spaceIdx, spaceName: room.name || `Space ${spaceIdx}`, seamData, nextLetter: seamData.letterCount || 0 });
    }
    if (!deferKeys.size) return rows;

    const packOwn = (row, ze) => {
      const packed = this._packSeamZoneEntry(ze, row.nextLetter);
      row.nextLetter = packed.letterIdx;
      row.seamData.purchaseRolls.push(...packed.groupRolls);
      row.seamData.productGroups.push({ product: ze.product, purchaseRolls: packed.groupRolls, colorIdx: ze._colorIdx });
    };
    const byKey = new Map();
    for (const row of rows) {
      for (const ze of (row.seamData.deferredEntries || [])) {
        if (!byKey.has(ze._key)) byKey.set(ze._key, []);
        byKey.get(ze._key).push({ row, ze });
      }
    }
    for (const members of byKey.values()) {
      const memberRows = [...new Set(members.map(m => m.row))];
      // Fewer than two areas: nothing to share. Two zones of one space are two areas and
      // pack together below (ordered = [lead], no borrowed pieces).
      if (members.length < 2) { for (const m of members) packOwn(m.row, m.ze); continue; }
      const areaOf = (r) => members.filter(m => m.row === r)
        .reduce((s, m) => s + Math.abs(RoomDesignerMath.signedPolygonArea(m.ze.zonePoly)), 0);
      const lead = memberRows.reduce((a, b) => (areaOf(b) > areaOf(a) ? b : a));
      const ordered = [lead, ...memberRows.filter(r => r !== lead)];
      const pieces = [];
      ordered.forEach((r, oi) => {
        for (const m of members) {
          if (m.row !== r) continue;
          for (const p of m.ze.pieces) {
            p._spaceOrder = oi;
            if (r !== lead) { p._shareSpace = r.spaceName; p._shareSpaceIndex = r.spaceIdx; }
            pieces.push(p);
          }
        }
      });
      const leadZe = members.find(m => m.row === lead).ze;
      const synth = { product: leadZe.product, stripWidth: leadZe.stripWidth, minLengthFt: leadZe.minLengthFt, pieces };
      const start = Math.max(...memberRows.map(r => r.nextLetter));
      const packed = this._packSeamZoneEntry(synth, start);
      for (const r of memberRows) r.nextLetter = packed.letterIdx;
      lead.seamData.purchaseRolls.push(...packed.groupRolls);
      lead.seamData.productGroups.push({
        product: leadZe.product, purchaseRolls: packed.groupRolls, colorIdx: leadZe._colorIdx,
        sharedWith: ordered.slice(1).map(r => r.spaceName),
      });
      for (const r of ordered.slice(1)) {
        const myZe = members.find(m => m.row === r).ze;
        const borrowedPieces = [];
        for (const roll of packed.groupRolls) {
          for (const rp of roll.pieces) {
            if (rp.spaceIndex !== r.spaceIdx) continue;
            borrowedPieces.push({ ref: rp.ref, roll: roll.letter, rollLength: roll.purchaseLength, width: roll.width,
              length: rp.length, fullLength: rp.fullLength, inRoomLength: rp.inRoomLength, cutPos: rp.cutPos });
          }
        }
        r.seamData.productGroups.push({
          product: myZe.product, purchaseRolls: [], colorIdx: myZe._colorIdx,
          borrowedFrom: lead.spaceName, borrowedPieces,
        });
      }
    }
    return rows;
  }

  /* ── Seam Plan PDF — Canvas Renderer (Page 1 diagram) ──────────── */

  _generateSeamPlanCanvas(room, seamData) {
    const rd = this.rd;
    // Print-friendly product colors: light fills + matching label colors
    const PRODUCT_COLORS = [
      { fill: '#bcf3ff', label: '#1a6cb0', badge: '#1a6cb0' },   // blue
      { fill: '#ffe0c0', label: '#b06a1a', badge: '#b06a1a' },   // orange
      { fill: '#b4f0be', label: '#2a7a2a', badge: '#2a7a2a' },   // green
      { fill: '#e0ccf5', label: '#6a3a9a', badge: '#6a3a9a' },   // purple
      { fill: '#f5f0b0', label: '#8a7a10', badge: '#8a7a10' },   // yellow
    ];
    // Match layout PDF canvas resolution for crisp print quality
    const CANVAS_W = 4800, CANVAS_H = 2667;
    const PAD = 3; // feet of padding (extra room for outside wall labels)

    const geo = rd._getRoomGeoCache(room);
    const poly = geo.roomPolygon;
    if (!poly || poly.length < 3) return null;

    // Bounding box from room walls + doors
    const points = [];
    for (const w of room.walls) { points.push(w.start); points.push(w.end); }
    if (room.doors) {
      for (const door of room.doors) {
        if (!door.wall || !door.swing || door.type === 'open') continue;
        const wall = door.wall;
        const hingePoint = rd.paramToPoint(wall, door.hingeParam);
        const endPoint = rd.paramToPoint(wall, door.endParam);
        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
        const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wLen === 0) continue;
        const perpX = -wdy / wLen, perpY = wdx / wLen;
        const doorWidthFt = door.width / 12;
        const s = door.swing;
        if (door.type === 'double') {
          const midX = (hingePoint.x + endPoint.x) / 2, midY = (hingePoint.y + endPoint.y) / 2;
          points.push({ x: midX + perpX * s.perpSign * (doorWidthFt / 2), y: midY + perpY * s.perpSign * (doorWidthFt / 2) });
        } else {
          const pivot = s.hingeEnd === 'hinge' ? hingePoint : endPoint;
          points.push({ x: pivot.x + perpX * s.perpSign * doorWidthFt, y: pivot.y + perpY * s.perpSign * doorWidthFt });
        }
      }
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
    minX -= PAD; minY -= PAD; maxX += PAD; maxY += PAD;

    const contentW = maxX - minX, contentH = maxY - minY;
    if (contentW <= 0 || contentH <= 0) return null;

    // Auto-rotate portrait plans to landscape for better PDF fit.
    // Label positioning is screen-space (via _toScreen), so rotation is safe.
    const needsRotation = contentH > contentW;
    const fitW = needsRotation ? contentH : contentW;
    const fitH = needsRotation ? contentW : contentH;

    const MARGIN = 0, LEGEND_H = 80;
    const drawAreaW = CANVAS_W - 2 * MARGIN;
    const drawAreaH = CANVAS_H - 2 * MARGIN - LEGEND_H;

    const scaleX = drawAreaW / (fitW * rd.basePixelsPerFoot);
    const scaleY = drawAreaH / (fitH * rd.basePixelsPerFoot);
    const printZoom = Math.min(scaleX, scaleY);
    const bpf = rd.basePixelsPerFoot;
    const scale = bpf * printZoom;
    const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2;

    let panX, panY;
    if (needsRotation) {
      panX = MARGIN + drawAreaW / 2 - centerY * scale;
      panY = MARGIN + drawAreaH / 2 + centerX * scale;
    } else {
      panX = MARGIN + drawAreaW / 2 - centerX * scale;
      panY = MARGIN + drawAreaH / 2 - centerY * scale;
    }

    const offscreen = document.createElement('canvas');
    offscreen.width = CANVAS_W;
    offscreen.height = CANVAS_H;
    const ctx = offscreen.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // World-to-screen helpers
    const w2sx = (wx) => panX + wx * scale;
    const w2sy = (wy) => panY + wy * scale;

    ctx.save();
    if (needsRotation) {
      ctx.translate(panX, panY);
      ctx.rotate(-Math.PI / 2);
      ctx.translate(-panX, -panY);
    }

    // Zone entries for rendering (multi-zone or single-zone backward compat)
    const zoneEntries = seamData.zoneEntries || [{
      pieces: seamData.pieces, layout: seamData.layout, floorAngle: seamData.floorAngle || 0,
      stripWidth: seamData.product.widthFt, centroid: this._getPolygonCentroid(poly),
      zonePoly: poly, _colorIdx: seamData.colorOffset || 0, product: seamData.product,
    }];

    // _colorIdx already assigned per zone entry in _computeSeamPlanData()
    // Ensure fallback entries have _colorIdx set
    for (const ze of zoneEntries) { if (ze._colorIdx == null) ze._colorIdx = 0; }
    const nrZones = seamData.nonRollZones || [];
    const uniqueProductKeys = new Set(zoneEntries.map(ze => ze.product ? `${ze.product.name}|${ze.product.color}` : 'default'));
    const isMultiProduct = uniqueProductKeys.size > 1;

    // Helper: apply zone rotation to canvas context
    const applyZoneRot = (ze) => {
      if (ze.floorAngle) {
        const c = ze.centroid;
        ctx.translate(w2sx(c.x), w2sy(c.y));
        ctx.rotate(ze.floorAngle);
        ctx.translate(-w2sx(c.x), -w2sy(c.y));
      }
    };

    // Product color fills inside room, clipped per zone polygon (always shown to match page 2+)
    {
      for (const ze of zoneEntries) {
        ctx.save();
        // Clip to this zone's polygon (or room polygon for single-zone)
        const clipPoly = (ze.zonePoly && ze.zonePoly.length >= 3) ? ze.zonePoly : poly;
        ctx.beginPath();
        ctx.moveTo(w2sx(clipPoly[0].x), w2sy(clipPoly[0].y));
        for (let k = 1; k < clipPoly.length; k++) ctx.lineTo(w2sx(clipPoly[k].x), w2sy(clipPoly[k].y));
        ctx.closePath();
        ctx.clip();
        applyZoneRot(ze);
        ctx.fillStyle = PRODUCT_COLORS[ze._colorIdx % PRODUCT_COLORS.length].fill;
        const sw0 = ze.stripWidth * scale;
        for (const strip of ze.layout.strips) {
          if (!strip.rolls || strip.rolls.length === 0) continue;
          for (const r of strip.rolls) {
            ctx.fillRect(w2sx(strip.x), w2sy(r.y), sw0, r.length * scale);
          }
        }
        ctx.restore();
      }
    }

    // Safety: white fill outside room polygon erases any leaked fills/seams
    ctx.save();
    ctx.beginPath();
    ctx.rect(-1e6, -1e6, 2e6, 2e6);
    ctx.moveTo(w2sx(poly[poly.length - 1].x), w2sy(poly[poly.length - 1].y));
    for (let k = poly.length - 2; k >= 0; k--) ctx.lineTo(w2sx(poly[k].x), w2sy(poly[k].y));
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill('evenodd');
    ctx.restore();

    // Helper: draw head seam lines for a zone (horizontal lines between adjacent rolls in each strip)
    const drawHeadSeams = (ze) => {
      const sw0 = ze.stripWidth * scale;
      for (const strip of ze.layout.strips) {
        if (!strip.segments) continue;
        for (const seg of strip.segments) {
          if (!seg.rolls || seg.rolls.length < 2) continue;
          for (let ri = 1; ri < seg.rolls.length; ri++) {
            const seamY = seg.rolls[ri].y;
            ctx.beginPath();
            ctx.moveTo(w2sx(strip.x), w2sy(seamY));
            ctx.lineTo(w2sx(strip.x) + sw0, w2sy(seamY));
            ctx.stroke();
          }
        }
      }
    };

    // Helper: draw top/bottom horizontal edges of each strip column
    const drawHorizEdges = (ze) => {
      const sw0 = ze.stripWidth * scale;
      const savedLW = ctx.lineWidth;
      ctx.lineWidth = 0.5;
      for (const strip of ze.layout.strips) {
        if (!strip.rolls || strip.rolls.length === 0) continue;
        let yMin = Infinity, yMax = -Infinity;
        for (const r of strip.rolls) { yMin = Math.min(yMin, r.y); yMax = Math.max(yMax, r.y + r.length); }
        const sx = w2sx(strip.x);
        // Top edge
        ctx.beginPath();
        ctx.moveTo(sx, w2sy(yMin));
        ctx.lineTo(sx + sw0, w2sy(yMin));
        ctx.stroke();
        // Bottom edge
        ctx.beginPath();
        ctx.moveTo(sx, w2sy(yMax));
        ctx.lineTo(sx + sw0, w2sy(yMax));
        ctx.stroke();
      }
      ctx.lineWidth = savedLW;
    };

    // Helper: draw outer vertical edges (left of first strip, right of last strip)
    const drawOuterEdges = (ze) => {
      const sw0 = ze.stripWidth * scale;
      const strips = ze.layout.strips;
      if (!strips || strips.length === 0) return;
      const savedLW = ctx.lineWidth;
      ctx.lineWidth = 0.5;
      for (const edgeStrip of [strips[0], strips[strips.length - 1]]) {
        if (!edgeStrip.rolls || edgeStrip.rolls.length === 0) continue;
        let yMin = Infinity, yMax = -Infinity;
        for (const r of edgeStrip.rolls) { yMin = Math.min(yMin, r.y); yMax = Math.max(yMax, r.y + r.length); }
        const isFirst = edgeStrip === strips[0];
        const ex = isFirst ? w2sx(edgeStrip.x) : w2sx(edgeStrip.x) + sw0;
        ctx.beginPath();
        ctx.moveTo(ex, w2sy(yMin));
        ctx.lineTo(ex, w2sy(yMax));
        ctx.stroke();
      }
      ctx.lineWidth = savedLW;
    };

    const _darkSeamColors = ['Buff Blue','Grippin Gray','Rippin Red','Whey Protein','Jet Black','Blue Jays','Cardinals','Raiders','Urban Slate'];
    const _exactDarkSeamColors = ['Navy','Maroon','Green'];
    const _seamStroke = (ze) => {
      const c = (ze.product && ze.product.color) || '';
      if (_exactDarkSeamColors.includes(c) || _darkSeamColors.some(d => c.includes(d))) return 'rgba(0,0,0,0.60)';
      return '#000000';
    };

    // Pass 2b (no Pass 2a — nothing drawn outside room on page 1): Head seams + outer edges INSIDE room — clipped per zone polygon
    // (prevents cross-zone overlap; each zone's head seams stay in its own territory)
    const isMultiZone = zoneEntries.length > 1;
    for (const ze of zoneEntries) {
      ctx.save();
      if (isMultiZone && ze.zonePoly && ze.zonePoly.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(w2sx(ze.zonePoly[0].x), w2sy(ze.zonePoly[0].y));
        for (let k = 1; k < ze.zonePoly.length; k++) ctx.lineTo(w2sx(ze.zonePoly[k].x), w2sy(ze.zonePoly[k].y));
        ctx.closePath();
        ctx.clip();
      } else {
        ctx.beginPath();
        ctx.moveTo(w2sx(poly[0].x), w2sy(poly[0].y));
        for (let k = 1; k < poly.length; k++) ctx.lineTo(w2sx(poly[k].x), w2sy(poly[k].y));
        ctx.closePath();
        ctx.clip();
      }
      applyZoneRot(ze);
      ctx.strokeStyle = _seamStroke(ze);
      ctx.lineWidth = 0.5;
      drawHeadSeams(ze);
      drawOuterEdges(ze);
      ctx.restore();
    }

    // Pass 2c: Strip seam lines (between columns), clipped per zone polygon (thin lines)
    for (const ze of zoneEntries) {
      ctx.save();
      const clipPoly2c = (isMultiZone && ze.zonePoly && ze.zonePoly.length >= 3) ? ze.zonePoly : poly;
      ctx.beginPath();
      ctx.moveTo(w2sx(clipPoly2c[0].x), w2sy(clipPoly2c[0].y));
      for (let k = 1; k < clipPoly2c.length; k++) ctx.lineTo(w2sx(clipPoly2c[k].x), w2sy(clipPoly2c[k].y));
      ctx.closePath();
      ctx.clip();
      applyZoneRot(ze);
      ctx.strokeStyle = _seamStroke(ze);
      ctx.lineWidth = 0.5;
      for (let si = 1; si < ze.layout.strips.length; si++) {
        const strip = ze.layout.strips[si];
        const prevStrip = ze.layout.strips[si - 1];
        let yMin = Infinity, yMax = -Infinity;
        for (const r of [...strip.rolls, ...prevStrip.rolls]) {
          yMin = Math.min(yMin, r.y);
          yMax = Math.max(yMax, r.y + r.length);
        }
        ctx.beginPath();
        ctx.moveTo(w2sx(strip.x), w2sy(yMin));
        ctx.lineTo(w2sx(strip.x), w2sy(yMax));
        ctx.stroke();
      }
      ctx.restore();
    }

    // Zone boundary lines drawn later (after non-roll zone fills) so they render on top

    // Safety: white fill outside room polygon — catches any seam line leaks
    ctx.save();
    ctx.beginPath();
    ctx.rect(-1e6, -1e6, 2e6, 2e6);
    ctx.moveTo(w2sx(poly[poly.length - 1].x), w2sy(poly[poly.length - 1].y));
    for (let k = poly.length - 2; k >= 0; k--) ctx.lineTo(w2sx(poly[k].x), w2sy(poly[k].y));
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill('evenodd');
    ctx.restore();

    // Overage hatch — rolls extending past zone polygon (covers both outside-room
    // and inter-zone overage). Mirrors the live render: clip to per-roll rects, then
    // evenodd-fill outside the rotated zone polygon. The previous per-strip bounding
    // rect (yMin/yMax of all rolls in strip) could bleed across multi-segment gaps
    // and into adjacent empty zones; per-roll rects are tight to actual roll bounds.
    for (const ze of zoneEntries) {
      if (!ze.layout || !ze.layout.strips || ze.layout.strips.length === 0) continue;
      if (!ze.zonePoly || ze.zonePoly.length < 3) continue;

      // Rotate zone polygon into the same coord system as ze.layout (zone-rotated layout coords)
      const _rotZonePoly = ze.floorAngle
        ? this._rotatePolygon(ze.zonePoly, -ze.floorAngle, ze.centroid.x, ze.centroid.y)
        : ze.zonePoly;
      if (!_rotZonePoly || _rotZonePoly.length < 3) continue;

      // Per-roll rects + bounding box (in zone-rotated layout coords)
      const _rolls = [];
      let _bMinX = Infinity, _bMinY = Infinity, _bMaxX = -Infinity, _bMaxY = -Infinity;
      const _swFt = ze.stripWidth;
      for (const strip of ze.layout.strips) {
        if (!strip.rolls || strip.rolls.length === 0) continue;
        for (const r of strip.rolls) {
          _rolls.push({ x: strip.x, y: r.y, w: _swFt, h: r.length });
          if (strip.x < _bMinX) _bMinX = strip.x;
          if (r.y < _bMinY) _bMinY = r.y;
          if (strip.x + _swFt > _bMaxX) _bMaxX = strip.x + _swFt;
          if (r.y + r.length > _bMaxY) _bMaxY = r.y + r.length;
        }
      }
      if (_rolls.length === 0) continue;

      ctx.save();
      // Clip 0: drawing area only — never let overage hatch bleed into the legend
      // strip below or off the canvas sides. Without this, rolls whose rect
      // extends past CANVAS_H - LEGEND_H would render hatch under/past the legend,
      // making the +X' Y" overflow arrow appear to stop short of the visual end.
      // DEVICE-space clip: this rect is in raw canvas pixels, so it must be applied
      // with the identity transform. On portrait plans `needsRotation` rotates the
      // whole canvas -90°, and applying this rect under that rotation mis-clipped it
      // sideways and dropped the entire overage hatch (regression from the 2026-04-27
      // rewrite — overage went unseen until a portrait Seam Plan PDF was generated).
      const _ovHasTf = !!(ctx.getTransform && ctx.setTransform);
      const _ovSavedTf = _ovHasTf ? ctx.getTransform() : null;
      if (_ovHasTf) ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.beginPath();
      ctx.rect(0, 0, CANVAS_W, CANVAS_H - LEGEND_H);
      ctx.clip();
      if (_ovHasTf) ctx.setTransform(_ovSavedTf);
      applyZoneRot(ze);

      // Clip 1: union of per-roll rects. Only roll areas can render.
      ctx.beginPath();
      for (const _r of _rolls) {
        ctx.rect(w2sx(_r.x), w2sy(_r.y), _r.w * scale, _r.h * scale);
      }
      ctx.clip();

      // Build the evenodd path (bigRect XOR rotated zone polygon).
      // When intersected with the per-roll clip, this fills only the parts of rolls
      // that fall outside the zone polygon = legitimate overage.
      const _bx = w2sx(_bMinX) - 50;
      const _by = w2sy(_bMinY) - 50;
      const _bw = (_bMaxX - _bMinX) * scale + 100;
      const _bh = (_bMaxY - _bMinY) * scale + 100;
      const _drawOveragePath = () => {
        ctx.beginPath();
        ctx.rect(_bx, _by, _bw, _bh);
        ctx.moveTo(w2sx(_rotZonePoly[0].x), w2sy(_rotZonePoly[0].y));
        for (let i = 1; i < _rotZonePoly.length; i++) {
          ctx.lineTo(w2sx(_rotZonePoly[i].x), w2sy(_rotZonePoly[i].y));
        }
        ctx.closePath();
      };

      // Red fill in overage area
      _drawOveragePath();
      ctx.fillStyle = 'rgba(220,40,40,0.25)';
      ctx.fill('evenodd');

      // Clip further to overage area for hatch lines + white seam lines
      _drawOveragePath();
      ctx.clip('evenodd');

      // Diagonal hatch lines
      ctx.strokeStyle = 'rgba(200,30,30,0.7)';
      ctx.lineWidth = 2;
      const _spacing = 8;
      for (let _d = -_bh; _d < _bw + _bh; _d += _spacing) {
        ctx.beginPath();
        ctx.moveTo(_bx + _d, _by);
        ctx.lineTo(_bx + _d + _bh, _by + _bh);
        ctx.stroke();
      }

      // Faint white seam lines in overage area (head seams, strip seams, top/bottom edges)
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      drawHeadSeams(ze);
      for (let si = 1; si < ze.layout.strips.length; si++) {
        const strip = ze.layout.strips[si];
        const prevStrip = ze.layout.strips[si - 1];
        let _yMin = Infinity, _yMax = -Infinity;
        for (const r of [...strip.rolls, ...prevStrip.rolls]) {
          if (r.y < _yMin) _yMin = r.y;
          if (r.y + r.length > _yMax) _yMax = r.y + r.length;
        }
        ctx.beginPath();
        ctx.moveTo(w2sx(strip.x), w2sy(_yMin));
        ctx.lineTo(w2sx(strip.x), w2sy(_yMax));
        ctx.stroke();
      }
      drawHorizEdges(ze);

      ctx.restore();
    }

    // White seam lines on non-roll zones (turf/OTHER/tiles) where rolls underlap
    if (nrZones.length > 0) {
      ctx.save();
      // Clip to union of all non-roll zone polygons
      ctx.beginPath();
      for (const nrz of nrZones) {
        if (!nrz.polygon || nrz.polygon.length < 3) continue;
        ctx.moveTo(w2sx(nrz.polygon[0].x), w2sy(nrz.polygon[0].y));
        for (let k = 1; k < nrz.polygon.length; k++) ctx.lineTo(w2sx(nrz.polygon[k].x), w2sy(nrz.polygon[k].y));
        ctx.closePath();
      }
      ctx.clip();
      for (const ze of zoneEntries) {
        ctx.save();
        applyZoneRot(ze);
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 1;
        drawHeadSeams(ze);
        for (let si = 1; si < ze.layout.strips.length; si++) {
          const strip = ze.layout.strips[si];
          const prevStrip = ze.layout.strips[si - 1];
          let yMin = Infinity, yMax = -Infinity;
          for (const r of [...strip.rolls, ...prevStrip.rolls]) {
            yMin = Math.min(yMin, r.y); yMax = Math.max(yMax, r.y + r.length);
          }
          ctx.beginPath();
          ctx.moveTo(w2sx(strip.x), w2sy(yMin));
          ctx.lineTo(w2sx(strip.x), w2sy(yMax));
          ctx.stroke();
        }
        ctx.restore();
      }
      ctx.restore();
    }

    // Build door gap exclusion rects (all door types — perimeter + dividing wall doors)
    const _wallLW = (4 / 12) * scale; // 4 inches in world units → pixels
    const _doorGapPad = 0.375 * 0.7; // wallThickness * 0.7
    const _doorGaps = [];
    const _addDoorGap = (door) => {
      if (!door.wall) return;
      const wall = door.wall;
      const hp = rd.paramToPoint(wall, door.hingeParam);
      const ep = rd.paramToPoint(wall, door.endParam);
      const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      const wLen = Math.sqrt(wdx * wdx + wdy * wdy);
      if (wLen === 0) return;
      const px = -wdy / wLen, py = wdx / wLen;
      _doorGaps.push([
        { x: hp.x + px * _doorGapPad, y: hp.y + py * _doorGapPad },
        { x: ep.x + px * _doorGapPad, y: ep.y + py * _doorGapPad },
        { x: ep.x - px * _doorGapPad, y: ep.y - py * _doorGapPad },
        { x: hp.x - px * _doorGapPad, y: hp.y - py * _doorGapPad },
      ]);
    };
    // Perimeter doors
    if (room.doors) {
      for (const door of room.doors) _addDoorGap(door);
    }
    // Dividing wall doors
    if (rd.wallSegmentGroups) {
      for (const group of rd.wallSegmentGroups) {
        if (!group.doors) continue;
        for (const door of group.doors) {
          // Only include doors whose midpoint is inside this room
          if (door.wall) {
            const hp = rd.paramToPoint(door.wall, door.hingeParam);
            const ep = rd.paramToPoint(door.wall, door.endParam);
            const mx = (hp.x + ep.x) / 2, my = (hp.y + ep.y) / 2;
            if (rd.pointInPolygon(mx, my, poly)) _addDoorGap(door);
          }
        }
      }
    }

    // Draw walls — thick outward, thin inward, with door gaps excluded via clip
    ctx.lineJoin = 'miter';
    ctx.lineCap = 'square';
    ctx.miterLimit = 10;
    if (room.walls.length >= 2) {
      // Build clip that excludes door gaps
      const _applyDoorClip = () => {
        if (_doorGaps.length === 0) return;
        // Even-odd: big rect minus door gap rects
        ctx.beginPath();
        ctx.rect(-1e6, -1e6, 2e6, 2e6);
        for (const gap of _doorGaps) {
          ctx.moveTo(w2sx(gap[0].x), w2sy(gap[0].y));
          for (let g = 1; g < gap.length; g++) ctx.lineTo(w2sx(gap[g].x), w2sy(gap[g].y));
          ctx.closePath();
        }
        ctx.clip('evenodd');
      };
      // First: thick stroke clipped to OUTSIDE the room + excluding door gaps
      ctx.save();
      ctx.beginPath();
      ctx.rect(-1e6, -1e6, 2e6, 2e6);
      ctx.moveTo(w2sx(poly[poly.length - 1].x), w2sy(poly[poly.length - 1].y));
      for (let k = poly.length - 2; k >= 0; k--) ctx.lineTo(w2sx(poly[k].x), w2sy(poly[k].y));
      ctx.closePath();
      ctx.clip('evenodd');
      _applyDoorClip();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = _wallLW;
      ctx.beginPath();
      ctx.moveTo(w2sx(room.walls[0].start.x), w2sy(room.walls[0].start.y));
      for (const wall of room.walls) ctx.lineTo(w2sx(wall.end.x), w2sy(wall.end.y));
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
      // Second: thin hairline on the interior edge, also excluding door gaps
      ctx.save();
      _applyDoorClip();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(w2sx(room.walls[0].start.x), w2sy(room.walls[0].start.y));
      for (const wall of room.walls) ctx.lineTo(w2sx(wall.end.x), w2sy(wall.end.y));
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
    if (room.cutouts) {
      for (const cutout of room.cutouts) {
        if (!cutout.walls || cutout.walls.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(w2sx(cutout.walls[0].start.x), w2sy(cutout.walls[0].start.y));
        for (const wall of cutout.walls) ctx.lineTo(w2sx(wall.end.x), w2sy(wall.end.y));
        if (cutout.closed) ctx.closePath();
        ctx.stroke();
      }
    }

    // Door gaps are handled by clipping walls above — no separate white rect needed

    // Redraw strip seams on top of doors (so they show through door gaps), clipped per zone
    for (const ze of zoneEntries) {
      ctx.save();
      const clipPolyDoor = (isMultiZone && ze.zonePoly && ze.zonePoly.length >= 3) ? ze.zonePoly : poly;
      ctx.beginPath();
      ctx.moveTo(w2sx(clipPolyDoor[0].x), w2sy(clipPolyDoor[0].y));
      for (let k = 1; k < clipPolyDoor.length; k++) ctx.lineTo(w2sx(clipPolyDoor[k].x), w2sy(clipPolyDoor[k].y));
      ctx.closePath();
      ctx.clip();
      applyZoneRot(ze);
      ctx.strokeStyle = _seamStroke(ze);
      ctx.lineWidth = 0.5;
      for (let si = 1; si < ze.layout.strips.length; si++) {
        const strip = ze.layout.strips[si];
        const prevStrip = ze.layout.strips[si - 1];
        let yMin = Infinity, yMax = -Infinity;
        for (const r of [...strip.rolls, ...prevStrip.rolls]) {
          yMin = Math.min(yMin, r.y);
          yMax = Math.max(yMax, r.y + r.length);
        }
        ctx.beginPath();
        ctx.moveTo(w2sx(strip.x), w2sy(yMin));
        ctx.lineTo(w2sx(strip.x), w2sy(yMax));
        ctx.stroke();
      }
      drawOuterEdges(ze);
      ctx.restore();
    }

    // Non-roll zones (turf, tile, paver): solid outline + centered label
    // Outline is drawn in the rotated space, labels are deferred to screen space (drawn upright later)
    const _nrzLabels = [];
    for (const nrz of nrZones) {
      if (!nrz.polygon || nrz.polygon.length < 3) continue;
      ctx.save();
      // Clip to room polygon so turf/platform fills don't bleed past walls
      ctx.beginPath();
      ctx.moveTo(w2sx(poly[0].x), w2sy(poly[0].y));
      for (let k = 1; k < poly.length; k++) ctx.lineTo(w2sx(poly[k].x), w2sy(poly[k].y));
      ctx.closePath();
      ctx.clip();
      ctx.beginPath();
      ctx.moveTo(w2sx(nrz.polygon[0].x), w2sy(nrz.polygon[0].y));
      for (let k = 1; k < nrz.polygon.length; k++) ctx.lineTo(w2sx(nrz.polygon[k].x), w2sy(nrz.polygon[k].y));
      ctx.closePath();
      ctx.fillStyle = 'rgba(245,245,245,0.9)';
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.restore();
      // Collect label data for upright rendering after rotation restore
      // Use bounding box center (not polygon centroid) for consistent centering
      let _nrzMinX = Infinity, _nrzMinY = Infinity, _nrzMaxX = -Infinity, _nrzMaxY = -Infinity;
      for (const p of nrz.polygon) { _nrzMinX = Math.min(_nrzMinX, p.x); _nrzMinY = Math.min(_nrzMinY, p.y); _nrzMaxX = Math.max(_nrzMaxX, p.x); _nrzMaxY = Math.max(_nrzMaxY, p.y); }
      const cx = (_nrzMinX + _nrzMaxX) / 2;
      const cy = (_nrzMinY + _nrzMaxY) / 2;
      _nrzLabels.push({ wx: cx, wy: cy, label: nrz.label, productName: nrz.productName, dimLabel: nrz.dimLabel || null, _obj: nrz._obj || null, polygon: nrz.polygon });
    }

    // Zone boundary lines (divider segments) — drawn after non-roll zone fills,
    // clipped to exclude turf zones so dividers underlap turf
    if (seamData.dividerSegs && seamData.dividerSegs.length > 0) {
      const _divWallLW = (4 / 12) * scale;
      ctx.save();
      // Clip out turf zones so dividers don't show through turf
      const _turfNrzs = nrZones.filter(nrz => nrz.label === 'TURF' && nrz.polygon && nrz.polygon.length >= 3);
      if (_turfNrzs.length > 0) {
        ctx.beginPath();
        ctx.rect(-1e6, -1e6, 2e6, 2e6);
        for (const tnrz of _turfNrzs) {
          ctx.moveTo(w2sx(tnrz.polygon[0].x), w2sy(tnrz.polygon[0].y));
          for (let k = 1; k < tnrz.polygon.length; k++) ctx.lineTo(w2sx(tnrz.polygon[k].x), w2sy(tnrz.polygon[k].y));
          ctx.closePath();
        }
        ctx.clip('evenodd');
      }
      // Also exclude door gaps from dividing walls
      if (_doorGaps.length > 0) {
        ctx.beginPath();
        ctx.rect(-1e6, -1e6, 2e6, 2e6);
        for (const gap of _doorGaps) {
          ctx.moveTo(w2sx(gap[0].x), w2sy(gap[0].y));
          for (let g = 1; g < gap.length; g++) ctx.lineTo(w2sx(gap[g].x), w2sy(gap[g].y));
          ctx.closePath();
        }
        ctx.clip('evenodd');
      }
      ctx.setLineDash([]);
      ctx.strokeStyle = '#000000';
      ctx.lineJoin = 'miter';
      ctx.lineCap = 'square';
      for (const seg of seamData.dividerSegs) {
        ctx.lineWidth = seg.isWall ? _divWallLW / 2 : 1;
        ctx.beginPath();
        ctx.moveTo(w2sx(seg.start.x), w2sy(seg.start.y));
        ctx.lineTo(w2sx(seg.end.x), w2sy(seg.end.y));
        ctx.stroke();
      }
      ctx.restore();
    }

    // Pass 3: Piece labels (drawn after walls/doors so they're on top)
    // Scale labels: bigger canvas = bigger base, and scale up for large rooms
    const labelFontLarge = Math.max(30, Math.min(46, 30 * (scale / 20)));
    const labelFontSmall = Math.max(24, Math.min(34, 22 * (scale / 20)));

    // Helper: convert pre-rotation coords to final screen coords
    // (moved here so labels can be positioned in screen space directly)
    const _toScreen = (sx, sy, ze) => {
      let rx = sx, ry = sy;
      if (ze.floorAngle) {
        const c = ze.centroid;
        const csx = w2sx(c.x), csy = w2sy(c.y);
        const dx = sx - csx, dy = sy - csy;
        const cos = Math.cos(ze.floorAngle), sin = Math.sin(ze.floorAngle);
        rx = csx + dx * cos - dy * sin;
        ry = csy + dx * sin + dy * cos;
      }
      if (needsRotation) {
        const dx = rx - panX, dy = ry - panY;
        return { x: panX + dy, y: panY - dx };
      }
      return { x: rx, y: ry };
    };

    // Screen-space visible bounds (labels must stay within visible canvas area)
    const _visMinX = 0, _visMaxX = CANVAS_W;
    const _visMinY = 0, _visMaxY = CANVAS_H - LEGEND_H;

    // Collect all label positions in SCREEN SPACE for collision detection.
    // Labels are positioned where pieces visually appear on the final canvas,
    // eliminating coordinate conversion issues on rotated plans.
    const labelEntries = [];
    for (const ze of zoneEntries) {
      const sw0 = ze.stripWidth * scale;
      const _zonePoly = (ze.zonePoly && ze.zonePoly.length >= 3) ? ze.zonePoly : poly;
      // Get the rotated polygon from the layout (matches piece coordinates exactly).
      // The layout stores the pre-rotated polygon it computed against, avoiding
      // centroid mismatches between the render pass cache and seam plan data.
      // Room polygon in screen space (without floor angle — matches visual room boundary)
      const _zonePolyScr = _zonePoly.map(p => _toScreen(w2sx(p.x), w2sy(p.y), { floorAngle: 0 }));
      for (const piece of ze.pieces) {
        // Convert full piece rect corners to screen space
        const prLeft = w2sx(piece.stripX), prRight = prLeft + sw0;
        const _rlScale = (piece.rollLength || piece.length) * scale;
        const prTop = w2sy(piece.rollY), prBot = prTop + _rlScale;
        const c0 = _toScreen(prLeft, prTop, ze), c1 = _toScreen(prRight, prTop, ze);
        const c2 = _toScreen(prRight, prBot, ze), c3 = _toScreen(prLeft, prBot, ze);
        const pScrL = Math.min(c0.x, c1.x, c2.x, c3.x);
        const pScrR = Math.max(c0.x, c1.x, c2.x, c3.x);
        const pScrT = Math.min(c0.y, c1.y, c2.y, c3.y);
        const pScrB = Math.max(c0.y, c1.y, c2.y, c3.y);
        // Sample a grid across the piece to find the VISIBLE fill centroid in screen space.
        // Each sample is converted to screen WITH floor angle (where the fill renders),
        // then checked against the room polygon WITHOUT floor angle (where the room boundary is).
        // Points that pass BOTH = visible fill area = where the label should go.
        const _stepsY = 20, _stepsX = 4;
        let _irScrXSum = 0, _irScrYSum = 0, _irCount = 0;
        for (let _xi = 0; _xi <= _stepsX; _xi++) {
          const _sx = piece.stripX + ze.stripWidth * _xi / _stepsX;
          for (let _yi = 0; _yi <= _stepsY; _yi++) {
            const _sy = piece.rollY + (piece.rollLength || piece.length) * _yi / _stepsY;
            const _sp = _toScreen(w2sx(_sx), w2sy(_sy), ze);
            if (rd.pointInPolygon(_sp.x, _sp.y, _zonePolyScr)) {
              _irScrXSum += _sp.x; _irScrYSum += _sp.y; _irCount++;
            }
          }
        }
        // Skip pieces with no visible fill inside the room on screen
        if (_irCount === 0) continue;
        const irCenter = { x: _irScrXSum / _irCount, y: _irScrYSum / _irCount };
        if (pScrR - pScrL < 40 || pScrB - pScrT < 30) continue;
        const refText = piece.ref;
        const lenText = rd.formatMeasurement(piece.length);
        ctx.font = `bold ${labelFontLarge}px sans-serif`;
        const refW = ctx.measureText(refText).width;
        ctx.font = `${labelFontSmall}px sans-serif`;
        const lenW = ctx.measureText(lenText).width;
        let pillW = Math.max(refW, lenW) + 16;
        let pillH = labelFontLarge + labelFontSmall + 12;
        let showLen = true;
        // Clamp bounds: piece ∩ visible canvas
        const clampLeft = Math.max(pScrL + 2, _visMinX + 2);
        const clampRight = Math.min(pScrR - 2, _visMaxX - 2);
        const clampTop = Math.max(pScrT + 2, _visMinY + 2);
        const clampBot = Math.min(pScrB - 2, _visMaxY - 2);
        // Size check
        if (clampBot - clampTop < pillH || clampRight - clampLeft < pillW) {
          pillW = refW + 8;
          pillH = labelFontLarge + 6;
          showLen = false;
          if (clampBot - clampTop < pillH || clampRight - clampLeft < pillW) continue;
        }
        // Place label at the in-room center (from polygon sampling), not the piece center.
        // This ensures labels land inside the room even for mostly-overage pieces.
        let sx = irCenter.x;
        let sy = irCenter.y;
        const halfW = pillW / 2, halfH = pillH / 2;
        // Clamp to stay within piece bounds and visible canvas
        if (sx - halfW < clampLeft) sx = clampLeft + halfW;
        if (sx + halfW > clampRight) sx = clampRight - halfW;
        if (sy - halfH < clampTop) sy = clampTop + halfH;
        if (sy + halfH > clampBot) sy = clampBot - halfH;
        labelEntries.push({ sx, sy, pillW, pillH, refText, lenText: showLen ? lenText : null, ze, piece,
          pScrT, pScrB, pScrL, pScrR });
      }
    }

    // Pre-compute wall label bounding boxes as obstacles for roll labels
    const wallLabelRects = [];
    try {
      const labelWalls = geo.labelWalls || [];
      const wallThickness = 0.375;
      const centroidX = geo.centroidX, centroidY = geo.centroidY;
      const dimFontSize = Math.max(28, Math.min(40, 28 * (scale / 20)));
      for (const wall of labelWalls) {
        const distance = rd.getDistance(wall.start, wall.end);
        if (distance < 0.5) continue;
        const wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
        const wlen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wlen === 0) continue;
        const wnx = -wdy / wlen, wny = wdx / wlen;
        const midX = (wall.start.x + wall.end.x) / 2, midY = (wall.start.y + wall.end.y) / 2;
        const testDist = 0.2;
        const testA = { x: midX + wnx * testDist, y: midY + wny * testDist };
        const testB = { x: midX - wnx * testDist, y: midY - wny * testDist };
        const aInside = rd.pointInPolygon(testA.x, testA.y, poly);
        const bInside = rd.pointInPolygon(testB.x, testB.y, poly);
        let sign;
        if (aInside && !bInside) sign = -1;
        else if (!aInside && bInside) sign = 1;
        else { const dot = (midX - centroidX) * wnx + (midY - centroidY) * wny; sign = dot >= 0 ? 1 : -1; }
        ctx.font = `600 ${dimFontSize}px Arial`;
        const wtw = ctx.measureText(rd.formatMeasurement(distance)).width;
        const wth = dimFontSize;
        const wPadX = 4, wPadY = 3;
        const wLabelOffset = (wallThickness / 2 + 0.3) * scale + wth / 2 + wPadY;
        const wlx = w2sx(midX) + sign * wnx * wLabelOffset;
        const wly = w2sy(midY) + sign * wny * wLabelOffset;
        // Convert to screen space for collision detection with screen-space labels
        const wScr = _toScreen(wlx, wly, { floorAngle: 0 });
        wallLabelRects.push({ cx: wScr.x, cy: wScr.y, w: wtw + wPadX * 2, h: wth + wPadY * 2 });
      }
    } catch (e) { /* wall label pre-compute failed — proceed without */ }

    // Collision detection: nudge overlapping labels within their screen-space piece bounds
    const GAP = 4;

    // Pass 1: pairwise label-vs-label collision
    for (let i = 0; i < labelEntries.length; i++) {
      const a = labelEntries[i];
      for (let j = 0; j < i; j++) {
        const b = labelEntries[j];
        const overlapX = (a.pillW / 2 + b.pillW / 2 + GAP) - Math.abs(a.sx - b.sx);
        const overlapY = (a.pillH / 2 + b.pillH / 2 + GAP) - Math.abs(a.sy - b.sy);
        if (overlapX > 0 && overlapY > 0) {
          const shift = a.pillH / 2 + b.pillH / 2 + GAP;
          if (a.sy >= b.sy) {
            a.sy = Math.min(b.sy + shift, a.pScrB - a.pillH / 2 - 2);
          } else {
            a.sy = Math.max(b.sy - shift, a.pScrT + a.pillH / 2 + 2);
          }
        }
      }
    }

    // Pass 2 removed: labels freely overlap dividing walls/lines to stay aligned

    // Pass 3: avoid wall dimension labels (Y nudge, then X fallback if clamped)
    for (const lbl of labelEntries) {
      const halfH = lbl.pillH / 2, halfW = lbl.pillW / 2;
      for (const wr of wallLabelRects) {
        const overlapX = (halfW + wr.w / 2 + GAP) - Math.abs(lbl.sx - wr.cx);
        const overlapY = (halfH + wr.h / 2 + GAP) - Math.abs(lbl.sy - wr.cy);
        if (overlapX > 0 && overlapY > 0) {
          // Try vertical nudge first
          const shift = halfH + wr.h / 2 + GAP;
          if (lbl.sy >= wr.cy) {
            lbl.sy = Math.min(wr.cy + shift, lbl.pScrB - halfH - 2);
          } else {
            lbl.sy = Math.max(wr.cy - shift, lbl.pScrT + halfH + 2);
          }
          // Re-check: if still overlapping after Y clamped to piece bounds, try horizontal nudge
          const stillOverlapY = (halfH + wr.h / 2 + GAP) - Math.abs(lbl.sy - wr.cy);
          if (stillOverlapY > 0 && overlapX > 0) {
            const hShift = halfW + wr.w / 2 + GAP;
            const nudgeL = wr.cx - hShift;
            const nudgeR = wr.cx + hShift;
            const canL = nudgeL >= lbl.pScrL + halfW + 2;
            const canR = nudgeR <= lbl.pScrR - halfW - 2;
            if (canL || canR) {
              if (canL && canR) lbl.sx = Math.abs(nudgeL - lbl.sx) < Math.abs(nudgeR - lbl.sx) ? nudgeL : nudgeR;
              else lbl.sx = canL ? nudgeL : nudgeR;
            }
          }
        }
      }
    }

    // Pass 4: push roll labels away from non-roll zones (platforms, turf, tiles)
    const _nrzBuffer = 0.5 * scale;
    const _nrzRects = [];
    for (const nrz of nrZones) {
      if (!nrz.polygon || nrz.polygon.length < 3) continue;
      if (nrz.label === 'TURF' || nrz.label !== 'PLATFORM') continue;
      let nMinX = Infinity, nMinY = Infinity, nMaxX = -Infinity, nMaxY = -Infinity;
      for (const p of nrz.polygon) {
        // Convert to screen space
        const sp = _toScreen(w2sx(p.x), w2sy(p.y), { floorAngle: 0 });
        nMinX = Math.min(nMinX, sp.x); nMinY = Math.min(nMinY, sp.y);
        nMaxX = Math.max(nMaxX, sp.x); nMaxY = Math.max(nMaxY, sp.y);
      }
      _nrzRects.push({ cx: (nMinX + nMaxX) / 2, cy: (nMinY + nMaxY) / 2, w: nMaxX - nMinX + _nrzBuffer * 2, h: nMaxY - nMinY + _nrzBuffer * 2 });
    }
    for (const lbl of labelEntries) {
      const halfH = lbl.pillH / 2, halfW = lbl.pillW / 2;
      for (const nr of _nrzRects) {
        const overlapX = (halfW + nr.w / 2 + GAP) - Math.abs(lbl.sx - nr.cx);
        const overlapY = (halfH + nr.h / 2 + GAP) - Math.abs(lbl.sy - nr.cy);
        if (overlapX > 0 && overlapY > 0) {
          const shiftX = halfW + nr.w / 2 + GAP;
          const nudgeLeft = nr.cx - shiftX;
          const nudgeRight = nr.cx + shiftX;
          const canLeft = nudgeLeft - halfW >= lbl.pScrL + 2;
          const canRight = nudgeRight + halfW <= lbl.pScrR - 2;
          if (canLeft || canRight) {
            if (canLeft && canRight) {
              lbl.sx = Math.abs(nudgeLeft - lbl.sx) < Math.abs(nudgeRight - lbl.sx) ? nudgeLeft : nudgeRight;
            } else {
              lbl.sx = canLeft ? nudgeLeft : nudgeRight;
            }
          }
          const overlapX2 = (halfW + nr.w / 2 + GAP) - Math.abs(lbl.sx - nr.cx);
          const overlapY2 = (halfH + nr.h / 2 + GAP) - Math.abs(lbl.sy - nr.cy);
          if (overlapX2 > 0 && overlapY2 > 0) {
            const shiftY = halfH + nr.h / 2 + GAP;
            if (lbl.sy >= nr.cy) {
              lbl.sy = Math.min(nr.cy + shiftY, lbl.pScrB - halfH - 2);
            } else {
              lbl.sy = Math.max(nr.cy - shiftY, lbl.pScrT + halfH + 2);
            }
          }
        }
      }
    }

    // Un-rotate canvas BEFORE drawing labels so they stay upright
    ctx.restore();

    // Roll labels drawn later (after platform dims) so they overlap measurement lines

    // Draw non-roll zone labels (TURF, TILES, etc.) upright in screen space
    for (const nrl of _nrzLabels) {
      const scr = _toScreen(w2sx(nrl.wx), w2sy(nrl.wy), { floorAngle: 0 });
      // Compute zone width in screen space for text truncation
      let zoneScreenW = 200; // fallback
      if (nrl.polygon && nrl.polygon.length >= 3) {
        const scrPts = nrl.polygon.map(p => _toScreen(w2sx(p.x), w2sy(p.y), { floorAngle: 0 }));
        const sMinX = Math.min(...scrPts.map(p => p.x)), sMaxX = Math.max(...scrPts.map(p => p.x));
        zoneScreenW = sMaxX - sMinX;
      }
      const maxTextW = zoneScreenW * 0.9; // 90% of zone width
      const nrzFontSize = Math.max(30, Math.min(56, 38 * (scale / 20)));
      const dimFontSize = Math.max(24, Math.min(40, 28 * (scale / 20)));
      // Stack: label + dims (no product name)
      const hasOwnDims = !!nrl.dimLabel;
      const lineH = nrzFontSize * 0.9;
      const startY = hasOwnDims ? scr.y - lineH / 2 : scr.y;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Line 1: label (TURF / PLATFORM / OTHER)
      let _nrzLabelSize = nrzFontSize;
      ctx.font = `bold ${_nrzLabelSize}px sans-serif`;
      // Scale down if label text exceeds zone width
      while (_nrzLabelSize > 14 && ctx.measureText(nrl.label).width > maxTextW) {
        _nrzLabelSize -= 2;
        ctx.font = `bold ${_nrzLabelSize}px sans-serif`;
      }
      ctx.fillStyle = '#444444';
      ctx.fillText(nrl.label, scr.x, startY);
      // Line 2: dimensions (8' × 6')
      if (hasOwnDims) {
        ctx.font = `600 ${dimFontSize}px sans-serif`;
        ctx.fillStyle = '#333333';
        ctx.fillText(nrl.dimLabel, scr.x, startY + lineH);
      }
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }

    // Platform dimension lines: distance from each platform edge to nearest wall
    // Only draws the 2 shortest (most useful) distances to avoid clutter
    // Bright blue for visibility, avoids overlapping roll labels
    const _dimFont = labelFontSmall; // match roll label sub-text size
    const _dimBlue = '#006BCC';
    const _dimBlueLine = 'rgba(0,107,204,0.7)';
    // Collect all existing label rects for collision avoidance
    // lbl.sx/sy are already in screen space — no _toScreen conversion needed
    const _allLabelRects = labelEntries.map(l => {
      return { cx: l.sx, cy: l.sy, w: l.pillW, h: l.pillH };
    });
    // Collect all platform objects for inter-platform ray casting
    const _platNrls = _nrzLabels.filter(n => n.label === 'PLATFORM' && n._obj);
    // Build wall + divider segments once (shared across all platforms)
    const _dimWallSegs = [...room.walls.map(w => ({ start: w.start, end: w.end }))];
    if (seamData.dividerSegs) {
      for (const seg of seamData.dividerSegs) {
        if (seg.isWall) _dimWallSegs.push({ start: seg.start, end: seg.end });
      }
    }
    // Track measured platform pairs so only one platform measures the gap
    const _measuredPlatPairs = new Set();
    for (const nrl of _nrzLabels) {
      if (nrl.label !== 'PLATFORM' || !nrl._obj) continue;
      const obj = nrl._obj;
      const hw = obj.width / 2, hd = obj.depth / 2;
      const rot = obj.rotation || 0;
      const cos = Math.cos(rot), sin = Math.sin(rot);
      // Edge midpoints + outward normals in world space (rotation-aware)
      const edges = [
        { wx: obj.x - hd * sin, wy: obj.y + hd * cos, nx: -sin, ny: cos, axis: 'y' },
        { wx: obj.x + hw * cos, wy: obj.y + hw * sin, nx: cos, ny: sin, axis: 'x' },
        { wx: obj.x + hd * sin, wy: obj.y - hd * cos, nx: sin, ny: -cos, axis: 'y' },
        { wx: obj.x - hw * cos, wy: obj.y - hw * sin, nx: -cos, ny: -sin, axis: 'x' },
      ];
      // Build other platform edges as ray-cast obstacles
      const _otherPlatSegs = [];
      for (const other of _platNrls) {
        if (other._obj === obj) continue;
        const o = other._obj;
        const ohw = o.width / 2, ohd = o.depth / 2;
        const orot = o.rotation || 0;
        const ocos = Math.cos(orot), osin = Math.sin(orot);
        const oc = [
          { x: o.x + (-ohw) * ocos - (-ohd) * osin, y: o.y + (-ohw) * osin + (-ohd) * ocos },
          { x: o.x + ( ohw) * ocos - (-ohd) * osin, y: o.y + ( ohw) * osin + (-ohd) * ocos },
          { x: o.x + ( ohw) * ocos - ( ohd) * osin, y: o.y + ( ohw) * osin + ( ohd) * ocos },
          { x: o.x + (-ohw) * ocos - ( ohd) * osin, y: o.y + (-ohw) * osin + ( ohd) * ocos },
        ];
        for (let i = 0; i < 4; i++) _otherPlatSegs.push({ start: oc[i], end: oc[(i + 1) % 4], _otherObj: o });
      }
      const dims = [];
      for (const edge of edges) {
        const nx = edge.nx, ny = edge.ny;
        let minDist = Infinity;
        let hitObj = null;
        // Check walls + dividers
        for (const wall of _dimWallSegs) {
          const ax = edge.wx, ay = edge.wy;
          const bx = wall.start.x, by = wall.start.y;
          const dx = wall.end.x - bx, dy = wall.end.y - by;
          const denom = nx * dy - ny * dx;
          if (Math.abs(denom) < 1e-10) continue;
          const t = ((bx - ax) * dy - (by - ay) * dx) / denom;
          const u = ((bx - ax) * ny - (by - ay) * nx) / denom;
          if (t > 0.01 && u >= 0 && u <= 1 && t < minDist) { minDist = t; hitObj = null; }
        }
        // Check other platform edges (may be closer than walls)
        for (const ps of _otherPlatSegs) {
          const ax = edge.wx, ay = edge.wy;
          const bx = ps.start.x, by = ps.start.y;
          const dx = ps.end.x - bx, dy = ps.end.y - by;
          const denom = nx * dy - ny * dx;
          if (Math.abs(denom) < 1e-10) continue;
          const t = ((bx - ax) * dy - (by - ay) * dx) / denom;
          const u = ((bx - ax) * ny - (by - ay) * nx) / denom;
          if (t > 0.01 && u >= 0 && u <= 1 && t < minDist) { minDist = t; hitObj = ps._otherObj; }
        }
        if (minDist < Infinity && minDist > 0.1) {
          // Skip if this is an inter-platform measurement and the pair was already measured
          if (hitObj) {
            const pairKey = [obj, hitObj].sort((a, b) => (a.x + a.y) - (b.x + b.y)).map(o => `${o.x},${o.y}`).join('|');
            if (_measuredPlatPairs.has(pairKey)) continue;
            _measuredPlatPairs.add(pairKey);
          }
          dims.push({ edge, dist: minDist, nx, ny, axis: edge.axis, hitObj });
        }
      }
      // Wall dims: pick one per axis (shortest). Inter-platform dims: always add as extras.
      const wallDims = dims.filter(d => !d.hitObj);
      const platDims = dims.filter(d => d.hitObj);
      const toDraw = [];
      const bestX = wallDims.filter(d => d.axis === 'x').sort((a, b) => a.dist - b.dist)[0];
      const bestY = wallDims.filter(d => d.axis === 'y').sort((a, b) => a.dist - b.dist)[0];
      if (bestX) toDraw.push(bestX);
      if (bestY) toDraw.push(bestY);
      for (const pd of platDims) toDraw.push(pd);
      for (const d of toDraw) {
        const e = d.edge;
        const endWx = e.wx + d.nx * d.dist, endWy = e.wy + d.ny * d.dist;
        const s1 = _toScreen(w2sx(e.wx), w2sy(e.wy), { floorAngle: 0 });
        const s2 = _toScreen(w2sx(endWx), w2sy(endWy), { floorAngle: 0 });
        const ldx = s2.x - s1.x, ldy = s2.y - s1.y;
        const llen = Math.sqrt(ldx * ldx + ldy * ldy);
        if (llen < 10) continue;
        // Dimension line — dotted bright blue
        ctx.strokeStyle = _dimBlue;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(s1.x, s1.y);
        ctx.lineTo(s2.x, s2.y);
        ctx.stroke();
        // Arrowhead ticks at both ends (perpendicular, solid)
        ctx.setLineDash([]);
        const pnx = -ldy / llen * 6, pny = ldx / llen * 6;
        ctx.strokeStyle = _dimBlue;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(s1.x + pnx, s1.y + pny); ctx.lineTo(s1.x - pnx, s1.y - pny);
        ctx.moveTo(s2.x + pnx, s2.y + pny); ctx.lineTo(s2.x - pnx, s2.y - pny);
        ctx.stroke();
        // Label at midpoint — check for overlap with roll labels
        let mx = (s1.x + s2.x) / 2, my = (s1.y + s2.y) / 2;
        const label = rd.formatMeasurement(d.dist);
        ctx.font = `600 ${_dimFont}px sans-serif`;
        const tw = ctx.measureText(label).width;
        const th = _dimFont;
        const pillW = tw + 10, pillH = th + 6;
        // Nudge if overlapping any roll label
        for (const lr of _allLabelRects) {
          const ox = (pillW / 2 + lr.w / 2 + 4) - Math.abs(mx - lr.cx);
          const oy = (pillH / 2 + lr.h / 2 + 4) - Math.abs(my - lr.cy);
          if (ox > 0 && oy > 0) {
            // Shift along the dimension line direction
            const shiftAmt = pillH / 2 + lr.h / 2 + 6;
            if (d.ny !== 0) mx += (mx > lr.cx ? shiftAmt : -shiftAmt);
            else my += (my > lr.cy ? shiftAmt : -shiftAmt);
          }
        }
        // Blue pill background
        const pr = 4;
        ctx.fillStyle = _dimBlue;
        ctx.beginPath();
        ctx.moveTo(mx - pillW / 2 + pr, my - pillH / 2);
        ctx.lineTo(mx + pillW / 2 - pr, my - pillH / 2);
        ctx.quadraticCurveTo(mx + pillW / 2, my - pillH / 2, mx + pillW / 2, my - pillH / 2 + pr);
        ctx.lineTo(mx + pillW / 2, my + pillH / 2 - pr);
        ctx.quadraticCurveTo(mx + pillW / 2, my + pillH / 2, mx + pillW / 2 - pr, my + pillH / 2);
        ctx.lineTo(mx - pillW / 2 + pr, my + pillH / 2);
        ctx.quadraticCurveTo(mx - pillW / 2, my + pillH / 2, mx - pillW / 2, my + pillH / 2 - pr);
        ctx.lineTo(mx - pillW / 2, my - pillH / 2 + pr);
        ctx.quadraticCurveTo(mx - pillW / 2, my - pillH / 2, mx - pillW / 2 + pr, my - pillH / 2);
        ctx.closePath();
        ctx.fill();
        // White text on blue
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, mx, my);
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
        // Track this dim label for future collision checks
        _allLabelRects.push({ cx: mx, cy: my, w: pillW, h: pillH });
      }
    }

    // Draw roll labels LAST so they overlap platform dims and NRZ labels
    for (const lbl of labelEntries) {
      // lbl.sx/sy are already in screen space — no _toScreen needed
      const scr = { x: lbl.sx, y: lbl.sy };
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      const pX = scr.x - lbl.pillW / 2, pY = scr.y - lbl.pillH / 2, r = 4;
      ctx.moveTo(pX + r, pY);
      ctx.lineTo(pX + lbl.pillW - r, pY);
      ctx.quadraticCurveTo(pX + lbl.pillW, pY, pX + lbl.pillW, pY + r);
      ctx.lineTo(pX + lbl.pillW, pY + lbl.pillH - r);
      ctx.quadraticCurveTo(pX + lbl.pillW, pY + lbl.pillH, pX + lbl.pillW - r, pY + lbl.pillH);
      ctx.lineTo(pX + r, pY + lbl.pillH);
      ctx.quadraticCurveTo(pX, pY + lbl.pillH, pX, pY + lbl.pillH - r);
      ctx.lineTo(pX, pY + r);
      ctx.quadraticCurveTo(pX, pY, pX + r, pY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
      ctx.fillStyle = PRODUCT_COLORS[lbl.ze._colorIdx % PRODUCT_COLORS.length].label;
      ctx.font = `bold ${labelFontLarge}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (lbl.lenText) {
        ctx.fillText(lbl.refText, scr.x, scr.y - labelFontSmall / 2 - 1);
        ctx.fillStyle = '#000000';
        ctx.font = `${labelFontSmall}px sans-serif`;
        ctx.fillText(lbl.lenText, scr.x, scr.y + labelFontLarge / 2 + 1);
      } else {
        ctx.fillText(lbl.refText, scr.x, scr.y);
      }
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }

    // Wall measurements on the OUTSIDE of perimeter walls (measure-tool style)
    try {
    const labelWalls = geo.labelWalls || [];
    const wallThickness = 0.375;
    const centroidX = geo.centroidX, centroidY = geo.centroidY;
    const dimFontSize = labelFontSmall; // match roll label sub-text size

    // Collect all wall label positions first, then filter overlaps
    // Positions are in pre-rotation space; converted to screen space at draw time
    const _wallLabels = [];
    for (const wall of labelWalls) {
      const distance = rd.getDistance(wall.start, wall.end);
      if (distance < 0.5) continue;
      const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) continue;
      const nx = -dy / len, ny = dx / len;
      const midX = (wall.start.x + wall.end.x) / 2, midY = (wall.start.y + wall.end.y) / 2;
      const testDist = 0.2;
      const testA = { x: midX + nx * testDist, y: midY + ny * testDist };
      const testB = { x: midX - nx * testDist, y: midY - ny * testDist };
      const aInside = rd.pointInPolygon(testA.x, testA.y, poly);
      const bInside = rd.pointInPolygon(testB.x, testB.y, poly);
      let sign;
      if (aInside && !bInside) sign = -1;
      else if (!aInside && bInside) sign = 1;
      else { const dot = (midX - centroidX) * nx + (midY - centroidY) * ny; sign = dot >= 0 ? 1 : -1; }
      const label = rd.formatMeasurement(distance);
      ctx.font = `600 ${dimFontSize}px Arial`;
      const tww = ctx.measureText(label).width;
      const th = dimFontSize;
      const pillPadX = 4, pillPadY = 3;
      const labelOffset = (wallThickness / 2 + 0.3) * scale + th / 2 + pillPadY;
      const lx = w2sx(midX) + sign * nx * labelOffset;
      const ly = w2sy(midY) + sign * ny * labelOffset;
      const bw = tww + pillPadX * 2, bh = th + pillPadY * 2;
      // Skip if the label pill is wider than the wall segment on screen
      const wallScreenLen = len * scale;
      if (bw > wallScreenLen * 1.2) continue;
      _wallLabels.push({ lx, ly, bw, bh, label, distance });
    }
    // Convert to screen space for overlap detection (rotation-safe)
    for (const wl of _wallLabels) {
      const scr = _toScreen(wl.lx, wl.ly, { floorAngle: 0 });
      wl.scrX = scr.x; wl.scrY = scr.y;
    }
    // Remove overlapping labels — keep the longer wall's label (tiebreak by array order)
    const _wallLabelsFiltered = [];
    for (let _wli = 0; _wli < _wallLabels.length; _wli++) {
      const wl = _wallLabels[_wli];
      let dominated = false;
      for (let _wlj = 0; _wlj < _wallLabels.length; _wlj++) {
        if (_wli === _wlj) continue;
        const other = _wallLabels[_wlj];
        const ox = (wl.bw / 2 + other.bw / 2 + 2) - Math.abs(wl.scrX - other.scrX);
        const oy = (wl.bh / 2 + other.bh / 2 + 2) - Math.abs(wl.scrY - other.scrY);
        if (ox > 0 && oy > 0 && (other.distance > wl.distance || (other.distance === wl.distance && _wlj < _wli))) { dominated = true; break; }
      }
      if (!dominated) _wallLabelsFiltered.push(wl);
    }
    // Draw surviving labels using pre-computed screen positions
    for (const wl of _wallLabelsFiltered) {
      const pillPadX = 4, pillPadY = 3, cr = 3;
      const th = dimFontSize;
      ctx.font = `600 ${dimFontSize}px Arial`;
      const tww = ctx.measureText(wl.label).width;
      const bx = wl.scrX - tww / 2 - pillPadX, by = wl.scrY - th / 2 - pillPadY;
      const bw = tww + pillPadX * 2, bh = th + pillPadY * 2;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(bx + cr, by); ctx.lineTo(bx + bw - cr, by);
      ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cr);
      ctx.lineTo(bx + bw, by + bh - cr);
      ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cr, by + bh);
      ctx.lineTo(bx + cr, by + bh);
      ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cr);
      ctx.lineTo(bx, by + cr);
      ctx.quadraticCurveTo(bx, by, bx + cr, by);
      ctx.fill();
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(wl.label, wl.scrX, wl.scrY);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }
    } catch (wallLabelErr) { console.error('[RD] Seam plan wall labels error:', wallLabelErr); }

    // Legend text at bottom — list all products/colors
    ctx.fillStyle = '#000000';
    const uniqueProducts = [];
    const seen = new Set();
    for (const ze of zoneEntries) {
      const key = `${ze.product.name}|${ze.product.color}`;
      if (!seen.has(key)) { seen.add(key); uniqueProducts.push(ze.product); }
    }
    // Roll width label: whole foot ("6'") or half foot ("5' 6"") render in feet/inches;
    // any other irregular width renders as pure inches ("70"") — never decimal feet.
    const _rollWidthLabel = (feet) => {
      const totalInches = Math.round(feet * 12);
      if (totalInches % 6 === 0) {
        const ft = Math.floor(totalInches / 12);
        const inches = totalInches - ft * 12;
        if (inches === 0) return `${ft}'`;
        return `${ft}' ${inches}"`;
      }
      return `${totalInches}"`;
    };
    if (uniqueProducts.length === 1) {
      ctx.font = 'bold 36px sans-serif';
      ctx.fillText(`${_rollWidthLabel(uniqueProducts[0].widthFt)} Wide Rolls  —  ${uniqueProducts[0].name}`, 30, CANVAS_H - 30);
      if (uniqueProducts[0].color) {
        ctx.font = '34px sans-serif';
        ctx.fillText(`Color: ${uniqueProducts[0].color}`, 30, CANVAS_H - 74);
      }
    } else {
      let legendY = CANVAS_H - 30;
      for (let pi = 0; pi < uniqueProducts.length; pi++) {
        const p = uniqueProducts[pi];
        const productColor = PRODUCT_COLORS[pi % PRODUCT_COLORS.length];
        // Color swatch
        ctx.fillStyle = productColor.fill;
        ctx.fillRect(30, legendY - 30, 36, 36);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.strokeRect(30, legendY - 30, 36, 36);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 36px sans-serif';
        ctx.fillText(`${_rollWidthLabel(p.widthFt)} Wide  —  ${p.name}  —  ${p.color || ''}`, 78, legendY);
        legendY -= 50;
      }
    }

    // (Title removed — already in PDF header band)

    // Overflow indicators: "+X' Y"" labels where rolls extend past visible canvas edges.
    // Only shown when overage is actually clipped by the canvas — not when it merely
    // extends past the room boundary (overage always does that by definition).
    {
      const _ofMinPx = 10; // ignore < 10px of canvas overflow (sub-pixel rounding)
      const _ofDrawBottom = CANVAS_H - LEGEND_H;
      const _ofEdges = { top: { ft: 0, pts: [] }, bottom: { ft: 0, pts: [] }, left: { ft: 0, pts: [] }, right: { ft: 0, pts: [] } };
      for (const ze of zoneEntries) {
        const sw = ze.stripWidth;
        const angle = ze.floorAngle || 0;
        const _ofCos = Math.cos(angle), _ofSin = Math.sin(angle);
        const _ofCx = ze.centroid ? ze.centroid.x : 0, _ofCy = ze.centroid ? ze.centroid.y : 0;
        for (const strip of ze.layout.strips) {
          if (!strip.rolls || strip.rolls.length === 0) continue;
          let yMin = Infinity, yMax = -Infinity;
          for (const r of strip.rolls) { yMin = Math.min(yMin, r.y); yMax = Math.max(yMax, r.y + r.length); }
          const _ofCorners = [
            { x: strip.x, y: yMin }, { x: strip.x + sw, y: yMin },
            { x: strip.x + sw, y: yMax }, { x: strip.x, y: yMax },
          ];
          for (const lc of _ofCorners) {
            let wx, wy;
            if (angle) {
              const dx = lc.x - _ofCx, dy = lc.y - _ofCy;
              wx = _ofCx + dx * _ofCos - dy * _ofSin;
              wy = _ofCy + dx * _ofSin + dy * _ofCos;
            } else { wx = lc.x; wy = lc.y; }
            const scr = _toScreen(w2sx(wx), w2sy(wy), { floorAngle: 0 });
            // Check if screen position is off-canvas (not just off-room)
            const topOF = -scr.y; // pixels above canvas top (y=0)
            if (topOF > _ofMinPx) { const ft = topOF / scale; if (ft > _ofEdges.top.ft) _ofEdges.top.ft = ft; _ofEdges.top.pts.push(scr); }
            const botOF = scr.y - _ofDrawBottom; // pixels below draw area
            if (botOF > _ofMinPx) { const ft = botOF / scale; if (ft > _ofEdges.bottom.ft) _ofEdges.bottom.ft = ft; _ofEdges.bottom.pts.push(scr); }
            const leftOF = -scr.x; // pixels left of canvas
            if (leftOF > _ofMinPx) { const ft = leftOF / scale; if (ft > _ofEdges.left.ft) _ofEdges.left.ft = ft; _ofEdges.left.pts.push(scr); }
            const rightOF = scr.x - CANVAS_W; // pixels right of canvas
            if (rightOF > _ofMinPx) { const ft = rightOF / scale; if (ft > _ofEdges.right.ft) _ofEdges.right.ft = ft; _ofEdges.right.pts.push(scr); }
          }
        }
      }
      const _ofArrowLen = 24;
      const _ofFont = Math.max(24, Math.min(36, 28 * (scale / 20)));
      // Overflow detection is already in screen space (via _toScreen), so no
      // edge remapping needed — even with needsRotation the edges are correct.
      for (const [edge, info] of Object.entries(_ofEdges)) {
        if (info.ft < 0.25 || info.pts.length === 0) continue;
        const label = '+' + rd.formatMeasurement(info.ft);
        ctx.font = `bold ${_ofFont}px sans-serif`;
        const tw = ctx.measureText(label).width;
        const pillW = tw + 24, pillH = _ofFont + 14;
        // Average position along the edge
        let avg;
        if (edge === 'top' || edge === 'bottom') {
          avg = info.pts.reduce((s, p) => s + p.x, 0) / info.pts.length;
          avg = Math.max(pillW / 2 + 10, Math.min(CANVAS_W - pillW / 2 - 10, avg));
        } else {
          avg = info.pts.reduce((s, p) => s + p.y, 0) / info.pts.length;
          avg = Math.max(pillH / 2 + 10, Math.min(_ofDrawBottom - pillH / 2 - 10, avg));
        }
        let px, py, adx, ady;
        // Position the pill so the arrow tip touches the page edge exactly
        // (4 = pill-to-arrow gap; no extra buffer between tip and edge).
        if (edge === 'top') { px = avg; py = pillH / 2 + _ofArrowLen + 4; adx = 0; ady = -1; }
        else if (edge === 'bottom') { px = avg; py = _ofDrawBottom - pillH / 2 - _ofArrowLen - 4; adx = 0; ady = 1; }
        else if (edge === 'left') { px = pillW / 2 + _ofArrowLen + 4; py = avg; adx = -1; ady = 0; }
        else { px = CANVAS_W - pillW / 2 - _ofArrowLen - 4; py = avg; adx = 1; ady = 0; }
        // Arrow line from pill to canvas edge
        const ax1 = px + adx * (pillW / 2 + 4), ay1 = py + ady * (pillH / 2 + 4);
        const ax2 = ax1 + adx * _ofArrowLen, ay2 = ay1 + ady * _ofArrowLen;
        ctx.strokeStyle = 'rgba(200,30,30,0.85)';
        ctx.lineWidth = 5;
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(ax1, ay1); ctx.lineTo(ax2, ay2); ctx.stroke();
        // Arrowhead
        const _headLen = 16, _headAng = Math.PI / 5;
        const _arAng = Math.atan2(ady, adx);
        ctx.beginPath();
        ctx.moveTo(ax2, ay2);
        ctx.lineTo(ax2 - _headLen * Math.cos(_arAng - _headAng), ay2 - _headLen * Math.sin(_arAng - _headAng));
        ctx.moveTo(ax2, ay2);
        ctx.lineTo(ax2 - _headLen * Math.cos(_arAng + _headAng), ay2 - _headLen * Math.sin(_arAng + _headAng));
        ctx.stroke();
        // Red pill
        const _pr = 6, _bx = px - pillW / 2, _by = py - pillH / 2;
        ctx.fillStyle = 'rgba(200,30,30,0.9)';
        ctx.beginPath();
        ctx.moveTo(_bx + _pr, _by);
        ctx.lineTo(_bx + pillW - _pr, _by);
        ctx.quadraticCurveTo(_bx + pillW, _by, _bx + pillW, _by + _pr);
        ctx.lineTo(_bx + pillW, _by + pillH - _pr);
        ctx.quadraticCurveTo(_bx + pillW, _by + pillH, _bx + pillW - _pr, _by + pillH);
        ctx.lineTo(_bx + _pr, _by + pillH);
        ctx.quadraticCurveTo(_bx, _by + pillH, _bx, _by + pillH - _pr);
        ctx.lineTo(_bx, _by + _pr);
        ctx.quadraticCurveTo(_bx, _by, _bx + _pr, _by);
        ctx.closePath();
        ctx.fill();
        // White text
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `bold ${_ofFont}px sans-serif`;
        ctx.fillText(label, px, py);
        ctx.textAlign = 'start';
        ctx.textBaseline = 'alphabetic';
      }
    }

    const dataUrl = offscreen.toDataURL('image/jpeg', 0.82);
    // Explicitly release canvas GPU memory to prevent evicting equipment bitmap caches
    offscreen.width = 0;
    offscreen.height = 0;
    return dataUrl;
  }

  _collectHandoffItems() {
    const rd = this.rd;

    // Build space polygon lookup for equipment-to-space assignment
    const spacePolygons = [];
    let spIdx = 0;
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      spIdx++;
      const geo = rd._getRoomGeoCache(room);
      const poly = geo.roomPolygon;
      if (poly && poly.length >= 3) {
        spacePolygons.push({
          room, spaceName: room.name || `Space ${spIdx}`,
          spaceIndex: spIdx, polygon: poly,
        });
      }
    }

    function findSpaceForObj(obj) {
      for (const sp of spacePolygons) {
        if (RoomDesignerMath.pointInPolygon(obj.x, obj.y, sp.polygon)) return sp;
      }
      return null;
    }

    // Collect flooring items using bin-packed purchase quantities
    const flooringItems = [];
    const flooringSwatches = [];
    let spaceIdx = 0;
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      spaceIdx++;
      const spaceName = room.name || `Space ${spaceIdx}`;
      const entries = this._computePerRoomFlooringShared(room);
      for (const entry of entries) {
        const product = entry._product;
        const color = entry._color;
        const isTileProduct = product.type === 'tile' || product.shape === 'paver';
        // For tiles/pavers, quantity = tile count (how they're sold on Shopify); for rolls, quantity = sq ft
        const purchaseArea = isTileProduct
          ? (entry.purchaseRolls && entry.purchaseRolls[0] ? entry.purchaseRolls[0].count : Math.ceil(entry.materialArea))
          : Math.ceil(entry.materialArea);
        // Build structured roll details for PDF (headline + cut sub-lines per roll group)
        const purchaseRollDetails = entry.purchaseRolls.map(r => {
          const mg = r._mg || {};
          const rd2 = this.rd;
          const rlStr = mg.purchaseLen ? rd2.formatMeasurement(mg.purchaseLen) : '';
          const rollWord = (mg.totalCount || r.count || 1) > 1 ? 'rolls' : 'roll';
          const count = mg.totalCount || r.count || 1;
          const headline = `${count} ${rollWord} @ ${rlStr}`;
          const cuts = (mg.cutPatterns || []).map(cp => {
            const pieces = cp.cuts.map(c => c.count > 1 ? `${c.count} \u00d7 ${rd2.formatMeasurement(c.length)}` : rd2.formatMeasurement(c.length)).join(' + ');
            const cpRollWord = cp.count > 1 ? `${cp.count} ${rollWord}` : '1 roll';
            let line = `${cpRollWord} > cut into ${pieces}`;
            if (cp.waste > 0.04) line += ` (${rd2.formatMeasurement(cp.waste)} waste)`;
            return line;
          });
          return { headline, cuts };
        });
        // Flat fallback string for backward compat
        const purchaseDetails = entry.purchaseRolls.map(r => {
          const label = r.label.split('\u2014')[0].trim();
          return label.replace(/<[^>]+>/g, '').replace(/\u2192/g, '>');
        }).join(', ');
        // Roll sharing (rep): the lead space's line carries the shared rolls and says whose
        // pieces they include; a borrowing space buys nothing and says where its pieces come from.
        let _shareNote = '';
        if (entry._shareWith && entry._shareWith.length) {
          _shareNote = 'Includes the pieces for ' + entry._shareWith.map(o => `${o.spaceName} (${Math.round(o.floorArea)} sq ft)`).join(', ');
          purchaseRollDetails.push({ headline: _shareNote, cuts: [] });
        }
        const _borrowNote = entry._borrowedFrom
          ? `${Math.round(entry.floorArea)} sq ft, cut from ${entry._borrowedFrom} rolls`
          : '';

        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — qtyUnit lets the worker label QTY column correctly (tiles vs pavers vs sq ft) instead of always saying "sq ft"
        const qtyUnit = product.shape === 'paver' ? 'pavers' : (product.type === 'tile' ? 'tiles' : 'sq ft');
        // END CUSTOM_ROOMDESIGNER_BY_CLAUDE
        flooringItems.push({
          variantId: color ? color.variantId : 0,
          productId: product.shopifyId || 0,
          title: product.name,
          variantTitle: color ? color.name : '',
          price: '0',
          quantity: entry._borrowedFrom ? 0 : purchaseArea,
          qtyUnit: qtyUnit,
          image: color ? color.imageUrl : '',
          url: product.url || '',
          note: '',
          purchaseDetails: _borrowNote || (_shareNote ? purchaseDetails + '; ' + _shareNote : purchaseDetails),
          purchaseRollDetails: entry._borrowedFrom ? [] : purchaseRollDetails,
          _purchaseRolls: entry._borrowedFrom ? [] : entry.purchaseRolls,
          _borrowedFrom: entry._borrowedFrom || '',
          _shareNote: _shareNote,
          _productWidthFt: product.widthFt || 0,
          _isTile: isTileProduct,
          _shape: product.shape || '',
          _vendor: product.vendor || '',
          _category: product.category || '', // Rubber Rolls / Turf / Tiles / … for the packet's list page
          _sku: (color && color.sku) ? color.sku : '',
          source: 'room-designer',
          spaceName: spaceName,
        });

        const swatchKey = (product.id || product.name) + '|' + (color ? color.id : '');
        if (!flooringSwatches.find(s => s.key === swatchKey)) {
          flooringSwatches.push({
            key: swatchKey,
            productName: product.name,
            colorName: color ? color.name : '',
            hex: color ? color.hex : '#888',
            imageUrl: color ? color.imageUrl : ''
          });
        }
      }
    }

    // Collect equipment items grouped by (equipmentId, spaceName)
    const equipmentMap = new Map();
    for (const obj of rd.objects) {
      if (obj.type !== 'equipment') continue;
      const eqId = obj.equipmentId;
      const sp = findSpaceForObj(obj);
      const spaceName = sp ? sp.spaceName : '';
      const groupKey = `${eqId}|${spaceName}`;
      if (!equipmentMap.has(groupKey)) {
        const info = rd._equipmentImages && rd._equipmentImages[eqId];
        equipmentMap.set(groupKey, {
          eqId,
          title: info ? info.title : eqId,
          sku: info ? info.sku : '',
          count: 0,
          imageUrl: info ? info.imageUrl : '',
          url: info ? info.url : '',
          vendor: info ? (info.vendor || '') : '',
          productType: info ? (info.productType || '') : '', // for the packet's High-Level list
          shopifyId: info ? info.shopifyId : 0,
          widthIn: info ? info.widthIn : 0,
          depthIn: info ? info.depthIn : 0,
          spaceName: spaceName,
        });
      }
      equipmentMap.get(groupKey).count++;
    }

    const equipmentItems = [];
    for (const [groupKey, data] of equipmentMap) {
      let hash = 0;
      for (let c = 0; c < groupKey.length; c++) hash = ((hash << 5) - hash + groupKey.charCodeAt(c)) | 0;
      const syntheticVariantId = -(Math.abs(hash) || 1);
      const eqColors = rd._getEquipColorNames ? rd._getEquipColorNames(data.vendor, data.eqId) : { frame: null, pad: null };
      equipmentItems.push({
        variantId: syntheticVariantId,
        productId: data.shopifyId || 0,
        eqId: data.eqId,
        productType: data.productType || '',
        title: data.title,
        sku: data.sku || '',
        vendor: data.vendor || '',
        price: '0',
        quantity: data.count,
        image: data.imageUrl,
        url: data.url,
        // A hardwood platform with the decal is a review item, the same way the rubber inlay logo is
        note: (this._hardwoodLogo && this._hardwoodLogo.dataURL && window.RoomDesignerRender && RoomDesignerRender.HARDWOOD_PLATFORM_SKUS.has(String(data.sku || ''))) ? 'Custom logo decal (review required)' : '',
        dimensions: `${Math.round(data.widthIn)}" × ${Math.round(data.depthIn)}"`,
        frameColor: (eqColors.frame && eqColors.frame !== '-') ? eqColors.frame : '',
        padColor: (eqColors.pad && eqColors.pad !== '-') ? eqColors.pad : '',
        source: 'room-designer',
        spaceName: data.spaceName,
      });
    }

    // Collect turf items grouped by (turfProductId, turfColorId, spaceName, customizations)
    const turfItems = [];
    const turfMap = new Map();
    for (const obj of rd.objects) {
      if (obj.type !== 'turf' || !obj.turfProductId) continue;
      const sp = findSpaceForObj(obj);
      const spaceName = sp ? sp.spaceName : '';
      const turfProduct = this._getProduct(obj.turfProductId);
      const customs = this._serializeTurfCustomizations(obj, turfProduct);
      const groupKey = `${obj.turfProductId}|${obj.turfColorId || ''}|${spaceName}|${customs.summary}`;

      if (!turfMap.has(groupKey)) {
        const product = turfProduct;
        const color = product ? product.colors.find(c => c.id === obj.turfColorId) : null;
        turfMap.set(groupKey, {
          product, color, spaceName, customs,
          pieces: [], totalArea: 0
        });
      }
      const g = turfMap.get(groupKey);
      g.pieces.push({
        dims: obj.turfZone ? 'Filled area' : `${rd.formatMeasurement(obj.width)} × ${rd.formatMeasurement(obj.depth)}`,
        area: Math.round(this._turfPieceArea(obj))
      });
      g.totalArea += this._turfPieceArea(obj);
    }

    for (const [groupKey, data] of turfMap) {
      let hash = 0;
      for (let c = 0; c < groupKey.length; c++) hash = ((hash << 5) - hash + groupKey.charCodeAt(c)) | 0;
      const syntheticVariantId = -(Math.abs(hash) || 1);
      turfItems.push({
        variantId: syntheticVariantId,
        productId: data.product ? (data.product.shopifyId || 0) : 0,
        title: data.product ? data.product.name : 'Turf',
        variantTitle: data.color ? data.color.name : '',
        vendor: (data.product && data.product.vendor) || 'Blue Sky Fitness Supply',
        sku: 'CUSTOM',
        price: '0',
        quantity: data.pieces.length,
        _totalAreaSqFt: Math.round(data.totalArea),
        image: data.color ? (data.color.imageUrl || '') : '',
        url: data.product ? (data.product.url || '') : '',
        note: '',
        source: 'room-designer',
        spaceName: data.spaceName,
        itemType: 'turf',
        turfCustomizations: { ...data.customs, pieces: data.pieces }
      });
    }

    // Collect platform items — group identical platforms by config
    const _platGroups = {};
    for (const obj of rd.objects) {
      if (obj.type !== 'platform') continue;
      const presetLabel = obj.preset ? obj.preset.replace('x', "' × ") + "'" : (rd.formatMeasurement(obj.width) + ' × ' + rd.formatMeasurement(obj.depth));
      let description = 'Inlaid Platform ' + presetLabel;
      if (obj.wings) description += ' with wings';
      if (obj.colorConfirmed) {
        description += ' — Center: ' + (obj.centerColorName || 'Custom');
        if (obj.wings) description += ', Wings: ' + (obj.wingColorName || 'Custom');
      }
      const hasLogo = !!(this._platformLogo && this._platformLogo.dataURL);
      if (hasLogo) description += ' — Custom logo (review required)';
      const key = description;
      if (_platGroups[key]) {
        _platGroups[key].quantity++;
      } else {
        _platGroups[key] = {
          variantId: 0,
          productId: 0,
          title: 'CUSTOM Inlaid Weightlifting Platform',
          variantTitle: presetLabel,
          vendor: 'Blue Sky Fitness Supply',
          sku: 'CUSTOM',
          price: '0',
          quantity: 1,
          image: '',
          url: '',
          note: '',
          source: 'room-designer',
          spaceName: '',
          itemType: 'platform',
          dimensions: presetLabel,
          platformDescription: description
        };
      }
    }
    const platformItems = Object.values(_platGroups);

    // Build space summary for PDF
    const spaceSummary = [];
    let totalArea = 0;
    let ssIdx = 0;
    for (const room of rd.rooms) {
      if (!room.closed || room.type !== 'add') continue;
      ssIdx++;
      const stats = rd._computeSpaceStats(room);
      totalArea += stats.area;
      spaceSummary.push({
        name: room.name || `Space ${ssIdx}`,
        area: Math.round(stats.area),
        doorCount: stats.doorCount,
        perimeterLF: Math.round(stats.perimeterLF),
      });
    }

    return { flooringItems, equipmentItems, turfItems, platformItems, flooringSwatches, spaceSummary, totalArea: Math.round(totalArea) };
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21) — Save-first prompt
     for the Create-a-Quote flow. Fires when the rep clicks Create a Quote
     on a design that hasn't been portal-saved yet. Returns Promise<{name}>
     on confirm or null on cancel. Cancel aborts the handoff entirely
     (rep can't quote off an unsaved plan — the dashboard's LINKED FILES
     section requires a designId to link back to). Reuses the existing
     .rd-qmp* styles (injected by _showQuoteModePicker) so visual language
     matches the next modal in the flow. */
  async _showSaveDesignPrompt(currentName, onSave) {
    return new Promise((resolve) => {
      // Fallback style injection — only used when the Mode picker hasn't
      // been opened yet this session (its larger style block is the source
      // of truth and includes all the option / radio / list / footer
      // rules). Includes the subset of .rd-qmp* rules my modal uses, in
      // BOTH light AND dark variants — the previous fallback only had
      // light, so the save-first prompt rendered white-on-white in dark
      // mode when it fired before the picker. Separate ID from the
      // picker's main style block so the picker can still inject its
      // full set later; the overlapping panel/head/title/close rules are
      // identical between the two so source-order conflicts are a no-op.
      if (!document.getElementById('rd-quote-mode-picker-styles') && !document.getElementById('rd-qmp-save-first-styles')) {
        const style = document.createElement('style');
        style.id = 'rd-qmp-save-first-styles';
        style.textContent = ''
          + '.rd-qmp{position:fixed;inset:0;z-index:99999;display:none;align-items:center;justify-content:center;background:rgba(0,15,28,.55);padding:16px;font-family:inherit}'
          + '.rd-qmp.visible{display:flex}'
          + '.rd-qmp__panel{background:#fff;border-radius:14px;max-width:580px;width:100%;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 18px 50px rgba(0,0,0,.35)}'
          + '.rd-qmp__head{padding:18px 22px;border-bottom:1px solid #EAEAEA;display:flex;align-items:center;justify-content:space-between}'
          + '.rd-qmp__title{font-size:17px;font-weight:600;color:#000F1C;margin:0}'
          + '.rd-qmp__close{background:none;border:none;cursor:pointer;padding:4px 10px;font-size:24px;line-height:1;color:#5a6b78}'
          + '.rd-qmp__close:hover{color:#000F1C}'
          + '.rd-qmp__body{padding:18px 22px;overflow-y:auto;flex:1 1 auto}'
          // Dark mode variants — applied via .rd-qmp--dark on the overlay
          + '.rd-qmp--dark{background:rgba(0,0,0,.7)}'
          + '.rd-qmp--dark .rd-qmp__panel{background:#1a2735;box-shadow:0 18px 50px rgba(0,0,0,.6)}'
          + '.rd-qmp--dark .rd-qmp__head{border-bottom-color:#2a3a4a}'
          + '.rd-qmp--dark .rd-qmp__title{color:#E5EDF5}'
          + '.rd-qmp--dark .rd-qmp__close{color:#8997a3}'
          + '.rd-qmp--dark .rd-qmp__close:hover{color:#E5EDF5}';
        document.head.appendChild(style);
      }
      // Dark mode detection — matches _showQuoteModePicker. Modal lives on
      // document.body (outside both .room-designer and .rep-quote-portal-
      // wrap), so we detect by querying for the active dark-mode class on
      // either container. Adding `rd-qmp--dark` to the overlay lets the
      // existing dark CSS rules style the panel / head / title / close
      // (defined alongside _showQuoteModePicker's styles around L16562+).
      // Inline styles on the body/footer/input/buttons can't be reached by
      // the existing rules, so we resolve them once into a `c` map.
      const isDark = !!(
        document.querySelector('.room-designer.dark-mode') ||
        document.querySelector('.rep-quote-portal-wrap.rep-quote-dark')
      );
      const c = isDark ? {
        help: '#8997a3', label: '#E5EDF5',
        inputBg: '#0f1822', inputColor: '#E5EDF5', inputBorder: '#2a3a4a', inputFocus: '#3399FF',
        footerBorder: '#2a3a4a',
        cancelBg: 'transparent', cancelColor: '#E5EDF5', cancelBorder: '#2a3a4a',
        saveBg: '#3399FF', saveColor: '#fff', saveBorder: '#3399FF',
        errorColor: '#f87171',
      } : {
        help: '#5a6b78', label: '#000F1C',
        inputBg: '#ffffff', inputColor: '#000F1C', inputBorder: '#EAEAEA', inputFocus: '#006BCC',
        footerBorder: '#EAEAEA',
        cancelBg: '#ffffff', cancelColor: '#000F1C', cancelBorder: '#EAEAEA',
        saveBg: '#006BCC', saveColor: '#ffffff', saveBorder: '#006BCC',
        errorColor: '#b91c1c',
      };
      const wrap = document.createElement('div');
      wrap.className = 'rd-qmp rd-qmp--save-first visible' + (isDark ? ' rd-qmp--dark' : '');
      wrap.innerHTML = ''
        + '<div class="rd-qmp__panel" style="max-width:460px;">'
        +   '<div class="rd-qmp__head">'
        +     '<h3 class="rd-qmp__title">Save your floor plan first</h3>'
        +     '<button type="button" class="rd-qmp__close" aria-label="Cancel">&times;</button>'
        +   '</div>'
        +   '<div class="rd-qmp__body">'
        +     '<p style="font-size:13px;color:' + c.help + ';margin:0 0 14px;line-height:1.45;">'
        +       'Name your design before creating a quote. The Deal Dashboard links this saved file back to the quote so you can re-open it any time from the LINKED FILES section.'
        +     '</p>'
        +     '<label style="display:block;font-size:12px;font-weight:600;color:' + c.label + ';margin-bottom:6px;text-transform:uppercase;letter-spacing:0.3px;">Design name</label>'
        +     '<input type="text" class="rd-qmp__name-input" placeholder="e.g. Smith Gym — Main Floor" value="' + String(currentName || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;') + '" style="width:100%;padding:10px 12px;border:1.5px solid ' + c.inputBorder + ';border-radius:8px;font-size:14px;font-family:inherit;box-sizing:border-box;outline:none;background:' + c.inputBg + ';color:' + c.inputColor + ';">'
        +     '<div class="rd-qmp__name-error" style="display:none;font-size:12px;color:' + c.errorColor + ';margin-top:6px;">Name is required.</div>'
        +   '</div>'
        +   '<div style="padding:14px 22px;border-top:1px solid ' + c.footerBorder + ';display:flex;justify-content:flex-end;gap:8px;">'
        +     '<button type="button" class="rd-qmp__cancel" style="padding:8px 16px;border:1.5px solid ' + c.cancelBorder + ';border-radius:8px;background:' + c.cancelBg + ';color:' + c.cancelColor + ';cursor:pointer;font-size:13px;font-weight:500;">Cancel</button>'
        +     '<button type="button" class="rd-qmp__save-confirm" style="padding:8px 18px;border:1.5px solid ' + c.saveBorder + ';border-radius:8px;background:' + c.saveBg + ';color:' + c.saveColor + ';cursor:pointer;font-size:13px;font-weight:600;">Save &amp; Continue</button>'
        +   '</div>'
        + '</div>';
      document.body.appendChild(wrap);
      const input = wrap.querySelector('.rd-qmp__name-input');
      const errorEl = wrap.querySelector('.rd-qmp__name-error');
      const saveBtn = wrap.querySelector('.rd-qmp__save-confirm');
      const cancelBtn = wrap.querySelector('.rd-qmp__cancel');
      const closeBtn = wrap.querySelector('.rd-qmp__close');
      setTimeout(() => { try { input.focus(); input.select(); } catch (e) {} }, 30);
      // _saving guards every dismiss path while the portal save is in
      // flight — otherwise Esc / click-outside / Cancel could remove the
      // modal mid-request and leave the rep on a half-state with no
      // visible feedback.
      let _saving = false;
      function cleanup(result) {
        document.removeEventListener('keydown', onKey);
        if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
        resolve(result);
      }
      function onKey(e) {
        if (_saving) return;
        if (e.key === 'Escape') { cleanup(null); return; }
        if (e.key === 'Enter') { e.preventDefault(); saveBtn.click(); }
      }
      document.addEventListener('keydown', onKey);
      cancelBtn.addEventListener('click', () => { if (!_saving) cleanup(null); });
      closeBtn.addEventListener('click', () => { if (!_saving) cleanup(null); });
      wrap.addEventListener('click', (e) => { if (!_saving && e.target === wrap) cleanup(null); });
      input.addEventListener('input', () => {
        errorEl.style.display = 'none';
        input.style.borderColor = c.inputBorder;
      });
      saveBtn.addEventListener('click', async () => {
        const name = (input.value || '').trim();
        if (!name) {
          errorEl.textContent = 'Name is required.';
          errorEl.style.display = 'block';
          input.style.borderColor = c.errorColor;
          try { input.focus(); } catch (e) {}
          return;
        }
        saveBtn.textContent = 'Saving…';
        saveBtn.disabled = true;
        cancelBtn.disabled = true;
        closeBtn.disabled = true;
        input.disabled = true;
        _saving = true;
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21) — Run the save
           INSIDE the modal so it stays visible (showing "Saving…") during
           the portal round-trip. Closing the modal first + then awaiting
           the save outside left ~3s of empty space where the rep could
           re-click Create a Quote and trigger a duplicate handoff. */
        if (typeof onSave === 'function') {
          try {
            await onSave(name);
            cleanup({ name });
          } catch (err) {
            _saving = false;
            saveBtn.textContent = 'Save & Continue';
            saveBtn.disabled = false;
            cancelBtn.disabled = false;
            closeBtn.disabled = false;
            input.disabled = false;
            errorEl.textContent = 'Save failed: ' + (err && err.message ? err.message : 'unknown error') + ' — try again.';
            errorEl.style.display = 'block';
            input.style.borderColor = c.errorColor;
          }
        } else {
          cleanup({ name });
        }
      });
    });
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Quote handoff mode picker
     Lets reps choose between (1) brand new quote, (2) new revision in an existing
     chain, or (3) appending items to an existing quote. Portal mode only — falls
     back to mode 1 silently if RepPortal isn't available.
     Ryan 2026-05-21: optional linkedDeals arg — array of { ref, linkedAt,
     linkedBy } pulled from design.linkedDeals. When non-empty, Mode 3 is
     pre-selected with label "Append to {ref}" and targetRef pre-filled
     so the rep doesn't have to search.
     Returns Promise<{mode, baseRef?, targetRef?} | null> (null = cancelled). */
  async _showQuoteModePicker(linkedDeals) {
    return new Promise((resolve) => {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — ⛔ `linkedDeals[0]` THREW AWAY THE REST OF
         THE CHAIN. Ryan: *"the 3rd option ignores that versions -B and -C for this customer exist
         too... I'd like the option to pick between Appending to RW-1376-A, RW-1376-B, and
         RW-1376-C as well."*

         `design.linkedDeals` is genuinely a multi-entry array — a new design revision INHERITS the
         whole array (design-routes.js), and linking one version links every sibling — so a design
         worked across -A, -B and -C carries all three. `[0]` is insertion order, i.e. the first
         deal ever linked, which is almost always the -A. The picker then suppressed its own list
         and pre-committed that ref before the rep touched anything.

         ⚠️ THE DEFAULT IS THE HIGHEST REVISION LETTER, NOT `[0]` AND NOT THE LAST ELEMENT. Array
         order is insertion order and the linking loop does not promise one; the letter is the
         thing that actually orders a chain everywhere else in the portal. */
      const _revLetterOf = (r) => { const m = String(r || '').match(/-([A-Z]+)$/i); return m ? m[1].toUpperCase() : ''; };
      const _byRevision = (a, b) => {
        const la = _revLetterOf(a), lb = _revLetterOf(b);
        if (la.length !== lb.length) return la.length - lb.length;   // -AA sorts above -Z
        return la.localeCompare(lb);
      };
      const _linkedRefs = (Array.isArray(linkedDeals) ? linkedDeals : [])
        .map(d => (d && d.ref) ? String(d.ref) : '')
        .filter(Boolean)
        .filter((r, i, a) => a.indexOf(r) === i)
        .sort(_byRevision);
      const _backlinkRef = _linkedRefs.length ? _linkedRefs[_linkedRefs.length - 1] : null;
      function escapeHtml(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
      }
      const baseRefOf = (ref) => (ref || '').replace(/-[A-Z]+$/, '');

      // Quote ownership — reps can only revise/append their OWN quotes via this picker.
      // /rep-quotes is already scoped server-side for non-admins; for admins it returns
      // everything, so this client-side filter scopes the picker for them too. Mirrors
      // the worker's extractOrderRepInitials logic (auth.js): prefer the rep:XX tag,
      // fall back to the ref prefix (RW-1059-A → RW), excluding generic prefixes.
      const GENERIC_REF_PREFIXES = new Set(['QUOTE', 'PLAN', 'WEIGHT', 'EQLIST']);
      const getMyInitials = () => {
        try {
          const sess = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession && window.RepPortal.Auth.getSession();
          if (!sess) return '';
          return (sess.initials || (sess.code || '').replace(/[0-9]/g, '')).toUpperCase();
        } catch (e) { return ''; }
      };
      const ownedByMe = (q, me) => {
        if (!me) return false;
        const tagged = (q.rep || '').toUpperCase();
        if (tagged) return tagged === me;
        const ref = q.reference || q.ref || '';
        const m = ref.match(/^([A-Z]{2,4})-(?:BSFS-)?\d+-[A-Z]+$/i);
        if (!m) return false;
        const prefix = m[1].toUpperCase();
        if (GENERIC_REF_PREFIXES.has(prefix)) return false;
        return prefix === me;
      };

      // Timestamp parsing — worker returns createdAt as an ISO string from Shopify GraphQL.
      // Compared against a numeric 0 (initial _ts), JS coerces the string to NaN and NaN >= 0
      // is false — _latestQuote would stay null and the chain row falls back to "Unknown".
      // Parse to ms once, then all comparisons + sorts work in number space.
      const parseTs = (v) => {
        if (!v) return 0;
        if (typeof v === 'number') return v;
        const n = Date.parse(v);
        return isNaN(n) ? 0 : n;
      };

      // Display name for a quote — prefers organization, falls back to the contact's name
      // when org is a residence marker ("n/a - residence", "residence", "n/a", "home gym",
      // etc.). Matches the same n/a fallback logic the Workstation uses for residential
      // quote PDF filenames so the picker stays consistent with the rest of the tool.
      const isResidenceOrg = (org) => {
        if (!org) return false;
        const norm = String(org).toLowerCase().replace(/[^a-z]/g, '');
        return norm === '' || norm === 'residence' || norm === 'na' || norm === 'naresidence' || norm === 'home' || norm === 'homegym';
      };
      const displayNameFor = (q) => {
        const org = (q.customerCompany || '').trim();
        const name = (q.customer || '').trim();
        if (org && !isResidenceOrg(org)) return org;
        if (name && name !== 'Unknown') return name;
        return 'Unknown';
      };

      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-10-15) — "queue off the name of the saved layout,
         and look for deals with a same or similar name and pull them in first... matching even just a
         single word". With the search box empty, quotes whose customer shares a word with the layout
         name sort first (more shared words first, then newest), under the linked ones. Generic words
         ("Home Gym", "High School", "Basement") never count, or every school would match. */
      const _NAME_STOP = new Set(('the and for with from of at in on to a an llc inc co corp company ltd '
        + 'gym gyms home house residence fitness training weight weights room rooms space spaces plan plans '
        + 'layout layouts floor floors flooring design designs quote quotes rev revision new old copy draft '
        + 'school schools high middle elementary academy center centre facility club studio basement garage '
        + 'rubber turf area main upstairs downstairs north south east west unknown').split(' '));
      const _nameWords = (str) => String(str || '').toLowerCase().replace(/@.*$/, ' ').split(/[^a-z0-9]+/)
        .filter(w => w.length >= 3 && !_NAME_STOP.has(w) && !/^\d+$/.test(w));
      let _layoutName = '';
      try { const _ni = document.getElementById('designNameInput'); if (_ni) _layoutName = (_ni.value || '').trim(); } catch (e) {}
      const _layoutWords = [...new Set(_nameWords(_layoutName))];
      const _wordHit = (a, b) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));
      const _nameScore = (q) => {
        if (!_layoutWords.length || !q) return 0;
        const words = _nameWords([q.customerCompany, q.customer, q.customerEmail].join(' '));
        let n = 0;
        for (const lw of _layoutWords) if (words.some(w => _wordHit(lw, w))) n++;
        return n;
      };
      const _matchTag = ' <span style="font-size:11px;font-weight:600;color:#166534;background:rgba(22,163,74,.12);padding:1px 7px;border-radius:999px;">Name match</span>';

      // Inject styles once
      if (!document.getElementById('rd-quote-mode-picker-styles')) {
        const style = document.createElement('style');
        style.id = 'rd-quote-mode-picker-styles';
        style.textContent = `
.rd-qmp { position:fixed; inset:0; z-index:99999; display:none; align-items:center; justify-content:center; background:rgba(0,15,28,.55); padding:16px; font-family:inherit; }
.rd-qmp.visible { display:flex; }
.rd-qmp__panel { background:#fff; border-radius:14px; max-width:580px; width:100%; max-height:90vh; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 18px 50px rgba(0,0,0,.35); }
.rd-qmp__head { padding:18px 22px; border-bottom:1px solid #EAEAEA; display:flex; align-items:center; justify-content:space-between; }
.rd-qmp__title { font-size:17px; font-weight:600; color:#000F1C; margin:0; }
.rd-qmp__close { background:none; border:none; cursor:pointer; padding:4px 10px; font-size:24px; line-height:1; color:#5a6b78; }
.rd-qmp__body { padding:18px 22px; overflow-y:auto; flex:1 1 auto; }
.rd-qmp__opt { display:block; border:1.5px solid #EAEAEA; border-radius:10px; padding:14px 16px; margin-bottom:10px; cursor:pointer; transition:border-color .15s, background .15s; }
.rd-qmp__opt:hover { border-color:#006BCC; }
.rd-qmp__opt--sel { border-color:#006BCC; background:#F2F8FF; }
.rd-qmp__opt-row { display:flex; gap:10px; align-items:flex-start; }
.rd-qmp__radio { width:18px; height:18px; flex-shrink:0; margin-top:2px; appearance:none; border:1.5px solid #B8C4CF; border-radius:50%; cursor:pointer; position:relative; }
.rd-qmp__radio:checked { border-color:#006BCC; }
.rd-qmp__radio:checked::after { content:''; position:absolute; inset:3px; border-radius:50%; background:#006BCC; }
.rd-qmp__opt-body { flex:1; min-width:0; }
.rd-qmp__opt-label { font-size:14px; font-weight:600; color:#000F1C; }
.rd-qmp__opt-desc { font-size:12.5px; color:#5a6b78; margin-top:3px; line-height:1.4; }
.rd-qmp__sub { margin-top:12px; padding-top:12px; border-top:1px dashed #EAEAEA; display:none; }
.rd-qmp__opt--sel .rd-qmp__sub { display:block; }
.rd-qmp__search { width:100%; padding:9px 12px; font-size:14px; border:1.5px solid #D5DDE5; border-radius:8px; box-sizing:border-box; }
.rd-qmp__search:focus { outline:none; border-color:#006BCC; }
.rd-qmp__list { margin-top:10px; max-height:200px; overflow-y:auto; border:1px solid #EAEAEA; border-radius:8px; }
.rd-qmp__list-item { padding:10px 14px; cursor:pointer; border-bottom:1px solid #F2F2F2; display:flex; flex-direction:column; gap:2px; transition:background .12s; }
.rd-qmp__list-item:last-child { border-bottom:none; }
.rd-qmp__list-item:hover { background:#F9F9F9; }
.rd-qmp__list-item--picked { background:#F2F8FF; border-left:3px solid #006BCC; padding-left:11px; }
.rd-qmp__list-org { font-size:13px; font-weight:600; color:#000F1C; }
.rd-qmp__list-meta { font-size:11.5px; color:#5a6b78; }
/* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — the append-as-revision choice. */
.rd-qmp__asrev { display:flex; align-items:flex-start; gap:8px; margin-top:10px; font-size:12.5px; color:#1f2d3a; cursor:pointer; }
.rd-qmp__asrev input { margin-top:2px; flex:0 0 auto; }
.rd-qmp__asrev-note { margin-top:6px; font-size:11.5px; line-height:1.45; color:#5a6b78; }
.rd-qmp__asrev-note:empty { display:none; }
.rd-qmp__asrev-note--warn { color:#9A3412; font-weight:600; }
.rd-qmp__list-empty, .rd-qmp__list-loading { padding:18px; text-align:center; font-size:13px; color:#8997a3; }
.rd-qmp__foot { padding:14px 22px; border-top:1px solid #EAEAEA; display:flex; gap:10px; justify-content:flex-end; }
.rd-qmp__btn { padding:9px 18px; font-size:14px; font-weight:600; border-radius:8px; border:none; cursor:pointer; }
.rd-qmp__btn--cancel { background:#F2F2F2; color:#000F1C; }
.rd-qmp__btn--cancel:hover { background:#E5E5E5; }
.rd-qmp__btn--continue { background:#006BCC; color:#fff; }
.rd-qmp__btn--continue:hover { background:#0058a8; }
.rd-qmp__btn--continue:disabled { background:#B8C4CF; cursor:not-allowed; }

/* Dark mode — applied via .rd-qmp--dark on the overlay (modal lives on document.body,
   outside .room-designer / .rep-quote-portal-wrap, so the in-app dark scopes can't reach it) */
.rd-qmp--dark { background:rgba(0,0,0,.7); }
.rd-qmp--dark .rd-qmp__panel { background:#1a2735; box-shadow:0 18px 50px rgba(0,0,0,.6); }
.rd-qmp--dark .rd-qmp__head { border-bottom-color:#2a3a4a; }
.rd-qmp--dark .rd-qmp__title { color:#E5EDF5; }
.rd-qmp--dark .rd-qmp__close { color:#8997a3; }
.rd-qmp--dark .rd-qmp__close:hover { color:#E5EDF5; }
.rd-qmp--dark .rd-qmp__opt { border-color:#2a3a4a; }
.rd-qmp--dark .rd-qmp__opt:hover { border-color:#3399FF; }
.rd-qmp--dark .rd-qmp__opt--sel { border-color:#3399FF; background:rgba(51,153,255,.08); }
.rd-qmp--dark .rd-qmp__radio { border-color:#5a6b78; background:transparent; }
.rd-qmp--dark .rd-qmp__radio:checked { border-color:#3399FF; }
.rd-qmp--dark .rd-qmp__radio:checked::after { background:#3399FF; }
.rd-qmp--dark .rd-qmp__opt-label { color:#E5EDF5; }
.rd-qmp--dark .rd-qmp__opt-desc { color:#8997a3; }
.rd-qmp--dark .rd-qmp__sub { border-top-color:#2a3a4a; }
.rd-qmp--dark .rd-qmp__search { background:#0f1822; color:#E5EDF5; border-color:#2a3a4a; }
.rd-qmp--dark .rd-qmp__search::placeholder { color:#5a6b78; }
.rd-qmp--dark .rd-qmp__search:focus { border-color:#3399FF; }
.rd-qmp--dark .rd-qmp__list { border-color:#2a3a4a; background:#0f1822; }
.rd-qmp--dark .rd-qmp__list-item { border-bottom-color:#1f2c3a; }
.rd-qmp--dark .rd-qmp__list-item:hover { background:#1a2735; }
.rd-qmp--dark .rd-qmp__list-item--picked { background:rgba(51,153,255,.12); border-left-color:#3399FF; }
.rd-qmp--dark .rd-qmp__list-org { color:#E5EDF5; }
.rd-qmp--dark .rd-qmp__list-org span { color:#8997a3 !important; }
.rd-qmp--dark .rd-qmp__list-meta { color:#8997a3; }
.rd-qmp--dark .rd-qmp__asrev { color:#dce6ee; }
.rd-qmp--dark .rd-qmp__asrev-note { color:#8997a3; }
.rd-qmp--dark .rd-qmp__asrev-note--warn { color:#F9A97A; }
.rd-qmp--dark .rd-qmp__list-empty, .rd-qmp--dark .rd-qmp__list-loading { color:#5a6b78; }
.rd-qmp--dark .rd-qmp__foot { border-top-color:#2a3a4a; }
.rd-qmp--dark .rd-qmp__btn--cancel { background:#2a3a4a; color:#E5EDF5; }
.rd-qmp--dark .rd-qmp__btn--cancel:hover { background:#34465a; }
.rd-qmp--dark .rd-qmp__btn--continue { background:#3399FF; }
.rd-qmp--dark .rd-qmp__btn--continue:hover { background:#1a85ff; }
.rd-qmp--dark .rd-qmp__btn--continue:disabled { background:#2a3a4a; color:#5a6b78; }
`;
        document.head.appendChild(style);
      }

      // Dark mode detection — modal lives on document.body, outside both the
      // .room-designer wrapper (RD has its own dark toggle) and the
      // .rep-quote-portal-wrap.rep-quote-dark scope (rep portal's toggle).
      // Either being active means the rep is in dark mode visually.
      const isDark = !!(
        document.querySelector('.room-designer.dark-mode') ||
        document.querySelector('.rep-quote-portal-wrap.rep-quote-dark')
      );

      const overlay = document.createElement('div');
      overlay.className = 'rd-qmp' + (isDark ? ' rd-qmp--dark' : '');
      overlay.innerHTML = `
        <div class="rd-qmp__panel">
          <div class="rd-qmp__head">
            <h3 class="rd-qmp__title">Send Floor Plan to Workstation</h3>
            <button type="button" class="rd-qmp__close" aria-label="Close">&times;</button>
          </div>
          <div class="rd-qmp__body">
            <label class="rd-qmp__opt${_backlinkRef ? '' : ' rd-qmp__opt--sel'}" data-mode="new">
              <div class="rd-qmp__opt-row">
                <input type="radio" name="rd-qmp-mode" value="new" class="rd-qmp__radio"${_backlinkRef ? '' : ' checked'}>
                <div class="rd-qmp__opt-body">
                  <div class="rd-qmp__opt-label">Brand new quote</div>
                  <div class="rd-qmp__opt-desc">Start a fresh quote with a new reference number (e.g. AB-1000-A).</div>
                </div>
              </div>
            </label>
            <label class="rd-qmp__opt" data-mode="revision">
              <div class="rd-qmp__opt-row">
                <input type="radio" name="rd-qmp-mode" value="revision" class="rd-qmp__radio">
                <div class="rd-qmp__opt-body">
                  <div class="rd-qmp__opt-label">New revision in an existing chain</div>
                  <div class="rd-qmp__opt-desc">Pick a base reference (e.g. AB-1000) and the next letter is created from this floor plan (e.g. AB-1000-B).</div>
                  <div class="rd-qmp__sub">
                    <input type="text" class="rd-qmp__search" data-search="revision" placeholder="Search base ref or company...">
                    <div class="rd-qmp__list" data-list="revision"><div class="rd-qmp__list-loading">Loading quotes...</div></div>
                  </div>
                </div>
              </div>
            </label>
            <label class="rd-qmp__opt${_backlinkRef ? ' rd-qmp__opt--sel' : ''}" data-mode="append">
              <div class="rd-qmp__opt-row">
                <input type="radio" name="rd-qmp-mode" value="append" class="rd-qmp__radio"${_backlinkRef ? ' checked' : ''}>
                <div class="rd-qmp__opt-body">
                  <div class="rd-qmp__opt-label">Add to an existing quote</div>
                  <div class="rd-qmp__opt-desc">${_linkedRefs.length
                    ? 'This floor plan is linked to ' + escapeHtml(_linkedRefs.join(', ')) + '. Pick which one its items go into.'
                    : "Add this floor plan's items to an existing quote you've already begun working on."}</div>
                  <div class="rd-qmp__sub">
                    <input type="text" class="rd-qmp__search" data-search="append" placeholder="Search ref, company, or contact...">
                    <div class="rd-qmp__list" data-list="append"><div class="rd-qmp__list-loading">Loading quotes...</div></div>
                    <!-- CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) - appending in place EDITS the
                         Shopify draft the customer can already see. Ryan: "what reps will do is
                         append to that quote, then immediately 'Save as' - so maybe this is how you
                         do it, just combine those things somehow from the GLD." So that is one
                         move here, and it is the DEFAULT the moment the target has been sent. -->
                    <label class="rd-qmp__asrev">
                      <input type="checkbox" data-asrev>
                      <span data-asrev-label>Save as a new revision instead of changing this quote</span>
                    </label>
                    <div class="rd-qmp__asrev-note" data-asrev-note></div>
                  </div>
                </div>
              </div>
            </label>
          </div>
          <div class="rd-qmp__foot">
            <button type="button" class="rd-qmp__btn rd-qmp__btn--cancel">Cancel</button>
            <button type="button" class="rd-qmp__btn rd-qmp__btn--continue">Continue</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('visible'));

      let selectedMode = _backlinkRef ? 'append' : 'new';
      let selectedBaseRef = null;
      let selectedTargetRef = _backlinkRef || null;
      let quotesCache = null;
      let quotesLoadStarted = false;

      const opts = overlay.querySelectorAll('.rd-qmp__opt');
      const radios = overlay.querySelectorAll('.rd-qmp__radio');
      const continueBtn = overlay.querySelector('.rd-qmp__btn--continue');
      const cancelBtn = overlay.querySelector('.rd-qmp__btn--cancel');
      const closeBtn = overlay.querySelector('.rd-qmp__close');

      const updateContinueState = () => {
        if (selectedMode === 'new') continueBtn.disabled = false;
        else if (selectedMode === 'revision') continueBtn.disabled = !selectedBaseRef;
        else if (selectedMode === 'append') continueBtn.disabled = !selectedTargetRef;
      };

      const showLoadError = (msg) => {
        overlay.querySelector('[data-list="revision"]').innerHTML = `<div class="rd-qmp__list-empty">${escapeHtml(msg)}</div>`;
        overlay.querySelector('[data-list="append"]').innerHTML = `<div class="rd-qmp__list-empty">${escapeHtml(msg)}</div>`;
      };

      const fetchQuotes = async () => {
        if (quotesCache !== null) return quotesCache;
        if (!window.RepPortal || !window.RepPortal.API) { showLoadError('Rep portal session unavailable.'); return null; }
        const me = getMyInitials();
        if (!me) { showLoadError('Could not identify your rep account.'); quotesCache = []; return quotesCache; }
        try {
          const data = await this._prefetchRepQuotes();
          if (!data) throw new Error('quote list unavailable');
          const all = data.quotes || [];
          // Scope to current rep only — defense in depth (server already filters non-admins;
          // this also scopes admins so revise/append can only target THEIR own quotes).
          quotesCache = all.filter(q => ownedByMe(q, me));
          return quotesCache;
        } catch (err) {
          console.error('[RD] Failed to load rep quotes:', err);
          showLoadError('Could not load quotes. Try again.');
          return null;
        }
      };

      const renderRevisionList = (filter) => {
        const listEl = overlay.querySelector('[data-list="revision"]');
        if (!quotesCache) {
          // Still loading: the chains this floor plan is linked to are known already — show them now.
          const bases = [...new Set(_linkedRefs.map(baseRefOf))].filter(Boolean);
          if (!bases.length || quotesLoadStarted === 'failed') return;
          listEl.innerHTML = bases.map(b => `<div class="rd-qmp__list-item${b === selectedBaseRef ? ' rd-qmp__list-item--picked' : ''}" data-base-ref="${escapeHtml(b)}">
            <div class="rd-qmp__list-org">${escapeHtml(b)} <span style="font-size:11px;font-weight:600;color:#075985;background:rgba(2,132,199,.12);padding:1px 7px;border-radius:999px;">Linked</span></div>
            <div class="rd-qmp__list-meta">Linked to this floor plan</div></div>`).join('')
            + '<div class="rd-qmp__list-loading">Loading your other quotes...</div>';
          listEl.querySelectorAll('.rd-qmp__list-item').forEach(el => el.addEventListener('click', () => {
            selectedBaseRef = el.getAttribute('data-base-ref');
            renderRevisionList(overlay.querySelector('[data-search="revision"]').value);
            updateContinueState();
          }));
          return;
        }
        const chains = {};
        for (const q of quotesCache) {
          const ref = q.reference || q.ref || '';
          const base = baseRefOf(ref);
          if (!base || base === ref) continue;
          const ts = parseTs(q.updatedAt || q.createdAt);
          if (!chains[base]) chains[base] = { _base: base, _ts: -1, _letters: new Set(), _latestQuote: null };
          const entry = chains[base];
          entry._letters.add((ref.match(/-([A-Z]+)$/) || [])[1] || '');
          if (ts >= entry._ts) {
            entry._ts = ts;
            entry._latestQuote = q;
          }
        }
        let chainList = Object.values(chains);
        const f = (filter || '').trim().toLowerCase();
        if (f) {
          chainList = chainList.filter(c => {
            const lq = c._latestQuote || {};
            const search = ((c._base || '') + ' ' + (lq.customerCompany || '') + ' ' + (lq.customer || '')).toLowerCase();
            return search.indexOf(f) !== -1;
          });
        }
        chainList.sort((a, b) => (b._ts || 0) - (a._ts || 0));
        const _linkedBases = new Set(_linkedRefs.map(baseRefOf));
        if (!f) {
          for (const c of chainList) c._score = _linkedBases.has(c._base) ? 1000 : _nameScore(c._latestQuote);
          chainList.sort((a, b) => (b._score - a._score) || ((b._ts || 0) - (a._ts || 0)));
        }
        chainList = chainList.slice(0, 50);
        if (!chainList.length) { listEl.innerHTML = '<div class="rd-qmp__list-empty">No matching chains found.</div>'; return; }
        let html = '';
        for (const c of chainList) {
          const letters = [...c._letters].filter(Boolean).sort();
          const latest = letters[letters.length - 1] || '?';
          const next = latest && latest.length === 1 && latest < 'Z' ? String.fromCharCode(latest.charCodeAt(0) + 1) : '?';
          const org = displayNameFor(c._latestQuote || {});
          const isPicked = c._base === selectedBaseRef;
          // Pending latest = no quote to clone — the workstation fills the
          // pending deal itself instead of minting the next letter
          // (Ryan 2026-06-12). Say so here so the rep isn't promised "-B".
          const latestIsPending = !!(c._latestQuote && c._latestQuote.isPending);
          const metaLine = latestIsPending
            ? `Pending deal -${escapeHtml(latest)} — floor plan items will fill it`
            : `Latest: -${escapeHtml(latest)} (${letters.length} revision${letters.length === 1 ? '' : 's'}) → next: -${escapeHtml(next)}`;
          html += `<div class="rd-qmp__list-item${isPicked ? ' rd-qmp__list-item--picked' : ''}" data-base-ref="${escapeHtml(c._base)}">
            <div class="rd-qmp__list-org">${escapeHtml(org)} <span style="font-weight:500;color:#5a6b78;">— ${escapeHtml(c._base)}</span>${_linkedBases.has(c._base) ? ' <span style="font-size:11px;font-weight:600;color:#075985;background:rgba(2,132,199,.12);padding:1px 7px;border-radius:999px;">Linked</span>' : (!f && c._score > 0 ? _matchTag : '')}</div>
            <div class="rd-qmp__list-meta">${metaLine}</div>
          </div>`;
        }
        listEl.innerHTML = html;
        listEl.querySelectorAll('.rd-qmp__list-item').forEach(el => {
          el.addEventListener('click', () => {
            selectedBaseRef = el.getAttribute('data-base-ref');
            renderRevisionList(overlay.querySelector('[data-search="revision"]').value);
            updateContinueState();
          });
        });
      };

      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — "has the customer seen this?" off the row
         the picker already has. `/rep-quotes` carries `lifecycleStatus` (quoted / sold) and the
         derived `status` (quote_sent / accepted / expired / draft); a PENDING quote has no Shopify
         draft at all, so it cannot have been sent whatever else the row says.
         ⚠️ Unknown reads as NOT sent, which selects in-place append. That is the pre-existing
         behaviour, so an unrecognised status can never be worse than what shipped before. */
      const _isSentQuote = (q) => {
        if (!q || q.isPending) return false;
        const st = String(q.lifecycleStatus || q.status || '').toLowerCase();
        return /quote_sent|invoice_sent|accepted|quoted|sold/.test(st);
      };
      const _quoteByRef = (ref) => (quotesCache || []).find(q => (q.reference || q.ref || '') === ref) || null;
      const _shortDate = (v) => {
        const t = parseTs(v);
        if (!t) return '';
        try { return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
        catch (e) { return ''; }
      };
      const _shortMoney = (v) => {
        const n = Number(v);
        if (!isFinite(n) || n <= 0) return '';
        try { return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 }); }
        catch (e) { return '$' + Math.round(n); }
      };

      /* The as-revision choice follows the selected target: ON and explained for a quote that has
         already gone out, OFF and optional for a draft. The rep can always override it — this
         picks the safe default, it does not take the decision away. */
      let asRevisionTouched = false;
      let asRevision = false;
      const syncAsRevision = () => {
        const box = overlay.querySelector('[data-asrev]');
        const note = overlay.querySelector('[data-asrev-note]');
        if (!box) return;
        const q = selectedTargetRef ? _quoteByRef(selectedTargetRef) : null;
        const sent = !!(q && _isSentQuote(q));
        if (!asRevisionTouched) { asRevision = sent; box.checked = sent; }
        box.disabled = !selectedTargetRef;
        if (!note) return;
        if (!selectedTargetRef) { note.textContent = ''; return; }
        if (asRevision) {
          note.textContent = selectedTargetRef + ' is left exactly as it is; its items plus this floor plan become the next revision in the chain.';
          note.className = 'rd-qmp__asrev-note';
        } else if (sent) {
          note.textContent = 'Careful: ' + selectedTargetRef + ' has already been sent, and adding to it in place changes the quote the customer can already see.';
          note.className = 'rd-qmp__asrev-note rd-qmp__asrev-note--warn';
        } else {
          note.textContent = 'This floor plan\'s items are added straight into ' + selectedTargetRef + ' for you to review before saving.';
          note.className = 'rd-qmp__asrev-note';
        }
      };

      const renderAppendList = (filter) => {
        const listEl = overlay.querySelector('[data-list="append"]');
        if (!quotesCache) {
          // Still loading: list the linked quotes now (the ref is all a pick needs).
          if (!_linkedRefs.length || quotesLoadStarted === 'failed') return;
          listEl.innerHTML = _linkedRefs.map(r => `<div class="rd-qmp__list-item${r === selectedTargetRef ? ' rd-qmp__list-item--picked' : ''}" data-target-ref="${escapeHtml(r)}">
            <div class="rd-qmp__list-org">${escapeHtml(r)} <span style="font-size:11px;font-weight:600;color:#075985;background:rgba(2,132,199,.12);padding:1px 7px;border-radius:999px;">Linked</span></div>
            <div class="rd-qmp__list-meta">Linked to this floor plan</div></div>`).join('')
            + '<div class="rd-qmp__list-loading">Loading your other quotes...</div>';
          listEl.querySelectorAll('.rd-qmp__list-item').forEach(el => el.addEventListener('click', () => {
            selectedTargetRef = el.getAttribute('data-target-ref');
            renderAppendList(overlay.querySelector('[data-search="append"]').value);
            syncAsRevision();
            updateContinueState();
          }));
          return;
        }
        let list = quotesCache.slice();
        const f = (filter || '').trim().toLowerCase();
        if (f) {
          list = list.filter(q => {
            const search = ((q.reference || q.ref || '') + ' ' + (q.customerCompany || '') + ' ' + (q.customer || '') + ' ' + (q.customerEmail || '')).toLowerCase();
            return search.indexOf(f) !== -1;
          });
        }
        list.sort((a, b) => parseTs(b.updatedAt || b.createdAt) - parseTs(a.updatedAt || a.createdAt));
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — the revisions this floor plan is actually
           linked to go to the TOP, in chain order, ahead of the rep's other 200 quotes. That is
           the whole point of the card: -A, -B and -C should be sitting there together. */
        if (!f) {
          const linked = [], rest = [];
          for (const q of list) (_linkedRefs.indexOf(q.reference || q.ref || '') !== -1 ? linked : rest).push(q);
          linked.sort((a, b) => _byRevision(a.reference || a.ref || '', b.reference || b.ref || ''));
          // Then the quotes whose customer shares a word with the layout name (stable: newest first within a score)
          const scored = rest.map((q, i) => ({ q, i, s: _nameScore(q) }));
          scored.sort((a, b) => (b.s - a.s) || (a.i - b.i));
          list = linked.concat(scored.map(x => { x.q._nameHit = x.s > 0; return x.q; }));
        }
        list = list.slice(0, 50);
        if (!list.length) { listEl.innerHTML = '<div class="rd-qmp__list-empty">No matching quotes found.</div>'; return; }
        let html = '';
        for (const q of list) {
          const ref = q.reference || q.ref || '';
          const org = displayNameFor(q);
          // Show contact name as sub-meta only when it's distinct from the primary display
          // (e.g. quote shows "Acme Gyms" → meta "John Smith"; quote shows "John Smith" → no meta)
          const contactName = (q.customer || '').trim();
          const showContactMeta = contactName && contactName !== 'Unknown' && contactName !== org;
          const isPicked = ref === selectedTargetRef;
          const pendTag = q.isPending ? ' <span style="font-size:11px;font-weight:600;color:#B45309;background:rgba(245,158,11,.12);padding:1px 7px;border-radius:999px;">Pending</span>' : '';
          /* ⚠️ `-A / -B / -C` alone is unreadable three weeks later, and the rep is now choosing
             between siblings rather than confirming one. Status is the load-bearing one: it is
             what says whether editing this quote changes a document the customer is holding. */
          const sentTag = _isSentQuote(q) ? ' <span style="font-size:11px;font-weight:600;color:#9A3412;background:rgba(234,88,12,.12);padding:1px 7px;border-radius:999px;">Sent</span>' : '';
          const linkTag = _linkedRefs.indexOf(ref) !== -1 ? ' <span style="font-size:11px;font-weight:600;color:#075985;background:rgba(2,132,199,.12);padding:1px 7px;border-radius:999px;">Linked</span>'
            : (!f && q._nameHit ? _matchTag : '');
          const when = _shortDate(q.updatedAt || q.createdAt);
          const amount = _shortMoney(q.total);
          const metaBits = [showContactMeta ? escapeHtml(contactName) : '', when, amount].filter(Boolean);
          html += `<div class="rd-qmp__list-item${isPicked ? ' rd-qmp__list-item--picked' : ''}" data-target-ref="${escapeHtml(ref)}">
            <div class="rd-qmp__list-org">${escapeHtml(org)} <span style="font-weight:500;color:#5a6b78;">— ${escapeHtml(ref)}</span>${linkTag}${pendTag}${sentTag}</div>
            ${metaBits.length ? `<div class="rd-qmp__list-meta">${metaBits.join(' · ')}</div>` : ''}
          </div>`;
        }
        listEl.innerHTML = html;
        listEl.querySelectorAll('.rd-qmp__list-item').forEach(el => {
          el.addEventListener('click', () => {
            selectedTargetRef = el.getAttribute('data-target-ref');
            const _s = overlay.querySelector('[data-search="append"]');
            renderAppendList(_s ? _s.value : '');
            syncAsRevision();
            updateContinueState();
          });
        });
      };

      const ensureQuotesLoaded = async () => {
        if (quotesCache !== null || quotesLoadStarted) return;
        quotesLoadStarted = true;
        renderRevisionList(''); renderAppendList('');
        const _got = await fetchQuotes();
        if (!_got) quotesLoadStarted = 'failed';
        const _revSearch = overlay.querySelector('[data-search="revision"]');
        const _appSearch = overlay.querySelector('[data-search="append"]');
        if (_revSearch) renderRevisionList(_revSearch.value);
        if (_appSearch) renderAppendList(_appSearch.value);
        /* ⚠️ The default for the as-revision box depends on the TARGET'S STATUS, which lives on a
           row that does not exist until this resolves. Without this the box would sit unchecked on
           an already-sent quote — the one case it exists to catch. */
        syncAsRevision();
      };

      radios.forEach(radio => {
        radio.addEventListener('change', async () => {
          selectedMode = radio.value;
          opts.forEach(o => o.classList.toggle('rd-qmp__opt--sel', o.getAttribute('data-mode') === selectedMode));
          updateContinueState();
          if (selectedMode === 'revision' || selectedMode === 'append') await ensureQuotesLoaded();
        });
      });

      /* Ryan 2026-05-21 fix: search inputs are only rendered when their sub
         <div> exists. The append sub is suppressed when _backlinkRef is set
         (the picker already knows the target ref — no search needed). Guard
         the input-listener attach so a missing element doesn't throw and
         abort the rest of the picker wiring (Cancel / × / Continue handlers
         live BELOW these lines — an unhandled throw here killed them too). */
      const _revSearchEl = overlay.querySelector('[data-search="revision"]');
      if (_revSearchEl) _revSearchEl.addEventListener('input', e => renderRevisionList(e.target.value));
      const _appSearchEl = overlay.querySelector('[data-search="append"]');
      if (_appSearchEl) _appSearchEl.addEventListener('input', e => renderAppendList(e.target.value));
      const _asRevEl = overlay.querySelector('[data-asrev]');
      if (_asRevEl) _asRevEl.addEventListener('change', e => {
        /* Once the rep has an opinion, stop re-deciding for them on the next target they click. */
        asRevisionTouched = true;
        asRevision = !!e.target.checked;
        syncAsRevision();
      });
      // Stop click on label radios from toggling selection when clicking inside sub-area inputs
      overlay.querySelectorAll('.rd-qmp__sub').forEach(sub => sub.addEventListener('click', e => e.stopPropagation()));

      const cleanup = (result) => {
        overlay.classList.remove('visible');
        setTimeout(() => { try { overlay.remove(); } catch (e) {} }, 100);
        resolve(result);
      };

      cancelBtn.addEventListener('click', () => cleanup(null));
      closeBtn.addEventListener('click', () => cleanup(null));
      overlay.addEventListener('click', e => { if (e.target === overlay) cleanup(null); });
      continueBtn.addEventListener('click', () => {
        if (selectedMode === 'new') cleanup({ mode: 'new' });
        else if (selectedMode === 'revision' && selectedBaseRef) cleanup({ mode: 'revision', baseRef: selectedBaseRef });
        else if (selectedMode === 'append' && selectedTargetRef) cleanup({ mode: 'append', targetRef: selectedTargetRef, asRevision: !!asRevision });
      });

      updateContinueState();
      syncAsRevision();
      /* ⛔ THE APPEND LIST USED TO BE SUPPRESSED WHEN A LINKED REF EXISTED, so nothing had to be
         fetched until the rep clicked a radio. It is always rendered now and append is
         pre-selected whenever this floor plan is linked to a deal — without this the rep would
         open the picker onto a permanent "Loading quotes...". Fire and forget: it renders itself. */
      /* Ryan 2026-10-15: start loading straight away whatever is selected, so the list is
         (usually) already there when the rep clicks one of the two search options. */
      ensureQuotesLoaded();
    });
  }

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-10-15) — the quote list for the picker, started the
     moment "Create a Quote" is clicked (it loads while the save prompt and auto-save run) and kept
     for 2 minutes so reopening the picker is instant. A failed load is not kept. */
  _prefetchRepQuotes() {
    const now = Date.now();
    const c = this._repQuotesPrefetch;
    if (c && now - c.at < 120000) return c.promise;
    if (!window.RepPortal || !window.RepPortal.API) return Promise.resolve(null);
    const entry = { at: now, promise: null };
    entry.promise = window.RepPortal.API.get('/rep-quotes?status=all&limit=200').catch((err) => {
      if (this._repQuotesPrefetch === entry) this._repQuotesPrefetch = null;
      throw err;
    });
    entry.promise.catch(() => {});
    this._repQuotesPrefetch = entry;
    return entry.promise;
  }

  async _prepareQuoteHandoff() {
    const rd = this.rd;

    // Validate: at least one closed room
    if (!rd.rooms.some(r => r.closed && r.type === 'add')) {
      rd.updateStatus('Draw at least one closed room before getting your plan.', true);
      return;
    }

    // Portal mode: block if workstation is not live for this user
    if (rd._portalMode) {
      try {
        const sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || '{}');
        const pageConfig = JSON.parse(sessionStorage.getItem('rep_portal_page_config') || '{}');
        if ((sess.role || '') === 'agency') {
          rd.updateStatus('Quoting is for the sales team. Your layout is saved under All Layouts.', true);
          return;
        }
        if (pageConfig.workstation === false && !sess.admin) {
          rd.updateStatus('Quote Workstation is not yet available. Contact your admin.', true);
          return;
        }
      } catch (e) {}
    }
    // Portal mode: send to workstation instead of RFQ form
    if (rd._portalMode) {
      try { this._prefetchRepQuotes(); } catch (e) {}
      try { rd._doAutoSave(); } catch (e) {}

      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21, revised) — When
         the design isn't portal-saved yet, prompt the rep to name + save
         it FIRST. Save uses the rep's chosen name (no more "Untitled
         Design" auto-fills). After save success, the Mode picker opens.
         Cancel on the prompt aborts the whole quote-handoff. Saved-
         already designs skip this step entirely (silent pass-through). */
      const _rdStorage = rd._storage || null;
      if (_rdStorage && typeof _rdStorage._saveToPortal === 'function' && !_rdStorage._portalDesignId) {
        let _currentName = '';
        try {
          const _ni = document.getElementById('designNameInput');
          if (_ni) _currentName = (_ni.value || '').trim();
        } catch (e) {}
        /* The modal now performs the save inline (button flips to
           "Saving…" while the round-trip runs). On success it dismisses
           and resolves with the name; on failure the modal stays open
           with the error inline so the rep can retry. This eliminates
           the ~3s blank gap between Save & Continue → Mode picker where
           a rep could double-trigger the handoff. */
        const _saveResult = await this._showSaveDesignPrompt(_currentName, async (name) => {
          try {
            const _ni = document.getElementById('designNameInput');
            if (_ni) _ni.value = name;
          } catch (e) {}
          await _rdStorage._saveToPortal();
        });
        if (!_saveResult) return; // rep cancelled or hit unrecoverable error
      }

      /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Mode picker: rep chooses new / revision / append before items are collected */
      // Ryan 2026-05-21: pass through any deal backlinks on the design so
      // the picker can pre-select Mode 3 ("Append to {ref}") when this
      // floor plan is already linked to a deal.
      const _linkedDeals = (rd._storage && Array.isArray(rd._storage._portalDesignLinkedDeals))
        ? rd._storage._portalDesignLinkedDeals
        : [];
      const pickerResult = await this._showQuoteModePicker(_linkedDeals);
      if (!pickerResult) return; // cancelled

      const { flooringItems, equipmentItems, turfItems, platformItems } = this._collectHandoffItems();
      const wsItems = [];
      // Sort flooring by product so same-product variants are grouped together
      flooringItems.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      // Map flooring items
      for (const fi of flooringItems) {
        // Roll sharing: a borrowing space buys nothing, so it gets no worksheet line (a
        // zero line would be billed as 1). The lead space's line names its pieces instead.
        if (fi._borrowedFrom) continue;
        // Extract vendor from product title if not set (flooring catalog doesn't include vendor)
        let fiVendor = fi._vendor || fi.vendor || '';
        let fiTitle = fi.title;
        if (!fiVendor && fiTitle) {
          // ECORE products: any title starting with "ECORE" → vendor "ECORE"
          if (/^ECORE\b/i.test(fiTitle)) {
            fiVendor = 'ECORE';
          } else {
            const vendorMatch = fiTitle.match(/^([A-Z][A-Za-z]+(?:\s[A-Z][A-Za-z]+)*)\s/);
            if (vendorMatch && vendorMatch[1] !== fiTitle.trim()) fiVendor = vendorMatch[1];
          }
        }
        if (!fiVendor) fiVendor = 'Blue Sky Fitness Supply';
        if (fiVendor && fiVendor !== 'Blue Sky Fitness Supply') fiTitle = fiTitle.replace(new RegExp('^' + fiVendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '');
        const fiSubs = [];
        // "Quoted as X" — first sub-text line so reader knows what qty column means
        const fiUnit = fi._isTile ? (fi._shape === 'paver' ? 'pavers' : fi._shape === 'plank' ? 'planks' : 'tiles') : 'sq ft';
        fiSubs.push('Quoted as ' + fiUnit);
        if (fi.variantTitle) fiSubs.push(fi.variantTitle);
        // Rolls: build "(Nx) rolls @ L'×W' = X,XXX sq ft" per roll group
        if (!fi._isTile && fi._purchaseRolls && fi._purchaseRolls.length > 0 && fi._productWidthFt > 0) {
          for (const pr of fi._purchaseRolls) {
            const mg = pr._mg;
            if (mg && mg.totalCount && mg.purchaseLen) {
              const count = mg.totalCount;
              const lenFt = Math.round(mg.purchaseLen);
              /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-12) \u2014 true width +
                 billed area. Rounding a 70" (5.83') roll to 6' overstated the
                 line ("41'\u00d76' = 246 sq ft" vs the billed 240). Show inches
                 when the width isn't whole feet; area = ceil of the REAL
                 material, matching the QW quantity (Math.ceil(materialArea)). */
              const widFtTrue = fi._productWidthFt;
              const widStr = Math.abs(widFtTrue - Math.round(widFtTrue)) < 0.01
                ? `${Math.round(widFtTrue)}'`
                : `${Math.round(widFtTrue * 12)}"`;
              const area = Math.ceil(count * mg.purchaseLen * widFtTrue).toLocaleString();
              fiSubs.push(`(${count}x) ${count === 1 ? 'roll' : 'rolls'} @ ${lenFt}'\u00d7${widStr} = ${area} sq ft`);
            } else if (pr.count) {
              fiSubs.push(`(${pr.count}x) ${pr.count === 1 ? 'roll' : 'rolls'}`);
            }
          }
        } else if (fi._isTile) {
          // Tiles/planks: show all purchase detail lines
          if (fi._purchaseRolls) {
            for (const pr of fi._purchaseRolls) {
              if (pr.label) fiSubs.push(pr.label);
            }
          }
        } else {
          fiSubs.push(fi.purchaseDetails || (fi.quantity + ' sq ft'));
        }
        if (fi._shareNote) fiSubs.push(fi._shareNote + ', cut from these rolls');
        // Extract product handle from URL for pricing enrichment (Shopify-linked products only)
        const fiHandle = fi.url ? fi.url.replace(/^.*\/products\//, '').split('?')[0] : null;
        const fiSku = fi._sku || '';
        wsItems.push({
          type: 'custom',
          title: fiTitle,
          sku: fiSku,
          quantity: fi.quantity || 1,
          vendor: fiVendor,
          subDescriptions: fiSubs.filter(Boolean),
          productHandle: fiHandle || null,
          _productHandle: fiHandle || null,
          configured: true
        });
      }
      // Map equipment items — include product handle for workstation pricing lookup
      // Colors go on the LAST item per vendor only
      const eqByVendor = {};
      for (const ei of equipmentItems) {
        const v = ei.vendor || 'Unknown';
        if (!eqByVendor[v]) eqByVendor[v] = [];
        eqByVendor[v].push(ei);
      }
      for (const vendor in eqByVendor) {
        const items = eqByVendor[vendor];
        // Get colors from the last item that has them (they're all the same per vendor)
        let vendorFrame = '', vendorPad = '';
        for (const ei of items) {
          if (ei.frameColor) vendorFrame = ei.frameColor;
          if (ei.padColor) vendorPad = ei.padColor;
        }
        for (let i = 0; i < items.length; i++) {
          const ei = items[i];
          const handle = ei.url ? ei.url.replace(/^.*\/products\//, '').split('?')[0] : null;
          const isLast = i === items.length - 1;
          const subs = [];
          if (isLast && vendorFrame) subs.push('Frames: ' + vendorFrame);
          if (isLast && vendorPad) subs.push('Pads: ' + vendorPad);
          wsItems.push({
            type: 'custom',
            title: (() => {
              let t = ei.vendor ? ei.title.replace(new RegExp('^' + ei.vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '') : ei.title;
              if (ei.sku) { const re = new RegExp('\\b' + ei.sku.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b[\\s\\-–—:]*', 'i'); t = t.replace(re, '').replace(/\s{2,}/g, ' ').trim() || t; }
              return t;
            })(),
            sku: ei.sku || '',
            quantity: ei.quantity || 1,
            vendor: ei.vendor || '',
            subDescriptions: subs,
            productHandle: handle || null,
            _productHandle: handle || null,
            configured: true
          });
        }
      }
      // Map turf items — include color/line color + customization features as sub-text
      for (const ti of (turfItems || [])) {
        const turfVendor = 'Blue Sky Fitness Supply';
        let tiTitle = ti.title;
        if (ti.vendor) tiTitle = tiTitle.replace(new RegExp('^' + ti.vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '');
        const tiSubs = [];
        tiSubs.push('Quoted as pieces');
        // Color + line color summary (e.g. "Green turf / white lines")
        const tc = ti.turfCustomizations || {};
        const turfColorName = ti.variantTitle || '';
        const lineColorName = tc.fieldLines ? tc.fieldLines.color : '';
        if (turfColorName && lineColorName) {
          tiSubs.push(turfColorName + ' turf / ' + lineColorName.toLowerCase() + ' lines');
        } else if (turfColorName) {
          tiSubs.push(turfColorName);
        }
        // Features summary from customizations
        const featureParts = [];
        if (tc.hashMarks) featureParts.push('hash lines every ' + tc.hashMarks.spacing);
        if (tc.yardNumbers) featureParts.push('yard numbers');
        if (tc.borderLine) featureParts.push('border line');
        if (tc.sledLanes) featureParts.push('sled lanes');
        if (tc.logos) {
          const ladders = tc.logos.filter(l => l.type === 'ladder').length;
          const dots = tc.logos.filter(l => l.type === 'dotdrill').length;
          const imgs = tc.logos.filter(l => l.type === 'image').length;
          if (ladders) featureParts.push('agility ladder');
          if (dots) featureParts.push('dot drill');
          if (imgs) featureParts.push(imgs > 1 ? imgs + ' logos' : 'logo');
        }
        if (featureParts.length > 0) tiSubs.push('Custom markings: ' + featureParts.join(', '));
        if (ti._totalAreaSqFt) tiSubs.push(ti._totalAreaSqFt.toLocaleString() + ' sq ft total');
        const tiHandle = ti.url ? ti.url.replace(/^.*\/products\//, '').split('?')[0] : null;
        wsItems.push({
          type: 'custom',
          title: tiTitle,
          sku: ti.sku || '',
          quantity: ti.quantity || 1,
          vendor: turfVendor,
          subDescriptions: tiSubs,
          productHandle: tiHandle || null,
          _productHandle: tiHandle || null,
          points: 30,
          configured: true
        });
      }
      // Map platform items — include color names + logo as sub-text
      for (const pi of (platformItems || [])) {
        const piVendor = 'ECORE';
        let piTitle = pi.title;
        if (piVendor) piTitle = piTitle.replace(new RegExp('^' + piVendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i'), '');
        const piSubs = [];
        piSubs.push('Quoted as platforms');
        if (pi.variantTitle) piSubs.push(pi.variantTitle + ' platform');
        // Extract color and logo info from platformDescription
        const desc = pi.platformDescription || '';
        const colorMatch = desc.match(/Center:\s*([^,—]+)/);
        const wingMatch = desc.match(/Wings:\s*([^,—]+)/);
        if (colorMatch && wingMatch) {
          piSubs.push(colorMatch[1].trim() + ' center / ' + wingMatch[1].trim() + ' drop zones');
        } else if (colorMatch) {
          piSubs.push(colorMatch[1].trim() + ' center');
        }
        if (desc.includes('Custom logo')) piSubs.push('Custom logo included');
        wsItems.push({
          type: 'custom',
          title: piTitle,
          sku: pi.sku || 'CUSTOM',
          quantity: pi.quantity || 1,
          vendor: piVendor,
          subDescriptions: piSubs,
          productHandle: null,
          points: 30,
          configured: true
        });
      }
      if (wsItems.length > 0) {
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Wrap items in mode-aware payload (new/revision/append). Workstation reads `mode` to branch intake. */
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-05-21) — Pass designRef +
           designId through the handoff so the Workstation can call
           /quote-link-design after the save lands, stamping wm.linkedFiles[]
           with this floor plan. Picked up by the dashboard's LINKED FILES
           section on next /rep-quotes load. Null when the rep hasn't saved
           the design yet (Save badge not minted) — link write is skipped on
           the workstation side in that case. */
        const _storage = rd._storage || null;
        /* CUSTOM_ROOMDESIGNER_BY_CLAUDE (Ryan 2026-06-19) — Auto-save the design
           first when it has no portal ref/id yet, so the floor plan ALWAYS links
           to the deal (DD → Linked Files) with zero manual step, AND so the
           saved design carries spaceImages for the optional "add layout to PDF"
           toggle to pull from. Best-effort: on failure we fall through and hand
           off unlinked (prior behavior), so a save hiccup never blocks the quote. */
        if (_storage && _storage._saveToPortal && (!_storage._portalDesignId || !_storage._portalDesignRef)) {
          try { await _storage._saveToPortal(); } catch (e) { /* proceed unlinked */ }
        }
        const _designRef = _storage && _storage._portalDesignRef ? _storage._portalDesignRef : null;
        const _designId = _storage && _storage._portalDesignId ? _storage._portalDesignId : null;
        const importPayload = {
          mode: pickerResult.mode,
          baseRef: pickerResult.baseRef || null,
          targetRef: pickerResult.targetRef || null,
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-11) — append-and-save-as, as one move. */
          asRevision: !!pickerResult.asRevision,
          designRef: _designRef,
          designId: _designId,
          items: wsItems
        };
        localStorage.setItem('rep_workstation_import', JSON.stringify(importPayload));
      }
      window.open('/pages/rep-quote-workstation', '_blank');
      return;
    }

    // Auto-save the design so it's preserved even if user hasn't saved yet
    try { rd._doAutoSave(); } catch (e) { /* localStorage full — proceed anyway */ }

    // Show loading overlay
    const container = document.getElementById('canvasContainer');
    let loadingEl = document.getElementById('rdExportLoading');
    if (!loadingEl) {
      loadingEl = document.createElement('div');
      loadingEl.id = 'rdExportLoading';
      loadingEl.className = 'rd-export-loading';
      loadingEl.innerHTML = '<div class="rd-export-loading-spinner"></div><div class="rd-export-loading-text">Preparing your floor plan...</div>';
      container.appendChild(loadingEl);
    }
    loadingEl.classList.add('visible');

    try {
      // Small delay to let loading overlay paint
      await new Promise(r => setTimeout(r, 50));

      /* Generate per-space print canvases (JPEG) at the best quality that fits.
         ⚠️ This used to carry its own copy of the size guard, character-for-character
         the same rule as the one in _uploadSpaceImages — two copies that could
         disagree about whether a design was exportable at all, and both measuring
         base64 length rather than image bytes. One definition now, on rd. */
      const _sizeNote = {};
      let spaceImages = await rd._renderPrintImagesWithinBudget(_sizeNote);
      if (!spaceImages || spaceImages.length === 0) {
        try { console.warn('[RD] per-space images unusable — ' + (_sizeNote.reason || 'no reason captured') + '; falling back to the whole-design page'); } catch (_e) {}
      }

      if (!spaceImages || spaceImages.length === 0) {
        const fallback = await rd._generatePrintCanvas();
        if (fallback) {
          spaceImages = [{ spaceName: 'Full Design', dataUrl: fallback, spaceIndex: 0 }];
        }
      }

      // Collect items via shared helper
      const { flooringItems, equipmentItems, turfItems, platformItems, flooringSwatches, spaceSummary, totalArea } = this._collectHandoffItems();

      // Extract unique turf logo images for attachment
      const turfLogos = [];
      const seenLogos = new Set();
      for (const obj of rd.objects) {
        if (!obj.turfLogos) continue;
        for (const logo of obj.turfLogos) {
          if (!logo.dataURL || seenLogos.has(logo.dataURL)) continue;
          seenLogos.add(logo.dataURL);
          const label = obj.name || obj.productTitle || ('Turf Logo ' + (turfLogos.length + 1));
          turfLogos.push({ dataUrl: logo.dataURL, label });
        }
      }

      // Include platform logo if present
      if (this._platformLogo && this._platformLogo.dataURL) {
        turfLogos.push({ dataUrl: this._platformLogo.dataURL, label: 'Platform Logo' });
      }
      if (this._hardwoodLogo && this._hardwoodLogo.dataURL) {
        turfLogos.push({ dataUrl: this._hardwoodLogo.dataURL, label: 'Hardwood Platform Logo Decal' });
      }

      // Serialize design JSON
      const designData = rd._serializeDesign();
      const designJson = JSON.stringify(designData);

      // Create editable share link (non-blocking — quote proceeds even if this fails)
      let editPlanUrl = null;
      try {
        editPlanUrl = await rd._buildShareUrl(designData, { source: 'quote-handoff' });
      } catch (e) {
        console.warn('[RD] Could not create edit link for quote:', e);
      }

      // Get design name
      const designName = (rd._storage && rd._storage._activeDesignName) || 'Untitled Design';

      // Build handoff payload
      const handoff = {
        spaceImages: spaceImages || [],
        designJson,
        editPlanUrl,
        items: [...flooringItems, ...equipmentItems, ...turfItems, ...(platformItems || [])],
        flooringSwatches,
        spaceSummary,
        totalArea,
        turfLogos,
        designName,
        timestamp: Date.now()
      };

      // Store in sessionStorage with progressive fallback
      try {
        sessionStorage.setItem('rd_quote_handoff', JSON.stringify(handoff));
      } catch (e) {
        // sessionStorage full — strip images and try again
        console.warn('[RD] sessionStorage full, stripping space images');
        handoff.spaceImages = [];
        try {
          sessionStorage.setItem('rd_quote_handoff', JSON.stringify(handoff));
        } catch (e2) {
          // Still too large — strip turf logos too
          console.warn('[RD] sessionStorage still full, stripping turf logos');
          handoff.turfLogos = [];
          try {
            sessionStorage.setItem('rd_quote_handoff', JSON.stringify(handoff));
          } catch (e3) {
            throw e3;
          }
        }
      }

      // Navigate to quote form in a new tab (preserves the design in this tab)
      window.open('/pages/request-a-quote-form?source=room-designer', '_blank');

      // Dismiss loading overlay (we're staying on this page now)
      loadingEl.classList.remove('visible');
      rd.updateStatus('Floor plan opened in a new tab.', false);
    } catch (err) {
      console.error('[RD] Quote handoff error:', err);
      rd.updateStatus('Error preparing floor plan. Please try again.', true);
      loadingEl.classList.remove('visible');
    }
  }

  // ── View-Only Reply: Diff + Button State ──────────

  _diffViewOnlyChanges() {
    const rd = this.rd;
    const equipment = [];
    const turf = [];
    if (!rd._viewOnly) return { equipment, turf };

    // Equipment brand color diff
    const orig = rd._viewOnlyOriginalBrandColors || {};
    const curr = rd._brandColors || {};
    const config = this._getBrandColorConfig();
    const allVendors = new Set([...Object.keys(orig), ...Object.keys(curr)]);

    for (const vendor of allVendors) {
      const o = orig[vendor] || {};
      const c = curr[vendor] || {};
      const vendorConf = config[vendor];
      if (!vendorConf) continue;

      // Check powderCoat
      if ((o.powderCoat || '') !== (c.powderCoat || '') || !!o.pcMatte !== !!c.pcMatte || (o.pcPattern || '') !== (c.pcPattern || '')) {
        const fromName = this._findSwatchName(vendorConf.powderCoat || [], o, 'powderCoat') || 'Default';
        const toName = this._findSwatchName(vendorConf.powderCoat || [], c, 'powderCoat') || 'Default';
        if (fromName !== toName) {
          equipment.push({ vendor, type: 'Frame', from: fromName, to: toName });
        }
      }
      // Check upholstery
      if ((o.upholstery || '') !== (c.upholstery || '') || (o.uphPattern || '') !== (c.uphPattern || '')) {
        const fromName = this._findSwatchName(vendorConf.upholstery || [], o, 'upholstery') || 'Default';
        const toName = this._findSwatchName(vendorConf.upholstery || [], c, 'upholstery') || 'Default';
        if (fromName !== toName) {
          equipment.push({ vendor, type: 'Upholstery', from: fromName, to: toName });
        }
      }
    }

    // Turf diff
    const origTurf = rd._viewOnlyOriginalTurf || [];
    const currTurfObjs = (rd.objects || []).filter(o => o.type === 'turf');
    for (const ot of origTurf) {
      const ct = currTurfObjs.find(o => o.id === ot.id);
      if (!ct) continue;
      if ((ot.turfColorId || '') !== (ct.turfColorId || '')) {
        turf.push({ label: 'Turf Color', from: ot.turfColorId || 'Default', to: ct.turfColorId || 'Default' });
      }
      if ((ot.turfColorHex || '') !== (ct.turfColorHex || '')) {
        if (!turf.find(t => t.label === 'Turf Color')) {
          turf.push({ label: 'Turf Color Hex', from: ot.turfColorHex || '', to: ct.turfColorHex || '' });
        }
      }
      const boolFields = [
        ['turfLines', 'Field Lines'], ['turfHashes', 'Hash Marks'],
        ['turfBorderLine', 'Border Line'],
        ['turfYardNumbers', 'Yard Numbers'],
        ['turfCenterHashes', 'Center Hashes']
      ];
      for (const [key, label] of boolFields) {
        if (!!ot[key] !== !!ct[key]) {
          turf.push({ label, from: ot[key] ? 'On' : 'Off', to: ct[key] ? 'On' : 'Off' });
        }
      }
      // Line Direction (string comparison)
      const otHs = ot.turfHashSpacing === 1 ? "1'" : '1 yd';
      const ctHs = ct.turfHashSpacing === 1 ? "1'" : '1 yd';
      if (otHs !== ctHs) turf.push({ label: 'Hash Spacing', from: otHs, to: ctHs });
      const otDir = ot.turfLineDirection || 'lengthwise';
      const ctDir = ct.turfLineDirection || 'lengthwise';
      if (otDir !== ctDir) {
        turf.push({ label: 'Line Direction', from: otDir.charAt(0).toUpperCase() + otDir.slice(1), to: ctDir.charAt(0).toUpperCase() + ctDir.slice(1) });
      }
      if ((ot.turfLineColor || '') !== (ct.turfLineColor || '')) {
        turf.push({ label: 'Line Color', from: ot.turfLineColor || 'Default', to: ct.turfLineColor || 'Default' });
      }
      const oEdges = (ot.turfBorderLineEdges || ['top','right','bottom','left']).slice().sort().join(',');
      const cEdges = (ct.turfBorderLineEdges || ['top','right','bottom','left']).slice().sort().join(',');
      if (oEdges !== cEdges) {
        turf.push({ label: 'Border Edges', from: oEdges || 'None', to: cEdges || 'None' });
      }
    }

    return { equipment, turf };
  }

  _updateViewOnlyReplyBtn() {
    const rd = this.rd;
    const btn = rd._viewOnlyReplyBtn;
    if (!btn) return;

    const diff = this._diffViewOnlyChanges();
    const hasChanges = diff.equipment.length > 0 || diff.turf.length > 0;
    const currentDiffKey = JSON.stringify({ equipment: diff.equipment, turf: diff.turf });
    const firstName = rd._viewOnlyRepFirstName || 'your rep';

    let label, cssClass, disabled;
    if (rd._viewOnlyReplySent && !hasChanges) {
      label = 'Sent!'; cssClass = 'sent'; disabled = true;
    } else if (rd._viewOnlyReplySent && currentDiffKey === rd._viewOnlyLastSentDiff) {
      label = 'Sent!'; cssClass = 'sent'; disabled = true;
    } else if (hasChanges) {
      label = 'Send My Changes to ' + firstName; cssClass = 'changes'; disabled = false;
    } else {
      label = 'Looks Good!'; cssClass = 'approval'; disabled = false;
    }

    // Update bottom button
    btn.className = 'rd-viewonly-reply-btn ' + cssClass;
    btn.querySelector('.rd-viewonly-reply-label').textContent = label;
    btn.disabled = disabled;

    // Update floating pill (create on first call)
    this._syncFloatingPill(label, cssClass, disabled, hasChanges);
  }

  _syncFloatingPill(label, cssClass, disabled, hasChanges) {
    const rd = this.rd;

    // Create floating pill once
    if (!rd._viewOnlyFloatingPill) {
      const pill = document.createElement('div');
      pill.className = 'rd-viewonly-floating-pill';
      pill.innerHTML =
        '<button class="rd-viewonly-reply-btn approval" id="rdFloatingPillBtn">' +
        '<svg class="rd-reply-icon-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
        '<svg class="rd-reply-icon-send" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>' +
        '<span class="rd-viewonly-reply-label">Looks Good!</span>' +
        '</button>';
      document.body.appendChild(pill);
      rd._viewOnlyFloatingPill = pill;

      const pillBtn = pill.querySelector('#rdFloatingPillBtn');
      pillBtn.addEventListener('click', () => {
        if (rd._viewOnlyReplyEl) {
          const msgDiv = rd._viewOnlyReplyEl.closest('.rd-viewonly-message');
          const el = msgDiv || rd._viewOnlyReplyEl;
          const rect = el.getBoundingClientRect();
          const elCenter = rect.top + window.scrollY + rect.height / 2;
          const y = elCenter - window.innerHeight / 2;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        } else {
          if (rd._storage) rd._storage._showReplyConfirmModal();
        }
      });

      // IntersectionObserver: hide pill when bottom button is visible
      if (rd._viewOnlyReplyEl) {
        const observer = new IntersectionObserver((entries) => {
          const bottomVisible = entries[0].isIntersecting;
          pill.classList.toggle('hidden', bottomVisible);
        }, { threshold: 0.5 });
        observer.observe(rd._viewOnlyReplyEl);
        rd._viewOnlyPillObserver = observer;
      }
    }

    const pill = rd._viewOnlyFloatingPill;
    const pillBtn = pill.querySelector('#rdFloatingPillBtn');
    pillBtn.className = 'rd-viewonly-reply-btn ' + cssClass;
    pillBtn.querySelector('.rd-viewonly-reply-label').textContent = label;
    pillBtn.disabled = disabled;

    // Show pill when there are changes (or sent state), hide when approval with no prior send
    if (hasChanges || (rd._viewOnlyReplySent && cssClass === 'sent')) {
      pill.classList.add('visible');
    } else if (!rd._viewOnlyReplySent) {
      pill.classList.remove('visible');
    }
  }

  // ══════════════════════════════════════════
  // INLAID PLATFORM SYSTEM
  // ══════════════════════════════════════════

  /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — Inlaid platform system: EPDM colors, panel UI, placement, serialization */

  /** EPDM color palette — 36 standard rubber colors for inlaid platforms */
  _getEpdmColors() {
    if (this._epdmColorsCache) return this._epdmColorsCache;
    const urls = window.__rdEpdmSwatchUrls || {};
    this._epdmColorsCache = [
      { id: 'black', name: 'Black', hex: '#1C1C1C', textureUrl: urls['black'] || null },
      { id: 'dark-gray', name: 'Dark Gray', hex: '#4A4A4A', textureUrl: urls['dark-gray'] || null },
      { id: 'medium-gray', name: 'Medium Gray', hex: '#7A7A7A', textureUrl: urls['medium-gray'] || null },
      { id: 'warm-gray', name: 'Warm Gray', hex: '#808078', textureUrl: urls['warm-gray'] || null },
      { id: 'clay-gray', name: 'Clay Gray', hex: '#9A9489', textureUrl: urls['clay-gray'] || null },
      { id: 'light-gray', name: 'Light Gray', hex: '#A8A8A8', textureUrl: urls['light-gray'] || null },
      { id: 'eggshell', name: 'Eggshell', hex: '#D8D4CC', textureUrl: urls['eggshell'] || null },
      { id: 'greige', name: 'Greige', hex: '#B8AFA0', textureUrl: urls['greige'] || null },
      { id: 'sand', name: 'Sand', hex: '#C9B98A', textureUrl: urls['sand'] || null },
      { id: 'mid-camel', name: 'Mid Camel', hex: '#B8925A', textureUrl: urls['mid-camel'] || null },
      { id: 'dark-camel', name: 'Dark Camel', hex: '#8B6D3F', textureUrl: urls['dark-camel'] || null },
      { id: 'deep-taupe', name: 'Deep Taupe', hex: '#6B5B4F', textureUrl: urls['deep-taupe'] || null },
      { id: 'mid-taupe', name: 'Mid Taupe', hex: '#8B7D6B', textureUrl: urls['mid-taupe'] || null },
      { id: 'brown', name: 'Brown', hex: '#5C3317', textureUrl: urls['brown'] || null },
      { id: 'dark-chocolate', name: 'Dark Chocolate', hex: '#3B1F0B', textureUrl: urls['dark-chocolate'] || null },
      { id: 'navy', name: 'Navy', hex: '#1B2A4A', textureUrl: urls['navy'] || null },
      { id: 'midnight-blue', name: 'Midnight Blue', hex: '#1A1A3E', textureUrl: urls['midnight-blue'] || null },
      { id: 'cobalt', name: 'Cobalt', hex: '#0047AB', textureUrl: urls['cobalt'] || null },
      { id: 'sky-blue', name: 'Sky Blue', hex: '#6EAED6', textureUrl: urls['sky-blue'] || null },
      { id: 'retro-deep-aqua', name: 'Retro Deep Aqua', hex: '#007D7A', textureUrl: urls['retro-deep-aqua'] || null },
      { id: 'army-green', name: 'Army Green', hex: '#4B5320', textureUrl: urls['army-green'] || null },
      { id: 'emerald-green', name: 'Emerald Green', hex: '#2D7A3A', textureUrl: urls['emerald-green'] || null },
      { id: 'veggie-green', name: 'Veggie Green', hex: '#4CBB17', textureUrl: urls['veggie-green'] || null },
      { id: 'shadow-lime', name: 'Shadow Lime', hex: '#6B8E3A', textureUrl: urls['shadow-lime'] || null },
      { id: 'true-lime', name: 'True Lime', hex: '#7EC850', textureUrl: urls['true-lime'] || null },
      { id: 'maroon', name: 'Maroon', hex: '#5B1A2A', textureUrl: urls['maroon'] || null },
      { id: 'shadow-red', name: 'Shadow Red', hex: '#9B1B30', textureUrl: urls['shadow-red'] || null },
      { id: 'bright-red', name: 'Bright Red', hex: '#D32F2F', textureUrl: urls['bright-red'] || null },
      { id: 'red-orange', name: 'Red Orange', hex: '#CC5500', textureUrl: urls['red-orange'] || null },
      { id: 'orange', name: 'Orange', hex: '#E65100', textureUrl: urls['orange'] || null },
      { id: 'melon', name: 'Melon', hex: '#E8875A', textureUrl: urls['melon'] || null },
      { id: 'gold', name: 'Gold', hex: '#D4A017', textureUrl: urls['gold'] || null },
      { id: 'yellow', name: 'Yellow', hex: '#F5C518', textureUrl: urls['yellow'] || null },
      { id: 'fuchsia', name: 'Fuchsia', hex: '#E06287', textureUrl: urls['fuchsia'] || null },
      { id: 'purple', name: 'Purple', hex: '#582C83', textureUrl: urls['purple'] || null },
      { id: 'blue-purple', name: 'Blue Purple', hex: '#5B3E8C', textureUrl: urls['blue-purple'] || null },
    ];
    return this._epdmColorsCache;
  }

  /** Platform presets — fixed sizes */
  _getPlatformPresets() {
    return [
      { id: '8x6', label: "8' × 6'", width: 8, depth: 6, wingsAvail: true },
      { id: '8x5', label: "8' × 5'", width: 8, depth: 5, wingsAvail: true },
      { id: '8x4', label: "8' × 4'", width: 8, depth: 4, wingsAvail: true },
      { id: '4x4', label: "4' × 4'", width: 4, depth: 4, wingsAvail: false },
    ];
  }

  /** Check if any room has platform-compatible flooring */
  _hasAnyPlatformCompatibleRoom() {
    return this._getPlatformCompatibleRooms().length > 0;
  }

  /** Get rooms that have at least one zone with platform-compatible flooring.
   *  Checks per-zone flooring (zoneFlooring[]) first, falls back to room.flooring. */
  _getPlatformCompatibleRooms() {
    const rd = this.rd;
    const rooms = [];
    if (!rd.rooms) return rooms;
    for (let i = 0; i < rd.rooms.length; i++) {
      const room = rd.rooms[i];
      if (!room.closed) continue;
      if (this._roomHasAnyCompatibleZone(room)) {
        const product = room.flooring ? this._getProduct(room.flooring.productId) : null;
        rooms.push({ room, idx: i, product });
      }
    }
    return rooms;
  }

  /** Check if any zone in this room has platform-compatible flooring. */
  _roomHasAnyCompatibleZone(room) {
    // Check per-zone flooring entries
    if (room.zoneFlooring && room.zoneFlooring.length > 0) {
      for (const zf of room.zoneFlooring) {
        const product = this._getProduct(zf.productId);
        if (product && product.platformCompatible) return true;
      }
    }
    // Fall back to room-level flooring (single-zone rooms or unzoned)
    if (room.flooring) {
      const product = this._getProduct(room.flooring.productId);
      if (product && product.platformCompatible) return true;
    }
    return false;
  }

  /** Check if a specific point in a room is on platform-compatible flooring.
   *  Returns true if the zone under the point has a compatible product. */
  _isPointOnCompatibleZone(room, px, py) {
    const geo = this.rd._getRoomGeoCache(room);
    const zones = this._getZonesForRoom(room, geo);
    if (zones.length <= 1) {
      // Single zone — check room-level flooring
      const fl = room.flooring;
      if (!fl) return false;
      const product = this._getProduct(fl.productId);
      return !!(product && product.platformCompatible);
    }
    // Multi-zone: find which zone this point is in
    for (const zone of zones) {
      if (this._zoneHitTest(zone, px, py)) {
        const zf = this._getZoneFlooring(room, zone);
        if (!zf) return false;
        const product = this._getProduct(zf.productId);
        return !!(product && product.platformCompatible);
      }
    }
    return false;
  }

  /** Get flooring info (product + angle + zone) for a specific point in a room.
   *  Returns { product, flooringAngle, zone } or null. */
  _getZoneFlooringAtPoint(room, px, py) {
    const geo = this.rd._getRoomGeoCache(room);
    const zones = this._getZonesForRoom(room, geo);
    if (zones.length <= 1) {
      const fl = room.flooring;
      if (!fl) return null;
      const product = this._getProduct(fl.productId);
      if (!product) return null;
      return { product, flooringAngle: room.flooringAngle || 0, zone: zones[0] || null };
    }
    for (const zone of zones) {
      if (this._zoneHitTest(zone, px, py)) {
        const zf = this._getZoneFlooring(room, zone);
        if (!zf) return null;
        const product = this._getProduct(zf.productId);
        if (!product) return null;
        return { product, flooringAngle: zf.flooringAngle || 0, zone };
      }
    }
    return null;
  }

  /** Snap a platform center to the tile grid so all 4 corners land on tile seam intersections.
   *  @param {number} cx - platform center X (world coords)
   *  @param {number} cy - platform center Y (world coords)
   *  @param {number} pw - platform width (feet)
   *  @param {number} pd - platform depth (feet)
   *  @param {number} platRot - platform rotation (radians, 0 or 90° increments)
   *  @param {number} tileW - tile width (feet)
   *  @param {number} tileH - tile height (feet)
   *  @param {number} flooringAngle - tile grid rotation (radians)
   *  @param {Array} roomPoly - room polygon [{x,y},...] for grid origin
   *  @returns {{ x: number, y: number }} snapped center */
  _snapPlatformToTileGrid(cx, cy, pw, pd, platRot, tileW, tileH, flooringAngle, roomPoly) {
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — only a platform SQUARE TO THE TILE GRID snaps.
    // Everything below assumes 90° increments: it swaps width/depth on the odd
    // quarter turns and puts a "top-left corner" on a seam. At 34° there is no such
    // corner, so the old maths would shove the platform sideways and align nothing.
    //
    // Platforms can be rotated freely now — in a room built from curves there is no
    // square to be, which is exactly when a rep needs an odd angle — so off-square is
    // a real state rather than an impossible one, and the honest answer there is to
    // leave the position where it was put. Squared-up platforms snap as before.
    const Q = Math.PI / 2;
    const rel = (((platRot - (flooringAngle || 0)) % Q) + Q) % Q;
    if (Math.min(rel, Q - rel) > 0.5 * Math.PI / 180) return { x: cx, y: cy };
    // Effective dims after platform rotation (90° increments only)
    const rot90 = (Math.round(platRot / (Math.PI / 2)) % 2 !== 0);
    const effW = rot90 ? pd : pw;
    const effD = rot90 ? pw : pd;

    // Rotate into tile-grid-aligned space (undo flooring angle)
    const cosA = Math.cos(-flooringAngle);
    const sinA = Math.sin(-flooringAngle);
    const gx = cx * cosA - cy * sinA;
    const gy = cx * sinA + cy * cosA;

    // Tile grid origin = room polygon bounding box min in grid-aligned space
    // (matches _computeTileLayout which starts tiles from room edge)
    let gridOX = 0, gridOY = 0;
    if (roomPoly && roomPoly.length >= 3) {
      gridOX = Infinity; gridOY = Infinity;
      for (const p of roomPoly) {
        const rx = p.x * cosA - p.y * sinA;
        const ry = p.x * sinA + p.y * cosA;
        if (rx < gridOX) gridOX = rx;
        if (ry < gridOY) gridOY = ry;
      }
    }

    // Snap the top-left corner to nearest tile intersection relative to grid origin
    const cornerX = gx - effW / 2;
    const cornerY = gy - effD / 2;
    const snappedCornerX = gridOX + Math.round((cornerX - gridOX) / tileW) * tileW;
    const snappedCornerY = gridOY + Math.round((cornerY - gridOY) / tileH) * tileH;

    // Derive center from snapped corner
    const snappedGx = snappedCornerX + effW / 2;
    const snappedGy = snappedCornerY + effD / 2;

    // Rotate back to world space
    const cosB = Math.cos(flooringAngle);
    const sinB = Math.sin(flooringAngle);
    return {
      x: snappedGx * cosB - snappedGy * sinB,
      y: snappedGx * sinB + snappedGy * cosB
    };
  }

  /** Initialize platform defaults */
  _initPlatformDefaults() {
    if (!this._platformDefaults) {
      this._platformDefaults = {
        preset: '8x6',
        wings: false,
        centerColor: '#7A7A7A',
        centerColorName: 'Medium Gray',
        wingColor: '#D32F2F',
        wingColorName: 'Bright Red',
        centerColorSource: 'epdm',
        wingColorSource: 'epdm',
        centerTextureUrl: null,
        wingTextureUrl: null,
        colorConfirmed: false,
      };
    }
    // Re-evaluate smart color from stock whenever user hasn't confirmed yet
    if (!this._platformDefaults.colorConfirmed) {
      this._pickSmartPlatformColor();
    }
  }

  /** Pick Jet Black (or Steel Appeal if floor is Jet Black) from stock colors */
  _pickSmartPlatformColor() {
    const pd = this._platformDefaults;
    const compatRooms = this._getPlatformCompatibleRooms();
    if (compatRooms.length === 0) return; // no compatible rooms yet — keep current default
    const product = compatRooms[0].product;
    const room = compatRooms[0].room;
    const floorColorId = room.flooring && room.flooring.colorId;
    const floorColor = product.colors.find(c => c.id === floorColorId);
    const floorIsJetBlack = floorColor && floorColor.name.startsWith('Jet Black');
    const targetName = floorIsJetBlack ? 'Steel Appeal' : 'Jet Black';
    const match = product.colors.find(c => c.name.startsWith(targetName));
    if (match) {
      pd.centerColor = match.hex;
      pd.centerColorName = match.name;
      pd.centerColorSource = 'stock';
      pd.centerTextureUrl = match.textureUrl || null;
    }
  }

  /** Build the inlaid platforms section HTML for the floor panel */
  _buildPlatformSectionHtml() {
    this._initPlatformDefaults();
    const pd = this._platformDefaults;
    const presets = this._getPlatformPresets();
    const compatRooms = this._getPlatformCompatibleRooms();
    const hasCompat = compatRooms.length > 0;

    // Count existing platforms
    const platformCount = this.rd.objects ? this.rd.objects.filter(o => o.type === 'platform').length : 0;

    // Find compatible product names for the disabled message
    const compatProductNames = [];
    if (!hasCompat && this.products) {
      for (const p of this.products) {
        if (p.platformCompatible) compatProductNames.push(p.name);
      }
    }

    let html = '';
    html += '<div class="rd-product-category rd-platform-section collapsed' + (hasCompat ? '' : ' rd-platform-disabled') + '">';
    html += '<div class="rd-product-category-title" data-platform-toggle>';
    html += '<svg class="rd-product-category-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 2.5L8 6L4.5 9.5"/></svg>';
    html += '<span class="rd-product-category-name">Inlaid Platforms</span>';
    if (platformCount > 0) {
      html += '<span class="rd-product-category-count">' + platformCount + '</span>';
    }
    html += '</div>';
    html += '<div class="rd-product-category-body rd-platform-body">';

    if (!hasCompat) {
      // Disabled state — tell user what flooring to add
      html += '<div class="rd-platform-disabled-msg">';
      if (compatProductNames.length > 0) {
        html += '<p>Add <strong>' + compatProductNames.map(n => this._escHtml(n)).join('</strong> or <strong>') + '</strong> flooring to a room to unlock inlaid platforms.</p>';
      } else {
        html += '<p>No platform-compatible flooring products are configured.</p>';
      }
      html += '</div>';
    } else {
      // Size presets — product-card style rows with isometric icons
      const _platIcons = window.__rdPlatformPresetIcons || {};
      html += '<div class="rd-platform-presets">';
      for (const p of presets) {
        const active = this._platformPresetPicked && p.id === pd.preset ? ' rd-platform-preset--active' : '';
        const lightUrl = _platIcons[p.id + '-light'] || '';
        const darkUrl = _platIcons[p.id + '-dark'] || '';
        const subtitle = p.wingsAvail ? 'Two-tone available' : 'Solid fill only';
        html += '<div class="rd-platform-preset' + active + '" data-platform-preset="' + p.id + '">';
        html += '<div class="rd-product-card-icon">';
        if (lightUrl) html += '<img class="rd-icon-light" src="' + this._escAttr(lightUrl) + '" width="42" height="42" alt="">';
        if (darkUrl) html += '<img class="rd-icon-dark" src="' + this._escAttr(darkUrl) + '" width="42" height="42" alt="">';
        html += '</div>';
        html += '<div class="rd-product-card-info">';
        html += '<div class="rd-product-card-name">' + p.label + '</div>';
        html += '<div class="rd-product-card-meta">' + subtitle + '</div>';
        html += '</div>';
        html += '</div>';
      }
      html += '</div>';
    }

    html += '</div>'; // .rd-platform-body
    html += '</div>'; // .rd-platform-section

    return html;
  }

  /** Refresh only the platform section in the floor panel (lightweight, preserves scroll) */
  /** Remove all platform objects whose center is inside the given room's polygon */
  _removePlatformsInRoom(room) {
    const rd = this.rd;
    if (!rd.objects || rd.objects.length === 0) return;
    const geo = rd._getRoomGeoCache(room);
    if (!geo.roomPolygon || geo.roomPolygon.length < 3) return;
    for (let i = rd.objects.length - 1; i >= 0; i--) {
      const obj = rd.objects[i];
      if (obj.type !== 'platform') continue;
      if (rd.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) {
        rd.objects.splice(i, 1);
      }
    }
  }

  _refreshPlatformSection() {
    const existing = document.querySelector('.rd-platform-section');
    if (!existing) return;
    const newHtml = this._buildPlatformSectionHtml();
    const temp = document.createElement('div');
    temp.innerHTML = newHtml;
    const newSection = temp.firstElementChild;
    if (!newSection) return;
    existing.replaceWith(newSection);
    // Re-wire controls on the new section
    const body = newSection.parentElement || document.getElementById('rdSidePanelBody');
    if (body) this._wirePlatformControls(body);
  }

  /** Wire platform panel event handlers */
  _wirePlatformControls(body) {
    const self = this;

    // Init accordion max-height for platform section
    this._accordionInit(body, '.rd-platform-section', '.rd-product-category-body');

    // Toggle header is handled by the delegated click handler on body
    // (see _buildFloorPanelContent — matches .rd-platform-section via classList check)

    // Preset pills — select size and activate placement mode
    body.querySelectorAll('.rd-platform-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        self._initPlatformDefaults();
        const newPresetId = btn.dataset.platformPreset;
        self._platformDefaults.preset = newPresetId;
        self._platformPresetPicked = true;
        // Update active state visually
        body.querySelectorAll('.rd-platform-preset').forEach(b => b.classList.remove('rd-platform-preset--active'));
        btn.classList.add('rd-platform-preset--active');
        // Resize all existing platforms to the new preset (keep colors & logo)
        self._resizeAllPlatformsToPreset(newPresetId);
        self._activatePlatformPlace();
      });
    });
  }

  /** Apply current platform defaults to ALL platforms globally (colors, wings, textures) */
  _applyPlatformDefaultsToAll() {
    const rd = this.rd;
    const allPlatforms = rd.objects.filter(o => o.type === 'platform');
    if (allPlatforms.length === 0) return;
    const pd = this._platformDefaults;
    const presets = this._getPlatformPresets();
    rd.saveUndoState();
    for (const obj of allPlatforms) {
      const objPreset = presets.find(p => p.id === obj.preset) || presets[0];
      obj.wings = pd.wings && objPreset.wingsAvail;
      obj.centerColor = pd.centerColor;
      obj.centerColorName = pd.centerColorName;
      obj.centerColorSource = pd.centerColorSource;
      obj.centerTextureUrl = pd.centerTextureUrl || null;
      obj.wingColor = pd.wingColor;
      obj.wingColorName = pd.wingColorName;
      obj.wingColorSource = pd.wingColorSource;
      obj.wingTextureUrl = pd.wingTextureUrl || null;
      obj.colorConfirmed = pd.colorConfirmed || false;
    }
    rd.render();
    rd.updateMeasurements();
    if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
  }

  /** Resize all existing platforms to a new preset size (keeps colors, logo, position) */
  _resizeAllPlatformsToPreset(presetId) {
    const rd = this.rd;
    const allPlatforms = rd.objects.filter(o => o.type === 'platform');
    if (allPlatforms.length === 0) return;
    const presets = this._getPlatformPresets();
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;
    // Block 8x5 if any existing platform is on tile flooring
    if (presetId === '8x5') {
      for (const obj of allPlatforms) {
        let _room = null;
        for (const room of (rd.rooms || [])) {
          if (!room.closed) continue;
          const geo = rd._getRoomGeoCache(room);
          if (geo.roomPolygon && rd.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { _room = room; break; }
        }
        const _zInfo = _room ? this._getZoneFlooringAtPoint(_room, obj.x, obj.y) : null;
        if (_zInfo && _zInfo.product.type === 'tile') {
          rd.updateStatus("Can\u2019t switch to 8\u2032\u00d75\u2032 \u2014 platforms on tile flooring require tile-aligned sizes.", { error: true });
          return;
        }
      }
    }
    rd.saveUndoState();
    for (const obj of allPlatforms) {
      obj.width = preset.width;
      obj.depth = preset.depth;
      obj.preset = preset.id;
      // Disable wings if new preset doesn't support them
      if (!preset.wingsAvail) obj.wings = false;
      // Re-snap to tile grid after resize (new dims may shift alignment)
      let _snapRoom = null;
      for (const room of (rd.rooms || [])) {
        if (!room.closed) continue;
        const geo = rd._getRoomGeoCache(room);
        if (geo.roomPolygon && rd.pointInPolygon(obj.x, obj.y, geo.roomPolygon)) { _snapRoom = room; break; }
      }
      const _snapZone = _snapRoom ? this._getZoneFlooringAtPoint(_snapRoom, obj.x, obj.y) : null;
      if (_snapZone && _snapZone.product.type === 'tile') {
        const _stW = _snapZone.product.widthFt || 2;
        const _stH = _snapZone.product.heightFt || _stW;
        const _snapGeo = rd._getRoomGeoCache(_snapRoom);
        const _snapped = this._snapPlatformToTileGrid(obj.x, obj.y, obj.width, obj.depth, obj.rotation || 0, _stW, _stH, _snapZone.flooringAngle, _snapGeo.roomPolygon);
        obj.x = _snapped.x;
        obj.y = _snapped.y;
      }
    }
    // Update global defaults to match
    this._platformDefaults.preset = preset.id;
    if (!preset.wingsAvail) this._platformDefaults.wings = false;
    rd.render();
    rd.updateMeasurements();
    if (rd._storage && typeof rd._storage._triggerAutoSave === 'function') rd._storage._triggerAutoSave();
  }

  /** Activate platform placement mode */
  _activatePlatformPlace() {
    const rd = this.rd;
    this._initPlatformDefaults();
    rd._platformPlaceMode = true;
    rd.activeTool = 'platform-place';
    rd.mode = 'platform-place';
    rd.canvas.style.cursor = 'crosshair';
    rd.selectedObjects.clear();
    rd.updateStatus('Click inside a compatible room to place platform.');
    rd.render();
  }

  /** Sync platform panel defaults from the first placed platform (global, not selection-based) */
  _syncPlatformPanelToSelection() {
    const rd = this.rd;
    // Read from any platform (first one) — all platforms share the same colors/wings
    const anyPlatform = rd.objects.find(o => o.type === 'platform');
    if (!anyPlatform) return;
    this._initPlatformDefaults();
    this._platformDefaults.wings = !!anyPlatform.wings;
    this._platformDefaults.centerColor = anyPlatform.centerColor;
    this._platformDefaults.centerColorName = anyPlatform.centerColorName;
    this._platformDefaults.centerColorSource = anyPlatform.centerColorSource || 'stock';
    this._platformDefaults.centerTextureUrl = anyPlatform.centerTextureUrl || null;
    this._platformDefaults.wingColor = anyPlatform.wingColor;
    this._platformDefaults.wingColorName = anyPlatform.wingColorName;
    this._platformDefaults.wingColorSource = anyPlatform.wingColorSource || 'stock';
    this._platformDefaults.wingTextureUrl = anyPlatform.wingTextureUrl || null;
    this._platformDefaults.colorConfirmed = !!anyPlatform.colorConfirmed;
    // Only rebuild Customize tab if we're actually on it — don't hijack the Floor tab
    if (this.currentStep === 'finishing') {
      const body = document.getElementById('rdSidePanelBody');
      if (body) this._buildFinishingPanelBody(body);
    }
  }

  /** Rehydrate platform logo from saved data */
  _rehydratePlatformLogo() {
    if (this._platformLogo && this._platformLogo.dataURL && !this._platformLogo.image) {
      const logo = this._platformLogo;
      const img = new Image();
      const self = this;
      img.onload = () => {
        logo.image = img;
        if (!logo.tightBounds) logo.tightBounds = self._computeLogoTightBounds(img);
        self.rd.render();
      };
      img.src = logo.dataURL;
    }
  }

  /** Same as _rehydratePlatformLogo, for the hardwood platform decal. */
  _rehydrateHardwoodLogo() {
    if (this._hardwoodLogo && this._hardwoodLogo.dataURL && !this._hardwoodLogo.image) {
      const logo = this._hardwoodLogo;
      const img = new Image();
      const self = this;
      img.onload = () => {
        logo.image = img;
        if (!logo.tightBounds) logo.tightBounds = self._computeLogoTightBounds(img);
        self.rd.render();
      };
      img.src = logo.dataURL;
    }
  }

  /* END CUSTOM_ROOMDESIGNER_BY_CLAUDE */

}

/* ──────────────────────────────────────────
   INITIALIZATION
   ────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  if (window.roomDesigner) {
    window.roomDesignerOverlay = new RoomDesignerOverlay(window.roomDesigner);
  }
});
